import React, { useState, useMemo, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { capAcronyms, fmtLabel } from './lib/acronyms';
import { Search, Plus, Building2, User, UserPlus, FileText, Calendar, MapPin, Zap, TrendingUp, TrendingDown, Clock, AlertCircle, ChevronRight, ChevronUp, ChevronDown, ChevronLeft, ChevronsLeft, ChevronsRight, X, Filter, BarChart3, Globe, Users, FolderOpen, Mail, CheckCircle, CheckCircle2, XCircle, Pause, ArrowRight, Edit2, Edit3, Trash2, Eye, EyeOff, Upload, Linkedin, ExternalLink, Phone, Loader2, Settings, Download, Star, Bell, MessageSquare, PhoneCall, Video, Send, Archive, LayoutGrid, List, Target, Activity, PieChart as PieChartIcon, ArrowUpRight, ArrowDownRight, Sparkles, Pin, History, Columns, GripVertical, Square, CheckSquare, CalendarClock, Repeat, Link2, Moon, Sun, Tag, CreditCard, Crown, Shield, Award, Cloud, CloudOff, RefreshCw, HardDrive, Share2, Link, Check, Copy, AlertTriangle, GitBranch, Key, DollarSign, Database, Briefcase, Bookmark, Rss, Newspaper, Lock } from 'lucide-react';
import { PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, Treemap } from 'recharts';
import { SignedIn, SignedOut, SignInButton, SignUpButton, UserButton, useUser, useClerk, useSignIn, useAuth, AuthenticateWithRedirectCallback, RedirectToSignIn, RedirectToSignUp } from '@clerk/clerk-react';
import { usePostHog } from 'posthog-js/react';
import './linkedin-theme.css';
import {
  TopNav,
  MobileBottomNav,
  MobileMoreMenu,
} from './components';
import SlackShell from './components/SlackShell';
import { InvestmentPreferences } from './components/InvestmentPreferences';
import { loadUserData, saveUserData, loadCentralizedData, loadDashboardStats, loadCounterpartDimensions, loadCounterparts, loadDealsWithCounterparts } from './lib/supabase';
import { setAuthToken, setTokenGetter, authFetch } from './lib/authFetch';
import { formatDealValue } from './lib/dealValue';
import DashboardDealsFeed from './views/deals/DashboardDealsFeed';
import { normalizeTech } from './lib/techNormalize';
import { consumeQuota, checkQuota, getUsageSummary, getUpgradeText, USAGE_QUOTAS } from './lib/usageQuotas';
import FollowButton from './components/FollowButton';
import EmailAlertButton from './components/EmailAlertButton';
import ShareAnalysisIcons from './components/ShareAnalysisPopover';
import { getSearchShareUrl } from './lib/shareUtils';
import { CONTACT_DISPLAY, contactClick } from './lib/contact';

// Lazy-loaded components — split into separate chunks
const HelpCenter = lazy(() => import('./HelpCenter'));
const AboutPage = lazy(() => import('./views/about/AboutPage'));
const LiveFeedMockup = lazy(() => import('./views/insights/LiveFeedMockup'));
const LiveFeedLumaDemo = lazy(() => import('./views/insights/LiveFeedLumaDemo'));
const DealSubmissions = lazy(() => import('./views/admin/DealSubmissions'));
const NewsletterGenerator = lazy(() => import('./views/admin/NewsletterGenerator'));
const ResearchMarketing = lazy(() => import('./views/admin/ResearchMarketing'));
const Crm = lazy(() => import('./views/admin/Crm'));
const CrmDueCard = lazy(() => import('./views/admin/CrmDueCard'));
const Agents = lazy(() => import('./views/admin/Agents'));
const DealFilterSidebar = lazy(() => import('./views/deals/DealFilterSidebar'));
const CounterpartDetailPage = lazy(() => import('./views/counterparts/CounterpartDetailPage'));
const CounterpartFilterSidebar = lazy(() => import('./views/counterparts/CounterpartFilterSidebar'));
const DealDetailPage = lazy(() => import('./views/deals/DealDetailPage'));
const BloombergDashboard = lazy(() => import('./views/insights/LiveFeed'));
const SherpaDashboard = lazy(() => import('./views/home/SherpaDashboard'));
const EcosystemPage = lazy(() => import('./views/ecosystem/EcosystemPage'));
const OrganizationsPage = lazy(() => import('./views/organizations/OrganizationsPage'));
const SignalsPage = lazy(() => import('./views/signals/SignalsPage'));
const DealModal = lazy(() => import('./components/modals/DealModal'));
const CounterpartModal = lazy(() => import('./components/modals/CounterpartModal'));
const ShareCounterpartModal = lazy(() => import('./components/modals/ShareCounterpartModal'));
const ShareDealModal = lazy(() => import('./components/modals/ShareDealModal'));
const SharePage = lazy(() => import('./views/share/SharePage'));
const DealSharePage = lazy(() => import('./views/share/DealSharePage'));
const ManageAlerts = lazy(() => import('./views/alerts/ManageAlerts'));
const SearchDemoPage = lazy(() => import('./views/search/SearchDemoPage'));
const DealDesignDemo = lazy(() => import('./views/deals/DealDesignDemo'));
const CounterpartDesignDemo = lazy(() => import('./views/counterparts/CounterpartDesignDemo'));
const SubscribePage = lazy(() => import('./views/subscribe/SubscribePage'));
const LockedDeal = lazy(() => import('./views/deals/LockedDeal'));
const MatchupPage = lazy(() => import('./views/matchup/MatchupPage'));
const ResearchList = lazy(() => import('./views/research/ResearchList'));
const ResearchDetail = lazy(() => import('./views/research/ResearchDetail'));
const BuyReportModal = lazy(() => import('./views/research/BuyReportModal'));
const PurchaseComplete = lazy(() => import('./views/research/PurchaseComplete'));
const DataList = lazy(() => import('./views/data/DataList'));
const HomePage = lazy(() => import('./views/home/HomePage'));
const DataDetail = lazy(() => import('./views/data/DataDetail'));
const AtlasSection = lazy(() => import('./views/atlas/AtlasPage'));
const LandingPage = lazy(() => import('./views/landing/LandingPage'));

// ============================================
// GLOBAL INFRASTRUCTURE SHERPA v1.0.0
// ============================================
// Data storage: Centralized Supabase database
// - All user data stored in Supabase (not local)
// - No Google Drive sync (centralized instead)
// - Each user's data is completely separate
// - Supports future multi-provider backup (Dropbox, etc.)
// ============================================


// Subscription tier configuration — 4-tier model (Free/Basic/Pro/Enterprise)
// Free = registered users, see 10 deals in feed, limited views
// Pro links include 7-day free trial (card required)
// LinkedIn-style usage quotas — users experience full value first, hit
// soft gates after N uses per month. Quota details in src/lib/usageQuotas.js.
const SUBSCRIPTION_TIERS = {
  free: {
    name: 'Free', price: 0,
    dealViews: 20, cpViews: 40, searches: 3, ecosystemSearches: 10, matchupComparisons: 5,
    feedLimit: 30, feedDealsPerSector: 30,
    tagline: '20 deal & 40 company views/month',
  },
  basic: {
    name: 'Basic', price: 20, annualPrice: 200,
    dealViews: 100, cpViews: 200, searches: 10, ecosystemSearches: 50, matchupComparisons: 25,
    feedLimit: 100, feedDealsPerSector: 100,
    tagline: '100 views/month + 10 AI searches/day',
    paymentLink: 'https://buy.stripe.com/cNi9AT5wT0TM57F0C74Rq04',
    annualPaymentLink: 'https://buy.stripe.com/3cI9AT2kHbyq9nV84z4Rq05',
  },
  pro: {
    name: 'Pro', price: 200, annualPrice: 2000,
    dealViews: Infinity, cpViews: Infinity, searches: Infinity, ecosystemSearches: Infinity, matchupComparisons: Infinity,
    feedLimit: Infinity, feedDealsPerSector: Infinity,
    tagline: 'Unlimited access to everything',
    paymentLink: 'https://buy.stripe.com/4gMfZhbVh0TM2Zx4Sn4Rq06',
    annualPaymentLink: 'https://buy.stripe.com/eVq6oHcZl6e657Ffx14Rq07',
  },
};

// Helper to safely format capacity (handles object {amount, unit} or string/number)
const safeFormatCapacity = (capacity, unit) => {
  if (!capacity) return null;
  if (typeof capacity === 'object' && capacity !== null) {
    const amount = capacity.amount || capacity.value;
    const capUnit = capacity.unit || unit || 'MW';
    return amount ? `${amount} ${capUnit}` : null;
  }
  if (typeof capacity === 'string') {
    if (/[a-zA-Z]/.test(capacity)) return capacity;
    return `${capacity} ${unit || 'MW'}`;
  }
  if (typeof capacity === 'number') {
    return `${capacity} ${unit || 'MW'}`;
  }
  return String(capacity);
};

// Helper to safely format value/price (handles object {amount, unit, currency} or string/number)
const safeFormatValue = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && value !== null) {
    let amount = value.amount || value.value;
    if (!amount) return null;
    const currency = value.currency || '$';
    let unit = value.unit || 'M';
    // Normalize raw numbers that slipped through
    if (amount >= 1_000_000_000) { amount = amount / 1_000_000_000; unit = 'B'; }
    else if (amount >= 1_000_000) { amount = amount / 1_000_000; unit = 'M'; }
    // Format: show 1 decimal for non-integer, integer otherwise
    const formatted = amount % 1 === 0 ? amount.toLocaleString() : amount.toFixed(1);
    return `${currency}${formatted}${unit}`;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return null;
};

// Helper to escape special regex characters
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Utility function to linkify counterpart names in text
const linkifyCounterparts = (text, counterparts, onCounterpartClick, isDark = false) => {
  if (!text || !counterparts?.length) return text;

  // Build regex from names (longest first to avoid partial matches)
  const sortedNames = counterparts
    .map(cp => cp.name)
    .filter(name => name && name.length > 2) // Skip very short names
    .sort((a, b) => b.length - a.length);

  if (sortedNames.length === 0) return text;

  const pattern = new RegExp(`\\b(${sortedNames.map(escapeRegex).join('|')})\\b`, 'gi');

  // Split and map to React elements
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const cp = counterparts.find(c => c.name.toLowerCase() === match[1].toLowerCase());
    if (cp) {
      parts.push(
        <button
          key={`cp-link-${keyIndex++}`}
          onClick={(e) => {
            e.stopPropagation();
            onCounterpartClick(cp);
          }}
          className={`${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} hover:underline font-medium`}
        >
          {match[1]}
        </button>
      );
    } else {
      parts.push(match[1]);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return <>{parts}</>;
};

// Format relative time (e.g., "10 min ago", "2h ago", "3d ago")
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  // For older dates, use clean absolute format: "Jan 27" (same year) or "Jan 27, 2025" (different year)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const day = date.getDate();
  if (date.getFullYear() === now.getFullYear()) {
    return `${month} ${day}`;
  }
  return `${month} ${day}, ${date.getFullYear()}`;
};

// Format large numbers (e.g., 3200000000 -> "$3.2B")
const formatLargeValue = (value, prefix = '$') => {
  if (!value || value === 0) return `${prefix}0`;
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return `${prefix}${(value / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `${prefix}${(value / 1e6).toFixed(1)}M`;
  if (absValue >= 1e3) return `${prefix}${(value / 1e3).toFixed(1)}K`;
  return `${prefix}${value}`;
};

// Sector Presets - defines all configurable aspects per sector
const SECTOR_PRESETS = {
  all: {
    id: 'all',
    name: 'All Industries',
    icon: '📊',
    categoryLabel: 'Category',
    categories: ['Solar PV', 'Onshore Wind', 'Offshore Wind', 'Hydro', 'Battery Storage', 'Biomass', 'Multifamily', 'Office', 'Retail', 'Industrial', 'Series A', 'Series B', 'Seed', 'Growth', 'Biotech', 'Digital Health', 'Medical Devices', 'Logistics', 'Manufacturing', 'SaaS', 'Consumer', 'Fintech'],
    categoryIcons: {},
    primaryMetric: { label: 'Value', unit: '', field: 'capacity' },
    secondaryMetric: { label: 'Investment', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Return', unit: '%', field: 'expectedReturn' },
    stages: ['Early', 'Mid', 'Late', 'Closed'],
    pipelineStatuses: ['Screening', 'Review', 'Due Diligence', 'Negotiation', 'Closed', 'Passed'],
    counterpartCategories: ['Client', 'Partner', 'Vendor', 'Investor', 'Other'],
    regions: ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'],
    customFields: []
  },
  renewables: {
    id: 'renewables',
    name: 'Renewables & Energy',
    icon: '⚡',
    categoryLabel: 'Technology',
    categories: ['Solar PV', 'Onshore Wind', 'Offshore Wind', 'Hydro', 'Battery Storage', 'Biomass'],
    categoryIcons: {
      'Solar PV': '☀️',
      'Onshore Wind': '💨',
      'Offshore Wind': '🌊',
      'Hydro': '💧',
      'Battery Storage': '🔋',
      'Biomass': '🌿'
    },
    primaryMetric: { label: 'Capacity', unit: 'MW', field: 'capacity' },
    secondaryMetric: { label: 'Investment', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Expected Return', unit: '%', field: 'expectedReturn' },
    stages: ['Under Development', 'Ready to Build', 'Operating'],
    pipelineStatuses: ['Screening', 'Presented', 'LOI', 'Due Diligence', 'Deal', 'No Deal'],
    counterpartCategories: ['Investor', 'Project Developer', 'Project Owner', 'Intermediary', 'Other'],
    regions: ['Europe', 'North America', 'South America', 'Asia Pacific', 'Middle East', 'Africa'],
    customFields: []
  },
  realEstate: {
    id: 'realEstate',
    name: 'Real Estate',
    icon: '🏢',
    categoryLabel: 'Property Type',
    categories: ['Multifamily', 'Office', 'Retail', 'Industrial', 'Hospitality', 'Mixed-Use', 'Land'],
    categoryIcons: {
      'Multifamily': '🏘️',
      'Office': '🏢',
      'Retail': '🏬',
      'Industrial': '🏭',
      'Hospitality': '🏨',
      'Mixed-Use': '🏗️',
      'Land': '🌍'
    },
    primaryMetric: { label: 'Size', unit: 'sqft', field: 'capacity' },
    secondaryMetric: { label: 'Price', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Cap Rate', unit: '%', field: 'expectedReturn' },
    stages: ['Off-Market', 'Listed', 'Under Contract', 'Closed'],
    pipelineStatuses: ['Prospecting', 'Initial Review', 'LOI', 'Due Diligence', 'Closed', 'Passed'],
    counterpartCategories: ['Buyer', 'Seller', 'Broker', 'Lender', 'Property Manager', 'Other'],
    regions: ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'International'],
    customFields: [
      { id: 'units', label: 'Units', type: 'number' },
      { id: 'yearBuilt', label: 'Year Built', type: 'number' },
      { id: 'occupancy', label: 'Occupancy', type: 'percent' }
    ]
  },
  ventureCapital: {
    id: 'ventureCapital',
    name: 'Venture Capital',
    icon: '🚀',
    categoryLabel: 'Sector',
    categories: ['SaaS', 'Fintech', 'Healthcare', 'Consumer', 'Enterprise', 'Deeptech', 'Marketplace', 'Climate'],
    categoryIcons: {
      'SaaS': '☁️',
      'Fintech': '💳',
      'Healthcare': '🏥',
      'Consumer': '🛍️',
      'Enterprise': '🏛️',
      'Deeptech': '🔬',
      'Marketplace': '🛒',
      'Climate': '🌱'
    },
    primaryMetric: { label: 'ARR', unit: '$', field: 'capacity', isCurrency: true },
    secondaryMetric: { label: 'Raise Amount', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Growth Rate', unit: '%', field: 'expectedReturn' },
    stages: ['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Growth'],
    pipelineStatuses: ['Sourced', 'First Meeting', 'Deep Dive', 'Partner Meeting', 'Term Sheet', 'Closed', 'Passed'],
    counterpartCategories: ['Founder', 'Co-Investor', 'Advisor', 'LP', 'Service Provider', 'Other'],
    regions: ['Silicon Valley', 'New York', 'Boston', 'Los Angeles', 'Europe', 'Asia', 'Other'],
    customFields: [
      { id: 'mrr', label: 'MRR', type: 'currency' },
      { id: 'employees', label: 'Employees', type: 'number' },
      { id: 'runway', label: 'Runway (months)', type: 'number' }
    ]
  },
  privateEquity: {
    id: 'privateEquity',
    name: 'Private Equity',
    icon: '💼',
    categoryLabel: 'Industry',
    categories: ['Manufacturing', 'Business Services', 'Healthcare Services', 'Technology', 'Consumer', 'Financial Services', 'Industrial'],
    categoryIcons: {
      'Manufacturing': '🏭',
      'Business Services': '💼',
      'Healthcare Services': '⚕️',
      'Technology': '💻',
      'Consumer': '🛒',
      'Financial Services': '🏦',
      'Industrial': '⚙️'
    },
    primaryMetric: { label: 'EBITDA', unit: '$', field: 'capacity', isCurrency: true },
    secondaryMetric: { label: 'Enterprise Value', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'IRR Target', unit: '%', field: 'expectedReturn' },
    stages: ['Platform', 'Add-on', 'Growth', 'Turnaround', 'Carve-out'],
    pipelineStatuses: ['Screening', 'Management Meeting', 'IOI Submitted', 'Due Diligence', 'LOI/Exclusivity', 'Closed', 'Passed'],
    counterpartCategories: ['Target Company', 'Seller/Sponsor', 'Investment Bank', 'Lender', 'Advisor', 'Co-Investor', 'Other'],
    regions: ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'],
    customFields: [
      { id: 'revenue', label: 'Revenue', type: 'currency' },
      { id: 'employees', label: 'Employees', type: 'number' },
      { id: 'evMultiple', label: 'EV/EBITDA', type: 'number' }
    ]
  },
  infrastructure: {
    id: 'infrastructure',
    name: 'Infrastructure',
    icon: '🌉',
    categoryLabel: 'Asset Type',
    categories: ['Transport', 'Telecom', 'Utilities', 'Social Infrastructure', 'Digital Infrastructure', 'Logistics'],
    categoryIcons: {
      'Transport': '🚄',
      'Telecom': '📡',
      'Utilities': '💡',
      'Social Infrastructure': '🏫',
      'Digital Infrastructure': '🌐',
      'Logistics': '📦'
    },
    primaryMetric: { label: 'Asset Value', unit: '$', field: 'capacity', isCurrency: true },
    secondaryMetric: { label: 'Investment', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Yield', unit: '%', field: 'expectedReturn' },
    stages: ['Greenfield', 'Brownfield', 'Operating', 'Expansion'],
    pipelineStatuses: ['Screening', 'Preliminary Analysis', 'Due Diligence', 'Bid Preparation', 'Final Bid', 'Awarded', 'Passed'],
    counterpartCategories: ['Government/Authority', 'Concessionaire', 'EPC Contractor', 'O&M Provider', 'Lender', 'Co-Investor', 'Other'],
    regions: ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa', 'Oceania'],
    customFields: [
      { id: 'concessionYears', label: 'Concession (years)', type: 'number' },
      { id: 'operatingIncome', label: 'Operating Income', type: 'currency' }
    ]
  },
  ma: {
    id: 'ma',
    name: 'M&A / Corporate Development',
    icon: '🤝',
    categoryLabel: 'Deal Type',
    categories: ['Strategic Acquisition', 'Merger', 'Divestiture', 'Joint Venture', 'Spin-off', 'Carve-out'],
    categoryIcons: {
      'Strategic Acquisition': '🎯',
      'Merger': '🤝',
      'Divestiture': '📤',
      'Joint Venture': '🔗',
      'Spin-off': '🔄',
      'Carve-out': '✂️'
    },
    primaryMetric: { label: 'Revenue', unit: '$', field: 'capacity', isCurrency: true },
    secondaryMetric: { label: 'Deal Value', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Synergies', unit: '$', field: 'expectedReturn', isCurrency: true },
    stages: ['Identified', 'Approached', 'Negotiating', 'Signed', 'Integration'],
    pipelineStatuses: ['Target Identified', 'Initial Contact', 'NDA Signed', 'Due Diligence', 'Negotiation', 'Signed', 'Closed', 'Terminated'],
    counterpartCategories: ['Target Company', 'Seller', 'Investment Bank', 'Legal Counsel', 'Integration Partner', 'Other'],
    regions: ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Global'],
    customFields: [
      { id: 'employees', label: 'Employees', type: 'number' },
      { id: 'ebitda', label: 'EBITDA', type: 'currency' }
    ]
  },
  dataCenters: {
    id: 'dataCenters',
    name: 'Data Centers',
    icon: '🖥️',
    categoryLabel: 'Facility Type',
    categories: ['Hyperscale', 'Colocation', 'Enterprise', 'Edge', 'Modular', 'Wholesale'],
    categoryIcons: {
      'Hyperscale': '🏢',
      'Colocation': '🔌',
      'Enterprise': '🏛️',
      'Edge': '📡',
      'Modular': '📦',
      'Wholesale': '🏭'
    },
    primaryMetric: { label: 'IT Load', unit: 'MW', field: 'capacity' },
    secondaryMetric: { label: 'Investment', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Target IRR', unit: '%', field: 'expectedReturn' },
    stages: ['Site Selection', 'Permitting', 'Construction', 'Commissioning', 'Operating'],
    pipelineStatuses: ['Prospecting', 'LOI', 'Due Diligence', 'Under Contract', 'Closed', 'Passed'],
    counterpartCategories: ['Operator', 'Developer', 'Tenant', 'Utility', 'Contractor', 'Investor', 'Other'],
    regions: ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'],
    customFields: [
      { id: 'pue', label: 'PUE Target', type: 'number' },
      { id: 'rackCount', label: 'Rack Count', type: 'number' },
      { id: 'fiberConnectivity', label: 'Fiber Carriers', type: 'number' }
    ]
  },
  custom: {
    id: 'custom',
    name: 'Custom',
    icon: '⚙️',
    categoryLabel: 'Category',
    categories: ['Category 1', 'Category 2', 'Category 3'],
    categoryIcons: {},
    primaryMetric: { label: 'Primary Metric', unit: '', field: 'capacity' },
    secondaryMetric: { label: 'Secondary Metric', unit: '$', field: 'investmentSize', isCurrency: true },
    tertiaryMetric: { label: 'Return', unit: '%', field: 'expectedReturn' },
    stages: ['Stage 1', 'Stage 2', 'Stage 3'],
    pipelineStatuses: ['New', 'In Progress', 'Review', 'Approved', 'Completed', 'Rejected'],
    counterpartCategories: ['Client', 'Partner', 'Vendor', 'Advisor', 'Other'],
    regions: ['Region 1', 'Region 2', 'Region 3'],
    customFields: []
  }
};

// Default sector config
const DEFAULT_SECTOR_CONFIG = SECTOR_PRESETS.all;

// Universal counterpart categories (work across all sectors)
const UNIVERSAL_COUNTERPART_CATEGORIES = [
  'Investor',
  'Advisor', 
  'Legal Counsel',
  'Broker / Agent',
  'Lender',
  'Developer',
  'Operator',
  'Consultant',
  'HR / Recruiter',
  'Government / Authority',
  'Service Provider',
  'Other'
];


// Complete Translations
const translations = {
  en: {
    // Navigation
    dashboard: "Dashboard",
    projects: "Projects",
    counterparts: "Counterparts",
    tasks: "Tasks",
    documents: "Documents",
    screener: "Screener",
    settings: "Settings",
    pipelineManager: "Pipeline Manager",
    billing: "Billing",
    
    // Search & Actions
    search: "Search projects, counterparts...",
    upload: "Upload",
    newProject: "New Project",
    newTask: "New Task",
    addCounterpart: "Add Counterpart",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    create: "Create",
    clearFilters: "Clear filters",
    view: "View",
    
    // Welcome
    welcome: "Welcome to Global Infrastructure Sherpa",
    welcomeDesc: "Start building your project pipeline by adding your first project or counterpart.",
    
    // Dashboard Stats
    liveProjects: "Live Projects",
    totalCapacity: "Total Capacity",
    pipelineValue: "Pipeline Value",
    avgReturn: "Avg. Return",
    needsAttention: "Needs Attention",
    byGeography: "By Geography",
    byTechnology: "By Category",
    pipelineStatus: "Pipeline Status",
    developmentStage: "Development Stage",
    investmentByRegion: "Investment by Region",
    topCounterparts: "Top Counterparts",
    requiresAttention: "Requires Attention",
    countries: "countries",
    
    // Project filters
    all: "All",
    live: "Live",
    stale: "Stale",
    deadlines: "Deadlines",
    allTechnologies: "All Technologies",
    allRegions: "All Regions",
    allStatuses: "All Statuses",
    allCounterparts: "All Counterparts",
    sortBy: "Sort:",
    lastActivity: "Last Activity",
    showingOf: "Showing {0} of {1} {2}",
    
    // Project Cards
    capacity: "Capacity",
    investment: "Investment",
    return: "Return",
    
    // Tasks
    pending: "pending",
    overdue: "overdue",
    completed: "completed",
    allTasks: "All Tasks",
    dueToday: "Due Today",
    thisWeek: "This Week",
    noTasks: "No tasks yet",
    createFirstTask: "Create your first task to start tracking follow-ups",
    createTask: "Create Task",
    editTask: "Edit Task",
    
    // Filters
    filters: "Filters",
    allTypes: "All Types",
    allCategories: "All Categories",
    allTags: "All Tags",
    allPriorities: "All Priorities",
    allLinks: "All Links",
    sortByName: "Sort by Name",
    sortByCategory: "Sort by Category",
    sortByProjects: "Sort by Projects",
    sortByLastContact: "Sort by Last Contact",
    sortByDate: "Sort by Date",
    sortBySize: "Sort by Size",
    ascending: "Ascending",
    descending: "Descending",
    companies: "Companies",
    people: "People",
    linkedToProject: "Linked to Project",
    linkedToCounterpart: "Linked to Counterpart",
    unlinked: "Unlinked",
    
    // Priority
    high: "High",
    medium: "Medium",
    low: "Low",
    priority: "Priority",
    
    // Form fields
    title: "Title",
    name: "Name",
    description: "Description",
    dueDate: "Due Date",
    linkTo: "Link to",
    project: "Project",
    counterpart: "Counterpart",
    general: "General",
    tags: "Tags",
    addTag: "Add Tag",
    selectExistingTag: "Select existing tag...",
    orCreateNewTag: "Or create new tag...",
    email: "Email",
    phone: "Phone",
    company: "Company",
    person: "Person",
    category: "Category",
    type: "Type",
    location: "Location",
    website: "Website",
    notes: "Notes",
    
    // Counterparts
    investor: "Investor",
    projectDeveloper: "Project Developer",
    projectOwner: "Project Owner",
    intermediary: "Intermediary",
    other: "Other",
    investmentPrefs: "Investment Preferences",
    associatedCompany: "Associated Company",
    noCompanyAssociation: "No company association",
    relatedCounterparts: "Related Counterparts",
    contact: "Contact",
    sendEmail: "Send Email",
    lastContact: "Last contact",
    lastInteraction: "Last interaction",
    nProjects: "projects",
    showing: "Showing",
    of: "of",
    projectHistory: "Project History",
    relatedDocuments: "Related Documents",
    recentActivity: "Recent Activity",
    noActivityRecorded: "No activity recorded yet",
    preferencesSet: "Preferences set",
    noOtherCounterparts: "No other counterparts available",
    clickToSelect: "Click to select/deselect related counterparts",
    
    // Projects
    screening: "Screening",
    presented: "Presented",
    loi: "LOI",
    dueDiligence: "Due Diligence",
    deal: "Deal",
    noDeal: "No Deal",
    investmentSize: "Investment Size",
    technology: "Technology",
    geography: "Geography",
    stage: "Stage",
    status: "Status",
    associatedCounterparts: "Associated Counterparts",
    noCounterpartsYet: "No counterparts yet. Add counterparts first to associate them.",
    projectIsLive: "Project is live",
    timeline: "Timeline",
    created: "Created",
    deadline: "Deadline",
    suggestedMatches: "Suggested Matches",
    noMatchingInvestors: "No matching investors found",
    addInvestmentPrefs: "Add investment preferences to counterparts to enable matching",
    linkToProject: "+ Link to project",
    match: "match",
    uploadDocuments: "Upload Documents",
    noDocumentsYet: "No documents uploaded yet",
    archived: "Archived",
    underDevelopment: "Under Development",
    readyToBuild: "Ready to Build",
    operating: "Operating",
    
    // Documents
    documentsUploaded: "documents uploaded",
    document: "Document",
    uploaded: "Uploaded",
    actions: "Actions",
    dragDropFiles: "Drop files here or click to browse",
    supportedFormats: "Supports PDF, Word, Excel, PowerPoint, Images, CSV, TXT",
    selectDocuments: "Select documents to upload",
    reviewing: "Reviewing",
    attachToProject: "Attach to Project",
    noProject: "No project",
    documentCategory: "Document Category",
    linkToCounterparts: "Link to Counterparts",
    noCounterpartsAvailable: "No counterparts available",
    addNotes: "Add notes about this document...",
    previous: "Previous",
    next: "Next",
    saveDocument: "Save Document",
    saveAllDocuments: "Save All Documents",
    linkedProject: "Linked Project",
    noProjectLinked: "No project linked",
    linkedCounterparts: "Linked Counterparts",
    noCounterpartsLinked: "No counterparts linked",
    documentContent: "Document Content",
    downloadPdf: "Download PDF",
    extractInfo: "Extract Info",
    pdfNeedsReupload: "PDF needs re-upload",
    pdfReuploadDesc: "This PDF was uploaded before document viewing was available. Delete and re-upload to enable viewing.",
    downloadImage: "Download Image",
    noTextContent: "No text content available for this document type",
    documentName: "Document Name",
    
    // Settings
    language: "Language",
    theme: "Theme",
    light: "Light",
    dark: "Dark",
    exportData: "Export Data",
    importData: "Import Data",
    clearAllData: "Clear All Data",
    dataManagement: "Data Management",
    dataManagementDesc: "Export your data for backup or import previously exported data.",
    manageCategories: "Manage Categories",
    manageCategoriesDesc: "Customize the categories used throughout the platform.",
    technologies: "Technologies",
    developmentStages: "Development Stages",
    pipelineStatuses: "Pipeline Statuses",
    counterpartCategories: "Counterpart Categories",
    documentCategories: "Document Categories",
    regions: "Regions",
    resetToDefaults: "Reset to defaults",
    dataImportedSuccess: "Data imported successfully!",
    dataImportFailed: "Failed to import data. Invalid file format.",
    allDataCleared: "All data cleared.",
    confirmClearData: "Are you sure you want to delete all your projects, counterparts, documents, and tasks?",
    cannotBeUndone: "This action cannot be undone",
    yesClearAllData: "Yes, Clear All Data",
    loadingData: "Loading your data...",
    
    // Time
    today: "Today",
    yesterday: "Yesterday",
    daysAgo: "days ago",
    
    // Misc
    noResults: "No results found",
    loading: "Loading...",
    favorite: "Favorite",
    logInteraction: "Log Interaction",
    addTask: "Add Task",
    viewList: "View as List",
    viewKanban: "View as Kanban",
    dropProjectsHere: "Drop projects here",
    unlimited: "Unlimited",
    
    // Analytics
    pipelineInsights: "Pipeline insights and performance metrics",
    likelyPipeline: "Likely Pipeline",
    possiblePipeline: "Possible Pipeline",
    totalPipeline: "Total Pipeline",
    probability: "probability",
    loiDdStage: "LOI + DD stage",
    presentedStage: "Presented stage",
    activeProjects: "active projects",
    pipelineByStatus: "Pipeline by Status",
    topCounterpartsByEngagement: "Top Counterparts by Engagement",
    interactions: "interactions",
    lastActive: "Last active",
    noActivity: "No activity",
    score: "Score",
    recentActivitySummary: "Recent Activity Summary",
    actionsLabel: "actions",
    addCounterpartsLogInteractions: "Add counterparts and log interactions to see engagement scores",
    
    // Billing
    billingTitle: "Billing & Subscription",
    billingDesc: "Manage your subscription plan",
    currentPlan: "Current Plan",
    upgrade: "Upgrade",
    mostPopular: "Most Popular",
    currentPlanBadge: "Current",
    counterpartsLimit: "counterparts",
    perMonth: "/month",
    choosePlan: "Choose Plan",
    manageBilling: "Manage Billing",
    limitReached: "Limit Reached",
    limitReachedDesc: "You've reached your entity limit. Upgrade to add more projects and counterparts.",
    usageWarning: "You're approaching your limit. Upgrade to add more counterparts.",
    unlimitedProjects: "Unlimited projects",
    unlimitedDocuments: "Unlimited documents",
    prioritySupport: "Priority support",
    needHelpBilling: "Need help with billing?",
    billingHelpDesc: "After upgrading, your new limits will be applied automatically. If you have any questions about billing or need to manage your subscription, contact our support team.",
    contactSupport: "Contact Support",
    upgradeTo: "Upgrade to",
    
    // Interaction types
    phoneCall: "Phone Call",
    emailLabel: "Email",
    meeting: "Meeting",
    videoCall: "Video Call",
    note: "Note",
    recordingInteraction: "Recording interaction with",
    notesOptional: "Notes (optional)",
    addDetails: "Add any details about this interaction...",
    
    // Delete confirmation
    deleteProject: "Delete Project?",
    deleteCounterpart: "Delete Counterpart?",
    deleteProjectWarning: "All associated documents will also be deleted.",
    deleteCounterpartWarning: "This will remove them from all associated projects.",
    
    // Extract project
    extractProjectInfo: "Extract Project Information",
    from: "From",
    extractProjectData: "Extract Project Data",
    useAiAnalyze: "Use AI to analyze this document and extract project information.",
    extractWithAi: "Extract with AI",
    extracting: "Extracting...",
    extractionComplete: "Extraction complete. Review and save below.",
    createNewProject: "Create New Project",
    linkToExisting: "Link to Existing",
    selectProject: "Select Project",
    appendNotesToProject: "Append notes to project",
    projectNameRequired: "Project name is required",
    pleaseSelectProject: "Please select a project",
    entitiesToCreate: "Entities to Create/Link",
    selected: "selected",
    createProject: "Create Project",
    linkAndSave: "Link & Save",
    
    // Misc UI
    newCounterpart: "New Counterpart",
    editProject: "Edit Project",
    editCounterpart: "Edit Counterpart",
    editDocument: "Edit Document",
    saveChanges: "Save Changes",
    saveProject: "Save Project",
    saveCounterpart: "Save Counterpart",
    noProjectsYet: "No projects yet",
    addFirstProject: "Add your first project to start tracking your pipeline.",
    addProject: "Add Project",
    noCounterpartsYetList: "No counterparts yet",
    addFirstCounterpart: "Add your first counterpart to start building relationships.",
    capacityMW: "Capacity (MW)",
    investmentEuro: "Investment ($)",
    expectedReturn: "Expected Return (%)",
    pipelineStatus: "Pipeline Status",
    preferredTechnologies: "Preferred Technologies",
    preferredStages: "Preferred Stages",
    preferredRegions: "Preferred Regions",
    preferenceNotes: "Preference Notes",
    minCapacity: "Min Capacity (MW)",
    maxCapacity: "Max Capacity (MW)",
    minReturn: "Min Return (%)",
    recommendedForInvestors: "(Recommended for investors)",
    linkedInUrl: "LinkedIn URL",
    websiteUrl: "Website URL",
    fillRequiredFields: "Please fill in Project Name, Capacity, and Location",
  }
};


// Start with empty data
const initialProjects = [];
const initialCounterparts = [];
const initialDocuments = [];
const initialActivities = [];

// Default categories from sector config
const defaultTechnologies = DEFAULT_SECTOR_CONFIG.categories;
const defaultStages = DEFAULT_SECTOR_CONFIG.stages;
const defaultPipelineStatuses = DEFAULT_SECTOR_CONFIG.pipelineStatuses;
const defaultCounterpartCategories = UNIVERSAL_COUNTERPART_CATEGORIES;
const defaultDocumentCategories = ["Technical", "Financial", "Legal", "Correspondence", "Other"];
const defaultRegions = DEFAULT_SECTOR_CONFIG.regions;

// Default deal technologies (infrastructure assets)
const defaultDealTechnologies = [
  { value: 'solar', label: 'Solar PV' },
  { value: 'onshore_wind', label: 'Onshore Wind' },
  { value: 'offshore_wind', label: 'Offshore Wind' },
  { value: 'hydro', label: 'Hydro' },
  { value: 'storage', label: 'Battery Storage' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'hydrogen', label: 'Green Hydrogen' },
  { value: 'green_ammonia', label: 'Green Ammonia' },
  { value: 'biomass', label: 'Biomass' },
  { value: 'geothermal', label: 'Geothermal' },
  { value: 'gas', label: 'Gas-fired Power' },
  { value: 'nuclear', label: 'Nuclear' },
  { value: 'oil_gas', label: 'Oil & Gas' },
  { value: 'data_center', label: 'Data Center' },
  { value: 'industrial', label: 'Industrial/Heavy Industry' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'transport', label: 'Transport & Logistics' },
  { value: 'agriculture', label: 'Agriculture & Natural Resources' },
  { value: 'mining', label: 'Mining & Extractives' },
  { value: 'defense', label: 'Defense' },
  { value: 'telecom', label: 'Telecom & Digital Infra' },
  { value: 'water_waste', label: 'Water & Waste' },
  { value: 'other', label: 'Other' },
];

// Role/Profession categories for fuzzy grouping
const ROLE_CATEGORIES = {
  'C-Suite': ['ceo', 'cfo', 'coo', 'cto', 'cio', 'chief', 'president', 'chairman', 'chairwoman'],
  'Director': ['director', 'managing director', 'md', 'head of', 'vp', 'vice president', 'svp', 'evp'],
  'Partner': ['partner', 'principal', 'shareholder'],
  'Investment': ['investment', 'investor', 'portfolio', 'fund manager', 'asset manager'],
  'Business Development': ['business development', 'bd', 'origination', 'commercial', 'sales'],
  'M&A / Finance': ['m&a', 'merger', 'acquisition', 'finance', 'financial', 'treasury', 'controller'],
  'Technical': ['engineer', 'technical', 'technology', 'developer', 'architect', 'scientist'],
  'Legal': ['legal', 'counsel', 'lawyer', 'attorney', 'compliance'],
  'Operations': ['operations', 'project manager', 'program manager', 'coordinator'],
  'Analyst': ['analyst', 'associate', 'research'],
  'Consultant': ['consultant', 'advisor', 'advisory'],
  'Founder': ['founder', 'co-founder', 'entrepreneur', 'owner'],
};

// Function to categorize a role
const categorizeRole = (role) => {
  if (!role) return 'Other';
  const roleLower = role.toLowerCase();

  for (const [category, keywords] of Object.entries(ROLE_CATEGORIES)) {
    if (keywords.some(keyword => roleLower.includes(keyword))) {
      return category;
    }
  }
  return 'Other';
};

// Helper to extract domain from URL for favicon
const getDomainFromUrl = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    return urlObj.hostname;
  } catch {
    return null;
  }
};

// Counterpart Avatar with favicon support
const CounterpartAvatar = ({ counterpart, size = 'md', className = '' }) => {
  const [showImg, setShowImg] = useState(true);
  const logoUrl = counterpart?.logo_url;
  const websiteUrl = counterpart?.websiteUrl || counterpart?.website || counterpart?.investmentPreferences?.website;
  const domain = getDomainFromUrl(websiteUrl);
  const counterpartId = counterpart?.id;

  // Reset when counterpart changes
  useEffect(() => { setShowImg(true); }, [counterpartId]);

  const sizeClasses = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-11 h-11 text-sm',
    lg: 'w-14 h-14 text-base'
  };

  // Cascade: manual logo_url → Brandfetch → Google Favicons → initials
  const [imgStage, setImgStage] = useState(0); // 0=brandfetch, 1=google, 2=hidden
  useEffect(() => { setImgStage(0); }, [counterpartId]);

  const BRANDFETCH_CLIENT = '1idrTt0p2-SJacppl92';
  const brandfetchSrc = domain ? `https://cdn.brandfetch.io/${domain}/icon?c=${BRANDFETCH_CLIENT}` : null;
  const googleSrc = domain ? `https://cdn.brandfetch.io/${domain}/icon?c=1idrTt0p2-SJacppl92` : null;
  const src = logoUrl || (imgStage === 0 ? brandfetchSrc : imgStage === 1 ? googleSrc : null);

  if (src && showImg) {
    return (
      <div className={`${sizeClasses[size]} rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${className}`}>
        <img
          src={src}
          alt=""
          className="w-full h-full object-contain p-1"
          onError={() => { if (logoUrl) { setShowImg(false); } else if (imgStage === 0) { setImgStage(1); } else { setShowImg(false); } }}
        />
      </div>
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-xl bg-gradient-to-br from-blue-700 to-teal-600 flex items-center justify-center text-white font-bold flex-shrink-0 ${className}`}>
      {counterpart?.name?.charAt(0) || '?'}
    </div>
  );
};

// Simple Searchable Select for large lists
const SearchableSelect = ({ 
  items, 
  value, 
  onChange, 
  placeholder = "Search...",
  allLabel = "All",
  showAll = false
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const selected = items.find(i => i.id === value);
  
  const filtered = useMemo(() => {
    if (!search) return items.slice(0, 20);
    const q = search.toLowerCase();
    return items.filter(i => 
      i.name?.toLowerCase().includes(q) || 
      i.company?.toLowerCase().includes(q) ||
      i.role?.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [items, search]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={open ? search : (value === 'all' ? allLabel : selected?.name || '')}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => { setOpen(true); setSearch(''); }}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-700/50"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {showAll && (
            <div onClick={() => { onChange('all'); setOpen(false); }} 
              className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">{allLabel}</div>
          )}
          {filtered.map(i => (
            <div key={i.id} onClick={() => { onChange(i.id); setOpen(false); }}
              className="px-3 py-2 hover:bg-slate-50 cursor-pointer">
              <div className="text-sm font-medium">{i.name}</div>
              {(i.role || i.company) && <div className="text-xs text-slate-500">{i.role}{i.role && i.company && ' · '}{i.company}</div>}
            </div>
          ))}
          {filtered.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No results</div>}
          {!search && items.length > 20 && <div className="px-3 py-2 text-xs text-slate-400 border-t">Type to search {items.length} items</div>}
        </div>
      )}
    </div>
  );
};

// Simple Multi-select with search
const SearchableMultiSelect = ({ items, selectedIds = [], onChange, placeholder = "Search..." }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const selected = items.filter(i => selectedIds.includes(i.id));
  const available = items.filter(i => !selectedIds.includes(i.id));
  
  const filtered = useMemo(() => {
    if (!search) return available.slice(0, 15);
    const q = search.toLowerCase();
    return available.filter(i => 
      i.name?.toLowerCase().includes(q) || 
      i.company?.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [available, search]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} className="relative">
      <div className="flex flex-wrap gap-1 p-2 bg-slate-100 rounded-xl min-h-[44px]">
        {selected.map(i => (
          <span key={i.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-700 text-white rounded-full text-xs">
            {i.name}
            <button type="button" onClick={() => onChange(selectedIds.filter(id => id !== i.id))} className="hover:bg-blue-800 rounded-full">
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length ? "Add..." : placeholder}
          className="flex-1 min-w-[80px] bg-transparent border-none outline-none text-sm"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
          {filtered.map(i => (
            <div key={i.id} onClick={() => { onChange([...selectedIds, i.id]); setSearch(''); }}
              className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">{i.name}</div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ AI OPTIMIZATION UTILITIES ============

// Compress image to max width while maintaining aspect ratio (returns base64)
const compressImageForAI = async (base64Data, mimeType, maxWidth = 1000) => {
  return new Promise((resolve) => {
    // Only compress images
    if (!mimeType?.startsWith('image/')) {
      resolve(base64Data);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      // Only resize if larger than max
      if (img.width <= maxWidth) {
        resolve(base64Data);
        return;
      }
      
      const canvas = document.createElement('canvas');
      const ratio = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * ratio;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Get compressed base64 (use jpeg for photos, png for screenshots)
      const outputType = mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
      const quality = outputType === 'image/jpeg' ? 0.8 : undefined;
      const dataUrl = canvas.toDataURL(outputType, quality);
      const compressedBase64 = dataUrl.split(',')[1];
      
      resolve(compressedBase64);
    };
    img.onerror = () => resolve(base64Data); // Fallback to original on error
    img.src = `data:${mimeType};base64,${base64Data}`;
  });
};

// Cache expiration in days
const AI_CACHE_EXPIRY_DAYS = 30;

// Check if cached AI result is still valid
const isCacheValid = (cacheTimestamp) => {
  if (!cacheTimestamp) return false;
  const cacheDate = new Date(cacheTimestamp);
  const now = new Date();
  const daysDiff = (now - cacheDate) / (1000 * 60 * 60 * 24);
  return daysDiff < AI_CACHE_EXPIRY_DAYS;
};

// Format metric value based on sector config
const formatMetricValue = (value, metric) => {
  if (!value && value !== 0) return "—";
  if (metric?.isCurrency) {
    const num = Number(value);
    if (num >= 1000000000) return `${metric.unit}${(num / 1000000000).toFixed(1)}B`;
    if (num >= 1000000) return `${metric.unit}${(num / 1000000).toFixed(0)}M`;
    if (num >= 1000) return `${metric.unit}${(num / 1000).toFixed(0)}K`;
    return `${metric.unit}${num.toLocaleString()}`;
  }
  return `${Number(value).toLocaleString()} ${metric?.unit || ''}`.trim();
};

// Counterpart investment sizes, not deal values — these records carry no
// currency field at all, so the euro sign here was an assumption rather than a
// fact. USD is the platform's stated currency and its majority by a wide margin.
const formatCurrency = (value) => {
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getDaysAgo = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff} days ago`;
};

const isStale = (lastActivity, threshold = 30) => {
  if (!lastActivity) return false;
  const date = new Date(lastActivity);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  return diff > threshold;
};

const getStatusColor = (status) => {
  const colors = {
    "Screening": "bg-slate-100 text-slate-700",
    "Presented": "bg-blue-100 text-blue-700",
    "LOI": "bg-blue-100 text-blue-700",
    "Due Diligence": "bg-blue-100 text-blue-700",
    "Deal": "bg-blue-100 text-blue-700",
    "No Deal": "bg-slate-100 text-slate-700"
  };
  return colors[status] || "bg-slate-100 text-slate-700";
};

const getTechIcon = (tech, sectorConfig = null) => {
  // Use sector config icons if available
  if (sectorConfig?.categoryIcons?.[tech]) {
    return sectorConfig.categoryIcons[tech];
  }
  // Fallback to generic
  return "📁";
};

const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return "📄";
  if (['doc', 'docx'].includes(ext)) return "📝";
  if (['xls', 'xlsx'].includes(ext)) return "📊";
  if (['ppt', 'pptx'].includes(ext)) return "📽️";
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return "🖼️";
  return "📎";
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Category Editor Component
const CategoryEditor = ({ title, items, setItems, defaultItems }) => {
  const [newItem, setNewItem] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");

  const handleAdd = () => {
    if (newItem.trim() && !items.includes(newItem.trim())) {
      setItems([...items, newItem.trim()]);
      setNewItem("");
    }
  };

  const handleDelete = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditValue(items[index]);
  };

  const handleSaveEdit = () => {
    if (editValue.trim()) {
      const newItems = [...items];
      newItems[editingIndex] = editValue.trim();
      setItems(newItems);
    }
    setEditingIndex(null);
    setEditValue("");
  };

  const handleReset = () => {
    setItems([...defaultItems]);
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-slate-900">{title}</h3>
        <button onClick={handleReset} className="text-xs text-slate-600 hover:text-slate-700">
          Reset to defaults
        </button>
      </div>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {items.map((item, index) => (
          <div key={index} className="group flex items-center gap-1 bg-slate-100 rounded-full">
            {editingIndex === index ? (
              <div className="flex items-center">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                  className="px-3 py-1 text-sm bg-white border border-blue-700 rounded-full w-32 focus:outline-none"
                  autoFocus
                />
                <button onClick={handleSaveEdit} className="p-1 text-blue-700 hover:text-blue-800">
                  <CheckCircle size={14} />
                </button>
                <button onClick={() => setEditingIndex(null)} className="p-1 text-slate-600 hover:text-slate-600">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <span className="px-3 py-1 text-sm text-slate-700">{item}</span>
                <button onClick={() => handleEdit(index)} className="p-1 text-slate-600 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => handleDelete(index)} className="p-1 pr-2 text-slate-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={12} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      
      <div className="flex gap-2">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder={`Add new ${title.toLowerCase().replace(/s$/, '')}...`}
          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-700/50 focus:border-blue-700"
        />
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800 text-sm">
          Add
        </button>
      </div>
    </div>
  );
};

// DocumentUploadModal component removed - documents feature disabled

// Deal Technology Editor Component (handles {value, label} pairs)
const DealTechnologyEditor = ({ items, setItems, defaultItems }) => {
  const [newLabel, setNewLabel] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Generate a value from label (lowercase, replace spaces with underscores)
  const generateValue = (label) => {
    return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  const handleAdd = () => {
    if (newLabel.trim() && !items.some(item => item.label.toLowerCase() === newLabel.trim().toLowerCase())) {
      const value = generateValue(newLabel.trim());
      setItems([...items, { value, label: newLabel.trim() }]);
      setNewLabel("");
    }
  };

  const handleDelete = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditValue(items[index].label);
  };

  const handleSaveEdit = () => {
    if (editValue.trim()) {
      const newItems = [...items];
      newItems[editingIndex] = {
        value: generateValue(editValue.trim()),
        label: editValue.trim()
      };
      setItems(newItems);
    }
    setEditingIndex(null);
    setEditValue("");
  };

  const handleReset = () => {
    setItems([...defaultItems]);
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-slate-900">Deal Technologies</h3>
        <button onClick={handleReset} className="text-xs text-slate-600 hover:text-slate-700">
          Reset to defaults
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {items.map((item, index) => (
          <div key={index} className="group flex items-center gap-1 bg-slate-100 rounded-full">
            {editingIndex === index ? (
              <div className="flex items-center">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                  className="px-3 py-1 text-sm bg-white border border-blue-700 rounded-full w-32 focus:outline-none"
                  autoFocus
                />
                <button onClick={handleSaveEdit} className="p-1 text-blue-700 hover:text-blue-800">
                  <CheckCircle size={14} />
                </button>
                <button onClick={() => setEditingIndex(null)} className="p-1 text-slate-600 hover:text-slate-600">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <span className="px-3 py-1 text-sm text-slate-700">{item.label}</span>
                <button onClick={() => handleEdit(index)} className="p-1 text-slate-600 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => handleDelete(index)} className="p-1 pr-2 text-slate-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={12} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="Add new technology..."
          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-700/50 focus:border-blue-700"
        />
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800 text-sm">
          Add
        </button>
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({
  projects, counterparts, documents, tasks, activities, favorites, recentItems,
  technologies, stages, pipelineStatuses, counterpartCategories, documentCategories, regions, dealTechnologies,
  setProjects, setCounterparts, setDocuments, setTasks, setActivities, setFavorites, setRecentItems,
  setTechnologies, setStages, setPipelineStatuses, setCounterpartCategories, setDocumentCategories, setRegions, setDealTechnologies,
  sectorConfig, setSectorConfig,
  // Google Drive props
  gDriveConnected, gDriveLoading, gDriveSyncing, gDriveLastSync, gDriveError, gDriveFileInfo,
  connectGoogleDrive, disconnectGoogleDrive, manualSyncGoogleDrive, loadFromGoogleDrive,
  // AI Settings props
  aiProvider, setAiProvider, aiApiKey, setAiApiKey, saveAiSettings,
  // Tier props
  userTier, user,
  t
}) => {
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const [importStatus, setImportStatus] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  // CSV Import state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [csvType, setCsvType] = useState('contacts'); // 'contacts' or 'projects'
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);

  // Parse CSV text into array of objects
  const parseCSV = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    // Skip LinkedIn notes header - look for the actual header row
    let startIndex = 0;
    
    // Find the real header row (contains "First Name" for LinkedIn, or standard headers)
    for (let i = 0; i < Math.min(lines.length, 10); i++) {
      const line = lines[i];
      // LinkedIn format header
      if (line.includes('First Name') && line.includes('Last Name')) {
        startIndex = i;
        break;
      }
      // Generic header patterns
      if (line.includes('Name') && (line.includes('Email') || line.includes('Company'))) {
        startIndex = i;
        break;
      }
    }
    
    // Find header row
    const headerLine = lines[startIndex];
    if (!headerLine) return [];
    
    // Parse CSV properly handling quoted fields
    const parseRow = (row) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };
    
    const headers = parseRow(headerLine);
    const data = [];
    
    for (let i = startIndex + 1; i < lines.length; i++) {
      const values = parseRow(lines[i]);
      if (values.length >= 2 && values.some(v => v)) { // Skip empty rows
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        data.push(row);
      }
    }
    
    return data;
  };

  // Handle CSV file selection
  const handleCsvSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const data = parseCSV(text);
        setCsvData(data);
        setCsvPreview(data.slice(0, 5)); // Preview first 5 rows
        setShowCsvModal(true);
      } catch (error) {
        setImportStatus({ type: 'error', message: 'Failed to parse CSV file.' });
        setTimeout(() => setImportStatus(null), 3000);
      }
    };
    reader.readAsText(file);
    
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  // Import contacts from CSV
  const importContacts = () => {
    if (!csvData || csvData.length === 0) return;
    
    // Check tier limits
    const tierInfo = SUBSCRIPTION_TIERS[userTier] || SUBSCRIPTION_TIERS.basic;
    const isAdmin = user?.publicMetadata?.admin;
    const currentEntityCount = projects.length + counterparts.length;
    const availableSlots = isAdmin ? Infinity : (tierInfo.entityLimit || Infinity) - currentEntityCount;

    if (availableSlots <= 0 && !isAdmin) {
      setCsvImporting(false);
      setShowCsvModal(false);
      setImportStatus({ 
        type: 'error', 
        message: `Entity limit reached (${tierInfo.entityLimit}). Upgrade your plan to import more contacts.` 
      });
      setTimeout(() => setImportStatus(null), 5000);
      return;
    }
    
    setCsvImporting(true);
    
    // Step 1: Extract all unique company names from CSV
    const companyNames = new Set();
    const parsedData = csvData.map((row) => {
      const normalizedRow = {};
      Object.keys(row).forEach(key => {
        normalizedRow[key.toLowerCase().trim()] = row[key];
      });
      
      const firstName = row['First Name'] || normalizedRow['first name'] || '';
      const lastName = row['Last Name'] || normalizedRow['last name'] || '';
      const name = row['Name'] || normalizedRow['name'] || `${firstName} ${lastName}`.trim();
      const email = row['Email Address'] || normalizedRow['email address'] || normalizedRow['email'] || '';
      const company = (row['Company'] || normalizedRow['company'] || normalizedRow['organization'] || '').trim();
      const position = row['Position'] || normalizedRow['position'] || normalizedRow['title'] || '';
      const linkedIn = row['URL'] || normalizedRow['url'] || normalizedRow['linkedin'] || '';
      const phone = row['Phone'] || normalizedRow['phone'] || normalizedRow['phone number'] || '';
      const notes = row['Notes'] || normalizedRow['notes'] || '';
      const category = row['Category'] || normalizedRow['category'] || normalizedRow['type'] || counterpartCategories[0] || 'Other';
      const location = normalizedRow['location'] || '';
      
      if (company) companyNames.add(company);
      
      return { name, email, company, position, linkedIn, phone, notes, category, location };
    }).filter(c => c.name && c.name.trim());

    // Step 2: Find existing companies and create missing ones
    const existingCompanies = counterparts.filter(c => c.type === 'company');
    const companyMap = {}; // company name -> counterpart id
    const newCompanies = [];
    
    companyNames.forEach(companyName => {
      const companyLower = companyName.toLowerCase();
      // Check if company already exists (fuzzy match)
      const existing = existingCompanies.find(c => 
        c.name.toLowerCase() === companyLower ||
        c.name.toLowerCase().includes(companyLower) ||
        companyLower.includes(c.name.toLowerCase())
      );
      
      if (existing) {
        companyMap[companyName] = existing.id;
      } else {
        // Check if we already created this company in this import
        const alreadyCreated = newCompanies.find(c => c.name.toLowerCase() === companyLower);
        if (alreadyCreated) {
          companyMap[companyName] = alreadyCreated.id;
        } else {
          // Create new company counterpart
          const newCompany = {
            id: `cp-company-${Date.now()}-${newCompanies.length}`,
            name: companyName,
            type: 'company',
            category: 'Other',
            company: '',
            role: '',
            contactEmail: '',
            contactPhone: '',
            linkedinUrl: '',
            websiteUrl: '',
            location: '',
            notes: 'Auto-created from CSV import',
            companyId: null,
            relatedCounterpartIds: [],
            investmentPreferences: null,
            tags: [],
            lastInteraction: new Date().toISOString().split('T')[0],
            created: new Date().toISOString(),
          };
          newCompanies.push(newCompany);
          companyMap[companyName] = newCompany.id;
        }
      }
    });

    // Step 3: Create person counterparts with company links
    // Helper to infer category from role (MULTILINGUAL)
    const inferCategoryFromRole = (role, explicitCategory) => {
      // If explicit category provided and valid, use it
      if (explicitCategory && counterpartCategories.includes(explicitCategory)) {
        return explicitCategory;
      }
      
      if (!role) return counterpartCategories[0] || 'Other';
      const r = role.toLowerCase();
      
      // HR / Recruitment - Check FIRST before Advisor (Recruitment Consultant = HR, not Advisor)
      if (r.includes('recruitment') || r.includes('recruiter') || r.includes('recruiting') ||
          r.includes('human resource') || r.includes('talent acqui') || r.includes('talent manager') ||
          r.includes('hr manager') || r.includes('hr director') || r.includes('head of people') || 
          r.includes('people operation') || r.includes('hrbp') || r.includes('hr business partner') ||
          r.includes('staffing') || r.includes('headhunter') || r.includes('executive search') ||
          r.includes('talent partner') || r.includes('people partner') || r.includes('workforce') ||
          r.includes('employee relation') || r.includes('learning & development') ||
          r.includes('personalleiter') || r.includes('personaldirektor') || r.includes('personalwesen') ||  // German
          r.includes('rekrutierung') || r.includes('personalreferent') ||
          r.includes('recursos humanos') || r.includes('reclutamiento') || r.includes('selección') ||  // Spanish
          r.includes('ressources humaines') || r.includes('recrutement') || r.includes('responsable rh') ||  // French
          r.includes('risorse umane') || r.includes('selezione')) {  // Italian
        return counterpartCategories.includes('HR / Recruiter') ? 'HR / Recruiter' : counterpartCategories[0];
      }
      
      // Advisor / Consultant (EN, DE, ES, FR, PT, IT)
      if (r.includes('advisor') || r.includes('adviser') || r.includes('consultant') || r.includes('advisory') || 
          r.includes('counsel') ||
          r.includes('berater') || r.includes('beratung') ||  // German
          r.includes('asesor') || r.includes('consultor') || r.includes('consejero') ||  // Spanish
          r.includes('conseiller') ||  // French
          r.includes('assessor') ||  // Portuguese
          r.includes('consulente') || r.includes('consigliere')) {  // Italian
        return counterpartCategories.includes('Advisor') ? 'Advisor' : counterpartCategories[0];
      }
      
      // Investor / Investment (EN, DE, ES, FR, PT, IT)
      if (r.includes('invest') || r.includes('portfolio') || r.includes('fund') ||
          r.includes('asset manage') || r.includes('capital') ||
          r.includes('anlage') || r.includes('vermögen') ||  // German
          r.includes('inversión') || r.includes('gestora') ||  // Spanish
          r.includes('investimento')) {  // Portuguese/Italian
        return counterpartCategories.includes('Investor') ? 'Investor' : counterpartCategories[0];
      }
      
      // Developer / Development (EN, DE, ES, FR, PT, IT)
      if (r.includes('development manager') || r.includes('project manager') || r.includes('origination') ||
          r.includes('projektleiter') || r.includes('projektmanager') ||  // German
          r.includes('jefe de proyecto') || r.includes('director de proyecto') ||  // Spanish
          r.includes('chef de projet') ||  // French
          r.includes('gerente de projeto')) {  // Portuguese
        return counterpartCategories.includes('Developer') ? 'Developer' : counterpartCategories[0];
      }
      
      // Banker / Finance (EN, DE, ES, FR, PT, IT)
      if (r.includes('banker') || r.includes('banking') || r.includes('finance director') ||
          r.includes('cfo') || r.includes('treasurer') || r.includes('chief financial') ||
          r.includes('finanzleiter') || r.includes('finanzdirektor') ||  // German
          r.includes('director financiero') || r.includes('tesorero') ||  // Spanish
          r.includes('directeur financier') ||  // French
          r.includes('diretor financeiro') ||  // Portuguese
          r.includes('direttore finanziario')) {  // Italian
        return counterpartCategories.includes('Banker') ? 'Banker' : 
               counterpartCategories.includes('Financial Advisor') ? 'Financial Advisor' : counterpartCategories[0];
      }
      
      // Legal (EN, DE, ES, FR, PT, IT)
      if (r.includes('legal') || r.includes('lawyer') || r.includes('attorney') ||
          r.includes('solicitor') || r.includes('counsel') ||
          r.includes('rechtsanwalt') || r.includes('anwalt') || r.includes('jurist') ||  // German
          r.includes('abogado') || r.includes('jurídico') ||  // Spanish
          r.includes('avocat') || r.includes('juriste') ||  // French
          r.includes('advogado') ||  // Portuguese
          r.includes('avvocato')) {  // Italian
        return counterpartCategories.includes('Legal') ? 'Legal' : 
               counterpartCategories.includes('Advisor') ? 'Advisor' : counterpartCategories[0];
      }
      
      // Default
      return counterpartCategories[0] || 'Other';
    };

    const newPeople = parsedData.map((data, idx) => ({
      id: `cp-import-${Date.now()}-${idx}`,
      name: data.name,
      type: 'person',
      category: inferCategoryFromRole(data.position, data.category),
      company: data.company,
      role: data.position,
      contactEmail: data.email,
      contactPhone: data.phone,
      linkedinUrl: data.linkedIn,
      websiteUrl: '',
      location: data.location,
      notes: data.notes,
      companyId: data.company ? companyMap[data.company] || null : null,
      relatedCounterpartIds: [],
      investmentPreferences: null,
      tags: [],
      lastInteraction: new Date().toISOString().split('T')[0],
      created: new Date().toISOString(),
    }));

    // Step 4: Filter duplicates (by name+email)
    const existingKeys = new Set(counterparts.map(c => `${c.name.toLowerCase()}-${(c.contactEmail || '').toLowerCase()}`));
    const uniqueNewPeople = newPeople.filter(c => !existingKeys.has(`${c.name.toLowerCase()}-${(c.contactEmail || '').toLowerCase()}`));
    
    // Combine all new counterparts
    let allNew = [...newCompanies, ...uniqueNewPeople];
    
    // Step 5: Apply tier limits - truncate if exceeds available slots
    let tierLimitSkipped = 0;
    if (!isAdmin && allNew.length > availableSlots) {
      tierLimitSkipped = allNew.length - availableSlots;
      allNew = allNew.slice(0, availableSlots);
    }
    
    setCounterparts([...counterparts, ...allNew]);
    
    setCsvImporting(false);
    setShowCsvModal(false);
    setCsvData(null);
    setCsvPreview([]);
    
    const companiesMsg = newCompanies.length > 0 ? `, ${Math.min(newCompanies.length, allNew.filter(c => c.type === 'company').length)} companies created` : '';
    const tierMsg = tierLimitSkipped > 0 ? `, ${tierLimitSkipped} skipped (tier limit)` : '';
    const duplicatesSkipped = newPeople.length - uniqueNewPeople.length;
    
    setImportStatus({ 
      type: tierLimitSkipped > 0 ? 'warning' : 'success', 
      message: `Imported ${allNew.filter(c => c.type === 'person').length} contacts${companiesMsg}${tierMsg}${duplicatesSkipped > 0 ? ` (${duplicatesSkipped} duplicates skipped)` : ''}` 
    });
    setTimeout(() => setImportStatus(null), 5000);
  };

  // Import projects from CSV
  const importProjects = () => {
    if (!csvData || csvData.length === 0) return;
    
    // Check tier limits
    const tierInfo = SUBSCRIPTION_TIERS[userTier] || SUBSCRIPTION_TIERS.basic;
    const isAdmin = user?.publicMetadata?.admin;
    const currentEntityCount = projects.length + counterparts.length;
    const availableSlots = isAdmin ? Infinity : (tierInfo.entityLimit || Infinity) - currentEntityCount;

    if (availableSlots <= 0 && !isAdmin) {
      setCsvImporting(false);
      setShowCsvModal(false);
      setImportStatus({ 
        type: 'error', 
        message: `Entity limit reached (${tierInfo.entityLimit}). Upgrade your plan to import more projects.` 
      });
      setTimeout(() => setImportStatus(null), 5000);
      return;
    }
    
    setCsvImporting(true);
    
    const newProjects = csvData.map((row, idx) => {
      const name = row['Name'] || row['name'] || row['Project Name'] || row['project_name'] || '';
      const category = row['Category'] || row['category'] || row['Technology'] || row['Type'] || technologies[0] || 'Other';
      const stage = row['Stage'] || row['stage'] || stages[0] || 'Early Stage';
      const status = row['Status'] || row['status'] || pipelineStatuses[0] || 'Active';
      
      return {
        id: `proj-import-${Date.now()}-${idx}`,
        name: name || 'Unnamed Project',
        description: row['Description'] || row['description'] || '',
        technology: technologies.includes(category) ? category : technologies[0] || 'Other',
        stage: stages.includes(stage) ? stage : stages[0] || 'Early Stage',
        pipelineStatus: pipelineStatuses.includes(status) ? status : pipelineStatuses[0] || 'Active',
        geography: row['Geography'] || row['geography'] || row['Location'] || row['Region'] || regions[0] || '',
        capacity: row['Capacity'] || row['capacity'] || row['Size'] || '',
        investment: row['Investment'] || row['investment'] || row['Value'] || '',
        irr: row['IRR'] || row['irr'] || '',
        counterpart: row['Counterpart'] || row['counterpart'] || row['Partner'] || '',
        notes: row['Notes'] || row['notes'] || '',
        lastActivity: new Date().toISOString().split('T')[0],
        created: new Date().toISOString(),
        documents: [],
        activities: [],
      };
    }).filter(p => p.name && p.name !== 'Unnamed Project');

    // Add to existing projects (avoiding exact duplicates by name)
    const existingNames = new Set(projects.map(p => p.name.toLowerCase()));
    let uniqueNew = newProjects.filter(p => !existingNames.has(p.name.toLowerCase()));
    
    // Apply tier limits - truncate if exceeds available slots
    let tierLimitSkipped = 0;
    if (!isAdmin && uniqueNew.length > availableSlots) {
      tierLimitSkipped = uniqueNew.length - availableSlots;
      uniqueNew = uniqueNew.slice(0, availableSlots);
    }
    
    setProjects([...projects, ...uniqueNew]);
    
    setCsvImporting(false);
    setShowCsvModal(false);
    setCsvData(null);
    setCsvPreview([]);
    
    const tierMsg = tierLimitSkipped > 0 ? `, ${tierLimitSkipped} skipped (tier limit)` : '';
    const duplicatesSkipped = newProjects.length - uniqueNew.length - tierLimitSkipped;
    
    setImportStatus({ 
      type: tierLimitSkipped > 0 ? 'warning' : 'success', 
      message: `Imported ${uniqueNew.length} projects${tierMsg}${duplicatesSkipped > 0 ? ` (${duplicatesSkipped} duplicates skipped)` : ''}` 
    });
    setTimeout(() => setImportStatus(null), 5000);
  };

  // Download CSV template
  const downloadTemplate = (type) => {
    let csv = '';
    if (type === 'contacts') {
      // Match LinkedIn format: First Name,Last Name,URL,Email Address,Company,Position
      // Then add Global Infrastructure Sherpa extras: Category,Notes
      csv = 'First Name,Last Name,URL,Email Address,Company,Position,Category,Notes\n';
      csv += 'John,Doe,https://linkedin.com/in/johndoe,john@example.com,Acme Corp,CEO,Investor,Met at conference\n';
      csv += 'Jane,Smith,https://linkedin.com/in/janesmith,jane@example.com,Tech Inc,Partner,Advisor,\n';
      csv += 'Olivier,Hochet,https://www.linkedin.com/in/olivierhochet,,TotalEnergies,Digital Business Leader,Developer,\n';
    } else {
      csv = 'Name,Description,Category,Stage,Status,Geography,Capacity,Investment,Counterpart,Notes\n';
      csv += 'Solar Farm Alpha,100MW solar project,Solar,Development,Active,Spain,100 MW,$50M,Acme Energy,Q2 deadline\n';
      csv += 'Wind Project Beta,Offshore wind development,Wind,Early Stage,Pipeline,Germany,200 MW,$120M,WindCo,Permitting phase\n';
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gis-${type}-template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export data to JSON file
  const handleExport = () => {
    const data = {
      exportDate: new Date().toISOString(),
      version: "2.0",
      projects,
      counterparts,
      documents,
      tasks,
      activities,
      favorites,
      recentItems,
      categories: {
        technologies,
        stages,
        pipelineStatuses,
        counterpartCategories,
        documentCategories,
        regions
      }
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gis-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import data from JSON file
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        
        // Validate and import data
        if (data.projects) setProjects(data.projects);
        if (data.counterparts) setCounterparts(data.counterparts);
        if (data.documents) setDocuments(data.documents);
        if (data.tasks) setTasks(data.tasks);
        if (data.activities) setActivities(data.activities);
        if (data.favorites) setFavorites(data.favorites);
        if (data.recentItems) setRecentItems(data.recentItems);
        
        // Import categories if present
        if (data.categories) {
          if (data.categories.technologies) setTechnologies(data.categories.technologies);
          if (data.categories.stages) setStages(data.categories.stages);
          if (data.categories.pipelineStatuses) setPipelineStatuses(data.categories.pipelineStatuses);
          if (data.categories.counterpartCategories) setCounterpartCategories(data.categories.counterpartCategories);
          if (data.categories.documentCategories) setDocumentCategories(data.categories.documentCategories);
          if (data.categories.regions) setRegions(data.categories.regions);
        }
        
        setImportStatus({ type: 'success', message: 'Data imported successfully!' });
        setTimeout(() => setImportStatus(null), 3000);
      } catch (error) {
        setImportStatus({ type: 'error', message: 'Failed to import data. Invalid file format.' });
        setTimeout(() => setImportStatus(null), 3000);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Clear all data
  const handleClearData = () => {
    setShowClearConfirm(true);
  };
  
  const confirmClearData = async () => {
    setProjects([]);
    setCounterparts([]);
    setDocuments([]);
    setTasks([]);
    setActivities([]);
    setFavorites({ projects: [], counterparts: [] });
    setRecentItems([]);
    // Also clear from storage
    try {
      await window.storage.delete('gis-data-v4');
    } catch (e) {
      console.log('Storage clear error:', e);
    }
    setShowClearConfirm(false);
    setImportStatus({ type: 'success', message: 'All data cleared.' });
    setTimeout(() => setImportStatus(null), 3000);
  };

  // Fix categories from roles - update counterpart categories based on their role
  const fixCategoriesFromRoles = () => {
    const inferCategory = (role) => {
      if (!role) return null;
      const r = role.toLowerCase();
      
      // HR / Recruitment - Check FIRST before Advisor
      if (r.includes('recruitment') || r.includes('recruiter') || r.includes('recruiting') ||
          r.includes('human resource') || r.includes('talent acqui') || r.includes('talent manager') ||
          r.includes('hr manager') || r.includes('hr director') || r.includes('head of people') ||
          r.includes('people operation') || r.includes('hrbp') || r.includes('hr business partner') ||
          r.includes('staffing') || r.includes('headhunter') || r.includes('executive search') ||
          r.includes('talent partner') || r.includes('people partner') || r.includes('workforce') ||
          r.includes('employee relation') || r.includes('learning & development') ||
          r.includes('personalleiter') || r.includes('personalwesen') || r.includes('rekrutierung') ||
          r.includes('recursos humanos') || r.includes('reclutamiento') || r.includes('selección') ||
          r.includes('ressources humaines') || r.includes('recrutement') || r.includes('responsable rh') ||
          r.includes('risorse umane') || r.includes('selezione')) {
        return counterpartCategories.includes('HR / Recruiter') ? 'HR / Recruiter' : null;
      }
      
      // Advisor / Consultant
      if (r.includes('advisor') || r.includes('adviser') || r.includes('consultant') || r.includes('advisory') || 
          r.includes('counsel') || r.includes('berater') || r.includes('asesor') || r.includes('consultor') ||
          r.includes('conseiller') || r.includes('consulente')) {
        return counterpartCategories.includes('Advisor') ? 'Advisor' : null;
      }
      
      // Investor / Investment
      if (r.includes('invest') || r.includes('portfolio') || r.includes('fund') ||
          r.includes('asset manage') || r.includes('capital') || r.includes('private equity')) {
        return counterpartCategories.includes('Investor') ? 'Investor' : null;
      }
      
      // Developer / Development
      if (r.includes('development manager') || r.includes('project manager') || r.includes('origination') ||
          r.includes('projektleiter') || r.includes('jefe de proyecto')) {
        return counterpartCategories.includes('Developer') ? 'Developer' : null;
      }
      
      // Banker / Finance
      if (r.includes('banker') || r.includes('banking') || r.includes('cfo') || r.includes('chief financial') ||
          r.includes('finance director') || r.includes('treasurer')) {
        return counterpartCategories.includes('Banker') ? 'Banker' : 
               counterpartCategories.includes('Financial Advisor') ? 'Financial Advisor' : null;
      }
      
      // Legal
      if (r.includes('legal') || r.includes('lawyer') || r.includes('attorney') || r.includes('solicitor') ||
          r.includes('rechtsanwalt') || r.includes('abogado') || r.includes('avocat') || r.includes('avvocato')) {
        return counterpartCategories.includes('Legal') ? 'Legal' : 
               counterpartCategories.includes('Advisor') ? 'Advisor' : null;
      }
      
      return null;
    };

    let updated = 0;
    const updatedCounterparts = counterparts.map(c => {
      if (c.type !== 'person' || !c.role) return c;
      
      const inferredCategory = inferCategory(c.role);
      if (inferredCategory && inferredCategory !== c.category) {
        updated++;
        return { ...c, category: inferredCategory };
      }
      return c;
    });

    if (updated > 0) {
      setCounterparts(updatedCounterparts);
      setImportStatus({ type: 'success', message: `Updated ${updated} counterpart categories based on roles.` });
    } else {
      setImportStatus({ type: 'success', message: 'No categories needed updating.' });
    }
    setTimeout(() => setImportStatus(null), 5000);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-900">Settings</h2>
      
      {/* Cloud Sync - Supabase */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
            <Cloud size={20} className="text-blue-800" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-slate-900">Cloud Sync</h3>
            <p className="text-sm text-slate-500">Your data is automatically synced to the cloud</p>
          </div>
          <span className="flex items-center gap-1 text-sm text-blue-800">
            <CheckCircle size={14} /> Active
          </span>
        </div>

        <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle size={20} className="text-green-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">Cloud sync is enabled</p>
            <p className="text-xs text-green-600">
              Your data is automatically saved and synced across all your devices
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-slate-50 rounded-xl">
          <ul className="text-xs text-slate-600 space-y-1">
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-blue-700" /> Automatic sync on every change</li>
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-blue-700" /> Access from any device</li>
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-blue-700" /> Secure, encrypted storage</li>
            <li className="flex items-center gap-2"><CheckCircle size={12} className="text-blue-700" /> Your data is isolated and private</li>
          </ul>
        </div>
      </div>

      {/* AI Document Extraction Settings */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
            <Sparkles size={20} className="text-blue-800" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">AI Document Extraction</h3>
            <p className="text-sm text-slate-500">Extract project and counterpart info from documents automatically</p>
          </div>
          <span className="ml-auto px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
            <CheckCircle size={12} /> Enabled
          </span>
        </div>

        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="text-sm text-blue-800">
            AI extraction is included with your subscription. Open any document and click "Extract with AI" to automatically extract project details, counterparty information, and contact details.
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs text-blue-700">
            <span className="flex items-center gap-1">
              <CheckCircle size={12} /> PDF support
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle size={12} /> Image support
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle size={12} /> Text extraction
            </span>
          </div>
        </div>
      </div>
      
      {/* Sector Configuration */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">{sectorConfig.icon}</span>
          <div>
            <h3 className="font-medium text-slate-900">Sector Configuration</h3>
            <p className="text-sm text-slate-500">Choose your industry to customize labels and categories</p>
          </div>
        </div>
        
        {/* Sector Presets */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Object.values(SECTOR_PRESETS).map(preset => (
            <button
              key={preset.id}
              onClick={() => {
                setSectorConfig(preset);
                setTechnologies(preset.categories);
                setStages(preset.stages);
                setPipelineStatuses(preset.pipelineStatuses);
                setRegions(preset.regions);
              }}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                sectorConfig.id === preset.id 
                  ? 'border-blue-700 bg-blue-50' 
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className="text-2xl mb-2 block">{preset.icon}</span>
              <span className="text-sm font-medium text-slate-900">{preset.name}</span>
            </button>
          ))}
        </div>
        
        {/* Current Sector Details */}
        <div className="bg-slate-50 rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Category Label</label>
              <input 
                type="text"
                value={sectorConfig.categoryLabel}
                onChange={(e) => setSectorConfig({...sectorConfig, categoryLabel: e.target.value})}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Primary Metric</label>
              <div className="flex gap-2">
                <input 
                  type="text"
                  value={sectorConfig.primaryMetric.label}
                  onChange={(e) => setSectorConfig({
                    ...sectorConfig, 
                    primaryMetric: {...sectorConfig.primaryMetric, label: e.target.value}
                  })}
                  className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                />
                <input 
                  type="text"
                  value={sectorConfig.primaryMetric.unit}
                  onChange={(e) => setSectorConfig({
                    ...sectorConfig, 
                    primaryMetric: {...sectorConfig.primaryMetric, unit: e.target.value}
                  })}
                  className="w-16 px-2 py-2 bg-white border border-slate-200 rounded-lg text-sm text-center"
                  placeholder="Unit"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Secondary Metric</label>
              <div className="flex gap-2">
                <input 
                  type="text"
                  value={sectorConfig.secondaryMetric.label}
                  onChange={(e) => setSectorConfig({
                    ...sectorConfig, 
                    secondaryMetric: {...sectorConfig.secondaryMetric, label: e.target.value}
                  })}
                  className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                />
                <input 
                  type="text"
                  value={sectorConfig.secondaryMetric.unit}
                  onChange={(e) => setSectorConfig({
                    ...sectorConfig, 
                    secondaryMetric: {...sectorConfig.secondaryMetric, unit: e.target.value}
                  })}
                  className="w-16 px-2 py-2 bg-white border border-slate-200 rounded-lg text-sm text-center"
                  placeholder="Unit"
                />
              </div>
            </div>
          </div>
          
          {/* Custom Fields Preview */}
          {sectorConfig.customFields && sectorConfig.customFields.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">Custom Fields</label>
              <div className="flex flex-wrap gap-2">
                {sectorConfig.customFields.map(field => (
                  <span key={field.id} className="px-3 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-600">
                    {field.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Data Management */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 p-6">
        <h3 className="font-medium text-slate-900 mb-4">{t('dataManagement')}</h3>
        <p className="text-sm text-slate-600 mb-4">
          Export your data for backup or import previously exported data. Your data includes {projects.length} projects, {counterparts.length} counterparts, {documents.length} documents, and {tasks.length} tasks.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800 text-sm font-medium">
            <Download size={16} />
            Export Data
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".json"
            className="hidden"
          />
          <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium text-slate-600">
            <Upload size={16} />
            Import Data
          </button>
          
          <button onClick={handleClearData} className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-xl hover:bg-red-100 text-sm font-medium">
            <Trash2 size={16} />
            Clear All Data
          </button>
          
          <button onClick={fixCategoriesFromRoles} className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-800 rounded-xl hover:bg-blue-100 text-sm font-medium">
            <RefreshCw size={16} />
            Fix Categories
          </button>
        </div>
        
        {importStatus && (
          <div className={`mt-4 p-3 rounded-xl text-sm ${
            importStatus.type === 'success' ? 'bg-blue-50 text-blue-800' : 
            importStatus.type === 'warning' ? 'bg-blue-50 text-blue-800' : 
            'bg-red-50 text-red-600'
          }`}>
            {importStatus.message}
          </div>
        )}
      </div>

      {/* CSV Import */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Upload size={20} className="text-blue-800" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">CSV Import</h3>
            <p className="text-sm text-slate-500">Import contacts or projects from CSV files (LinkedIn compatible)</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="p-4 bg-slate-50 rounded-xl">
            <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
              <Users size={16} /> Import Contacts
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              Upload LinkedIn exports or custom CSV with: Name, Email, Company, Position, etc.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setCsvType('contacts'); csvInputRef.current?.click(); }}
                className="flex-1 px-3 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Upload CSV
              </button>
              <button
                onClick={() => downloadTemplate('contacts')}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
              >
                Template
              </button>
            </div>
          </div>
          
          <div className="p-4 bg-slate-50 rounded-xl">
            <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
              <FolderOpen size={16} /> Import Projects
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              Upload CSV with: Name, Description, Category, Stage, Status, etc.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setCsvType('projects'); csvInputRef.current?.click(); }}
                className="flex-1 px-3 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Upload CSV
              </button>
              <button
                onClick={() => downloadTemplate('projects')}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
              >
                Template
              </button>
            </div>
          </div>
        </div>
        
        <input
          type="file"
          ref={csvInputRef}
          onChange={handleCsvSelect}
          accept=".csv"
          className="hidden"
        />
      </div>

      {/* CSV Import Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Import {csvType === 'contacts' ? 'Contacts' : 'Projects'}
                </h3>
                <p className="text-sm text-slate-500">
                  {csvData?.length || 0} rows found in CSV
                </p>
              </div>
              <button onClick={() => { setShowCsvModal(false); setCsvData(null); setCsvPreview([]); }} 
                className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5">
              {/* Preview Table */}
              {csvPreview.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Preview (first 5 rows)</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          {Object.keys(csvPreview[0]).slice(0, 6).map((header, i) => (
                            <th key={i} className="px-3 py-2 text-left font-medium text-slate-600 border-b border-slate-200">
                              {header}
                            </th>
                          ))}
                          {Object.keys(csvPreview[0]).length > 6 && (
                            <th className="px-3 py-2 text-left font-medium text-slate-400 border-b border-slate-200">
                              +{Object.keys(csvPreview[0]).length - 6} more
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.map((row, rowIdx) => (
                          <tr key={rowIdx} className="border-b border-slate-100 last:border-0">
                            {Object.values(row).slice(0, 6).map((val, colIdx) => (
                              <td key={colIdx} className="px-3 py-2 text-slate-700 truncate max-w-[150px]">
                                {val || <span className="text-slate-300">—</span>}
                              </td>
                            ))}
                            {Object.keys(row).length > 6 && (
                              <td className="px-3 py-2 text-slate-400">...</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Field Mapping Info */}
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Automatic Field Mapping</h4>
                {csvType === 'contacts' ? (
                  <div className="text-xs text-blue-800 space-y-1">
                    <p><strong>LinkedIn format supported:</strong> First Name, Last Name, URL, Email Address, Company, Position</p>
                    <p><strong>Optional extras:</strong> Category, Notes (add columns after Position)</p>
                    <p className="text-blue-700 mt-2">💡 Export from LinkedIn → Upload directly → Position maps to Role</p>
                  </div>
                ) : (
                  <p className="text-xs text-blue-800">
                    Recognized columns: <strong>Name, Description, Category/Technology, Stage, Status, Geography, Capacity, Investment, Counterpart, Notes</strong>
                  </p>
                )}
              </div>
            </div>
            
            <div className="p-5 border-t border-slate-100 flex gap-3">
              <button 
                onClick={() => { setShowCsvModal(false); setCsvData(null); setCsvPreview([]); }}
                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={csvType === 'contacts' ? importContacts : importProjects}
                disabled={csvImporting || !csvData?.length}
                className="flex-1 px-4 py-2.5 bg-blue-700 text-white rounded-xl hover:bg-blue-800 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {csvImporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Import {csvData?.length || 0} {csvType === 'contacts' ? 'Contacts' : 'Projects'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories */}
      <div>
        <h3 className="font-medium text-slate-900 mb-4">{t('manageCategories')}</h3>
        <p className="text-sm text-slate-600 mb-4">
          Customize the categories for <strong>{sectorConfig.name}</strong>. Hover over items to edit or delete them.
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CategoryEditor 
            title={sectorConfig.categoryLabel + "s"} 
            items={technologies} 
            setItems={setTechnologies}
            defaultItems={sectorConfig.categories}
          />
          <CategoryEditor 
            title="Development Stages" 
            items={stages} 
            setItems={setStages}
            defaultItems={sectorConfig.stages}
          />
          <CategoryEditor 
            title="Pipeline Statuses" 
            items={pipelineStatuses} 
            setItems={setPipelineStatuses}
            defaultItems={sectorConfig.pipelineStatuses}
          />
          <CategoryEditor 
            title="Counterpart Categories (Universal)" 
            items={counterpartCategories} 
            setItems={setCounterpartCategories}
            defaultItems={UNIVERSAL_COUNTERPART_CATEGORIES}
          />
          <CategoryEditor 
            title="Document Categories" 
            items={documentCategories} 
            setItems={setDocumentCategories}
            defaultItems={defaultDocumentCategories}
          />
          <CategoryEditor
            title="Regions"
            items={regions}
            setItems={setRegions}
            defaultItems={sectorConfig.regions}
          />
          <DealTechnologyEditor
            items={dealTechnologies}
            setItems={setDealTechnologies}
            defaultItems={defaultDealTechnologies}
          />
        </div>
      </div>
      
      {/* Clear Data Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{t('clearAllData')}</h3>
                <p className="text-sm text-slate-600">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete all your projects, counterparts, documents, and tasks? This will permanently remove all data from the application.
            </p>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setShowClearConfirm(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={confirmClearData}
                className="px-4 py-2 bg-red-600 text-slate-900 rounded-xl hover:bg-red-700 text-sm font-medium"
              >
                Yes, Clear All Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// CLOUD STORAGE (Supabase - no Google Drive)
// ============================================
// NOTE: Google Drive sync has been removed.
// Data is now stored centrally in Supabase.
// The following stubs exist for backwards compatibility.

// Stub config - Google Drive is disabled
const GOOGLE_DRIVE_CONFIG = {
  clientId: '', // Empty = disabled
  apiKey: '',
  scopes: '',
  discoveryDocs: [],
  fileName: 'data.json',
  folderName: 'Backups'
};

// Stub functions - no-op
const loadGoogleApi = () => Promise.resolve(null);
const loadGoogleIdentity = () => Promise.resolve(null);


// IndexedDB Helper Class - handles unlimited local storage (replaces localStorage)
class IndexedDBStorage {
  constructor(dbName = 'GISDB', storeName = 'data') {
    this.dbName = dbName;
    this.storeName = storeName;
    this.db = null;
    this.initFailed = false;
  }

  async init() {
    if (this.db) return this.db;
    if (this.initFailed) return null;

    // Check if IndexedDB is available
    if (typeof indexedDB === 'undefined') {
      this.initFailed = true;
      return null;
    }

    return new Promise((resolve) => {
      // Timeout after 3 seconds - Safari/Firefox can hang indefinitely
      const timeout = setTimeout(() => {
        console.log('IndexedDB init timeout - falling back');
        this.initFailed = true;
        resolve(null);
      }, 3000);

      try {
        const request = indexedDB.open(this.dbName, 1);

        request.onerror = () => {
          clearTimeout(timeout);
          console.error('IndexedDB error:', request.error);
          this.initFailed = true;
          resolve(null);
        };

        request.onsuccess = () => {
          clearTimeout(timeout);
          this.db = request.result;
          resolve(this.db);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName, { keyPath: 'key' });
          }
        };
        
        request.onblocked = () => {
          clearTimeout(timeout);
          console.log('IndexedDB blocked');
          this.initFailed = true;
          resolve(null);
        };
      } catch (e) {
        clearTimeout(timeout);
        console.error('IndexedDB exception:', e);
        this.initFailed = true;
        resolve(null);
      }
    });
  }

  async get(key) {
    const db = await this.init();
    if (!db) return null;
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(key);

        request.onerror = () => resolve(null);
        request.onsuccess = () => resolve(request.result?.value || null);
      } catch (e) {
        resolve(null);
      }
    });
  }

  async set(key, value) {
    const db = await this.init();
    if (!db) return false;
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.put({ key, value });

        request.onerror = () => resolve(false);
        request.onsuccess = () => resolve(true);
      } catch (e) {
        resolve(false);
      }
    });
  }

  async delete(key) {
    const db = await this.init();
    if (!db) return false;
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(key);

        request.onerror = () => resolve(false);
        request.onsuccess = () => resolve(true);
      } catch (e) {
        resolve(false);
      }
    });
  }
}

// Create global IndexedDB instance
const indexedDBStorage = new IndexedDBStorage();

// Google Drive API helper class
class GoogleDriveSync {
  constructor() {
    this.accessToken = null;
    this.fileId = null;
    this.legacyFileId = null; // For migration from old shared file
    this.folderId = null;
    this.tokenClient = null;
    this.isInitialized = false;
    this.userId = null;
  }

  // Set the current user ID for user-specific storage
  setUserId(userId) {
    // If user changed, clear cached tokens and reinitialize
    if (this.userId && this.userId !== userId) {
      this.accessToken = null;
      this.fileId = null;
      this.legacyFileId = null;
      this.folderId = null;
    }
    this.userId = userId;
  }

  // Get user-specific localStorage key
  getStorageKey(key) {
    return this.userId ? `${key}_${this.userId}` : key;
  }

  // Get user-specific data file name
  getDataFileName() {
    return this.userId ? `gis-${this.userId}.json` : GOOGLE_DRIVE_CONFIG.fileName;
  }

  async init() {
    if (!GOOGLE_DRIVE_CONFIG.clientId) {
      console.log('Google Drive: No client ID configured');
      return false;
    }

    try {
      await loadGoogleApi();
      await new Promise((resolve) => window.gapi.load('client', resolve));
      await window.gapi.client.init({
        discoveryDocs: GOOGLE_DRIVE_CONFIG.discoveryDocs,
      });

      await loadGoogleIdentity();
      
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_DRIVE_CONFIG.clientId,
        scope: GOOGLE_DRIVE_CONFIG.scopes,
        callback: () => {}, // Will be set per-request
      });

      // Check for stored token (user-specific)
      let storedToken = null;
      let tokenExpiry = null;
      try {
        storedToken = localStorage.getItem(this.getStorageKey('gdrive_access_token'));
        tokenExpiry = localStorage.getItem(this.getStorageKey('gdrive_token_expiry'));
      } catch (e) {
        // Safari private browsing
      }
      
      if (storedToken && tokenExpiry && Date.now() < parseInt(tokenExpiry)) {
        this.accessToken = storedToken;
        window.gapi.client.setToken({ access_token: storedToken });
        await this.findOrCreateFolder();
        await this.findDataFile();
      }

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Google Drive init error:', error);
      return false;
    }
  }

  async signIn() {
    return new Promise((resolve, reject) => {
      this.tokenClient.callback = async (response) => {
        if (response.error) {
          reject(response);
          return;
        }
        this.accessToken = response.access_token;
        // Store token with expiry (1 hour) - user-specific
        try {
          localStorage.setItem(this.getStorageKey('gdrive_access_token'), response.access_token);
          localStorage.setItem(this.getStorageKey('gdrive_token_expiry'), (Date.now() + 3600000).toString());
        } catch (e) {
          // Safari private browsing
        }
        
        await this.findOrCreateFolder();
        await this.findDataFile();
        resolve(response);
      };
      this.tokenClient.requestAccessToken({ prompt: 'consent' });
    });
  }

  signOut() {
    if (this.accessToken) {
      window.google.accounts.oauth2.revoke(this.accessToken);
    }
    this.accessToken = null;
    this.fileId = null;
    this.folderId = null;
    try {
      localStorage.removeItem(this.getStorageKey('gdrive_access_token'));
      localStorage.removeItem(this.getStorageKey('gdrive_token_expiry'));
      localStorage.removeItem(this.getStorageKey('gdrive_file_id'));
      localStorage.removeItem(this.getStorageKey('gdrive_folder_id'));
    } catch (e) {
      // Safari private browsing
    }
    window.gapi.client.setToken(null);
  }

  isConnected() {
    return !!this.accessToken;
  }

  async findOrCreateFolder() {
    try {
      // Check if folder exists
      const response = await window.gapi.client.drive.files.list({
        q: `name='${GOOGLE_DRIVE_CONFIG.folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
        fields: 'files(id, name)',
      });

      if (response.result.files.length > 0) {
        this.folderId = response.result.files[0].id;
      } else {
        // Create folder
        const createResponse = await window.gapi.client.drive.files.create({
          resource: {
            name: GOOGLE_DRIVE_CONFIG.folderName,
            mimeType: 'application/vnd.google-apps.folder',
          },
          fields: 'id',
        });
        this.folderId = createResponse.result.id;
      }
      try {
        localStorage.setItem(this.getStorageKey('gdrive_folder_id'), this.folderId);
      } catch (e) {
        // Safari private browsing
      }
    } catch (error) {
      console.error('Error finding/creating folder:', error);
    }
  }

  async findDataFile() {
    try {
      const fileName = this.getDataFileName();
      const legacyFileName = GOOGLE_DRIVE_CONFIG.fileName; // 'gis-data.json'
      
      // First try to find user-specific file
      let response = await window.gapi.client.drive.files.list({
        q: `name='${fileName}' and '${this.folderId}' in parents and trashed=false`,
        fields: 'files(id, name, modifiedTime)',
      });

      if (response.result.files.length > 0) {
        this.fileId = response.result.files[0].id;
        try {
          localStorage.setItem(this.getStorageKey('gdrive_file_id'), this.fileId);
        } catch (e) {
          // Safari private browsing
        }
        return response.result.files[0];
      }
      
      // If not found and we have a userId, try legacy file as fallback
      if (this.userId && fileName !== legacyFileName) {
        console.log('User-specific file not found, checking for legacy file...');
        response = await window.gapi.client.drive.files.list({
          q: `name='${legacyFileName}' and '${this.folderId}' in parents and trashed=false`,
          fields: 'files(id, name, modifiedTime)',
        });
        
        if (response.result.files.length > 0) {
          console.log('Found legacy file, will migrate on next save');
          // Use legacy file for reading, but DON'T save the fileId
          // This way, next save will create a new user-specific file
          this.legacyFileId = response.result.files[0].id;
          return { ...response.result.files[0], isLegacy: true };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding data file:', error);
      return null;
    }
  }

  async saveData(data) {
    if (!this.accessToken || !this.folderId) return false;

    try {
      const content = JSON.stringify(data, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const fileName = this.getDataFileName();

      const metadata = {
        name: fileName,
        mimeType: 'application/json',
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', blob);

      let url;
      let method;

      if (this.fileId) {
        // Update existing file
        url = `https://www.googleapis.com/upload/drive/v3/files/${this.fileId}?uploadType=multipart`;
        method = 'PATCH';
      } else {
        // Create new file
        metadata.parents = [this.folderId];
        form.set('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        url = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: form,
      });

      const result = await response.json();
      if (result.id) {
        this.fileId = result.id;
        try {
          localStorage.setItem(this.getStorageKey('gdrive_file_id'), this.fileId);
        } catch (e) {
          // Safari private browsing
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving to Google Drive:', error);
      return false;
    }
  }

  async loadData() {
    // Use fileId if available, otherwise try legacyFileId
    const fileIdToUse = this.fileId || this.legacyFileId;
    if (!this.accessToken || !fileIdToUse) return null;

    try {
      const response = await window.gapi.client.drive.files.get({
        fileId: fileIdToUse,
        alt: 'media',
      });
      return response.result;
    } catch (error) {
      console.error('Error loading from Google Drive:', error);
      return null;
    }
  }

  async getFileInfo() {
    const fileIdToUse = this.fileId || this.legacyFileId;
    if (!this.accessToken || !fileIdToUse) return null;

    try {
      const response = await window.gapi.client.drive.files.get({
        fileId: fileIdToUse,
        fields: 'id, name, modifiedTime, size',
      });
      return response.result;
    } catch (error) {
      console.error('Error getting file info:', error);
      return null;
    }
  }

  // Upload a file directly to Google Drive
  async uploadFile(file, onProgress) {
    if (!this.accessToken || !this.folderId) return null;

    try {
      const metadata = {
        name: file.name,
        parents: [this.folderId],
        mimeType: file.type || 'application/octet-stream',
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', file);

      const response = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,size,mimeType,webViewLink,webContentLink',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: form,
        }
      );

      const result = await response.json();
      if (result.id) {
        return {
          driveFileId: result.id,
          driveName: result.name,
          driveSize: result.size,
          driveMimeType: result.mimeType,
          driveWebViewLink: result.webViewLink,
          driveWebContentLink: result.webContentLink,
        };
      }
      return null;
    } catch (error) {
      console.error('Error uploading file to Google Drive:', error);
      return null;
    }
  }

  // Download a file from Google Drive
  async downloadFile(fileId) {
    if (!this.accessToken) return null;

    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        return blob;
      }
      return null;
    } catch (error) {
      console.error('Error downloading file from Google Drive:', error);
      return null;
    }
  }

  // Get file as base64 from Google Drive
  async getFileAsBase64(fileId) {
    const blob = await this.downloadFile(fileId);
    if (!blob) return null;

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });
  }

  // Delete a file from Google Drive
  async deleteFile(fileId) {
    if (!this.accessToken) return false;

    try {
      await window.gapi.client.drive.files.delete({
        fileId: fileId,
      });
      return true;
    } catch (error) {
      console.error('Error deleting file from Google Drive:', error);
      return false;
    }
  }

  // Make a file publicly accessible (anyone with link can view)
  async makeFilePublic(fileId) {
    if (!this.accessToken) return false;

    try {
      // Use fetch API directly - more reliable than gapi.client.drive.permissions
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            role: 'reader',
            type: 'anyone',
          }),
        }
      );

      if (response.ok) {
        return true;
      } else {
        const error = await response.json();
        console.error('Permission API error:', error);
        return false;
      }
    } catch (error) {
      console.error('Error making file public:', error);
      return false;
    }
  }

  // Get public download/view URL for a file
  getPublicUrl(fileId) {
    return `https://drive.google.com/file/d/${fileId}/view`;
  }

  getPublicDownloadUrl(fileId) {
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }

  getPublicPreviewUrl(fileId) {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }
}

// Singleton instance
// ============================================
// IMPROVED DOCUMENTS TAB COMPONENT
// ============================================
const DocumentsTabContent = ({
  documents, setDocuments, filteredDocuments, projects, counterparts, documentCategories,
  setShowDocumentUploadModal, setViewingDocument, setEditingDocument, setExtractingFromDocument,
  setSharingDocument, setSelectedProject, setSelectedCounterpart, documentsPage, setDocumentsPage, ITEMS_PER_PAGE,
  formatDate, getFileIcon, gDriveConnected, t, searchQuery, setSearchQuery, theme, themeClasses
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docCategoryFilter, setDocCategoryFilter] = useState('all');
  const [docProjectFilter, setDocProjectFilter] = useState('all');
  const [docCounterpartFilter, setDocCounterpartFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [quickPreview, setQuickPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      await processFiles(droppedFiles);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      await processFiles(selectedFiles);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const processFiles = async (files) => {
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, fileName: '' });

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length, fileName: file.name });

      // Extract content for local storage/preview
      const content = await extractFileContent(file);
      
      // Create document record
      const newDoc = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: formatFileSize(file.size),
        sizeBytes: file.size,
        category: guessCategory(file.name),
        projectId: null,
        counterpartIds: [],
        content: content.text,
        contentType: content.type,
        base64: null, // Don't store base64 if using Google Drive
        thumbnail: content.thumbnail,
        uploadedAt: new Date().toISOString().split('T')[0],
        notes: '',
        driveFileId: null,
        driveWebViewLink: null,
      };

      // Upload to Google Drive if connected
      if (gDriveConnected && googleDriveSync.isConnected()) {
        try {
          const driveResult = await googleDriveSync.uploadFile(file);
          if (driveResult) {
            newDoc.driveFileId = driveResult.driveFileId;
            newDoc.driveWebViewLink = driveResult.driveWebViewLink;
            newDoc.driveWebContentLink = driveResult.driveWebContentLink;
            // Clear base64 since file is in Drive (save space)
            newDoc.base64 = null;
          } else {
            // Fallback to local storage
            newDoc.base64 = content.base64;
          }
        } catch (error) {
          console.error('Drive upload failed, storing locally:', error);
          newDoc.base64 = content.base64;
        }
      } else {
        // No Google Drive, store locally
        newDoc.base64 = content.base64;
      }

      setDocuments(prev => [newDoc, ...prev]);
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0, fileName: '' });
  };

  const extractFileContent = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      if (file.type.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(file.name)) {
        reader.onload = (e) => {
          const base64 = e.target.result.split(',')[1];
          resolve({ 
            text: '[Image file]', 
            type: 'image', 
            base64, 
            thumbnail: e.target.result // Full data URL for thumbnail
          });
        };
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        reader.onload = (e) => {
          const base64 = e.target.result.split(',')[1];
          resolve({ text: '[PDF Document]', type: 'pdf', base64 });
        };
        reader.readAsDataURL(file);
      } else if (file.name.endsWith('.eml') || file.type === 'message/rfc822') {
        // Parse .eml email files (MIME format)
        reader.onload = (e) => {
          const content = e.target.result;
          // Extract key email headers and body
          const fromMatch = content.match(/^From:\s*(.+)$/mi);
          const toMatch = content.match(/^To:\s*(.+)$/mi);
          const subjectMatch = content.match(/^Subject:\s*(.+)$/mi);
          const dateMatch = content.match(/^Date:\s*(.+)$/mi);
          
          // Find the body (after double newline)
          const bodyStart = content.indexOf('\n\n');
          let body = bodyStart > -1 ? content.substring(bodyStart + 2) : '';
          
          // Clean up body - remove HTML tags if present
          body = body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
          
          const emailInfo = [
            fromMatch ? `From: ${fromMatch[1]}` : '',
            toMatch ? `To: ${toMatch[1]}` : '',
            subjectMatch ? `Subject: ${subjectMatch[1]}` : '',
            dateMatch ? `Date: ${dateMatch[1]}` : '',
            '',
            body.substring(0, 2000) // First 2000 chars of body
          ].filter(Boolean).join('\n');
          
          resolve({ 
            text: emailInfo || '[Email file]', 
            type: 'email',
            emailMetadata: {
              from: fromMatch?.[1]?.trim(),
              to: toMatch?.[1]?.trim(),
              subject: subjectMatch?.[1]?.trim(),
              date: dateMatch?.[1]?.trim()
            }
          });
        };
        reader.readAsText(file);
      } else if (file.name.endsWith('.msg')) {
        // .msg files are binary Outlook format - store as binary with metadata
        reader.onload = (e) => {
          const base64 = e.target.result.split(',')[1];
          resolve({ 
            text: '[Outlook Email (.msg)]', 
            type: 'email-msg', 
            base64,
            emailMetadata: {
              format: 'Outlook MSG'
            }
          });
        };
        reader.readAsDataURL(file);
      } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        reader.onload = (e) => resolve({ text: e.target.result, type: 'text' });
        reader.readAsText(file);
      } else {
        resolve({ text: '[Binary file]', type: 'binary' });
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const guessCategory = (filename) => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.eml') || lower.endsWith('.msg')) return 'Correspondence';
    if (lower.includes('contract') || lower.includes('agreement') || lower.includes('nda')) return 'Legal';
    if (lower.includes('invoice') || lower.includes('financial') || lower.includes('budget')) return 'Financial';
    if (lower.includes('technical') || lower.includes('spec') || lower.includes('diagram')) return 'Technical';
    if (lower.includes('email') || lower.includes('letter')) return 'Correspondence';
    return 'Other';
  };

  // Filter documents by search, category, project, and counterpart
  const searchedDocuments = useMemo(() => {
    return filteredDocuments.filter(doc => {
      // Text search
      const matchesSearch = !docSearchQuery || 
        doc.name.toLowerCase().includes(docSearchQuery.toLowerCase()) ||
        (doc.notes && doc.notes.toLowerCase().includes(docSearchQuery.toLowerCase())) ||
        (doc.category && doc.category.toLowerCase().includes(docSearchQuery.toLowerCase()));
      
      // Category filter
      const matchesCategory = docCategoryFilter === 'all' || doc.category === docCategoryFilter;
      
      // Project filter
      const matchesProject = docProjectFilter === 'all' || 
        (docProjectFilter === 'unassigned' && !doc.projectId) ||
        doc.projectId === parseInt(docProjectFilter);
      
      // Counterpart filter
      const matchesCounterpart = docCounterpartFilter === 'all' || 
        (docCounterpartFilter === 'unassigned' && (!doc.counterpartIds || doc.counterpartIds.length === 0)) ||
        (doc.counterpartIds && doc.counterpartIds.includes(parseInt(docCounterpartFilter)));
      
      return matchesSearch && matchesCategory && matchesProject && matchesCounterpart;
    });
  }, [filteredDocuments, docSearchQuery, docCategoryFilter, docProjectFilter, docCounterpartFilter]);

  // Get projects that have documents
  const projectsWithDocs = useMemo(() => {
    const projectIds = new Set(filteredDocuments.filter(d => d.projectId).map(d => d.projectId));
    return projects.filter(p => projectIds.has(p.id));
  }, [filteredDocuments, projects]);

  // Get counterparts that have documents
  const counterpartsWithDocs = useMemo(() => {
    const counterpartIds = new Set();
    filteredDocuments.forEach(d => {
      if (d.counterpartIds) {
        d.counterpartIds.forEach(id => counterpartIds.add(id));
      }
    });
    return counterparts.filter(c => counterpartIds.has(c.id));
  }, [filteredDocuments, counterparts]);

  // Get document thumbnail
  const getDocThumbnail = (doc) => {
    // Image with actual content
    if (doc.thumbnail) {
      return <img src={doc.thumbnail} alt="" className="w-full h-full object-cover" />;
    }
    if (doc.contentType === 'image' && doc.base64) {
      const mimeType = doc.name.match(/\.(png|gif|webp)$/i) ? `image/${doc.name.split('.').pop().toLowerCase()}` : 'image/jpeg';
      return <img src={`data:${mimeType};base64,${doc.base64}`} alt="" className="w-full h-full object-cover" />;
    }
    
    // PDF - show nice placeholder
    if (doc.contentType === 'pdf' || doc.name?.endsWith('.pdf')) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-red-50 to-red-100 flex flex-col items-center justify-center p-4">
          <FileText size={48} className="text-red-500 mb-2" />
          <span className="text-xs font-medium text-red-600 bg-red-200 px-2 py-0.5 rounded">PDF</span>
        </div>
      );
    }
    
    // Word docs
    if (doc.name?.match(/\.(doc|docx)$/i)) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex flex-col items-center justify-center p-4">
          <FileText size={48} className="text-blue-700 mb-2" />
          <span className="text-xs font-medium text-blue-800 bg-blue-200 px-2 py-0.5 rounded">DOC</span>
        </div>
      );
    }
    
    // Excel
    if (doc.name?.match(/\.(xls|xlsx|csv)$/i)) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-green-50 to-green-100 flex flex-col items-center justify-center p-4">
          <BarChart3 size={48} className="text-green-500 mb-2" />
          <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-0.5 rounded">XLS</span>
        </div>
      );
    }
    
    // Text files
    if (doc.name?.match(/\.(txt|md)$/i)) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
          <FileText size={48} className="text-slate-500 mb-2" />
          <span className="text-xs font-medium text-slate-600 bg-slate-200 px-2 py-0.5 rounded">TXT</span>
        </div>
      );
    }
    
    // Default
    return (
      <div className="w-full h-full bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
        <span className="text-4xl mb-2">{getFileIcon(doc.name)}</span>
        <span className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-0.5 rounded uppercase">
          {doc.name?.split('.').pop() || 'FILE'}
        </span>
      </div>
    );
  };

  return (
    <div 
      className={`space-y-4 min-h-[400px] relative ${isDragging ? 'ring-2 ring-blue-700 ring-inset' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Global Search Banner */}
      {searchQuery && (
        <div className={`${theme === 'dark' ? 'bg-blue-700/10 border-blue-700/30' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4 flex justify-between items-center`}>
          <div className="flex items-center gap-2">
            <Search size={18} className="text-blue-700" />
            <span className={`${theme === 'dark' ? 'text-blue-800' : 'text-blue-800'}`}>
              Showing documents matching "{searchQuery}" — {filteredDocuments.length} results
            </span>
          </div>
          <button 
            onClick={() => setSearchQuery('')}
            className={`flex items-center gap-1 ${theme === 'dark' ? 'text-blue-800 hover:text-blue-300' : 'text-blue-800 hover:text-blue-700'}`}
          >
            <X size={14} /> Clear search
          </button>
        </div>
      )}
      
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-blue-50/90 z-50 flex items-center justify-center rounded-2xl border-2 border-dashed border-blue-700">
          <div className="text-center">
            <Upload size={48} className="mx-auto text-blue-700 mb-4" />
            <p className="text-lg font-medium text-blue-700">Drop files here to upload</p>
            <p className="text-sm text-blue-800">
              {gDriveConnected ? 'Files will be uploaded to Google Drive' : 'PDF, Images, Documents'}
            </p>
          </div>
        </div>
      )}

      {/* Upload progress overlay */}
      {uploading && (
        <div className="absolute inset-0 bg-white/95 z-50 flex items-center justify-center rounded-2xl">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-700 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900 mb-1">
              Uploading {uploadProgress.current} of {uploadProgress.total}
            </p>
            <p className="text-sm text-slate-600 truncate max-w-xs">{uploadProgress.fileName}</p>
            {gDriveConnected && (
              <p className="text-xs text-blue-800 mt-2 flex items-center justify-center gap-1">
                <Cloud size={14} /> Uploading to Google Drive
              </p>
            )}
          </div>
        </div>
      )}

      {/* Header with search, filters, and actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Documents</h2>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>{documents.length} documents</span>
            {gDriveConnected && (
              <span className="flex items-center gap-1 text-blue-800">
                <Cloud size={14} /> Google Drive
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowDocumentUploadModal(true)}
            className={`flex items-center gap-2 px-4 py-2 border ${theme === 'dark' ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200 text-slate-700 hover:bg-slate-50'} rounded-xl text-sm font-medium`}>
            <Sparkles size={16} className="text-blue-700" />
            AI Extract
          </button>
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            {gDriveConnected ? <Cloud size={18} /> : <Upload size={18} />}
            {gDriveConnected ? 'Upload to Drive' : 'Upload'}
          </button>
          <input 
            ref={fileInputRef}
            type="file" 
            multiple 
            className="hidden" 
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls,.csv"
          />
        </div>
      </div>

      {/* Search and filters bar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search documents by name, notes..."
              value={docSearchQuery}
              onChange={(e) => setDocSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-700/50"
            />
          </div>
          <div className="flex bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white shadow text-blue-800' : 'text-slate-600'}`}
              title="Grid view">
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white shadow text-blue-800' : 'text-slate-600'}`}
              title="List view">
              <List size={18} />
            </button>
          </div>
        </div>
        
        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Filter size={16} />
            <span>Filters:</span>
          </div>
          
          {/* Category filter */}
          <select 
            value={docCategoryFilter} 
            onChange={(e) => setDocCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm">
            <option value="all">All Categories</option>
            {documentCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          
          {/* Project filter */}
          <select 
            value={docProjectFilter} 
            onChange={(e) => setDocProjectFilter(e.target.value)}
            className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm">
            <option value="all">All Projects</option>
            <option value="unassigned">Unassigned</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          
          {/* Counterpart filter */}
          <div className="relative min-w-[160px]">
            <SearchableSelect
              items={[{id: 'unassigned', name: 'Unassigned'}, ...counterparts]}
              value={docCounterpartFilter}
              onChange={(val) => setDocCounterpartFilter(val)}
              placeholder="All Counterparts"
              showAll={true}
              allLabel="All Counterparts"
            />
          </div>
          
          {/* Clear filters button */}
          {(docCategoryFilter !== 'all' || docProjectFilter !== 'all' || docCounterpartFilter !== 'all' || docSearchQuery) && (
            <button 
              onClick={() => {
                setDocCategoryFilter('all');
                setDocProjectFilter('all');
                setDocCounterpartFilter('all');
                setDocSearchQuery('');
              }}
              className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl flex items-center gap-1">
              <X size={14} /> Clear
            </button>
          )}
          
          {/* Active filter count */}
          {(docCategoryFilter !== 'all' || docProjectFilter !== 'all' || docCounterpartFilter !== 'all') && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {[docCategoryFilter !== 'all', docProjectFilter !== 'all', docCounterpartFilter !== 'all'].filter(Boolean).length} active
            </span>
          )}
        </div>
      </div>

      {/* Empty state */}
      {documents.length === 0 ? (
        <div 
          className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Upload size={24} className="text-slate-600" />
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">No documents yet</h3>
          <p className="text-slate-600 mb-4">Drag & drop files here or click to browse</p>
          <p className="text-xs text-slate-500">Supports PDF, Images, Word, Excel, Text files</p>
        </div>
      ) : searchedDocuments.length === 0 ? (
        <div className="text-center py-12">
          <Search size={32} className="mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600">No documents match your search</p>
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid View with Thumbnails */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {searchedDocuments.slice((documentsPage - 1) * ITEMS_PER_PAGE, documentsPage * ITEMS_PER_PAGE).map(doc => {
            const project = projects.find(p => p.id === doc.projectId);
            return (
              <div 
                key={doc.id} 
                className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer"
                onClick={() => setViewingDocument(doc)}
                onMouseEnter={() => setQuickPreview(doc.id)}
                onMouseLeave={() => setQuickPreview(null)}
              >
                {/* Thumbnail - Square */}
                <div className="aspect-square bg-slate-50 relative overflow-hidden">
                  {getDocThumbnail(doc)}
                  {/* Hover overlay */}
                  <div className={`absolute inset-0 bg-black/50 flex items-center justify-center gap-1 transition-opacity ${quickPreview === doc.id ? 'opacity-100' : 'opacity-0'}`}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setViewingDocument(doc); }}
                      className="p-1.5 bg-white rounded-full text-slate-700 hover:bg-blue-700 hover:text-white">
                      <Eye size={14} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setExtractingFromDocument(doc); }}
                      className="p-1.5 bg-white rounded-full text-slate-700 hover:bg-blue-700 hover:text-white"
                      title="AI Extract">
                      <Sparkles size={14} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setSharingDocument(doc); }}
                      className="p-1.5 bg-white rounded-full text-slate-700 hover:bg-blue-700 hover:text-white">
                      <Share2 size={14} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setEditingDocument(doc); }}
                      className="p-1.5 bg-white rounded-full text-slate-700 hover:bg-blue-700 hover:text-white">
                      <Edit2 size={14} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setDocuments(documents.filter(d => d.id !== doc.id)); }}
                      className="p-1.5 bg-white rounded-full text-slate-700 hover:bg-red-500 hover:text-white">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {/* Share button always visible in corner */}
                  <button 
                    onClick={(e) => { e.stopPropagation(); setSharingDocument(doc); }}
                    className="absolute bottom-1 right-1 p-1 bg-blue-700 hover:bg-blue-800 rounded text-white opacity-80 hover:opacity-100 transition-opacity"
                    title="Share document">
                    <Share2 size={12} />
                  </button>
                  {/* Google Drive indicator */}
                  {doc.driveFileId && (
                    <div className="absolute top-1 right-1 p-0.5 bg-blue-700 rounded">
                      <Cloud size={10} className="text-white" />
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="p-2">
                  <p className="font-medium text-slate-900 text-xs truncate" title={doc.name}>{doc.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-slate-400">{doc.size}</span>
                    <span className="px-1 py-0.5 bg-slate-100 rounded text-[10px] text-slate-500">{doc.category}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600 uppercase">Document</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600 uppercase">{t('category')}</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600 uppercase">Project</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600 uppercase">Uploaded</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {searchedDocuments.slice((documentsPage - 1) * ITEMS_PER_PAGE, documentsPage * ITEMS_PER_PAGE).map(doc => {
                const project = projects.find(p => p.id === doc.projectId);
                const isImage = doc.contentType === 'image' || doc.thumbnail;
                return (
                  <tr key={doc.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setViewingDocument(doc)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {/* Mini thumbnail */}
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                          {isImage && (doc.thumbnail || doc.base64) ? (
                            <img 
                              src={doc.thumbnail || `data:image/jpeg;base64,${doc.base64}`} 
                              alt="" 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg">
                              {getFileIcon(doc.name)}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">{doc.name}</p>
                          <p className="text-xs text-slate-500">{doc.size}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-slate-100 rounded-full text-xs text-slate-600">{doc.category}</span>
                    </td>
                    <td className="px-4 py-3">
                      {project ? (
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedProject(project); }} 
                          className="text-sm text-blue-800 hover:text-blue-700">
                          {project.name}
                        </button>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {formatDate(doc.uploadedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={(e) => { e.stopPropagation(); setViewingDocument(doc); }}
                          className="p-1.5 text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded" title="View">
                          <Eye size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setExtractingFromDocument(doc); }}
                          className="p-1.5 text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded" title="AI Extract">
                          <Sparkles size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setSharingDocument(doc); }}
                          className="p-1.5 text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded" title="Share">
                          <Share2 size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setEditingDocument(doc); }}
                          className="p-1.5 text-slate-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Edit">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setDocuments(documents.filter(d => d.id !== doc.id)); }}
                          className="p-1.5 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {searchedDocuments.length > ITEMS_PER_PAGE && (
        <div className="flex justify-center">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setDocumentsPage(Math.max(1, documentsPage - 1))}
              disabled={documentsPage === 1}
              className="px-3 py-1 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
              Previous
            </button>
            <span className="text-sm text-slate-600">
              Page {documentsPage} of {Math.ceil(searchedDocuments.length / ITEMS_PER_PAGE)}
            </span>
            <button 
              onClick={() => setDocumentsPage(Math.min(Math.ceil(searchedDocuments.length / ITEMS_PER_PAGE), documentsPage + 1))}
              disabled={documentsPage >= Math.ceil(searchedDocuments.length / ITEMS_PER_PAGE)}
              className="px-3 py-1 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      )}

      {/* Storage info */}
      {gDriveConnected && (
        <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
          <Cloud size={14} className="text-blue-700" />
          Documents synced to Google Drive
        </div>
      )}
    </div>
  );
};

const googleDriveSync = new GoogleDriveSync();

// ShareDocumentModal component removed - documents feature disabled


export default function GISApp() {
  const [activeTab, _setActiveTab] = useState(() => {
    // Check URL path for legal pages
    const path = window.location.pathname;
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    if (path === '/pricing') return 'subscribe';
    if (path === '/preview/livefeed') return 'preview-livefeed';
    if (path === '/signals' || path.startsWith('/signals/')) return 'signals';
    if (path === '/research' || path.startsWith('/research/')) return 'research';
    if (path === '/data' || path.startsWith('/data/')) return 'data';
    // Sherpa Atlas.
    if (path === '/atlas' || path.startsWith('/atlas/')) return 'atlas';
    // Deep links keep their own landing tab. /deal/ is masked by dealPageLoading
    // and selectedDeal wins the render chain, but the tab still decides what is
    // revealed if the id is malformed or the fetch 404s, and what shows when the
    // deal is closed. /counterpart/ has NO loading flag, so the initial tab is
    // painted for real: leaving it 'research' put the price list on top of the
    // server-rendered company page Google sent the visitor to, until the fetch
    // resolved. ~230 people a month arrive that way.
    if (path.startsWith('/deal/')) return 'home';
    if (path.startsWith('/counterpart/')) return 'counterparts';

    // Otherwise the home page (redesign 2026-09-28): it presents Research, Data
    // and Platform together, so every section that sells is one click away.
    return 'homepage';
  });
  // Research storefront: which report slug is open (null = the catalogue).
  const [researchSlug, setResearchSlug] = useState(() => {
    const m = window.location.pathname.match(/^\/research\/([\w-]+)$/);
    return m && m[1] !== 'complete' ? m[1] : null;
  });
  // Post-checkout return from Stripe. Presence of the session id only decides
  // which view renders — payment state is read from the server.
  const [checkoutSession, setCheckoutSession] = useState(() =>
    window.location.pathname === '/research/complete'
      ? new URLSearchParams(window.location.search).get('session') : null);
  const [buyingReport, setBuyingReport] = useState(null);
  // Data storefront: which data file slug is open (null = the catalogue).
  const [dataSlug, setDataSlug] = useState(() => {
    const m = window.location.pathname.match(/^\/data\/([\w-]+)$/);
    return m ? m[1] : null;
  });
  // Sherpa Atlas: the current /atlas… path (the section routes on it).
  const [atlasPath, setAtlasPath] = useState(() =>
    window.location.pathname.startsWith('/atlas') ? window.location.pathname : '/atlas');
  const goAtlas = useCallback((path) => {
    if (window.location.pathname + window.location.search !== path) window.history.pushState({}, '', path);
    setAtlasPath(path);
    window.scrollTo(0, 0);
  }, []);
  // Back/forward between Atlas pages.
  useEffect(() => {
    const onPop = () => {
      const p = window.location.pathname;
      if (p === '/atlas' || p.startsWith('/atlas/')) { setAtlasPath(p); _setActiveTab('atlas'); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const setActiveTab = useCallback((tab) => {
    try { window.gtag?.('event', 'page_view', { page_title: tab }); } catch (e) { /* silent */ }
    // Selecting Research in the rail means "show me the catalogue". Without
    // this, the post-checkout confirmation and an open report both persist and
    // the nav item appears to do nothing — the confirmation page in particular
    // became a dead end with no way back out of it.
    // Home lives at the site root.
    if (tab === 'homepage' && window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
    // The Atlas rail item opens Sherpa Atlas at sherpaatlas.com.
    if (tab === 'atlas') {
      window.location.href = 'https://sherpaatlas.com';
      return;
    }
    // Same for Data: the rail item always means "show me the catalogue".
    if (tab === 'data') {
      setDataSlug(null);
      if (window.location.pathname !== '/data') window.history.pushState({}, '', '/data');
    }
    if (tab === 'research') {
      setResearchSlug(null);
      setCheckoutSession(null);
      if (window.location.pathname !== '/research') {
        window.history.pushState({}, '', '/research');
      }
    }
    _setActiveTab(tab);
  }, []);
  // Projects and Documents features disabled - counterparts-only platform
  // State variables kept for backward compatibility with existing code/data
  const [projects, setProjects] = useState([]); // Keep for data compatibility
  const [counterparts, setCounterparts] = useState([]);
  const [deals, setDeals] = useState([]); // Standalone deals with multi-counterpart linking
  const [totalCounts, setTotalCounts] = useState({ deals: 0, counterparts: 0 }); // Database totals for stats strip
  const [documents, setDocuments] = useState([]); // Keep for data compatibility
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [favorites, setFavorites] = useState({ projects: [], counterparts: [] });
  const [recentItems, setRecentItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(null); // Keep for code compatibility
  const [selectedCounterpart, setSelectedCounterpart] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null); // Full deal detail page
  const [panelCounterpart, setPanelCounterpart] = useState(null); // Slide-in panel counterpart
  const [panelDeal, setPanelDeal] = useState(null); // Slide-in panel deal (for Counterparts section)
  const [panelDealSource, setPanelDealSource] = useState(null); // Source counterpart for deal panel
  const [panelDealEnrichment, setPanelDealEnrichment] = useState(null); // Lazy-fetched enrichment for deal panel
  // Fetch enrichment when deal panel opens
  useEffect(() => {
    if (!panelDeal?.id) { setPanelDealEnrichment(null); return; }
    if (panelDeal.enrichment?.narrative) { setPanelDealEnrichment(panelDeal.enrichment); return; }
    setPanelDealEnrichment(null);
    let cancelled = false;
    authFetch(`/api/deals?id=${panelDeal.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data?.deal?.enrichment) setPanelDealEnrichment(data.deal.enrichment); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [panelDeal?.id]);
  const [expandedDealId, setExpandedDealId] = useState(null); // Track expanded deal from global search
  // showCpInsightsPanel removed — replaced by CounterpartFilterSidebar
  const [showDashInsightsPanel, setShowDashInsightsPanel] = useState(true); // Left column visibility in Dashboard
  const [showProjectModal, setShowProjectModal] = useState(false); // Keep for code compatibility
  const [showCounterpartModal, setShowCounterpartModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resettingData, setResettingData] = useState(false);
  const [showCounterpartPicker, setShowCounterpartPicker] = useState(null); // Keep for code compatibility
  const [editingProject, setEditingProject] = useState(null); // Keep for code compatibility
  const [editingCounterpart, setEditingCounterpart] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [dashboardFilter, setDashboardFilter] = useState("all"); // Keep for code compatibility
  const [projectFilters, setProjectFilters] = useState({ technology: "all", geography: "all", counterpart: "all", status: "all" }); // Keep for code compatibility
  const [projectSort, setProjectSort] = useState({ field: "lastActivity", direction: "desc" }); // Keep for code compatibility
  const [counterpartFilters, setCounterpartFilters] = useState({
    type: "all",
    category: "all",
    role: "all",
    // Advanced screener filters (taxonomy-based)
    counterpartTypes: [],  // Array of type IDs from counterpart_types
    technologies: [],      // Array of technology IDs
    regions: [],           // Array of geography IDs
    stages: [],            // Array of stage IDs
    irrMin: '',
    irrMax: ''
  });
  const [counterpartSort, setCounterpartSort] = useState({ field: "name", direction: "asc" });
  // cpFlowSearch removed — replaced by sidebar filters + smart search
  // Typeahead filter state for advanced filters
  const [filterTypeahead, setFilterTypeahead] = useState({ tech: '', region: '', stage: '' });
  const [filterTypeaheadOpen, setFilterTypeaheadOpen] = useState({ tech: false, region: false, stage: false });
  const [dealFilterCounterpart, setDealFilterCounterpart] = useState('all'); // For filtering deals feed
  const [screenerFilters, setScreenerFilters] = useState({
    sectors: [],
    geographies: [],
    dealTypes: [],
    statuses: [],
    counterparts: [],
    timePeriod: 'all'
  }); // Screener lens filters (arrays for multi-select)

  // Taxonomy-based screener filter options (loaded from database)
  const [taxonomyFilters, setTaxonomyFilters] = useState({
    types: [],
    technologies: [],
    geographies: [],
    stages: [],
    loaded: false
  });

  // Server-side screener results
  const [screenerResults, setScreenerResults] = useState({
    counterparts: [],
    total: 0,
    loading: false,
    error: null,
    hasActiveFilters: false
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false); // Keep for code compatibility
  const [viewingDocument, setViewingDocument] = useState(null); // Keep for code compatibility
  const [documentReturnTo, setDocumentReturnTo] = useState(null); // Keep for code compatibility
  const [extractingFromDocument, setExtractingFromDocument] = useState(null); // Keep for code compatibility
  const [aiSummary, setAiSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [smartSearchResponse, setSmartSearchResponse] = useState(null);
  const [smartSearchLoading, setSmartSearchLoading] = useState(false);

  const [smartSearchExplanation, setSmartSearchExplanation] = useState(null);
  const [searchPanelCp, setSearchPanelCp] = useState(null); // Sidebar counterpart from search results
  // Fetch full counterpart when a lean search result is clicked
  useEffect(() => {
    if (!searchPanelCp?.id) return;
    if (searchPanelCp.enrichment?.summary || searchPanelCp.investmentPreferences?.summary) return; // already full
    let cancelled = false;
    authFetch(`/api/counterparts?id=${searchPanelCp.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        const full = data?.counterpart || data?.counterparts?.[0];
        if (full) {
          setSearchPanelCp(prev => prev?.id === full.id ? { ...prev, ...full, websiteUrl: full.website } : prev);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [searchPanelCp?.id]);
  const [searchPanelDeal, setSearchPanelDeal] = useState(null); // Sidebar deal from search results
  const [searchPanelDealEnrichment, setSearchPanelDealEnrichment] = useState(null);
  useEffect(() => {
    if (!searchPanelDeal?.id) { setSearchPanelDealEnrichment(null); return; }
    // If the deal already has full data (deal_counterparts), skip the fetch
    if (searchPanelDeal.deal_counterparts?.length > 0 && searchPanelDeal.enrichment) {
      setSearchPanelDealEnrichment(searchPanelDeal.enrichment);
      return;
    }
    // Fetch the full deal (with counterparts, enrichment, etc.) from the API
    setSearchPanelDealEnrichment(null);
    let cancelled = false;
    authFetch(`/api/deals?id=${searchPanelDeal.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data?.deal) {
          // Replace the lean search result with the full deal
          setSearchPanelDeal(prev => prev?.id === data.deal.id ? { ...prev, ...data.deal } : prev);
          if (data.deal.enrichment) setSearchPanelDealEnrichment(data.deal.enrichment);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [searchPanelDeal?.id]);
  const [smartSearchCpIds, setSmartSearchCpIds] = useState(null);   // counterpart IDs from smart search
  const keepSearchOnTabSwitch = React.useRef(false);               // skip clearing search state on next tab switch
  const [smartSearchQuery, setSmartSearchQuery] = useState('');      // active search query text
  const [smartSearchDeals, setSmartSearchDeals] = useState([]);      // deals from smart search
  const [cpSidebarOpen, setCpSidebarOpen] = useState(false);         // mobile sidebar toggle
  const [smartSearchDealIds, setSmartSearchDealIds] = useState(null);    // deal IDs from smart search
  const [smartSearchDealQuery, setSmartSearchDealQuery] = useState('');   // active deal search query
  const [smartSearchDealCps, setSmartSearchDealCps] = useState([]);      // counterparts from deal search
  const [searchesRemaining, setSearchesRemaining] = useState(null); // daily search limit counter (non-Pro users)
  const [pendingDashboardFilter, setPendingDashboardFilter] = useState(null); // { type, value, label, id } — injected into LatestFeed feedFilters
  const [navCollapsed, setNavCollapsed] = useState(false); // Slack shell: contextual sidebar collapsed?
  const [dealSidebarOpen, setDealSidebarOpen] = useState(false);         // mobile deal sidebar toggle
  // searchClearSignal removed — setHeroSearch('') called directly in logo click handler
  const [heroSearch, setHeroSearch] = useState('');                        // lifted from GlobalSearchBar to survive remounts
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sherpa_recent_searches') || '[]'); }
    catch { return []; }
  });
  const [heroSuggestions, setHeroSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [welcomeToast, setWelcomeToast] = useState(null); // { message, type } — auto-dismisses

  // Debounced suggestions for search bar (lifted to App level — must be before any early returns)
  useEffect(() => {
    if (heroSearch.length < 2) {
      setHeroSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      const searchLower = heroSearch.toLowerCase();
      const cpMatches = counterparts
        .filter(cp => cp.name?.toLowerCase().includes(searchLower))
        .map(cp => ({
          id: cp.id,
          name: cp.name,
          type: cp.type,
          category: cp.category,
          kind: 'counterpart',
          dealCount: deals.filter(d =>
            (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cp.id)
          ).length,
          createdAt: cp.created_at
        }))
        .sort((a, b) => b.dealCount - a.dealCount || new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 4);
      const dealMatches = deals
        .filter(d => d.headline?.toLowerCase().includes(searchLower) || d.project_name?.toLowerCase().includes(searchLower) || (Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).join(' ').toLowerCase().includes(searchLower))
        .sort((a, b) => new Date(b.announcement_date || b.created_at || 0) - new Date(a.announcement_date || a.created_at || 0))
        .slice(0, 4)
        .map(d => ({
          id: d.id,
          name: d.headline || d.project_name || 'Untitled Deal',
          type: d.deal_type,
          category: d.technology,
          country: (Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).join(', '),
          kind: 'deal',
        }));
      setHeroSuggestions([...cpMatches, ...dealMatches].slice(0, 6));
    }, 150);
    return () => clearTimeout(timer);
  }, [heroSearch, counterparts, deals]);

  const [compareSelection, setCompareSelection] = useState([]);  // counterpart IDs for comparison (max 3)
  const [showCompareModal, setShowCompareModal] = useState(false);

  // Rotating welcome messages for landing page
  const welcomeMessages = useMemo(() => [
    "Your guide through the infrastructure landscape.",
    "Navigating infrastructure deals, so you don't have to.",
    "The infrastructure market, mapped and tracked for you.",
    "Your sherpa through the world of infrastructure deals.",
    "Follow the deals. Know the players. Stay ahead.",
    "Infrastructure intelligence, one deal at a time.",
    "Charting the path through global infrastructure.",
    "From renewables to data centers — we track it all.",
    "A live map of who is doing what, with whom, where, and how often across global infrastructure."
  ], []);

  const welcomeMessage = useMemo(() => {
    return welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const homeGreeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);


  // Clear search results when switching tabs (unless navigating from "View all")
  useEffect(() => {
    if (keepSearchOnTabSwitch.current) {
      keepSearchOnTabSwitch.current = false;
      return;
    }
    setSmartSearchResponse(null);
    setSmartSearchLoading(false);
    setSearchPanelCp(null);
    setSearchPanelDeal(null);
    setSmartSearchCpIds(null);
    setSmartSearchQuery('');
    setSmartSearchDeals([]);
    setSmartSearchDealIds(null);
    setSmartSearchDealQuery('');
    setSmartSearchDealCps([]);
    setCompareSelection([]);
  }, [activeTab]);
  const [editingDocument, setEditingDocument] = useState(null); // Keep for code compatibility
  const [sharingDocument, setSharingDocument] = useState(null); // Keep for code compatibility
  const [sharingProject, setSharingProject] = useState(null); // Keep for code compatibility
  const [sharingCounterpart, setSharingCounterpart] = useState(null);
  const [sharingDeal, setSharingDeal] = useState(null);
  const [sharedDocuments, setSharedDocuments] = useState([]); // Keep for code compatibility
  const [isLoading, setIsLoading] = useState(true);
  const [centralDataLoaded, setCentralDataLoaded] = useState(false);
  const [orgsLoaded, setOrgsLoaded] = useState(false); // public top-N Organizations fetch resolved (success or retries exhausted)
  const [dashboardStats, setDashboardStats] = useState(null);
  const [storageAvailable, setStorageAvailable] = useState(false);
  const [projectViewMode, setProjectViewMode] = useState("list"); // Keep for code compatibility
  const [tasksViewMode, setTasksViewMode] = useState("list"); // "list" or "kanban"
  const [adminTab, setAdminTab] = useState("overview"); // Admin panel tab: "overview", "deals", "counterparts", "settings"
  const [dealRoles, setDealRoles] = useState([]);
  const [dealRolesLoaded, setDealRolesLoaded] = useState(false);
  // Taxonomy state (loaded from DB)
  const [taxonomies, setTaxonomies] = useState({
    'counterpart-types': [],
    'sectors': [],
    'technologies': [],
    'geographies': [],
    'investment-stages': [],
    'deal-types': [],
    'deal-statuses': []
  });
  const [taxonomiesLoaded, setTaxonomiesLoaded] = useState(false);
  const [activeTaxonomy, setActiveTaxonomy] = useState('counterpart-types');
  const [adminDealSearch, setAdminDealSearch] = useState(''); // Search filter for admin deals
  const [adminCounterpartSearch, setAdminCounterpartSearch] = useState(''); // Search filter for admin counterparts
  const [adminDraftResults, setAdminDraftResults] = useState([]); // Draft counterparts found via API search
  const [dealSubmissions, setDealSubmissions] = useState([]); // Crawled deals awaiting review
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState(new Set()); // Selected for bulk actions
  const [submissionAgeDays, setSubmissionAgeDays] = useState(30); // Filter submissions by age in days
  const [submissionSearch, setSubmissionSearch] = useState(''); // Search submissions
  // AI Deal Discovery state
  const [discoverQuery, setDiscoverQuery] = useState('');
  const [discoverResults, setDiscoverResults] = useState([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverError, setDiscoverError] = useState(null);
  const [selectedDiscoverDeals, setSelectedDiscoverDeals] = useState(new Set());
  const [importingDeal, setImportingDeal] = useState(null); // Deal being imported
  // Newsletter state (verticals: ppa, general, datacenter)
  const [newsletterVertical, setNewsletterVertical] = useState('general');
  // PPA Digest state
  const [digestMarkdown, setDigestMarkdown] = useState('');
  const [digestDays, setDigestDays] = useState(1);
  const [digestCopied, setDigestCopied] = useState(false);
  // Data Center Digest state
  const [dcDigestMarkdown, setDcDigestMarkdown] = useState('');
  const [dcDigestDays, setDcDigestDays] = useState(7);
  const [dcDigestCopied, setDcDigestCopied] = useState(false);
  const [dcDigestGenerating, setDcDigestGenerating] = useState(false);
  const [defenseDigestMarkdown, setDefenseDigestMarkdown] = useState('');
  const [defenseDigestDays, setDefenseDigestDays] = useState(7);
  const [defenseDigestCopied, setDefenseDigestCopied] = useState(false);
  const [defenseDigestGenerating, setDefenseDigestGenerating] = useState(false);
  // Global Infrastructure Update (General) state
  const [updateMarkdown, setUpdateMarkdown] = useState('');
  const [updateDays, setUpdateDays] = useState(7);
  const [updateCopied, setUpdateCopied] = useState(false);
  const [updateGenerating, setUpdateGenerating] = useState(false);
  const [crawlSources, setCrawlSources] = useState([]); // RSS feed sources for crawler
  const [editingSource, setEditingSource] = useState(null); // Source being edited
  const [crawlKeywords, setCrawlKeywords] = useState([]); // Keywords for filtering
  const [editingKeywords, setEditingKeywords] = useState(false); // Editing keywords mode
  const [keywordsText, setKeywordsText] = useState(''); // Keywords as text for editing
  const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false);
  // Bulk re-enrichment state
  // Comparison tool state
  const [compareList, setCompareList] = useState([]); // Array of counterpart IDs (max 3)
  const [showCompareView, setShowCompareView] = useState(false);
  const [dealCompareList, setDealCompareList] = useState([]); // Array of deal IDs (max 3)
  const [showDealCompareView, setShowDealCompareView] = useState(false);
  const [bulkEnrichStats, setBulkEnrichStats] = useState(null);
  const [bulkEnrichRunning, setBulkEnrichRunning] = useState(false);
  const [bulkEnrichProgress, setBulkEnrichProgress] = useState({ processed: 0, total: 0, errors: [] });
  const [backfillAllDealsStats, setBackfillAllDealsStats] = useState(null);
  const [backfillAllDealsRunning, setBackfillAllDealsRunning] = useState(false);
  const [backfillAllDealsProgress, setBackfillAllDealsProgress] = useState({ updated: 0, skipped: 0, total: 0 });
  const [backfillDescStats, setBackfillDescStats] = useState(null);
  const [backfillDescRunning, setBackfillDescRunning] = useState(false);
  const [backfillDescProgress, setBackfillDescProgress] = useState({ updated: 0, skipped: 0, errors: 0, total: 0, phase: '' });
  const [backfillWebsitesStats, setBackfillWebsitesStats] = useState(null);
  const [backfillWebsitesRunning, setBackfillWebsitesRunning] = useState(false);
  const [backfillWebsitesProgress, setBackfillWebsitesProgress] = useState({ updated: 0, skipped: 0, total: 0 });
  const [websiteReviewList, setWebsiteReviewList] = useState([]);
  const [autoDiscoverStats, setAutoDiscoverStats] = useState(null);
  const [autoDiscoverRunning, setAutoDiscoverRunning] = useState(false);
  const [autoDiscoverProgress, setAutoDiscoverProgress] = useState({ feedsDiscovered: 0, noFeedFound: 0, total: 0 });
  const [mergeDuplicates, setMergeDuplicates] = useState(null);
  const [mergeRunning, setMergeRunning] = useState(false);
  const [mergeFinding, setMergeFinding] = useState(false);
  const [mergeProgress, setMergeProgress] = useState({ merged: 0, total: 0 });
  const [mergeSelected, setMergeSelected] = useState(new Set()); // Set of group indices
  const [counterpartSources, setCounterpartSources] = useState([]);
  const [editingCpSource, setEditingCpSource] = useState(null);
  const [cpSourceStats, setCpSourceStats] = useState(null);
  const [cpSourceRunning, setCpSourceRunning] = useState(false);
  const [cpSourceProgress, setCpSourceProgress] = useState({ processed: 0, dealsCreated: 0, errors: 0, total: 0 });
  const [cpSourceLog, setCpSourceLog] = useState([]);
  // Journal Discovery state
  const [journalSources, setJournalSources] = useState([]);
  const [editingJournal, setEditingJournal] = useState(null);
  const [journalDiscoveryStats, setJournalDiscoveryStats] = useState(null);
  const [journalDiscoveryRunning, setJournalDiscoveryRunning] = useState(false);
  const [journalDiscoveryProgress, setJournalDiscoveryProgress] = useState({ processed: 0, dealsCreated: 0, newCounterparts: 0, errors: 0, total: 0 });
  const [journalDiscoveryLog, setJournalDiscoveryLog] = useState([]);
  // Deep Historical Backfill state
  const [deepBackfillStats, setDeepBackfillStats] = useState(null);
  const [deepBackfillRunning, setDeepBackfillRunning] = useState(false);
  const [deepBackfillCpSearch, setDeepBackfillCpSearch] = useState('');
  const [deepBackfillCpId, setDeepBackfillCpId] = useState(null);
  const [deepBackfillCpName, setDeepBackfillCpName] = useState('');
  const [deepBackfillResult, setDeepBackfillResult] = useState(null);
  const [urlVerifyStats, setUrlVerifyStats] = useState(null);
  const [urlVerifyRunning, setUrlVerifyRunning] = useState(false);
  const [urlVerifyProgress, setUrlVerifyProgress] = useState({ processed: 0, valid: 0, invalid: 0 });
  // Tearsheet Downloads tracking
  const [tearsheetStats, setTearsheetStats] = useState(null);
  // Email Subscribers tracking
  const [emailSubStats, setEmailSubStats] = useState(null);
  const [enrichMissingRunning, setEnrichMissingRunning] = useState(false);
  const [enrichMissingProgress, setEnrichMissingProgress] = useState({ processed: 0, total: 0, errors: 0 });
  // Quality Monitor state
  const [qualityReport, setQualityReport] = useState(null);
  const [qualityRunning, setQualityRunning] = useState(false);
  const [qualityLastChecked, setQualityLastChecked] = useState(null);

  // S4: Pipeline Rejections dashboard state
  const [rejectionStats, setRejectionStats] = useState(null);
  const [rejectionLoading, setRejectionLoading] = useState(false);
  const [rejectionWindow, setRejectionWindow] = useState(24); // hours
  const [rejectionExpandedReason, setRejectionExpandedReason] = useState(null);
  const [rejectionSamples, setRejectionSamples] = useState({}); // { reason: [samples] }
  const [rejectionSamplesLoading, setRejectionSamplesLoading] = useState(null);
  const [rejectionPipelineFilter, setRejectionPipelineFilter] = useState(null); // null = all

  // Admin-only deal export. The endpoint is admin-gated server-side; this is
  // just the trigger. Fetched via authFetch (Clerk JWT) rather than a plain
  // <a href> because a bare link carries no Authorization header and would
  // simply 401 — which is exactly the behaviour we want from the gate.
  const [exportStatus, setExportStatus] = useState('');
  const [exportRunning, setExportRunning] = useState(false);
  const [exportMessage, setExportMessage] = useState('');

  const handleExportDeals = async () => {
    setExportRunning(true);
    setExportMessage('');
    try {
      const qs = exportStatus ? `?status=${encodeURIComponent(exportStatus)}` : '';
      const res = await authFetch(`/api/export-deals${qs}`);
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`${res.status} ${detail.slice(0, 120)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gis-deals-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke on a delay: Safari aborts the download if the object URL is
      // revoked in the same tick as the click.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      const kb = Math.round(blob.size / 1024);
      setExportMessage(`Downloaded — ${kb.toLocaleString()} KB`);
    } catch (err) {
      setExportMessage(`Failed: ${err.message}`);
    } finally {
      setExportRunning(false);
    }
  };

  // Netherlands Visitor Watch — server-side NL request log (the Amsterdam visitor)
  const [visitorLogs, setVisitorLogs] = useState(null); // { rows, total, distinctIps, bots, humans, selfIp }
  const [visitorLoading, setVisitorLoading] = useState(false);
  const [hideSelf, setHideSelf] = useState(() => { try { return localStorage.getItem('gis_vw_hide_self') === '1'; } catch { return false; } });
  const [vwSort, setVwSort] = useState({ key: 'visits', dir: 'desc' }); // Visitor Log "by IP" table sort
  const loadVisitorLogs = useCallback(async () => {
    setVisitorLoading(true);
    try {
      const r = await authFetch('/api/visitor-watch?limit=200');
      if (r.ok) setVisitorLogs(await r.json());
      else {
        console.error('[VisitorWatch] fetch failed:', r.status);
        setVisitorLogs({ rows: [], byIp: [], total: 0, distinctIps: 0, bots: 0, humans: 0, error: `Failed to load (HTTP ${r.status})` });
      }
    } catch (e) {
      console.error('[VisitorWatch] error:', e.message);
      setVisitorLogs({ rows: [], byIp: [], total: 0, distinctIps: 0, bots: 0, humans: 0, error: e.message });
    } finally {
      setVisitorLoading(false);
    }
  }, []);

  const loadRejectionStats = useCallback(async () => {
    setRejectionLoading(true);
    try {
      const params = new URLSearchParams({ hours: String(rejectionWindow) });
      if (rejectionPipelineFilter) params.set('pipeline', rejectionPipelineFilter);
      const r = await authFetch(`/api/pipeline-rejections?${params.toString()}`);
      if (r.ok) {
        setRejectionStats(await r.json());
      } else {
        console.error('[Rejections] stats fetch failed:', r.status);
      }
    } catch (e) {
      console.error('[Rejections] stats error:', e.message);
    } finally {
      setRejectionLoading(false);
    }
  }, [rejectionWindow, rejectionPipelineFilter]);

  const loadRejectionSamples = useCallback(async (reason) => {
    setRejectionSamplesLoading(reason);
    try {
      const params = new URLSearchParams({ hours: String(rejectionWindow), reason, limit: '30' });
      if (rejectionPipelineFilter) params.set('pipeline', rejectionPipelineFilter);
      const r = await authFetch(`/api/pipeline-rejections?${params.toString()}`);
      if (r.ok) {
        const d = await r.json();
        setRejectionSamples(prev => ({ ...prev, [reason]: d.samples || [] }));
      }
    } catch (e) {
      console.error('[Rejections] samples error:', e.message);
    } finally {
      setRejectionSamplesLoading(null);
    }
  }, [rejectionWindow, rejectionPipelineFilter]);

  // Quality Monitor auto-refresh — fetch via GET (no email) on admin tab mount + every 5 min
  useEffect(() => {
    if (activeTab !== 'admin') return;
    let cancelled = false;
    const fetchQuality = async () => {
      try {
        const res = await authFetch('/api/quality-check');
        if (res.ok && !cancelled) {
          const data = await res.json();
          setQualityReport(data);
          setQualityLastChecked(data.checkedAt);
        }
      } catch (e) {
        console.error('Quality auto-check failed:', e);
      }
    };
    fetchQuality();
    const interval = setInterval(fetchQuality, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeTab]);

  // S4: Pipeline Rejections auto-load on admin tab mount
  useEffect(() => {
    if (activeTab !== 'admin') return;
    loadRejectionStats();
  }, [activeTab, loadRejectionStats]);

  // Visitor Log auto-load when the Visitors admin tab is opened
  useEffect(() => {
    if (activeTab === 'admin' && adminTab === 'visitors') loadVisitorLogs();
  }, [activeTab, adminTab, loadVisitorLogs]);

  // First-party visit beacon — fires on every page view (incl. the anonymous
  // landing page) so the server-side visitor log captures all traffic with geo,
  // IP, page, external referrer and user-agent. Uncached endpoint, fire-and-forget.
  useEffect(() => {
    try {
      const path = (window.location.pathname || '/') + (window.location.search || '');
      const ref = document.referrer || '';
      const url = `/api/pv?path=${encodeURIComponent(path)}&ref=${encodeURIComponent(ref)}`;
      if (navigator.sendBeacon) navigator.sendBeacon(url);
      else fetch(url, { keepalive: true, cache: 'no-store' }).catch(() => {});
    } catch { /* never let analytics affect the app */ }
  }, [activeTab]);

  // Market Map view state
  const [counterpartViewMode, setCounterpartViewMode] = useState('list'); // 'list' | 'treemap' | 'matrix'

  // Load deal roles from DB — deferred until deal modal opens
  useEffect(() => {
    if (!(showDealModal || editingDeal) || dealRolesLoaded) return;
    fetch('/api/deal-roles')
      .then(r => r.json())
      .then(result => {
        if (result.success && result.roles?.length > 0) {
          setDealRoles(result.roles);
        }
        setDealRolesLoaded(true);
      })
      .catch(err => {
        console.error('Error loading deal roles:', err);
        setDealRolesLoaded(true);
      });
  }, [showDealModal, editingDeal, dealRolesLoaded]);

  // Load all taxonomies when admin settings tab opens
  useEffect(() => {
    if (adminTab === 'settings' && !taxonomiesLoaded) {
      const taxonomyTypes = ['counterpart-types', 'sectors', 'technologies', 'geographies', 'investment-stages', 'deal-types', 'deal-statuses'];
      Promise.all(
        taxonomyTypes.map(type =>
          fetch(`/api/taxonomy?type=${type}`)
            .then(r => r.json())
            .then(result => ({ type, data: result.data || [] }))
            .catch(() => ({ type, data: [] }))
        )
      ).then(results => {
        const newTaxonomies = {};
        results.forEach(({ type, data }) => {
          newTaxonomies[type] = data;
        });
        setTaxonomies(newTaxonomies);
        setTaxonomiesLoaded(true);
      });
    }
  }, [adminTab, taxonomiesLoaded]);

  // Load taxonomy-based screener filter options — deferred until counterparts tab
  useEffect(() => {
    if (activeTab !== 'counterparts' || taxonomyFilters.loaded) return;
    fetch('/api/counterpart-screener?filters=true')
      .then(r => r.json())
      .then(result => {
        if (result.success && result.filters) {
          setTaxonomyFilters({
            types: result.filters.types || [],
            technologies: result.filters.technologies || [],
            geographies: result.filters.geographies || [],
            stages: result.filters.stages || [],
            loaded: true
          });
        }
      })
      .catch(err => console.error('Error loading taxonomy filters:', err));
  }, [activeTab, taxonomyFilters.loaded]);

  // Sync counterpartCategories dropdown with taxonomy database types
  useEffect(() => {
    if (taxonomyFilters.loaded && taxonomyFilters.types?.length > 0) {
      const dbLabels = taxonomyFilters.types.map(t => t.label);
      if (dbLabels.length > 0) {
        setCounterpartCategories(dbLabels);
      }
    }
  }, [taxonomyFilters.loaded, taxonomyFilters.types]);

  // Server-side filtering - call screener API when taxonomy filters change
  useEffect(() => {
    const hasActiveFilters =
      (counterpartFilters.counterpartTypes?.length > 0) ||
      (counterpartFilters.technologies?.length > 0) ||
      (counterpartFilters.regions?.length > 0) ||
      (counterpartFilters.stages?.length > 0);

    if (!hasActiveFilters) {
      // No taxonomy filters active - use client-side filtering
      setScreenerResults({ counterparts: [], total: 0, loading: false, error: null, hasActiveFilters: false });
      return;
    }

    // Debounce API calls
    const timeoutId = setTimeout(() => {
      setScreenerResults(prev => ({ ...prev, loading: true, hasActiveFilters: true }));

      fetch('/api/counterpart-screener', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          types: counterpartFilters.counterpartTypes || [],
          technologies: counterpartFilters.technologies || [],
          geographies: counterpartFilters.regions || [],
          stages: counterpartFilters.stages || [],
          search: searchQuery || '',
          limit: 500, // Get all matching results
          offset: 0,
          sortBy: counterpartSort.field === 'name' ? 'name' : 'created_at',
          sortOrder: counterpartSort.direction
        })
      })
        .then(r => r.json())
        .then(result => {
          if (result.success) {
            setScreenerResults({
              counterparts: result.counterparts || [],
              total: result.total || 0,
              loading: false,
              error: null,
              hasActiveFilters: true
            });
          } else {
            setScreenerResults(prev => ({ ...prev, loading: false, error: result.error }));
          }
        })
        .catch(err => {
          console.error('Screener API error:', err);
          setScreenerResults(prev => ({ ...prev, loading: false, error: err.message }));
        });
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [counterpartFilters.counterpartTypes, counterpartFilters.technologies, counterpartFilters.regions, counterpartFilters.stages, searchQuery, counterpartSort]);

  // Load crawl sources, keywords, and submissions when admin submissions tab opens
  useEffect(() => {
    if (adminTab === 'submissions') {
      // Load crawl sources
      fetch('/api/crawl-sources')
        .then(r => r.json())
        .then(result => {
          if (result.data) setCrawlSources(result.data);
        })
        .catch(err => console.error('Error loading crawl sources:', err));

      // Load keywords
      fetch('/api/crawl-settings')
        .then(r => r.json())
        .then(result => {
          if (result.keywords) {
            setCrawlKeywords(result.keywords);
            setKeywordsText(result.keywords.join(', '));
          }
        })
        .catch(err => console.error('Error loading keywords:', err));

      // Load submissions
      fetch('/api/deal-submissions')
        .then(r => r.json())
        .then(result => {
          if (result.data) setDealSubmissions(result.data);
        })
        .catch(err => console.error('Error loading submissions:', err));
    }
  }, [adminTab]);

  // Check if we're on a share page (e.g., /#/share/... or /?s=...)
  const [sharePageData, setSharePageData] = useState(null);
  // Initialize shareLoading to true if we detect a share URL on initial load
  const [shareLoading, setShareLoading] = useState(() => {
    const hash = window.location.hash;
    const urlParams = new URLSearchParams(window.location.search);
    return hash.startsWith('#/share/') || urlParams.get('s') !== null;
  });
  
  useEffect(() => {
    const checkShareRoute = async () => {
      const hash = window.location.hash;
      const urlParams = new URLSearchParams(window.location.search);
      const queryShareId = urlParams.get('s');
      
      // Check query param first (from OG redirect), then hash (legacy)
      let shareId = null;
      if (queryShareId) {
        shareId = queryShareId;
      } else if (hash.startsWith('#/share/')) {
        shareId = hash.replace('#/share/', '');
      }
      
      if (shareId) {
        // Short ID (10 chars) = fetch from API
        // Long string = legacy base64 encoded (try decode)
        if (shareId.length <= 12) {
          setShareLoading(true);
          try {
            const response = await fetch(`/api/share?id=${shareId}`);
            if (response.ok) {
              const data = await response.json();
              setSharePageData(data);
            } else {
              setSharePageData({ error: 'Share link expired or not found' });
            }
          } catch (e) {
            console.error('Failed to load share:', e);
            setSharePageData({ error: 'Failed to load shared document' });
          }
          setShareLoading(false);
        } else {
          // Legacy: try base64 decode
          try {
            const decoded = JSON.parse(decodeURIComponent(escape(atob(shareId))));
            setSharePageData(decoded);
          } catch (e) {
            console.error('Invalid share link:', e);
            setSharePageData({ error: 'Invalid share link' });
          }
          setShareLoading(false);
        }
      } else {
        setSharePageData(null);
        setShareLoading(false);
      }
    };
    
    checkShareRoute();
    window.addEventListener('hashchange', checkShareRoute);
    return () => window.removeEventListener('hashchange', checkShareRoute);
  }, []);

  // TEMPORARY: Demo mode for design evaluation (?demo=deals or ?demo=counterparts)
  const [demoMode, setDemoMode] = useState(() => {
    const d = new URLSearchParams(window.location.search).get('demo');
    return d === 'deals' || d === 'counterparts' ? d : null;
  });

  // Search demo deep-link: ?demo=search opens the new search UI (highlighting + entity rail).
  useEffect(() => {
    const d = new URLSearchParams(window.location.search).get('demo');
    if (d === 'search') setActiveTab('search-demo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Matchup deep-link: ?matchup=uuid1,uuid2[,uuid3]
  const [matchupPreselected, setMatchupPreselected] = useState(() => {
    const m = new URLSearchParams(window.location.search).get('matchup');
    return m ? m.split(',').filter(Boolean) : [];
  });

  // Check if we're on a deal page (e.g., /deal/{slug} or ?deal={id})
  const [dealPageLoading, setDealPageLoading] = useState(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    return path.startsWith('/deal/') || urlParams.get('deal') !== null;
  });

  useEffect(() => {
    const checkDealRoute = async () => {
      const path = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const queryDealId = urlParams.get('deal');

      let dealId = null;

      // Check for /deal/{slug} URL
      if (path.startsWith('/deal/')) {
        const slug = path.replace('/deal/', '');
        // Extract UUID from end of slug
        const uuidMatch = slug.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i);
        if (uuidMatch) {
          dealId = uuidMatch[1];
        } else if (slug.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i)) {
          dealId = slug;
        }
      }

      // Check for ?deal={id} query param (from server page CTA)
      if (!dealId && queryDealId) {
        dealId = queryDealId;
      }

      if (dealId) {
        setDealPageLoading(true);
        try {
          const response = await authFetch(`/api/deals?id=${dealId}`);
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.deal) {
              if (isUserLoaded && !isSignedIn) trackGuestView();
              setSelectedDeal(result.deal);
              // URL kept in place — selectedDeal sync effect will canonicalize the slug.
            }
          }
        } catch (e) {
          console.error('Failed to load deal:', e);
        }
        setDealPageLoading(false);
      } else {
        setDealPageLoading(false);
      }
    };

    checkDealRoute();
  }, []);

  // Check if we're on a counterpart deep-link (e.g., /counterpart/slug-<uuid>,
  // ?cp={id} from leaderboard, or ?counterpart={id} from share links).
  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const queryCpId = urlParams.get('cp') || urlParams.get('counterpart');

    let cpId = null;
    if (path.startsWith('/counterpart/')) {
      const slug = path.replace('/counterpart/', '');
      const uuidMatch = slug.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i);
      if (uuidMatch) cpId = uuidMatch[1];
      else if (slug.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i)) cpId = slug;
    }
    if (!cpId && queryCpId) cpId = queryCpId;
    if (!cpId) return;

    const openCounterpart = async () => {
      try {
        const response = await authFetch(`/api/counterparts?id=${cpId}`);
        if (response.ok) {
          const result = await response.json();
          const cp = result.counterpart || result.data;
          if (cp) {
            if (isUserLoaded && !isSignedIn) trackGuestView();
            setSelectedCounterpart(cp);
            setActiveTab('counterparts');
            // URL kept in place — selectedCounterpart sync effect canonicalizes it.
          }
        }
      } catch (e) {
        console.error('Failed to load counterpart:', e);
      }
    };

    openCounterpart();
  }, []);

  // ─── URL sync ────────────────────────────────────────────────
  // Keep the address bar in sync with selectedCounterpart / selectedDeal.
  // Click ADNOC → URL becomes /counterpart/adnoc-<uuid>; close panel → URL
  // returns to /. Pasting /counterpart/<slug> works because the deep-link
  // useEffect above sets selectedCounterpart, and this effect canonicalizes
  // the slug. Browser back/forward handled by popstate listener below.
  const slugify = useCallback((name) => {
    if (!name) return '';
    return String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 60);
  }, []);

  useEffect(() => {
    if (selectedCounterpart) {
      const slug = slugify(selectedCounterpart.name);
      const target = slug
        ? `/counterpart/${slug}-${selectedCounterpart.id}`
        : `/counterpart/${selectedCounterpart.id}`;
      if (window.location.pathname !== target) {
        window.history.pushState({ kind: 'counterpart', id: selectedCounterpart.id }, '', target);
      }
    } else if (window.location.pathname.startsWith('/counterpart/')) {
      window.history.pushState({}, '', '/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCounterpart?.id]);

  useEffect(() => {
    if (selectedDeal) {
      const slug = slugify(selectedDeal.headline || selectedDeal.project_name);
      const target = slug
        ? `/deal/${slug}-${selectedDeal.id}`
        : `/deal/${selectedDeal.id}`;
      if (window.location.pathname !== target) {
        window.history.pushState({ kind: 'deal', id: selectedDeal.id }, '', target);
      }
    } else if (window.location.pathname.startsWith('/deal/')) {
      window.history.pushState({}, '', '/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeal?.id]);

  // Browser back/forward — close the panel when the user navigates away from
  // a /counterpart or /deal path. The deep-link useEffects above handle
  // re-opening when navigating BACK into one.
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      if (!path.startsWith('/counterpart/') && selectedCounterpart) {
        setSelectedCounterpart(null);
      }
      if (!path.startsWith('/deal/') && selectedDeal) {
        setSelectedDeal(null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCounterpart, selectedDeal]);

  // Pagination state
  const [projectsPage, setProjectsPage] = useState(1);
  const [counterpartsPage, setCounterpartsPage] = useState(1);
  const [expandedCounterpartIds, setExpandedCounterpartIds] = useState(new Set());
  const [documentsPage, setDocumentsPage] = useState(1);
  const [tasksPage, setTasksPage] = useState(1);
  const [tasksPerPage, setTasksPerPage] = useState(25);
  const ITEMS_PER_PAGE = 20;
  
  // Task filtering
  const [taskStatusFilter, setTaskStatusFilter] = useState('all'); // all, overdue, dueToday, dueThisWeek, pending, completed
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('all'); // all, high, medium, low
  const [taskLinkFilter, setTaskLinkFilter] = useState('all'); // all, project, counterpart, unlinked
  const [mainTaskSortField, setMainTaskSortField] = useState('dueDate'); // dueDate, priority, status, title
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [deletingTask, setDeletingTask] = useState(null);
  
  // Theme
  const [theme, setTheme] = useState('light'); // 'light' or 'dark'
  
  // Sector configuration
  const [sectorConfig, setSectorConfig] = useState(DEFAULT_SECTOR_CONFIG);
  
  // Google Drive sync state
  const [gDriveConnected, setGDriveConnected] = useState(false);
  const [gDriveLoading, setGDriveLoading] = useState(false);
  const [gDriveSyncing, setGDriveSyncing] = useState(false);
  const [gDriveLastSync, setGDriveLastSync] = useState(null);
  const [gDriveError, setGDriveError] = useState(null);
  const [gDriveFileInfo, setGDriveFileInfo] = useState(null);
  const gDriveSyncTimeoutRef = useRef(null);
  
  // AI API key for document extraction (supports Gemini or OpenRouter)
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [aiProvider, setAiProvider] = useState('groq'); // 'none', 'groq', 'gemini', or 'openrouter'
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  
  // Load AI API key from localStorage
  useEffect(() => {
    try {
      const savedKey = localStorage.getItem('ai_api_key');
      const savedProvider = localStorage.getItem('ai_provider');
      if (savedKey) {
        setGeminiApiKey(savedKey);
      }
      if (savedProvider) {
        setAiProvider(savedProvider);
      }
    } catch (e) {
      // Safari private browsing may throw "The operation is insecure"
    }
  }, []);

  // Set favicon dynamically
  useEffect(() => {
    const setFavicon = () => {
      // Create an SVG favicon with the Global Infrastructure Sherpa "D" logo
      const svgFavicon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
          <rect width="32" height="32" rx="8" fill="#10b981"/>
          <text x="16" y="23" font-family="Arial, sans-serif" font-size="20" font-weight="bold" fill="white" text-anchor="middle">D</text>
        </svg>
      `;
      
      // Convert SVG to data URL
      const svgBlob = new Blob([svgFavicon], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);
      
      // Update or create favicon link
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = url;

      // The page title is NOT set here.
      //
      // This effect runs on mount for every route, including the
      // server-rendered ones (/deal, /counterpart, /research, /research/<slug>,
      // /signals), each of which ships a specific <title> from the server.
      // Overwriting it meant that once React booted, every page in the site
      // reported the same generic title — and Google evaluates the RENDERED
      // document, so that is the title it saw on all of them.
      //
      // Measured 2026-08-28 with a headless Googlebot user agent: the EV
      // Caribbean report served "Electric Vehicles and EV Charging — Caribbean
      // Edition 2026 | Sherpa Guide" and rendered as "Global Infrastructure
      // Sherpa". Search Console called that URL a Soft 404.
      //
      // SPA-only routes need no assignment: public/index.html already carries
      // the site title as its static default.
    };
    
    setFavicon();
  }, []);
  
  // Save AI API key to localStorage
  const saveGeminiApiKey = (key, provider = aiProvider) => {
    setGeminiApiKey(key);
    setAiProvider(provider);
    try {
      if (key) {
        localStorage.setItem('ai_api_key', key);
        localStorage.setItem('ai_provider', provider);
      } else {
        localStorage.removeItem('ai_api_key');
        localStorage.removeItem('ai_provider');
      }
    } catch (e) {
      // Safari private browsing may throw "The operation is insecure"
    }
  };
  
  // GA4 event tracking helper
  // Events fan out to BOTH Google Analytics (gtag) and PostHog. PostHog is the
  // accurate, queryable product-analytics layer (funnels, replay) — gtag stays
  // for now. posthog is a safe no-op if the key isn't configured.
  const posthog = usePostHog();
  const trackEvent = useCallback((eventName, params = {}) => {
    try { window.gtag?.('event', eventName, params); } catch (e) { /* silent */ }
    try { posthog?.capture(eventName, params); } catch (e) { /* silent */ }
  }, [posthog]);

  // Subscription tier state
  const { user, isSignedIn, isLoaded: isUserLoaded } = useUser();
  const { getToken } = useAuth();
  const clerk = useClerk();

  // PostHog identify: tie the (previously anonymous) session to the Clerk user
  // so we get whole-person journeys, and fire a `signup` event for brand-new
  // accounts (created in the last 10 min). reset() on sign-out unlinks them.
  const phIdentified = useRef(false);
  useEffect(() => {
    if (!posthog || !isUserLoaded) return;
    if (isSignedIn && user) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        tier: user.publicMetadata?.tier || 'free',
      });
      if (!phIdentified.current) {
        phIdentified.current = true;
        const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
        if (createdAt && Date.now() - createdAt < 10 * 60 * 1000) {
          posthog.capture('signup', { tier: user.publicMetadata?.tier || 'free' });
        }
      }
    } else if (!isSignedIn && phIdentified.current) {
      phIdentified.current = false;
      posthog.reset();
    }
  }, [posthog, isUserLoaded, isSignedIn, user]);

  // Register the on-demand token getter during RENDER (runs before child
  // effects), so the first auth-required GET on a fresh load — useFollows /
  // useSavedSearches / ManageAlerts, which fire the instant isSignedIn flips —
  // can pull a Clerk token even before the setAuthToken effect below resolves.
  // Without this those GETs went out unauthenticated → 401 → empty list →
  // "followed companies / saved searches vanish on refresh."
  setTokenGetter(isSignedIn ? getToken : null);

  // Refresh Clerk JWT token and store it for authFetch
  useEffect(() => {
    if (!isSignedIn) { setAuthToken(null); return; }
    const refreshToken = async () => {
      try {
        const token = await getToken();
        setAuthToken(token);
      } catch (e) { /* silent */ }
    };
    refreshToken();
    const interval = setInterval(refreshToken, 50000); // Refresh every 50s
    return () => clearInterval(interval);
  }, [isSignedIn, getToken]);
  const openSignUp = useCallback(() => {
    trackEvent('signup_prompt_shown', { trigger: 'manual' });
    clerk.openSignUp();
  }, [clerk, trackEvent]);
  const rawTier = user?.publicMetadata?.tier || 'free';
  const userTierFromClerk = rawTier;
  const [userTier, setUserTier] = useState(userTierFromClerk);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'
  const [paywallInfo, setPaywallInfo] = useState(null); // { type: 'deal'|'counterpart'|'search', used, limit }
  const isAdmin = !!user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net');
  const isProUser = userTier === 'pro';
  const isFreeUser = isSignedIn && userTier === 'free' && !isAdmin;
  const isBasicUser = isSignedIn && userTier === 'basic' && !isAdmin;
  const isCancelledUser = isSignedIn && userTier === 'cancelled' && !isAdmin;
  // Deal pages are for paying subscribers only (decision 2026-10-02). The API
  // enforces the same rule, so this is presentation, not the protection.
  const isPaidViewer = isAdmin || (isSignedIn && ['basic', 'pro', 'enterprise'].includes(userTier));
  // A deal opened before sign-in finished loading arrives as the headline card.
  // Once we know the viewer subscribes, fetch the full deal and swap it in.
  useEffect(() => {
    if (!isUserLoaded || !isPaidViewer || !selectedDeal?.locked || !selectedDeal?.id) return;
    let cancelled = false;
    authFetch(`/api/deals?id=${selectedDeal.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j?.deal && !j.locked) setSelectedDeal(j.deal); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isUserLoaded, isPaidViewer, selectedDeal?.id, selectedDeal?.locked]);

  // Guest metered paywall: allow 5 free detail page views before gating
  const GUEST_VIEW_LIMIT = 5;
  const GUEST_VIEWS_KEY = 'gis_guest_views';

  const [guestViewCount, setGuestViewCount] = useState(() => {
    try { return parseInt(localStorage.getItem('gis_guest_views') || '0', 10); }
    catch { return 0; }
  });

  const [showRegModal, setShowRegModal] = useState(false);
  const [regModalDismissed, setRegModalDismissed] = useState(0); // click count when last dismissed
  // LinkedIn-style gating: users see full content until their monthly quota
  // is exceeded. The gate appears AFTER the user has experienced the value.
  // isContentGated is now dynamic — starts false, becomes true when quota exceeded.
  // This replaces the old always-on gate for anonymous users.
  const [quotaExceeded, setQuotaExceeded] = useState(null); // null = not exceeded, 'dealViews' | 'counterpartViews' etc.
  const isContentGated = !!quotaExceeded;
  const isActionGated = !isSignedIn; // Follow, Share, Export still require sign-in

  const trackGuestView = useCallback(() => {
    if (isSignedIn) return;
    setGuestViewCount(prev => {
      const next = prev + 1;
      try { localStorage.setItem(GUEST_VIEWS_KEY, String(next)); } catch {}
      return next;
    });
  }, [isSignedIn]);

  const guestViewsRemaining = isSignedIn ? null : Math.max(0, GUEST_VIEW_LIMIT - guestViewCount);

  // Sync tier from Clerk when user data loads/changes
  useEffect(() => {
    if (user?.publicMetadata?.tier) {
      setUserTier(user.publicMetadata.tier);
    }
  }, [user?.publicMetadata?.tier]);

  // Local session usage counters — seeded from Clerk metadata, incremented synchronously
  // This fixes the race condition where async tracking completed AFTER the limit check
  const [sessionUsage, setSessionUsage] = useState({ dealViews: 0, cpViews: 0, searches: 0 });
  const sessionUsageSeeded = useRef(false);

  // Seed local counters from Clerk metadata when user data loads
  useEffect(() => {
    if (sessionUsageSeeded.current || !user?.publicMetadata?.usage) return;
    const now = new Date();
    const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const monthUsage = user.publicMetadata.usage[period] || {};
    setSessionUsage({
      dealViews: monthUsage.dealViews || 0,
      cpViews: monthUsage.cpViews || 0,
      searches: monthUsage.searchUsage || 0,
    });
    sessionUsageSeeded.current = true;
  }, [user?.publicMetadata?.usage]);

  // Helper: get current month's usage from local session counter (instant, no race condition)
  const getMonthlyUsage = useCallback((namespace) => {
    return sessionUsage[namespace] || 0;
  }, [sessionUsage]);

  // Track usage: increment local counter synchronously, then fire API in background
  const trackUsageAndReload = useCallback((type) => {
    // Increment local counter immediately (synchronous — no race condition)
    const nsMap = { deal: 'dealViews', counterpart: 'cpViews', search: 'searches' };
    const ns = nsMap[type];
    if (ns) {
      setSessionUsage(prev => ({ ...prev, [ns]: (prev[ns] || 0) + 1 }));
    }
    // Fire API in background (for persistence across sessions)
    fetch('/api/track-usage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, type }),
    })
      .then(() => user?.reload?.())
      .catch(() => {});
  }, [user]);

  // Auth guard: check if user can view a deal detail
  // PAYWALL restored 2026-09-26 (user: "bring back the paywall as we had it
  // before"). LinkedIn-style: monthly view quotas per tier from
  // src/lib/usageQuotas.js; over quota, the page renders behind the gate and
  // PaywallOverlay. Removed in 33a9433 (2026-04-25), re-applied as it was.
  const viewDeal = useCallback((deal) => {
    trackEvent('view_deal', { deal_id: deal?.id, signed_in: !!isSignedIn });
    window.scrollTo(0, 0);

    const effectiveTier = isAdmin ? 'admin' : (isSignedIn ? userTier : 'anonymous');
    const result = consumeQuota('dealViews', effectiveTier);
    if (!result.allowed) {
      setQuotaExceeded('dealViews');
      setPaywallInfo({ type: 'deals', used: result.used, limit: result.limit });
      setSelectedDeal(deal);
      return;
    }

    setQuotaExceeded(null);
    setSelectedDeal(deal);
  }, [isSignedIn, userTier, isAdmin]);

  const viewCounterpart = useCallback((cp) => {
    window.scrollTo(0, 0);
    const fullCp = (cp?.id && counterparts.find(c => c.id === cp.id)) || cp;
    const effectiveTier = isAdmin ? 'admin' : (isSignedIn ? userTier : 'anonymous');
    const result = consumeQuota('counterpartViews', effectiveTier);
    if (!result.allowed) {
      setQuotaExceeded('counterpartViews');
      setPaywallInfo({ type: 'counterparts', used: result.used, limit: result.limit });
      setSelectedCounterpart(fullCp);
      return;
    }
    setQuotaExceeded(null);
    setSelectedCounterpart(fullCp);
  }, [isSignedIn, userTier, isAdmin, counterparts]);

  const viewPanelCounterpart = useCallback((cp) => {
    const fullCp = (cp?.id && counterparts.find(c => c.id === cp.id)) || cp;
    const effectiveTier = isAdmin ? 'admin' : (isSignedIn ? userTier : 'anonymous');
    const result = consumeQuota('counterpartViews', effectiveTier);
    if (!result.allowed) {
      setQuotaExceeded('counterpartViews');
      setPaywallInfo({ type: 'counterparts', used: result.used, limit: result.limit });
      return;
    }
    setQuotaExceeded(null);
    setPanelCounterpart(fullCp);
  }, [isSignedIn, userTier, isAdmin, counterparts]);

  const viewPanelDeal = useCallback((deal) => {
    const effectiveTier = isAdmin ? 'admin' : (isSignedIn ? userTier : 'anonymous');
    const result = consumeQuota('dealViews', effectiveTier);
    if (!result.allowed) {
      setQuotaExceeded('dealViews');
      setPaywallInfo({ type: 'deals', used: result.used, limit: result.limit });
      return;
    }
    setQuotaExceeded(null);
    setPanelDeal(deal);
    setPanelDealSource(null);
  }, [isSignedIn, userTier, isAdmin]);

  // Notify admin of new user registrations
  useEffect(() => {
    if (!user?.id || !isSignedIn) return;
    
    try {
      const notifiedKey = `gis_notified_${user.id}`;
      const alreadyNotified = localStorage.getItem(notifiedKey);
      
      if (!alreadyNotified) {
        // GA4: track signup conversion
        trackEvent('sign_up', { method: 'clerk' });
        // Welcome toast for new users (only if no follow deep-link — that handler shows its own toast)
        const hasFollowParam = new URLSearchParams(window.location.search).has('follow');
        if (!hasFollowParam) {
          const firstName = user.firstName || user.fullName?.split(' ')[0] || '';
          setWelcomeToast({
            message: firstName
              ? `Welcome, ${firstName}! Follow companies and sectors to get daily deal alerts.`
              : 'Welcome! Follow companies and sectors to get daily deal alerts.',
          });
        }
        // New user - send notification
        fetch('/api/notify-registration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            email: user.primaryEmailAddress?.emailAddress,
            name: user.fullName || user.firstName,
            createdAt: user.createdAt,
          }),
        }).then(() => {
          // Mark as notified
          try {
            localStorage.setItem(notifiedKey, Date.now().toString());
          } catch (e) { /* Safari private browsing */ }
        }).catch(err => {
          console.log('Registration notification failed:', err);
        });
      }
    } catch (e) {
      // Safari private browsing may throw "The operation is insecure"
    }
  }, [user?.id, isSignedIn]);

  // Deep-link handler for ?follow= URLs from SEO pages
  // e.g. /?follow=technology:hydrogen or /?follow=counterpart:{uuid}
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const followParam = params.get('follow');
    if (!followParam) return;

    const [targetType, ...rest] = followParam.split(':');
    const targetId = rest.join(':'); // rejoin in case UUID contains colons
    if (!targetType || !targetId) return;
    if (!['counterpart', 'technology', 'country'].includes(targetType)) return;

    if (isSignedIn && user?.id) {
      // Auto-create follow and clean URL
      fetch('/api/follows', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, targetType, targetId }),
      }).catch(() => {});
      trackEvent('follow_from_seo', { target_type: targetType, target_id: targetId });
      // Show welcome toast with context
      const label = targetType === 'technology'
        ? targetId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\b(Ppa|Ipo|Epc|Ipp)\b/g, m => m.toUpperCase())
        : targetType === 'country' ? targetId : targetId;
      setWelcomeToast({
        message: targetType === 'counterpart'
          ? "You're all set! You'll get daily alerts when new deals are announced."
          : `You're now following ${label}. You'll get a daily email with new deals.`,
      });
      // Navigate to insights so they see the dashboard with their follow
      setActiveTab('insights');
      // Clean URL
      params.delete('follow');
      const clean = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', clean);
    } else if (isUserLoaded && !isSignedIn) {
      // Show sign-up prompt — follow will happen after sign-up on next page load
      openSignUp();
    }
  }, [isSignedIn, isUserLoaded, user?.id]);



  // Auto-dismiss welcome toast after 6s
  useEffect(() => {
    if (!welcomeToast) return;
    const timer = setTimeout(() => setWelcomeToast(null), 6000);
    return () => clearTimeout(timer);
  }, [welcomeToast]);

  // PartnerStack tracking for affiliate attribution
  useEffect(() => {
    if (!user?.id || !isSignedIn) return;

    try {
      const partnerStackKey = `gis_partnerstack_${user.id}`;
      const alreadyTracked = localStorage.getItem(partnerStackKey);
      
      if (!alreadyTracked && window.growsumo) {
        window.growsumo.data.name = user.fullName || user.firstName || '';
        window.growsumo.data.email = user.primaryEmailAddress?.emailAddress || '';
        window.growsumo.data.customer_key = user.primaryEmailAddress?.emailAddress || user.id;
        window.growsumo.createSignup();
        
        try {
          localStorage.setItem(partnerStackKey, Date.now().toString());
        } catch (e) { /* Safari private browsing */ }
      }
    } catch (e) {
      // Fail silently
    }
  }, [user?.id, isSignedIn]);

  // Check for pending share import after login
  useEffect(() => {
    if (!user?.id || !isSignedIn) return;
    
    try {
      const pendingShare = localStorage.getItem('pendingImportShare');
      if (pendingShare) {
        // Clear it immediately to prevent double import
        localStorage.removeItem('pendingImportShare');
        // Navigate to the share page to trigger import
        window.location.hash = pendingShare.replace('#', '');
      }
    } catch (e) {
      // Safari private browsing
    }
  }, [user?.id, isSignedIn]);
  
  // Counterpart tags
  const [counterpartTags, setCounterpartTags] = useState(['VIP', 'Priority', 'New', 'Follow-up', 'Strategic']);
  const [counterpartTagFilter, setCounterpartTagFilter] = useState('all');

  // Editable categories
  const [technologies, setTechnologies] = useState(defaultTechnologies);
  const [stages, setStages] = useState(defaultStages);
  const [pipelineStatuses, setPipelineStatuses] = useState(defaultPipelineStatuses);
  const [counterpartCategories, setCounterpartCategories] = useState(defaultCounterpartCategories);
  const [documentCategories, setDocumentCategories] = useState(defaultDocumentCategories);
  const [regions, setRegions] = useState(defaultRegions);
  const [dealTechnologies, setDealTechnologies] = useState(defaultDealTechnologies);

  // Load deal technologies from taxonomy database — deferred until deal modal opens
  const [dealTechLoaded, setDealTechLoaded] = useState(false);
  useEffect(() => {
    if (!(showDealModal || editingDeal) || dealTechLoaded) return;
    fetch('/api/taxonomy?type=technologies')
      .then(r => r.json())
      .then(result => {
        if (result.data && result.data.length > 0) {
          const techOptions = result.data
            .sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99))
            .map(t => ({ value: t.value, label: t.label }));
          setDealTechnologies(techOptions);
        }
        setDealTechLoaded(true);
      })
      .catch(err => {
        console.error('Error loading technologies taxonomy, using defaults:', err);
        setDealTechLoaded(true);
      });
  }, [showDealModal, editingDeal, dealTechLoaded]);

  // Refresh centralized data (deals and counterparts) - callable on demand
  const refreshCentralizedData = useCallback(async () => {
    try {
      const centralizedData = await loadCentralizedData();
      if (centralizedData.deals) {
        setDeals(centralizedData.deals);
      }
      if (centralizedData.counterparts) {
        setCounterparts(centralizedData.counterparts);
      }
      return centralizedData;
    } catch (error) {
      console.error('Error refreshing centralized data:', error);
      return null;
    }
  }, []);

  // Load data from Supabase (centralized storage)
  useEffect(() => {
    // If Clerk hasn't loaded yet, wait
    if (!isUserLoaded) return;

    const applyData = (data) => {
      if (data.projects) setProjects(data.projects);
      // Don't load counterparts/deals from user data - use centralized data only
      // if (data.counterparts) setCounterparts(data.counterparts);
      // if (data.deals) setDeals(data.deals);
      if (data.documents) setDocuments(data.documents);
      if (data.activities) setActivities(data.activities);
      if (data.tasks) setTasks(data.tasks);
      if (data.favorites) setFavorites(data.favorites);
      if (data.recentItems) setRecentItems(data.recentItems);
      if (data.technologies) setTechnologies(data.technologies);
      if (data.stages) setStages(data.stages);
      if (data.pipelineStatuses) setPipelineStatuses(data.pipelineStatuses);
      if (data.counterpartCategories) setCounterpartCategories(data.counterpartCategories);
      if (data.documentCategories) setDocumentCategories(data.documentCategories);
      if (data.regions) setRegions(data.regions);
      if (data.theme) setTheme(data.theme);
      if (data.counterpartTags) setCounterpartTags(data.counterpartTags);
      if (data.sectorConfig) setSectorConfig({...DEFAULT_SECTOR_CONFIG, ...data.sectorConfig});
    };

    const loadData = async () => {
      try {
        // Fast path: only user data blocks the spinner (~100-200ms)
        if (user?.id) {
          const userData = await loadUserData(user.id);
          if (userData) applyData(userData);
          setStorageAvailable(true);
        }
      } catch (error) {
        console.error('Error loading from Supabase:', error);
        setStorageAvailable(false);
      }

      // Spinner clears immediately — page renders without waiting for API data
      setIsLoading(false);

      // Background: dashboard stats (~35KB) — BloombergDashboard shows its own loading state
      loadDashboardStats().then(stats => {
        if (stats) setDashboardStats(stats);
      }).catch(() => {});

      // Background: load deals + counterparts
      // - Counterparts (public top-N) are fetched on mount by the instant-paint
      //   effect below, so the Organizations landing paints without waiting on Clerk.
      // - Anonymous: still load the FULL public deal set here for the Deals tab.
      // - Authenticated: full deals + counterparts + dimensions (below).
      if (!isSignedIn) {
        // Anonymous visitors: /api/deals now returns only the 20 most recent
        // published deals to them (paywall restored 2026-09-26 — "protect the
        // DB"); the full set needs a signed-in session.
        loadDealsWithCounterparts().then(d => {
          if (Array.isArray(d)) {
            setDeals(d);
            setTotalCounts(prev => ({ ...prev, deals: d.length }));
          }
        }).catch(err => console.error('[Anonymous deals load] error:', err));
        setCentralDataLoaded(true);
        return;
      }
      loadCentralizedData().catch(() => ({ deals: null, counterparts: null })).then(centralizedData => {
        if (centralizedData.deals) {
          setDeals(centralizedData.deals);
        }
        if (centralizedData.counterparts) {
          setCounterparts(centralizedData.counterparts);

          // Background: fetch dimensions separately (~3.2 MB deferred from critical path)
          loadCounterpartDimensions().then(dimData => {
            if (dimData?.length) {
              const dimMap = new Map(dimData.map(d => [d.id, d.dimensions]));
              setCounterparts(prev => prev.map(cp => {
                const dims = dimMap.get(cp.id);
                if (!dims) return cp;
                return {
                  ...cp,
                  investmentPreferences: { ...cp.investmentPreferences, dimensions: dims }
                };
              }));
            }
          }).catch(err => console.error('[Dimensions] Background fetch error:', err));
        }
        setTotalCounts({
          deals: centralizedData.deals?.length || 0,
          counterparts: centralizedData.counterparts?.length || 0
        });
        setCentralDataLoaded(true);
      });
    };

    loadData();
  }, [user?.id, isUserLoaded]);

  // Instant paint: fetch the public top organizations immediately on mount, in
  // parallel with Clerk init (which the loader above waits on). The Organizations
  // landing renders from this so its table isn't blank while auth initializes.
  // authFetch has no token yet → the server returns the public top-N (with
  // _cpInfo). Guarded so it never clobbers a fuller set the authenticated load
  // may have already populated — race-safe in both arrival orders.
  // Retries on empty/failed response (loadCounterparts swallows errors → []) so a
  // transient 429/5xx doesn't leave the landing on its skeleton; setOrgsLoaded lets
  // the page distinguish "still loading" from "loaded/failed empty".
  useEffect(() => {
    let cancelled = false;
    const attempt = (triesLeft) => {
      loadCounterparts().then(cps => {
        if (cancelled) return;
        if (cps && cps.length) {
          setCounterparts(prev => (prev.length ? prev : cps));
          if (typeof cps.totalCount === 'number') {
            setTotalCounts(prev => (prev.counterparts ? prev : { ...prev, counterparts: cps.totalCount }));
          }
          setOrgsLoaded(true);
          return;
        }
        if (triesLeft > 0) { setTimeout(() => attempt(triesLeft - 1), 1500); return; }
        setOrgsLoaded(true); // give up gracefully → page shows a "couldn't load" state, not an endless skeleton
      });
    };
    attempt(2);
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh central deals + counterparts every 5 min so newly-discovered
  // deals appear in the Live Feed without a manual page reload. Silent refresh
  // (no loading spinner); /api/deals is edge-cached (s-maxage=120) so most polls
  // are served from cache — minimal DB load.
  useEffect(() => {
    if (!isUserLoaded) return;
    const interval = setInterval(() => { refreshCentralizedData(); }, 300000);
    return () => clearInterval(interval);
  }, [isUserLoaded, refreshCentralizedData]);

  // Save data to Supabase whenever it changes (centralized storage)
  useEffect(() => {
    // Don't save if no user, storage not available, or still loading
    if (!user?.id || !storageAvailable || isLoading) return;

    // Debounce saves to avoid too many API calls
    const saveTimeout = setTimeout(async () => {
      const dataToSave = {
        projects,
        // counterparts and deals excluded — loaded from centralized APIs, never read back from user_data
        documents,
        activities,
        tasks,
        favorites,
        recentItems,
        technologies,
        stages,
        pipelineStatuses,
        counterpartCategories,
        documentCategories,
        regions,
        theme,
        sectorConfig,
        counterpartTags
      };

      try {
        await saveUserData(user.id, dataToSave);
      } catch (error) {
        console.error('Error saving to Supabase:', error);
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(saveTimeout);
  }, [user?.id, projects, documents, activities, tasks, favorites, recentItems, technologies, stages, pipelineStatuses, counterpartCategories, documentCategories, regions, theme, sectorConfig, counterpartTags, storageAvailable, isLoading]);

  // Note: Google Drive sync removed - using centralized Supabase storage instead

  // Google Drive connect handler
  const connectGoogleDrive = async () => {
    if (!user?.id) {
      setGDriveError('Please sign in first');
      return;
    }
    
    setGDriveLoading(true);
    setGDriveError(null);
    try {
      // Ensure user ID is set
      googleDriveSync.setUserId(user.id);
      
      await googleDriveSync.init();
      await googleDriveSync.signIn();
      setGDriveConnected(true);
      
      // Check if there's existing data in Drive
      const cloudData = await googleDriveSync.loadData();
      if (cloudData && (cloudData.projects?.length || cloudData.counterparts?.length)) {
        // Smart merge function - merges by ID, keeps most recently modified
        const mergeArrays = (local, cloud, entityName) => {
          const localMap = new Map((local || []).map(item => [item.id, item]));
          const cloudMap = new Map((cloud || []).map(item => [item.id, item]));
          const allIds = new Set([...localMap.keys(), ...cloudMap.keys()]);
          
          let added = 0, updated = 0, kept = 0;
          const merged = [];
          
          for (const id of allIds) {
            const localItem = localMap.get(id);
            const cloudItem = cloudMap.get(id);
            
            if (localItem && !cloudItem) {
              // Only in local - keep it
              merged.push(localItem);
              added++;
            } else if (cloudItem && !localItem) {
              // Only in cloud - add it
              merged.push(cloudItem);
              added++;
            } else {
              // In both - use most recently modified
              const localTime = new Date(localItem.updatedAt || localItem.createdAt || 0).getTime();
              const cloudTime = new Date(cloudItem.updatedAt || cloudItem.createdAt || 0).getTime();
              if (localTime >= cloudTime) {
                merged.push(localItem);
                kept++;
              } else {
                merged.push(cloudItem);
                updated++;
              }
            }
          }
          
          return { merged, stats: { added, updated, kept, total: merged.length } };
        };
        
        // Merge all entity types
        const projectsMerge = mergeArrays(projects, cloudData.projects, 'projects');
        const counterpartsMerge = mergeArrays(counterparts, cloudData.counterparts, 'counterparts');
        const documentsMerge = mergeArrays(documents, cloudData.documents, 'documents');
        const activitiesMerge = mergeArrays(activities, cloudData.activities, 'activities');
        const tasksMerge = mergeArrays(tasks, cloudData.tasks, 'tasks');
        
        // Calculate summary
        const localOnly = {
          projects: projects.filter(p => !cloudData.projects?.find(c => c.id === p.id)).length,
          counterparts: counterparts.filter(p => !cloudData.counterparts?.find(c => c.id === p.id)).length
        };
        const cloudOnly = {
          projects: (cloudData.projects || []).filter(c => !projects.find(p => p.id === c.id)).length,
          counterparts: (cloudData.counterparts || []).filter(c => !counterparts.find(p => p.id === c.id)).length
        };
        
        // Show merge confirmation
        const doMerge = window.confirm(
          'Found existing Global Infrastructure Sherpa data in your Google Drive.\n\n' +
          'Data will be MERGED (no data will be lost):\n\n' +
          `📁 Projects: ${projectsMerge.stats.total} total\n` +
          `   • ${localOnly.projects} only on this device\n` +
          `   • ${cloudOnly.projects} only in cloud\n` +
          `   • ${projectsMerge.stats.kept + projectsMerge.stats.updated} in both (using newest)\n\n` +
          `👥 Counterparts: ${counterpartsMerge.stats.total} total\n` +
          `   • ${localOnly.counterparts} only on this device\n` +
          `   • ${cloudOnly.counterparts} only in cloud\n` +
          `   • ${counterpartsMerge.stats.kept + counterpartsMerge.stats.updated} in both (using newest)\n\n` +
          'Click OK to merge, or Cancel to skip (you can sync later).'
        );
        
        if (doMerge) {
          setProjects(projectsMerge.merged);
          setCounterparts(counterpartsMerge.merged);
          setDocuments(documentsMerge.merged);
          setActivities(activitiesMerge.merged);
          setTasks(tasksMerge.merged);
          
          // Merge favorites (union of both)
          const localFavorites = favorites || { projects: [], counterparts: [] };
          const cloudFavorites = cloudData.favorites || { projects: [], counterparts: [] };
          setFavorites({
            projects: [...new Set([...localFavorites.projects, ...cloudFavorites.projects])],
            counterparts: [...new Set([...localFavorites.counterparts, ...cloudFavorites.counterparts])]
          });
          
          // Merge recent items (union, sorted by timestamp, keep latest 50)
          const mergedRecent = [...(recentItems || []), ...(cloudData.recentItems || [])]
            .reduce((acc, item) => {
              const existing = acc.find(i => i.id === item.id && i.type === item.type);
              if (!existing || new Date(item.timestamp) > new Date(existing.timestamp)) {
                return [...acc.filter(i => !(i.id === item.id && i.type === item.type)), item];
              }
              return acc;
            }, [])
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 50);
          setRecentItems(mergedRecent);
          
          // For settings, use cloud if local is default/empty
          if (cloudData.technologies?.length) setTechnologies(prev => prev.length <= 5 ? cloudData.technologies : prev);
          if (cloudData.stages?.length) setStages(prev => prev.length <= 4 ? cloudData.stages : prev);
          if (cloudData.pipelineStatuses?.length) setPipelineStatuses(prev => prev.length <= 4 ? cloudData.pipelineStatuses : prev);
          if (cloudData.counterpartCategories?.length) setCounterpartCategories(prev => prev.length <= 5 ? cloudData.counterpartCategories : prev);
          if (cloudData.sectorConfig) setSectorConfig(cloudData.sectorConfig);
        }
      }
      
      // Get file info
      const info = await googleDriveSync.getFileInfo();
      if (info) {
        setGDriveFileInfo(info);
        setGDriveLastSync(new Date(info.modifiedTime));
      }
      
      setGDriveLoading(false);
    } catch (error) {
      console.error('Google Drive connect error:', error);
      setGDriveError('Failed to connect to Google Drive');
      setGDriveLoading(false);
    }
  };

  // Google Drive disconnect handler
  const disconnectGoogleDrive = () => {
    googleDriveSync.signOut();
    setGDriveConnected(false);
    setGDriveFileInfo(null);
    setGDriveLastSync(null);
  };

  // Sync to Google Drive (debounced)
  // silent=true prevents UI state updates and skips merge (used for auto-sync to avoid re-renders)
  // silent=false (Sync Now button) merges cloud data first to prevent data loss
  const syncToGoogleDrive = useCallback(async (silent = false) => {
    if (!gDriveConnected || gDriveSyncing) return;
    
    if (!silent) {
      setGDriveSyncing(true);
      setGDriveError(null);
    }
    
    try {
      // Helper function for merging arrays by ID
      const mergeArrays = (local, cloud) => {
        const localMap = new Map((local || []).map(item => [item.id, item]));
        const cloudMap = new Map((cloud || []).map(item => [item.id, item]));
        const allIds = new Set([...localMap.keys(), ...cloudMap.keys()]);
        
        const merged = [];
        for (const id of allIds) {
          const localItem = localMap.get(id);
          const cloudItem = cloudMap.get(id);
          
          if (localItem && !cloudItem) {
            merged.push(localItem);
          } else if (cloudItem && !localItem) {
            merged.push(cloudItem);
          } else {
            // Use most recently modified
            const localTime = new Date(localItem.updatedAt || localItem.createdAt || 0).getTime();
            const cloudTime = new Date(cloudItem.updatedAt || cloudItem.createdAt || 0).getTime();
            merged.push(localTime >= cloudTime ? localItem : cloudItem);
          }
        }
        return merged;
      };
      
      // For manual sync (Sync Now button), merge with cloud data first
      let finalProjects = projects;
      let finalCounterparts = counterparts;
      let finalDocuments = documents;
      let finalActivities = activities;
      let finalTasks = tasks;
      let finalFavorites = favorites;
      let finalRecentItems = recentItems;
      
      if (!silent) {
        try {
          const cloudData = await googleDriveSync.loadData();
          if (cloudData) {
            finalProjects = mergeArrays(projects, cloudData.projects);
            finalCounterparts = mergeArrays(counterparts, cloudData.counterparts);
            finalDocuments = mergeArrays(documents, cloudData.documents);
            finalActivities = mergeArrays(activities, cloudData.activities);
            finalTasks = mergeArrays(tasks, cloudData.tasks);
            
            // Merge favorites (union)
            const cloudFavorites = cloudData.favorites || { projects: [], counterparts: [] };
            finalFavorites = {
              projects: [...new Set([...(favorites?.projects || []), ...cloudFavorites.projects])],
              counterparts: [...new Set([...(favorites?.counterparts || []), ...cloudFavorites.counterparts])]
            };
            
            // Merge recent items (keep unique, sort by timestamp, limit to 50)
            const allRecent = [...(recentItems || []), ...(cloudData.recentItems || [])];
            finalRecentItems = allRecent
              .reduce((acc, item) => {
                const existing = acc.find(i => i.id === item.id && i.type === item.type);
                if (!existing || new Date(item.timestamp) > new Date(existing.timestamp)) {
                  return [...acc.filter(i => !(i.id === item.id && i.type === item.type)), item];
                }
                return acc;
              }, [])
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .slice(0, 50);
            
            // Update local state with merged data
            setProjects(finalProjects);
            setCounterparts(finalCounterparts);
            setDocuments(finalDocuments);
            setActivities(finalActivities);
            setTasks(finalTasks);
            setFavorites(finalFavorites);
            setRecentItems(finalRecentItems);
          }
        } catch (mergeError) {
          console.warn('Could not merge with cloud data, will overwrite:', mergeError);
        }
      }
      
      const data = {
        projects: finalProjects,
        counterparts: finalCounterparts,
        documents: finalDocuments,
        activities: finalActivities,
        tasks: finalTasks,
        favorites: finalFavorites,
        recentItems: finalRecentItems,
        technologies,
        stages,
        pipelineStatuses,
        counterpartCategories,
        documentCategories,
        regions,
        theme,
        sectorConfig,
        counterpartTags,
        // Note: userTier is NOT synced to Drive - Clerk is the source of truth for billing
        lastSyncedAt: new Date().toISOString()
      };
      
      const success = await googleDriveSync.saveData(data);
      if (success) {
        if (!silent) {
          setGDriveLastSync(new Date());
          const info = await googleDriveSync.getFileInfo();
          if (info) setGDriveFileInfo(info);
        }
      } else {
        if (!silent) setGDriveError('Sync failed');
      }
    } catch (error) {
      console.error('Google Drive sync error:', error);
      if (!silent) setGDriveError('Sync failed');
    }
    
    if (!silent) setGDriveSyncing(false);
  }, [gDriveConnected, gDriveSyncing, projects, counterparts, documents, activities, tasks, favorites, recentItems, technologies, stages, pipelineStatuses, counterpartCategories, documentCategories, regions, theme, sectorConfig, counterpartTags]);

  // Auto-sync to Google Drive when data changes (debounced)
  // Pause sync while modals are open to prevent form state reset
  useEffect(() => {
    if (!gDriveConnected || isLoading) return;
    
    // Don't sync while modals are open (prevents form state reset)
    const modalOpen = showProjectModal || showCounterpartModal || showDocumentUploadModal;
    if (modalOpen) return;
    
    // Clear existing timeout
    if (gDriveSyncTimeoutRef.current) {
      clearTimeout(gDriveSyncTimeoutRef.current);
    }
    
    // Debounce sync by 5 seconds (silent to avoid re-renders)
    gDriveSyncTimeoutRef.current = setTimeout(() => {
      syncToGoogleDrive(true); // silent mode
    }, 5000);
    
    return () => {
      if (gDriveSyncTimeoutRef.current) {
        clearTimeout(gDriveSyncTimeoutRef.current);
      }
    };
  }, [gDriveConnected, isLoading, projects, counterparts, documents, activities, favorites, recentItems, syncToGoogleDrive, showProjectModal, showCounterpartModal, showDocumentUploadModal]);

  // Manual sync handler (shows UI feedback)
  const manualSyncGoogleDrive = async () => {
    if (gDriveSyncTimeoutRef.current) {
      clearTimeout(gDriveSyncTimeoutRef.current);
    }
    await syncToGoogleDrive(false); // not silent - show UI feedback
  };

  // Load from Google Drive handler
  const loadFromGoogleDrive = async () => {
    if (!gDriveConnected) return;
    
    setGDriveLoading(true);
    try {
      const cloudData = await googleDriveSync.loadData();
      if (cloudData) {
        setProjects(cloudData.projects || []);
        setCounterparts(cloudData.counterparts || []);
        setDocuments(cloudData.documents || []);
        setActivities(cloudData.activities || []);
        setTasks(cloudData.tasks || []);
        setFavorites(cloudData.favorites || { projects: [], counterparts: [] });
        setRecentItems(cloudData.recentItems || []);
        if (cloudData.technologies) setTechnologies(cloudData.technologies);
        if (cloudData.stages) setStages(cloudData.stages);
        if (cloudData.pipelineStatuses) setPipelineStatuses(cloudData.pipelineStatuses);
        if (cloudData.counterpartCategories) setCounterpartCategories(cloudData.counterpartCategories);
        if (cloudData.sectorConfig) setSectorConfig(cloudData.sectorConfig);
        setGDriveLastSync(new Date());
      }
    } catch (error) {
      console.error('Error loading from Google Drive:', error);
      setGDriveError('Failed to load from Google Drive');
    }
    setGDriveLoading(false);
  };

  // Translation helper (English only)
  const t = useCallback((key) => {
    return translations.en[key] || key;
  }, []);

  // Subscription limit check functions — admin and pro users always allowed
  const canAddEntity = useCallback(() => {
    if (isAdmin || isProUser) return true;
    return true; // Free registered users can add entities (viewing is limited, not creation)
  }, [isAdmin, isProUser]);

  // Get usage stats for billing page
  const getUsageStats = useCallback(() => {
    const effectiveTier = userTier === 'free' ? 'basic' : userTier;
    const tierInfo = SUBSCRIPTION_TIERS[effectiveTier] || SUBSCRIPTION_TIERS.basic;
    return {
      dealViews: { used: getMonthlyUsage('dealViews'), limit: tierInfo.dealViews },
      cpViews: { used: getMonthlyUsage('cpViews'), limit: tierInfo.cpViews },
      searches: { used: getMonthlyUsage('searches'), limit: tierInfo.searches },
    };
  }, [userTier, getMonthlyUsage]);

  // Helper to get counterpart name (needed for search)
  const getCounterpartName = (id) => counterparts.find(c => c.id === id)?.name || "Unknown";

  // ============ ACTIVITY LOGGING ============
  // AI Summary function
  const generateAISummary = async (type, item) => {
    if (!user?.id) {
      setAiSummary({ error: 'Sign in to use AI Summary' });
      return;
    }
    
    setSummaryLoading(true);
    setAiSummary(null);
    
    try {
      // Gather context
      let context = '';
      if (type === 'project') {
        const projectDocs = documents.filter(d => d.projectId === item.id);
        const projectCounterparts = counterparts.filter(c => item.counterpartIds?.includes(c.id));
        const projectActivities = activities.filter(a => a.targetType === 'project' && a.targetId === item.id);
        const projectTasks = tasks.filter(t => t.projectId === item.id);
        
        context = `
PROJECT: ${item.name}
Technology: ${item.technology}
Location: ${item.location}
Capacity: ${item.capacity} MW
Stage: ${item.stage}
Pipeline Status: ${item.pipelineStatus}
Investment Size: ${item.investmentSize ? '$' + item.investmentSize.toLocaleString() : 'Not specified'}
Expected Return: ${item.expectedReturn || 'Not specified'}%
Notes: ${item.notes || 'None'}
Created: ${item.createdAt || 'Unknown'}

RELATED COUNTERPARTS (${projectCounterparts.length}):
${projectCounterparts.map(c => `- ${c.name} (${c.type}) - ${c.category || 'Uncategorized'}`).join('\n') || 'None'}

DOCUMENTS (${projectDocs.length}):
${projectDocs.map(d => `- ${d.name} (${d.category || 'Uncategorized'})`).join('\n') || 'None'}

RECENT ACTIVITIES (${projectActivities.length}):
${projectActivities.slice(0, 10).map(a => `- ${a.date}: ${a.description}`).join('\n') || 'None'}

TASKS (${projectTasks.length}):
${projectTasks.map(t => `- ${t.title} [${t.status}] ${t.dueDate ? 'Due: ' + t.dueDate : ''}`).join('\n') || 'None'}
`;
      } else if (type === 'counterpart') {
        const cpActivities = activities.filter(a => a.targetType === 'counterpart' && a.targetId === item.id);

        context = `
COUNTERPART: ${item.name}
Type: ${item.type}
Category: ${item.category || 'Not categorized'}
Company: ${item.company || 'N/A'}
Location: ${item.location || 'Not specified'}
Email: ${item.email || 'Not provided'}
Phone: ${item.phone || 'Not provided'}
LinkedIn: ${item.linkedin || 'Not provided'}
Notes: ${item.notes || 'None'}

RECENT ACTIVITIES (${cpActivities.length}):
${cpActivities.slice(0, 10).map(a => `- ${a.date}: ${a.description}`).join('\n') || 'None'}
`;
      }
      
      const response = await fetch('/api/ai-extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id,
          content: context,
          mode: 'summary',
          summaryType: type
        }),
      });
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setAiSummary({ error: 'AI service unavailable. Please try again later.' });
        setSummaryLoading(false);
        return;
      }
      
      const result = await response.json();
      
      if (!response.ok) {
        setAiSummary({ error: result.error || 'Failed to generate summary' });
      } else {
        setAiSummary({ 
          type, 
          itemId: item.id, 
          itemName: item.name,
          summary: result.data?.summary || result.data 
        });
      }
    } catch (error) {
      setAiSummary({ error: 'Failed to generate summary: ' + error.message });
    }
    
    setSummaryLoading(false);
  };
  
  // Unified Smart Search — every query goes through the same endpoint
  // Pro-only: non-Pro users see a subscribe/upgrade gate instead of results
  // Cached sample search result — shown to anonymous users to give a taste
  // of what AI search looks like. Same result for everyone, zero AI cost.
  const SAMPLE_SEARCH = {
    query: 'solar PPA deals in Europe',
    summary: 'European solar PPA activity has been concentrated among a handful of major developers and offtakers. TotalEnergies, Iberdrola, and Statkraft have been the most active developers signing corporate PPAs across Spain, France, and Germany. Key offtakers include Google, Amazon, and Microsoft, who collectively account for over 5 GW of contracted solar capacity in Europe. Recent deals show a trend toward longer-duration contracts (10-15 years) and hybrid structures combining solar with battery storage.',
    counterparts: [],
    deals: [],
    isSample: true,
  };

  const performSmartSearch = async (query) => {
    trackEvent('search', { search_term: query, signed_in: !!isSignedIn });

    // Search is unlimited for all users (semantic search = $0/query).
    // Only Pro users get the AI-powered Sherpa Analysis narrative.

    setSmartSearchLoading(true);
    setSmartSearchResponse(null);
    setSmartSearchExplanation(null);
    setCompareSelection([]);
    // Clear detail views so search results can display
    setSelectedCounterpart(null);
    setSelectedDeal(null);

    try {
      // Semantic search for everyone — instant, $0/query.
      // AI Sherpa Analysis is a Pro-only enhancement loaded AFTER results.
      const searchEndpoint = '/api/semantic-search';
      const response = await authFetch(searchEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, userId: user?.id })
      });

      // Handle 429 — search limit reached (signed-in monthly limit or anonymous daily limit)
      if (response.status === 429) {
        const limitData = await response.json();
        setSmartSearchResponse({
          limitReached: true,
          used: limitData.used,
          limit: limitData.limit,
          isAnonymousLimit: limitData.isAnonymousLimit || false,
        });
        setSmartSearchLoading(false);
        return;
      }

      const result = await response.json();

      // Track searches remaining (non-Pro users only; admins/Pro get no value)
      if (result.searchesRemaining !== undefined) {
        setSearchesRemaining(result.searchesRemaining);
      } else {
        setSearchesRemaining(null);
      }

      if (result.success) {
        if (activeTab === 'counterparts') {
          // Counterparts tab: filter the list in-place (no overlay)
          const cpIds = (result.counterparts || []).map(cp => cp.id).filter(Boolean);
          setSmartSearchCpIds(cpIds);
          setSmartSearchQuery(query);
          setSmartSearchDeals(result.deals || []);
          setCounterpartFilters(prev => ({
            ...prev,
            counterpartTypes: [],
            technologies: [],
            regions: [],
            stages: []
          }));
        } else if (activeTab === 'deals') {
          // Deals tab: filter the deal list in-place (no overlay)
          const dealIds = (result.deals || []).map(d => d.id).filter(Boolean);
          setSmartSearchDealIds(dealIds);
          setSmartSearchDealQuery(query);
          setSmartSearchDealCps(result.counterparts || []);
          setScreenerFilters({
            sectors: [],
            geographies: [],
            dealTypes: [],
            statuses: [],
            counterparts: [],
            timePeriod: 'all'
          });
        } else {
          // Other tabs (including insights): set response + query for feed filtering
          setSmartSearchResponse(result);
          setSmartSearchQuery(query);
        }
      }
    } catch (error) {
      console.error('Smart search error:', error);
    }

    // Update recent searches (all tabs)
    setRecentSearches(prev => {
      const updated = [query, ...prev.filter(q => q !== query)].slice(0, 8);
      try { localStorage.setItem('sherpa_recent_searches', JSON.stringify(updated)); } catch {}
      return updated;
    });

    setSmartSearchLoading(false);
  };

  // Simple markdown renderer for bold text (used by SmartSearchResults)
  const renderSimpleMarkdown = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
        : part
    );
  };

  // Deep-link handler for ?q= URLs (shared search results)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q || isLoading) return;
    setHeroSearch(q);
    performSmartSearch(q);
    params.delete('q');
    const clean = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.replaceState({}, '', clean);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link handler for ?matchup= URLs (shared matchup comparisons)
  useEffect(() => {
    if (matchupPreselected.length >= 2 && !isLoading) {
      setActiveTab('matchup');
    }
  }, [matchupPreselected, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const logActivity = useCallback((type, targetType, targetId, description, metadata = {}) => {
    const newActivity = {
      id: Date.now(),
      type, // 'call', 'email', 'meeting', 'note', 'status_change', 'created', 'document_added'
      targetType, // 'project', 'counterpart'
      targetId,
      description,
      metadata,
      timestamp: new Date().toISOString(),
    };
    setActivities(prev => [newActivity, ...prev]);
    
    // Update lastActivity on target
    if (targetType === 'project') {
      setProjects(prev => prev.map(p => 
        p.id === targetId ? { ...p, lastActivity: new Date().toISOString().split('T')[0] } : p
      ));
    } else if (targetType === 'counterpart') {
      setCounterparts(prev => prev.map(c => 
        c.id === targetId ? { ...c, lastInteraction: new Date().toISOString().split('T')[0] } : c
      ));
    }
  }, []);

  // Get activities for a specific item
  const getActivitiesFor = useCallback((targetType, targetId) => {
    return activities.filter(a => a.targetType === targetType && a.targetId === targetId);
  }, [activities]);

  // ============ TASK MANAGEMENT ============
  const addTask = useCallback((task) => {
    const newTask = {
      id: Date.now(),
      ...task,
      completed: false,
      createdAt: new Date().toISOString(),
    };
    setTasks(prev => [...prev, newTask]);
    return newTask;
  }, []);

  const updateTask = useCallback((taskId, updates) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
  }, []);

  const deleteTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }, []);

  const toggleTaskComplete = useCallback((taskId) => {
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { 
        ...t, 
        status: t.status === 'done' ? 'pending' : 'done',
        completed: t.status !== 'done',
        completedAt: t.status !== 'done' ? new Date().toISOString() : null 
      } : t
    ));
  }, []);

  const moveTask = useCallback((taskId, direction) => {
    setTasks(prev => {
      const index = prev.findIndex(t => t.id === taskId);
      if (index === -1) return prev;
      if (direction === 'up' && index === 0) return prev;
      if (direction === 'down' && index === prev.length - 1) return prev;
      
      const newTasks = [...prev];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      [newTasks[index], newTasks[swapIndex]] = [newTasks[swapIndex], newTasks[index]];
      return newTasks;
    });
  }, []);

  const handleTaskDragStart = useCallback((e, taskId) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleTaskDragOver = useCallback((e, taskId) => {
    e.preventDefault();
    if (taskId !== draggedTaskId) {
      setDragOverTaskId(taskId);
    }
  }, [draggedTaskId]);

  const handleTaskDrop = useCallback((e, targetTaskId) => {
    e.preventDefault();
    if (!draggedTaskId || draggedTaskId === targetTaskId) {
      setDraggedTaskId(null);
      setDragOverTaskId(null);
      return;
    }
    
    setTasks(prev => {
      const draggedIndex = prev.findIndex(t => t.id === draggedTaskId);
      const targetIndex = prev.findIndex(t => t.id === targetTaskId);
      if (draggedIndex === -1 || targetIndex === -1) return prev;
      
      const newTasks = [...prev];
      const [draggedTask] = newTasks.splice(draggedIndex, 1);
      newTasks.splice(targetIndex, 0, draggedTask);
      return newTasks;
    });
    
    setDraggedTaskId(null);
    setDragOverTaskId(null);
  }, [draggedTaskId]);

  const handleTaskDragEnd = useCallback(() => {
    setDraggedTaskId(null);
    setDragOverTaskId(null);
  }, []);

  // Get tasks for specific item or all
  const getTasksFor = useCallback((targetType, targetId) => {
    if (!targetType) return tasks;
    return tasks.filter(t => t.targetType === targetType && t.targetId === targetId);
  }, [tasks]);

  // Upcoming/overdue tasks
  const taskStats = useMemo(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const pending = tasks.filter(t => !t.completed);
    const overdue = pending.filter(t => t.dueDate && t.dueDate < today);
    const dueToday = pending.filter(t => t.dueDate === today);
    const dueThisWeek = pending.filter(t => t.dueDate && t.dueDate > today && t.dueDate <= nextWeek); // excludes today to avoid duplicates
    const dueThisWeekIncludingToday = pending.filter(t => t.dueDate && t.dueDate >= today && t.dueDate <= nextWeek); // for count display
    const completed = tasks.filter(t => t.completed);
    
    return { pending, overdue, dueToday, dueThisWeek, dueThisWeekIncludingToday, completed, total: tasks.length };
  }, [tasks]);

  // Reset pagination when filters change
  useEffect(() => { setProjectsPage(1); }, [projectFilters, dashboardFilter, searchQuery]);
  useEffect(() => { setCounterpartsPage(1); }, [counterpartFilters, counterpartTagFilter, searchQuery, smartSearchCpIds]);
  useEffect(() => { setDocumentsPage(1); }, [searchQuery]);
  useEffect(() => { setTasksPage(1); }, [searchQuery, taskStatusFilter, taskPriorityFilter, taskLinkFilter, tasksPerPage]);

  // ============ FAVORITES & RECENT ============
  const toggleFavorite = useCallback((type, id) => {
    setFavorites(prev => {
      const key = type === 'project' ? 'projects' : 'counterparts';
      const current = prev[key] || [];
      if (current.includes(id)) {
        return { ...prev, [key]: current.filter(x => x !== id) };
      } else {
        return { ...prev, [key]: [...current, id] };
      }
    });
  }, []);

  const isFavorite = useCallback((type, id) => {
    const key = type === 'project' ? 'projects' : 'counterparts';
    return (favorites[key] || []).includes(id);
  }, [favorites]);

  const trackRecentItem = useCallback((type, id) => {
    setRecentItems(prev => {
      const filtered = prev.filter(item => !(item.type === type && item.id === id));
      return [{ type, id, timestamp: new Date().toISOString() }, ...filtered].slice(0, 20);
    });
  }, []);

  // Get recent items with full data
  const recentItemsWithData = useMemo(() => {
    return recentItems.map(item => {
      if (item.type === 'project') {
        const project = projects.find(p => p.id === item.id);
        return project ? { ...item, data: project } : null;
      } else {
        const counterpart = counterparts.find(c => c.id === item.id);
        return counterpart ? { ...item, data: counterpart } : null;
      }
    }).filter(Boolean).slice(0, 10);
  }, [recentItems, projects, counterparts]);

  // ============ SMART MATCHING ============
  const findMatchingCounterparts = useCallback((project) => {
    if (!project) return [];
    
    return counterparts
      .filter(c => c.investmentPreferences)
      .map(c => {
        const prefs = c.investmentPreferences;
        let score = 0;
        let reasons = [];
        
        // Capacity match
        if (prefs.minCapacity && prefs.maxCapacity) {
          if (project.capacity >= prefs.minCapacity && project.capacity <= prefs.maxCapacity) {
            score += 30;
            reasons.push('Capacity in range');
          }
        } else if (prefs.minCapacity && project.capacity >= prefs.minCapacity) {
          score += 20;
          reasons.push('Meets min capacity');
        }
        
        // Technology match
        if (prefs.technologies?.length > 0) {
          if (prefs.technologies.includes(project.technology)) {
            score += 30;
            reasons.push(`Interested in ${project.technology}`);
          }
        }
        
        // Stage match
        if (prefs.stages?.length > 0) {
          if (prefs.stages.includes(project.stage)) {
            score += 20;
            reasons.push(`Targets ${project.stage}`);
          }
        }
        
        // Region match
        if (prefs.regions?.length > 0) {
          const projectRegion = regions.find(r => project.location?.toLowerCase().includes(r.toLowerCase()));
          if (projectRegion && prefs.regions.includes(projectRegion)) {
            score += 20;
            reasons.push(`Active in ${projectRegion}`);
          }
        }
        
        // Return match if excluded from project already
        const alreadyLinked = project.counterpartIds?.includes(c.id);
        
        return { counterpart: c, score, reasons, alreadyLinked };
      })
      .filter(m => m.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [counterparts, regions]);


  // Search-filtered base data for stats
  // Filter projects by current sector
  const sectorProjects = useMemo(() => {
    // If "All Industries" is selected, show all projects
    if (sectorConfig.id === 'all') return projects;
    return projects.filter(p => !p.sectorId || p.sectorId === sectorConfig.id);
  }, [projects, sectorConfig.id]);

  const otherSectorProjectCount = projects.length - sectorProjects.length;

  const searchFilteredProjects = useMemo(() => {
    if (!searchQuery) return sectorProjects;
    const q = searchQuery.toLowerCase();
    return sectorProjects.filter(p => 
      p.name.toLowerCase().includes(q) || 
      (Array.isArray(p.technology) ? p.technology.join(' ') : (p.technology || '')).toLowerCase().includes(q) ||
      p.location.toLowerCase().includes(q) ||
      p.counterpartIds.some(id => getCounterpartName(id).toLowerCase().includes(q))
    );
  }, [sectorProjects, searchQuery, counterparts]);

  // Computed stats (based on search when active)
  const stats = useMemo(() => {
    const baseProjects = searchFilteredProjects;
    const liveProjects = baseProjects.filter(p => p.isLive);
    const totalCapacity = liveProjects.reduce((sum, p) => sum + p.capacity, 0);
    const totalInvestment = liveProjects.reduce((sum, p) => sum + p.investmentSize, 0);
    const avgReturn = liveProjects.length > 0 ? liveProjects.reduce((sum, p) => sum + (p.expectedReturn || 0), 0) / liveProjects.length : 0;
    const needsAttention = baseProjects.filter(p => p.isLive && (isStale(p.lastActivity) || (p.deadline && new Date(p.deadline) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))));
    
    const byTechnology = {};
    const byRegion = {};
    const byStage = {};
    const byStatus = {};
    const capacityByTech = {};
    const capacityByRegion = {};
    const investmentByRegion = {};
    
    // Extract country from location (first part before comma, or the whole thing)
    const getCountry = (location) => {
      if (!location) return 'Unknown';
      const parts = location.split(',').map(s => s.trim());
      return parts[parts.length - 1] || parts[0] || 'Unknown';
    };
    
    liveProjects.forEach(p => {
      const techs = Array.isArray(p.technology) ? p.technology : [p.technology];
      techs.filter(Boolean).forEach(t => {
        byTechnology[t] = (byTechnology[t] || 0) + 1;
        capacityByTech[t] = (capacityByTech[t] || 0) + p.capacity;
      });
      
      const country = getCountry(p.location);
      byRegion[country] = (byRegion[country] || 0) + 1;
      capacityByRegion[country] = (capacityByRegion[country] || 0) + p.capacity;
      investmentByRegion[country] = (investmentByRegion[country] || 0) + p.investmentSize;
      
      byStage[p.stage] = (byStage[p.stage] || 0) + 1;
      byStatus[p.pipelineStatus] = (byStatus[p.pipelineStatus] || 0) + 1;
    });
    
    // Get unique geographies for filtering (from sector projects, not filtered)
    const geographies = [...new Set(sectorProjects.map(p => getCountry(p.location)))].filter(Boolean).sort();
    
    return { 
      liveProjects: liveProjects.length, 
      totalCapacity, 
      totalInvestment, 
      avgReturn,
      needsAttention: needsAttention.length, 
      byTechnology, 
      byRegion, 
      byStage, 
      byStatus,
      capacityByTech,
      capacityByRegion,
      investmentByRegion,
      geographies
    };
  }, [searchFilteredProjects, sectorProjects]);
  
  // Extract country from location
  const getCountry = (location) => {
    if (!location) return 'Unknown';
    const parts = location.split(',').map(s => s.trim());
    return parts[parts.length - 1] || parts[0] || 'Unknown';
  };
  
  const filteredProjects = useMemo(() => {
    let filtered = sectorProjects;
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(q) || 
        (Array.isArray(p.technology) ? p.technology.join(' ') : (p.technology || '')).toLowerCase().includes(q) ||
        p.location.toLowerCase().includes(q) ||
        p.counterpartIds.some(id => getCounterpartName(id).toLowerCase().includes(q))
      );
    }
    
    // Dashboard quick filter
    if (dashboardFilter !== "all") {
      if (dashboardFilter === "live") filtered = filtered.filter(p => p.isLive);
      if (dashboardFilter === "stale") filtered = filtered.filter(p => p.isLive && isStale(p.lastActivity));
      if (dashboardFilter === "deadline") filtered = filtered.filter(p => p.deadline && new Date(p.deadline) < new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
    }
    
    // Advanced filters
    if (projectFilters.technology !== "all") {
      filtered = filtered.filter(p => {
        const techs = Array.isArray(p.technology) ? p.technology : [p.technology];
        return techs.includes(projectFilters.technology);
      });
    }
    if (projectFilters.geography !== "all") {
      filtered = filtered.filter(p => getCountry(p.location) === projectFilters.geography);
    }
    if (projectFilters.counterpart !== "all") {
      filtered = filtered.filter(p => p.counterpartIds.includes(Number(projectFilters.counterpart)));
    }
    if (projectFilters.status !== "all") {
      filtered = filtered.filter(p => p.pipelineStatus === projectFilters.status);
    }
    
    // Sorting
    filtered = [...filtered].sort((a, b) => {
      let aVal, bVal;
      switch (projectSort.field) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "capacity":
          aVal = a.capacity;
          bVal = b.capacity;
          break;
        case "investment":
          aVal = a.investmentSize;
          bVal = b.investmentSize;
          break;
        case "geography":
          aVal = getCountry(a.location).toLowerCase();
          bVal = getCountry(b.location).toLowerCase();
          break;
        case "technology":
          aVal = (Array.isArray(a.technology) ? a.technology.join(', ') : (a.technology || '')).toLowerCase();
          bVal = (Array.isArray(b.technology) ? b.technology.join(', ') : (b.technology || '')).toLowerCase();
          break;
        case "lastActivity":
        default:
          aVal = new Date(a.lastActivity || 0);
          bVal = new Date(b.lastActivity || 0);
          break;
      }
      if (aVal < bVal) return projectSort.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return projectSort.direction === "asc" ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [sectorProjects, searchQuery, dashboardFilter, projectFilters, projectSort, counterparts]);

  // Helper functions for extracting investment preferences (used in counterpart filtering and display)
  const getDimValues = (prefs, ...keys) => {
    for (const key of keys) {
      const dim = prefs?.dimensions?.find(d => d.key === key);
      if (dim?.values?.length) return dim.values.map(v => v.value || v.region || v);
    }
    return [];
  };

  const getDimRange = (prefs, ...keys) => {
    for (const key of keys) {
      const dim = prefs?.dimensions?.find(d => d.key === key);
      if (dim?.range) return dim.range;
    }
    return {};
  };

  // Derive filter options from ALL counterparts for the advanced screener filters
  const screenerFilterOptions = useMemo(() => ({
    technologies: [...new Set(counterparts.flatMap(c => getDimValues(c.investmentPreferences, 'technologies', 'sectors', 'assetClasses')))].filter(Boolean).sort(),
    regions: [...new Set(counterparts.flatMap(c => getDimValues(c.investmentPreferences, 'geographies')))].filter(Boolean).sort(),
    stages: [...new Set(counterparts.flatMap(c => getDimValues(c.investmentPreferences, 'dealStage', 'stage')))].filter(Boolean).sort(),
  }), [counterparts]);

  const filteredCounterparts = useMemo(() => {
    // Use server-side results when taxonomy filters are active
    if (screenerResults.hasActiveFilters && !screenerResults.loading) {
      let filtered = screenerResults.counterparts;

      // Smart search ID filter — narrow to search-matched counterparts
      if (smartSearchCpIds !== null) {
        const idSet = new Set(smartSearchCpIds);
        filtered = filtered.filter(c => idSet.has(c.id));
      }

      // Apply non-taxonomy filters (type, category, search) on top of server results
      if (counterpartFilters.type && counterpartFilters.type !== "all") {
        filtered = filtered.filter(c => c.type === counterpartFilters.type);
      }

      // Apply sorting
      filtered = [...filtered].sort((a, b) => {
        let aVal, bVal;
        switch (counterpartSort.field) {
          case "name":
            aVal = (a.name || '').toLowerCase();
            bVal = (b.name || '').toLowerCase();
            break;
          case "deals":
            aVal = a.classifications?.technologies?.length || 0;
            bVal = b.classifications?.technologies?.length || 0;
            break;
          case "newest":
            aVal = new Date(a.created_at || 0);
            bVal = new Date(b.created_at || 0);
            break;
          default:
            aVal = (a.name || '').toLowerCase();
            bVal = (b.name || '').toLowerCase();
        }
        if (aVal < bVal) return counterpartSort.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return counterpartSort.direction === "asc" ? 1 : -1;
        return 0;
      });

      return filtered;
    }

    // Fall back to client-side filtering when no taxonomy filters active
    let filtered = counterparts;

    // Smart search ID filter — narrow to search-matched counterparts
    if (smartSearchCpIds !== null) {
      const idSet = new Set(smartSearchCpIds);
      filtered = filtered.filter(c => idSet.has(c.id));
    }

    // Role grouping helper - maps roles to categories with fuzzy matching (MULTILINGUAL)
    const getRoleGroup = (role) => {
      if (!role) return 'Unknown';
      const r = role.toLowerCase();
      
      // EXECUTIVE / Leadership - Check FIRST (most specific)
      // English
      if (r.includes('ceo') || r.includes('chief executive') || r.includes('managing director') || 
          r.includes('partner') || r.includes('founder') || r.includes('president') ||
          r.includes('chairman') || r.includes('board member') || r.includes('director general') ||
          r.includes('general manager') || r.includes('managing partner') || r.includes('head of') ||
          r.includes('vice president') || r.includes(' vp ') || r.includes('principal') ||
          // German
          r.includes('geschäftsführer') || r.includes('geschaeftsfuehrer') || r.includes('geschäftsleiter') ||
          r.includes('vorstand') || r.includes('vorstandsvorsitz') || r.includes('generaldirektor') ||
          r.includes('gesellschafter') || r.includes('inhaber') || r.includes('gründer') ||
          // Spanish
          r.includes('director general') || r.includes('gerente general') || r.includes('socio director') ||
          r.includes('presidente') || r.includes('fundador') || r.includes('consejero delegado') ||
          // French
          r.includes('directeur général') || r.includes('directeur general') || r.includes('pdg') ||
          r.includes('gérant') || r.includes('associé') || r.includes('fondateur') ||
          // Portuguese
          r.includes('diretor geral') || r.includes('diretor executivo') || r.includes('sócio') ||
          // Italian
          r.includes('amministratore delegato') || r.includes('direttore generale') || 
          r.includes('amministratore unico') || r.includes('socio')) return 'Executive';
      
      // Investment / Portfolio
      if (r.includes('invest') || r.includes('portfolio') || r.includes('asset manage') || 
          r.includes('fund manager') || r.includes('capital') ||
          // German
          r.includes('anlage') || r.includes('vermögen') ||
          // Spanish/Portuguese/Italian
          r.includes('inversión') || r.includes('investimento') || r.includes('gestora')) return 'Investment';
      
      // Business Development
      if (r.includes('business develop') || r.includes('bd ') || r.includes('commercial') || 
          r.includes('sales') || r.includes('account manager') || r.includes('client') ||
          // German
          r.includes('geschäftsentwicklung') || r.includes('vertrieb') || r.includes('verkauf') ||
          // Spanish
          r.includes('desarrollo de negocio') || r.includes('comercial') || r.includes('ventas') ||
          // French
          r.includes('développement commercial') ||
          // Portuguese
          r.includes('desenvolvimento de negócios')) return 'Business Development';
      
      // M&A / Corporate Finance
      if (r.includes('m&a') || r.includes('merger') || r.includes('acquisition') || 
          r.includes('corporate finance') || r.includes('transaction') ||
          // German
          r.includes('übernahme') || r.includes('fusion') ||
          // Spanish
          r.includes('fusiones') || r.includes('adquisiciones')) return 'M&A';
      
      // Project / Development
      if (r.includes('project manager') || r.includes('project director') || r.includes('construction') ||
          r.includes('origination') || r.includes('development manager') ||
          // German
          r.includes('projektleiter') || r.includes('projektmanager') || r.includes('bauleiter') ||
          // Spanish
          r.includes('jefe de proyecto') || r.includes('director de proyecto') ||
          // French
          r.includes('chef de projet') || r.includes('directeur de projet')) return 'Project Development';
      
      // Finance / Accounting
      if (r.includes('cfo') || r.includes('chief financial') || r.includes('finance director') || 
          r.includes('controller') || r.includes('treasurer') || r.includes('accountant') ||
          r.includes('financial controller') ||
          // German
          r.includes('finanzleiter') || r.includes('finanzdirektor') || r.includes('buchhalter') ||
          r.includes('leiter finanzen') ||
          // Spanish
          r.includes('director financiero') || r.includes('contador') || r.includes('tesorero') ||
          // French
          r.includes('directeur financier') || r.includes('comptable') ||
          // Italian
          r.includes('direttore finanziario')) return 'Finance';
      
      // Legal / Compliance
      if (r.includes('legal') || r.includes('counsel') || r.includes('compliance') || 
          r.includes('lawyer') || r.includes('attorney') || r.includes('solicitor') ||
          // German
          r.includes('rechtsanwalt') || r.includes('anwalt') || r.includes('jurist') || r.includes('syndikus') ||
          // Spanish
          r.includes('abogado') || r.includes('asesor legal') || r.includes('jurídico') ||
          // French
          r.includes('avocat') || r.includes('juridique') || r.includes('juriste') ||
          // Portuguese
          r.includes('advogado') ||
          // Italian
          r.includes('avvocato') || r.includes('legale')) return 'Legal';
      
      // Technical / Engineering
      if (r.includes('cto') || r.includes('engineer') || r.includes('technical') || 
          r.includes('technology') || r.includes('architect') || r.includes('developer') ||
          // German
          r.includes('ingenieur') || r.includes('techniker') || r.includes('entwickler') ||
          r.includes('technischer leiter') ||
          // Spanish
          r.includes('ingeniero') || r.includes('técnico') ||
          // French
          r.includes('ingénieur') || r.includes('technicien') ||
          // Portuguese
          r.includes('engenheiro') ||
          // Italian
          r.includes('ingegnere')) return 'Technical';
      
      // Operations
      if (r.includes('coo') || r.includes('operation') || r.includes('procurement') ||
          r.includes('supply chain') || r.includes('logistics') ||
          // German
          r.includes('betriebsleiter') || r.includes('einkauf') || r.includes('logistik') ||
          // Spanish
          r.includes('operaciones') || r.includes('logística') || r.includes('compras')) return 'Operations';
      
      // HR / Talent - Check BEFORE Advisor to catch "Recruitment Consultant"
      if (r.includes('human resource') || r.includes('talent acqui') || r.includes('talent manager') ||
          r.includes('people operation') || r.includes('hr director') || r.includes('hr manager') ||
          r.includes('head of people') || r.includes('chief people') || r.includes('head of hr') || 
          r.includes('head of talent') || r.includes('hrbp') || r.includes('hr business partner') ||
          r.includes('recruitment') || r.includes('recruiter') || r.includes('recruiting') ||
          r.includes('staffing') || r.includes('headhunter') || r.includes('executive search') ||
          r.includes('talent partner') || r.includes('people partner') || r.includes('people & culture') ||
          r.includes('workforce') || r.includes('employee relation') || r.includes('compensation') ||
          r.includes('learning & development') || r.includes('l&d') || r.includes('training manager') ||
          r.includes('organizational development') || r.includes('org development') ||
          // German
          r.includes('personalleiter') || r.includes('personaldirektor') || r.includes('personalwesen') ||
          r.includes('rekrutierung') || r.includes('personalabteilung') || r.includes('personalreferent') ||
          // Spanish
          r.includes('recursos humanos') || r.includes('director de personal') || r.includes('reclutamiento') ||
          r.includes('selección') || r.includes('seleccion') || r.includes('talento humano') ||
          // French
          r.includes('ressources humaines') || r.includes('directeur rh') || r.includes('recrutement') ||
          r.includes('responsable rh') || r.includes('chargé de recrutement') ||
          // Portuguese
          r.includes('recursos humanos') || r.includes('recrutamento') || r.includes('seleção') ||
          // Italian
          r.includes('risorse umane') || r.includes('selezione del personale')) return 'HR';
      
      // Marketing / Communications
      if (r.includes('marketing') || r.includes('communication') || r.includes('public relation') ||
          r.includes('brand') || r.includes('media') ||
          // German
          r.includes('kommunikation') || r.includes('öffentlichkeitsarbeit') ||
          // Spanish
          r.includes('mercadeo') || r.includes('comunicación')) return 'Marketing';
      
      // Analyst / Research
      if (r.includes('analyst') || r.includes('research') || r.includes('associate') ||
          // German
          r.includes('analytiker') || r.includes('analyst') ||
          // Spanish
          r.includes('analista') || r.includes('investigador')) return 'Analyst';
      
      // Advisor / Consultant - Check LAST as it's broad
      if (r.includes('advisor') || r.includes('adviser') || r.includes('consultant') || r.includes('advisory') ||
          // German
          r.includes('berater') || r.includes('beratung') ||
          // Spanish
          r.includes('asesor') || r.includes('consultor') || r.includes('consejero') ||
          // French
          r.includes('conseiller') || r.includes('consultant') ||
          // Portuguese
          r.includes('assessor') || r.includes('consultor') ||
          // Italian
          r.includes('consulente') || r.includes('consigliere')) return 'Advisor';
      
      return 'Other';
    };
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(q) || 
        c.category.toLowerCase().includes(q) ||
        c.location?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.role?.toLowerCase().includes(q)
      );
    }
    
    // Type filter
    if (counterpartFilters.type !== "all") {
      filtered = filtered.filter(c => c.type === counterpartFilters.type);
    }
    
    // Category filter
    if (counterpartFilters.category !== "all") {
      filtered = filtered.filter(c => c.category === counterpartFilters.category);
    }
    
    // Role filter (fuzzy grouped)
    if (counterpartFilters.role !== "all") {
      filtered = filtered.filter(c => getRoleGroup(c.role) === counterpartFilters.role);
    }
    
    // Tag filter
    if (counterpartTagFilter !== "all") {
      filtered = filtered.filter(c => c.tags?.includes(counterpartTagFilter));
    }

    // Advanced screener filters - map taxonomy IDs to labels for comparison
    // Get label lookup maps from taxonomyFilters
    const techIdToLabel = Object.fromEntries(
      (taxonomyFilters.technologies || []).map(t => [t.id, t.label])
    );
    const geoIdToLabel = Object.fromEntries(
      (taxonomyFilters.geographies || []).map(g => [g.id, g.label])
    );
    const stageIdToLabel = Object.fromEntries(
      (taxonomyFilters.stages || []).map(s => [s.id, s.label])
    );

    if (counterpartFilters.technologies?.length > 0) {
      filtered = filtered.filter(c => {
        const techs = getDimValues(c.investmentPreferences, 'technologies', 'sectors', 'assetClasses')
          .map(t => t.toLowerCase());
        // Get selected filter labels
        const selectedLabels = counterpartFilters.technologies
          .map(id => techIdToLabel[id]?.toLowerCase())
          .filter(Boolean);
        // Also try matching by ID directly (for legacy string-based filters)
        return selectedLabels.some(label => techs.some(t => t.includes(label) || label.includes(t))) ||
               counterpartFilters.technologies.some(f => techs.includes(f.toLowerCase?.() || f));
      });
    }

    if (counterpartFilters.regions?.length > 0) {
      filtered = filtered.filter(c => {
        const regions = getDimValues(c.investmentPreferences, 'geographies')
          .map(r => r.toLowerCase());
        // Also include headquarters country for matching
        const hqCountry = (c.investmentPreferences?.headquarters?.country || c.headquarters?.country || c.country || '').toLowerCase().trim();
        if (hqCountry && !regions.includes(hqCountry)) {
          regions.push(hqCountry);
        }
        // Get selected filter labels (from taxonomy IDs or direct values for HQ-derived filters)
        const selectedLabels = counterpartFilters.regions
          .map(id => {
            // Try taxonomy lookup first, then use the ID itself as a label (for hq_ prefixed values)
            const label = geoIdToLabel[id];
            if (label) return label.toLowerCase();
            // For hq_ prefixed values from CounterpartFilterSidebar, strip prefix
            if (typeof id === 'string' && id.startsWith('hq_')) return id.slice(3);
            return (typeof id === 'string' ? id : '').toLowerCase();
          })
          .filter(Boolean);
        return selectedLabels.some(label => regions.some(r => r.includes(label) || label.includes(r))) ||
               counterpartFilters.regions.some(f => regions.includes((typeof f === 'string' ? f : '').toLowerCase()));
      });
    }

    if (counterpartFilters.stages?.length > 0) {
      filtered = filtered.filter(c => {
        const stages = getDimValues(c.investmentPreferences, 'dealStage', 'stage')
          .map(s => s.toLowerCase());
        // Get selected filter labels
        const selectedLabels = counterpartFilters.stages
          .map(id => stageIdToLabel[id]?.toLowerCase())
          .filter(Boolean);
        return selectedLabels.some(label => stages.some(s => s.includes(label) || label.includes(s))) ||
               counterpartFilters.stages.some(f => stages.includes(f.toLowerCase?.() || f));
      });
    }

    if (counterpartFilters.irrMin || counterpartFilters.irrMax) {
      filtered = filtered.filter(c => {
        const irr = getDimRange(c.investmentPreferences, 'targetReturn', 'irr');
        const irrVal = irr.min || irr.sweetSpot || null;
        if (counterpartFilters.irrMin && (!irrVal || irrVal < parseFloat(counterpartFilters.irrMin))) return false;
        if (counterpartFilters.irrMax && (!irrVal || irrVal > parseFloat(counterpartFilters.irrMax))) return false;
        return true;
      });
    }

    // Sorting
    filtered = [...filtered].sort((a, b) => {
      let aVal, bVal;
      switch (counterpartSort.field) {
        case "name":
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case "deals":
          // Sort by number of linked DB deals
          aVal = deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === a.id)).length;
          bVal = deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === b.id)).length;
          break;
        case "newest":
          aVal = new Date(a.createdAt || 0);
          bVal = new Date(b.createdAt || 0);
          break;
        case "category":
          aVal = (a.category || '').toLowerCase();
          bVal = (b.category || '').toLowerCase();
          break;
        case "role":
          aVal = getRoleGroup(a.role);
          bVal = getRoleGroup(b.role);
          break;
        case "company":
          aVal = (a.company || '').toLowerCase();
          bVal = (b.company || '').toLowerCase();
          break;
        case "projects":
          aVal = projects.filter(p => p.counterpartIds.includes(a.id)).length;
          bVal = projects.filter(p => p.counterpartIds.includes(b.id)).length;
          break;
        case "lastInteraction":
        default:
          aVal = new Date(a.lastInteraction || 0);
          bVal = new Date(b.lastInteraction || 0);
          break;
      }
      if (aVal < bVal) return counterpartSort.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return counterpartSort.direction === "asc" ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [counterparts, searchQuery, counterpartFilters, counterpartTagFilter, counterpartSort, projects, taxonomyFilters, screenerResults, smartSearchCpIds]);

  // displayedCounterparts removed — now using filteredCounterparts directly

  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return documents;
    const q = searchQuery.toLowerCase();
    return documents.filter(d => 
      d.name.toLowerCase().includes(q) || 
      d.category?.toLowerCase().includes(q) ||
      projects.find(p => p.id === d.projectId)?.name.toLowerCase().includes(q) ||
      d.counterpartIds?.some(id => getCounterpartName(id).toLowerCase().includes(q))
    );
  }, [documents, searchQuery, projects, counterparts]);

  // Get documents for a counterpart (through their associated projects OR directly linked)
  const getCounterpartDocuments = (counterpartId) => {
    const relatedProjectIds = projects
      .filter(p => p.counterpartIds.includes(counterpartId))
      .map(p => p.id);
    return documents.filter(d => 
      relatedProjectIds.includes(d.projectId) || 
      (d.counterpartIds && d.counterpartIds.includes(counterpartId))
    );
  };

  // Pagination Component
  const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange, itemName = "items" }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    // Tier 0 (unregistered): locked to page 1
    const tier0Locked = !isSignedIn && currentPage === 1;

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const getPageNumbers = () => {
      const pages = [];
      const showPages = 5;
      let start = Math.max(1, currentPage - Math.floor(showPages / 2));
      let end = Math.min(totalPages, start + showPages - 1);
      if (end - start + 1 < showPages) {
        start = Math.max(1, end - showPages + 1);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    };

    // Gate page navigation for non-signed-in users (content is clipped anyway)
    const handlePageChange = (page) => {
      if (!isSignedIn && page > 1) {
        openSignUp();
        return;
      }
      onPageChange(page);
    };

    return (
      <div className="flex items-center justify-between py-4 border-t border-slate-200">
        <p className="text-sm text-slate-600">
          Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> {itemName}
          {!isSignedIn && totalPages > 1 && (
            <span className="ml-2 text-blue-700 font-medium">· Sign up to see more</span>
          )}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="px-2 py-1 text-sm rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ««
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ‹ Prev
          </button>
          {getPageNumbers().map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              disabled={!isSignedIn && page > 1}
              className={`px-3 py-1 text-sm rounded-lg ${
                currentPage === page
                  ? 'bg-blue-700 text-white'
                  : !isSignedIn && page > 1
                    ? 'text-slate-400 cursor-not-allowed'
                    : 'hover:bg-slate-100 text-slate-700'
              }`}
            >
              {!isSignedIn && page > 1 ? <Lock size={12} className="inline" /> : page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || (!isSignedIn && currentPage >= 1)}
            className="px-3 py-1 text-sm rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || (!isSignedIn && currentPage >= 1)}
            className="px-2 py-1 text-sm rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            »»
          </button>
        </div>
      </div>
    );
  };

  // Delete Confirmation Modal
  const DeleteConfirmModal = ({ type, item, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete {type}?</h3>
        <p className="text-slate-600 mb-4">
          Are you sure you want to delete <span className="font-medium">"{item.name}"</span>? 
          {type === "project" && " All associated documents will also be deleted."}
          {type === "counterpart" && " This will remove them from all associated projects."}
          This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-600 text-slate-900 rounded-xl hover:bg-red-700">
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  // ============ DEALS TAB COMPONENT ============
  const DealsTab = ({ deals, counterpartName, projects }) => {
    const [expandedDeal, setExpandedDeal] = useState(null);
    const [filterYear, setFilterYear] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [filterRegion, setFilterRegion] = useState('all');
    const [filterTechnology, setFilterTechnology] = useState('all');
    const [sortOrder, setSortOrder] = useState('newest');
    
    if (!deals || deals.length === 0) {
      return (
        <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-8 text-center`}>
          <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <TrendingUp size={28} className={themeClasses.textMuted} />
          </div>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-2`}>No deal history yet</h3>
          <p className={`${themeClasses.textMuted} mb-4`}>No deal history yet. Add deals from the Deals tab.</p>
        </div>
      );
    }

    // Extract unique values for filters
    const years = [...new Set(deals.map(d => d.date?.match(/\d{4}/)?.[0]).filter(Boolean))].sort((a, b) => b - a);
    const types = [...new Set(deals.map(d => d.type).filter(Boolean))];
    
    // Extract regions (country from location)
    const regions = [...new Set(deals.map(d => {
      if (!d.location) return null;
      const parts = d.location.split(',');
      return parts[parts.length - 1]?.trim() || d.location;
    }).filter(Boolean))].sort();
    
    // Extract technologies from description
    const extractTechnology = (text) => {
      if (!text) return null;
      const lowerText = text.toLowerCase();
      if (lowerText.includes('solar') || lowerText.includes('pv')) return 'Solar';
      if (lowerText.includes('wind')) return 'Wind';
      if (lowerText.includes('battery') || lowerText.includes('storage') || lowerText.includes('bess')) return 'Storage';
      if (lowerText.includes('hydro')) return 'Hydro';
      if (lowerText.includes('transmission') || lowerText.includes('grid')) return 'Transmission';
      if (lowerText.includes('biomass')) return 'Biomass';
      if (lowerText.includes('geothermal')) return 'Geothermal';
      return null;
    };
    const technologies = [...new Set(deals.map(d => extractTechnology(d.description)).filter(Boolean))].sort();
    
    // Apply filters
    const filteredDeals = deals.filter(d => {
      if (filterYear !== 'all' && !d.date?.includes(filterYear)) return false;
      if (filterType !== 'all' && d.type !== filterType) return false;
      if (filterRegion !== 'all') {
        const dealRegion = d.location?.split(',').pop()?.trim();
        if (dealRegion !== filterRegion) return false;
      }
      if (filterTechnology !== 'all') {
        const dealTech = extractTechnology(d.description);
        if (dealTech !== filterTechnology) return false;
      }
      return true;
    });
    
    // Sort deals
    const sortedDeals = [...filteredDeals].sort((a, b) => {
      const yearA = a.date?.match(/\d{4}/)?.[0] || '0';
      const yearB = b.date?.match(/\d{4}/)?.[0] || '0';
      if (sortOrder === 'newest') return yearB.localeCompare(yearA);
      if (sortOrder === 'oldest') return yearA.localeCompare(yearB);
      return 0;
    });

    // === PROJECT MATCHING ===
    const findMatchingProjects = (deal) => {
      if (!projects || projects.length === 0) return [];
      
      const matches = [];
      const dealText = `${deal.description || ''} ${deal.location || ''} ${deal.capacity || ''}`.toLowerCase();
      
      projects.forEach(project => {
        let score = 0;
        const reasons = [];
        
        // Check technology match
        const projectTech = (project.technology || '').toLowerCase();
        if (projectTech && dealText.includes(projectTech)) {
          score += 40;
          reasons.push('Same technology');
        } else if (
          (dealText.includes('solar') && projectTech.includes('solar')) ||
          (dealText.includes('wind') && projectTech.includes('wind')) ||
          (dealText.includes('bess') && (projectTech.includes('bess') || projectTech.includes('battery') || projectTech.includes('storage'))) ||
          (dealText.includes('hydro') && projectTech.includes('hydro')) ||
          (dealText.includes('transmission') && projectTech.includes('transmission'))
        ) {
          score += 40;
          reasons.push('Same technology');
        }
        
        // Check location match
        const projectLocation = (project.location || '').toLowerCase();
        const dealLocation = (deal.location || '').toLowerCase();
        if (projectLocation && dealLocation) {
          // Check country/region match
          const projectCountry = projectLocation.split(',').pop()?.trim();
          const dealCountry = dealLocation.split(',').pop()?.trim();
          if (projectCountry && dealCountry && (
            projectCountry.includes(dealCountry) || dealCountry.includes(projectCountry) ||
            projectLocation.includes(dealCountry) || dealLocation.includes(projectCountry)
          )) {
            score += 35;
            reasons.push('Same region');
          }
        }
        
        // Check capacity similarity (within 50% range)
        const dealCapMatch = (deal.capacity || '').match(/([\d,]+\.?\d*)/);
        const projectCap = parseFloat(project.capacity) || 0;
        if (dealCapMatch && projectCap > 0) {
          const dealCap = parseFloat(dealCapMatch[1].replace(/,/g, ''));
          if (dealCap > 0 && Math.abs(dealCap - projectCap) / Math.max(dealCap, projectCap) < 0.5) {
            score += 25;
            reasons.push('Similar size');
          }
        }
        
        if (score >= 35) {
          matches.push({
            project,
            score,
            reasons,
            confidence: score >= 65 ? 'high' : score >= 50 ? 'medium' : 'low'
          });
        }
      });
      
      return matches.sort((a, b) => b.score - a.score).slice(0, 2);
    };

    const getDealTypeLabel = (type) => {
      if (!type) return 'Deal';
      if (type.includes('acqui')) return 'Acquisition';
      if (type.includes('sale') || type.includes('divest')) return 'Divestiture';
      if (type.includes('invest')) return 'Investment';
      if (type.includes('partner') || type.includes('jv')) return 'Partnership';
      if (type.includes('launch')) return 'Launch';
      return capAcronyms(type.charAt(0).toUpperCase() + type.slice(1));
    };

    const getIcon = (type) => {
      if (type?.includes('acqui') || type?.includes('purchase')) return TrendingUp;
      if (type?.includes('sale') || type?.includes('divest')) return ArrowUpRight;
      return TrendingUp;
    };
    
    // Check if any filters are active
    const hasActiveFilters = filterYear !== 'all' || filterType !== 'all' || filterRegion !== 'all' || filterTechnology !== 'all';
    const clearAllFilters = () => {
      setFilterYear('all');
      setFilterType('all');
      setFilterRegion('all');
      setFilterTechnology('all');
    };

    return (
      <div className="space-y-6">
        {/* Header & Filters */}
        <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`font-semibold ${themeClasses.text}`}>Deal History</h3>
              <p className={`text-sm ${themeClasses.textMuted}`}>
                {sortedDeals.length === deals.length
                  ? `${deals.length} transaction${deals.length !== 1 ? 's' : ''} on record`
                  : `${sortedDeals.length} of ${deals.length} shown`
                }
              </p>
            </div>
            {/* Sort Order */}
            <select 
              value={sortOrder} 
              onChange={(e) => setSortOrder(e.target.value)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
          
          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Year Filter */}
            {years.length > 0 && (
              <select 
                value={filterYear} 
                onChange={(e) => setFilterYear(e.target.value)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${filterYear !== 'all' ? 'border-blue-700 bg-blue-50 dark:bg-blue-700/10' : themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
              >
                <option value="all">All Years</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {/* Type Filter */}
            {types.length > 0 && (
              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${filterType !== 'all' ? 'border-blue-700 bg-blue-50 dark:bg-blue-700/10' : themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
              >
                <option value="all">All Types</option>
                {types.map(t => <option key={t} value={t}>{getDealTypeLabel(t)}</option>)}
              </select>
            )}
            {/* Region Filter */}
            {regions.length > 0 && (
              <select 
                value={filterRegion} 
                onChange={(e) => setFilterRegion(e.target.value)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${filterRegion !== 'all' ? 'border-blue-700 bg-blue-50 dark:bg-blue-700/10' : themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
              >
                <option value="all">All Regions</option>
                {regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
            {/* Technology Filter */}
            {technologies.length > 0 && (
              <select 
                value={filterTechnology} 
                onChange={(e) => setFilterTechnology(e.target.value)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${filterTechnology !== 'all' ? 'border-blue-700 bg-blue-50 dark:bg-blue-700/10' : themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
              >
                <option value="all">All Technologies</option>
                {technologies.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {/* Clear Filters */}
            {hasActiveFilters && (
              <button 
                onClick={clearAllFilters}
                className="px-3 py-1.5 text-sm text-blue-800 hover:text-blue-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Deals List */}
        <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-6`}>
          <div className={`divide-y ${themeClasses.border}`}>
            {sortedDeals.map((deal, i) => {
              const Icon = getIcon(deal.type);
              const isExpanded = expandedDeal === i;
              const matchingProjects = findMatchingProjects(deal);
              const dealTechArray = Array.isArray(deal.technology) ? deal.technology.filter(Boolean) : deal.technology ? [deal.technology] : [];
              const dealTech = dealTechArray[0] || extractTechnology(deal.description);
              
              return (
                <div key={i} className="py-4 first:pt-0 last:pb-0">
                  <button
                    onClick={() => setExpandedDeal(isExpanded ? null : i)}
                    className={`w-full flex items-start gap-4 text-left ${themeClasses.cardHover} rounded-lg p-2 -m-2`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-100'}`}>
                      <Icon size={18} className="text-blue-800" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${themeClasses.text} ${isExpanded ? '' : 'line-clamp-1'}`}>{deal.description}</p>
                      <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-sm ${themeClasses.textMuted}`}>
                        {deal.location && <span className="flex items-center gap-1"><MapPin size={12} /> {deal.location}</span>}
                        {safeFormatCapacity(deal.capacity, deal.capacity_unit) && <span>• {safeFormatCapacity(deal.capacity, deal.capacity_unit)}</span>}
                        {dealTechArray.length > 0
                          ? dealTechArray.map(t => <span key={t} className={`px-1.5 py-0.5 rounded text-xs ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-100'}`}>{t}</span>)
                          : dealTech && <span className={`px-1.5 py-0.5 rounded text-xs ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-100'}`}>{dealTech}</span>
                        }
                        {safeFormatValue(deal.value || deal.deal_value) && <span className="text-blue-800 font-medium">• {safeFormatValue(deal.value || deal.deal_value)}</span>}
                        {/* Source link inline */}
                        {deal.source && (
                          <a 
                            href={deal.source} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 text-blue-800 hover:text-blue-700"
                          >
                            <ExternalLink size={12} /> Source
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-sm ${themeClasses.textMuted}`}>{deal.date}</span>
                      <ChevronDown size={16} className={`${themeClasses.textMuted} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  
                  {/* Expanded Details - Full Deal Info (matches dashboard view) */}
                  {isExpanded && (
                    <div className={`mt-4 ml-14 p-4 rounded-xl ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'} border ${themeClasses.border}`}>

                      {/* Headline if different from description */}
                      {deal.headline && deal.headline !== deal.description && (
                        <h3 className={`text-base font-semibold ${themeClasses.text} mb-3`}>{deal.headline}</h3>
                      )}

                      {/* Status & Type Badges */}
                      <div className="flex items-center gap-2 mb-4 flex-wrap">
                        {deal.status && (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            deal.status === 'closed' ? 'bg-green-100 text-green-700' :
                            deal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            deal.status === 'announced' ? 'bg-purple-100 text-purple-700' :
                            deal.status === 'tender' ? 'bg-amber-100 text-amber-700' :
                            deal.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {deal.status.charAt(0).toUpperCase() + deal.status.slice(1).replace('_', ' ')}
                          </span>
                        )}
                        {(deal.dealType || deal.deal_type || deal.type) && (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                            {getDealTypeLabel(deal.dealType || deal.deal_type || deal.type)}
                          </span>
                        )}
                        {(Array.isArray(deal.technology) ? deal.technology : [deal.technology])
                          .filter(Boolean).map(t => (
                            <span key={t} className={`text-xs px-2 py-1 rounded-full font-medium bg-amber-100 text-amber-700`}>
                              {t}
                            </span>
                          ))}
                        {deal.stage && (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium bg-indigo-100 text-indigo-700`}>
                            {deal.stage}
                          </span>
                        )}
                      </div>

                      {/* Key Metrics Row */}
                      {((deal.deal_value || deal.financials?.dealValue || deal.capacity || deal.number_of_assets || deal.value)) && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                          {(deal.deal_value || deal.financials?.dealValue || deal.value) && (
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs ${themeClasses.textMuted}`}>Deal Size</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>
                                {safeFormatValue(deal.deal_value || deal.financials?.dealValue || deal.value)}
                              </p>
                            </div>
                          )}
                          {deal.capacity && (
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs ${themeClasses.textMuted}`}>Capacity</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>
                                {typeof deal.capacity === 'string' ? deal.capacity : `${deal.capacity} ${deal.capacity_unit || deal.capacityUnit || 'MW'}`}
                              </p>
                            </div>
                          )}
                          {deal.number_of_assets && (
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs ${themeClasses.textMuted}`}># Assets</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.number_of_assets}</p>
                            </div>
                          )}
                          {deal.annual_generation && (
                            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs ${themeClasses.textMuted}`}>Annual Gen.</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.annual_generation}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Financials Section */}
                      {deal.financials && typeof deal.financials === 'object' && Object.keys(deal.financials).length > 0 && (
                        <div className="mb-4">
                          <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                            <DollarSign size={12} /> Financials
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {deal.financials.enterpriseValue && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Enterprise Value</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{safeFormatValue(deal.financials.enterpriseValue)}</p>
                              </div>
                            )}
                            {deal.financials.equityValue && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Equity Value</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{safeFormatValue(deal.financials.equityValue)}</p>
                              </div>
                            )}
                            {deal.financials.debt && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Debt</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{safeFormatValue(deal.financials.debt)}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Timeline Section */}
                      {(deal.announcement_date || deal.signing_date || deal.expected_closing || deal.actual_closing || deal.cod) && (
                        <div className="mb-4">
                          <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                            <Clock size={12} /> Timeline
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {deal.announcement_date && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                <span className={`text-xs ${themeClasses.text}`}>Announced: {deal.announcement_date}</span>
                              </div>
                            )}
                            {deal.signing_date && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                <span className={`text-xs ${themeClasses.text}`}>Signed: {deal.signing_date}</span>
                              </div>
                            )}
                            {deal.expected_closing && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                                <span className={`text-xs ${themeClasses.text}`}>Expected Close: {deal.expected_closing}</span>
                              </div>
                            )}
                            {deal.actual_closing && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className={`text-xs ${themeClasses.text}`}>Closed: {deal.actual_closing}</span>
                              </div>
                            )}
                            {deal.cod && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                                <span className={`text-xs ${themeClasses.text}`}>COD: {deal.cod}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Geography Section */}
                      {((Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).length > 0 || deal.region || deal.state || deal.city || deal.project_site || deal.project_name) && (
                        <div className="mb-4">
                          <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                            <MapPin size={12} /> Location
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {deal.project_name && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Project</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.project_name}</p>
                              </div>
                            )}
                            {(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).length > 0 && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Country</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).join(', ')}</p>
                              </div>
                            )}
                            {deal.region && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Region</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.region}</p>
                              </div>
                            )}
                            {deal.state && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>State</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.state}</p>
                              </div>
                            )}
                            {deal.city && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>City</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.city}</p>
                              </div>
                            )}
                            {deal.project_site && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Site</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.project_site}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* PPA Details */}
                      {deal.ppa_info && typeof deal.ppa_info === 'object' && Object.keys(deal.ppa_info).length > 0 && (
                        <div className="mb-4">
                          <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                            <FileText size={12} /> PPA Details
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {deal.ppa_info.offtaker && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Offtaker</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.ppa_info.offtaker}</p>
                              </div>
                            )}
                            {deal.ppa_info.term && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Term</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.ppa_info.term}</p>
                              </div>
                            )}
                            {deal.ppa_info.price && (
                              <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <p className={`text-xs ${themeClasses.textMuted}`}>Price</p>
                                <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.ppa_info.price}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Key People Section */}
                      {Array.isArray(deal.people) && deal.people.length > 0 && (
                        <div className="mb-4">
                          <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                            <Users size={12} /> Key People
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {deal.people.map((person, idx) => (
                              <div key={idx} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <div className={`w-6 h-6 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-white'} flex items-center justify-center`}>
                                  <User size={12} className="text-blue-700" />
                                </div>
                                <div>
                                  <p className={`text-xs font-medium ${themeClasses.text}`}>{person.name}</p>
                                  {(person.title || person.company) && <p className={`text-[10px] ${themeClasses.textMuted}`}>{person.title || person.role}{person.company ? ` · ${person.company}` : ''}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notes/Description */}
                      {(deal.notes || deal.description) && (
                        <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'} mb-4`}>
                          <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Notes</p>
                          <p className={`text-sm ${themeClasses.text}`}>{deal.notes || deal.description}</p>
                        </div>
                      )}

                      {/* Source Link */}
                      {(deal.source || deal.source_url || deal.sourceUrl) && (
                        <a
                          href={deal.source || deal.source_url || deal.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-800 hover:text-blue-700 flex items-center gap-1 mb-4"
                        >
                          <ExternalLink size={14} /> {deal.source_title || 'View source'}
                        </a>
                      )}

                      {/* View Deal Page Link */}
                      {deal.id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); viewDeal(deal); }}
                          className="text-sm text-blue-800 hover:text-blue-700 font-medium flex items-center gap-1 mb-4"
                        >
                          View Deal <ChevronRight size={14} />
                        </button>
                      )}

                      {/* Counterparts Section */}
                      {deal.deal_counterparts && deal.deal_counterparts.length > 0 && (
                        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                          <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2`}>
                            Counterparts ({deal.deal_counterparts.length})
                          </p>
                          <div className="space-y-2">
                            {deal.deal_counterparts.map((dc, idx) => {
                              const fullCp = counterparts.find(c => c.id === (dc.counterpart?.id || dc.counterpartId))
                                || (dc.counterpart?.name && counterparts.find(c => c.name?.toLowerCase() === dc.counterpart.name.toLowerCase()));
                              const cpData = fullCp || dc.counterpart || dc;
                              return (
                              <button
                                key={idx}
                                onClick={(e) => { e.stopPropagation(); viewPanelCounterpart(cpData); }}
                                className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${theme === 'dark' ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-slate-600' : 'bg-white'}`}>
                                    <Building2 size={14} className="text-blue-700" />
                                  </div>
                                  <div>
                                    <p className={`text-sm font-medium ${themeClasses.text}`}>{cpData.name || dc.name}</p>
                                    <p className={`text-xs ${themeClasses.textMuted}`}>{cpData.type || 'company'}</p>
                                  </div>
                                </div>
                                {dc.role && (
                                  <span className={`text-xs px-2 py-1 rounded-lg ${theme === 'dark' ? 'bg-slate-600' : 'bg-white'} ${themeClasses.textMuted}`}>
                                    {dc.role}
                                  </span>
                                )}
                              </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Matching Projects Section (for enrichment deals without full data) */}
                      {matchingProjects.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                          <p className={`text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wide mb-2`}>Similar to your projects</p>
                          <div className="space-y-2">
                            {matchingProjects.map((match, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProject(match.project);
                                }}
                                className={`w-full p-2 rounded-lg border ${themeClasses.border} ${themeClasses.cardHover} flex items-center gap-3 text-left`}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-100'}`}>
                                  <FolderOpen size={14} className="text-blue-800" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{match.project.name}</p>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>{match.reasons.join(' · ')}</p>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  match.confidence === 'high'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-700/20 dark:text-blue-800'
                                    : match.confidence === 'medium'
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                                    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                }`}>
                                  {match.score}%
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {sortedDeals.length === 0 && (
            <div className="py-8 text-center">
              <p className={themeClasses.textMuted}>No deals match the selected filters</p>
              <button 
                onClick={clearAllFilters}
                className="mt-2 text-sm text-blue-800 hover:text-blue-700 font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============ ACTIVITY TIMELINE COMPONENT ============
  const ActivityTimeline = ({ targetType, targetId, limit = 10 }) => {
    const itemActivities = getActivitiesFor(targetType, targetId).slice(0, limit);
    const [expandedId, setExpandedId] = useState(null);
    
    const getActivityIcon = (type) => {
      switch(type) {
        case 'call': return <PhoneCall size={14} />;
        case 'email': return <Mail size={14} />;
        case 'meeting': return <Users size={14} />;
        case 'video': return <Video size={14} />;
        case 'note': return <FileText size={14} />;
        case 'status_change': return <ArrowRight size={14} />;
        case 'created': return <Plus size={14} />;
        case 'document_added': return <Upload size={14} />;
        default: return <Activity size={14} />;
      }
    };

    const getActivityLabel = (type) => {
      switch(type) {
        case 'call': return 'Call';
        case 'email': return 'Email';
        case 'meeting': return 'Meeting';
        case 'video': return 'Video Call';
        case 'note': return 'Note';
        case 'status_change': return 'Status Change';
        case 'created': return 'Created';
        case 'document_added': return 'Document';
        default: return 'Activity';
      }
    };

    if (itemActivities.length === 0) {
      return (
        <div className="text-center py-6 text-slate-600">
          <Activity size={24} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('noActivityRecorded')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
        {itemActivities.map(activity => {
          const isLong = activity.description && activity.description.length > 150;
          const isExpanded = expandedId === activity.id;
          
          return (
            <div key={activity.id} className={`flex gap-3 text-sm p-3 rounded-lg ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <div className={`w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} flex items-center justify-center ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'} flex-shrink-0`}>
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                    {getActivityLabel(activity.type)}
                  </span>
                  <span className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-500'}`}>
                    {getDaysAgo(activity.timestamp.split('T')[0])}
                  </span>
                </div>
                <p className={`${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'} ${!isExpanded && isLong ? 'line-clamp-3' : ''}`}>
                  {activity.description}
                </p>
                {isLong && (
                  <button 
                    onClick={() => setExpandedId(isExpanded ? null : activity.id)}
                    className="text-xs text-blue-700 hover:text-blue-800 mt-1"
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ============ KANBAN BOARD ============
  const KanbanBoard = () => {
    const columns = pipelineStatuses.filter(s => s !== 'No Deal');
    
    const handleDragStart = (e, project) => {
      e.dataTransfer.setData('projectId', project.id.toString());
    };
    
    const handleDragOver = (e) => {
      e.preventDefault();
    };
    
    const handleDrop = (e, newStatus) => {
      e.preventDefault();
      const projectId = parseInt(e.dataTransfer.getData('projectId'));
      const project = projects.find(p => p.id === projectId);
      if (project && project.pipelineStatus !== newStatus) {
        const oldStatus = project.pipelineStatus;
        setProjects(prev => prev.map(p => 
          p.id === projectId ? { ...p, pipelineStatus: newStatus, lastActivity: new Date().toISOString().split('T')[0] } : p
        ));
        logActivity('status_change', 'project', projectId, `Status changed from ${oldStatus} to ${newStatus}`, { oldStatus, newStatus });
      }
    };

    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map(status => {
          const columnProjects = filteredProjects.filter(p => p.pipelineStatus === status && p.isLive);
          const columnValue = columnProjects.reduce((sum, p) => sum + (p.investmentSize || 0), 0);
          
          return (
            <div key={status} className="flex-shrink-0 w-72"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}>
              <div className="bg-slate-100 rounded-t-lg p-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-slate-900">{status}</h3>
                  <span className="px-2 py-0.5 bg-white rounded-full text-xs text-slate-600">{columnProjects.length}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">{formatCurrency(columnValue)}</p>
              </div>
              
              <div className="bg-slate-100 rounded-b-lg p-2 min-h-[400px] space-y-2">
                {columnProjects.map(project => (
                  <div key={project.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, project)}
                    onClick={() => { setSelectedProject(project); trackRecentItem('project', project.id); }}
                    className="bg-white rounded-xl p-3 shadow-lg border border-slate-200 cursor-pointer hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-slate-900 text-sm">{project.name}</h4>
                      <GripVertical size={14} className="text-slate-600" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
                      <span>{getTechIcon(project.technology, sectorConfig)}</span>
                      <span>{project.capacity} MW</span>
                      <span>•</span>
                      <span>{project.location?.split(',')[0]}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-blue-700">{formatCurrency(project.investmentSize)}</span>
                      {isStale(project.lastActivity) && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{t('stale')}</span>
                      )}
                    </div>
                  </div>
                ))}
                
                {columnProjects.length === 0 && (
                  <div className="text-center py-8 text-slate-600 text-sm">
                    Drop projects here
                  </div>
                )}
              </div>
            </div>
          );
        })}
        
        {/* No Deal column */}
        <div className="flex-shrink-0 w-72">
          <div className="bg-slate-100 rounded-t-lg p-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-700">No Deal</h3>
              <span className="px-2 py-0.5 bg-white rounded-full text-xs text-slate-600">
                {filteredProjects.filter(p => p.pipelineStatus === 'No Deal').length}
              </span>
            </div>
          </div>
          <div className="bg-slate-50/50 rounded-b-lg p-2 min-h-[400px] space-y-2"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'No Deal')}>
            {filteredProjects.filter(p => p.pipelineStatus === 'No Deal').slice(0, 5).map(project => (
              <div key={project.id}
                onClick={() => { setSelectedProject(project); trackRecentItem('project', project.id); }}
                className="bg-white rounded-xl p-3 shadow-lg border border-slate-200 cursor-pointer hover:shadow-md transition-shadow opacity-60">
                <h4 className="font-medium text-slate-900 text-sm">{project.name}</h4>
                <p className="text-xs text-slate-600">{project.technology} • {project.capacity} MW</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============ SMART MATCHING PANEL ============
  const SmartMatchingPanel = ({ project }) => {
    const matches = findMatchingCounterparts(project);
    
    if (matches.length === 0) {
      return (
        <div className="text-center py-4 text-slate-600 text-sm">
          <Sparkles size={20} className="mx-auto mb-2 opacity-50" />
          <p>No matching investors found</p>
          <p className="text-xs">Add investment preferences to counterparts to enable matching</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {matches.slice(0, 5).map(({ counterpart, score, reasons, alreadyLinked }) => (
          <div key={counterpart.id} 
            className={`p-3 rounded-xl border ${alreadyLinked ? 'bg-blue-50 border-green-200' : 'bg-slate-100 border-slate-200'}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                {counterpart.type === 'company' ? <Building2 size={14} className="text-slate-600" /> : <User size={14} className="text-slate-600" />}
                <span className="font-medium text-sm text-slate-900">{counterpart.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  score >= 60 ? 'bg-blue-50 text-green-700' : score >= 40 ? 'bg-blue-50 text-blue-800' : 'bg-slate-100 text-slate-600'
                }`}>
                  {score}% match
                </span>
                {alreadyLinked && <CheckCircle size={14} className="text-green-600" />}
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {reasons.map((r, i) => (
                <span key={i} className="text-xs text-slate-600">{r}{i < reasons.length - 1 ? ' •' : ''}</span>
              ))}
            </div>
            {!alreadyLinked && (
              <button 
                onClick={() => {
                  setProjects(prev => prev.map(p => 
                    p.id === project.id 
                      ? { ...p, counterpartIds: [...(p.counterpartIds || []), counterpart.id] }
                      : p
                  ));
                }}
                className="mt-2 text-xs text-blue-700 hover:text-blue-800 font-medium">
                + Link to project
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Document Upload Component
  const DocumentUpload = ({ projectId, onUpload }) => {
    const fileInputRef = useRef(null);
    const [selectedCategory, setSelectedCategory] = useState("Technical");
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
    const [isDragging, setIsDragging] = useState(false);

    const formatFileSizeLocal = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const extractFileContent = async (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        
        if (file.type.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(file.name)) {
          reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            resolve({ 
              text: '[Image file]', 
              type: 'image', 
              base64, 
              thumbnail: e.target.result
            });
          };
          reader.readAsDataURL(file);
        } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
          reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            resolve({ text: '[PDF Document]', type: 'pdf', base64 });
          };
          reader.readAsDataURL(file);
        } else if (file.name.endsWith('.eml') || file.type === 'message/rfc822') {
          // Parse .eml email files from Apple Mail, Outlook, etc.
          reader.onload = (e) => {
            const content = e.target.result;
            const fromMatch = content.match(/^From:\s*(.+)$/mi);
            const toMatch = content.match(/^To:\s*(.+)$/mi);
            const subjectMatch = content.match(/^Subject:\s*(.+)$/mi);
            const dateMatch = content.match(/^Date:\s*(.+)$/mi);
            
            const bodyStart = content.indexOf('\n\n');
            let body = bodyStart > -1 ? content.substring(bodyStart + 2) : '';
            body = body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            
            const emailInfo = [
              fromMatch ? `From: ${fromMatch[1]}` : '',
              toMatch ? `To: ${toMatch[1]}` : '',
              subjectMatch ? `Subject: ${subjectMatch[1]}` : '',
              dateMatch ? `Date: ${dateMatch[1]}` : '',
              '',
              body.substring(0, 2000)
            ].filter(Boolean).join('\n');
            
            resolve({ text: emailInfo || '[Email file]', type: 'email', base64: btoa(unescape(encodeURIComponent(content))) });
          };
          reader.readAsText(file);
        } else if (file.name.endsWith('.msg')) {
          // .msg files are binary Outlook format
          reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            resolve({ text: '[Outlook Email (.msg)]', type: 'email-msg', base64 });
          };
          reader.readAsDataURL(file);
        } else if (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.csv')) {
          reader.onload = (e) => resolve({ text: e.target.result, type: 'text', base64: btoa(e.target.result) });
          reader.readAsText(file);
        } else {
          reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            resolve({ text: '[Binary file]', type: 'binary', base64 });
          };
          reader.readAsDataURL(file);
        }
      });
    };

    const handleFileSelect = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        await processFiles(files);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const processFiles = async (files) => {
      setUploading(true);
      setUploadProgress({ current: 0, total: files.length, fileName: '' });

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ current: i + 1, total: files.length, fileName: file.name });

        // Extract content for preview
        const content = await extractFileContent(file);
        
        let driveInfo = null;
        
        // Upload to Google Drive if connected
        if (gDriveConnected && googleDriveSync.isConnected()) {
          try {
            driveInfo = await googleDriveSync.uploadFile(file);
          } catch (err) {
            console.error('Drive upload failed:', err);
          }
        }

        const newDoc = {
          id: Date.now() + Math.random(),
          projectId: projectId,
          counterpartIds: [],
          name: file.name,
          category: selectedCategory,
          uploadedAt: new Date().toISOString().split('T')[0],
          size: formatFileSizeLocal(file.size),
          sizeBytes: file.size,
          content: content.text,
          contentType: content.type,
          base64: driveInfo ? null : content.base64,
          thumbnail: content.thumbnail,
          notes: '',
          driveFileId: driveInfo?.driveFileId || null,
          driveWebViewLink: driveInfo?.driveWebViewLink || null,
          driveWebContentLink: driveInfo?.driveWebContentLink || null,
        };
        onUpload(newDoc);
      }

      setUploading(false);
      setUploadProgress({ current: 0, total: 0, fileName: '' });
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        await processFiles(files);
      }
    };

    return (
      <div 
        className={`border-2 border-dashed rounded-xl p-4 transition-colors relative ${
          isDragging ? 'border-blue-700 bg-blue-50' : 'border-slate-200 hover:border-blue-800'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {uploading && (
          <div className="absolute inset-0 bg-white/90 rounded-xl flex items-center justify-center z-10">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-700 rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-slate-700">{uploadProgress.current}/{uploadProgress.total}</p>
              <p className="text-xs text-slate-500 truncate max-w-[150px]">{uploadProgress.fileName}</p>
              {gDriveConnected && <p className="text-xs text-blue-800 mt-1">↑ Google Drive</p>}
            </div>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800 transition-colors text-sm disabled:opacity-50"
          >
            {gDriveConnected ? <Cloud size={16} /> : <Upload size={16} />}
            Upload
          </button>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-700/50"
          >
            {documentCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {gDriveConnected ? (
            <span className="flex items-center gap-1"><Cloud size={12} className="text-blue-700" /> Uploads to Google Drive</span>
          ) : (
            'Drag & drop or click to upload'
          )}
        </p>
      </div>
    );
  };

  // ProjectModal and ProjectDetailPage removed - counterparts-only platform

  // Relationship Map Component - Visual network of counterpart connections
  const RelationshipMap = ({ centerCounterpart, relationships, onSelectCounterpart, onClose }) => {
    const [hoveredNode, setHoveredNode] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'list'

    if (!centerCounterpart || !relationships || relationships.length === 0) return null;

    // Role colors
    const roleColors = {
      lender: { bg: '#059669', text: 'Lender' },
      investor: { bg: '#0891b2', text: 'Investor' },
      advisor: { bg: '#7c3aed', text: 'Advisor' },
      advisor_buy: { bg: '#7c3aed', text: 'Advisor' },
      advisor_sell: { bg: '#7c3aed', text: 'Advisor' },
      legal: { bg: '#6366f1', text: 'Legal' },
      legal_buy: { bg: '#6366f1', text: 'Legal' },
      legal_sell: { bg: '#6366f1', text: 'Legal' },
      developer: { bg: '#ea580c', text: 'Developer' },
      buyer: { bg: '#0284c7', text: 'Buyer' },
      seller: { bg: '#dc2626', text: 'Seller' },
      offtaker: { bg: '#16a34a', text: 'Offtaker' },
      epc: { bg: '#ca8a04', text: 'EPC' },
      supplier: { bg: '#9333ea', text: 'Supplier' },
      partner: { bg: '#2563eb', text: 'Partner' },
      other: { bg: '#64748b', text: 'Other' }
    };

    const getPrimaryRole = (roles) => {
      if (!roles || roles.size === 0) return 'other';
      const roleArray = Array.from(roles);
      // Priority order for display
      const priority = ['lender', 'investor', 'advisor', 'legal', 'developer', 'buyer', 'seller', 'offtaker', 'epc', 'supplier'];
      for (const r of priority) {
        if (roleArray.some(role => role?.includes(r))) return r;
      }
      return roleArray[0] || 'other';
    };

    const getRoleColor = (roles) => {
      const primary = getPrimaryRole(roles);
      return roleColors[primary] || roleColors.other;
    };

    // Get favicon for a counterpart
    const getFavicon = (cp) => {
      const website = cp?.websiteUrl || cp?.website || cp?.investmentPreferences?.website || cp?.enrichment?.website;
      if (!website) return null;
      const domain = website.replace(/^https?:\/\//, '').split('/')[0];
      if (!domain) return null;
      return `https://cdn.brandfetch.io/${domain}/icon?c=1idrTt0p2-SJacppl92`;
    };

    // Graph layout calculations
    const centerX = 300;
    const centerY = 250;
    const maxNodes = 15;
    const displayRels = relationships.slice(0, maxNodes);
    const maxDeals = Math.max(...displayRels.map(r => r.dealCount), 1);

    // Adaptive radius based on node count
    const baseRadius = displayRels.length <= 6 ? 140 : displayRels.length <= 10 ? 160 : 180;

    const getPosition = (index, total) => {
      const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
      // Slight variation in radius for visual interest
      const radiusVariation = (index % 2 === 0) ? 0 : 15;
      const r = baseRadius + radiusVariation;
      return {
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle)
      };
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <div
          className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {getFavicon(centerCounterpart) && (
                <img src={getFavicon(centerCounterpart)} alt="" className="w-10 h-10 rounded-lg" />
              )}
              <div>
                <h2 className={`text-xl font-bold ${themeClasses.text}`}>{centerCounterpart.name}</h2>
                <p className={`text-sm ${themeClasses.textMuted}`}>
                  {relationships.length} deal partner{relationships.length !== 1 ? 's' : ''} • {relationships.reduce((sum, r) => sum + r.dealCount, 0)} shared deals
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* View mode toggle */}
              <div className={`flex rounded-lg border ${themeClasses.border} p-0.5`}>
                <button
                  onClick={() => setViewMode('graph')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'graph' ? 'bg-blue-600 text-white' : themeClasses.textMuted}`}
                >
                  Graph
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white' : themeClasses.textMuted}`}
                >
                  List
                </button>
              </div>
              <button onClick={onClose} className={`p-2 rounded-lg ${themeClasses.cardHover}`}>
                <X size={20} className={themeClasses.textMuted} />
              </button>
            </div>
          </div>

          {/* Graph View */}
          {viewMode === 'graph' && (
            <div className="flex-1 min-h-0 flex">
              {/* SVG Graph */}
              <div className="flex-1 relative">
                <svg width="100%" height="450" viewBox="0 0 600 500" className="mx-auto">
                  <defs>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                      <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                    </filter>
                  </defs>

                  {/* Connection Lines */}
                  {displayRels.map((rel, idx) => {
                    const pos = getPosition(idx, displayRels.length);
                    const lineWidth = 1.5 + (rel.dealCount / maxDeals) * 3;
                    const isHovered = hoveredNode === idx || selectedNode === idx;
                    const roleColor = getRoleColor(rel.roles);
                    return (
                      <line
                        key={`line-${idx}`}
                        x1={centerX}
                        y1={centerY}
                        x2={pos.x}
                        y2={pos.y}
                        stroke={isHovered ? roleColor.bg : (theme === 'dark' ? '#475569' : '#cbd5e1')}
                        strokeWidth={isHovered ? lineWidth + 1 : lineWidth}
                        strokeOpacity={isHovered ? 1 : 0.6}
                        className="transition-all duration-200"
                      />
                    );
                  })}

                  {/* Center Node */}
                  <g filter="url(#glow)">
                    <circle cx={centerX} cy={centerY} r={45} fill={theme === 'dark' ? '#1e40af' : '#2563eb'} />
                    {getFavicon(centerCounterpart) ? (
                      <image
                        href={getFavicon(centerCounterpart)}
                        x={centerX - 18}
                        y={centerY - 18}
                        width="36"
                        height="36"
                        clipPath="circle(18px)"
                      />
                    ) : (
                      <text x={centerX} y={centerY + 5} textAnchor="middle" fill="white" fontSize="16" fontWeight="700">
                        {centerCounterpart.name.slice(0, 2).toUpperCase()}
                      </text>
                    )}
                  </g>

                  {/* Partner Nodes */}
                  {displayRels.map((rel, idx) => {
                    const pos = getPosition(idx, displayRels.length);
                    const baseSize = 28 + (rel.dealCount / maxDeals) * 12;
                    const isHovered = hoveredNode === idx;
                    const isSelected = selectedNode === idx;
                    const nodeSize = (isHovered || isSelected) ? baseSize + 4 : baseSize;
                    const roleColor = getRoleColor(rel.roles);
                    const favicon = getFavicon(rel);

                    return (
                      <g
                        key={`node-${idx}`}
                        className="cursor-pointer"
                        onMouseEnter={() => setHoveredNode(idx)}
                        onMouseLeave={() => setHoveredNode(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNode(selectedNode === idx ? null : idx);
                        }}
                        onDoubleClick={() => {
                          onSelectCounterpart(rel);
                          onClose();
                        }}
                      >
                        {/* Node circle */}
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r={nodeSize}
                          fill={theme === 'dark' ? '#1e293b' : '#ffffff'}
                          stroke={isHovered || isSelected ? roleColor.bg : (theme === 'dark' ? '#475569' : '#e2e8f0')}
                          strokeWidth={isHovered || isSelected ? 3 : 2}
                          className="transition-all duration-200"
                          filter={isHovered || isSelected ? 'url(#glow)' : ''}
                        />

                        {/* Role indicator dot */}
                        <circle
                          cx={pos.x + nodeSize - 6}
                          cy={pos.y - nodeSize + 6}
                          r={6}
                          fill={roleColor.bg}
                        />

                        {/* Favicon or initials */}
                        {favicon ? (
                          <image
                            href={favicon}
                            x={pos.x - 12}
                            y={pos.y - 12}
                            width="24"
                            height="24"
                            clipPath="circle(12px)"
                          />
                        ) : (
                          <text
                            x={pos.x}
                            y={pos.y + 4}
                            textAnchor="middle"
                            fill={theme === 'dark' ? '#94a3b8' : '#64748b'}
                            fontSize="11"
                            fontWeight="600"
                          >
                            {rel.name.slice(0, 2).toUpperCase()}
                          </text>
                        )}

                        {/* Deal count badge */}
                        {rel.dealCount > 1 && (
                          <>
                            <circle
                              cx={pos.x - nodeSize + 8}
                              cy={pos.y + nodeSize - 8}
                              r={9}
                              fill={theme === 'dark' ? '#0f172a' : '#f8fafc'}
                              stroke={theme === 'dark' ? '#334155' : '#e2e8f0'}
                              strokeWidth="1"
                            />
                            <text
                              x={pos.x - nodeSize + 8}
                              y={pos.y + nodeSize - 5}
                              textAnchor="middle"
                              fill={theme === 'dark' ? '#94a3b8' : '#64748b'}
                              fontSize="9"
                              fontWeight="600"
                            >
                              {rel.dealCount}
                            </text>
                          </>
                        )}
                      </g>
                    );
                  })}

                  {/* Hover tooltip */}
                  {hoveredNode !== null && (
                    <g>
                      <rect
                        x={getPosition(hoveredNode, displayRels.length).x - 80}
                        y={getPosition(hoveredNode, displayRels.length).y - 70}
                        width="160"
                        height="40"
                        rx="8"
                        fill={theme === 'dark' ? '#1e293b' : '#ffffff'}
                        stroke={theme === 'dark' ? '#334155' : '#e2e8f0'}
                        strokeWidth="1"
                        filter="drop-shadow(0 4px 6px rgba(0,0,0,0.1))"
                      />
                      <text
                        x={getPosition(hoveredNode, displayRels.length).x}
                        y={getPosition(hoveredNode, displayRels.length).y - 52}
                        textAnchor="middle"
                        fill={theme === 'dark' ? '#f1f5f9' : '#1e293b'}
                        fontSize="11"
                        fontWeight="600"
                      >
                        {displayRels[hoveredNode].name.length > 22
                          ? displayRels[hoveredNode].name.slice(0, 22) + '...'
                          : displayRels[hoveredNode].name}
                      </text>
                      <text
                        x={getPosition(hoveredNode, displayRels.length).x}
                        y={getPosition(hoveredNode, displayRels.length).y - 38}
                        textAnchor="middle"
                        fill={theme === 'dark' ? '#94a3b8' : '#64748b'}
                        fontSize="10"
                      >
                        {displayRels[hoveredNode].dealCount} deal{displayRels[hoveredNode].dealCount !== 1 ? 's' : ''} • {getRoleColor(displayRels[hoveredNode].roles).text}
                      </text>
                    </g>
                  )}
                </svg>

                {relationships.length > maxNodes && (
                  <p className={`text-center text-xs ${themeClasses.textMuted} -mt-2`}>
                    Showing top {maxNodes} of {relationships.length} partners • Switch to List view for all
                  </p>
                )}
              </div>

              {/* Selected node detail panel */}
              {selectedNode !== null && (
                <div className={`w-64 border-l ${themeClasses.border} pl-4 ml-4 overflow-y-auto`}>
                  <div className="flex items-center gap-2 mb-3">
                    {getFavicon(displayRels[selectedNode]) && (
                      <img src={getFavicon(displayRels[selectedNode])} alt="" className="w-8 h-8 rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold ${themeClasses.text} text-sm truncate`}>
                        {displayRels[selectedNode].name}
                      </h3>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: getRoleColor(displayRels[selectedNode].roles).bg }}
                      >
                        {getRoleColor(displayRels[selectedNode].roles).text}
                      </span>
                    </div>
                  </div>
                  <p className={`text-xs ${themeClasses.textMuted} mb-3`}>
                    {displayRels[selectedNode].dealCount} shared deal{displayRels[selectedNode].dealCount !== 1 ? 's' : ''}
                  </p>
                  <div className="space-y-2">
                    {displayRels[selectedNode].deals?.slice(0, 5).map((deal, i) => (
                      <div key={i} className={`text-xs ${themeClasses.textMuted} p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'}`}>
                        {deal?.length > 60 ? deal.slice(0, 60) + '...' : deal}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => { onSelectCounterpart(displayRels[selectedNode]); onClose(); }}
                    className="mt-3 w-full text-xs font-medium text-blue-600 hover:text-blue-700 py-2"
                  >
                    View Full Profile →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="flex-1 overflow-y-auto">
              <div className="grid gap-2">
                {relationships.map((rel, idx) => {
                  const roleColor = getRoleColor(rel.roles);
                  const favicon = getFavicon(rel);
                  return (
                    <div
                      key={idx}
                      onClick={() => { onSelectCounterpart(rel); onClose(); }}
                      className={`flex items-center gap-3 p-3 rounded-xl border ${themeClasses.border} cursor-pointer hover:border-blue-400 transition-colors ${themeClasses.card}`}
                    >
                      {favicon ? (
                        <img src={favicon} alt="" className="w-10 h-10 rounded-lg" />
                      ) : (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'}`}>
                          <span className={`text-sm font-semibold ${themeClasses.textMuted}`}>
                            {rel.name.slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-medium ${themeClasses.text} truncate`}>{rel.name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className="text-xs px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: roleColor.bg }}
                          >
                            {roleColor.text}
                          </span>
                          <span className={`text-xs ${themeClasses.textMuted}`}>
                            {rel.dealCount} deal{rel.dealCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={16} className={themeClasses.textMuted} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className={`mt-4 pt-4 border-t ${themeClasses.border}`}>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {Object.entries(roleColors).slice(0, 8).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: val.bg }}></div>
                  <span className={`text-xs ${themeClasses.textMuted}`}>{val.text}</span>
                </div>
              ))}
            </div>
            {viewMode === 'graph' && (
              <p className={`text-center text-xs ${themeClasses.textMuted} mt-2`}>
                Click node for details • Double-click to view profile
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Inline Counterpart Panel Component (for right column)
  const CounterpartPanel = ({ counterpart, onClose, onViewFull, onEnrich, isAdmin }) => {
    if (!counterpart) return null;

    const prefs = counterpart.investmentPreferences;
    const techDim = prefs?.dimensions?.find(d => d.key === 'technologies' || d.key === 'sectors');
    const geoDim = prefs?.dimensions?.find(d => d.key === 'geographies');

    // Get linked deals for this counterpart
    const linkedDeals = deals.filter(d =>
      d.deal_counterparts?.some(dc => dc.counterpart?.id === counterpart.id) ||
      d.linkedCounterparts?.some(lc => lc.counterpartId === counterpart.id)
    );

    // Get key people — only those belonging to THIS counterpart (not from other orgs in shared deals)
    const cpNameLower = counterpart.name?.toLowerCase() || '';
    const keyPeople = linkedDeals.flatMap(d => d.people || []).filter(person => {
      const org = (person.organization || person.company || '').toLowerCase();
      if (!org) return false;
      return cpNameLower.includes(org) || org.includes(cpNameLower);
    });

    return (
      <div className="flex flex-col h-full bg-[color:var(--gis-bg)]">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <CounterpartAvatar counterpart={counterpart} size="md" className="rounded-xl" />
            <div className="min-w-0">
              <h2 className={`font-bold text-base ${themeClasses.text} truncate`}>{counterpart.name}</h2>
              <p className={`text-[13px] ${themeClasses.textMuted}`}>{counterpart.category || counterpart.type}</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${themeClasses.cardHover} flex-shrink-0`}>
            <X size={18} className={themeClasses.textMuted} />
          </button>
        </div>

        {/* Follow + Email Alert buttons */}
        <div className="px-5 pb-3 flex items-center gap-2 flex-shrink-0">
          <FollowButton targetType="counterpart" targetId={counterpart.id} label={counterpart.name} compact />
          <EmailAlertButton targetType="counterpart" targetId={counterpart.id} label={counterpart.name} compact />
        </div>

        {/* Content */}
        <div className="px-5 pb-4 space-y-4 overflow-y-auto flex-1">
            {/* Summary */}
            <div className={`p-3 rounded-xl bg-[color:var(--gis-panel)] border border-[color:var(--gis-rule)]`}>
              {counterpart._isEnriching ? (
                <div className="flex items-center gap-2">
                  <Loader2 size={14} className="text-blue-700 animate-spin" />
                  <p className={`text-[15px] ${themeClasses.text}`}>Loading...</p>
                </div>
              ) : (() => {
                const rawSummary = counterpart.summary || prefs?.summary || '';
                const summaryIsVague = !rawSummary || /no (information|data|details) (available|found)/i.test(rawSummary) || /unable to (find|determine)/i.test(rawSummary) || /not (enough|sufficient) information/i.test(rawSummary) || /cannot be determined/i.test(rawSummary) || /no publicly available/i.test(rawSummary) || /limited (public )?(information|data)/i.test(rawSummary);
                return (
                  <>
                    <p className={`text-[15px] ${themeClasses.text} leading-relaxed line-clamp-5`}>
                      {summaryIsVague ? (isAdmin ? 'No summary yet. Click Enrich to generate.' : 'No summary available yet.') : rawSummary}
                    </p>
                    {summaryIsVague && isAdmin && (
                      <button
                        onClick={onEnrich}
                        className="mt-3 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-medium flex items-center gap-1.5"
                      >
                        <Sparkles size={12} /> Enrich
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Company Info - Website & HQ */}
            {(() => {
              const hq = (() => {
                const raw = counterpart.headquarters || prefs?.headquarters;
                if (!raw) return null;
                if (typeof raw === 'object') return raw;
                try { return JSON.parse(raw); } catch { return null; }
              })();
              return (counterpart.websiteUrl || counterpart.website || hq || prefs?.website) ? (
              <div className={`p-3 rounded-xl bg-[color:var(--gis-panel)] border border-[color:var(--gis-rule)] space-y-2`}>
                {(counterpart.websiteUrl || counterpart.website || prefs?.website) && (
                  <a
                    href={counterpart.websiteUrl || counterpart.website || prefs?.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[13px] text-blue-600 hover:text-blue-700"
                  >
                    <Globe size={14} />
                    <span className="truncate">{(counterpart.websiteUrl || counterpart.website || prefs?.website).replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                  </a>
                )}
                {hq && (hq.city || hq.country) && (
                  <div className={`flex items-center gap-2 text-[13px] ${themeClasses.textMuted}`}>
                    <MapPin size={14} />
                    <span>
                      {hq.city}
                      {hq.city && hq.country && ', '}
                      {hq.country}
                    </span>
                  </div>
                )}
              </div>
              ) : null;
            })()}

            {/* Quick Stats */}
            {prefs && (
              <div className="grid grid-cols-3 gap-2">
                {techDim?.values?.length > 0 && (
                  <div className={`p-2.5 rounded-lg text-center bg-[color:var(--gis-panel)] border border-[color:var(--gis-rule)]`}>
                    <p className={`text-xl font-bold ${themeClasses.text}`}>{techDim.values.length}</p>
                    <p className={`text-[13px] ${themeClasses.textMuted}`}>Tech</p>
                  </div>
                )}
                {geoDim?.values?.length > 0 && (
                  <div className={`p-2.5 rounded-lg text-center bg-[color:var(--gis-panel)] border border-[color:var(--gis-rule)]`}>
                    <p className={`text-xl font-bold ${themeClasses.text}`}>{geoDim.values.length}</p>
                    <p className={`text-[13px] ${themeClasses.textMuted}`}>Markets</p>
                  </div>
                )}
                <div className={`p-2.5 rounded-lg text-center bg-[color:var(--gis-panel)] border border-[color:var(--gis-rule)]`}>
                  <p className={`text-xl font-bold ${themeClasses.text}`}>{counterpart.deal_count || linkedDeals.length}</p>
                  <p className={`text-[13px] ${themeClasses.textMuted}`}>Deals</p>
                </div>
              </div>
            )}

            {/* Technologies */}
            {techDim?.values?.length > 0 && (
              <div>
                <p className={`text-[11px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>Technologies</p>
                <div className="flex flex-wrap gap-1.5">
                  {techDim.values.slice(0, 5).map((tech, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-lg text-[13px] bg-blue-50 text-blue-700 dark:bg-blue-700/20 dark:text-blue-300">
                      {tech.value || tech}
                    </span>
                  ))}
                  {techDim.values.length > 5 && (
                    <span className={`px-2.5 py-1 rounded-lg text-[13px] ${themeClasses.textMuted}`}>
                      +{techDim.values.length - 5}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Geographies */}
            {geoDim?.values?.length > 0 && (
              <div>
                <p className={`text-[11px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>Markets</p>
                <div className="flex flex-wrap gap-1.5">
                  {geoDim.values.slice(0, 5).map((geo, i) => (
                    <span key={i} className={`px-2.5 py-1 rounded-lg text-[13px] ${'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)]'}`}>
                      {geo.value || geo}
                    </span>
                  ))}
                  {geoDim.values.length > 5 && (
                    <span className={`px-2.5 py-1 rounded-lg text-[13px] ${themeClasses.textMuted}`}>
                      +{geoDim.values.length - 5}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Recent Deals */}
            {linkedDeals.length > 0 && (
              <div>
                <p className={`text-[11px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>Recent Deals</p>
                <div className="space-y-2">
                  {linkedDeals.slice(0, 3).map((deal, i) => (
                    <button key={deal.id || i} type="button" onClick={() => viewPanelDeal(deal)}
                      className={`w-full text-left p-3 rounded-xl bg-[color:var(--gis-panel)] border border-[color:var(--gis-rule)] ${themeClasses.cardHover} transition-colors cursor-pointer`}>
                      <p className={`text-[15px] ${themeClasses.text} line-clamp-2`}>{deal.headline || deal.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).length > 0 && <span className={`text-[13px] ${themeClasses.textMuted}`}>{(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).join(', ')}</span>}
                        {safeFormatCapacity(deal.capacity, deal.capacity_unit) && (
                          <>
                            <span className={`text-[13px] ${themeClasses.textMuted}`}>•</span>
                            <span className={`text-[13px] text-blue-600 font-medium`}>{safeFormatCapacity(deal.capacity, deal.capacity_unit)}</span>
                          </>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Key People */}
            {keyPeople.length > 0 && (
              <div>
                <p className={`text-[11px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>Key People</p>
                <div className="space-y-1">
                  {keyPeople.slice(0, 3).map((person, i) => {
                    const existingPerson = counterparts.find(c =>
                      c.type === 'person' &&
                      c.name?.toLowerCase().trim() === person.name?.toLowerCase().trim()
                    );
                    return (
                      <div
                        key={i}
                        onClick={async () => {
                          if (existingPerson) {
                            setSelectedCounterpart(existingPerson);
                          } else {
                            const newPerson = await createPersonAsCounterpart(person, counterpart?.id);
                            if (newPerson) {
                              setSelectedCounterpart(newPerson);
                            }
                          }
                        }}
                        className="flex items-center gap-2 p-1.5 -mx-1.5 rounded-lg cursor-pointer hover:bg-[color:var(--gis-bg)] dark:hover:bg-[color:var(--gis-bg)] transition-colors"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${'bg-[color:var(--gis-rule)]'}`}>
                          <User size={14} className="text-blue-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${themeClasses.text} truncate hover:text-blue-700`}>{person.name}</p>
                          <p className={`text-xs ${themeClasses.textMuted} truncate`}>{person.title || person.role}{person.company ? ` · ${person.company}` : ''}</p>
                        </div>
                        <ChevronRight size={14} className={`flex-shrink-0 ${themeClasses.textMuted}`} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Similar Companies */}
            {(() => {
              try {
                // Use category field (set by API from categories[0]) for type matching
                // Skip generic types like 'company' or 'person' - only match on real classifications
                const cpType = counterpart.category || counterpart.categories?.[0] || '';
                const cpCountry = (counterpart.investmentPreferences?.headquarters?.country || '').toLowerCase();

                if (!cpType || cpType === 'company' || cpType === 'person' || cpType === 'Other') return null;

                // Helper to get type category for flexible matching
                const getTypeCategory = (type) => {
                  if (!type) return null;
                  const t = type.toLowerCase();
                  if (t.includes('developer') || t.includes('ipp') || t === 'independent power producer') return 'developer';
                  if (t.includes('investor') || t.includes('asset manager') || t.includes('fund') || t.includes('private equity')) return 'investor';
                  if (t.includes('bank') || t.includes('lender') || t.includes('financ')) return 'lender';
                  if (t.includes('utility') || t.includes('offtaker') || t.includes('power company')) return 'utility';
                  if (t.includes('advisor') || t.includes('consultant')) return 'advisor';
                  if (t.includes('law') || t.includes('legal') || t.includes('counsel')) return 'legal';
                  if (t.includes('epc') || t.includes('contractor') || t.includes('construction')) return 'epc';
                  if (t.includes('oem') || t.includes('manufacturer') || t.includes('supplier') || t.includes('equipment')) return 'oem';
                  if (t.includes('insurance') || t.includes('broker')) return 'insurance';
                  return t; // Fall back to exact match
                };

                const cpCategory = getTypeCategory(cpType);

                const similar = (counterparts || [])
                  .filter(c => c && c.id !== counterpart.id && c.type !== 'person')
                  .map(c => {
                    // Skip generic types
                    const cType = c.category || c.categories?.[0] || '';
                    if (!cType || cType === 'company' || cType === 'person' || cType === 'Other') return null;

                    // Match by type category (flexible matching)
                    const cCategory = getTypeCategory(cType);
                    if (cCategory !== cpCategory) return null;

                    // Check if same country
                    const cCountry = (c.investmentPreferences?.headquarters?.country || '').toLowerCase();
                    const sameCountry = cpCountry && cCountry && cpCountry === cCountry;

                    // Get deal count (DB + enrichment)
                    const dealCount = (deals || []).filter(d =>
                      d.deal_counterparts?.some(dc => dc.counterpart?.id === c.id)
                    ).length;

                    return { ...c, _sameCountry: sameCountry, _dealCount: dealCount };
                  })
                  .filter(Boolean)
                  .sort((a, b) => {
                    // Same country first, then by deal count
                    if (a._sameCountry !== b._sameCountry) return b._sameCountry ? 1 : -1;
                    return b._dealCount - a._dealCount;
                  })
                  .slice(0, 4);

                if (similar.length === 0) return null;

              return (
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>Similar Companies</p>
                  <div className="space-y-2">
                    {similar.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => viewPanelCounterpart(c)}
                        className={`w-full p-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.card} ${themeClasses.cardHover} flex items-center gap-2 cursor-pointer transition-colors`}
                      >
                        <CounterpartAvatar counterpart={c} size="sm" className="rounded-lg flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${themeClasses.text} truncate hover:text-blue-700`}>{c.name}</p>
                          <p className={`text-xs ${themeClasses.textMuted} truncate`}>
                            {c._dealCount > 0 ? `${c._dealCount} deal${c._dealCount > 1 ? 's' : ''}` : (c.category || c.categories?.[0] || c.type)}
                          </p>
                        </div>
                        <ChevronRight size={14} className={`flex-shrink-0 ${themeClasses.textMuted}`} />
                      </div>
                    ))}
                  </div>
                </div>
              );
              } catch (e) {
                console.error('Similar Companies error:', e);
                return null;
              }
            })()}
          </div>

          {/* Footer — matches DealPanel */}
          <div className="px-4 py-4 flex-shrink-0">
            <button
              onClick={onViewFull}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
            >
              View Full Profile <ArrowRight size={16} />
            </button>
          </div>
        </div>
    );
  };

  // Inline Deal Panel Component (for right column in Counterparts section)
  const DealPanel = ({ deal, onClose, onViewFull, sourceCounterpart, onSelectCounterpart: onCpClick, panelEnrichment }) => {
    // Deal detail is for paying subscribers (2026-10-02): non-subscribers get the headline and the offer.
    if (!isPaidViewer && deal) {
      return (
        <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-5 space-y-3`}>
          <div className="flex items-start justify-between gap-3">
            <p className={`text-sm font-semibold ${themeClasses.text}`} style={{ fontFamily: 'Georgia, serif' }}>{deal.headline}</p>
            <button onClick={onClose} className={`text-xs ${themeClasses.textMuted} hover:underline flex-shrink-0`}>Close</button>
          </div>
          <p className={`text-xs ${themeClasses.textMuted}`}>{[deal.announcement_date, Array.isArray(deal.country) ? deal.country.join(', ') : deal.country].filter(Boolean).join(' · ')}</p>
          <div className="rounded-lg p-3 text-center bg-[color:var(--gis-bg)]">
            <Lock size={16} className="mx-auto mb-1 text-blue-700" />
            <p className={`text-xs ${themeClasses.textMuted} mb-2`}>Deal details are for subscribers.</p>
            <button onClick={() => { onClose?.(); setActiveTab('subscribe'); }} className="px-4 py-1.5 bg-blue-700 text-white rounded-lg text-xs font-semibold hover:bg-blue-800">See plans</button>
          </div>
        </div>
      );
    }
    if (!deal) return null;

    // Get counterparts linked to this deal
    const linkedCounterparts = deal.deal_counterparts?.map(dc => ({ ...dc.counterpart, role: dc.role })).filter(Boolean) || [];

    // Format deal size
    const formatDealSize = (deal) => {
      const dv = deal.financials?.dealValue;
      if (dv?.amount) {
        const formatted = formatDealValue(dv);
        if (formatted) return formatted;
      }
      // Legacy flat fields carry no currency. They used to render with a euro
      // sign; USD is the platform's majority currency by an order of magnitude
      // (1,033 of 1,271 valued deals), so an unlabelled amount is far likelier
      // to be dollars than euros.
      if (deal.size || deal.value) return formatDealValue({ amount: deal.size || deal.value, unit: 'M', currency: 'USD' });
      if (deal.enterprise_value) return formatDealValue({ amount: deal.enterprise_value, unit: 'M', currency: 'USD' });
      return null;
    };

    // Get status color
    const getStatusColor = (status) => {
      const s = status?.toLowerCase();
      if (s === 'closed' || s === 'completed') return 'bg-green-100 text-green-700';
      if (s === 'in_progress' || s === 'ongoing') return 'bg-blue-100 text-blue-700';
      if (s === 'tender') return 'bg-amber-100 text-amber-700';
      if (s === 'cancelled' || s === 'failed') return 'bg-red-100 text-red-700';
      return 'bg-slate-100 text-slate-600';
    };

    // Get favicon for counterpart
    const getCounterpartFavicon = (cp) => {
      const website = cp?.websiteUrl || cp?.website || cp?.investmentPreferences?.website;
      if (!website) return null;
      const domain = website.replace(/^https?:\/\//, '').split('/')[0];
      if (!domain) return null;
      return `https://cdn.brandfetch.io/${domain}/icon?c=1idrTt0p2-SJacppl92`;
    };

    // Get lead counterpart favicon
    const leadCp = linkedCounterparts.find(cp => cp.is_lead) || linkedCounterparts[0];
    const leadFavicon = leadCp ? getCounterpartFavicon(leadCp) : null;

    const dealCountries = (Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).filter(Boolean);
    const dealTechs = (Array.isArray(deal.technology) ? deal.technology : [deal.technology]).filter(Boolean);
    const fmtTech = (t) => String(t).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\b(Ppa|Ipo|Epc|Ipp)\b/g, m => m.toUpperCase());
    const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return d; } };
    const timelineLabel = deal.expected_close_date ? 'Expected close' : deal.signing_date ? 'Signed' : deal.announcement_date ? 'Announced' : deal.year ? 'Year' : null;
    const timelineValue = deal.expected_close_date ? fmtDate(deal.expected_close_date) : deal.signing_date ? fmtDate(deal.signing_date) : deal.announcement_date ? fmtDate(deal.announcement_date) : deal.year || null;
    const statusRaw = deal.status || deal.content_status;
    const sectorTag = dealTechs[0];

    // label → value rows (mock's clean table)
    const Row = ({ label, children }) => (
      <div className={`flex items-center justify-between gap-3 px-4 py-3 border-b ${themeClasses.border} last:border-b-0`}>
        <span className={`text-sm ${themeClasses.textMuted} flex-shrink-0`}>{label}</span>
        <span className={`text-sm font-semibold ${themeClasses.text} text-right`}>{children}</span>
      </div>
    );

    return (
      <div className="flex flex-col h-full bg-[color:var(--gis-bg)]">
        {/* Header — headline + sector tag + close (mock) */}
        <div className="px-5 pt-5 pb-3 flex-shrink-0">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden border ${themeClasses.border} bg-[color:var(--gis-panel)]`}>
              {leadFavicon ? (
                <img src={leadFavicon} alt="" className="w-7 h-7 object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
              ) : null}
              <span className={`text-sm font-bold ${themeClasses.textMuted}`} style={{ display: leadFavicon ? 'none' : 'block' }}>{(leadCp?.name || deal.headline || '?').charAt(0)}</span>
            </div>
            <div className="min-w-0 flex-1">
              {sectorTag && (
                <span className="inline-block px-2 py-0.5 rounded-md text-[11px] font-bold mb-1" style={{ background: 'var(--gis-warn-soft)', color: 'var(--gis-warn)' }}>{fmtTech(sectorTag)}</span>
              )}
              <h2 className={`font-bold text-[15px] leading-snug ${themeClasses.text}`}>{deal.headline || deal.name || deal.project || 'Deal'}</h2>
            </div>
            <button onClick={onClose} className={`p-1.5 rounded-lg ${themeClasses.cardHover} flex-shrink-0`}><X size={18} className={themeClasses.textMuted} /></button>
          </div>

          {/* Follow + Get Alerts + open-in-new (mock) */}
          {leadCp?.id && (
            <div className="flex items-center gap-2 mt-4">
              <FollowButton targetType="counterpart" targetId={leadCp.id} label={leadCp.name} compact />
              <EmailAlertButton targetType="counterpart" targetId={leadCp.id} label={leadCp.name} compact />
              {onViewFull && (
                <button onClick={onViewFull} title="Open full deal" className={`ml-auto p-2 rounded-lg ${themeClasses.cardHover} ${themeClasses.textMuted}`}><ExternalLink size={15} /></button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-5 pb-4 space-y-4 overflow-y-auto flex-1">
          {/* Label→value table */}
          <div className={`rounded-xl border ${themeClasses.border} ${themeClasses.card} overflow-hidden`}>
            {statusRaw && <Row label="Status"><span className="px-2 py-0.5 rounded-md text-xs font-semibold" style={{ background: 'var(--gis-info-soft)', color: 'var(--gis-info)' }}>{fmtTech(statusRaw)}</span></Row>}
            {dealTechs.length > 0 && <Row label="Technology"><span className="px-2 py-0.5 rounded-md text-xs font-semibold" style={{ background: 'var(--gis-warn-soft)', color: 'var(--gis-warn)' }}>{dealTechs.map(fmtTech).join(', ')}</span></Row>}
            {dealCountries.length > 0 && <Row label="Location">{dealCountries.join(', ')}</Row>}
            {timelineLabel && <Row label="Timeline">{timelineLabel} {timelineValue}</Row>}
            {safeFormatCapacity(deal.capacity, deal.capacity_unit) && <Row label="Capacity">{safeFormatCapacity(deal.capacity, deal.capacity_unit)}</Row>}
            {formatDealSize(deal) && <Row label="Deal size">{formatDealSize(deal)}</Row>}
            {(deal.deal_type || deal.type) && <Row label="Deal type">{fmtTech(deal.deal_type || deal.type)}</Row>}
          </div>

          {/* Description card */}
          {(deal.description || deal.headline) && (
            <div className={`p-4 rounded-xl border ${themeClasses.border} ${themeClasses.card}`}>
              <p className={`text-sm ${themeClasses.body || themeClasses.text} leading-relaxed`}>
                {deal.description || deal.headline}
              </p>
            </div>
          )}

          {/* Deal Intelligence (when enriched) */}
          {panelEnrichment?.narrative && (
            <div className={`p-4 rounded-xl border-l-4 border-blue-500 ${theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50/50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={12} className="text-blue-600" />
                <span className={`text-[10px] font-semibold tracking-wide uppercase ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>Deal Intelligence</span>
              </div>
              <p className={`text-xs ${themeClasses.text} leading-relaxed line-clamp-4`}>{panelEnrichment.narrative}</p>
            </div>
          )}

          {/* Source link */}
          {deal.source_url && !deal.source_url.includes('news.google.com') && (
            <a href={deal.source_url} target="_blank" rel="noopener noreferrer"
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-xl transition-colors ${themeClasses.listItem} ${themeClasses.hover} text-blue-600`}>
              <ExternalLink size={14} />
              <span className="truncate">{(() => { try { return new URL(deal.source_url).hostname; } catch { return 'Source'; } })()}</span>
            </a>
          )}

          {/* Counterparts */}
          {(linkedCounterparts.length > 0 || deal.counterparty || sourceCounterpart) && (
            <div>
              <p className={`text-[11px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>Counterparties</p>
              <div className="space-y-2">
                {/* Source counterpart (the one we came from) */}
                {sourceCounterpart && (
                  <button
                    onClick={() => { if (onCpClick) { onCpClick(sourceCounterpart); } else { viewCounterpart(sourceCounterpart); setSearchQuery(''); setSmartSearchResponse(null); setSmartSearchCpIds(null); setSmartSearchQuery(''); setSmartSearchDeals([]); setSmartSearchDealIds(null); setSmartSearchDealQuery(''); setSmartSearchDealCps([]); } }}
                    className={`w-full p-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.card} ${themeClasses.cardHover} flex items-center gap-2 text-left transition-colors`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-[color:var(--gis-bg)] border ${themeClasses.border}`}>
                      {getCounterpartFavicon(sourceCounterpart) ? (
                        <img
                          src={getCounterpartFavicon(sourceCounterpart)}
                          alt=""
                          className="w-5 h-5 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span className={`text-xs font-semibold ${themeClasses.textMuted}`} style={{ display: getCounterpartFavicon(sourceCounterpart) ? 'none' : 'flex' }}>
                        {sourceCounterpart.name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${themeClasses.text} truncate hover:text-blue-600`}>{sourceCounterpart.name}</p>
                      <p className={`text-xs ${themeClasses.textMuted} capitalize`}>{(() => { const dc = deal.deal_counterparts?.find(dc => dc.counterpart?.id === sourceCounterpart.id); return dc?.role ? capAcronyms(dc.role.replace('_', ' ')) : (sourceCounterpart.category || 'Counterpart'); })()}</p>
                    </div>
                    <ExternalLink size={14} className={`${themeClasses.textMuted} flex-shrink-0`} />
                  </button>
                )}
                {/* Other linked counterparts */}
                {linkedCounterparts.filter(cp => cp.id !== sourceCounterpart?.id).map((cp, i) => (
                  <button
                    key={cp.id || i}
                    onClick={() => { if (onCpClick) { onCpClick(cp); } else { viewCounterpart(cp); setSearchQuery(''); setSmartSearchResponse(null); setSmartSearchCpIds(null); setSmartSearchQuery(''); setSmartSearchDeals([]); setSmartSearchDealIds(null); setSmartSearchDealQuery(''); setSmartSearchDealCps([]); } }}
                    className={`w-full p-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.card} ${themeClasses.cardHover} flex items-center gap-2 text-left transition-colors`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-[color:var(--gis-bg)] border ${themeClasses.border}`}>
                      {getCounterpartFavicon(cp) ? (
                        <img
                          src={getCounterpartFavicon(cp)}
                          alt=""
                          className="w-5 h-5 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span className={`text-xs font-semibold ${themeClasses.textMuted}`} style={{ display: getCounterpartFavicon(cp) ? 'none' : 'flex' }}>
                        {cp.name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${themeClasses.text} truncate hover:text-blue-600`}>{cp.name}</p>
                      <p className={`text-xs ${themeClasses.textMuted} capitalize`}>{cp.role ? capAcronyms(cp.role.replace('_', ' ')) : (cp.category || 'Counterpart')}</p>
                    </div>
                    <ExternalLink size={14} className={`${themeClasses.textMuted} flex-shrink-0`} />
                  </button>
                ))}
                {/* Counterparty string from enrichment deals */}
                {deal.counterparty && !sourceCounterpart && linkedCounterparts.length === 0 && (
                  <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className={`text-sm ${themeClasses.text}`}>{deal.counterparty}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Role in Deal (if available) */}
          {deal.role && (
            <div className={`flex items-center gap-2 text-sm`}>
              <span className={themeClasses.textMuted}>Role:</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                {deal.role}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        {onViewFull && (
          <div className="px-5 py-4 flex-shrink-0">
            <button
              onClick={onViewFull}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
            >
              View full deal <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Counterpart Modal Component

  // Counterpart Picker Modal - Select existing counterparts to link to a project
  const CounterpartPickerModal = ({ project, onClose }) => {
    const [search, setSearch] = useState('');
    
    // Get counterparts not already linked to this project
    const availableCounterparts = counterparts.filter(c => 
      !project.counterpartIds?.includes(c.id)
    );
    
    // Filter by search
    const filteredCounterparts = availableCounterparts.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.category?.toLowerCase().includes(search.toLowerCase()) ||
      c.company?.toLowerCase().includes(search.toLowerCase())
    );
    
    const handleLink = (counterpartId) => {
      const updated = { 
        ...project, 
        counterpartIds: [...(project.counterpartIds || []), counterpartId] 
      };
      setProjects(projects.map(p => p.id === project.id ? updated : p));
      const cp = counterparts.find(c => c.id === counterpartId);
      logActivity('counterpart_linked', 'project', project.id, `Linked ${cp?.name || 'counterpart'}`);
    };
    
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className={`${themeClasses.card} rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col`}>
          {/* Header */}
          <div className={`p-4 border-b ${themeClasses.border} flex items-center justify-between`}>
            <h2 className={`text-lg font-semibold ${themeClasses.text}`}>Link Counterpart</h2>
            <button onClick={onClose} className={`p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl ${themeClasses.textMuted}`}>
              <X size={20} />
            </button>
          </div>
          
          {/* Search */}
          <div className="p-4">
            <div className="relative">
              <Search size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textMuted}`} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search counterparts..."
                className={`w-full pl-10 pr-4 py-2.5 ${themeClasses.card} border ${themeClasses.border} rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-blue-700/50`}
                autoFocus
              />
            </div>
          </div>
          
          {/* Counterpart List */}
          <div className="flex-1 overflow-y-auto px-4">
            {filteredCounterparts.length > 0 ? (
              <div className="space-y-2 pb-4">
                {filteredCounterparts.map(c => (
                  <button
                    key={c.id}
                    onClick={() => handleLink(c.id)}
                    className={`w-full p-3 rounded-xl border ${themeClasses.border} ${themeClasses.cardHover} text-left flex items-center gap-3`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      theme === 'dark' ? 'bg-blue-700/20 text-blue-800' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {c.type === 'company' ? <Building2 size={18} /> : c.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${themeClasses.text} truncate`}>{c.name}</p>
                      <p className={`text-sm ${themeClasses.textMuted} truncate`}>
                        {c.category}{c.company ? ` · ${c.company}` : ''}
                      </p>
                    </div>
                    <Plus size={18} className="text-blue-700 flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : availableCounterparts.length === 0 ? (
              <div className="py-8 text-center">
                <Users size={40} className={`${themeClasses.textMuted} mx-auto mb-3`} />
                <p className={`${themeClasses.text} font-medium mb-1`}>All counterparts linked</p>
                <p className={`text-sm ${themeClasses.textMuted}`}>Every counterpart is already linked to this project</p>
              </div>
            ) : (
              <div className="py-8 text-center">
                <Search size={40} className={`${themeClasses.textMuted} mx-auto mb-3`} />
                <p className={`${themeClasses.text} font-medium mb-1`}>No matches found</p>
                <p className={`text-sm ${themeClasses.textMuted}`}>Try a different search term</p>
              </div>
            )}
          </div>
          
          {/* Footer with Create New option */}
          <div className={`p-4 border-t ${themeClasses.border}`}>
            <button
              onClick={() => {
                onClose();
                setEditingCounterpart(null);
                setShowCounterpartModal(true);
              }}
              className={`w-full p-3 rounded-xl border-2 border-dashed ${themeClasses.border} text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-700/10 text-sm font-medium flex items-center justify-center gap-2`}
            >
              <Plus size={18} /> Create New Counterpart
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ProjectDetail component removed - counterparts-only platform
  // NetworkPickerModal removed - network feature removed


  // Document Viewer Modal
  const DocumentViewerModal = ({ doc, onClose }) => {
    const project = projects.find(p => p.id === doc.projectId);
    const linkedCounterparts = counterparts.filter(c => 
      (doc.counterpartIds && doc.counterpartIds.includes(c.id)) ||
      (project && project.counterpartIds.includes(c.id))
    );
    
    // Group counterparts by type
    const linkedCompanies = linkedCounterparts.filter(c => c.type === 'company');
    const linkedPeople = linkedCounterparts.filter(c => c.type === 'person');
    
    const isPdf = doc.contentType === 'pdf' || doc.contentType === 'application/pdf' || doc.name?.endsWith('.pdf');
    const isImage = doc.contentType === 'image' || doc.contentType?.startsWith('image') || /\.(png|jpg|jpeg|gif|webp)$/i.test(doc.name);
    const hasContent = doc.content && !doc.content.startsWith('[PDF Document') && !doc.content.startsWith('[Binary') && !doc.content.startsWith('[Image');
    const isOnDrive = !!doc.driveFileId;

    // State for loading from Google Drive
    const [loadingFromDrive, setLoadingFromDrive] = useState(false);
    const [driveContent, setDriveContent] = useState(null);
    const [driveError, setDriveError] = useState(null);
    
    // State for tabbed sidebar
    const [activeTab, setActiveTab] = useState('summary');
    const [copiedNote, setCopiedNote] = useState(false);

    // Load content from Google Drive if needed
    useEffect(() => {
      if (isOnDrive && !doc.base64 && !driveContent && googleDriveSync.isConnected()) {
        setLoadingFromDrive(true);
        googleDriveSync.getFileAsBase64(doc.driveFileId)
          .then(base64 => {
            if (base64) {
              setDriveContent(base64);
            } else {
              setDriveError('Could not load file from Google Drive');
            }
          })
          .catch(err => {
            console.error('Error loading from Drive:', err);
            setDriveError('Error loading file');
          })
          .finally(() => setLoadingFromDrive(false));
      }
    }, [isOnDrive, doc.base64, doc.driveFileId, driveContent]);

    // Use driveContent or doc.base64
    const fileBase64 = driveContent || doc.base64;

    // Create blob URL for PDF
    const pdfUrl = useMemo(() => {
      if (isPdf && fileBase64) {
        try {
          const byteCharacters = atob(fileBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          return URL.createObjectURL(blob);
        } catch (e) {
          return null;
        }
      }
      return null;
    }, [isPdf, fileBase64]);

    // Cleanup blob URL on unmount
    useEffect(() => {
      return () => {
        if (pdfUrl) {
          try { URL.revokeObjectURL(pdfUrl); } catch (e) { /* Safari security error */ }
        }
      };
    }, [pdfUrl]);

    const handleDownload = () => {
      if (fileBase64) {
        const mimeType = isPdf ? 'application/pdf' : (doc.contentType || 'application/octet-stream');
        const byteCharacters = atob(fileBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (doc.driveWebContentLink) {
        window.open(doc.driveWebContentLink, '_blank');
      }
    };

    const handleCopyNotes = () => {
      if (doc.notes) {
        navigator.clipboard.writeText(doc.notes);
        setCopiedNote(true);
        setTimeout(() => setCopiedNote(false), 2000);
      }
    };

    const relatedCount = linkedCounterparts.length + (project ? 1 : 0);

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-2 sm:p-4">
        <div className="bg-white rounded-2xl max-w-7xl w-full h-[92vh] flex flex-col shadow-2xl overflow-hidden">
          {/* Minimal Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-blue-800" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-slate-900 truncate">{doc.name}</h2>
                  {isOnDrive && (
                    <span className="flex-shrink-0 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                      <Cloud size={10} /> Drive
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400">{doc.size} · Uploaded {formatDate(doc.uploadedAt)}</p>
              </div>
            </div>
            
            {/* Clean action buttons */}
            <div className="flex items-center gap-1">
              {(fileBase64 || doc.driveWebContentLink) && (
                <button onClick={handleDownload} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700 transition-colors" title="Download">
                  <Download size={18} />
                </button>
              )}
              {isOnDrive && doc.driveWebViewLink && (
                <a href={doc.driveWebViewLink} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700 transition-colors" title="Open in Drive">
                  <ExternalLink size={18} />
                </a>
              )}
              <button 
                onClick={() => { onClose(); setExtractingFromDocument(doc); }}
                className="flex items-center gap-1.5 ml-1 px-3 py-1.5 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 transition-all shadow-sm">
                <Sparkles size={14} />
                <span>Extract</span>
              </button>
              <div className="w-px h-6 bg-slate-200 mx-2" />
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex min-h-0">
            {/* Main preview area */}
            <div className="flex-1 bg-slate-100 overflow-auto">
              {loadingFromDrive ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-700 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-slate-600">Loading from Google Drive...</p>
                  </div>
                </div>
              ) : driveError ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <CloudOff size={64} className="mx-auto text-red-300 mb-4" />
                    <p className="text-red-600 font-medium mb-2">{driveError}</p>
                    {doc.driveWebViewLink && (
                      <a href={doc.driveWebViewLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-700 text-white rounded-lg text-sm hover:bg-blue-800">
                        <ExternalLink size={16} /> Open in Google Drive
                      </a>
                    )}
                  </div>
                </div>
              ) : isPdf && pdfUrl ? (
                <iframe 
                  src={`${pdfUrl}#toolbar=1&navpanes=0&view=FitH`}
                  className="w-full h-full bg-white"
                  title={doc.name}
                />
              ) : isPdf && !fileBase64 && isOnDrive ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <Cloud size={64} className="mx-auto text-blue-300 mb-4" />
                    <p className="text-slate-600 mb-4">This file is stored in Google Drive</p>
                    {doc.driveWebViewLink && (
                      <a href={doc.driveWebViewLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg text-sm hover:bg-blue-800">
                        <ExternalLink size={16} /> Open in Google Drive
                      </a>
                    )}
                  </div>
                </div>
              ) : isPdf && !fileBase64 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <FileText size={64} className="mx-auto text-blue-800 mb-4" />
                    <p className="text-blue-700 font-medium mb-2">PDF Preview Unavailable</p>
                    <p className="text-sm text-slate-600">Re-upload this document to enable preview</p>
                  </div>
                </div>
              ) : isImage && (fileBase64 || doc.thumbnail) ? (
                <div className="h-full flex items-center justify-center p-4">
                  <img
                    src={doc.thumbnail || `data:${doc.contentType || 'image/png'};base64,${fileBase64}`}
                    alt={doc.name}
                    className="max-w-full max-h-full rounded-lg shadow-lg object-contain"
                  />
                </div>
              ) : hasContent ? (
                <div className="p-6 flex justify-center">
                  <div className="w-full max-w-3xl bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">{doc.content}</pre>
                  </div>
                </div>
              ) : isOnDrive && doc.driveWebViewLink ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <Cloud size={64} className="mx-auto text-blue-300 mb-4" />
                    <p className="text-slate-600 mb-4">This file is stored in Google Drive</p>
                    <a href={doc.driveWebViewLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg text-sm hover:bg-blue-800">
                      <ExternalLink size={16} /> Open in Google Drive
                    </a>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <FileText size={64} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Preview not available for this file type</p>
                    {fileBase64 && (
                      <button onClick={handleDownload} className="mt-4 px-4 py-2 bg-blue-700 text-white rounded-lg text-sm hover:bg-blue-800">
                        <Download size={16} className="inline mr-2" /> Download to View
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Redesigned Sidebar */}
            <div className="w-80 border-l border-slate-200 flex flex-col bg-slate-50 flex-shrink-0">
              {/* Tabs */}
              <div className="flex border-b border-slate-200 bg-white px-2 pt-2">
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'summary' ? 'text-slate-900 bg-slate-50' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setActiveTab('related')}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'related' ? 'text-slate-900 bg-slate-50' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Related
                  {relatedCount > 0 && (
                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === 'related' ? 'bg-slate-200 text-slate-600' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {relatedCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'summary' && (
                  <div className="p-4 space-y-4">
                    {/* AI Summary Card */}
                    {doc.notes && (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-lg bg-blue-700 flex items-center justify-center">
                              <Sparkles size={12} className="text-white" />
                            </div>
                            <span className="text-sm font-semibold text-blue-900">AI Summary</span>
                          </div>
                          <button 
                            onClick={handleCopyNotes}
                            className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors group"
                            title="Copy to clipboard"
                          >
                            {copiedNote ? (
                              <span className="text-xs text-blue-800 font-medium">Copied!</span>
                            ) : (
                              <Copy size={14} className="text-blue-800 group-hover:text-blue-800" />
                            )}
                          </button>
                        </div>
                        <div className="p-4">
                          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{doc.notes}</p>
                        </div>
                      </div>
                    )}

                    {/* Document Info */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Document Info</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Category</span>
                          <span className="text-sm font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{doc.category}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Size</span>
                          <span className="text-sm font-medium text-slate-700">{doc.size}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Uploaded</span>
                          <span className="text-sm font-medium text-slate-700">{formatDate(doc.uploadedAt)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 gap-2">
                      <button 
                        onClick={() => { onClose(); setEditingDocument(doc); }}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                      >
                        <Edit2 size={14} />
                        <span>Edit</span>
                      </button>
                      <button 
                        onClick={() => { onClose(); setSharingDocument(doc); }}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                      >
                        <Link2 size={14} />
                        <span>Link</span>
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'related' && (
                  <div className="p-4 space-y-4">
                    {/* Linked Project - Featured Card */}
                    {project ? (
                      <button
                        onClick={() => { onClose(); setSelectedProject(project); }}
                        className="w-full bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md hover:border-blue-200 transition-all text-left group"
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-lg">{getTechIcon(project.technology, sectorConfig)}</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-800 transition-colors">{project.name}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{project.capacity} {sectorConfig?.primaryMetric?.unit || 'MW'} · {project.technology}</p>
                              </div>
                            </div>
                            <ChevronRight size={16} className="text-slate-300 group-hover:text-blue-700 transition-colors mt-1" />
                          </div>
                          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-800">
                              {project.pipelineStatus}
                            </span>
                            <span className="text-xs text-slate-400">Linked Project</span>
                          </div>
                        </div>
                      </button>
                    ) : (
                      <div className="bg-white rounded-xl border border-dashed border-slate-200 p-4 text-center">
                        <p className="text-sm text-slate-400">No project linked</p>
                      </div>
                    )}

                    {/* Companies Section */}
                    {linkedCompanies.length > 0 && (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                          <Building2 size={14} className="text-slate-400" />
                          <span className="text-sm font-semibold text-slate-700">Companies</span>
                          <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{linkedCompanies.length}</span>
                        </div>
                        <div className="divide-y divide-slate-50">
                          {linkedCompanies.slice(0, 5).map(c => (
                            <button 
                              key={c.id} 
                              onClick={() => { onClose(); setSelectedCounterpart(c); }}
                              className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors group"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-800 font-semibold text-xs">
                                  {c.name.charAt(0)}
                                </div>
                                <span className="text-sm text-slate-700 group-hover:text-slate-900">{c.name}</span>
                              </div>
                              <span className="text-xs text-slate-400">{c.category}</span>
                            </button>
                          ))}
                          {linkedCompanies.length > 5 && (
                            <div className="px-4 py-2.5 text-sm text-blue-800 font-medium">
                              +{linkedCompanies.length - 5} more companies
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* People Section */}
                    {linkedPeople.length > 0 && (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                          <User size={14} className="text-slate-400" />
                          <span className="text-sm font-semibold text-slate-700">People</span>
                          <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{linkedPeople.length}</span>
                        </div>
                        <div className="divide-y divide-slate-50">
                          {linkedPeople.slice(0, 5).map(c => (
                            <button 
                              key={c.id}
                              onClick={() => { onClose(); setSelectedCounterpart(c); }}
                              className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors group"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium text-xs">
                                  {c.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>
                                <span className="text-sm text-slate-700 group-hover:text-slate-900">{c.name}</span>
                              </div>
                              <span className="text-xs text-slate-400">{c.category}</span>
                            </button>
                          ))}
                          {linkedPeople.length > 5 && (
                            <div className="px-4 py-2.5 text-sm text-blue-800 font-medium">
                              +{linkedPeople.length - 5} more people
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {linkedCounterparts.length === 0 && !project && (
                      <div className="bg-white rounded-xl border border-dashed border-slate-200 p-6 text-center">
                        <Users size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-400">No related entities</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sticky Share CTA */}
              <div className="p-4 border-t border-slate-200 bg-white">
                <button 
                  onClick={() => { onClose(); setSharingDocument(doc); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-medium transition-colors shadow-sm"
                >
                  <Share2 size={16} />
                  Share Document
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============ SHARE DOCUMENT MODAL ============

  // ============ SHARE PAGE (Public Document Viewer) ============

  // ============ PROJECT SHARE PAGE (Public Project Viewer with Conversion CTAs) ============
  
  // Helper functions moved outside component to prevent recreation on each render
  const getContentTypeForShare = (filename) => {
    if (!filename) return 'application/pdf';
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'png') return 'image/png';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'gif') return 'image/gif';
    if (ext === 'webp') return 'image/webp';
    return 'application/pdf';
  };

  const isPdfFileForShare = (filename) => filename?.toLowerCase().endsWith('.pdf');
  
  const isImageFileForShare = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  };

  const getTechIconForShare = (tech) => {
    const icons = {
      'Solar PV': '☀️', 'Onshore Wind': '💨', 'Offshore Wind': '🌊',
      'Hydro': '💧', 'Battery Storage': '🔋', 'Biomass': '🌿',
    };
    return icons[tech] || '⚡';
  };

  const ProjectSharePage = ({ data, isSignedIn, user, onImportProject }) => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('documents');
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [exitModalDismissed, setExitModalDismissed] = useState(false);
    const [viewingDoc, setViewingDoc] = useState(null);
    const [hasDownloaded, setHasDownloaded] = useState(false);
    const [showDownloadPrompt, setShowDownloadPrompt] = useState(false);
    const [importing, setImporting] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(null);
    
    const { signIn } = useSignIn();

    // Store pending import before any auth redirect
    const storePendingImport = () => {
      try {
        localStorage.setItem('pendingImportShare', window.location.hash);
      } catch (e) {
        // Safari private browsing
      }
    };

    // Direct OAuth handlers - no modal, straight to provider
    const handleOAuthSignIn = async (strategy) => {
      if (!signIn) {
        console.error('SignIn not available');
        return;
      }
      try {
        setOauthLoading(strategy);
        storePendingImport();
        await signIn.authenticateWithRedirect({
          strategy: strategy,
          redirectUrl: '/sso-callback',
          redirectUrlComplete: window.location.href,
        });
      } catch (err) {
        console.error('OAuth error:', err);
        setOauthLoading(null);
      }
    };

    // Handle save to account
    const handleSaveToAccount = () => {
      if (isSignedIn && onImportProject) {
        setImporting(true);
        setTimeout(() => {
          onImportProject(data);
        }, 500);
      } else {
        // Not signed in - scroll to signup section
        document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Track view on mount
    useEffect(() => {
      const trackView = async () => {
        try {
          await fetch('/api/track-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shareId: data.shareId,
              docName: `Project: ${data.name}`,
              ownerEmail: data.email,
            }),
          });
        } catch (err) {
          console.log('View tracking not available');
        }
      };
      
      if (data.shareId && data.notify !== false) {
        trackView();
      }
      
      // Loading and progressive CTA reveal
      const timer = setTimeout(() => {
        setLoading(false);
        // Show floating save button after 10 seconds
        setTimeout(() => setShowSaveButton(true), 10000);
      }, 500);
      
      return () => clearTimeout(timer);
    }, [data]);

    // Exit intent detection (desktop only)
    useEffect(() => {
      const handleMouseLeave = (e) => {
        if (e.clientY <= 0 && !showExitModal && !exitModalDismissed) {
          setShowExitModal(true);
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);
      return () => document.removeEventListener('mouseleave', handleMouseLeave);
    }, [showExitModal, exitModalDismissed]);

    // Get project documents
    const projectDocs = data.documents || [];

    // Memoized blob URL for document viewer - prevents recreation on every render
    const viewingDocBlobUrl = useMemo(() => {
      if (!viewingDoc?.base64) return null;
      try {
        const binary = atob(viewingDoc.base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const contentType = getContentTypeForShare(viewingDoc.name);
        const blob = new Blob([bytes], { type: contentType });
        return URL.createObjectURL(blob);
      } catch (err) {
        console.error('Error creating blob URL:', err);
        return null;
      }
    }, [viewingDoc?.base64, viewingDoc?.name]);

    // Cleanup blob URL when viewingDoc changes or component unmounts
    useEffect(() => {
      return () => {
        if (viewingDocBlobUrl) {
          try {
            URL.revokeObjectURL(viewingDocBlobUrl);
          } catch (e) {
            // Safari may throw "The operation is insecure" - ignore
          }
        }
      };
    }, [viewingDocBlobUrl]);

    const handleDownload = (doc) => {
      if (doc.base64) {
        try {
          const binary = atob(doc.base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          // Detect content type from filename if not set
          let contentType = doc.contentType;
          if (!contentType || contentType === 'application/octet-stream') {
            const ext = doc.name?.split('.').pop()?.toLowerCase();
            if (ext === 'pdf') contentType = 'application/pdf';
            else if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) contentType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
            else contentType = 'application/pdf'; // Default to PDF
          }
          const blob = new Blob([bytes], { type: contentType });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.name;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          // Delay cleanup to ensure download starts
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        } catch (err) {
          console.error('Download error:', err);
        }
      } else if (doc.driveFileId) {
        window.open(`https://drive.google.com/uc?export=download&id=${doc.driveFileId}`, '_blank');
      }
      setHasDownloaded(true);
      setTimeout(() => setShowDownloadPrompt(true), 1000);
    };

    const handleDownloadAll = () => {
      projectDocs.forEach((doc, index) => {
        // Stagger downloads to avoid browser blocking
        setTimeout(() => handleDownload(doc), index * 500);
      });
    };

    if (loading) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500 text-sm">Loading project...</p>
          </div>
        </div>
      );
    }

    if (importing) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-800 animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Saving to your account...</h3>
            <p className="text-slate-500 text-sm">Importing project and documents</p>
          </div>
        </div>
      );
    }

    const isExpiringSoon = false; // Could calculate from data.shared date

    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        {/* TIER 3: Expiring Banner (conditional) */}
        {isExpiringSoon && (
          <div className="bg-gradient-to-r from-blue-50 to-yellow-50 border-b border-blue-200 px-4 py-3">
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-700 rounded-full flex items-center justify-center text-white">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">This link expires in 3 days</p>
                  <p className="text-xs text-blue-700">Save a permanent copy to your account</p>
                </div>
              </div>
              <a
                href={window.location.origin}
                className="px-4 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Save now →
              </a>
            </div>
          </div>
        )}

        {/* TIER 1: Shared Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-50 border-b border-blue-200 px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-700 rounded-full flex items-center justify-center text-white">
                <Share2 size={18} />
              </div>
              <p className="text-sm text-blue-800">
                Shared with you by <strong>{data.owner}</strong>
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Clock size={12} /> {new Date(data.shared).toLocaleDateString()}</span>
              <span className="flex items-center gap-1"><Eye size={12} /> View only</span>
            </div>
          </div>
        </div>

        {/* TIER 1: Branded Header */}
        <header className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-700 to-blue-700 flex items-center justify-center text-white shadow-lg">
                <TrendingUp size={20} />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">
                  <span>Deal</span><span className="text-blue-700">Flow</span>
                </h1>
                <p className="text-xs text-slate-500">Shared Project</p>
              </div>
            </div>
            {isSignedIn ? (
              <button
                onClick={handleSaveToAccount}
                disabled={importing}
                className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 disabled:opacity-70 flex items-center gap-2"
              >
                {importing ? <Loader2 size={14} className="animate-spin" /> : null}
                {importing ? 'Saving...' : 'Save to my account'}
              </button>
            ) : (
              <button
                onClick={() => document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 flex items-center gap-2"
              >
                Sign up to save
              </button>
            )}
          </div>
        </header>

        {/* Project Card */}
        <div className="flex-1 p-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Project Header */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-yellow-50 rounded-2xl flex items-center justify-center text-3xl">
                    {getTechIcon(data.technology)}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{data.name}</h2>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="flex items-center gap-1.5 text-sm text-slate-600">
                        <MapPin size={14} className="text-slate-400" /> {data.location}
                      </span>
                      <span className="flex items-center gap-1.5 text-sm text-slate-600">
                        <Zap size={14} className="text-slate-400" /> {data.capacity} MW
                      </span>
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                        {data.pipelineStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-0 px-6 border-b border-slate-100">
                {data.includeOverview && (
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'overview' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Overview
                  </button>
                )}
                {data.includeDocuments && projectDocs.length > 0 && (
                  <button
                    onClick={() => setActiveTab('documents')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                      activeTab === 'documents' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Documents
                    <span className={`px-1.5 py-0.5 rounded text-xs ${activeTab === 'documents' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100'}`}>
                      {projectDocs.length}
                    </span>
                  </button>
                )}
                {data.includeCounterparts && data.counterparts?.length > 0 && (
                  <button
                    onClick={() => setActiveTab('counterparts')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                      activeTab === 'counterparts' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Counterparts
                    <span className={`px-1.5 py-0.5 rounded text-xs ${activeTab === 'counterparts' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100'}`}>
                      {data.counterparts.length}
                    </span>
                  </button>
                )}
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && data.includeOverview && (
                  <div className="space-y-6">
                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs text-slate-500 mb-1">Capacity</p>
                        <p className="text-xl font-bold text-slate-900">{data.capacity} MW</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs text-slate-500 mb-1">Technology</p>
                        <p className="text-xl font-bold text-slate-900">{data.technology}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs text-slate-500 mb-1">Stage</p>
                        <p className="text-xl font-bold text-slate-900">{data.stage}</p>
                      </div>
                      {data.investmentSize && (
                        <div className="p-4 bg-slate-50 rounded-xl">
                          <p className="text-xs text-slate-500 mb-1">Investment</p>
                          <p className="text-xl font-bold text-slate-900">${(data.investmentSize / 1000000).toFixed(1)}M</p>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {data.notes && (
                      <div>
                        <h3 className="text-sm font-semibold text-slate-700 mb-3">Description</h3>
                        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{data.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'documents' && data.includeDocuments && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-700">Shared Documents</h3>
                      {projectDocs.length > 1 && (
                        <button 
                          onClick={handleDownloadAll}
                          className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                        >
                          <Download size={14} /> Download All
                        </button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {projectDocs.map((doc, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-600">
                            <FileText size={24} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{doc.name}</p>
                            <p className="text-xs text-slate-500">{doc.size} · {doc.category || 'Document'}</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setViewingDoc(doc)}
                              className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 flex items-center gap-1"
                            >
                              <Eye size={14} /> View
                            </button>
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDownload(doc); }}
                              className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-1"
                            >
                              <Download size={14} /> Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'counterparts' && data.includeCounterparts && data.counterparts?.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-700">Project Counterparts</h3>
                      {data.hideContactInfo && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <EyeOff size={12} /> Contact info hidden
                        </span>
                      )}
                    </div>
                    <div className="space-y-3">
                      {data.counterparts.map((cp, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                          <CounterpartAvatar counterpart={cp} size="lg" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900">{cp.name}</p>
                            <p className="text-sm text-slate-500">
                              {cp.role && <span>{cp.role}</span>}
                              {cp.role && cp.company && <span> at </span>}
                              {cp.company && <span className="font-medium">{cp.company}</span>}
                              {!cp.role && !cp.company && <span>{cp.type || cp.category}</span>}
                            </p>
                            {!data.hideContactInfo && (cp.email || cp.phone || cp.linkedin) && (
                              <div className="flex items-center gap-3 mt-2">
                                {cp.email && (
                                  <a href={`mailto:${cp.email}`} className="text-xs text-blue-800 hover:text-blue-700 flex items-center gap-1">
                                    <Mail size={12} /> {cp.email}
                                  </a>
                                )}
                                {cp.phone && (
                                  <a href={`tel:${cp.phone}`} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                                    <Phone size={12} /> {cp.phone}
                                  </a>
                                )}
                                {cp.linkedin && (
                                  <a href={cp.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-800 hover:text-blue-700 flex items-center gap-1">
                                    <Linkedin size={12} /> LinkedIn
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="px-2 py-1 bg-slate-200 text-slate-600 rounded text-xs font-medium">
                              {cp.type || cp.category}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline Signup - Only for non-signed-in users */}
                {!isSignedIn && (
                  <div id="signup-section" className="mt-6 pt-6 border-t border-slate-100">
                    <div className="text-center mb-5">
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">Save this project</h3>
                      <p className="text-sm text-slate-500">Create a free account to save and track updates</p>
                    </div>
                    
                    <div className="space-y-3 max-w-sm mx-auto">
                      {/* Direct OAuth Buttons - No modal */}
                      <button 
                        onClick={() => handleOAuthSignIn('oauth_google')}
                        disabled={oauthLoading}
                        className="w-full py-3 px-4 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 flex items-center justify-center gap-3 transition-all shadow-sm disabled:opacity-70"
                      >
                        {oauthLoading === 'oauth_google' ? (
                          <Loader2 size={20} className="animate-spin" />
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        Continue with Google
                      </button>
                      
                      <button 
                        onClick={() => handleOAuthSignIn('oauth_linkedin_oidc')}
                        disabled={oauthLoading}
                        className="w-full py-3 px-4 bg-[#0A66C2] border border-[#0A66C2] rounded-xl font-medium text-white hover:bg-[#004182] flex items-center justify-center gap-3 transition-all shadow-sm disabled:opacity-70"
                      >
                        {oauthLoading === 'oauth_linkedin_oidc' ? (
                          <Loader2 size={20} className="animate-spin" />
                        ) : (
                          <Linkedin size={20} />
                        )}
                        Continue with LinkedIn
                      </button>

                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-slate-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-2 bg-white text-slate-400">or</span>
                        </div>
                      </div>

                      <SignUpButton mode="redirect" forceRedirectUrl={window.location.href}>
                        <button 
                          onClick={storePendingImport}
                          className="w-full py-3 px-4 bg-slate-900 border border-slate-900 rounded-xl font-medium text-white hover:bg-slate-800 flex items-center justify-center gap-2 transition-all shadow-sm"
                        >
                          <Mail size={18} />
                          Continue with email
                        </button>
                      </SignUpButton>
                      
                      <p className="text-center text-xs text-slate-400 mt-4">
                        Free • No credit card required
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TIER 1: Footer CTA */}
        <footer className="bg-white border-t border-slate-200 px-4 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <div className="w-5 h-5 rounded bg-blue-700 flex items-center justify-center text-white font-bold text-xs">D</div>
              Powered by <strong className="text-slate-900">Deal</strong><strong className="text-blue-700">Flow</strong>
            </div>
            <button
              onClick={() => {
                if (isSignedIn) {
                  window.location.href = window.location.origin;
                } else {
                  document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="flex items-center gap-2 text-sm text-blue-800 hover:text-blue-700 font-medium group"
            >
              Create your own
              <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </footer>

        {/* TIER 2: Floating Save Button (appears after 10s) */}
        {showSaveButton && (
          <button
            onClick={() => {
              if (isSignedIn) {
                handleSaveToAccount();
              } else {
                document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            disabled={importing}
            className="fixed bottom-20 right-6 flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all animate-bounce-once disabled:opacity-70"
          >
            {importing ? <Loader2 size={18} className="animate-spin" /> : <Star size={18} />}
            <span className="text-sm font-medium">{importing ? 'Saving...' : (isSignedIn ? 'Save to my account' : 'Sign in to save')}</span>
          </button>
        )}

        {/* TIER 2: Post-Download Prompt */}
        {showDownloadPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-8 text-center shadow-2xl">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-blue-800" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Download complete!</h3>
              <p className="text-sm text-slate-500 mb-6">Want to organize and share your own projects like this?</p>
              <div className="space-y-3">
                <button
                  onClick={() => handleOAuthSignIn('oauth_google')}
                  disabled={oauthLoading}
                  className="w-full py-3 px-4 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-3 disabled:opacity-70"
                >
                  {oauthLoading === 'oauth_google' ? <Loader2 size={20} className="animate-spin" /> : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Continue with Google
                </button>
                <button
                  onClick={() => handleOAuthSignIn('oauth_linkedin_oidc')}
                  disabled={oauthLoading}
                  className="w-full py-3 px-4 bg-[#0A66C2] border border-[#0A66C2] rounded-xl font-medium text-white hover:bg-[#004182] flex items-center justify-center gap-3 disabled:opacity-70"
                >
                  {oauthLoading === 'oauth_linkedin_oidc' ? <Loader2 size={20} className="animate-spin" /> : <Linkedin size={20} />}
                  Continue with LinkedIn
                </button>
              </div>
              <button
                onClick={() => setShowDownloadPrompt(false)}
                className="text-sm text-slate-400 hover:text-slate-600 mt-4"
              >
                Maybe later
              </button>
            </div>
          </div>
        )}

        {/* TIER 3: Exit Intent Modal */}
        {showExitModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-8 text-center shadow-2xl">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Share2 size={36} className="text-blue-800" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Share projects like this?</h3>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                Create a free account to organize, share, and track your own projects.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => { setShowExitModal(false); handleOAuthSignIn('oauth_google'); }}
                  disabled={oauthLoading}
                  className="w-full py-3 px-4 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-3 disabled:opacity-70"
                >
                  {oauthLoading === 'oauth_google' ? <Loader2 size={20} className="animate-spin" /> : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Continue with Google
                </button>
                <button
                  onClick={() => { setShowExitModal(false); handleOAuthSignIn('oauth_linkedin_oidc'); }}
                  disabled={oauthLoading}
                  className="w-full py-3 px-4 bg-[#0A66C2] border border-[#0A66C2] rounded-xl font-medium text-white hover:bg-[#004182] flex items-center justify-center gap-3 disabled:opacity-70"
                >
                  {oauthLoading === 'oauth_linkedin_oidc' ? <Loader2 size={20} className="animate-spin" /> : <Linkedin size={20} />}
                  Continue with LinkedIn
                </button>
              </div>
              <button
                onClick={() => { setShowExitModal(false); setExitModalDismissed(true); }}
                className="text-sm text-slate-400 hover:text-slate-600 mt-4"
              >
                No thanks
              </button>
            </div>
          </div>
        )}

        {/* Document Viewer Modal */}
        {viewingDoc && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getFileIcon(viewingDoc.name)}</span>
                  <div>
                    <p className="font-medium text-slate-900">{viewingDoc.name}</p>
                    <p className="text-xs text-slate-500">{viewingDoc.size}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDownload(viewingDoc); }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1"
                  >
                    <Download size={14} /> Download
                  </button>
                  <button
                    onClick={() => setViewingDoc(null)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden bg-slate-100 p-4">
                {viewingDoc.base64 && viewingDocBlobUrl ? (
                  // Has base64 data and blob URL created successfully
                  isPdfFileForShare(viewingDoc.name) ? (
                    <iframe
                      src={viewingDocBlobUrl}
                      className="w-full h-full min-h-[60vh] rounded-lg bg-white"
                      title={viewingDoc.name}
                      type="application/pdf"
                    />
                  ) : isImageFileForShare(viewingDoc.name) ? (
                    <div className="flex items-center justify-center h-full min-h-[60vh] bg-white rounded-lg">
                      <img src={viewingDocBlobUrl} alt={viewingDoc.name} className="max-w-full max-h-[60vh] object-contain" />
                    </div>
                  ) : (
                    <iframe
                      src={viewingDocBlobUrl}
                      className="w-full h-full min-h-[60vh] rounded-lg bg-white"
                      title={viewingDoc.name}
                    />
                  )
                ) : viewingDoc.driveFileId ? (
                  // Google Drive file
                  <iframe
                    src={`https://drive.google.com/file/d/${viewingDoc.driveFileId}/preview`}
                    className="w-full h-full min-h-[60vh] rounded-lg bg-white"
                    title={viewingDoc.name}
                    allow="autoplay"
                  />
                ) : (
                  // No preview available
                  <div className="flex flex-col items-center justify-center h-full min-h-[60vh]">
                    <div className="w-16 h-16 bg-slate-200 rounded-2xl flex items-center justify-center mb-4">
                      <FileText size={32} className="text-slate-400" />
                    </div>
                    <p className="text-slate-600 font-medium mb-2">Preview not available</p>
                    <p className="text-slate-400 text-sm mb-4">Download the file to view it</p>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDownload(viewingDoc); }}
                      className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 flex items-center gap-2"
                    >
                      <Download size={14} /> Download
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes bounce-once {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
          .animate-bounce-once {
            animation: bounce-once 0.6s ease-in-out 2;
          }
        `}</style>
      </div>
    );
  };

  // CounterpartSharePage - Public share page for counterparts
  const CounterpartSharePage = ({ data, isSignedIn, user, onImportCounterpart }) => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [viewingDoc, setViewingDoc] = useState(null);
    const [importing, setImporting] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(null);
    const [expandedSections, setExpandedSections] = useState({ 
      technologies: true, 
      geographies: true, 
      dealStage: true, 
      dealSize: true, 
      dealStructure: true 
    });
    
    const { signIn } = useSignIn();

    const storePendingImport = () => {
      try {
        localStorage.setItem('pendingImportShare', window.location.hash);
      } catch (e) {}
    };

    const handleOAuthSignIn = async (strategy) => {
      if (!signIn) return;
      try {
        setOauthLoading(strategy);
        storePendingImport();
        await signIn.authenticateWithRedirect({
          strategy: strategy,
          redirectUrl: '/sso-callback',
          redirectUrlComplete: window.location.href,
        });
      } catch (err) {
        console.error('OAuth error:', err);
        setOauthLoading(null);
      }
    };

    const handleSaveToAccount = () => {
      if (isSignedIn && onImportCounterpart) {
        setImporting(true);
        setTimeout(() => {
          onImportCounterpart(data);
        }, 500);
      } else {
        document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    useEffect(() => {
      const trackView = async () => {
        try {
          await fetch('/api/track-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shareId: data.shareId,
              docName: `Counterpart: ${data.name}`,
              ownerEmail: data.ownerEmail,
            }),
          });
        } catch (err) {}
      };
      if (data.shareId && data.notify !== false) {
        trackView();
      }
      const timer = setTimeout(() => setLoading(false), 500);
      const buttonTimer = setTimeout(() => setShowSaveButton(true), 2000);
      return () => { clearTimeout(timer); clearTimeout(buttonTimer); };
    }, [data]);

    // Data extraction
    const counterpartDocs = data.documents || [];
    const hasPreferences = data.includePreferences && data.investmentPreferences;
    const hasDeals = false; // Enrichment recentDeals removed — share page deals section disabled
    const prefs = data.investmentPreferences || {};
    const dims = prefs.dimensions || [];

    // Get logo URL from website
    const getLogoUrl = () => {
      if (!data.websiteUrl) return null;
      const domain = data.websiteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      return `https://img.logo.dev/${domain}?token=pk_XiNSLHqcTlW74IhJdYzZ4w&size=64`;
    };
    const logoUrl = getLogoUrl();

    // Get markets count from geographies
    const geoDim = dims.find(d => d.key === 'geographies');
    const marketsCount = geoDim?.values?.length || 0;

    // Helper to get dimension by key (check multiple possible keys)
    const getDimension = (key) => {
      const keyVariants = {
        'technologies': ['technologies', 'sectors', 'technology', 'tech'],
        'geographies': ['geographies', 'geography', 'regions', 'region', 'markets'],
        'dealStage': ['dealStage', 'dealStages', 'stage', 'stages', 'projectStage'],
        'dealSize': ['dealSize', 'projectSize', 'size', 'capacity', 'capacityRange', 'dealSizeRange', 'investmentSize'],
        'dealStructure': ['dealStructure', 'structure', 'structures', 'transactionType', 'dealType']
      };
      const keys = keyVariants[key] || [key];
      return dims.find(d => keys.includes(d.key));
    };

    // Also check for flat capacity fields that might be outside dimensions
    const minCap = prefs.minCapacity || prefs.min || prefs.capacityMin;
    const maxCap = prefs.maxCapacity || prefs.max || prefs.capacityMax;
    const hasDealSize = getDimension('dealSize')?.values?.length > 0 || minCap || maxCap;

    const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

    // Format enriched date
    const formatEnrichedDate = (dateStr) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return date.toLocaleDateString();
    };

    const viewingDocBlobUrl = useMemo(() => {
      if (!viewingDoc?.base64) return null;
      try {
        const binary = atob(viewingDoc.base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const contentType = viewingDoc.contentType || 'application/pdf';
        const blob = new Blob([bytes], { type: contentType });
        return URL.createObjectURL(blob);
      } catch (e) { return null; }
    }, [viewingDoc?.base64, viewingDoc?.name]);

    useEffect(() => {
      return () => { if (viewingDocBlobUrl) URL.revokeObjectURL(viewingDocBlobUrl); };
    }, [viewingDocBlobUrl]);

    const handleDownload = (doc) => {
      if (doc.base64) {
        const link = document.createElement('a');
        link.href = `data:${doc.contentType || 'application/octet-stream'};base64,${doc.base64}`;
        link.download = doc.name;
        link.click();
      } else if (doc.driveFileId) {
        window.open(`https://drive.google.com/uc?export=download&id=${doc.driveFileId}`, '_blank');
      }
    };

    const isPdf = (name) => name?.toLowerCase().endsWith('.pdf');
    const isImage = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);

    // Count technologies stats
    const techDim = getDimension('technologies');
    const techStated = techDim?.values?.filter(v => v.stated || v.source === 'stated')?.length || 0;
    const techWithDeals = techDim?.values?.filter(v => v.deals > 0)?.length || 0;

    if (loading) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-3 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading shared counterpart...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-700 rounded-xl flex items-center justify-center">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <p className="font-bold text-slate-800">Global Infrastructure Sherpa</p>
                <p className="text-xs text-slate-500">Shared Counterpart</p>
              </div>
            </div>
            {isSignedIn ? (
              <button
                onClick={handleSaveToAccount}
                disabled={importing}
                className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 disabled:opacity-70 flex items-center gap-2"
              >
                {importing ? <Loader2 size={14} className="animate-spin" /> : null}
                {importing ? 'Saving...' : 'Save to my account'}
              </button>
            ) : (
              <button
                onClick={() => document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 flex items-center gap-2"
              >
                Sign up to save
              </button>
            )}
          </div>
        </div>

        {/* Share Info Banner */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center gap-3">
            <Share2 size={18} className="text-blue-800" />
            <span className="text-sm">
              Shared with you by <span className="font-medium">{data.owner}</span>
              {data.shared && <span className="text-slate-400 ml-2">• {new Date(data.shared).toLocaleDateString()}</span>}
            </span>
            <span className="ml-auto text-xs text-slate-400 flex items-center gap-1">
              <Eye size={14} /> View only
            </span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Counterpart Header Card - Matching App Design */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm">
            <div className="flex items-start gap-4">
              {/* Logo */}
              <div className="w-16 h-16 rounded-2xl overflow-hidden bg-slate-800 flex items-center justify-center flex-shrink-0 relative">
                {logoUrl && (
                  <img 
                    src={logoUrl} 
                    alt={data.name} 
                    className="w-full h-full object-contain p-1 absolute inset-0"
                    onError={(e) => { e.target.style.opacity = '0'; }}
                    onLoad={(e) => { e.target.style.opacity = '1'; }}
                    style={{ opacity: 0, transition: 'opacity 0.2s' }}
                  />
                )}
                <span className="text-2xl font-bold text-white">
                  {data.name?.charAt(0) || '?'}
                </span>
              </div>
              <div className="flex-1">
                {/* Name + Badge */}
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-900">{data.name}</h1>
                  {data.category && (
                    <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold uppercase tracking-wide">
                      {data.category}
                    </span>
                  )}
                </div>
                {/* Markets + Links Row */}
                <div className="flex items-center gap-4 mt-2 text-sm">
                  {marketsCount > 0 && (
                    <span className="text-slate-500">• {marketsCount} markets</span>
                  )}
                  {data.linkedin && (
                    <a href={data.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-800 hover:text-blue-700 flex items-center gap-1">
                      <Link2 size={14} /> LinkedIn
                    </a>
                  )}
                  {data.websiteUrl && (
                    <a href={data.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-800 hover:text-blue-700 flex items-center gap-1">
                      <Globe size={14} /> Website
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Card - Prominent Gray Box (Like App) */}
            {data.summary && (
              <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Summary</h3>
                <p className="text-slate-600 leading-relaxed">{data.summary}</p>
              </div>
            )}

            {/* Contact Info - Only if shared */}
            {data.includeContactInfo && (data.contactEmail || data.contactPhone) && (
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-4">
                {data.contactEmail && (
                  <a href={`mailto:${data.contactEmail}`} className="text-sm text-blue-800 hover:text-blue-700 flex items-center gap-1">
                    <Mail size={14} /> {data.contactEmail}
                  </a>
                )}
                {data.contactPhone && (
                  <a href={`tel:${data.contactPhone}`} className="text-sm text-blue-800 hover:text-blue-700 flex items-center gap-1">
                    <Phone size={14} /> {data.contactPhone}
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Tabs Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Tab Navigation */}
            <div className="border-b border-slate-200 flex px-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Overview
              </button>
              {hasPreferences && (
                <button
                  onClick={() => setActiveTab('preferences')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'preferences' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Preferences
                  <span className="w-2 h-2 rounded-full bg-blue-700" />
                </button>
              )}
              {hasDeals && (
                <button
                  onClick={() => setActiveTab('deals')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'deals' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Deals
                  <span className={`px-1.5 py-0.5 rounded text-xs ${activeTab === 'deals' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                    0
                  </span>
                </button>
              )}
              {data.includeDocuments && counterpartDocs.length > 0 && (
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'documents' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Documents
                  <span className={`px-1.5 py-0.5 rounded text-xs ${activeTab === 'documents' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                    {counterpartDocs.length}
                  </span>
                </button>
              )}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {prefs.summary && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Investment Thesis</h3>
                      <p className="text-slate-700 leading-relaxed">{prefs.summary}</p>
                    </div>
                  )}
                  {data.websiteUrl && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Website</h3>
                      <a href={data.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-800 hover:text-blue-700 flex items-center gap-1">
                        <Globe size={14} /> {data.websiteUrl}
                      </a>
                    </div>
                  )}
                  {!prefs.summary && !data.websiteUrl && (
                    <p className="text-slate-400 text-center py-8">No overview information available</p>
                  )}
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && data.includeDocuments && (
                <div className="space-y-2">
                  {counterpartDocs.map(doc => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => setViewingDoc(doc)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 flex items-center justify-center">
                          <FileText size={18} className="text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-700">{doc.name}</p>
                          <p className="text-xs text-slate-400">{doc.size || 'Document'}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                        className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                      >
                        <Download size={16} className="text-slate-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && hasPreferences && (
                <div className="space-y-4">
                  {/* Auto-Enriched Banner */}
                  {(prefs.sources?.length > 0 || prefs.enrichedAt) && (
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100 mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Sparkles size={20} className="text-blue-800" />
                        </div>
                        <div>
                          <p className="font-semibold text-blue-800">Investment Preferences Auto-Enriched</p>
                          <p className="text-sm text-blue-800">
                            {prefs.sources?.length > 0 && `Based on ${prefs.sources.length} sources`}
                            {prefs.sources?.length > 0 && prefs.enrichedAt && ' • '}
                            {prefs.enrichedAt && `Last updated ${formatEnrichedDate(prefs.enrichedAt)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Investment Thesis */}
                  {prefs.summary && (
                    <div className="p-4 bg-[color:var(--gis-bg)] rounded-xl border border-[color:var(--gis-rule)] mb-4">
                      <h3 className="text-sm font-semibold text-[color:var(--gis-body)] mb-2">Investment Thesis</h3>
                      <p className="text-[color:var(--gis-body)] leading-relaxed">{prefs.summary}</p>
                    </div>
                  )}

                  {/* Technologies Section */}
                  {getDimension('technologies') && (
                    <div className="border border-[color:var(--gis-rule)] rounded-xl overflow-hidden">
                      <button 
                        onClick={() => toggleSection('technologies')}
                        className="w-full px-4 py-3 flex items-center justify-between bg-[color:var(--gis-panel)] hover:bg-[color:var(--gis-bg)] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                            <Zap size={18} className="text-amber-600" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-[color:var(--gis-ink)]">Technologies</p>
                            <p className="text-sm text-[color:var(--gis-dim)]">
                              {techStated > 0 ? `${techStated} stated` : ''}
                              {techStated > 0 && techWithDeals > 0 ? ' • ' : ''}
                              {techWithDeals > 0 ? `${techWithDeals} with deal activity` : ''}
                              {techStated === 0 && techWithDeals === 0 ? `${getDimension('technologies').values?.length || 0} sectors` : ''}
                            </p>
                          </div>
                        </div>
                        <ChevronRight size={20} className={`text-[color:var(--gis-dim)] transition-transform ${expandedSections.technologies ? 'rotate-90' : ''}`} />
                      </button>
                      {expandedSections.technologies && (
                        <div className="border-t border-[color:var(--gis-rule)] p-4">
                          <div className="flex flex-wrap gap-2">
                            {getDimension('technologies').values?.map((tech, i) => (
                              <span key={i} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium">
                                {tech.value || tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Geographies Section */}
                  {getDimension('geographies') && (
                    <div className="border border-[color:var(--gis-rule)] rounded-xl overflow-hidden">
                      <button 
                        onClick={() => toggleSection('geographies')}
                        className="w-full px-4 py-3 flex items-center justify-between bg-[color:var(--gis-panel)] hover:bg-[color:var(--gis-bg)] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Globe size={18} className="text-blue-800" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-[color:var(--gis-ink)]">Geographies</p>
                            <p className="text-sm text-[color:var(--gis-dim)]">
                              {getDimension('geographies').values?.filter(v => v.status === 'active').length || getDimension('geographies').values?.length || 0} active
                              {getDimension('geographies').values?.filter(v => v.status === 'expanding').length > 0 && 
                                ` • ${getDimension('geographies').values?.filter(v => v.status === 'expanding').length} expanding`}
                            </p>
                          </div>
                        </div>
                        <ChevronRight size={20} className={`text-[color:var(--gis-dim)] transition-transform ${expandedSections.geographies ? 'rotate-90' : ''}`} />
                      </button>
                      {expandedSections.geographies && (
                        <div className="border-t border-[color:var(--gis-rule)] p-4">
                          <div className="flex flex-wrap gap-2">
                            {getDimension('geographies').values?.map((geo, i) => (
                              <span 
                                key={i} 
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
                                  geo.status === 'expanding' ? 'bg-green-100 text-green-700' :
                                  geo.status === 'active' ? 'bg-blue-100 text-blue-700' :
                                  geo.status === 'selective' ? 'bg-amber-100 text-amber-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {geo.status === 'expanding' && <ArrowUpRight size={14} />}
                                {geo.value || geo.region || geo}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Deal Stage Section */}
                  {getDimension('dealStage') && (
                    <div className="border border-[color:var(--gis-rule)] rounded-xl overflow-hidden">
                      <button 
                        onClick={() => toggleSection('dealStage')}
                        className="w-full px-4 py-3 flex items-center justify-between bg-[color:var(--gis-panel)] hover:bg-[color:var(--gis-bg)] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                            <BarChart3 size={18} className="text-blue-800" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-[color:var(--gis-ink)]">Deal Stage</p>
                            <p className="text-sm text-[color:var(--gis-dim)]">{getDimension('dealStage').values?.length || 0} options</p>
                          </div>
                        </div>
                        <ChevronRight size={20} className={`text-[color:var(--gis-dim)] transition-transform ${expandedSections.dealStage ? 'rotate-90' : ''}`} />
                      </button>
                      {expandedSections.dealStage && (
                        <div className="border-t border-[color:var(--gis-rule)] p-4">
                          <div className="flex flex-wrap gap-2">
                            {getDimension('dealStage').values?.map((stage, i) => (
                              <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                                {stage.value || stage}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Deal Size Section */}
                  {hasDealSize && (
                    <div className="border border-[color:var(--gis-rule)] rounded-xl overflow-hidden">
                      <button 
                        onClick={() => toggleSection('dealSize')}
                        className="w-full px-4 py-3 flex items-center justify-between bg-[color:var(--gis-panel)] hover:bg-[color:var(--gis-bg)] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                            <BarChart3 size={18} className="text-blue-800" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-[color:var(--gis-ink)]">Deal Size</p>
                            <p className="text-sm text-[color:var(--gis-dim)]">
                              {minCap && maxCap ? `${minCap} - ${maxCap} MW` 
                                : minCap ? `${minCap}+ MW` 
                                : maxCap ? `Up to ${maxCap} MW` 
                                : getDimension('dealSize')?.values?.map(v => v.value || v).join(' - ') 
                                || 'See details'}
                            </p>
                          </div>
                        </div>
                        <ChevronRight size={20} className={`text-[color:var(--gis-dim)] transition-transform ${expandedSections.dealSize ? 'rotate-90' : ''}`} />
                      </button>
                      {expandedSections.dealSize && (
                        <div className="border-t border-[color:var(--gis-rule)] p-4">
                          {/* 3-column layout like app */}
                          {(minCap || maxCap) ? (
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <p className="text-xs text-[color:var(--gis-dim)] mb-1">Min</p>
                                <p className="text-xl font-bold text-[color:var(--gis-ink)]">{minCap || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-[color:var(--gis-dim)] mb-1">Sweet Spot</p>
                                <p className="text-xl font-bold text-blue-800">
                                  {prefs.sweetSpotMin && prefs.sweetSpotMax 
                                    ? `${prefs.sweetSpotMin}-${prefs.sweetSpotMax} MW`
                                    : minCap && maxCap 
                                      ? `${Math.round((Number(minCap) + Number(maxCap)) / 2)} MW`
                                      : '—'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-[color:var(--gis-dim)] mb-1">Max</p>
                                <p className="text-xl font-bold text-[color:var(--gis-ink)]">{maxCap || '—'}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {getDimension('dealSize')?.values?.map((size, i) => (
                                <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                                  {size.value || size}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Deal Structure Section */}
                  {getDimension('dealStructure') && (
                    <div className="border border-[color:var(--gis-rule)] rounded-xl overflow-hidden">
                      <button 
                        onClick={() => toggleSection('dealStructure')}
                        className="w-full px-4 py-3 flex items-center justify-between bg-[color:var(--gis-panel)] hover:bg-[color:var(--gis-bg)] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[color:var(--gis-rule)] rounded-xl flex items-center justify-center">
                            <BarChart3 size={18} className="text-[color:var(--gis-body)]" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-[color:var(--gis-ink)]">Deal Structure</p>
                            <p className="text-sm text-[color:var(--gis-dim)]">{getDimension('dealStructure').values?.length || 0} options</p>
                          </div>
                        </div>
                        <ChevronRight size={20} className={`text-[color:var(--gis-dim)] transition-transform ${expandedSections.dealStructure ? 'rotate-90' : ''}`} />
                      </button>
                      {expandedSections.dealStructure && (
                        <div className="border-t border-[color:var(--gis-rule)] p-4">
                          <div className="flex flex-wrap gap-2">
                            {getDimension('dealStructure').values?.map((struct, i) => (
                              <span key={i} className="px-3 py-1.5 bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] rounded-lg text-sm font-medium">
                                {struct.value || struct}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Investment Profile Section */}
                  {(() => {
                    // Derive Primary Focus from technologies if not set
                    const techDimValues = getDimension('technologies')?.values || [];
                    const derivedPrimaryFocus = prefs.primaryFocus?.length > 0 
                      ? prefs.primaryFocus 
                      : techDimValues.slice(0, 3).map(t => t.value || t);
                    
                    // Check if we have any data to show
                    const hasProfileData = prefs.confidence || derivedPrimaryFocus.length > 0 || prefs.strategicShift || prefs.summary;
                    
                    if (!hasProfileData) return null;
                    
                    return (
                      <div className="mt-6 p-5 bg-[color:var(--gis-panel)] border border-[color:var(--gis-rule)] rounded-xl">
                        <h3 className="text-lg font-semibold text-[color:var(--gis-ink)] mb-4">Investment Profile</h3>
                        
                        {/* Overall Confidence */}
                        {prefs.confidence && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-[color:var(--gis-body)]">Overall Confidence</span>
                              <span className="text-sm font-semibold text-[color:var(--gis-ink)]">{Math.round((typeof prefs.confidence === 'number' ? prefs.confidence : parseFloat(prefs.confidence) || 0) * 100)}%</span>
                            </div>
                            <div className="w-full h-2 bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-700 rounded-full transition-all" 
                                style={{ width: `${(typeof prefs.confidence === 'number' ? prefs.confidence : parseFloat(prefs.confidence) || 0) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Primary Focus */}
                        {derivedPrimaryFocus.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm text-[color:var(--gis-body)] mb-2">Primary Focus</p>
                            <div className="flex flex-wrap gap-2">
                              {derivedPrimaryFocus.map((focus, i) => (
                                <span key={i} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
                                  {focus}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Strategic Shift */}
                        {prefs.strategicShift && (
                          <div>
                            <p className="text-sm text-[color:var(--gis-body)] mb-2">Strategic Shift</p>
                            <p className="text-[color:var(--gis-body)]">{prefs.strategicShift}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Deals Tab */}
              {activeTab === 'deals' && hasDeals && (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-800">Deal History</h3>
                      <p className="text-sm text-slate-500">0 transactions on record</p>
                    </div>
                  </div>
                  
                  {/* Deals list removed — enrichment recentDeals data was hallucinated */}
                </div>
              )}
            </div>
          </div>

          {/* Sign Up CTA - matches ProjectSharePage */}
          {!isSignedIn && (
            <div id="signup-section" className="mt-6 pt-6 border-t border-slate-100">
              <div className="text-center mb-5">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Save this counterpart</h3>
                <p className="text-sm text-slate-500">Create a free account to save and track updates</p>
              </div>
              
              <div className="space-y-3 max-w-sm mx-auto">
                <button 
                  onClick={() => handleOAuthSignIn('oauth_google')}
                  disabled={oauthLoading}
                  className="w-full py-3 px-4 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 flex items-center justify-center gap-3 transition-all shadow-sm disabled:opacity-70"
                >
                  {oauthLoading === 'oauth_google' ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Continue with Google
                </button>
                
                <button 
                  onClick={() => handleOAuthSignIn('oauth_linkedin_oidc')}
                  disabled={oauthLoading}
                  className="w-full py-3 px-4 bg-[#0A66C2] border border-[#0A66C2] rounded-xl font-medium text-white hover:bg-[#004182] flex items-center justify-center gap-3 transition-all shadow-sm disabled:opacity-70"
                >
                  {oauthLoading === 'oauth_linkedin_oidc' ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Linkedin size={20} />
                  )}
                  Continue with LinkedIn
                </button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-slate-400">or</span>
                  </div>
                </div>

                <SignUpButton mode="redirect" forceRedirectUrl={window.location.href}>
                  <button 
                    onClick={storePendingImport}
                    className="w-full py-3 px-4 bg-slate-900 border border-slate-900 rounded-xl font-medium text-white hover:bg-slate-800 flex items-center justify-center gap-2 transition-all shadow-sm"
                  >
                    <Mail size={18} />
                    Continue with email
                  </button>
                </SignUpButton>
                
                <p className="text-center text-xs text-slate-400 mt-4">
                  Free • No credit card required
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <footer className="bg-white border-t border-slate-200 px-4 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <div className="w-5 h-5 rounded bg-blue-700 flex items-center justify-center text-white font-bold text-xs">D</div>
              Powered by <strong className="text-slate-900">Deal</strong><strong className="text-blue-700">Flow</strong>
            </div>
            <button
              onClick={() => {
                if (isSignedIn) {
                  window.location.href = window.location.origin;
                } else {
                  document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="flex items-center gap-2 text-sm text-blue-800 hover:text-blue-700 font-medium group"
            >
              Create your own
              <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </footer>

        {/* Floating Save Button */}
        {showSaveButton && (
          <button
            onClick={() => {
              if (isSignedIn) {
                handleSaveToAccount();
              } else {
                document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            disabled={importing}
            className="fixed bottom-20 right-6 flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all animate-bounce-once disabled:opacity-70"
          >
            {importing ? <Loader2 size={18} className="animate-spin" /> : <Star size={18} />}
            <span className="text-sm font-medium">{importing ? 'Saving...' : (isSignedIn ? 'Save to my account' : 'Sign in to save')}</span>
          </button>
        )}

        {/* Document Viewer Modal */}
        {viewingDoc && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText size={24} className="text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-900">{viewingDoc.name}</p>
                    <p className="text-xs text-slate-500">{viewingDoc.size}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleDownload(viewingDoc)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1">
                    <Download size={14} /> Download
                  </button>
                  <button onClick={() => setViewingDoc(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden bg-slate-100 p-4">
                {viewingDoc.base64 && viewingDocBlobUrl ? (
                  isPdf(viewingDoc.name) ? (
                    <iframe src={viewingDocBlobUrl} className="w-full h-full min-h-[60vh] rounded-lg bg-white" title={viewingDoc.name} />
                  ) : isImage(viewingDoc.name) ? (
                    <div className="flex items-center justify-center h-full min-h-[60vh] bg-white rounded-lg">
                      <img src={viewingDocBlobUrl} alt={viewingDoc.name} className="max-w-full max-h-[60vh] object-contain" />
                    </div>
                  ) : (
                    <iframe src={viewingDocBlobUrl} className="w-full h-full min-h-[60vh] rounded-lg bg-white" title={viewingDoc.name} />
                  )
                ) : viewingDoc.driveFileId ? (
                  <iframe src={`https://drive.google.com/file/d/${viewingDoc.driveFileId}/preview`} className="w-full h-full min-h-[60vh] rounded-lg bg-white" title={viewingDoc.name} allow="autoplay" />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full min-h-[60vh]">
                    <FileText size={32} className="text-slate-400 mb-4" />
                    <p className="text-slate-600 font-medium mb-2">Preview not available</p>
                    <p className="text-slate-400 text-sm mb-4">Download the file to view it</p>
                    <button onClick={() => handleDownload(viewingDoc)} className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 flex items-center gap-2">
                      <Download size={14} /> Download
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };


  // Project Extraction Modal
  const ProjectExtractionModal = ({ doc, onClose }) => {
    const [extracting, setExtracting] = useState(false);
    const [extractedData, setExtractedData] = useState(null);
    const [editedData, setEditedData] = useState(null);
    const [mode, setMode] = useState('new');
    const [selectedProjectId, setSelectedProjectId] = useState(doc?.projectId || null);
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [appendNotes, setAppendNotes] = useState(true);
    const [extractionError, setExtractionError] = useState(null);

    const extractProjectInfo = async () => {
      // Require sign-in for extraction - prompt user to sign in
      if (!user?.id) {
        setExtractionError('Sign in to use AI extraction — it\'s free!');
        return;
      }

      const hasPdfBase64 = doc?.base64 && (doc?.contentType === 'pdf' || doc?.name?.endsWith('.pdf'));
      const hasImageBase64 = doc?.base64 && (doc?.contentType === 'image' || /\.(png|jpg|jpeg|gif|webp)$/i.test(doc?.name));
      let hasTextContent = doc?.content && !doc.content.startsWith('[PDF Document') && !doc.content.startsWith('[Binary') && !doc.content.startsWith('[Image');
      
      // For PDFs, try to extract text from the PDF first
      let extractedPdfText = '';
      if (hasPdfBase64 && !hasTextContent) {
        setExtracting(true);
        setExtractionError(null);
        
        try {
          // Load pdf.js library dynamically
          if (!window.pdfjsLib) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
            document.head.appendChild(script);
            await new Promise((resolve, reject) => {
              script.onload = resolve;
              script.onerror = reject;
            });
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          }
          
          // Convert base64 to ArrayBuffer
          const binaryString = atob(doc.base64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Load and extract text from PDF
          const pdf = await window.pdfjsLib.getDocument({ data: bytes }).promise;
          const textParts = [];
          
          for (let i = 1; i <= Math.min(pdf.numPages, 20); i++) { // Limit to first 20 pages
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            textParts.push(pageText);
          }
          
          extractedPdfText = textParts.join('\n\n');
          
          if (extractedPdfText.trim().length > 100) {
            hasTextContent = true;
          }
        } catch (pdfError) {
          console.error('PDF text extraction failed:', pdfError);
          // Continue anyway - backend may handle it
        }
      }
      
      if (!hasPdfBase64 && !hasImageBase64 && !hasTextContent && !extractedPdfText) {
        setExtractionError('No readable content available. Please re-upload this document.');
        return;
      }

      // Check file size
      if (doc?.base64) {
        const base64SizeBytes = (doc.base64.length * 3) / 4;
        const sizeMB = base64SizeBytes / (1024 * 1024);
        if (sizeMB > 15) {
          setExtractionError(`File is too large (${sizeMB.toFixed(1)}MB). Maximum size is ~15MB. Try with a smaller file.`);
          return;
        }
      }
      
      setExtracting(true);
      setExtractionError(null);
      
      try {
        // Determine content and type to send
        let content, isBase64 = false, mimeType = null;
        
        if (extractedPdfText) {
          content = extractedPdfText;
        } else if (hasPdfBase64) {
          content = doc.base64;
          isBase64 = true;
          mimeType = 'application/pdf';
        } else if (hasImageBase64) {
          content = doc.base64;
          isBase64 = true;
          mimeType = doc.name.match(/\.png$/i) ? 'image/png' : 
            doc.name.match(/\.gif$/i) ? 'image/gif' : 
            doc.name.match(/\.webp$/i) ? 'image/webp' : 'image/jpeg';
          
          // Compress image before sending to AI (max 1000px width)
          content = await compressImageForAI(content, mimeType, 1000);
        } else {
          content = doc.content.substring(0, 30000);
        }

        // Call centralized AI extraction API
        const response = await fetch('/api/ai-extract', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user?.id,
            content,
            isBase64,
            mimeType,
          }),
        });

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setExtractionError('AI extraction service unavailable. Please try again later.');
          setExtracting(false);
          return;
        }

        const result = await response.json();

        if (!response.ok) {
          if (response.status === 429) {
            setExtractionError(`Monthly extraction limit reached (${result.used}/${result.limit}). Upgrade your plan for more extractions.`);
          } else {
            setExtractionError(result.error || 'Extraction failed');
          }
          setExtracting(false);
          return;
        }

        const extracted = result.data;
        
        if (extracted.parseError) {
          setExtractionError('Failed to parse AI response. Please try again.');
          setExtracting(false);
          return;
        }

        // Extract counterpart names from the new structure
        const counterpartsList = extracted.counterparts || [];
        const counterpartNames = counterpartsList.map(c => c.name).filter(Boolean);

        setExtractedData({
          ...extracted,
          counterpartsList, // Store full counterpart data including preferences
          counterpartNames  // Keep names for backward compatibility
        });
        setEditedData({
          name: extracted.project?.name || extracted.projectName || '',
          technology: technologies.includes(extracted.project?.technology || extracted.technology) 
            ? (extracted.project?.technology || extracted.technology) 
            : (technologies[0] || 'Other'),
          capacity: extracted.project?.capacity || extracted.capacity || '',
          location: extracted.project?.location || extracted.location || '',
          investmentSize: extracted.investmentSize || '',
          expectedReturn: extracted.expectedReturn || '',
          stage: stages.includes(extracted.project?.stage || extracted.stage) 
            ? (extracted.project?.stage || extracted.stage) 
            : 'Under Development',
          pipelineStatus: 'Screening',
          notes: [extracted.project?.description || extracted.description, extracted.summary, 
            (extracted.keyDates || []).join(', ')].filter(Boolean).join('\n\n'),
          counterpartsList, // Full counterpart data
          counterpartNames,
          emails: counterpartsList.map(c => c.email).filter(Boolean),
          phones: counterpartsList.map(c => c.phone).filter(Boolean),
          documentType: extracted.documentType || 'Other',
          isLive: true
        });
        setSelectedEntities(counterpartNames);
        if (doc?.projectId) {
          setMode('existing');
          setSelectedProjectId(doc.projectId);
        }
      } catch (error) {
        console.error('Extraction failed:', error);
        setExtractionError(`Extraction failed: ${error.message}`);
      }
      
      setExtracting(false);
    };

    const toggleEntity = (name) => {
      setSelectedEntities(prev => 
        prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
      );
    };

    const handleSave = () => {
      if (!editedData) return;
      
      let targetProjectId;
      
      if (mode === 'new') {
        if (!editedData.name?.trim()) {
          alert('Project name is required');
          return;
        }
        const newProject = {
          id: Date.now(),
          name: editedData.name,
          technology: editedData.technology,
          capacity: Number(editedData.capacity) || 0,
          location: editedData.location,
          investmentSize: Number(editedData.investmentSize) || 0,
          expectedReturn: Number(editedData.expectedReturn) || 0,
          stage: editedData.stage,
          pipelineStatus: editedData.pipelineStatus,
          notes: editedData.notes,
          isLive: true,
          counterpartIds: [],
          deadline: '',
          lastActivity: new Date().toISOString().split('T')[0],
          createdAt: new Date().toISOString().split('T')[0]
        };
        setProjects(prev => [...prev, newProject]);
        targetProjectId = newProject.id;
      } else {
        if (!selectedProjectId) {
          alert('Please select a project');
          return;
        }
        targetProjectId = selectedProjectId;
        if (appendNotes && editedData.notes) {
          setProjects(prev => prev.map(p => 
            p.id === selectedProjectId 
              ? {...p, notes: (p.notes || '') + `\n\n--- From ${doc.name} ---\n${editedData.notes}`, lastActivity: new Date().toISOString().split('T')[0]}
              : p
          ));
        }
      }
      
      // Link document
      if (doc?.id) {
        setDocuments(prev => prev.map(d => d.id === doc.id ? {...d, projectId: targetProjectId, notes: editedData.notes} : d));
      }
      
      // Create counterparts with extracted contact info and investment preferences
      if (selectedEntities.length > 0) {
        const newCounterpartIds = [];
        const toCreate = selectedEntities.filter(name => !counterparts.find(c => c.name.toLowerCase() === name.toLowerCase()));
        
        if (toCreate.length > 0) {
          const counterpartsList = editedData?.counterpartsList || [];
          
          const newOnes = toCreate.map((name) => {
            const id = Date.now() + Math.random();
            newCounterpartIds.push(id);
            
            // Find matching extracted counterpart data
            const extractedCounterpart = counterpartsList.find(
              c => c.name?.toLowerCase() === name.toLowerCase()
            ) || {};
            
            // Determine category based on role
            let category = 'Other';
            const role = (extractedCounterpart.role || '').toLowerCase();
            if (role.includes('investor') || role.includes('fund') || role.includes('bank')) category = 'Investor';
            else if (role.includes('developer')) category = 'Developer';
            else if (role.includes('advisor') || role.includes('consultant')) category = 'Advisor';
            else if (role.includes('legal') || role.includes('lawyer')) category = 'Legal Counsel';
            else if (role.includes('broker') || role.includes('agent')) category = 'Broker / Agent';
            else if (role.includes('lender')) category = 'Lender';
            else if (role.includes('operator')) category = 'Operator';
            
            // Process investment preferences if available
            let investmentPreferences = null;
            const prefs = extractedCounterpart.investmentPreferences;
            if (prefs && (prefs.minCapacity || prefs.maxCapacity || prefs.minReturn || 
                prefs.technologies?.length > 0 || prefs.stages?.length > 0 || 
                prefs.regions?.length > 0 || prefs.notes)) {
              investmentPreferences = {
                minCapacity: prefs.minCapacity ? Number(prefs.minCapacity) : null,
                maxCapacity: prefs.maxCapacity ? Number(prefs.maxCapacity) : null,
                minReturn: prefs.minReturn ? Number(prefs.minReturn) : null,
                technologies: prefs.technologies || [],
                stages: prefs.stages || [],
                regions: prefs.regions || [],
                notes: prefs.notes || ''
              };
            }
            
            return { 
              id, 
              type: extractedCounterpart.type?.toLowerCase() === 'person' ? 'person' : 'company', 
              name, 
              category,
              contactEmail: extractedCounterpart.email || '', 
              contactPhone: extractedCounterpart.phone || '', 
              location: extractedCounterpart.location || editedData?.location || '', 
              linkedinUrl: '', 
              websiteUrl: extractedCounterpart.website || '', 
              relatedCounterpartIds: [], 
              investmentPreferences,
              notes: `Extracted from: ${doc.name}${extractedCounterpart.role ? `\nRole: ${extractedCounterpart.role}` : ''}`, 
              tags: [],
              lastInteraction: new Date().toISOString().split('T')[0] 
            };
          });
          setCounterparts(prev => [...prev, ...newOnes]);
        }
        
        const existingIds = selectedEntities.map(name => counterparts.find(c => c.name.toLowerCase() === name.toLowerCase())?.id).filter(Boolean);
        const allIds = [...newCounterpartIds, ...existingIds];
        
        if (allIds.length > 0) {
          setProjects(prev => prev.map(p => 
            p.id === targetProjectId 
              ? {...p, counterpartIds: [...new Set([...(p.counterpartIds || []), ...allIds])]}
              : p
          ));
        }
      }
      
      onClose();
    };

    const isPdfWithoutData = (doc?.contentType === 'pdf' || doc?.name?.endsWith('.pdf')) && !doc?.base64;

    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center flex-shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Extract with AI</h2>
              <p className="text-sm text-slate-600">From: {doc?.name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-xl text-slate-600 hover:text-slate-700"><X size={20} /></button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6">
            {!extractedData ? (
              <div className="space-y-6">
                {/* No Provider Selected */}
                {/* Error Message */}
                {extractionError && (
                  <div className={`p-4 rounded-xl border ${extractionError.includes('Sign in') ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
                    {extractionError.includes('Sign in') ? (
                      <SignInButton mode="redirect">
                        <button className="text-sm text-blue-700 flex items-center gap-2 hover:text-blue-800 transition-colors w-full justify-center font-medium">
                          <User size={16} /> {extractionError} <ArrowRight size={14} />
                        </button>
                      </SignInButton>
                    ) : (
                      <p className="text-sm text-red-700 flex items-center gap-2">
                        <AlertCircle size={16} /> {extractionError}
                      </p>
                    )}
                  </div>
                )}

                {/* Main Extraction UI */}
                <div className="text-center py-8">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-blue-100 to-blue-100">
                    <Sparkles size={32} className="text-blue-800" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">AI Document Extraction</h3>
                  {isPdfWithoutData ? (
                    <p className="text-blue-800 mb-6">This PDF needs to be re-uploaded to enable extraction.</p>
                  ) : (
                    <p className="text-slate-600 mb-6 max-w-md mx-auto">
                      AI will analyze your document and extract project details, counterparty information, contact details, and more.
                    </p>
                  )}
                  
                  <button 
                    onClick={extractProjectInfo} 
                    disabled={extracting || isPdfWithoutData}
                    className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg bg-gradient-to-r from-blue-700 to-blue-700 hover:from-blue-800 hover:to-blue-800 shadow-blue-700/25">
                    {extracting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Analyzing document...
                      </>
                    ) : (
                      <>
                        <Zap size={20} />
                        Extract with AI
                      </>
                    )}
                  </button>
                </div>

                {/* What will be extracted */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="font-medium text-slate-700 mb-1">📋 Project Info</p>
                    <p className="text-slate-500 text-xs">Name, location, capacity, stage</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="font-medium text-slate-700 mb-1">👥 Counterparts</p>
                    <p className="text-slate-500 text-xs">Companies and people mentioned</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="font-medium text-slate-700 mb-1">📧 Contact Info</p>
                    <p className="text-slate-500 text-xs">Emails and phone numbers</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="font-medium text-slate-700 mb-1">📝 Summary</p>
                    <p className="text-slate-500 text-xs">Key dates and description</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-700 flex items-center gap-2">
                    <CheckCircle size={16} className="text-blue-700" />
                    <span className="font-medium">Extraction complete!</span>
                    {editedData?.documentType && (
                      <span className="px-2 py-0.5 bg-white rounded-full text-xs">{editedData.documentType}</span>
                    )}
                  </p>
                </div>

                {/* Share prompt */}
                <div className="p-4 bg-gradient-to-r from-blue-100 to-blue-100 rounded-xl border border-blue-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-700 flex items-center justify-center">
                      <Share2 size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-900">Share this document?</p>
                      <p className="text-xs text-blue-700">Send it to colleagues or counterparts</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => { onClose(); setSharingDocument(doc); }}
                    className="px-4 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800 text-sm font-medium flex items-center gap-2">
                    <Share2 size={16} /> Share
                  </button>
                </div>

                {/* Extracted Contact Info */}
                {(editedData?.emails?.length > 0 || editedData?.phones?.length > 0) && (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2">📇 Extracted Contact Info</h4>
                    <div className="flex flex-wrap gap-2">
                      {editedData.emails?.map((email, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-lg text-sm text-blue-700">
                          <Mail size={12} /> {email}
                        </span>
                      ))}
                      {editedData.phones?.map((phone, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-lg text-sm text-blue-700">
                          <Phone size={12} /> {phone}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <button onClick={() => setMode('new')}
                    className={`flex-1 p-3 rounded-xl border-2 text-left ${mode === 'new' ? 'border-blue-700 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <p className="font-medium text-slate-900">Create New Project</p>
                    <p className="text-xs text-slate-500">Start fresh with extracted data</p>
                  </button>
                  <button onClick={() => setMode('existing')}
                    className={`flex-1 p-3 rounded-xl border-2 text-left ${mode === 'existing' ? 'border-blue-700 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <p className="font-medium text-slate-900">Link to Existing</p>
                    <p className="text-xs text-slate-500">Append to existing project</p>
                  </button>
                </div>
                
                {mode === 'existing' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Select Project</label>
                    <select value={selectedProjectId || ''} onChange={(e) => setSelectedProjectId(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl">
                      <option value="">Choose...</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <label className="flex items-center gap-2 mt-2 text-sm">
                      <input type="checkbox" checked={appendNotes} onChange={(e) => setAppendNotes(e.target.checked)} className="rounded" />
                      Append extracted notes to project
                    </label>
                  </div>
                )}
                
                {mode === 'new' && editedData && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Project Name *</label>
                      <input type="text" value={editedData.name || ''} onChange={(e) => setEditedData({...editedData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{sectorConfig.categoryLabel}</label>
                      <select value={editedData.technology || technologies[0]} onChange={(e) => setEditedData({...editedData, technology: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl">
                        {[...technologies].sort().map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{sectorConfig.primaryMetric.label} {sectorConfig.primaryMetric.unit && `(${sectorConfig.primaryMetric.unit})`}</label>
                      <input type="number" value={editedData.capacity || ''} onChange={(e) => setEditedData({...editedData, capacity: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('location')}</label>
                      <input type="text" value={editedData.location || ''} onChange={(e) => setEditedData({...editedData, location: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('stage')}</label>
                      <select value={editedData.stage || 'Under Development'} onChange={(e) => setEditedData({...editedData, stage: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl">
                        {[...stages].sort().map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                )}
                
                {editedData && (
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('notes')}</label>
                    <textarea value={editedData.notes || ''} onChange={(e) => setEditedData({...editedData, notes: e.target.value})}
                      rows={4} className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm" />
                  </div>
                )}
                
                {editedData?.counterpartNames?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Entities to Create/Link ({selectedEntities.length} selected)</label>
                    <div className="flex flex-wrap gap-2 p-3 bg-slate-100 rounded-xl">
                      {editedData.counterpartNames.map((name, i) => (
                        <button key={i} onClick={() => toggleEntity(name)}
                          className={`px-3 py-1.5 rounded-full text-sm ${selectedEntities.includes(name) ? 'bg-blue-700 text-white' : 'bg-white border text-slate-700'}`}>
                          {selectedEntities.includes(name) && <CheckCircle size={14} className="inline mr-1" />}
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="p-6 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
            <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50">{t('cancel')}</button>
            {extractedData && (
              <button onClick={handleSave} className="px-6 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800">
                {mode === 'new' ? 'Create Project' : 'Link & Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Document Edit Modal
  const DocumentEditModal = ({ doc, onClose, onSave }) => {
    const [form, setForm] = useState({
      name: doc.name || '',
      category: doc.category || 'Other',
      projectId: doc.projectId || null,
      counterpartIds: doc.counterpartIds || [],
      notes: doc.notes || ''
    });
    const [counterpartSearch, setCounterpartSearch] = useState('');
    const [showCounterpartDropdown, setShowCounterpartDropdown] = useState(false);

    const filteredCounterparts = counterparts.filter(c => 
      c.name.toLowerCase().includes(counterpartSearch.toLowerCase()) &&
      !form.counterpartIds.includes(c.id)
    ).slice(0, 10);

    const selectedCounterparts = counterparts.filter(c => form.counterpartIds.includes(c.id));

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave({
        ...doc,
        ...form
      });
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center flex-shrink-0">
            <h2 className="text-xl font-semibold text-slate-900">{t('editDocument')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-xl text-slate-600 hover:text-slate-700"><X size={20} /></button>
          </div>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('documentName')}</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-700/50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('category')}</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-700/50"
                >
                  {documentCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('linkedProject')}</label>
                <SearchableSelect
                  items={projects}
                  value={form.projectId || 'all'}
                  onChange={(id) => setForm({...form, projectId: id === 'all' ? null : id})}
                  placeholder={t('noProjectLinked')}
                  showAll={true}
                  allLabel={t('noProjectLinked')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('linkedCounterparts')}</label>
                
                {/* Selected counterparts as removable chips */}
                {selectedCounterparts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedCounterparts.map(c => (
                      <span key={c.id} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {c.name}
                        <button
                          type="button"
                          onClick={() => setForm({...form, counterpartIds: form.counterpartIds.filter(id => id !== c.id)})}
                          className="hover:text-blue-900"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Searchable dropdown */}
                <div className="relative">
                  <input
                    type="text"
                    value={counterpartSearch}
                    onChange={(e) => { setCounterpartSearch(e.target.value); setShowCounterpartDropdown(true); }}
                    onFocus={() => setShowCounterpartDropdown(true)}
                    placeholder="Search counterparts..."
                    className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-700/50"
                  />
                  {showCounterpartDropdown && counterpartSearch && filteredCounterparts.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {filteredCounterparts.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setForm({...form, counterpartIds: [...form.counterpartIds, c.id]});
                            setCounterpartSearch('');
                            setShowCounterpartDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                        >
                          {c.type === 'company' ? <Building2 size={14} className="text-blue-700" /> : <User size={14} className="text-blue-700" />}
                          <span className="text-sm">{c.name}</span>
                          <span className="text-xs text-slate-400 ml-auto">{c.category}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('notes')}</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-700/50"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" className="px-6 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Save handlers
  const handleSaveProject = (project) => {
    if (editingProject) {
      setProjects(projects.map(p => p.id === project.id ? project : p));
    } else {
      setProjects([...projects, project]);
    }
    setEditingProject(null);
  };

  const handleSaveCounterpart = async (counterpart) => {
    // Check limit for new counterparts
    if (!editingCounterpart && !canAddEntity()) {
      setShowLimitModal(true);
      return;
    }

    // Save to database first
    try {
      if (editingCounterpart) {
        // Update existing counterpart in database
        const response = await authFetch(`/api/counterparts?id=${counterpart.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ counterpart })
        });
        if (!response.ok) {
          const err = await response.json();
          console.error('[SaveCounterpart] Update failed:', err);
          alert('Failed to save counterpart: ' + (err.error || 'Unknown error'));
          return;
        }
      } else {
        // Create new counterpart in database
        const response = await authFetch('/api/counterparts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ counterpart })
        });
        if (!response.ok) {
          const err = await response.json();
          console.error('[SaveCounterpart] Create failed:', err);
          alert('Failed to create counterpart: ' + (err.error || 'Unknown error'));
          return;
        }
        const result = await response.json();
        // Use the ID returned from database
        if (result.counterpart?.id) {
          counterpart.id = result.counterpart.id;
        }
      }
    } catch (err) {
      console.error('[SaveCounterpart] Error:', err);
      alert('Error saving counterpart: ' + err.message);
      return;
    }

    sessionStorage.setItem('counterparts_cache_bust', Date.now());
    sessionStorage.setItem('dashboard_cache_bust', Date.now());

    // Update local state
    if (editingCounterpart) {
      // Get old relatedCounterpartIds to compare
      const oldRelated = editingCounterpart.relatedCounterpartIds || [];
      const newRelated = counterpart.relatedCounterpartIds || [];

      // Find added and removed relationships
      const addedIds = newRelated.filter(id => !oldRelated.includes(id));
      const removedIds = oldRelated.filter(id => !newRelated.includes(id));

      // Update counterparts with bidirectional links
      setCounterparts(counterparts.map(c => {
        if (c.id === counterpart.id) {
          // Ensure websiteUrl is synced with website (API maps website → websiteUrl)
          return { ...c, ...counterpart, websiteUrl: counterpart.website || counterpart.websiteUrl || c.websiteUrl };
        }
        // Add reverse link for newly added relationships
        if (addedIds.includes(c.id)) {
          const currentRelated = c.relatedCounterpartIds || [];
          if (!currentRelated.includes(counterpart.id)) {
            return { ...c, relatedCounterpartIds: [...currentRelated, counterpart.id] };
          }
        }
        // Remove reverse link for removed relationships
        if (removedIds.includes(c.id)) {
          return { ...c, relatedCounterpartIds: (c.relatedCounterpartIds || []).filter(id => id !== counterpart.id) };
        }
        return c;
      }));
    } else {
      // For new counterparts, add reverse links for any relationships
      const newRelated = counterpart.relatedCounterpartIds || [];
      if (newRelated.length > 0) {
        setCounterparts(prev => {
          const updated = prev.map(c => {
            if (newRelated.includes(c.id)) {
              const currentRelated = c.relatedCounterpartIds || [];
              if (!currentRelated.includes(counterpart.id)) {
                return { ...c, relatedCounterpartIds: [...currentRelated, counterpart.id] };
              }
            }
            return c;
          });
          return [...updated, counterpart];
        });
      } else {
        setCounterparts([...counterparts, counterpart]);
      }
    }

    // Publish to public companies directory if company with preferences
    if (counterpart.type === 'company' && counterpart.investmentPreferences) {
      fetch('/api/publish-company', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          counterpart: counterpart,
          investmentPreferences: counterpart.investmentPreferences
        })
      }).then(res => res.json())
        .catch(err => console.error('[Publish] Error:', err));
    }

    setEditingCounterpart(null);
  };

  // Create a person from deal's Key People as a counterpart
  // Also links them to the deal if dealId is provided
  const createPersonAsCounterpart = async (person, linkedCompanyId = null, dealId = null) => {
    // Check if person already exists
    const existingPerson = counterparts.find(c =>
      c.type === 'person' &&
      c.name?.toLowerCase().trim() === person.name?.toLowerCase().trim()
    );
    if (existingPerson) {
      // If person exists but not linked to this deal, link them
      if (dealId && existingPerson.id) {
        try {
          await authFetch('/api/deals', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              dealId,
              linkCounterpart: { counterpartId: existingPerson.id, role: 'key_person' }
            })
          });
        } catch (e) {
          // Link failed silently - person already exists
        }
      }
      return existingPerson;
    }

    // Check limit
    if (!canAddEntity()) {
      setShowLimitModal(true);
      return null;
    }

    const newPerson = {
      name: person.name,
      type: 'person',
      role: person.title || '',
      company: person.organization || person.company || '',
      companyId: linkedCompanyId,
      website: person.linkedin || '',
      notes: person.dealHeadline ? `Added from deal: ${person.dealHeadline}` : '',
      tags: [],
      relatedCounterpartIds: linkedCompanyId ? [linkedCompanyId] : [],
    };

    try {
      const response = await authFetch('/api/counterparts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counterpart: newPerson })
      });

      if (!response.ok) {
        const err = await response.json();
        console.error('[CreatePerson] Create failed:', err);
        alert('Failed to create person: ' + (err.error || 'Unknown error'));
        return null;
      }

      const result = await response.json();
      if (result.counterpart?.id) {
        newPerson.id = result.counterpart.id;

        // Link person to the deal so they're not orphaned
        if (dealId) {
          try {
            await authFetch('/api/deals', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                dealId,
                linkCounterpart: { counterpartId: newPerson.id, role: 'key_person' }
              })
            });
          } catch (e) {
            // Link failed - non-critical
          }
        }
      }

      // Add to local state
      setCounterparts(prev => [...prev, newPerson]);

      return newPerson;
    } catch (err) {
      console.error('[CreatePerson] Error:', err);
      alert('Error creating person: ' + err.message);
      return null;
    }
  };

  // AI Deal Discovery - search for deals
  const handleDiscoverSearch = async (query) => {
    if (!query.trim()) return;

    setDiscoverLoading(true);
    setDiscoverError(null);
    setDiscoverResults([]);
    setSelectedDiscoverDeals(new Set());

    try {
      const response = await authFetch('/api/discover-deals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim(), maxResults: 15 })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setDiscoverError(result.error || 'Search failed');
        return;
      }

      setDiscoverResults(result.deals || []);

      if (result.deals?.length === 0) {
        setDiscoverError('No deals found. Try a different search query.');
      }
    } catch (error) {
      console.error('[Discover] Error:', error);
      setDiscoverError(error.message || 'Search failed');
    } finally {
      setDiscoverLoading(false);
    }
  };

  // Import a discovered deal - extract and open in deal modal
  const handleImportDiscoverDeal = async (deal) => {
    const idx = discoverResults.indexOf(deal);
    setImportingDeal(idx);

    try {
      // Use extract-deal API to get full structured data
      const response = await authFetch('/api/extract-deal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: deal.source_url })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        alert(`Import failed: ${result.error || 'Could not extract deal from source'}`);
        return;
      }

      // Pre-populate from AI discovery if extraction missed some fields
      const extractedDeal = result.deal;
      if (!extractedDeal.headline && deal.headline) extractedDeal.headline = deal.headline;
      if (!extractedDeal.asset?.technology && deal.technology) {
        extractedDeal.asset = { ...extractedDeal.asset, technology: deal.technology };
      }

      // Merge counterparts from extraction AND discovery (don't lose either source)
      const extractedCps = (extractedDeal.counterparts || []).map(cp => ({
        name: cp.name,
        role: cp.role,
        stake: cp.stake,
        description: cp.description,
        isNew: true,
        source: 'extraction'
      }));
      const discoveryCps = (deal.parties || []).map(cp => ({
        name: cp.name,
        role: cp.role,
        isNew: true,
        source: 'discovery'
      }));

      // Deduplicate by normalized name, preferring extraction data (more complete)
      const seenNames = new Set();
      const mergedCounterparts = [];
      for (const cp of [...extractedCps, ...discoveryCps]) {
        const normalizedName = cp.name.toLowerCase().trim();
        if (!seenNames.has(normalizedName)) {
          seenNames.add(normalizedName);
          mergedCounterparts.push(cp);
        }
      }

      // Open deal modal with extracted data
      setEditingDeal({
        ...extractedDeal,
        // Map to expected format - use extraction results, fall back to discovery data
        description: extractedDeal.description || deal.summary,
        dealType: extractedDeal.dealType || deal.deal_type,
        technology: (() => { const t = extractedDeal.asset?.technology || deal.technology; return Array.isArray(t) ? t : (t ? [t] : []); })(),
        country: (() => { const c = extractedDeal.geography?.country || deal.location; return Array.isArray(c) ? c.join(', ') : (c || ''); })(),
        region: extractedDeal.geography?.region,
        state: extractedDeal.geography?.state,
        city: extractedDeal.geography?.city,
        capacity: extractedDeal.asset?.capacity || (deal.capacity ? parseFloat(deal.capacity) : null),
        dealSize: extractedDeal.financials?.dealValue?.amount,
        dealSizeCurrency: extractedDeal.financials?.dealValue?.currency || 'USD',
        dealSizeUnit: extractedDeal.financials?.dealValue?.unit || 'M',
        // Use resolved source URL from extraction, not the Google redirect URL
        sourceUrl: extractedDeal.source?.url || deal.source_url,
        date: extractedDeal.dates?.announcementDate || deal.date,
        signingDate: extractedDeal.dates?.signingDate,
        status: extractedDeal.status || 'announced',
        linkedCounterparts: mergedCounterparts,
        people: extractedDeal.people || [],
        extractedData: extractedDeal
      });
      setShowDealModal(true);

    } catch (error) {
      console.error('[Import] Error:', error);
      alert(`Import failed: ${error.message}`);
    } finally {
      setImportingDeal(null);
    }
  };

  // Bulk import selected discovered deals
  const handleBulkImportDiscover = async () => {
    const selectedDeals = Array.from(selectedDiscoverDeals).map(idx => discoverResults[idx]);

    if (selectedDeals.length === 0) return;

    const confirmed = window.confirm(`Import ${selectedDeals.length} deals? Each will be extracted and opened for review.`);
    if (!confirmed) return;

    // Import first deal, then continue with rest
    for (const deal of selectedDeals) {
      await handleImportDiscoverDeal(deal);
      // Wait for user to save before continuing
      break; // For now, just import first one
    }
  };

  // Save deal handler - saves to centralized Supabase
  const handleSaveDeal = async (dealData) => {
    try {
      const isAdmin = user?.publicMetadata?.admin ||
        user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') ||
        user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net');

      if (!isAdmin) {
        console.error('Only admins can save deals');
        alert('Only admins can save deals. Your email: ' + user?.primaryEmailAddress?.emailAddress);
        return;
      }

      // Transform dealData to API format - capture ALL extracted fields
      const extracted = dealData.extractedData || {};
      const apiDeal = {
        headline: dealData.headline || dealData.description?.slice(0, 150) || 'Untitled Deal',
        description: dealData.description,
        dealType: dealData.dealType || extracted.dealType,
        dealSubType: dealData.dealSubType || extracted.dealSubType,
        status: dealData.status || extracted.status,
        geography: {
          country: dealData.country || extracted.geography?.country,
          region: dealData.region || extracted.geography?.region,
          state: dealData.state || extracted.geography?.state,
          city: dealData.city || extracted.geography?.city,
          projectSite: dealData.projectSite || extracted.geography?.projectSite
        },
        dates: {
          announcementDate: dealData.date || extracted.dates?.announcementDate,
          signingDate: dealData.signingDate || extracted.dates?.signingDate,
          expectedClosing: dealData.expectedClosing || extracted.dates?.expectedClosing,
          actualClosing: dealData.actualClosing || extracted.dates?.actualClosing,
          cod: dealData.cod || extracted.dates?.cod
        },
        asset: {
          projectName: dealData.projectName || dealData.asset?.projectName || extracted.asset?.projectName,
          technology: dealData.technology || dealData.asset?.technology || extracted.asset?.technology,
          capacity: dealData.capacity || dealData.asset?.capacity || extracted.asset?.capacity,
          capacityUnit: dealData.capacityUnit || dealData.asset?.capacityUnit || extracted.asset?.capacityUnit || 'MW',
          stage: dealData.stage || dealData.asset?.stage || extracted.asset?.stage,
          numberOfAssets: dealData.numberOfAssets || dealData.asset?.numberOfAssets || extracted.asset?.numberOfAssets,
          annualGeneration: dealData.annualGeneration || dealData.asset?.annualGeneration || extracted.asset?.annualGeneration,
          ppa: dealData.ppa || dealData.asset?.ppa || extracted.asset?.ppa
        },
        financials: {
          ...extracted.financials,
          ...dealData.financials,
          dealValue: dealData.size ? { amount: Number(dealData.size), currency: 'USD', unit: dealData.sizeUnit || 'M' } : (dealData.financials?.dealValue || extracted.financials?.dealValue)
        },
        people: dealData.people || extracted.people || [],
        source: {
          url: dealData.sourceUrl || extracted.source?.url,
          title: dealData.sourceTitle || extracted.source?.title,
          articleDate: dealData.sourceDate || extracted.source?.articleDate
        },
        notes: dealData.notes || extracted.notes,
        extractionConfidence: extracted.extractionConfidence
      };

      // Map linkedCounterparts to counterparts format
      const counterparts = (dealData.linkedCounterparts || []).map(cp => ({
        name: cp.name,
        type: cp.type || 'company',
        role: cp.role || 'other',
        stake: cp.stake,
        isLead: cp.isLead
      }));

      // Check if this is an existing database deal (has UUID) vs new/imported deal
      const isExistingDeal = editingDeal && dealData.id &&
        typeof dealData.id === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dealData.id);

      if (isExistingDeal) {
        // Update existing deal via API
        const response = await authFetch(`/api/deals?id=${dealData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deal: apiDeal,
            counterparts
          })
        });

        if (!response.ok) throw new Error('Failed to update deal');

        const result = await response.json();
        setDeals(prev => prev.map(d => d.id === dealData.id ? result.deal : d));
        // Refresh taxonomy filters so updated countries/technologies appear in filter dropdowns
        setTaxonomyFilters(prev => ({ ...prev, loaded: false }));
        sessionStorage.setItem('deals_cache_bust', Date.now());
        sessionStorage.setItem('dashboard_cache_bust', Date.now());
      } else {
        // Create new deal via API
        const response = await authFetch('/api/deals', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deal: apiDeal,
            counterparts,
            userId: user.id
          })
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          if (response.status === 409 && errData?.error) {
            alert(errData.details || 'This deal already exists.');
            return;
          }
          console.error('API error:', errData);
          throw new Error(errData?.error || 'Failed to create deal');
        }

        const result = await response.json();
        setDeals(prev => [result.deal, ...prev]);

        // Also add the counterparts to counterparts state if they're new
        const dealCounterparts = result.deal?.deal_counterparts || [];
        setCounterparts(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newCps = dealCounterparts
            .map(dc => dc.counterpart)
            .filter(c => c && !existingIds.has(c.id))
            .map(c => ({
              ...c,
              investmentPreferences: c.enrichment,
              websiteUrl: c.website
            }));
          if (newCps.length > 0) {
            return [...prev, ...newCps];
          }
          return prev;
        });

        // Refresh taxonomy filters so new countries/technologies appear in filter dropdowns
        setTaxonomyFilters(prev => ({ ...prev, loaded: false }));
        sessionStorage.setItem('deals_cache_bust', Date.now());
        sessionStorage.setItem('counterparts_cache_bust', Date.now());
        sessionStorage.setItem('dashboard_cache_bust', Date.now());
      }
    } catch (error) {
      console.error('[Save Deal] ERROR:', error);
      alert('Failed to save deal to database: ' + error.message + '\n\nThe deal will be added locally but will NOT persist after refresh.');
      // Fallback to local state only
      if (editingDeal) {
        setDeals(prev => prev.map(d => d.id === dealData.id ? dealData : d));
      } else {
        setDeals(prev => [...prev, { ...dealData, id: `local_${Date.now()}` }]);
      }
    }

    setShowDealModal(false);
    setEditingDeal(null);
  };

  // Delete handlers
  const handleDeleteProject = (projectId) => {
    setProjects(projects.filter(p => p.id !== projectId));
    setDocuments(documents.filter(d => d.projectId !== projectId));
    setSelectedProject(null);
    setShowDeleteConfirm(null);
  };

  const handleDeleteCounterpart = (counterpartId) => {
    setCounterparts(counterparts.filter(c => c.id !== counterpartId));
    // Remove counterpart from all projects
    setProjects(projects.map(p => ({
      ...p,
      counterpartIds: p.counterpartIds.filter(id => id !== counterpartId)
    })));
    setSelectedCounterpart(null);
    setShowDeleteConfirm(null);
  };

  // Reset all data (backup first, then delete)
  const handleResetData = async () => {
    setResettingData(true);
    try {
      // 1. Create backup
      const backup = {
        exportDate: new Date().toISOString(),
        deals: deals,
        counterparts: counterparts,
      };

      // Download backup as JSON
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sherpa-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log('[Reset] Backup downloaded');

      // 2. Delete all deals from database
      for (const deal of deals) {
        try {
          await authFetch(`/api/deals?id=${deal.id}`, { method: 'DELETE' });
        } catch (e) {
          console.error('Error deleting deal:', deal.id, e);
        }
      }
      console.log('[Reset] Deleted all deals');

      // 3. Delete all counterparts from database
      for (const cp of counterparts) {
        try {
          await authFetch(`/api/counterparts?id=${cp.id}`, { method: 'DELETE' });
        } catch (e) {
          console.error('Error deleting counterpart:', cp.id, e);
        }
      }
      console.log('[Reset] Deleted all counterparts');

      // 4. Clear local state
      setDeals([]);
      setCounterparts([]);
      setSelectedCounterpart(null);
      setPanelCounterpart(null);

      console.log('[Reset] Data reset complete');
    } catch (error) {
      console.error('[Reset] Error:', error);
      alert('Error resetting data: ' + error.message);
    }

    setResettingData(false);
    setShowResetConfirm(false);
  };

  // Handle saving documents from upload modal
  const handleSaveDocuments = (newDocs) => {
    setDocuments([...documents, ...newDocs]);
    
    // Update last activity for associated projects
    const projectIds = [...new Set(newDocs.map(d => d.projectId).filter(Boolean))];
    if (projectIds.length > 0) {
      setProjects(projects.map(p => 
        projectIds.includes(p.id) 
          ? {...p, lastActivity: new Date().toISOString().split('T')[0]} 
          : p
      ));
    }
  };

  // NavItem Component for sidebar
  // Theme classes
  // ============================================
  // EXPENSEIQ-EXACT THEME - COPIED VERBATIM
  // ============================================
  // ExpenseIQ only has dark theme. We replicate it exactly for dark mode.
  // Light mode is a clean equivalent.
  // Routed to the GIS --gis-* tokens (warm/evergreen, dark via .gis-dark on the
  // App root) so every themeClasses consumer — detail pages, deals feed, modals —
  // matches the Slack shell + Live Feed in both light and dark. Light values are
  // near-identical to the old warm scheme; dark becomes a clean flat token dark.
  const tokenClasses = {
    bg: 'bg-[color:var(--gis-bg)]',
    sidebar: 'bg-[color:var(--gis-panel)] border-[color:var(--gis-rule)] shadow-sm',
    card: 'bg-[color:var(--gis-panel)] border-[color:var(--gis-rule)] shadow-sm',
    text: 'text-[color:var(--gis-ink)]',
    textMuted: 'text-[color:var(--gis-body)]',
    textSecondary: 'text-[color:var(--gis-dim)]',
    input: 'bg-[color:var(--gis-bg)] border-[color:var(--gis-rule)] text-[color:var(--gis-ink)] placeholder-[color:var(--gis-dim)]',
    topbar: 'bg-[color:var(--gis-panel)] border-[color:var(--gis-rule)] shadow-sm',
    hover: 'hover:bg-[color:var(--gis-bg)]',
    listItem: 'bg-[color:var(--gis-bg)]',
    listItemHover: 'hover:bg-[color:var(--gis-rule)]',
    border: 'border-[color:var(--gis-rule)]',
    borderSolid: 'border-[color:var(--gis-rule)]',
    btnPrimary: 'bg-blue-700 hover:bg-blue-800 text-white',
    modalBg: 'bg-[color:var(--gis-panel)] border-[color:var(--gis-rule)]',
  };
  const themeClasses = theme === 'dark' ? {
    ...tokenClasses,
    navActive: 'bg-blue-700/25 text-blue-300 border border-blue-700/40',
    navInactive: 'text-[color:var(--gis-dim)] hover:bg-[color:var(--gis-bg)] hover:text-[color:var(--gis-body)]',
    cardHover: 'hover:border-blue-700/50',
    btnSecondary: 'bg-[color:var(--gis-rule)] hover:opacity-90 text-[color:var(--gis-ink)]',
  } : {
    ...tokenClasses,
    navActive: 'bg-blue-50 text-blue-800 border border-blue-200',
    navInactive: 'text-[color:var(--gis-body)] hover:bg-[color:var(--gis-bg)] hover:text-[color:var(--gis-ink)]',
    cardHover: 'hover:border-blue-300 hover:shadow-md',
    btnSecondary: 'bg-[color:var(--gis-rule)] hover:opacity-90 text-[color:var(--gis-ink)]',
  };

  // ============================================
  // EXPENSEIQ-EXACT NAVITEM - COPIED VERBATIM
  // ============================================
  // <button onClick={() => setActiveTab(tab)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${activeTab === tab ? 'bg-blue-700/20 text-blue-800 border border-blue-700/30' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}>
  //   <div className="flex items-center gap-3"><Icon /><span className="font-medium">{label}</span></div>
  //   {badge > 0 && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-700/20 text-blue-800">{badge}</span>}
  // </button>
  const NavItem = ({ icon: Icon, label, tab, badge }) => (
    <button 
      onClick={() => setActiveTab(tab)} 
      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
        activeTab === tab 
          ? themeClasses.navActive
          : themeClasses.navInactive
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} />
        <span className="font-medium">{label}</span>
      </div>
      {badge > 0 && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-700/20 text-blue-800">{badge}</span>}
    </button>
  );

  // ============================================
  // EXPENSEIQ-STYLED DASHBOARD COMPONENTS
  // ============================================

  // Dashboard Stat Cards - ExpenseIQ exact card design
  // Search Results Banner
  // Search Results Section - ROW 2 (legacy — smart search results now shown inline on Dashboard via SmartSearchResults)
  const SearchResultsSection = () => {

    if (!searchQuery) return null;
    
    const projectCount = searchFilteredProjects.length;
    const counterpartCount = counterparts.filter(c => 
      c.name.toLowerCase().includes(searchQuery.toLowerCase())
    ).length;
    const docCount = documents.filter(d => 
      d.name.toLowerCase().includes(searchQuery.toLowerCase())
    ).length;

    // By Geography data
    const geoData = (() => {
      const grouped = {};
      searchFilteredProjects.filter(p => p.isLive).forEach(p => {
        const country = getCountry(p.location);
        if (!grouped[country]) grouped[country] = { name: country, capacity: 0, count: 0 };
        grouped[country].capacity += p.capacity || 0;
        grouped[country].count += 1;
      });
      return Object.values(grouped).sort((a, b) => b.capacity - a.capacity).slice(0, 5);
    })();
    const maxGeoCapacity = Math.max(...geoData.map(g => g.capacity), 1);
    const countryCount = new Set(searchFilteredProjects.filter(p => p.isLive).map(p => getCountry(p.location))).size;

    // By Technology data
    const techData = (() => {
      const grouped = {};
      searchFilteredProjects.filter(p => p.isLive).forEach(p => {
        const tech = p.technology || 'Other';
        if (!grouped[tech]) grouped[tech] = { name: tech, capacity: 0, count: 0 };
        grouped[tech].capacity += p.capacity || 0;
        grouped[tech].count += 1;
      });
      return Object.values(grouped).sort((a, b) => b.capacity - a.capacity);
    })();

    // Related Tasks data - tasks linked to matching projects/counterparts or matching title
    const searchTasks = tasks.filter(task => {
      const q = searchQuery.toLowerCase();
      // Match by task title or description
      if (task.title?.toLowerCase().includes(q) || task.description?.toLowerCase().includes(q)) return true;
      // Match by linked project
      if (task.targetType === 'project' && searchFilteredProjects.some(p => p.id === task.targetId)) return true;
      // Match by linked counterpart
      if (task.targetType === 'counterpart') {
        const linkedCounterpart = counterparts.find(c => c.id === task.targetId);
        if (linkedCounterpart && linkedCounterpart.name.toLowerCase().includes(q)) return true;
      }
      return false;
    });

    // Matching Documents data
    const searchDocs = documents.filter(d => 
      d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.content?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Dev Stage data
    const stageData = stages.map(stage => ({
      name: stage,
      count: searchFilteredProjects.filter(p => p.stage === stage && p.isLive).length
    })).filter(s => s.count > 0);
    const maxStageCount = Math.max(...stageData.map(s => s.count), 1);

    // Investment by Region data
    const regionInvestment = (() => {
      const grouped = {};
      searchFilteredProjects.filter(p => p.isLive).forEach(p => {
        const country = getCountry(p.location);
        if (!grouped[country]) grouped[country] = 0;
        grouped[country] += p.investmentSize || 0;
      });
      return Object.entries(grouped)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);
    })();

    // Top Counterparts in search
    const searchCounterparts = counterparts
      .filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        searchFilteredProjects.some(p => p.counterpartIds?.includes(c.id)))
      .map(c => {
        const projectCount = searchFilteredProjects.filter(p => p.counterpartIds?.includes(c.id)).length;
        const nameMatches = c.name.toLowerCase().includes(searchQuery.toLowerCase());
        return { ...c, projectCount, nameMatches };
      })
      .sort((a, b) => {
        // Prioritize name matches, then by project count
        if (a.nameMatches && !b.nameMatches) return -1;
        if (!a.nameMatches && b.nameMatches) return 1;
        return b.projectCount - a.projectCount;
      })
      .slice(0, 5);

    return (
      <div className="space-y-4">
        {/* Search Banner */}
        <div className={`${theme === 'dark' ? 'bg-blue-700/10 border-blue-700/30' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4 flex justify-between items-center`}>
          <div className="flex items-center gap-2">
            <Search size={18} className="text-blue-700" />
            <span className={`${theme === 'dark' ? 'text-blue-800' : 'text-blue-800'}`}>
              Showing results for "{searchQuery}" — {projectCount} projects, {counterpartCount} counterparts, {docCount} documents
            </span>
          </div>
          <button 
            onClick={() => setSearchQuery('')}
            className={`flex items-center gap-1 ${theme === 'dark' ? 'text-blue-800 hover:text-blue-300' : 'text-blue-800 hover:text-blue-700'}`}
          >
            <X size={14} /> Clear search
          </button>
        </div>

        {/* Search Results Grid - Row 2a */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* By Geography */}
          <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
                <Globe size={16} className={themeClasses.textMuted} /> By Geography
              </h3>
              <span className={`text-xs ${themeClasses.textMuted}`}>{countryCount} countries</span>
            </div>
            {geoData.length > 0 ? (
              <div className="space-y-3">
                {geoData.map((geo, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`w-20 text-sm ${themeClasses.textMuted} truncate`}>{geo.name}</span>
                    <div className={`flex-1 h-6 ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'} rounded-full overflow-hidden`}>
                      <div className="h-full bg-blue-700 rounded-full" style={{ width: `${(geo.capacity / maxGeoCapacity) * 100}%` }} />
                    </div>
                    <span className={`text-sm font-medium ${themeClasses.text} whitespace-nowrap`}>
                      {formatMetricValue(geo.capacity, sectorConfig.primaryMetric)} ({geo.count})
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`${themeClasses.textSecondary} text-sm text-center py-4`}>No data</p>
            )}
          </div>

          {/* By Technology */}
          <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
            <h3 className={`text-sm font-medium ${themeClasses.text} mb-4`}>By {sectorConfig.categoryLabel}</h3>
            {techData.length > 0 ? (
              <div className="space-y-3">
                {techData.map((tech, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span>{sectorConfig.categoryIcons?.[tech.name] || '📁'}</span>
                      <span className={`text-sm ${themeClasses.textMuted}`}>{tech.name}</span>
                    </div>
                    <span className={`text-sm font-medium ${themeClasses.text}`}>
                      {tech.count} ({formatMetricValue(tech.capacity, sectorConfig.primaryMetric)})
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`${themeClasses.textSecondary} text-sm text-center py-4`}>No data</p>
            )}
          </div>

        </div>

        {/* Search Results Grid - Row 2b: Expandable boxes */}
        <ExpandableSearchResults 
          searchQuery={searchQuery}
          searchFilteredProjects={searchFilteredProjects}
          searchCounterparts={searchCounterparts}
          searchTasks={searchTasks}
          searchDocs={searchDocs}
          setSelectedProject={setSelectedProject}
          setSelectedCounterpart={setSelectedCounterpart}
          setActiveTab={setActiveTab}
          theme={theme}
          themeClasses={themeClasses}
          sectorConfig={sectorConfig}
          formatMetricValue={formatMetricValue}
          getCountry={getCountry}
          getDaysAgo={getDaysAgo}
          projects={projects}
          counterparts={counterparts}
        />
      </div>
    );
  };

  // Expandable Search Results Component
  const ExpandableSearchResults = ({ 
    searchQuery, searchFilteredProjects, searchCounterparts, searchTasks, searchDocs,
    setSelectedProject, setSelectedCounterpart, setActiveTab,
    theme, themeClasses, sectorConfig, formatMetricValue, getCountry, getDaysAgo,
    projects, counterparts
  }) => {
    const [expandedSection, setExpandedSection] = useState(null);
    
    const toggleExpand = (section) => {
      setExpandedSection(expandedSection === section ? null : section);
    };

    // If a section is expanded, show it full width
    if (expandedSection) {
      return (
        <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
          {/* Header with collapse button */}
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
              {expandedSection === 'documents' && <><FileText size={16} className={themeClasses.textMuted} /> Matching Documents ({searchDocs.length})</>}
              {expandedSection === 'projects' && <><FolderOpen size={16} className={themeClasses.textMuted} /> Matching Projects ({searchFilteredProjects.length})</>}
              {expandedSection === 'counterparts' && <><Users size={16} className={themeClasses.textMuted} /> Matching Counterparts ({searchCounterparts.length})</>}
              {expandedSection === 'tasks' && <><CheckSquare size={16} className={themeClasses.textMuted} /> Related Tasks ({searchTasks.length})</>}
            </h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => {
                  if (expandedSection === 'documents') setActiveTab('documents');
                  if (expandedSection === 'projects') setActiveTab('projects');
                  if (expandedSection === 'counterparts') setActiveTab('counterparts');
                  if (expandedSection === 'tasks') setActiveTab('tasks');
                }}
                className={`text-xs ${themeClasses.textMuted} hover:text-blue-700 flex items-center gap-1`}
              >
                Go to tab <ArrowRight size={12} />
              </button>
              <button 
                onClick={() => setExpandedSection(null)}
                className={`p-1.5 rounded-lg ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} ${themeClasses.textMuted}`}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Expanded content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
            {expandedSection === 'documents' && searchDocs.map(doc => (
              <div 
                key={doc.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800 bg-slate-800/50' : 'hover:bg-slate-100 bg-slate-50'} transition-colors`}
                onClick={() => setActiveTab('documents')}
              >
                <div className={`w-10 h-10 rounded-lg ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50'} flex items-center justify-center`}>
                  <FileText size={18} className="text-blue-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{doc.name}</p>
                  <p className={`text-xs ${themeClasses.textMuted}`}>{doc.uploadDate || 'No date'}</p>
                </div>
              </div>
            ))}
            {expandedSection === 'projects' && searchFilteredProjects.map(project => (
              <div 
                key={project.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800 bg-slate-800/50' : 'hover:bg-slate-100 bg-slate-50'} transition-colors`}
                onClick={() => setSelectedProject(project)}
              >
                <div className={`w-10 h-10 rounded-lg ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50'} flex items-center justify-center`}>
                  <FolderOpen size={18} className="text-blue-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{project.name}</p>
                  <p className={`text-xs ${themeClasses.textMuted} truncate`}>{project.technology} • {getCountry(project.location)}</p>
                </div>
                <span className={`text-sm font-medium ${themeClasses.text} whitespace-nowrap`}>
                  {formatMetricValue(project.capacity, sectorConfig.primaryMetric)}
                </span>
              </div>
            ))}
            {expandedSection === 'counterparts' && searchCounterparts.map(cp => (
              <div 
                key={cp.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800 bg-slate-800/50' : 'hover:bg-slate-100 bg-slate-50'} transition-colors`}
                onClick={() => viewCounterpart(cp)}
              >
                <div className={`w-10 h-10 rounded-lg ${cp.type === 'company' ? (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50') : (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50')} flex items-center justify-center`}>
                  {cp.type === 'company' ? <Building2 size={18} className="text-blue-700" /> : <User size={18} className="text-blue-700" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{cp.name}</p>
                  <p className={`text-xs ${themeClasses.textMuted} truncate`}>{cp.type === 'company' ? (cp.location || 'Company') : (cp.company || 'Person')}</p>
                </div>
                <span className={`text-xs ${themeClasses.textMuted}`}>{cp.projectCount} projects</span>
              </div>
            ))}
            {expandedSection === 'tasks' && searchTasks.map(task => {
              const linkedProject = projects.find(p => p.id === task.targetId && task.targetType === 'project');
              const linkedCounterpart = counterparts.find(c => c.id === task.targetId && task.targetType === 'counterpart');
              return (
                <div 
                  key={task.id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'} ${task.status === 'done' ? 'opacity-60' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    task.priority === 'high' ? (theme === 'dark' ? 'bg-red-500/20' : 'bg-red-50') :
                    task.priority === 'medium' ? (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50') :
                    (theme === 'dark' ? 'bg-slate-700' : 'bg-slate-100')
                  }`}>
                    <CheckSquare size={18} className={
                      task.priority === 'high' ? 'text-red-500' :
                      task.priority === 'medium' ? 'text-blue-700' : themeClasses.textMuted
                    } />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate ${task.status === 'done' ? 'line-through' : ''}`}>{task.title}</p>
                    <p className={`text-xs ${themeClasses.textMuted} truncate`}>
                      {linkedProject ? `📁 ${linkedProject.name}` : linkedCounterpart ? `👤 ${linkedCounterpart.name}` : 'General'}
                    </p>
                  </div>
                  {task.dueDate && <span className={`text-xs ${themeClasses.textMuted}`}>{getDaysAgo(task.dueDate)}</span>}
                </div>
              );
            })}
          </div>
          
          {/* Empty states */}
          {expandedSection === 'documents' && searchDocs.length === 0 && (
            <p className={`${themeClasses.textSecondary} text-sm text-center py-8 italic`}>No matching documents</p>
          )}
          {expandedSection === 'projects' && searchFilteredProjects.length === 0 && (
            <p className={`${themeClasses.textSecondary} text-sm text-center py-8 italic`}>No matching projects</p>
          )}
          {expandedSection === 'counterparts' && searchCounterparts.length === 0 && (
            <p className={`${themeClasses.textSecondary} text-sm text-center py-8 italic`}>No matching counterparts</p>
          )}
          {expandedSection === 'tasks' && searchTasks.length === 0 && (
            <p className={`${themeClasses.textSecondary} text-sm text-center py-8 italic`}>No related tasks</p>
          )}
        </div>
      );
    }

    // Default grid view with 3 expandable boxes
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Matching Documents */}
        <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
              <FileText size={16} className="text-blue-700" /> Matching Documents
            </h3>
            {searchDocs.length > 3 && (
              <button 
                onClick={() => toggleExpand('documents')}
                className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} ${themeClasses.textMuted}`}
                title="Expand"
              >
                <ArrowUpRight size={14} />
              </button>
            )}
          </div>
          {searchDocs.length > 0 ? (
            <div className="space-y-2">
              {searchDocs.slice(0, 3).map(doc => (
                <div 
                  key={doc.id}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} transition-colors`}
                  onClick={() => setActiveTab('documents')}
                >
                  <FileText size={14} className="text-blue-700 flex-shrink-0" />
                  <p className={`text-sm ${themeClasses.text} truncate`}>{doc.name}</p>
                </div>
              ))}
              {searchDocs.length > 3 && (
                <button 
                  onClick={() => toggleExpand('documents')}
                  className={`w-full text-center text-xs text-blue-700 hover:text-blue-800 py-2`}
                >
                  View all {searchDocs.length} documents →
                </button>
              )}
            </div>
          ) : (
            <p className={`${themeClasses.textSecondary} text-sm text-center py-4 italic`}>No matching documents</p>
          )}
        </div>

        {/* Matching Projects */}
        <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
              <FolderOpen size={16} className="text-blue-700" /> Matching Projects
            </h3>
            {searchFilteredProjects.length > 3 && (
              <button 
                onClick={() => toggleExpand('projects')}
                className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} ${themeClasses.textMuted}`}
                title="Expand"
              >
                <ArrowUpRight size={14} />
              </button>
            )}
          </div>
          {searchFilteredProjects.length > 0 ? (
            <div className="space-y-2">
              {searchFilteredProjects.slice(0, 3).map(project => (
                <div 
                  key={project.id} 
                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} transition-colors`}
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{project.name}</p>
                    <p className={`text-xs ${themeClasses.textMuted} truncate`}>{project.technology}</p>
                  </div>
                  <span className={`text-xs font-medium ${themeClasses.text} whitespace-nowrap ml-2`}>
                    {formatMetricValue(project.capacity, sectorConfig.primaryMetric)}
                  </span>
                </div>
              ))}
              {searchFilteredProjects.length > 3 && (
                <button 
                  onClick={() => toggleExpand('projects')}
                  className={`w-full text-center text-xs text-blue-700 hover:text-blue-800 py-2`}
                >
                  View all {searchFilteredProjects.length} projects →
                </button>
              )}
            </div>
          ) : (
            <p className={`${themeClasses.textSecondary} text-sm text-center py-4 italic`}>No matching projects</p>
          )}
        </div>

        {/* Matching Counterparts */}
        <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
              <Users size={16} className="text-blue-700" /> Matching Counterparts
            </h3>
            {searchCounterparts.length > 3 && (
              <button 
                onClick={() => toggleExpand('counterparts')}
                className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} ${themeClasses.textMuted}`}
                title="Expand"
              >
                <ArrowUpRight size={14} />
              </button>
            )}
          </div>
          {searchCounterparts.length > 0 ? (
            <div className="space-y-2">
              {searchCounterparts.slice(0, 3).map(cp => (
                <div 
                  key={cp.id} 
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} transition-colors`}
                  onClick={() => viewCounterpart(cp)}
                >
                  {cp.type === 'company' ? <Building2 size={14} className="text-blue-700 flex-shrink-0" /> : <User size={14} className="text-blue-700 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{cp.name}</p>
                  </div>
                  <span className={`text-xs ${themeClasses.textMuted}`}>{cp.projectCount}p</span>
                </div>
              ))}
              {searchCounterparts.length > 3 && (
                <button 
                  onClick={() => toggleExpand('counterparts')}
                  className={`w-full text-center text-xs text-blue-700 hover:text-blue-800 py-2`}
                >
                  View all {searchCounterparts.length} counterparts →
                </button>
              )}
            </div>
          ) : (
            <p className={`${themeClasses.textSecondary} text-sm text-center py-4 italic`}>No matching counterparts</p>
          )}
        </div>
      </div>
    );
  };

  // Search with Preview Dropdown Component
  const SearchWithPreview = ({
    searchQuery, setSearchQuery, projects, counterparts, documents, tasks, deals,
    selectedProject, selectedCounterpart,
    setSelectedProject, setSelectedCounterpart, setSelectedDeal, setActiveTab,
    theme, themeClasses, t, sectorConfig, formatMetricValue, getCountry,
    onSmartSearch, smartSearchLoading,
    onDealClick
  }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [localQuery, setLocalQuery] = useState(searchQuery);
    const [isSmartMode, setIsSmartMode] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Detect if query looks like natural language
    const looksLikeSmartQuery = (q) => {
      if (!q || q.length < 5) return false;
      const smartPatterns = [
        /show me/i, /find( all)?/i, /list/i, /what/i, /which/i,
        /over \d+/i, /under \d+/i, /more than/i, /less than/i,
        /greater than/i, /at least/i, /between/i,
        /in (italy|spain|germany|france|usa|uk|europe)/i,
        /with.*mw/i, /\d+\s*mw/i,
        /projects? (that|with|in|from)/i,
        /counterparts? (that|with|in|from)/i
      ];
      return smartPatterns.some(p => p.test(q));
    };

    // Debounce the main search query update
    useEffect(() => {
      const timer = setTimeout(() => {
        if (localQuery !== searchQuery) {
          // Only update main search on Enter or when dropdown item is clicked
        }
      }, 300);
      return () => clearTimeout(timer);
    }, [localQuery]);

    // Sync local query with external searchQuery
    useEffect(() => {
      setLocalQuery(searchQuery);
    }, [searchQuery]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
            inputRef.current && !inputRef.current.contains(e.target)) {
          setIsFocused(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Preview results (limited to 3-5 each for quick view)
    const previewResults = useMemo(() => {
      if (!localQuery || localQuery.length < 2) return null;
      const q = localQuery.toLowerCase();

      // Search deals (database deals + enrichment deals)
      const matchingDbDeals = (deals || []).filter(d =>
        d.headline?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q) ||
        (Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).join(' ').toLowerCase().includes(q) ||
        d.project_name?.toLowerCase().includes(q) ||
        (Array.isArray(d.technology) ? d.technology : (d.technology ? [d.technology] : [])).join(' ').toLowerCase().includes(q) ||
        (d.deal_counterparts || []).some(dc => dc.counterpart?.name?.toLowerCase().includes(q))
      ).slice(0, 5);

      const matchingProjects = projects.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.location?.toLowerCase().includes(q) ||
        p.technology?.toLowerCase().includes(q)
      ).slice(0, 3);

      const matchingCounterparts = counterparts.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.location?.toLowerCase().includes(q)
      ).slice(0, 5);

      const matchingDocs = documents.filter(d =>
        d.name?.toLowerCase().includes(q) ||
        d.category?.toLowerCase().includes(q)
      ).slice(0, 3);

      const matchingTasks = tasks.filter(t =>
        t.title?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q)
      ).slice(0, 3);

      const totalCount = matchingDbDeals.length + matchingProjects.length + matchingCounterparts.length +
                         matchingDocs.length + matchingTasks.length;

      return {
        deals: matchingDbDeals,
        enrichmentDeals: [],
        projects: matchingProjects,
        counterparts: matchingCounterparts,
        documents: matchingDocs,
        tasks: matchingTasks,
        totalCount,
        fullDealCount: (deals || []).filter(d => d.headline?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q) || (Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).join(' ').toLowerCase().includes(q)).length,
        fullProjectCount: projects.filter(p => p.name?.toLowerCase().includes(q) || p.location?.toLowerCase().includes(q) || p.technology?.toLowerCase().includes(q)).length,
        fullCounterpartCount: counterparts.filter(c => c.name?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q)).length,
        fullDocCount: documents.filter(d => d.name?.toLowerCase().includes(q)).length,
        fullTaskCount: tasks.filter(t => t.title?.toLowerCase().includes(q)).length
      };
    }, [localQuery, projects, counterparts, documents, tasks, deals]);

    const handleKeyDown = async (e) => {
      if (e.key === 'Enter') {
        // Check if this looks like a smart query and smart search is available
        if ((isSmartMode || looksLikeSmartQuery(localQuery)) && onSmartSearch) {
          e.preventDefault();
          setIsFocused(false);
          await onSmartSearch(localQuery);
          setActiveTab('dashboard'); // Smart search shows results on dashboard
        } else {
          // Regular search
          setSearchQuery(localQuery);
          // If viewing a detail page (single project or counterpart), navigate to dashboard
          if (selectedProject || selectedCounterpart) {
            setSelectedProject(null);
            setSelectedCounterpart(null);
            setActiveTab('dashboard');
          }
          // Otherwise stay on current tab and let it filter
          setIsFocused(false);
        }
      }
      if (e.key === 'Escape') {
        setIsFocused(false);
        inputRef.current?.blur();
      }
    };

    const handleResultClick = (type, item) => {
      setIsFocused(false);
      setLocalQuery('');
      setSearchQuery('');
      if (type === 'project') {
        if (setSelectedDeal) setSelectedDeal(null); // Close deal page if open
        setSelectedProject(item);
      } else if (type === 'counterpart') {
        if (setSelectedDeal) setSelectedDeal(null); // Close deal page if open
        viewCounterpart(item);
        setActiveTab('counterparts');
      } else if (type === 'deal') {
        if (onDealClick) onDealClick(item);
        setActiveTab('deals');
      } else if (type === 'enrichmentDeal') {
        if (setSelectedDeal) setSelectedDeal(null); // Close deal page if open
        // Navigate to the counterpart that owns this enrichment deal
        const cp = counterparts.find(c => c.id === item.counterpartId);
        if (cp) viewCounterpart(cp);
        setActiveTab('counterparts');
      } else if (type === 'document') {
        if (setSelectedDeal) setSelectedDeal(null); // Close deal page if open
        setActiveTab('documents');
      } else if (type === 'task') {
        if (setSelectedDeal) setSelectedDeal(null); // Close deal page if open
        setActiveTab('tasks');
      }
    };

    const handleViewAll = () => {
      setSearchQuery(localQuery);
      setIsFocused(false);
      setActiveTab('dashboard');
    };

    const showDropdown = isFocused && localQuery && localQuery.length >= 2 && previewResults;

    return (
      <div className="relative flex-1 max-w-md">
        <div className={`absolute left-3 top-1/2 -translate-y-1/2 ${looksLikeSmartQuery(localQuery) ? 'text-blue-700' : themeClasses.textSecondary}`}>
          {looksLikeSmartQuery(localQuery) || smartSearchLoading ? <Sparkles size={18} /> : <Search size={18} />}
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder={isSmartMode ? "Try: 'Show me wind projects over 50MW in Italy'" : t('search')}
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          className={`w-full ${themeClasses.input} border rounded-xl pl-10 pr-16 py-2 text-sm ${showDropdown ? 'rounded-b-none border-b-0' : ''} ${looksLikeSmartQuery(localQuery) ? 'border-blue-300 ring-1 ring-blue-200' : ''}`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {localQuery && (
            <button 
              onClick={() => { setLocalQuery(''); setSearchQuery(''); }}
              className={`p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 ${themeClasses.textMuted}`}
            >
              <X size={14} />
            </button>
          )}
          {onSmartSearch && (
            <button
              onClick={() => setIsSmartMode(!isSmartMode)}
              className={`p-1 rounded-full transition-colors ${isSmartMode ? 'bg-blue-100 text-blue-800' : 'hover:bg-slate-200 dark:hover:bg-slate-700 ' + themeClasses.textMuted}`}
              title="Smart Search (AI)"
            >
              <Sparkles size={14} />
            </button>
          )}
        </div>

        {/* Preview Dropdown */}
        {showDropdown && (
          <div 
            ref={dropdownRef}
            className={`absolute top-full left-0 right-0 ${theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} border border-t-0 rounded-b-xl shadow-xl z-50 max-h-96 overflow-y-auto`}
          >
            {previewResults.totalCount === 0 ? (
              <div className="p-4 text-center">
                <p className={`${themeClasses.textMuted} text-sm`}>No results for "{localQuery}"</p>
              </div>
            ) : (
              <>
                {/* Deals Section */}
                {previewResults.deals.length > 0 && (
                  <div className={`border-b ${theme === 'dark' ? 'border-slate-800' : 'border-slate-100'}`}>
                    <div className={`px-3 py-2 ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                      <span className={`text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider flex items-center gap-1.5`}>
                        <TrendingUp size={12} /> Deals
                        <span className={`ml-auto ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} px-1.5 py-0.5 rounded text-[10px]`}>
                          {previewResults.fullDealCount}
                        </span>
                      </span>
                    </div>
                    {previewResults.deals.map(deal => (
                      <div
                        key={deal.id}
                        onClick={() => handleResultClick('deal', deal)}
                        className={`px-3 py-2 cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} flex items-center gap-3`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${theme === 'dark' ? 'bg-green-700/20' : 'bg-green-50'} flex items-center justify-center flex-shrink-0`}>
                          <TrendingUp size={14} className="text-green-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{deal.headline || deal.description}</p>
                          <p className={`text-xs ${themeClasses.textMuted} truncate`}>
                            {(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).join(', ')}{(Array.isArray(deal.technology) ? deal.technology : [deal.technology]).filter(Boolean).length > 0 && ` • ${(Array.isArray(deal.technology) ? deal.technology : [deal.technology]).filter(Boolean).join(', ')}`}
                            {(deal.deal_counterparts || []).length > 0 && ` • ${deal.deal_counterparts[0].counterpart?.name}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Projects Section */}
                {previewResults.projects.length > 0 && (
                  <div className={`border-b ${theme === 'dark' ? 'border-slate-800' : 'border-slate-100'}`}>
                    <div className={`px-3 py-2 ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                      <span className={`text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider flex items-center gap-1.5`}>
                        <FolderOpen size={12} /> Projects
                        <span className={`ml-auto ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} px-1.5 py-0.5 rounded text-[10px]`}>
                          {previewResults.fullProjectCount}
                        </span>
                      </span>
                    </div>
                    {previewResults.projects.map(project => (
                      <div 
                        key={project.id}
                        onClick={() => handleResultClick('project', project)}
                        className={`px-3 py-2 cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} flex items-center gap-3`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50'} flex items-center justify-center flex-shrink-0`}>
                          <FolderOpen size={14} className="text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{project.name}</p>
                          <p className={`text-xs ${themeClasses.textMuted} truncate`}>{project.technology} • {getCountry(project.location)}</p>
                        </div>
                        <span className={`text-xs ${themeClasses.textMuted}`}>
                          {formatMetricValue(project.capacity, sectorConfig.primaryMetric)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Counterparts Section */}
                {previewResults.counterparts.length > 0 && (
                  <div className={`border-b ${theme === 'dark' ? 'border-slate-800' : 'border-slate-100'}`}>
                    <div className={`px-3 py-2 ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                      <span className={`text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider flex items-center gap-1.5`}>
                        <Users size={12} /> Counterparts
                        <span className={`ml-auto ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} px-1.5 py-0.5 rounded text-[10px]`}>
                          {previewResults.fullCounterpartCount}
                        </span>
                      </span>
                    </div>
                    {previewResults.counterparts.map(cp => (
                      <div 
                        key={cp.id}
                        onClick={() => handleResultClick('counterpart', cp)}
                        className={`px-3 py-2 cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} flex items-center gap-3`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${cp.type === 'company' ? (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50') : (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50')} flex items-center justify-center flex-shrink-0`}>
                          {cp.type === 'company' ? <Building2 size={14} className="text-blue-700" /> : <User size={14} className="text-blue-700" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{cp.name}</p>
                          <p className={`text-xs ${themeClasses.textMuted} truncate`}>{cp.type === 'company' ? (cp.location || 'Company') : (cp.company || 'Person')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Documents Section */}
                {previewResults.documents.length > 0 && (
                  <div className={`border-b ${theme === 'dark' ? 'border-slate-800' : 'border-slate-100'}`}>
                    <div className={`px-3 py-2 ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                      <span className={`text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider flex items-center gap-1.5`}>
                        <FileText size={12} /> Documents
                        <span className={`ml-auto ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} px-1.5 py-0.5 rounded text-[10px]`}>
                          {previewResults.fullDocCount}
                        </span>
                      </span>
                    </div>
                    {previewResults.documents.map(doc => (
                      <div 
                        key={doc.id}
                        onClick={() => handleResultClick('document', doc)}
                        className={`px-3 py-2 cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} flex items-center gap-3`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50'} flex items-center justify-center flex-shrink-0`}>
                          <FileText size={14} className="text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{doc.name}</p>
                          <p className={`text-xs ${themeClasses.textMuted}`}>{doc.category || 'Document'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tasks Section */}
                {previewResults.tasks.length > 0 && (
                  <div className={`border-b ${theme === 'dark' ? 'border-slate-800' : 'border-slate-100'}`}>
                    <div className={`px-3 py-2 ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                      <span className={`text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider flex items-center gap-1.5`}>
                        <CheckSquare size={12} /> Tasks
                        <span className={`ml-auto ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} px-1.5 py-0.5 rounded text-[10px]`}>
                          {previewResults.fullTaskCount}
                        </span>
                      </span>
                    </div>
                    {previewResults.tasks.map(task => (
                      <div 
                        key={task.id}
                        onClick={() => handleResultClick('task', task)}
                        className={`px-3 py-2 cursor-pointer ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} flex items-center gap-3`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${
                          task.priority === 'high' ? (theme === 'dark' ? 'bg-red-500/20' : 'bg-red-50') :
                          task.priority === 'medium' ? (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50') :
                          (theme === 'dark' ? 'bg-slate-700' : 'bg-slate-100')
                        } flex items-center justify-center flex-shrink-0`}>
                          <CheckSquare size={14} className={
                            task.priority === 'high' ? 'text-red-500' :
                            task.priority === 'medium' ? 'text-blue-700' : themeClasses.textMuted
                          } />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${themeClasses.text} truncate ${task.status === 'done' ? 'line-through opacity-60' : ''}`}>{task.title}</p>
                          <p className={`text-xs ${themeClasses.textMuted}`}>{task.status === 'done' ? 'Completed' : task.dueDate || 'No due date'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* View All Results */}
                <button 
                  onClick={handleViewAll}
                  className={`w-full px-4 py-3 text-sm font-medium text-blue-700 hover:text-blue-800 ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-50'} flex items-center justify-center gap-2`}
                >
                  View all results on Dashboard
                  <ArrowRight size={14} />
                </button>
              </>
            )}

            {/* Keyboard hint */}
            <div className={`px-3 py-2 ${theme === 'dark' ? 'bg-slate-800/30 border-slate-800' : 'bg-slate-50 border-slate-100'} border-t flex items-center justify-between`}>
              <span className={`text-[10px] ${themeClasses.textMuted}`}>
                <kbd className={`px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} font-mono`}>↵</kbd> Search all
              </span>
              <span className={`text-[10px] ${themeClasses.textMuted}`}>
                <kbd className={`px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'} font-mono`}>esc</kbd> Close
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Dashboard Header with Stats + AI Drop Zone (Option B)
  // Onboarding Hero - shown when no projects or counterparts
  const OnboardingHero = () => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleDragOver = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, []);

    const handleDrop = useCallback(async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;

      const file = files[0];
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1];
        const tempDoc = {
          id: Date.now(),
          name: file.name,
          contentType: file.type.includes('pdf') ? 'pdf' : file.type.includes('image') ? 'image' : 'document',
          base64: base64,
          content: file.type.includes('pdf') ? '[PDF Document - extracting...]' : '',
          uploadDate: new Date().toISOString().split('T')[0]
        };
        
        setDocuments(prev => [...prev, tempDoc]);
        setExtractingFromDocument(tempDoc);
      };
      reader.readAsDataURL(file);
    }, []);

    const handleClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileSelect = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1];
        const tempDoc = {
          id: Date.now(),
          name: file.name,
          contentType: file.type.includes('pdf') ? 'pdf' : file.type.includes('image') ? 'image' : 'document',
          base64: base64,
          content: file.type.includes('pdf') ? '[PDF Document - extracting...]' : '',
          uploadDate: new Date().toISOString().split('T')[0]
        };
        
        setDocuments(prev => [...prev, tempDoc]);
        setExtractingFromDocument(tempDoc);
      };
      reader.readAsDataURL(file);
      
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
      <div className={`${theme === 'dark' ? 'bg-gradient-to-br from-slate-800/80 to-blue-900/20 border-slate-700' : 'bg-gradient-to-br from-white to-blue-50 border-slate-200'} rounded-2xl border p-8`}>

        {/* Header */}
        <div className="text-center mb-6">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-100'} flex items-center justify-center`}>
            <img src="/logo192.png" alt="GIS" className="w-12 h-12 rounded-xl" />
          </div>
          <h2 className={`text-2xl font-bold ${themeClasses.text} mb-2`}>Global Infrastructure Sherpa</h2>
          <p className={`${themeClasses.textMuted} max-w-lg mx-auto`}>
            Infrastructure deal intelligence is loading. If this persists, the database may be temporarily unavailable.
          </p>
        </div>
        
        {/* Retry button */}
        <div className="flex justify-center">
          <button
            onClick={() => refreshCentralizedData()}
            className="px-6 py-3 bg-blue-700 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} /> Retry Loading Data
          </button>
        </div>
      </div>
    );
  };

  const DashboardStatCards = () => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleDragOver = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, []);

    const handleDrop = useCallback(async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;

      const file = files[0];
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1];
        const tempDoc = {
          id: Date.now(),
          name: file.name,
          contentType: file.type.includes('pdf') ? 'pdf' : file.type.includes('image') ? 'image' : 'document',
          base64: base64,
          content: file.type.includes('pdf') ? '[PDF Document - extracting...]' : '',
          uploadDate: new Date().toISOString().split('T')[0]
        };
        
        setDocuments(prev => [...prev, tempDoc]);
        setExtractingFromDocument(tempDoc);
      };
      reader.readAsDataURL(file);
    }, []);

    const handleClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileSelect = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1];
        const tempDoc = {
          id: Date.now(),
          name: file.name,
          contentType: file.type.includes('pdf') ? 'pdf' : file.type.includes('image') ? 'image' : 'document',
          base64: base64,
          content: file.type.includes('pdf') ? '[PDF Document - extracting...]' : '',
          uploadDate: new Date().toISOString().split('T')[0]
        };
        
        setDocuments(prev => [...prev, tempDoc]);
        setExtractingFromDocument(tempDoc);
      };
      reader.readAsDataURL(file);
      
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`
          ${themeClasses.card} rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer
          ${isDragging 
            ? 'border-blue-700 bg-blue-50 dark:bg-blue-900/20 scale-[1.01]' 
            : `border-slate-300 dark:border-slate-600 hover:border-blue-800 hover:bg-blue-50/50 dark:hover:bg-blue-900/10`
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <div className="flex items-center p-5 gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
            isDragging 
              ? 'bg-blue-700 scale-110' 
              : 'bg-gradient-to-br from-blue-700/20 to-blue-700/20 dark:from-blue-700/30 dark:to-blue-700/30'
          }`}>
            <Sparkles size={28} className={isDragging ? 'text-white' : 'text-blue-800 dark:text-blue-800'} />
          </div>
          <div className="flex-1">
            <p className={`font-semibold mb-0.5 ${isDragging ? 'text-blue-700 dark:text-blue-300' : themeClasses.text}`}>
              {isDragging ? 'Drop to extract with AI' : 'AI Document Extraction'}
            </p>
            <p className={`text-sm ${themeClasses.textMuted}`}>
              Drop PDF or image to auto-create projects & counterparts
            </p>
          </div>
          <div className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'}`}>
            <Upload size={16} className={themeClasses.textMuted} />
            <span className={`text-sm ${themeClasses.textMuted}`}>or click to upload</span>
          </div>
        </div>
      </div>
    );
  };

  // By Geography Widget
  const DashboardByGeography = () => {
    const geoData = useMemo(() => {
      const grouped = {};
      sectorProjects.filter(p => p.isLive).forEach(p => {
        const country = getCountry(p.location);
        if (!grouped[country]) grouped[country] = { name: country, capacity: 0, count: 0 };
        grouped[country].capacity += p.capacity || 0;
        grouped[country].count += 1;
      });
      return Object.values(grouped).sort((a, b) => b.capacity - a.capacity).slice(0, 5);
    }, [sectorProjects]);

    const maxCapacity = Math.max(...geoData.map(g => g.capacity), 1);
    const countryCount = new Set(sectorProjects.filter(p => p.isLive).map(p => getCountry(p.location))).size;

    return (
      <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border} col-span-2`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-sm font-medium ${themeClasses.text} flex items-center gap-2`}>
            <Globe size={16} className={themeClasses.textMuted} /> By Geography
          </h3>
          <span className={`text-xs ${themeClasses.textMuted}`}>{countryCount} countries</span>
        </div>
        {geoData.length > 0 ? (
          <div className="space-y-3">
            {geoData.map((geo, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className={`w-24 text-sm ${themeClasses.textMuted} truncate`}>{geo.name}</span>
                <div className={`flex-1 h-6 ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'} rounded-full overflow-hidden`}>
                  <div className="h-full bg-blue-700 rounded-full transition-all" style={{ width: `${(geo.capacity / maxCapacity) * 100}%` }} />
                </div>
                <span className={`text-sm font-medium ${themeClasses.text} w-28 text-right`}>
                  {formatMetricValue(geo.capacity, sectorConfig.primaryMetric)} ({geo.count})
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className={`${themeClasses.textSecondary} text-sm text-center py-4`}>No projects yet</p>
        )}
      </div>
    );
  };

  // Pipeline Status Widget
  const DashboardPipelineStatus = () => {
    const statusData = useMemo(() => {
      return pipelineStatuses.filter(s => s !== 'No Deal' && s !== 'Passed').map(status => ({
        name: status,
        count: sectorProjects.filter(p => p.pipelineStatus === status && p.isLive).length
      }));
    }, [sectorProjects, pipelineStatuses]);

    return (
      <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
        <h3 className={`text-sm font-medium ${themeClasses.text} mb-4`}>Pipeline Status</h3>
        <div className="space-y-2">
          {statusData.map((status, i) => (
            <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <span className={`text-sm ${themeClasses.textMuted}`}>{status.name}</span>
              <span className={`text-sm font-medium ${themeClasses.text}`}>{status.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Development Stage Widget
  const DashboardDevStage = () => {
    const stageData = useMemo(() => {
      return stages.map(stage => ({
        name: stage,
        count: sectorProjects.filter(p => p.stage === stage && p.isLive).length
      }));
    }, [sectorProjects, stages]);

    const maxCount = Math.max(...stageData.map(s => s.count), 1);

    return (
      <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
        <h3 className={`text-sm font-medium ${themeClasses.text} mb-4`}>Development Stage</h3>
        <div className="space-y-3">
          {stageData.map((stage, i) => (
            <div key={i}>
              <div className="flex justify-between text-sm mb-1">
                <span className={themeClasses.textMuted}>{stage.name}</span>
                <span className={`font-medium ${themeClasses.text}`}>{stage.count}</span>
              </div>
              <div className={`h-2 ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'} rounded-full overflow-hidden`}>
                <div className="h-full bg-blue-700 rounded-full" style={{ width: `${(stage.count / maxCount) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Investment by Region Widget
  const DashboardInvestmentByRegion = () => {
    const regionData = useMemo(() => {
      const grouped = {};
      sectorProjects.filter(p => p.isLive).forEach(p => {
        const country = getCountry(p.location);
        if (!grouped[country]) grouped[country] = 0;
        grouped[country] += p.investmentSize || 0;
      });
      return Object.entries(grouped)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);
    }, [sectorProjects]);

    return (
      <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
        <h3 className={`text-sm font-medium ${themeClasses.text} mb-4`}>Investment by Region</h3>
        {regionData.length > 0 ? (
          <div className="space-y-3">
            {regionData.map((region, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className={`text-sm ${themeClasses.textMuted}`}>{region.name}</span>
                <span className={`text-sm font-medium ${themeClasses.text}`}>{formatCurrency(region.total)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className={`${themeClasses.textSecondary} text-sm text-center py-4 italic`}>No investments yet</p>
        )}
      </div>
    );
  };

  // Priority Alert Box - ExpenseIQ's Unpaid Bills exact design
  const DashboardPriorityAlert = ({ onSelectProject }) => {
    const priorityItems = useMemo(() => {
      const now = new Date();
      return sectorProjects.map(p => {
        const daysUntilDeadline = p.deadline ? Math.ceil((new Date(p.deadline) - now) / (1000 * 60 * 60 * 24)) : 999;
        const daysSinceActivity = p.lastActivity ? Math.floor((now - new Date(p.lastActivity)) / (1000 * 60 * 60 * 24)) : 999;
        const isStale = daysSinceActivity > 30;
        const isOverdue = daysUntilDeadline < 0;
        const isUrgent = daysUntilDeadline <= 7 && daysUntilDeadline >= 0;
        return { ...p, daysUntilDeadline, daysSinceActivity, isStale, isOverdue, isUrgent };
      })
      .filter(p => p.isLive && (p.isOverdue || p.isUrgent || p.isStale))
      .sort((a, b) => a.daysUntilDeadline - b.daysUntilDeadline);
    }, [sectorProjects]);

    if (priorityItems.length === 0) return null;

    // Urgency color scheme
    const getUrgencyStyle = (item) => {
      if (item.isOverdue) return { bg: 'bg-red-100 dark:bg-red-900/30', icon: 'text-red-600 dark:text-red-400', text: 'text-red-600 dark:text-red-400' };
      if (item.isUrgent && item.daysUntilDeadline <= 3) return { bg: 'bg-blue-100 dark:bg-blue-900/30', icon: 'text-blue-800 dark:text-blue-800', text: 'text-blue-800 dark:text-blue-800' };
      if (item.isUrgent) return { bg: 'bg-blue-100 dark:bg-blue-900/30', icon: 'text-blue-800 dark:text-blue-800', text: 'text-blue-800 dark:text-blue-800' };
      return { bg: 'bg-slate-100 dark:bg-slate-800', icon: 'text-slate-500 dark:text-slate-400', text: 'text-slate-500 dark:text-slate-400' };
    };

    return (
      <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle size={18} className="text-slate-600" />
          <span className={`${themeClasses.text} font-semibold`}>Needs Attention ({priorityItems.length})</span>
        </div>
        <div className="space-y-2">
          {priorityItems.slice(0, 4).map(item => {
            const urgency = getUrgencyStyle(item);
            return (
              <div 
                key={item.id} 
                onClick={() => onSelectProject?.(item)}
                className={`flex items-center justify-between p-3 rounded-xl ${themeClasses.listItem} group cursor-pointer transition-all`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${urgency.bg}`}>
                    <Clock size={16} className={urgency.icon} />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>{item.name}</p>
                    <p className={`text-xs font-medium ${urgency.text}`}>
                      {item.isOverdue ? `${Math.abs(item.daysUntilDeadline)}d overdue` : item.isUrgent ? `Due in ${item.daysUntilDeadline}d` : `${item.daysSinceActivity}d inactive`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`${themeClasses.text} font-semibold tabular-nums`}>
                    {formatMetricValue(item.capacity, sectorConfig.primaryMetric)}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); setEditingProject(item); setShowProjectModal(true); }} className={`p-1 opacity-0 group-hover:opacity-100 hover:bg-slate-200 dark:hover:bg-slate-700 rounded ${themeClasses.textMuted}`}>
                    <Edit2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Pipeline Chart - ExpenseIQ's Spending Trend exact design
  const DashboardPipelineChart = () => {
    const chartData = useMemo(() => {
      const last30Days = [];
      const now = new Date();
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayProjects = sectorProjects.filter(p => p.createdAt && p.createdAt.split('T')[0] <= dateStr && p.isLive);
        const capacity = dayProjects.reduce((sum, p) => sum + (p.capacity || 0), 0);
        last30Days.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          amount: capacity,
        });
      }
      return last30Days;
    }, [sectorProjects]);

    return (
      <div className={`lg:col-span-2 ${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
        <h3 className={`text-base font-semibold ${themeClasses.text} mb-4`}>Pipeline Growth (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="pipelineGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="date" stroke="#64748b" fontSize={10} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={10} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '12px' }} 
              itemStyle={{ color: '#fff' }}
              labelStyle={{ color: '#94a3b8' }}
              formatter={(v) => [formatMetricValue(v, sectorConfig.primaryMetric), 'Capacity']} 
            />
            <Area type="monotone" dataKey="amount" stroke="#10b981" fill="url(#pipelineGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // ScreenerTab merged into Counterparts tab

  // Category Pie Chart - ExpenseIQ's DrillDownPieChart exact design
  const DashboardCategoryPieChart = () => {
    const [drillDown, setDrillDown] = useState(null);
    const [activeIndex, setActiveIndex] = useState(null);
    
    // Professional color palette - muted, sophisticated
    const PROFESSIONAL_COLORS = ['#1e3a5f', '#2d5a47', '#4a3f6b', '#6b4c3a', '#3d5a6b', '#5a4a3d', '#4b5563', '#374151'];
    const DRILL_COLORS = ['#3b5998', '#4a7c59', '#6b5b95', '#8b6b4a', '#5a7a8b', '#7a6a5a', '#6b7280', '#4b5563'];
    
    const categoryData = useMemo(() => {
      const grouped = {};
      sectorProjects.filter(p => p.isLive).forEach(p => {
        const tech = p.technology || 'Other';
        if (!grouped[tech]) grouped[tech] = { id: tech, name: tech, total: 0, color: '', projects: [] };
        grouped[tech].total += p.capacity || 0;
        grouped[tech].projects.push(p);
      });
      return Object.values(grouped)
        .sort((a, b) => b.total - a.total)
        .map((c, i) => ({ ...c, color: PROFESSIONAL_COLORS[i % PROFESSIONAL_COLORS.length] }));
    }, [sectorProjects]);

    const geoData = useMemo(() => {
      if (!drillDown) return [];
      const cat = categoryData.find(c => c.id === drillDown);
      if (!cat) return [];
      const grouped = {};
      cat.projects.forEach(p => {
        const geo = getCountry(p.location);
        if (!grouped[geo]) grouped[geo] = { name: geo, total: 0 };
        grouped[geo].total += p.capacity || 0;
      });
      return Object.values(grouped)
        .sort((a, b) => b.total - a.total)
        .map((g, i) => ({ ...g, color: DRILL_COLORS[i % DRILL_COLORS.length] }));
    }, [drillDown, categoryData]);

    const chartData = drillDown ? geoData : categoryData;
    const drillCat = categoryData.find(c => c.id === drillDown);
    const totalCapacity = chartData.reduce((sum, c) => sum + c.total, 0);
    
    // Filter out items below 1% for display (but keep in chart)
    const significantData = chartData.filter(item => 
      totalCapacity > 0 ? (item.total / totalCapacity) >= 0.01 : true
    );

    // Format large numbers
    const formatCompact = (num) => {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toFixed(1);
    };

    return (
      <div className={`${themeClasses.card} rounded-2xl p-6 border ${themeClasses.border}`}>
        <div className="flex items-center justify-between mb-4">
          {drillDown ? (
            <div className="flex items-center gap-2">
              <button onClick={() => setDrillDown(null)} className={`p-1.5 hover:bg-slate-100 rounded-lg ${themeClasses.textMuted}`}>
                <ChevronLeft size={18} />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: drillCat?.color }} />
                <h3 className={`text-sm font-semibold ${themeClasses.text}`}>{drillCat?.name}</h3>
              </div>
            </div>
          ) : (
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>By {sectorConfig.categoryLabel}</h3>
          )}
        </div>
        
        {chartData.length > 0 ? (
          <div className="flex flex-col items-center">
            {/* Large Donut Chart */}
            <div className="relative flex-shrink-0">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie 
                    data={chartData} 
                    cx="50%" 
                    cy="50%" 
                    innerRadius={55} 
                    outerRadius={85} 
                    paddingAngle={2} 
                    dataKey="total"
                    onClick={(data) => !drillDown && data.id && setDrillDown(data.id)}
                    onMouseEnter={(_, index) => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                    style={{ cursor: drillDown ? 'default' : 'pointer' }}
                    strokeWidth={0}
                  >
                    {chartData.map((entry, i) => (
                      <Cell 
                        key={i} 
                        fill={entry.color}
                        opacity={activeIndex === null || activeIndex === i ? 1 : 0.6}
                        style={{ transition: 'opacity 0.2s ease' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const percentage = totalCapacity > 0 ? ((data.total / totalCapacity) * 100).toFixed(1) : 0;
                        return (
                          <div className={`${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border rounded-lg shadow-lg px-3 py-2`}>
                            <p className={`text-sm font-medium ${themeClasses.text}`}>{data.name}</p>
                            <p className={`text-xs ${themeClasses.textMuted}`}>
                              {formatMetricValue(data.total, sectorConfig.primaryMetric)} ({percentage}%)
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Center Label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                {activeIndex !== null && chartData[activeIndex] ? (
                  <>
                    <span className={`text-lg font-bold ${themeClasses.text}`}>
                      {formatCompact(chartData[activeIndex].total)}
                    </span>
                    <span className={`text-xs ${themeClasses.textMuted} text-center px-2 truncate max-w-[80px]`}>
                      {chartData[activeIndex].name}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={`text-xl font-bold ${themeClasses.text}`}>
                      {formatCompact(totalCapacity)}
                    </span>
                    <span className={`text-xs ${themeClasses.textMuted}`}>{sectorConfig.primaryMetric.unit || 'Total'}</span>
                  </>
                )}
              </div>
            </div>

            {/* Legend - Below Chart */}
            <div className="w-full mt-4 space-y-1">
              {significantData.slice(0, 6).map((item, i) => {
                const percentage = totalCapacity > 0 ? ((item.total / totalCapacity) * 100) : 0;
                const originalIndex = chartData.findIndex(c => c.id === item.id || c.name === item.name);
                return (
                  <div 
                    key={i} 
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all ${!drillDown ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800' : ''} ${activeIndex === originalIndex ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                    onClick={() => !drillDown && item.id && setDrillDown(item.id)}
                    onMouseEnter={() => setActiveIndex(originalIndex)}
                    onMouseLeave={() => setActiveIndex(null)}
                  >
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className={`${themeClasses.text} text-sm truncate flex-1`}>{item.name}</span>
                    <span className={`${themeClasses.textMuted} text-xs tabular-nums`}>{percentage.toFixed(0)}%</span>
                  </div>
                );
              })}
              {chartData.length > significantData.length && (
                <p className={`${themeClasses.textMuted} text-xs pl-2 pt-1`}>
                  +{chartData.length - significantData.length} more
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-44">
            <p className={`${themeClasses.textSecondary} text-sm`}>No data yet</p>
          </div>
        )}
      </div>
    );
  };

  // DashboardRecentProjects removed - counterparts-only platform

  // Top Counterparts - ExpenseIQ's Top Vendors exact design
  const DashboardTopCounterparts = ({ onSelectCounterpart }) => {
    const topCounterparts = useMemo(() => {
      // Use all projects for ranking (not sector-filtered) for proper engagement metrics
      const withProjects = counterparts
        .map(c => {
          const projectCount = projects.filter(p => p.counterpartIds?.includes(c.id)).length;
          const totalCapacity = projects
            .filter(p => p.counterpartIds?.includes(c.id))
            .reduce((sum, p) => sum + (p.capacity || 0), 0);
          return { ...c, projectCount, totalCapacity };
        })
        .filter(c => c.projectCount > 0)
        .sort((a, b) => b.projectCount - a.projectCount || b.totalCapacity - a.totalCapacity)
        .slice(0, 5);
      
      // If no counterparts with projects, show most recently added/updated
      if (withProjects.length === 0 && counterparts.length > 0) {
        return counterparts
          .filter(c => c.type === 'person') // Prefer people over companies for "top contacts"
          .sort((a, b) => new Date(b.lastInteraction || b.created || 0) - new Date(a.lastInteraction || a.created || 0))
          .slice(0, 5)
          .map(c => ({ ...c, projectCount: 0, totalCapacity: 0 }));
      }
      
      return withProjects;
    }, [counterparts, projects]);

    return (
      <div className={`${themeClasses.card} rounded-2xl p-5 border ${themeClasses.border}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-base font-semibold ${themeClasses.text}`}>Top Counterparts</h3>
          <button onClick={() => setActiveTab('counterparts')} className="text-blue-800 text-xs hover:underline">View All</button>
        </div>
        {topCounterparts.length > 0 ? (
          <div className="space-y-2">
            {topCounterparts.map(cp => (
              <div
                key={cp.id}
                onClick={() => onSelectCounterpart?.(cp)}
                className={`flex items-center justify-between p-3 rounded-xl ${themeClasses.listItem} group cursor-pointer`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${cp.type === 'company' ? (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-100') : (theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-100')}`}>
                    {cp.type === 'company' 
                      ? <Building2 size={16} className={theme === 'dark' ? 'text-blue-800' : 'text-blue-800'} />
                      : <User size={16} className={theme === 'dark' ? 'text-blue-800' : 'text-blue-800'} />
                    }
                  </div>
                  <div>
                    <p className={`${themeClasses.text} text-sm font-medium`}>{cp.name}</p>
                    <p className={`${themeClasses.textSecondary} text-xs`}>
                      {cp.projectCount > 0 
                        ? `${cp.projectCount} project${cp.projectCount !== 1 ? 's' : ''}`
                        : cp.role || cp.company || cp.category || 'Contact'
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {cp.totalCapacity > 0 && (
                    <span className={`${themeClasses.text} text-sm font-medium`}>{formatMetricValue(cp.totalCapacity, sectorConfig.primaryMetric)}</span>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); setEditingCounterpart(cp); setShowCounterpartModal(true); }} className={`p-1 opacity-0 group-hover:opacity-100 hover:bg-slate-700 rounded ${themeClasses.textMuted}`}>
                    <Edit2 size={14} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteCounterpart(cp.id); }} className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={`${themeClasses.textSecondary} text-sm text-center py-8`}>No counterparts yet</p>
        )}
      </div>
    );
  };

  // ============================================
  // DEAL MODAL - Create/Edit deals with multi-counterpart linking
  // ============================================
  // Sorted alphabetically by label, with "Other" at the end
  const DEAL_ROLES_FALLBACK = [
    { value: 'advisor', label: 'Advisor' },
    { value: 'borrower', label: 'Borrower' },
    { value: 'buyer', label: 'Buyer/Acquirer' },
    { value: 'developer', label: 'Developer' },
    { value: 'epc', label: 'EPC Contractor' },
    { value: 'insurance_advisor', label: 'Insurance Advisor' },
    { value: 'investor', label: 'Investor' },
    { value: 'ipp', label: 'Independent Power Producer (IPP)' },
    { value: 'legal', label: 'Legal Counsel' },
    { value: 'legal_buy', label: 'Legal Counsel (Buy-side)' },
    { value: 'legal_sell', label: 'Legal Counsel (Sell-side)' },
    { value: 'lender', label: 'Lender/Financier' },
    { value: 'offtaker', label: 'Offtaker' },
    { value: 'parent_company', label: 'Parent Company' },
    { value: 'partner', label: 'Partner' },
    { value: 'regulator', label: 'Regulator' },
    { value: 'seller', label: 'Seller' },
    { value: 'supplier', label: 'Supplier/OEM' },
    { value: 'target', label: 'Target' },
    { value: 'technical_advisor', label: 'Technical Advisor' },
    { value: 'other', label: 'Other' },
  ];

  // Merge database roles with fallback to ensure all critical roles are always available
  const DEAL_ROLES = (() => {
    if (dealRoles.length === 0) return DEAL_ROLES_FALLBACK;
    // Create a set of values from database roles
    const dbValues = new Set(dealRoles.map(r => r.value));
    // Add any missing fallback roles
    const missingRoles = DEAL_ROLES_FALLBACK.filter(r => !dbValues.has(r.value));
    const merged = [...dealRoles, ...missingRoles];
    // Sort alphabetically by label, but keep "Other" at the end
    return merged.sort((a, b) => {
      if (a.value === 'other') return 1;
      if (b.value === 'other') return -1;
      return a.label.localeCompare(b.label);
    });
  })();

  const DEAL_TYPES = [
    { value: 'acquisition', label: 'Acquisition/M&A' },
    { value: 'investment', label: 'Investment' },
    { value: 'joint_venture', label: 'Joint Venture' },
    { value: 'development', label: 'Development' },
    { value: 'financing', label: 'Financing' },
    { value: 'divestiture', label: 'Divestiture/Sale' },
    { value: 'ppa', label: 'PPA/Offtake' },
    { value: 'supply_agreement', label: 'Supply Agreement' },
    { value: 'other', label: 'Other' },
  ];

  // DEAL_TECHNOLOGIES now comes from state (dealTechnologies) - see defaultDealTechnologies

  const DEAL_STATUSES = [
    { value: 'announced', label: 'Announced' },
    { value: 'tender', label: 'Tender' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'closed', label: 'Closed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'rumored', label: 'Rumored' },
  ];


  // Deal Flow Counterpart Filter with Autocomplete
  const DealCounterpartFilter = ({ value, onChange }) => {
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = React.useRef(null);

    // Get counterparts with deals
    const counterpartsWithDeals = useMemo(() => {
      return counterparts
        .filter(cp => cp.type === 'company' && deals.some(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cp.id)))
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [counterparts]);

    // Filter suggestions based on input
    const suggestions = useMemo(() => {
      if (!inputValue.trim()) return counterpartsWithDeals;
      const query = inputValue.toLowerCase().trim();
      return counterpartsWithDeals.filter(cp =>
        cp.name.toLowerCase().includes(query)
      );
    }, [counterpartsWithDeals, inputValue]);

    // Get selected counterpart name
    const selectedCounterpart = value !== 'all'
      ? counterparts.find(cp => cp.id === value)
      : null;

    const handleSelect = (cp) => {
      onChange(cp.id);
      setInputValue('');
      setIsOpen(false);
    };

    const handleClear = () => {
      onChange('all');
      setInputValue('');
      setIsOpen(false);
    };

    const totalDeals = useMemo(() => {
      return counterpartsWithDeals.reduce((sum, cp) =>
        sum + deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cp.id)).length, 0
      );
    }, [counterpartsWithDeals]);

    if (counterpartsWithDeals.length === 0) return null;

    return (
      <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-4`}>
        <h3 className={`font-semibold ${themeClasses.text} mb-3 flex items-center gap-2`}>
          <Filter size={16} className="text-blue-700" />
          Filter Deals
        </h3>

        <div className="relative">
          {/* Input field */}
          <div className="relative">
            <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${themeClasses.textMuted}`} />
            <input
              ref={inputRef}
              type="text"
              placeholder={selectedCounterpart ? selectedCounterpart.name : "All counterparts..."}
              value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); setIsOpen(true); }}
              onFocus={() => setIsOpen(true)}
              className={`w-full pl-8 pr-8 py-2 text-sm rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} placeholder:${themeClasses.textMuted} ${selectedCounterpart ? 'font-medium' : ''}`}
            />
            {(selectedCounterpart || inputValue) && (
              <button
                onClick={handleClear}
                className={`absolute right-2.5 top-1/2 -translate-y-1/2 ${themeClasses.textMuted} hover:text-red-500`}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Dropdown suggestions */}
          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsOpen(false)}
              />
              <div className={`absolute z-20 w-full mt-1 ${themeClasses.card} border ${themeClasses.border} rounded-lg shadow-lg max-h-48 overflow-y-auto`}>
                {/* All option */}
                <button
                  onClick={handleClear}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${themeClasses.cardHover} ${value === 'all' ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                >
                  <span className={themeClasses.text}>All Counterparts</span>
                  <span className={`text-xs ${themeClasses.textMuted}`}>{totalDeals} deals</span>
                </button>

                {/* Filtered suggestions */}
                {suggestions.length > 0 ? (
                  suggestions.map(cp => (
                    <button
                      key={cp.id}
                      onClick={() => handleSelect(cp)}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${themeClasses.cardHover} ${value === cp.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    >
                      <span className={`${themeClasses.text} truncate`}>{cp.name}</span>
                      <span className={`text-xs ${themeClasses.textMuted} flex-shrink-0 ml-2`}>
                        {deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cp.id)).length}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className={`px-3 py-2 text-sm ${themeClasses.textMuted}`}>
                    No matches found
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Selected indicator */}
        {selectedCounterpart && (
          <div className={`mt-2 flex items-center gap-2 text-xs ${themeClasses.textMuted}`}>
            <span>Showing deals from:</span>
            <span className="font-medium text-blue-700">{selectedCounterpart.name}</span>
          </div>
        )}
      </div>
    );
  };

  // Screener Bar - Dynamic universal filters with typeahead (Shibui: "Screeners are lenses")
  const ScreenerBar = ({ filters, onChange }) => {
    const [openFilter, setOpenFilter] = useState(null);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [newScreenerName, setNewScreenerName] = useState('');

    // Load saved screeners from localStorage
    const [savedScreeners, setSavedScreeners] = useState(() => {
      try {
        const saved = localStorage.getItem('sherpa_saved_screeners');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    });

    // Save screeners to localStorage
    const saveScreener = () => {
      if (!newScreenerName.trim()) return;
      const newScreener = {
        id: Date.now(),
        name: newScreenerName.trim(),
        filters: { ...filters },
        createdAt: new Date().toISOString()
      };
      const updated = [...savedScreeners, newScreener];
      setSavedScreeners(updated);
      localStorage.setItem('sherpa_saved_screeners', JSON.stringify(updated));
      setNewScreenerName('');
      setShowSaveDialog(false);
    };

    const deleteScreener = (id) => {
      const updated = savedScreeners.filter(s => s.id !== id);
      setSavedScreeners(updated);
      localStorage.setItem('sherpa_saved_screeners', JSON.stringify(updated));
    };

    const applyScreener = (screener) => {
      onChange(screener.filters);
    };

    // Count active filters (arrays with items or non-'all' values)
    const activeFiltersCount = Object.entries(filters).reduce((count, [key, val]) => {
      if (Array.isArray(val)) return count + val.length;
      if (val && val !== 'all') return count + 1;
      return count;
    }, 0);

    // Dynamically extract all filter options from actual deal data
    const filterOptions = useMemo(() => {
      const sectors = new Map();
      const geographies = new Map();
      const dealTypes = new Map();
      const statuses = new Map();

      // Process database deals
      deals.forEach(d => {
        const tech = d.technology || d.asset_type || d.sector;
        if (tech) {
          const techLower = tech.toLowerCase().replace(/[_-]/g, ' ');
          if (!sectors.has(techLower)) {
            const label = tech.split(/[_-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
            sectors.set(techLower, { value: tech, label });
          }
        }

        const dealCountries = Array.isArray(d.country) ? d.country : (d.country ? [d.country] : []);
        const geoFields = [...dealCountries, d.region, d.location].filter(Boolean);
        geoFields.forEach(geo => {
          const geoLower = geo.toLowerCase();
          if (!geographies.has(geoLower)) {
            geographies.set(geoLower, { value: geo, label: geo });
          }
        });

        const dt = d.deal_type || d.dealType;
        if (dt) {
          const dtLower = dt.toLowerCase();
          if (!dealTypes.has(dtLower)) {
            const label = capAcronyms(dt.split(/[_-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '));
            dealTypes.set(dtLower, { value: dt, label });
          }
        }

        const status = d.status;
        if (status) {
          const statusLower = status.toLowerCase();
          if (!statuses.has(statusLower)) {
            const label = status.split(/[_-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
            statuses.set(statusLower, { value: status, label });
          }
        }
      });

      // Build counterparts list from deals
      const counterpartsList = counterparts
        .filter(cp => cp.type === 'company')
        .map(cp => ({ value: cp.id, label: cp.name }))
        .sort((a, b) => a.label.localeCompare(b.label));

      return {
        sectors: Array.from(sectors.values()).sort((a, b) => a.label.localeCompare(b.label)),
        geographies: Array.from(geographies.values()).sort((a, b) => a.label.localeCompare(b.label)),
        dealTypes: Array.from(dealTypes.values()).sort((a, b) => a.label.localeCompare(b.label)),
        statuses: Array.from(statuses.values()).sort((a, b) => a.label.localeCompare(b.label)),
        counterparts: counterpartsList,
      };
    }, [deals, counterparts]);

    const timePeriods = [
      { value: '30', label: 'Last 30 days' },
      { value: '90', label: 'Last 90 days' },
      { value: '180', label: 'Last 6 months' },
      { value: '365', label: 'Last year' },
    ];

    // Typeahead filter component with search and multi-select
    const TypeaheadChip = ({ filterKey, options, selected = [], label, isMulti = true }) => {
      const isOpen = openFilter === filterKey;
      const [query, setQuery] = useState('');
      const selectedArray = Array.isArray(selected) ? selected : (selected && selected !== 'all' ? [selected] : []);
      const hasSelection = selectedArray.length > 0;

      const filtered = options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) &&
        !selectedArray.includes(o.value)
      );

      const toggleOption = (value) => {
        if (isMulti) {
          const newSelected = selectedArray.includes(value)
            ? selectedArray.filter(v => v !== value)
            : [...selectedArray, value];
          onChange({ ...filters, [filterKey]: newSelected });
        } else {
          onChange({ ...filters, [filterKey]: value });
          setOpenFilter(null);
        }
        setQuery('');
      };

      const removeOption = (value, e) => {
        e.stopPropagation();
        const newSelected = selectedArray.filter(v => v !== value);
        onChange({ ...filters, [filterKey]: isMulti ? newSelected : 'all' });
      };

      return (
        <div className="relative">
          <button
            onClick={() => setOpenFilter(isOpen ? null : filterKey)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              hasSelection
                ? 'bg-blue-700 text-white border-blue-700'
                : `${themeClasses.card} ${themeClasses.text} ${themeClasses.border} hover:border-blue-500`
            }`}
          >
            {hasSelection ? (
              <>
                <span>{label}: {selectedArray.length}</span>
              </>
            ) : (
              <span>{label}</span>
            )}
            <ChevronDown size={10} className={isOpen ? 'rotate-180' : ''} />
          </button>

          {isOpen && (
            <div className={`absolute z-50 mt-1 w-56 rounded-lg border ${themeClasses.border} ${themeClasses.card} shadow-xl`}>
              {/* Search input */}
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${themeClasses.textMuted}`} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={`w-full pl-8 pr-3 py-1.5 text-xs rounded-md border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                    autoFocus
                  />
                </div>
              </div>

              {/* Selected items */}
              {selectedArray.length > 0 && (
                <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-1">
                  {selectedArray.map(val => {
                    const opt = options.find(o => o.value === val);
                    return (
                      <span key={val} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {opt?.label || val}
                        <button onClick={(e) => removeOption(val, e)} className="hover:text-blue-900">
                          <X size={10} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Options list */}
              <div className="max-h-48 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className={`px-3 py-2 text-xs ${themeClasses.textMuted}`}>No matches</div>
                ) : (
                  filtered.slice(0, 50).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => toggleOption(opt.value)}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 ${themeClasses.text}`}
                    >
                      {opt.label}
                    </button>
                  ))
                )}
                {filtered.length > 50 && (
                  <div className={`px-3 py-2 text-xs ${themeClasses.textMuted}`}>
                    Type to narrow {filtered.length - 50} more...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    };

    // Simple select for time period (doesn't need typeahead)
    const SimpleChip = ({ value, options, onSelect, label }) => (
      <div className="relative">
        <select
          value={value || 'all'}
          onChange={(e) => onSelect(e.target.value)}
          className={`appearance-none pl-3 pr-7 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
            value && value !== 'all'
              ? 'bg-blue-700 text-white border-blue-700'
              : `${themeClasses.card} ${themeClasses.text} ${themeClasses.border} hover:border-blue-500`
          }`}
        >
          <option value="all">{label}</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={10} className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none ${value && value !== 'all' ? 'text-white' : themeClasses.textMuted}`} />
      </div>
    );

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (openFilter && !e.target.closest('.relative')) {
          setOpenFilter(null);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, [openFilter]);

    const clearAllFilters = () => {
      onChange({ sectors: [], geographies: [], dealTypes: [], statuses: [], timePeriod: 'all' });
    };

    return (
      <div className={`${themeClasses.card} rounded-lg border ${themeClasses.border} p-2.5 mb-4`}>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className={`${themeClasses.textMuted}`} />

          {filterOptions.sectors.length > 0 && (
            <TypeaheadChip
              filterKey="sectors"
              options={filterOptions.sectors}
              selected={filters.sectors || []}
              label="Sector"
            />
          )}

          {filterOptions.geographies.length > 0 && (
            <TypeaheadChip
              filterKey="geographies"
              options={filterOptions.geographies}
              selected={filters.geographies || []}
              label="Geography"
            />
          )}

          {filterOptions.dealTypes.length > 0 && (
            <TypeaheadChip
              filterKey="dealTypes"
              options={filterOptions.dealTypes}
              selected={filters.dealTypes || []}
              label="Deal Type"
            />
          )}

          {filterOptions.statuses.length > 0 && (
            <TypeaheadChip
              filterKey="statuses"
              options={filterOptions.statuses}
              selected={filters.statuses || []}
              label="Status"
            />
          )}

          {filterOptions.counterparts.length > 0 && (
            <TypeaheadChip
              filterKey="counterparts"
              options={filterOptions.counterparts}
              selected={filters.counterparts || []}
              label="Counterpart"
            />
          )}

          <SimpleChip
            value={filters.timePeriod}
            options={timePeriods}
            onSelect={(v) => onChange({ ...filters, timePeriod: v })}
            label="Time"
          />

          {activeFiltersCount > 0 && (
            <>
              <button
                onClick={() => setShowSaveDialog(true)}
                className="text-xs text-blue-700 hover:text-blue-800 font-medium flex items-center gap-1 ml-1"
              >
                <Star size={12} /> Save
              </button>
              <button
                onClick={clearAllFilters}
                className="text-xs text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1"
              >
                <X size={12} /> Clear
              </button>
            </>
          )}

          {/* Saved Screeners as Bubbles */}
          {savedScreeners.length > 0 && (
            <>
              <div className={`w-px h-5 ${themeClasses.border}`} />
              {savedScreeners.map(s => (
                <div key={s.id} className="relative group">
                  <button
                    onClick={() => applyScreener(s)}
                    className={`text-xs font-medium flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${
                      theme === 'dark'
                        ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50 border border-blue-700/50'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                    }`}
                  >
                    <Star size={10} className="fill-current" />
                    <span className="max-w-[100px] truncate">{s.name}</span>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteScreener(s.id); }}
                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${
                      theme === 'dark' ? 'bg-red-600 text-white' : 'bg-red-500 text-white'
                    } shadow-sm`}
                    title="Remove"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Save Dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSaveDialog(false)}>
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-4 w-80 shadow-xl`} onClick={e => e.stopPropagation()}>
              <h3 className={`font-semibold ${themeClasses.text} mb-3`}>Save Screener</h3>
              <input
                type="text"
                placeholder="e.g., APAC Solar Deals"
                value={newScreenerName}
                onChange={(e) => setNewScreenerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveScreener()}
                className={`w-full px-3 py-2 text-sm rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border ${themeClasses.border} ${themeClasses.text}`}
                >
                  Cancel
                </button>
                <button
                  onClick={saveScreener}
                  disabled={!newScreenerName.trim()}
                  className="flex-1 px-3 py-2 text-sm rounded-lg bg-blue-700 text-white font-medium disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============ INSIGHTS PANEL ============
  // Dynamic faceted insights from current deal results
  const InsightsPanel = ({ screenerFilters, onFilterChange, onCounterpartClick, onClose }) => {
    const [expandedSection, setExpandedSection] = useState(null);

    // Compute insights from FILTERED deals (matching what's displayed in center)
    const insights = useMemo(() => {
      // First, filter deals based on active screener filters
      let filteredDeals = deals;

      if (screenerFilters.sectors?.length > 0) {
        filteredDeals = filteredDeals.filter(deal => {
          const techs = Array.isArray(deal.technology) ? deal.technology : [deal.technology || deal.asset_type || deal.sector];
          const normalizedTechs = techs.filter(Boolean).map(t => normalizeTech(t)?.toLowerCase()).filter(Boolean);
          if (normalizedTechs.length === 0) return false;
          return screenerFilters.sectors.some(s => normalizedTechs.includes(s.toLowerCase()));
        });
      }

      if (screenerFilters.geographies?.length > 0) {
        filteredDeals = filteredDeals.filter(deal => {
          const dealCountries = (Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).map(c => c.toLowerCase().trim());
          const dealRegion = (deal.region || '').toLowerCase().trim();
          const dealLocation = (deal.location || '').toLowerCase().trim();
          return screenerFilters.geographies.some(g => {
            const filterVal = g.toLowerCase().trim();
            return dealCountries.some(dc => dc && (dc === filterVal || dc.includes(filterVal) || filterVal.includes(dc))) ||
                   (dealRegion && (dealRegion === filterVal || dealRegion.includes(filterVal) || filterVal.includes(dealRegion))) ||
                   (dealLocation && (dealLocation === filterVal || dealLocation.includes(filterVal) || filterVal.includes(dealLocation)));
          });
        });
      }

      if (screenerFilters.dealTypes?.length > 0) {
        filteredDeals = filteredDeals.filter(deal => {
          const dt = (deal.dealType || deal.deal_type || deal.type || '').toLowerCase();
          return screenerFilters.dealTypes.some(t => dt === t.toLowerCase() || dt.includes(t.toLowerCase()));
        });
      }

      if (screenerFilters.statuses?.length > 0) {
        filteredDeals = filteredDeals.filter(deal => {
          const status = (deal.status || '').toLowerCase();
          return screenerFilters.statuses.some(s => status === s.toLowerCase());
        });
      }

      if (screenerFilters.counterparts?.length > 0) {
        filteredDeals = filteredDeals.filter(deal => {
          const dealCounterpartIds = (deal.linkedCounterparts || []).map(lc => lc.counterpartId);
          const dbCounterpartIds = (deal.deal_counterparts || []).map(dc => dc.counterpart?.id);
          const allIds = [...dealCounterpartIds, ...dbCounterpartIds].filter(Boolean);
          return screenerFilters.counterparts.some(cpId => allIds.includes(cpId));
        });
      }

      const counterpartCounts = new Map();
      const geographyCounts = new Map();
      const sectorCounts = new Map();
      const statusCounts = new Map();

      filteredDeals.forEach(deal => {
        // Count counterparties
        const linkedCps = deal.linkedCounterparts ||
          (deal.deal_counterparts || []).map(dc => ({
            name: dc.counterpart?.name || dc.name || '',
            category: dc.counterpart?.type || dc.type || 'company',
            counterpartId: dc.counterpart?.id || dc.counterpart_id
          }));

        linkedCps.forEach(cp => {
          if (cp.name) {
            const existing = counterpartCounts.get(cp.name) || { count: 0, id: cp.counterpartId, category: cp.category };
            counterpartCounts.set(cp.name, { ...existing, count: existing.count + 1 });
          }
        });

        // Count geographies
        const dealCountries = Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : []);
        const geoList = [...dealCountries, deal.location, deal.region].filter(Boolean);
        geoList.forEach(geo => {
          geographyCounts.set(geo.trim(), (geographyCounts.get(geo.trim()) || 0) + 1);
        });

        // Count sectors/technologies
        const techs = Array.isArray(deal.technology) ? deal.technology : [deal.technology || deal.asset_type || deal.sector];
        techs.filter(Boolean).forEach(tech => {
          const techKey = tech.split(/[_-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
          sectorCounts.set(techKey, (sectorCounts.get(techKey) || 0) + 1);
        });

        // Count statuses
        if (deal.status) {
          const statusKey = deal.status.split(/[_-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
          statusCounts.set(statusKey, (statusCounts.get(statusKey) || 0) + 1);
        }
      });

      const sortByCount = (map) => [...map.entries()].sort((a, b) => b[1] - a[1]);
      const sortByCountObj = (map) => [...map.entries()].sort((a, b) => b[1].count - a[1].count);

      return {
        counterparts: sortByCountObj(counterpartCounts).slice(0, 12),
        geographies: sortByCount(geographyCounts).slice(0, 8),
        sectors: sortByCount(sectorCounts).slice(0, 6),
        statuses: sortByCount(statusCounts).slice(0, 5),
        totalDeals: filteredDeals.length,
        totalAllDeals: deals.length
      };
    }, [deals, screenerFilters]);

    // Check if a filter is active
    const isFilterActive = (type, value) => {
      if (type === 'sector') return screenerFilters.sectors?.includes(value);
      if (type === 'geography') return screenerFilters.geographies?.includes(value);
      if (type === 'status') return screenerFilters.statuses?.includes(value);
      return false;
    };

    // Toggle filter
    const toggleFilter = (type, value) => {
      const key = type === 'sector' ? 'sectors' : type === 'geography' ? 'geographies' : 'statuses';
      const current = screenerFilters[key] || [];
      const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      onFilterChange({ ...screenerFilters, [key]: updated });
    };

    // Active filters count
    const activeFiltersCount = (screenerFilters.sectors?.length || 0) +
      (screenerFilters.geographies?.length || 0) +
      (screenerFilters.statuses?.length || 0);

    const maxCpCount = insights.counterparts[0]?.[1]?.count || 1;
    const maxGeoCount = insights.geographies[0]?.[1] || 1;
    const maxSectorCount = insights.sectors[0]?.[1] || 1;

    // Section component with expandable content
    const InsightSection = ({ title, icon: Icon, items, type, maxCount, renderItem, color = 'blue' }) => {
      const isExpanded = expandedSection === type;
      const displayItems = isExpanded ? items : items.slice(0, 4);

      return (
        <div className={`${theme === 'dark' ? 'bg-slate-800/40' : 'bg-white'} rounded-xl overflow-hidden`}>
          <button
            onClick={() => setExpandedSection(isExpanded ? null : type)}
            className={`w-full flex items-center justify-between px-3 py-2.5 transition-colors ${
              theme === 'dark' ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                color === 'blue' ? 'bg-blue-100 text-blue-600' :
                color === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
                color === 'amber' ? 'bg-amber-100 text-amber-600' :
                'bg-purple-100 text-purple-600'
              }`}>
                <Icon size={14} />
              </div>
              <span className={`text-xs font-semibold ${themeClasses.text}`}>{title}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${theme === 'dark' ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                {items.length}
              </span>
            </div>
            <ChevronDown size={14} className={`${themeClasses.textMuted} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          <div className="px-3 pb-3 space-y-1.5">
            {displayItems.map(renderItem)}
          </div>
        </div>
      );
    };

    // Bar item component
    const BarItem = ({ label, count, maxCount, onClick, isActive, color = 'blue' }) => {
      const percentage = Math.round((count / maxCount) * 100);
      const colorClasses = {
        blue: { bar: 'bg-blue-500', activeBg: 'bg-blue-600', text: 'text-blue-600' },
        emerald: { bar: 'bg-emerald-500', activeBg: 'bg-emerald-600', text: 'text-emerald-600' },
        amber: { bar: 'bg-amber-500', activeBg: 'bg-amber-600', text: 'text-amber-600' },
        purple: { bar: 'bg-purple-500', activeBg: 'bg-purple-600', text: 'text-purple-600' },
      };
      const colors = colorClasses[color] || colorClasses.blue;

      return (
        <button
          onClick={onClick}
          className={`w-full group relative rounded-lg overflow-hidden transition-all ${
            isActive
              ? `${colors.activeBg} text-white`
              : theme === 'dark' ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
          }`}
        >
          <div className={`absolute inset-0 ${isActive ? 'opacity-0' : `${colors.bar} opacity-10`}`}
            style={{ width: `${percentage}%` }}
          />
          <div className="relative flex items-center justify-between px-2.5 py-1.5">
            <span className={`text-xs truncate flex-1 text-left ${
              isActive ? 'text-white font-medium' : themeClasses.text
            }`}>
              {label}
            </span>
            <div className="flex items-center gap-2 ml-2">
              <div className={`h-1.5 rounded-full overflow-hidden ${
                theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'
              }`} style={{ width: '40px' }}>
                <div
                  className={`h-full rounded-full transition-all ${isActive ? 'bg-white/60' : colors.bar}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className={`text-[10px] font-medium w-5 text-right ${
                isActive ? 'text-white/80' : themeClasses.textMuted
              }`}>
                {count}
              </span>
            </div>
          </div>
        </button>
      );
    };

    if (insights.totalDeals === 0) {
      return (
        <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-6`}>
          <div className={`text-center ${themeClasses.textMuted}`}>
            <PieChartIcon size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">No deals yet</p>
            <p className="text-xs mt-1 opacity-70">Insights will appear here</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        {/* Header */}
        <div className={`px-4 py-3 ${theme === 'dark' ? 'bg-gradient-to-r from-slate-800 to-slate-800/50' : 'bg-gradient-to-r from-slate-50 to-white'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
              }`}>
                <BarChart3 size={16} />
              </div>
              <div>
                <h3 className={`text-sm font-semibold ${themeClasses.text}`}>Deal Insights</h3>
                <p className={`text-[10px] ${themeClasses.textMuted}`}>
                  {insights.totalDeals === insights.totalAllDeals
                    ? `${insights.totalDeals} deals analyzed`
                    : `${insights.totalDeals} of ${insights.totalAllDeals} deals`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <button
                  onClick={() => onFilterChange({ sectors: [], geographies: [], statuses: [], dealTypes: [], timePeriod: 'all' })}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${
                    theme === 'dark' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  }`}
                >
                  <X size={12} />
                  {activeFiltersCount}
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className={`p-1.5 rounded-lg transition-colors ${themeClasses.cardHover}`}
                  title="Close panel"
                >
                  <X size={16} className={themeClasses.textMuted} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-3 space-y-2">
          {/* Top Counterparts */}
          {insights.counterparts.length > 0 && (
            <InsightSection
              title="Top Counterparts"
              icon={Building2}
              items={insights.counterparts}
              type="counterparts"
              maxCount={maxCpCount}
              color="blue"
              renderItem={([name, data]) => {
                const cp = counterparts.find(c => c.name === name);
                return (
                  <BarItem
                    key={name}
                    label={name}
                    count={data.count}
                    maxCount={maxCpCount}
                    onClick={() => cp && onCounterpartClick?.(cp)}
                    isActive={false}
                    color="blue"
                  />
                );
              }}
            />
          )}

          {/* Geography */}
          {insights.geographies.length > 0 && (
            <InsightSection
              title="Geography"
              icon={Globe}
              items={insights.geographies}
              type="geography"
              maxCount={maxGeoCount}
              color="emerald"
              renderItem={([geo, count]) => (
                <BarItem
                  key={geo}
                  label={geo}
                  count={count}
                  maxCount={maxGeoCount}
                  onClick={() => toggleFilter('geography', geo)}
                  isActive={isFilterActive('geography', geo)}
                  color="emerald"
                />
              )}
            />
          )}

          {/* Sectors */}
          {insights.sectors.length > 0 && (
            <InsightSection
              title="Technology"
              icon={Zap}
              items={insights.sectors}
              type="sector"
              maxCount={maxSectorCount}
              color="amber"
              renderItem={([sector, count]) => (
                <BarItem
                  key={sector}
                  label={sector}
                  count={count}
                  maxCount={maxSectorCount}
                  onClick={() => toggleFilter('sector', sector)}
                  isActive={isFilterActive('sector', sector)}
                  color="amber"
                />
              )}
            />
          )}

          {/* Status */}
          {insights.statuses.length > 0 && (
            <div className={`${theme === 'dark' ? 'bg-slate-800/40' : 'bg-white'} rounded-xl p-3`}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-purple-100 text-purple-600">
                  <Activity size={14} />
                </div>
                <span className={`text-xs font-semibold ${themeClasses.text}`}>Status</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {insights.statuses.map(([status, count]) => {
                  const isActive = isFilterActive('status', status);
                  const statusColors = {
                    closed: 'bg-green-100 text-green-700 hover:bg-green-200',
                    signed: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
                    announced: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
                    default: theme === 'dark' ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  };
                  const colorClass = isActive
                    ? 'bg-blue-600 text-white'
                    : statusColors[status.toLowerCase()] || statusColors.default;

                  return (
                    <button
                      key={status}
                      onClick={() => toggleFilter('status', status)}
                      className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${colorClass}`}
                    >
                      {status} <span className="opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============ COUNTERPART INSIGHTS PANEL ============
  // Analytics panel for Counterparts section (mirrors InsightsPanel)
  const CounterpartInsightsPanel = ({ filters, onFilterChange, counterpartsData, onClose }) => {
    const [expandedSection, setExpandedSection] = useState(null);

    // Use passed counterparts data (filtered) or fall back to all counterparts
    const dataToAnalyze = counterpartsData || counterparts;

    // Compute insights from counterparts data
    const insights = useMemo(() => {
      const categoryCounts = new Map();
      const technologyCounts = new Map();
      const regionCounts = new Map();
      const stageCounts = new Map();
      let companyCount = 0;
      let personCount = 0;

      dataToAnalyze.forEach(cp => {
        // Count types
        if (cp.type === 'company') companyCount++;
        else if (cp.type === 'person') personCount++;

        // Count categories
        if (cp.category) {
          categoryCounts.set(cp.category, (categoryCounts.get(cp.category) || 0) + 1);
        }

        // Count technologies from investmentPreferences
        const prefs = cp.investmentPreferences;
        const techs = prefs?.dimensions?.find(d => d.key === 'technologies' || d.key === 'sectors')?.values ||
                      prefs?.technologies || prefs?.sectors || [];
        techs.forEach(t => {
          const techName = typeof t === 'string' ? t : t.value || t.name;
          if (techName) {
            technologyCounts.set(techName, (technologyCounts.get(techName) || 0) + 1);
          }
        });

        // Count regions
        const regions = prefs?.dimensions?.find(d => d.key === 'geographies')?.values || prefs?.geographies || [];
        regions.forEach(r => {
          const regionName = typeof r === 'string' ? r : r.value || r.name;
          if (regionName) {
            regionCounts.set(regionName, (regionCounts.get(regionName) || 0) + 1);
          }
        });

        // Also count from headquarters
        if (cp.headquarters?.country) {
          regionCounts.set(cp.headquarters.country, (regionCounts.get(cp.headquarters.country) || 0) + 1);
        }

        // Count stages
        const stages = prefs?.dimensions?.find(d => d.key === 'dealStage' || d.key === 'stage')?.values || prefs?.stages || [];
        stages.forEach(s => {
          const stageName = typeof s === 'string' ? s : s.value || s.name;
          if (stageName) {
            stageCounts.set(stageName, (stageCounts.get(stageName) || 0) + 1);
          }
        });
      });

      const sortByCount = (map) => [...map.entries()].sort((a, b) => b[1] - a[1]);

      return {
        categories: sortByCount(categoryCounts).slice(0, 8),
        technologies: sortByCount(technologyCounts).slice(0, 8),
        regions: sortByCount(regionCounts).slice(0, 8),
        stages: sortByCount(stageCounts).slice(0, 6),
        companyCount,
        personCount,
        totalCounterparts: dataToAnalyze.length,
        totalAllCounterparts: counterparts.length
      };
    }, [dataToAnalyze, counterparts.length]);

    // Check if filter is active
    const isFilterActive = (type, value) => {
      if (type === 'category') return filters.category === value;
      if (type === 'technology') return filters.technologies?.includes(value);
      if (type === 'region') return filters.regions?.includes(value);
      if (type === 'stage') return filters.stages?.includes(value);
      return false;
    };

    // Toggle filter
    const toggleFilter = (type, value) => {
      if (type === 'category') {
        onFilterChange({ ...filters, category: filters.category === value ? 'all' : value });
      } else if (type === 'technology') {
        const current = filters.technologies || [];
        const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        onFilterChange({ ...filters, technologies: updated });
      } else if (type === 'region') {
        const current = filters.regions || [];
        const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        onFilterChange({ ...filters, regions: updated });
      } else if (type === 'stage') {
        const current = filters.stages || [];
        const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        onFilterChange({ ...filters, stages: updated });
      }
    };

    // Active filters count
    const activeFiltersCount =
      (filters.category && filters.category !== 'all' ? 1 : 0) +
      (filters.technologies?.length || 0) +
      (filters.regions?.length || 0) +
      (filters.stages?.length || 0) +
      (filters.type && filters.type !== 'all' ? 1 : 0);

    const maxCategoryCount = insights.categories[0]?.[1] || 1;
    const maxTechCount = insights.technologies[0]?.[1] || 1;
    const maxRegionCount = insights.regions[0]?.[1] || 1;

    // Section component
    const InsightSection = ({ title, icon: Icon, items, type, maxCount, color = 'blue' }) => {
      const isExpanded = expandedSection === type;
      const displayItems = isExpanded ? items : items.slice(0, 4);

      const colorClasses = {
        blue: { bar: 'bg-blue-500', activeBg: 'bg-blue-600', iconBg: 'bg-blue-100 text-blue-600' },
        emerald: { bar: 'bg-emerald-500', activeBg: 'bg-emerald-600', iconBg: 'bg-emerald-100 text-emerald-600' },
        amber: { bar: 'bg-amber-500', activeBg: 'bg-amber-600', iconBg: 'bg-amber-100 text-amber-600' },
        purple: { bar: 'bg-purple-500', activeBg: 'bg-purple-600', iconBg: 'bg-purple-100 text-purple-600' },
      };
      const colors = colorClasses[color] || colorClasses.blue;

      if (items.length === 0) return null;

      return (
        <div className={`${theme === 'dark' ? 'bg-slate-800/40' : 'bg-white'} rounded-xl overflow-hidden`}>
          <button
            onClick={() => setExpandedSection(isExpanded ? null : type)}
            className={`w-full flex items-center justify-between px-3 py-2.5 transition-colors ${
              theme === 'dark' ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${colors.iconBg}`}>
                <Icon size={14} />
              </div>
              <span className={`text-xs font-semibold ${themeClasses.text}`}>{title}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${theme === 'dark' ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                {items.length}
              </span>
            </div>
            <ChevronDown size={14} className={`${themeClasses.textMuted} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          <div className="px-3 pb-3 space-y-1.5">
            {displayItems.map(([label, count]) => {
              const isActive = isFilterActive(type, label);
              const percentage = Math.round((count / maxCount) * 100);

              return (
                <button
                  key={label}
                  onClick={() => toggleFilter(type, label)}
                  className={`w-full group relative rounded-lg overflow-hidden transition-all ${
                    isActive
                      ? `${colors.activeBg} text-white`
                      : theme === 'dark' ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className={`absolute inset-0 ${isActive ? 'opacity-0' : `${colors.bar} opacity-10`}`}
                    style={{ width: `${percentage}%` }}
                  />
                  <div className="relative flex items-center justify-between px-2.5 py-1.5">
                    <span className={`text-xs truncate flex-1 text-left ${
                      isActive ? 'text-white font-medium' : themeClasses.text
                    }`}>
                      {label}
                    </span>
                    <div className="flex items-center gap-2 ml-2">
                      <div className={`h-1.5 rounded-full overflow-hidden ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'
                      }`} style={{ width: '40px' }}>
                        <div
                          className={`h-full rounded-full transition-all ${isActive ? 'bg-white/60' : colors.bar}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className={`text-[10px] font-medium w-5 text-right ${
                        isActive ? 'text-white/80' : themeClasses.textMuted
                      }`}>
                        {count}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    };

    if (insights.totalCounterparts === 0) {
      return (
        <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-6`}>
          <div className={`text-center ${themeClasses.textMuted}`}>
            <Users size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">No counterparts yet</p>
            <p className="text-xs mt-1 opacity-70">Insights will appear here</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        {/* Header */}
        <div className={`px-4 py-3 ${theme === 'dark' ? 'bg-gradient-to-r from-slate-800 to-slate-800/50' : 'bg-gradient-to-r from-slate-50 to-white'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
              }`}>
                <Building2 size={16} />
              </div>
              <div>
                <h3 className={`text-sm font-semibold ${themeClasses.text}`}>Counterpart Insights</h3>
                <p className={`text-[10px] ${themeClasses.textMuted}`}>
                  {insights.totalCounterparts === insights.totalAllCounterparts
                    ? `${insights.totalCounterparts} counterparts analyzed`
                    : `${insights.totalCounterparts} of ${insights.totalAllCounterparts} counterparts`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <button
                  onClick={() => onFilterChange({ type: 'all', category: 'all', technologies: [], regions: [], stages: [], irrMin: '', irrMax: '' })}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${
                    theme === 'dark' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  }`}
                >
                  <X size={12} />
                  {activeFiltersCount}
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className={`p-1.5 rounded-lg transition-colors ${themeClasses.cardHover}`}
                  title="Close panel"
                >
                  <X size={16} className={themeClasses.textMuted} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Type Distribution */}
        <div className="p-3 space-y-2">
          {/* Company/Person chips */}
          <div className="flex gap-2">
            <button
              onClick={() => onFilterChange({ ...filters, type: filters.type === 'company' ? 'all' : 'company' })}
              className={`flex-1 text-xs px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 ${
                filters.type === 'company'
                  ? 'bg-blue-600 text-white'
                  : theme === 'dark' ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Building2 size={12} />
              Companies <span className="opacity-70">({insights.companyCount})</span>
            </button>
            <button
              onClick={() => onFilterChange({ ...filters, type: filters.type === 'person' ? 'all' : 'person' })}
              className={`flex-1 text-xs px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 ${
                filters.type === 'person'
                  ? 'bg-blue-600 text-white'
                  : theme === 'dark' ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <User size={12} />
              People <span className="opacity-70">({insights.personCount})</span>
            </button>
          </div>

          {/* Categories */}
          <InsightSection
            title="Category"
            icon={Briefcase}
            items={insights.categories}
            type="category"
            maxCount={maxCategoryCount}
            color="blue"
          />

          {/* Technologies */}
          <InsightSection
            title="Technology"
            icon={Zap}
            items={insights.technologies}
            type="technology"
            maxCount={maxTechCount}
            color="amber"
          />

          {/* Regions */}
          <InsightSection
            title="Geography"
            icon={Globe}
            items={insights.regions}
            type="region"
            maxCount={maxRegionCount}
            color="emerald"
          />

          {/* Stages */}
          {insights.stages.length > 0 && (
            <div className={`${theme === 'dark' ? 'bg-slate-800/40' : 'bg-white'} rounded-xl px-3 py-3`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-purple-100 text-purple-600">
                  <TrendingUp size={14} />
                </div>
                <span className={`text-xs font-semibold ${themeClasses.text}`}>Stage</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {insights.stages.map(([stage, count]) => {
                  const isActive = isFilterActive('stage', stage);
                  return (
                    <button
                      key={stage}
                      onClick={() => toggleFilter('stage', stage)}
                      className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${
                        isActive
                          ? 'bg-purple-600 text-white'
                          : theme === 'dark' ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                      }`}
                    >
                      {stage} <span className="opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============ MARKET MAP VIEW ============
  // Visual landscape of counterparts by technology & geography
  const MarketMapView = ({ counterpartsData, onSelectCounterpart }) => {
    const [mapTab, setMapTab] = useState('treemap'); // 'treemap' | 'matrix'
    const [selectedCell, setSelectedCell] = useState(null); // For matrix drill-down

    // Color palette for technology sectors
    const SECTOR_COLORS = {
      'Solar': '#f59e0b', 'Solar PV': '#f59e0b', 'Wind': '#3b82f6', 'Onshore Wind': '#3b82f6', 'Offshore Wind': '#1d4ed8',
      'Battery Storage': '#8b5cf6', 'Storage': '#8b5cf6', 'Data Center': '#ec4899', 'Data Centers': '#ec4899',
      'Hydro': '#06b6d4', 'Hydropower': '#06b6d4', 'Gas': '#6b7280', 'Natural Gas': '#6b7280',
      'Nuclear': '#ef4444', 'Transmission': '#14b8a6', 'Transport': '#f97316', 'Transport & Logistics': '#f97316', 'EV': '#84cc16', 'EV / Charging': '#84cc16',
      'Fiber': '#a855f7', 'Telecom': '#a855f7', 'Waste': '#78716c', 'Geothermal': '#dc2626',
      'Biomass': '#65a30d', 'Agriculture': '#16a34a', 'Agriculture & Natural Resources': '#16a34a', 'Mining': '#78716c', 'Mining & Extractives': '#78716c', 'Defense': '#0369a1', 'Telecom & Digital Infra': '#a855f7', 'Water & Waste': '#0891b2', 'Other': '#94a3b8'
    };

    const getColor = (tech) => {
      if (!tech) return '#94a3b8';
      const key = Object.keys(SECTOR_COLORS).find(k => tech.toLowerCase().includes(k.toLowerCase()));
      return key ? SECTOR_COLORS[key] : '#94a3b8';
    };

    // Build treemap data: group counterparts by their primary technology
    const treemapData = useMemo(() => {
      const techGroups = {};

      counterpartsData.forEach(cp => {
        const prefs = cp.investmentPreferences;
        const techs = cp.classifications?.technologies?.map(t => t.label) ||
          prefs?.dimensions?.find(d => d.key === 'technologies' || d.key === 'sectors')?.values?.map(v => v.label || v.value) ||
          prefs?.technologies || prefs?.sectors || [];

        const dealCount = deals.filter(d =>
          (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cp.id)
        ).length;

        const primaryTech = (techs.length > 0 ? techs[0] : 'Other');

        if (!techGroups[primaryTech]) {
          techGroups[primaryTech] = { name: primaryTech, children: [], totalDeals: 0 };
        }
        techGroups[primaryTech].children.push({
          name: cp.name,
          size: Math.max(dealCount, 1),
          dealCount,
          id: cp.id,
          category: cp.category || '',
          location: cp.headquarters ? [cp.headquarters.city, cp.headquarters.country].filter(Boolean).join(', ') : ''
        });
        techGroups[primaryTech].totalDeals += dealCount;

        // Also add to additional techs
        techs.slice(1).forEach(t => {
          if (!techGroups[t]) {
            techGroups[t] = { name: t, children: [], totalDeals: 0 };
          }
          techGroups[t].children.push({
            name: cp.name,
            size: Math.max(dealCount, 1),
            dealCount,
            id: cp.id,
            category: cp.category || '',
            location: cp.headquarters ? [cp.headquarters.city, cp.headquarters.country].filter(Boolean).join(', ') : ''
          });
          techGroups[t].totalDeals += dealCount;
        });
      });

      // Sort groups by total deals, take top sectors
      return Object.values(techGroups)
        .filter(g => g.children.length > 0)
        .sort((a, b) => b.totalDeals - a.totalDeals)
        .slice(0, 12)
        .map(g => ({
          ...g,
          children: g.children.sort((a, b) => b.size - a.size).slice(0, 20)
        }));
    }, [counterpartsData, deals]);

    // Build matrix data: technology × geography grid
    const matrixData = useMemo(() => {
      const techSet = new Set();
      const geoSet = new Set();
      const cellMap = {}; // "tech|geo" -> [counterparts]

      counterpartsData.forEach(cp => {
        const prefs = cp.investmentPreferences;
        const techs = cp.classifications?.technologies?.map(t => t.label) ||
          prefs?.dimensions?.find(d => d.key === 'technologies' || d.key === 'sectors')?.values?.map(v => v.label || v.value) ||
          prefs?.technologies || prefs?.sectors || [];
        const geos = cp.classifications?.geographies?.map(g => g.label) ||
          prefs?.dimensions?.find(d => d.key === 'geographies')?.values?.map(v => v.label || v.value) ||
          prefs?.geographies || [];

        const dealCount = deals.filter(d =>
          (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cp.id)
        ).length;

        const effectiveTechs = techs.length > 0 ? techs.slice(0, 3) : ['Other'];
        const effectiveGeos = geos.length > 0 ? geos.slice(0, 3) : ['Global'];

        effectiveTechs.forEach(tech => {
          techSet.add(tech);
          effectiveGeos.forEach(geo => {
            geoSet.add(geo);
            const key = `${tech}|${geo}`;
            if (!cellMap[key]) cellMap[key] = [];
            cellMap[key].push({ name: cp.name, id: cp.id, dealCount, category: cp.category });
          });
        });
      });

      // Sort by frequency
      const technologies = Array.from(techSet).sort((a, b) => {
        const aCount = Object.keys(cellMap).filter(k => k.startsWith(a + '|')).reduce((s, k) => s + cellMap[k].length, 0);
        const bCount = Object.keys(cellMap).filter(k => k.startsWith(b + '|')).reduce((s, k) => s + cellMap[k].length, 0);
        return bCount - aCount;
      }).slice(0, 10);

      const geographies = Array.from(geoSet).sort((a, b) => {
        const aCount = Object.keys(cellMap).filter(k => k.endsWith('|' + a)).reduce((s, k) => s + cellMap[k].length, 0);
        const bCount = Object.keys(cellMap).filter(k => k.endsWith('|' + b)).reduce((s, k) => s + cellMap[k].length, 0);
        return bCount - aCount;
      }).slice(0, 10);

      return { technologies, geographies, cellMap };
    }, [counterpartsData, deals]);

    // Custom treemap content renderer
    const TreemapContent = ({ x, y, width, height, name, dealCount, depth, index }) => {
      if (depth === 1) {
        // Sector header
        const sectorName = treemapData[index]?.name || name;
        return (
          <g>
            <rect x={x} y={y} width={width} height={height} fill={getColor(sectorName)} fillOpacity={0.15} stroke={getColor(sectorName)} strokeWidth={2} rx={4} />
            {width > 60 && height > 20 && (
              <text x={x + 8} y={y + 16} fontSize={11} fontWeight={600} fill={getColor(sectorName)}>{sectorName}</text>
            )}
          </g>
        );
      }
      if (depth === 2) {
        // Company cell
        const parentName = treemapData.find(g => g.children.some(c => c.name === name))?.name || '';
        const color = getColor(parentName);
        const showText = width > 50 && height > 24;
        const showDeals = width > 70 && height > 36;
        return (
          <g>
            <rect
              x={x + 1} y={y + 1} width={width - 2} height={height - 2}
              fill={color} fillOpacity={0.7} rx={3}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                const cp = counterpartsData.find(c => c.name === name);
                if (cp) onSelectCounterpart(cp);
              }}
            />
            {showText && (
              <text x={x + 6} y={y + 15} fontSize={10} fontWeight={500} fill="#fff" style={{ pointerEvents: 'none' }}>
                {name.length > Math.floor(width / 6) ? name.substring(0, Math.floor(width / 6)) + '...' : name}
              </text>
            )}
            {showDeals && dealCount > 0 && (
              <text x={x + 6} y={y + 28} fontSize={9} fill="rgba(255,255,255,0.8)" style={{ pointerEvents: 'none' }}>
                {dealCount} deal{dealCount !== 1 ? 's' : ''}
              </text>
            )}
          </g>
        );
      }
      return null;
    };

    // Max count for matrix bubble sizing
    const maxCellCount = useMemo(() => {
      return Math.max(1, ...Object.values(matrixData.cellMap).map(arr => arr.length));
    }, [matrixData]);

    return (
      <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        {/* Header with tab toggle */}
        <div className={`p-4 border-b ${themeClasses.border}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe size={18} className="text-blue-700" />
              <h3 className={`font-semibold ${themeClasses.text}`}>Market Map</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                {counterpartsData.length} companies
              </span>
            </div>
            <div className={`flex items-center gap-1 p-0.5 rounded-lg ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <button
                onClick={() => setMapTab('treemap')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  mapTab === 'treemap' ? 'bg-blue-700 text-white shadow-sm' : `${themeClasses.textMuted} hover:${themeClasses.text}`
                }`}
              >
                Treemap
              </button>
              <button
                onClick={() => { setMapTab('matrix'); setSelectedCell(null); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  mapTab === 'matrix' ? 'bg-blue-700 text-white shadow-sm' : `${themeClasses.textMuted} hover:${themeClasses.text}`
                }`}
              >
                Matrix
              </button>
            </div>
          </div>
        </div>

        {/* Treemap View */}
        {mapTab === 'treemap' && (
          <div className="p-4">
            {/* Legend */}
            <div className="flex flex-wrap gap-2 mb-4">
              {treemapData.slice(0, 8).map(sector => (
                <div key={sector.name} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: getColor(sector.name) }} />
                  <span className={`text-xs ${themeClasses.textMuted}`}>{sector.name} ({sector.children.length})</span>
                </div>
              ))}
            </div>

            {treemapData.length > 0 ? (
              <ResponsiveContainer width="100%" height={480}>
                <Treemap
                  data={treemapData}
                  dataKey="size"
                  aspectRatio={4 / 3}
                  stroke={theme === 'dark' ? '#1e293b' : '#fff'}
                  content={<TreemapContent />}
                  animationDuration={300}
                />
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12">
                <Globe size={32} className={themeClasses.textMuted} />
                <p className={`text-sm ${themeClasses.textMuted} mt-2`}>No technology data available for mapping</p>
              </div>
            )}

            <p className={`text-xs ${themeClasses.textMuted} mt-3`}>
              Rectangle size reflects deal activity. Click a company to view its profile.
            </p>
          </div>
        )}

        {/* Matrix View */}
        {mapTab === 'matrix' && (
          <div className="p-4">
            {selectedCell ? (
              /* Drill-down: show companies at selected intersection */
              <div>
                <button
                  onClick={() => setSelectedCell(null)}
                  className={`flex items-center gap-1 text-sm ${themeClasses.textMuted} hover:${themeClasses.text} mb-4`}
                >
                  <ChevronLeft size={16} /> Back to matrix
                </button>
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2 py-1 rounded-md text-xs font-medium text-white" style={{ backgroundColor: getColor(selectedCell.tech) }}>
                    {selectedCell.tech}
                  </span>
                  <span className={`text-xs ${themeClasses.textMuted}`}>in</span>
                  <span className={`px-2 py-1 rounded-md text-xs font-medium ${theme === 'dark' ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                    {selectedCell.geo}
                  </span>
                  <span className={`text-xs ${themeClasses.textMuted}`}>
                    ({selectedCell.companies.length} {selectedCell.companies.length === 1 ? 'company' : 'companies'})
                  </span>
                </div>
                <div className={`divide-y ${themeClasses.border} border ${themeClasses.border} rounded-xl overflow-hidden`}>
                  {selectedCell.companies.sort((a, b) => b.dealCount - a.dealCount).map(cp => (
                    <button
                      key={cp.id}
                      onClick={() => {
                        const full = counterpartsData.find(c => c.id === cp.id);
                        if (full) onSelectCounterpart(full);
                      }}
                      className={`w-full flex items-center justify-between p-3 ${themeClasses.cardHover} text-left`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                          {cp.name.charAt(0)}
                        </div>
                        <div>
                          <div className={`text-sm font-medium ${themeClasses.text}`}>{cp.name}</div>
                          {cp.category && <div className={`text-xs ${themeClasses.textMuted}`}>{cp.category}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {cp.dealCount > 0 && (
                          <span className={`text-xs font-medium ${theme === 'dark' ? 'text-blue-400' : 'text-blue-700'}`}>
                            {cp.dealCount} deal{cp.dealCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        <ChevronRight size={14} className={themeClasses.textMuted} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Main matrix grid */
              <div>
                <p className={`text-xs ${themeClasses.textMuted} mb-3`}>Technology x Geography — click a cell to see companies at that intersection</p>
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full border-collapse min-w-[600px]">
                    <thead>
                      <tr>
                        <th className={`p-2 text-left text-xs font-semibold ${themeClasses.textMuted} sticky left-0 ${themeClasses.card} z-10`}></th>
                        {matrixData.geographies.map(geo => (
                          <th key={geo} className={`p-2 text-center text-xs font-medium ${themeClasses.textMuted}`} style={{ minWidth: 70 }}>
                            <span className="block truncate max-w-[80px]" title={geo}>{geo}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrixData.technologies.map(tech => (
                        <tr key={tech}>
                          <td className={`p-2 text-xs font-medium ${themeClasses.text} sticky left-0 ${themeClasses.card} z-10 border-r ${themeClasses.border}`}>
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: getColor(tech) }} />
                              <span className="truncate max-w-[100px]" title={tech}>{tech}</span>
                            </div>
                          </td>
                          {matrixData.geographies.map(geo => {
                            const key = `${tech}|${geo}`;
                            const companies = matrixData.cellMap[key] || [];
                            const count = companies.length;
                            const intensity = count > 0 ? Math.min(count / maxCellCount, 1) : 0;
                            const color = getColor(tech);

                            return (
                              <td key={geo} className={`p-1 text-center border ${themeClasses.border}`}>
                                {count > 0 ? (
                                  <button
                                    onClick={() => setSelectedCell({ tech, geo, companies })}
                                    className="w-full flex items-center justify-center p-1.5 rounded-lg transition-all hover:scale-105"
                                    style={{ backgroundColor: color + Math.round(intensity * 40 + 15).toString(16).padStart(2, '0') }}
                                    title={`${tech} in ${geo}: ${count} companies`}
                                  >
                                    <div
                                      className="rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                                      style={{
                                        width: Math.max(20, Math.min(36, 16 + count * 3)),
                                        height: Math.max(20, Math.min(36, 16 + count * 3)),
                                        backgroundColor: color,
                                        opacity: 0.6 + intensity * 0.4
                                      }}
                                    >
                                      {count}
                                    </div>
                                  </button>
                                ) : (
                                  <div className={`p-1.5 text-[10px] ${themeClasses.textMuted}`}>-</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={`text-xs ${themeClasses.textMuted} mt-3`}>
                  Showing top {matrixData.technologies.length} technologies x {matrixData.geographies.length} geographies by company coverage
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ============ DEAL FILTER SIDEBAR ============

  // --- DealsSearchResultsBanner — shown on Deals tab when smart search active ---
  const DealsSearchResultsBanner = () => {
    if (smartSearchDealIds === null) return null;

    const clearSearch = () => {
      setSmartSearchDealIds(null);
      setSmartSearchDealQuery('');
      setSmartSearchDealCps([]);
      setScreenerFilters({ sectors: [], geographies: [], dealTypes: [], statuses: [], counterparts: [], timePeriod: 'all' });
    };

    return (
      <div className={`rounded-xl border p-3 ${isDark ? 'bg-blue-900/20 border-blue-800/40' : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-blue-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
              Search results for "{smartSearchDealQuery}"
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${isDark ? 'bg-blue-800/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
              {smartSearchDealIds.length} deal{smartSearchDealIds.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button onClick={clearSearch} className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${isDark ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`}>
            <X size={12} /> Clear
          </button>
        </div>
        {smartSearchDealCps.length > 0 && (
          <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1">
            <span className={`text-xs flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Related counterparts:</span>
            {smartSearchDealCps.slice(0, 6).map(cp => (
              <button
                key={cp.id}
                onClick={() => {
                  const fullCp = counterparts.find(c => c.id === cp.id);
                  if (fullCp) viewPanelCounterpart(fullCp);
                }}
                className={`flex-shrink-0 text-xs px-2.5 py-1 rounded-lg border transition-colors ${isDark
                  ? 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                {cp.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============ COUNTERPART FILTER SIDEBAR ============
  // Left sidebar for structured filters (replaces CounterpartScreenerBar + local search input)

  // ============ COUNTERPART SCREENER BAR ============
  // Top filter bar for Counterparts section (mirrors ScreenerBar)
  // Now uses taxonomy-based filter options from database
  const CounterpartScreenerBar = ({ filters, onChange, taxonomyOptions }) => {
    const [openFilter, setOpenFilter] = useState(null);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [newScreenerName, setNewScreenerName] = useState('');

    // Load saved counterpart screeners from localStorage
    const [savedScreeners, setSavedScreeners] = useState(() => {
      try {
        const saved = localStorage.getItem('sherpa_saved_cp_screeners');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    });

    // Save screener to localStorage
    const saveScreener = () => {
      if (!newScreenerName.trim()) return;
      const newScreener = {
        id: Date.now(),
        name: newScreenerName.trim(),
        filters: { ...filters },
        createdAt: new Date().toISOString()
      };
      const updated = [...savedScreeners, newScreener];
      setSavedScreeners(updated);
      localStorage.setItem('sherpa_saved_cp_screeners', JSON.stringify(updated));
      setNewScreenerName('');
      setShowSaveDialog(false);
    };

    const deleteScreener = (id) => {
      const updated = savedScreeners.filter(s => s.id !== id);
      setSavedScreeners(updated);
      localStorage.setItem('sherpa_saved_cp_screeners', JSON.stringify(updated));
    };

    const applyScreener = (screener) => {
      // Merge with defaults to ensure all keys exist
      const defaultFilters = { type: 'all', category: 'all', role: 'all', technologies: [], regions: [], stages: [], irrMin: '', irrMax: '' };
      onChange({ ...defaultFilters, ...screener.filters });
    };

    // Use taxonomy-based filter options from database when available
    const filterOptions = useMemo(() => {
      // If taxonomy options loaded, use them (with counts)
      if (taxonomyOptions?.loaded) {
        const alpha = (a, b) => a.label.localeCompare(b.label);

        // Start with taxonomy geographies (countries)
        const taxonomyRegions = taxonomyOptions.geographies.filter(g => g.type === 'country').map(g => ({
          value: g.id,
          label: g.label,
          parent: g.parent?.label,
          count: g.count || 0
        }));

        // Also collect countries from counterpart HQ data not in taxonomy
        const taxonomyLabelSet = new Set(taxonomyRegions.map(g => g.label.toLowerCase()));
        const extraCountries = new Map();
        counterparts.forEach(cp => {
          const hqCountry = cp.investmentPreferences?.headquarters?.country || cp.headquarters?.country || cp.country;
          if (hqCountry) {
            const key = hqCountry.toLowerCase().trim();
            if (!taxonomyLabelSet.has(key) && !extraCountries.has(key)) {
              const label = hqCountry.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
              extraCountries.set(key, { value: `hq_${key}`, label, count: 0 });
            }
            if (extraCountries.has(key)) {
              extraCountries.get(key).count++;
            }
          }
        });

        // Combine taxonomy + HQ countries, filter out empty (zero-count) options
        const allRegions = [...taxonomyRegions, ...extraCountries.values()]
          .filter(g => g.count > 0)
          .sort(alpha);

        return {
          categories: [], // Categories not in taxonomy yet
          types: taxonomyOptions.types.map(t => ({
            value: t.id,
            label: t.label,
            count: t.count || 0
          })).filter(t => t.count > 0).sort(alpha),
          technologies: taxonomyOptions.technologies.map(t => ({
            value: t.id,
            label: t.label,
            sector: t.sector?.label,
            count: t.count || 0
          })).filter(t => t.count > 0).sort(alpha),
          regions: allRegions,
          stages: taxonomyOptions.stages.map(s => ({
            value: s.id,
            label: s.label,
            count: s.count || 0
          })).filter(s => s.count > 0).sort(alpha)
        };
      }

      // Fallback to extracting from counterparts data
      const categories = new Set();
      const technologies = new Set();
      const regions = new Map();
      const stages = new Set();

      counterparts.forEach(cp => {
        if (cp.category) categories.add(cp.category);

        const prefs = cp.investmentPreferences;
        const techs = prefs?.dimensions?.find(d => d.key === 'technologies' || d.key === 'sectors')?.values ||
                      prefs?.technologies || prefs?.sectors || [];
        techs.forEach(t => {
          const name = typeof t === 'string' ? t : t.value || t.name;
          if (name) technologies.add(name);
        });

        const geos = prefs?.dimensions?.find(d => d.key === 'geographies')?.values || prefs?.geographies || [];
        geos.forEach(r => {
          const name = typeof r === 'string' ? r : r.value || r.name;
          if (name) regions.set(name.toLowerCase(), name);
        });
        // Also include headquarters country
        const hqCountry = prefs?.headquarters?.country || cp.headquarters?.country || cp.country;
        if (hqCountry) regions.set(hqCountry.toLowerCase().trim(), hqCountry.trim());

        const stgs = prefs?.dimensions?.find(d => d.key === 'dealStage' || d.key === 'stage')?.values || prefs?.stages || [];
        stgs.forEach(s => {
          const name = typeof s === 'string' ? s : s.value || s.name;
          if (name) stages.add(name);
        });
      });

      return {
        categories: [...categories].sort().map(c => ({ value: c, label: c })),
        types: [],
        technologies: [...technologies].sort().map(t => ({ value: t, label: t })),
        regions: [...regions.values()].sort().map(r => ({ value: r, label: r })),
        stages: [...stages].sort().map(s => ({ value: s, label: s })),
      };
    }, [counterparts, taxonomyOptions]);

    // Count active filters
    const activeFiltersCount =
      (filters.type && filters.type !== 'all' ? 1 : 0) +
      (filters.category && filters.category !== 'all' ? 1 : 0) +
      (filters.counterpartTypes?.length || 0) +
      (filters.technologies?.length || 0) +
      (filters.regions?.length || 0) +
      (filters.stages?.length || 0);

    // Typeahead filter component
    const TypeaheadChip = ({ filterKey, options, selected = [], label, isMulti = true }) => {
      const isOpen = openFilter === filterKey;
      const [query, setQuery] = useState('');
      const selectedArray = Array.isArray(selected) ? selected : (selected && selected !== 'all' ? [selected] : []);
      const hasSelection = selectedArray.length > 0;

      const filtered = options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) &&
        !selectedArray.includes(o.value)
      );

      const toggleOption = (value) => {
        if (isMulti) {
          const newSelected = selectedArray.includes(value)
            ? selectedArray.filter(v => v !== value)
            : [...selectedArray, value];
          onChange({ ...filters, [filterKey]: newSelected });
        } else {
          onChange({ ...filters, [filterKey]: value });
          setOpenFilter(null);
        }
        setQuery('');
      };

      const removeOption = (value, e) => {
        e.stopPropagation();
        if (isMulti) {
          onChange({ ...filters, [filterKey]: selectedArray.filter(v => v !== value) });
        } else {
          onChange({ ...filters, [filterKey]: 'all' });
        }
      };

      return (
        <div className="relative">
          <button
            onClick={() => setOpenFilter(isOpen ? null : filterKey)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              hasSelection
                ? 'bg-blue-700 text-white border-blue-700'
                : `${themeClasses.card} ${themeClasses.text} ${themeClasses.border} hover:border-blue-500`
            }`}
          >
            {hasSelection ? (
              <>
                <span>{label}: {selectedArray.length}</span>
              </>
            ) : (
              <span>{label}</span>
            )}
            <ChevronDown size={10} className={isOpen ? 'rotate-180' : ''} />
          </button>

          {isOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpenFilter(null)} />
              <div className={`absolute z-50 mt-1 w-56 rounded-lg border ${themeClasses.border} ${themeClasses.card} shadow-xl`}>
                {/* Search input */}
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${themeClasses.textMuted}`} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className={`w-full pl-8 pr-3 py-1.5 text-xs rounded-md border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Selected items */}
                {selectedArray.length > 0 && (
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-1">
                    {selectedArray.map(v => (
                      <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {options.find(o => o.value === v)?.label || v}
                        <button onClick={(e) => removeOption(v, e)} className="hover:text-blue-900">
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Options list */}
                <div className="max-h-48 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <div className={`px-3 py-2 text-xs ${themeClasses.textMuted}`}>No matches</div>
                  ) : (
                    filtered.slice(0, 50).map(option => (
                      <button
                        key={option.value}
                        onClick={() => toggleOption(option.value)}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 ${themeClasses.text} flex justify-between items-center`}
                      >
                        <span>{option.label}</span>
                        {option.count !== undefined && option.count > 0 && (
                          <span className={`text-xs ${themeClasses.textMuted} ml-2`}>({option.count})</span>
                        )}
                      </button>
                    ))
                  )}
                  {filtered.length > 50 && (
                    <div className={`px-3 py-2 text-xs ${themeClasses.textMuted}`}>
                      Type to narrow {filtered.length - 50} more...
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      );
    };

    // Get active filter chips to display
    const activeChips = [];
    if (filters.type && filters.type !== 'all') {
      activeChips.push({ key: 'type', value: filters.type, label: filters.type === 'company' ? 'Company' : 'Person', prefix: 'Entity' });
    }
    if (filters.category && filters.category !== 'all') {
      activeChips.push({ key: 'category', value: filters.category, label: filters.category });
    }
    filters.counterpartTypes?.forEach(t => {
      const typeOpt = filterOptions.types?.find(o => o.value === t);
      activeChips.push({ key: 'counterpartTypes', value: t, label: typeOpt?.label || t });
    });
    filters.technologies?.forEach(t => {
      const techOpt = filterOptions.technologies?.find(o => o.value === t);
      activeChips.push({ key: 'technologies', value: t, label: techOpt?.label || t });
    });
    filters.regions?.forEach(r => {
      const geoOpt = filterOptions.regions?.find(o => o.value === r);
      activeChips.push({ key: 'regions', value: r, label: geoOpt?.label || r });
    });
    filters.stages?.forEach(s => {
      const stageOpt = filterOptions.stages?.find(o => o.value === s);
      activeChips.push({ key: 'stages', value: s, label: stageOpt?.label || s });
    });

    const removeChip = (key, value) => {
      if (key === 'type' || key === 'category') {
        onChange({ ...filters, [key]: 'all' });
      } else {
        onChange({ ...filters, [key]: filters[key].filter(v => v !== value) });
      }
    };

    return (
      <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-3`}>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter icon */}
          <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-100'}`}>
            <Filter size={16} className={themeClasses.textMuted} />
          </div>

          {/* Entity filter (Company vs Person) */}
          <div className="relative">
            <button
              onClick={() => setOpenFilter(openFilter === 'type' ? null : 'type')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                filters.type && filters.type !== 'all'
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : theme === 'dark' ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Entity
              <ChevronDown size={14} className={`transition-transform ${openFilter === 'type' ? 'rotate-180' : ''}`} />
            </button>
            {openFilter === 'type' && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setOpenFilter(null)} />
                <div className={`absolute top-full left-0 mt-1 w-40 z-50 ${themeClasses.card} border ${themeClasses.border} rounded-xl shadow-xl overflow-hidden p-1`}>
                  {[
                    { value: 'all', label: 'All Entities' },
                    { value: 'company', label: 'Companies' },
                    { value: 'person', label: 'People' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { onChange({ ...filters, type: opt.value }); setOpenFilter(null); }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                        filters.type === opt.value ? 'bg-blue-50 text-blue-700' : ''
                      } ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} ${themeClasses.text}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Counterpart Type filter (Investor, Developer, etc from taxonomy) */}
          {filterOptions.types?.length > 0 && (
            <TypeaheadChip
              filterKey="counterpartTypes"
              options={filterOptions.types}
              selected={filters.counterpartTypes || []}
              label="Type"
            />
          )}

          {/* Technology filter */}
          <TypeaheadChip
            filterKey="technologies"
            options={filterOptions.technologies}
            selected={filters.technologies || []}
            label="Technology"
          />

          {/* Region filter */}
          <TypeaheadChip
            filterKey="regions"
            options={filterOptions.regions}
            selected={filters.regions || []}
            label="Geography"
          />

          {/* Stage filter */}
          <TypeaheadChip
            filterKey="stages"
            options={filterOptions.stages}
            selected={filters.stages || []}
            label="Stage"
          />

          {/* Active filters count & actions */}
          {activeFiltersCount > 0 && (
            <>
              <div className={`w-px h-5 ${themeClasses.border}`} />
              <button
                onClick={() => setShowSaveDialog(true)}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${
                  theme === 'dark' ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                }`}
              >
                <Bookmark size={12} /> Save
              </button>
              <button
                onClick={() => onChange({ type: 'all', category: 'all', counterpartTypes: [], technologies: [], regions: [], stages: [], irrMin: '', irrMax: '' })}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${
                  theme === 'dark' ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
              >
                <X size={12} /> Clear
              </button>
            </>
          )}

          {/* Saved Screeners as Bubbles */}
          {savedScreeners.length > 0 && (
            <>
              <div className={`w-px h-5 ${themeClasses.border}`} />
              {savedScreeners.map(s => (
                <div key={s.id} className="relative group">
                  <button
                    onClick={() => applyScreener(s)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      theme === 'dark' ? 'border-slate-600 text-slate-300 hover:border-blue-500 hover:text-blue-400' : 'border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600'
                    }`}
                  >
                    <Bookmark size={10} />
                    {s.name}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteScreener(s.id); }}
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Save Dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSaveDialog(false)}>
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-4 w-80 shadow-xl`} onClick={e => e.stopPropagation()}>
              <h3 className={`font-semibold ${themeClasses.text} mb-3`}>Save Screener</h3>
              <input
                type="text"
                placeholder="e.g., European Investors"
                value={newScreenerName}
                onChange={(e) => setNewScreenerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveScreener()}
                className={`w-full px-3 py-2 text-sm rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${themeClasses.cardHover}`}
                >
                  Cancel
                </button>
                <button
                  onClick={saveScreener}
                  disabled={!newScreenerName.trim()}
                  className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active filter chips */}
        {activeChips.length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {activeChips.map((chip, i) => (
              <span
                key={`${chip.key}-${chip.value}-${i}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium"
              >
                <Star size={10} className="fill-blue-500" />
                {chip.label}
                <button onClick={() => removeChip(chip.key, chip.value)} className="hover:text-blue-900">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // BLOOMBERG-STYLE DASHBOARD COMPONENTS
  // Green accent: #22c55e (Tailwind green-500)
  // Editorial/Magazine inspired by Substack
  // ============================================

  // Stats Ticker - Bloomberg-style dark bar with market data
  // Handle SSO callback for direct OAuth
  if (window.location.pathname === '/sso-callback') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700 mx-auto mb-3" />
          <p className="text-slate-500">Completing sign in...</p>
        </div>
        <AuthenticateWithRedirectCallback />
      </div>
    );
  }

  // Handle /login route - redirect to Clerk's hosted sign-in (production)
  if (window.location.pathname === '/login') {
    if (!isUserLoaded) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      );
    }
    if (isSignedIn) {
      window.location.href = '/';
      return null;
    }
    return <RedirectToSignIn afterSignInUrl="/" />;
  }

  // Handle /register route - redirect to Clerk's hosted sign-up (production)
  if (window.location.pathname === '/register') {
    if (!isUserLoaded) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      );
    }
    if (isSignedIn) {
      window.location.href = '/';
      return null;
    }
    return <RedirectToSignUp afterSignUpUrl="/" />;
  }

  // If we're on the help page, render the HelpCenter component
  if (window.location.pathname === '/help') {
    return <HelpCenter onBack={() => { window.location.href = '/'; }} />;
  }

  // About — a standalone page, same treatment as /help: it is read before
  // anyone signs in, so it must not sit behind the app chrome or auth.
  if (window.location.pathname === '/about') {
    return <AboutPage theme={theme} />;
  }

  // If we're loading a deal from URL, show loading state
  if (dealPageLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700 mx-auto mb-3" />
          <p className="text-slate-500">Loading deal details...</p>
        </div>
      </div>
    );
  }

  // If we're on a share page, render just the SharePage component
  if (sharePageData || shareLoading) {
    if (shareLoading) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-700 mx-auto mb-3" />
            <p className="text-slate-500">Loading shared content...</p>
          </div>
        </div>
      );
    }
    if (sharePageData?.error) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center max-w-md p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Unable to load content</h1>
            <p className="text-slate-500 mb-6">{sharePageData.error}</p>
            <a href="/" className="text-blue-800 hover:text-blue-700 font-medium">← Go to Global Infrastructure Sherpa</a>
          </div>
        </div>
      );
    }
    // Render ProjectSharePage for project shares, SharePage for documents
    if (sharePageData?.type === 'project') {
      return <ProjectSharePage 
        data={sharePageData} 
        isSignedIn={isSignedIn}
        user={user}
        onImportProject={(projectData) => {
          // Import counterparts first and collect their new IDs
          const counterpartIdMap = {};
          const newCounterparts = (projectData.counterparts || []).map((cp, i) => {
            const newId = `cp-${Date.now()}-${i}`;
            counterpartIdMap[cp.id] = newId;
            return {
              id: newId,
              name: cp.name,
              type: cp.type || 'Other',
              category: cp.category,
              company: cp.company,
              role: cp.role,
              email: cp.email,
              phone: cp.phone,
              linkedin: cp.linkedin,
              investmentPreferences: cp.investmentPreferences,
              source: `Imported from share by ${projectData.owner}`,
              created: new Date().toISOString(),
              updated: new Date().toISOString()
            };
          });

          // Create new project with linked counterpart IDs
          const newProject = {
            id: `project-${Date.now()}`,
            name: projectData.name + ' (Imported)',
            description: projectData.description || '',
            status: projectData.status || 'Active',
            stage: projectData.stage || 'Development',
            technology: projectData.technology || '',
            location: projectData.location || '',
            capacity: projectData.capacity || '',
            pipelineStatus: projectData.pipelineStatus || 'Screening',
            investmentSize: projectData.investmentSize,
            expectedReturn: projectData.expectedReturn,
            notes: projectData.notes,
            counterpartIds: newCounterparts.map(c => c.id),
            source: `Imported from share by ${projectData.owner}`,
            created: new Date().toISOString(),
            updated: new Date().toISOString()
          };
          
          // Import documents
          const newDocs = (projectData.documents || []).map((doc, i) => ({
            id: `doc-${Date.now()}-${i}`,
            name: doc.name,
            projectId: newProject.id,
            type: doc.type || 'Other',
            category: doc.category,
            base64: doc.base64,
            contentType: doc.contentType,
            size: doc.size,
            uploadedAt: new Date().toISOString()
          }));
          
          // Add counterparts to state
          if (newCounterparts.length > 0) {
            setCounterparts(prev => [...prev, ...newCounterparts]);
          }
          
          setProjects(prev => [...prev, newProject]);
          setDocuments(prev => [...prev, ...newDocs]);
          logActivity('project_imported', 'project', newProject.id, `Imported shared project: ${newProject.name}`);
          
          // Clear share page and navigate to the new project detail page
          setSharePageData(null);
          window.location.hash = '';
          setSelectedProject(newProject);
          setActiveTab('projects');
        }}
      />;
    }
    // Render DealSharePage for deal shares
    if (sharePageData?.type === 'deal') {
      return <DealSharePage data={sharePageData} />;
    }
    // Render CounterpartSharePage for counterpart shares
    if (sharePageData?.type === 'counterpart') {
      return <CounterpartSharePage
        data={sharePageData} 
        isSignedIn={isSignedIn}
        user={user}
        onImportCounterpart={(counterpartData) => {
          // Create new counterpart
          const newCounterpart = {
            id: `cp-${Date.now()}`,
            name: counterpartData.name,
            type: counterpartData.counterpartType || 'Other',
            category: counterpartData.category,
            company: counterpartData.company,
            role: counterpartData.role,
            summary: counterpartData.summary,
            location: counterpartData.location,
            websiteUrl: counterpartData.websiteUrl,
            email: counterpartData.contactEmail,
            phone: counterpartData.contactPhone,
            linkedinUrl: counterpartData.linkedin,
            investmentPreferences: counterpartData.investmentPreferences,
            source: `Imported from share by ${counterpartData.owner}`,
            created: new Date().toISOString(),
            updated: new Date().toISOString()
          };
          
          // Import documents
          const newDocs = (counterpartData.documents || []).map((doc, i) => ({
            id: `doc-${Date.now()}-${i}`,
            name: doc.name,
            counterpartIds: [newCounterpart.id],
            type: doc.type || 'Other',
            category: doc.category,
            base64: doc.base64,
            contentType: doc.contentType,
            size: doc.size,
            uploadedAt: new Date().toISOString()
          }));
          
          setCounterparts(prev => [...prev, newCounterpart]);
          setDocuments(prev => [...prev, ...newDocs]);
          logActivity('counterpart_imported', 'counterpart', newCounterpart.id, `Imported shared counterpart: ${newCounterpart.name}`);
          
          // Clear share page and navigate to the new counterpart
          setSharePageData(null);
          window.location.hash = '';
          setSelectedCounterpart(newCounterpart);
          setActiveTab('counterparts');
        }}
      />;
    }
    return <SharePage data={sharePageData} />;
  }

  // Live Feed redesign mockup — design review only, no auth required
  if (window.location.pathname === '/preview/livefeed') {
    return (
      <Suspense fallback={<div style={{minHeight:'100vh',backgroundColor:'#F5F1E8'}} />}>
        <LiveFeedMockup />
      </Suspense>
    );
  }

  // Luma-styled Live Feed demo — pure white, monochrome, large typography.
  // Design review only, no auth, hardcoded sample data.
  if (window.location.pathname === '/preview/luma') {
    return (
      <Suspense fallback={<div style={{minHeight:'100vh',backgroundColor:'#FFFFFF'}} />}>
        <LiveFeedLumaDemo />
      </Suspense>
    );
  }

  // Check if on privacy page (allow public access)
  const isPrivacyPage = window.location.pathname === '/privacy';

  // Standalone Privacy Page for non-signed-in users
  if (isUserLoaded && !isSignedIn && isPrivacyPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col">
        {/* Header */}
        <header className="px-6 py-4 flex justify-between items-center max-w-6xl mx-auto w-full border-b border-slate-200">
          <a href="/" className="flex items-center gap-3">
            <img src="/logo192.png" alt="Global Infrastructure Sherpa" className="w-10 h-10 rounded-xl" />
            <div>
              <h1 className="text-lg font-black text-slate-800">
                Global Infrastructure Sherpa
              </h1>
              <p className="text-xs text-slate-400">by Ganiz</p>
            </div>
          </a>
          <a href="/login" className="px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-sm font-semibold transition-colors">
            Sign in
          </a>
        </header>

        {/* Privacy Policy Content */}
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
              <p className="text-slate-600">Last updated: December 16, 2025</p>
            </div>

            <div className="prose prose-slate max-w-none">
              <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Introduction</h2>
                  <p className="text-slate-600">
                    Global Infrastructure Sherpa ("we," "our," or "us") is operated by Ganiz Holding Company, LLC. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our deal flow management application at gis.ganiz.net (the "Service").
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Information We Collect</h2>
                  <p className="text-slate-600 mb-3">We collect information that you provide directly to us:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li><strong>Account Information:</strong> When you sign in with Google, we receive your name, email address, and profile picture.</li>
                    <li><strong>Project Data:</strong> Information about projects, counterparts, tasks, and documents you create within the Service.</li>
                    <li><strong>Google Drive Data:</strong> If you connect Google Drive, we access only the files and folders you explicitly authorize for backup and sync purposes.</li>
                    <li><strong>Questions about our publications:</strong> If you use the "Ask about this report" box on a research or data page, we keep your question and the answer given, together with a one-way coded form of your IP address (never the address itself), to answer you, prevent abuse and improve our publications. Your question is processed by our AI provider, OpenAI, to produce the answer.</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">3. How We Use Your Information</h2>
                  <p className="text-slate-600 mb-3">We use the information we collect to:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li>Provide, maintain, and improve the Service</li>
                    <li>Store and sync your data to your Google Drive (at your request)</li>
                    <li>Send you technical notices and support messages</li>
                    <li>Respond to your comments and questions</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Data Storage and Security</h2>
                  <p className="text-slate-600">
                    Your data is primarily stored in your own Google Drive account. We do not maintain copies of your business data on our servers. Authentication tokens are stored securely and encrypted. We implement industry-standard security measures to protect your information.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Google API Services</h2>
                  <p className="text-slate-600">
                    Global Infrastructure Sherpa's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-blue-800 hover:underline">Google API Services User Data Policy</a>, including the Limited Use requirements. We only request the minimum permissions necessary to provide our Service.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Data Sharing</h2>
                  <p className="text-slate-600">
                    We do not sell, trade, or rent your personal information to third parties. We may share information only in the following circumstances: with your consent, to comply with legal obligations, or to protect our rights and safety.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Your Rights</h2>
                  <p className="text-slate-600 mb-3">You have the right to:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li>Access and export your data at any time</li>
                    <li>Delete your account and associated data</li>
                    <li>Revoke Google Drive access through your Google Account settings</li>
                    <li>Request information about data we hold about you</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Cookies and Tracking</h2>
                  <p className="text-slate-600">
                    We use essential cookies to maintain your session and preferences. We do not use third-party tracking or advertising cookies.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Children's Privacy</h2>
                  <p className="text-slate-600">
                    The Service is not intended for children under 13. We do not knowingly collect information from children under 13.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Changes to This Policy</h2>
                  <p className="text-slate-600">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Contact Us</h2>
                  <p className="text-slate-600">
                    If you have questions about this Privacy Policy, please contact us at:
                  </p>
                  <p className="text-slate-600 mt-2">
                    <strong>Ganiz Holding Company, LLC</strong><br />
                    Email: {CONTACT_DISPLAY}
                  </p>
                </div>
              </section>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-slate-200 py-8">
          <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">© 2026 Global Infrastructure Sherpa by Ganiz. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="/" className="text-slate-500 hover:text-blue-800 text-sm transition-colors">
                Home
              </a>
              <a href="/about" className="text-slate-500 hover:text-blue-800 text-sm transition-colors">
                About
              </a>
              <a href="#contact" onClick={contactClick()} className="text-slate-500 hover:text-blue-800 text-sm transition-colors">
                Contact
              </a>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Landing page removed - app is now fully public
  // Users can optionally sign in to save searches

  const isDark = theme === 'dark';

  // --- Global AI Search Bar (appears on every page) ---
  // All search bar state lifted to App level to survive remounts (GlobalSearchBar is an inline component)

  const handleSearchBarSearch = async (e) => {
    e?.preventDefault();
    if (!heroSearch.trim()) return;
    setShowSuggestions(false);

    // Dashboard power filter: on insights tab, find matching counterpart and filter Live Feed instead of AI search
    if (activeTab === 'insights') {
      const searchLower = heroSearch.trim().toLowerCase();
      const match = counterparts.find(cp => cp.name?.toLowerCase() === searchLower)
        || counterparts.find(cp => cp.name?.toLowerCase().startsWith(searchLower) && searchLower.length >= 3);
      if (match) {
        setPendingDashboardFilter({ type: 'counterpart', value: match.name, label: match.name, id: match.id });
        setHeroSearch('');
        return;
      }
      // No counterpart match — navigate to Search tab for full AI search
      setActiveTab('dashboard');
      await performSmartSearch(heroSearch);
      return;
    }

    await performSmartSearch(heroSearch);
  };

  const handleSearchBarSelect = (item) => {
    setHeroSearch('');
    setShowSuggestions(false);
    if (item.kind === 'deal') {
      viewDeal(deals.find(d => d.id === item.id));
    } else if (activeTab === 'insights') {
      // Dashboard power filter: add counterpart as feed filter instead of navigating
      setPendingDashboardFilter({ type: 'counterpart', value: item.name, label: item.name, id: item.id });
    } else {
      viewCounterpart(counterparts.find(c => c.id === item.id));
    }
  };

  const globalSearchBarJSX = (
    <div className="mb-6">
      <form onSubmit={handleSearchBarSearch} className="relative max-w-3xl mx-auto">
        <div className={`relative rounded-xl border shadow transition-all
          ${isDark
            ? 'bg-slate-800 border-slate-600 focus-within:border-blue-500 focus-within:shadow-blue-500/10'
            : 'bg-white border-slate-200 focus-within:border-blue-400 focus-within:shadow-blue-500/10'}
          focus-within:shadow-lg`}
        >
          <div className="flex items-center px-4 py-2.5 gap-3">
            <img
              src="/logo192.png"
              alt=""
              className={`w-6 h-6 rounded flex-shrink-0 transition-opacity ${smartSearchLoading ? 'animate-pulse opacity-80' : 'opacity-40'}`}
            />
            <input
              type="text"
              value={heroSearch}
              onChange={(e) => { setHeroSearch(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Search..."
              className={`flex-1 text-[16px] bg-transparent border-0 outline-none focus:outline-none focus:ring-0
                ${isDark
                  ? 'text-white placeholder-slate-500'
                  : 'text-slate-900 placeholder-slate-400'}`}
            />
            <button
              type="submit"
              disabled={smartSearchLoading || !heroSearch.trim()}
              className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all
                ${heroSearch.trim()
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                  : isDark
                    ? 'bg-slate-700 text-slate-500'
                    : 'bg-slate-100 text-slate-400'}
                disabled:opacity-50`}
            >
              {smartSearchLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <ArrowRight size={18} />
              )}
            </button>
          </div>
        </div>

        {/* Unified dropdown: recent searches (prefix-filtered when typing) + autocomplete suggestions */}
        {showSuggestions && (() => {
          const searchLower = heroSearch.toLowerCase();
          const matchedRecent = heroSearch
            ? recentSearches.filter(q => q.toLowerCase().includes(searchLower)).slice(0, 3)
            : recentSearches.slice(0, 5);
          const hasRecent = matchedRecent.length > 0;
          const hasSuggestions = heroSearch && heroSuggestions.length > 0;
          if (!hasRecent && !hasSuggestions) return null;

          return (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowSuggestions(false)} />
              <div className={`absolute z-50 w-full mt-2 rounded-2xl shadow-2xl border overflow-hidden text-left
                ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
              >
                {hasRecent && (
                  <>
                    <div className={`px-5 py-2 text-[12px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      Recent
                    </div>
                    {matchedRecent.map((q, i) => (
                      <button
                        key={`recent-${i}`}
                        onClick={() => {
                          setHeroSearch(q);
                          setShowSuggestions(false);
                          if (activeTab === 'insights') {
                            const searchLower = q.trim().toLowerCase();
                            const match = counterparts.find(cp => cp.name?.toLowerCase() === searchLower)
                              || counterparts.find(cp => cp.name?.toLowerCase().startsWith(searchLower) && searchLower.length >= 3);
                            if (match) {
                              setPendingDashboardFilter({ type: 'counterpart', value: match.name, label: match.name, id: match.id });
                              setHeroSearch('');
                              return;
                            }
                          }
                          performSmartSearch(q);
                        }}
                        className={`w-full px-5 py-3 flex items-center gap-3 transition-colors
                          ${isDark ? 'hover:bg-slate-700/70' : 'hover:bg-blue-50'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <History size={14} className={isDark ? 'text-slate-400' : 'text-slate-400'} />
                        </div>
                        <span className={`text-[15px] ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{q}</span>
                      </button>
                    ))}
                  </>
                )}
                {hasSuggestions && (
                  <>
                    {hasRecent && <div className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-100'}`} />}
                    <div className={`px-5 py-2 text-[12px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      Matches
                    </div>
                    {heroSuggestions.map((item) => (
                      <button
                        key={`${item.kind}-${item.id}`}
                        onClick={() => handleSearchBarSelect(item)}
                        className={`w-full px-5 py-3 flex items-center gap-3 transition-colors
                          ${isDark ? 'hover:bg-slate-700/70' : 'hover:bg-blue-50'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                          ${item.kind === 'deal'
                            ? (isDark ? 'bg-blue-900/40' : 'bg-blue-50')
                            : (isDark ? 'bg-slate-700' : 'bg-slate-100')}`}
                        >
                          {item.kind === 'deal'
                            ? <Briefcase size={14} className={isDark ? 'text-blue-400' : 'text-blue-500'} />
                            : <Building2 size={14} className={isDark ? 'text-slate-400' : 'text-slate-500'} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-[15px] truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.name}</p>
                          <p className={`text-[13px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {item.kind === 'deal'
                              ? [item.type, item.country].filter(Boolean).join(' · ') /* country already joined in heroSuggestions */
                              : <>{item.category || item.type} {item.dealCount > 0 && `· ${item.dealCount} deal${item.dealCount !== 1 ? 's' : ''}`}</>}
                          </p>
                        </div>
                        <ChevronRight size={14} className={isDark ? 'text-slate-600' : 'text-slate-300'} />
                      </button>
                    ))}
                  </>
                )}
                {heroSearch.trim() && (
                  <>
                    <div className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-100'}`} />
                    <button
                      onClick={() => {
                        setShowSuggestions(false);
                        if (activeTab === 'insights') {
                          // On dashboard: navigate to Search tab for full AI search
                          setActiveTab('dashboard');
                          performSmartSearch(heroSearch);
                        } else {
                          handleSearchBarSearch();
                        }
                      }}
                      className={`w-full px-5 py-3 text-sm font-medium
                        ${isDark ? 'text-blue-400 hover:bg-slate-700/70' : 'text-blue-600 hover:bg-blue-50'}`}
                    >
                      {activeTab === 'insights' ? `Search all for "${heroSearch}" on Search tab →` : `Search all results for "${heroSearch}" →`}
                    </button>
                  </>
                )}
              </div>
            </>
          );
        })()}
      </form>
    </div>
  );

  // GlobalSearchBar removed — use {globalSearchBarJSX} directly to avoid inline component remount issues

  // --- SearchResultsBanner — shown on Counterparts tab when smart search active ---
  const SearchResultsBanner = () => {
    if (smartSearchCpIds === null) return null;

    const clearSearch = () => {
      setSmartSearchCpIds(null);
      setSmartSearchQuery('');
      setSmartSearchDeals([]);
      setCounterpartFilters(prev => ({
        ...prev,
        counterpartTypes: [],
        technologies: [],
        regions: [],
        stages: []
      }));
    };

    return (
      <div className={`rounded-xl border p-3 ${isDark ? 'bg-blue-900/20 border-blue-800/40' : 'bg-blue-50 border-blue-200'}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-blue-500" />
            <span className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
              Search results for "{smartSearchQuery}"
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${isDark ? 'bg-blue-800/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
              {filteredCounterparts.length} counterpart{filteredCounterparts.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={clearSearch}
            className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${isDark ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`}
          >
            <X size={12} /> Clear
          </button>
        </div>
        {/* Matched deals chips */}
        {smartSearchDeals.length > 0 && (
          <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1">
            <span className={`text-xs flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Related deals:</span>
            {smartSearchDeals.slice(0, 6).map(deal => (
              <button
                key={deal.id}
                onClick={() => {
                  const fullDeal = deals.find(d => d.id === deal.id);
                  if (fullDeal) setPanelDeal(fullDeal);
                }}
                className={`flex-shrink-0 text-xs px-2.5 py-1 rounded-lg border transition-colors ${isDark
                  ? 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                {deal.headline || deal.project_name || 'Deal'}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // --- CompareCounterpartsModal — side-by-side counterpart comparison ---
  const CompareCounterpartsModal = () => {
    if (!showCompareModal || compareSelection.length < 2) return null;

    const selectedCps = compareSelection.map(id => counterparts.find(c => c.id === id)).filter(Boolean);
    if (selectedCps.length < 2) return null;

    const getDealsForCp = (cpId) => deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart_id === cpId || dc.counterpart?.id === cpId));

    const getField = (cp, field) => {
      const prefs = cp.investmentPreferences || {};
      switch (field) {
        case 'type':
          return cp.classifications?.types?.map(t => t.label).join(', ') || cp.categories?.join(', ') || cp.category || cp.type || '';
        case 'headquarters': {
          const parts = [cp.headquarters?.city, cp.headquarters?.country || cp.country].filter(Boolean);
          return parts.join(', ') || cp.location || '';
        }
        case 'website':
          return cp.websiteUrl || cp.website || prefs.website || '';
        case 'summary':
          return prefs.summary || prefs.description || cp.notes || '';
        case 'technologies':
          return cp.classifications?.technologies?.map(t => t.label) || getDimValues(prefs, 'technologies', 'sectors', 'assetClasses');
        case 'markets':
          return cp.classifications?.geographies?.map(g => g.label) || getDimValues(prefs, 'geographies');
        case 'dealCount':
          return getDealsForCp(cp.id).length;
        case 'linkedDeals':
          return getDealsForCp(cp.id).slice(0, 3);
        case 'dealSize': {
          const range = getDimRange(prefs, 'dealSize', 'investmentSize', 'capacity');
          if (range.min || range.max) return `${range.min || '?'} – ${range.max || '?'} ${range.unit || 'MW'}`;
          return prefs.minCapacity || prefs.maxCapacity ? `${prefs.minCapacity || '?'} – ${prefs.maxCapacity || '?'} MW` : '';
        }
        case 'stages':
          return cp.classifications?.stages?.map(s => s.label) || getDimValues(prefs, 'dealStage', 'stage');
        default:
          return '';
      }
    };

    const rows = [
      { key: 'type', label: 'Type' },
      { key: 'headquarters', label: 'Headquarters' },
      { key: 'website', label: 'Website', isLink: true },
      { key: 'summary', label: 'Summary' },
      { key: 'technologies', label: 'Technologies', isTags: true },
      { key: 'markets', label: 'Active Markets', isTags: true },
      { key: 'dealCount', label: 'Deal Count' },
      { key: 'linkedDeals', label: 'Recent Deals', isDeals: true },
      { key: 'dealSize', label: 'Deal Size' },
      { key: 'stages', label: 'Investment Stage', isTags: true },
    ];

    const tagColors = [
      isDark ? 'bg-blue-900/40 text-blue-300 border-blue-700/50' : 'bg-blue-50 text-blue-700 border-blue-200',
      isDark ? 'bg-green-900/40 text-green-300 border-green-700/50' : 'bg-green-50 text-green-700 border-green-200',
      isDark ? 'bg-amber-900/40 text-amber-300 border-amber-700/50' : 'bg-amber-50 text-amber-700 border-amber-200',
      isDark ? 'bg-purple-900/40 text-purple-300 border-purple-700/50' : 'bg-purple-50 text-purple-700 border-purple-200',
    ];

    const renderCell = (cp, row) => {
      const val = getField(cp, row.key);

      if (row.isLink && val) {
        const url = val.startsWith('http') ? val : `https://${val}`;
        return <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-sm truncate block">{val.replace(/^https?:\/\/(www\.)?/, '')}</a>;
      }

      if (row.isTags) {
        const tags = Array.isArray(val) ? val : [];
        if (!tags.length) return <span className={isDark ? 'text-slate-600' : 'text-slate-300'}>—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {tags.map((t, i) => (
              <span key={i} className={`px-2 py-0.5 rounded-full text-xs font-medium border ${tagColors[i % tagColors.length]}`}>{t}</span>
            ))}
          </div>
        );
      }

      if (row.isDeals) {
        const dealList = Array.isArray(val) ? val : [];
        if (!dealList.length) return <span className={isDark ? 'text-slate-600' : 'text-slate-300'}>—</span>;
        return (
          <div className="space-y-1">
            {dealList.map(d => (
              <p key={d.id} className={`text-xs truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {d.headline || 'Untitled Deal'}
                {d.capacity ? ` (${d.capacity} ${d.capacity_unit || 'MW'})` : ''}
              </p>
            ))}
          </div>
        );
      }

      if (val === 0) return <span className="text-sm">0</span>;
      if (!val) return <span className={isDark ? 'text-slate-600' : 'text-slate-300'}>—</span>;
      return <span className="text-sm">{val}</span>;
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowCompareModal(false)}>
        <div className={`${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} border rounded-2xl w-full ${selectedCps.length >= 3 ? 'max-w-6xl' : 'max-w-4xl'} my-8 flex flex-col max-h-[90vh]`} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className={`px-6 py-4 flex items-center justify-between border-b ${isDark ? 'border-slate-700 bg-slate-800' : 'border-slate-100 bg-slate-50'} rounded-t-2xl`}>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Compare Counterparts</h2>
            <button onClick={() => setShowCompareModal(false)} className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}`}>
              <X size={20} />
            </button>
          </div>

          {/* Body — scrollable table */}
          <div className="overflow-auto flex-1">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className="w-32" />
                {selectedCps.map(cp => <col key={cp.id} />)}
              </colgroup>
              {/* Column headers with counterpart names + favicons */}
              <thead className={`sticky top-0 z-10 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'} sticky left-0 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>Field</th>
                  {selectedCps.map(cp => {
                    const w = cp.websiteUrl || cp.website || cp.investmentPreferences?.website || '';
                    const domain = w ? w.replace(/^https?:\/\//, '').split('/')[0] : '';
                    const favicon = domain ? `https://cdn.brandfetch.io/${domain}/icon?c=1idrTt0p2-SJacppl92` : null;
                    return (
                      <th key={cp.id} className={`text-left px-5 py-3 font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                            {favicon ? <img src={favicon} alt="" className="w-4 h-4" onError={e => { e.target.style.display = 'none'; }} /> : null}
                            <Building2 size={14} className={`text-slate-400 ${favicon ? 'hidden' : ''}`} style={favicon ? { display: 'none' } : {}} />
                          </div>
                          <span className="truncate">{cp.name}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className={`border-b ${isDark ? 'border-slate-800' : 'border-slate-100'} ${idx % 2 === 0 ? (isDark ? 'bg-slate-800/30' : 'bg-slate-50/50') : ''}`}>
                    <td className={`px-4 py-3 font-medium align-top whitespace-nowrap sticky left-0 ${isDark ? 'text-slate-400' : 'text-slate-500'} ${idx % 2 === 0 ? (isDark ? 'bg-slate-800/30' : 'bg-slate-50/50') : (isDark ? 'bg-slate-900' : 'bg-white')}`}>
                      {row.label}
                    </td>
                    {selectedCps.map(cp => (
                      <td key={cp.id} className={`px-4 py-3 align-top break-words ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {renderCell(cp, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // --- PaywallOverlay — LinkedIn-style: shows AFTER user experienced value ---
  const effectiveTier = isAdmin ? 'admin' : (isSignedIn ? userTier : 'anonymous');
  const PaywallOverlay = ({ type, used, limit, onClose }) => {
    const upgrade = getUpgradeText(effectiveTier);
    if (!upgrade) return null; // Pro/admin — should never see this
    return (
      <div className="absolute inset-0 backdrop-blur-md bg-white/60 dark:bg-slate-900/60 z-10 flex items-center justify-center">
        <div className="text-center p-8 max-w-sm">
          <Lock size={32} className="mx-auto mb-3 text-blue-500" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            You've viewed {used} of {limit} free {type}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">
            {upgrade.benefit}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">
            {!isSignedIn ? 'No credit card required.' : 'Start with a 7-day free trial. Cancel anytime.'}
          </p>
          {!isSignedIn ? (
            <SignUpButton mode="modal">
              <button className="inline-block px-6 py-2.5 bg-blue-700 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors shadow-md cursor-pointer">
                {upgrade.action}
              </button>
            </SignUpButton>
          ) : (
            <a
              href={`${SUBSCRIPTION_TIERS[userTier === 'free' ? 'basic' : 'pro'].paymentLink}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.primaryEmailAddress?.emailAddress || '')}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-block px-6 py-2.5 bg-blue-700 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors shadow-md"
            >
              {upgrade.action}
            </a>
          )}
          <button
            onClick={() => { setPaywallInfo(null); setActiveTab('subscribe'); }}
            className="block mx-auto mt-3 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            See all plans
          </button>
          {onClose && (
            <button onClick={onClose} className="block mx-auto mt-2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
              Go back
            </button>
          )}
        </div>
      </div>
    );
  };

  // --- RegistrationModal — LinkedIn-style dismissible modal over content ---
  // Appears after 3rd anonymous click. User can dismiss and keep browsing. Re-shows every 3 clicks.
  const RegistrationModal = () => showRegModal && !isSignedIn ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => { setShowRegModal(false); setRegModalDismissed(guestViewCount); }}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className={`relative max-w-md w-full mx-4 rounded-2xl shadow-2xl p-8 ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        <button
          onClick={() => { setShowRegModal(false); setRegModalDismissed(guestViewCount); }}
          className={`absolute top-4 right-4 p-1 rounded-lg transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mx-auto mb-4">
            <Sparkles size={24} className="text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
            Create your free account
          </h3>
          <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
            Track deals, follow companies, and get daily alerts — free forever.
          </p>
          <SignUpButton mode="modal">
            <button className="w-full px-6 py-3 bg-blue-700 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors shadow-lg cursor-pointer mb-3">
              Register Free
            </button>
          </SignUpButton>
          <SignInButton mode="modal">
            <button className={`w-full px-6 py-2.5 rounded-xl text-sm font-medium border transition-colors cursor-pointer ${theme === 'dark' ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              Already have an account? Sign in
            </button>
          </SignInButton>
          <button
            onClick={() => { setShowRegModal(false); setRegModalDismissed(guestViewCount); }}
            className={`mt-4 text-xs ${theme === 'dark' ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'} transition-colors`}
          >
            Continue browsing
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // --- SmartSearchResults — inline AI search results panel ---
  const SmartSearchResults = ({ bubbleOnly = false } = {}) => {
    const [dealsToShow, setDealsToShow] = useState(20);
    const [summaryExpanded, setSummaryExpanded] = useState(true);
    const [activeFilters, setActiveFilters] = useState([]); // Array of { label, color, value } for active filters
    const res = smartSearchResponse;
    const hasResults = res && !res.limitReached && (res.counterparts?.length > 0 || res.deals?.length > 0);

    // Auto-detect temporal queries and default to date sort
    const isTemporalQuery = res?.query && /\b(latest|recent|newest|this week|this month|this year|last week|last month|last year|past week|past month|2026|2025)\b/i.test(res.query);
    const [dealSortMode, setDealSortMode] = useState('relevance');

    // Reset sort mode and summary expansion when query changes
    // In bubbleOnly mode (embedded in feed), default to collapsed; in full mode, expanded
    useEffect(() => {
      setDealSortMode(isTemporalQuery ? 'date' : 'relevance');
      setSummaryExpanded(!bubbleOnly);
    }, [res?.query]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sort deals based on mode — relevance keeps AI-ranked order, date sorts newest first
    const sortedDeals = useMemo(() => {
      if (!res?.deals) return [];
      if (dealSortMode === 'date') {
        return [...res.deals].sort((a, b) => {
          const dateA = new Date(a.announcement_date || a.created_at || 0);
          const dateB = new Date(b.announcement_date || b.created_at || 0);
          return dateB - dateA;
        });
      }
      return res.deals;
    }, [res?.deals, dealSortMode]);

    // Filter deals based on active chips (client-side filtering)
    const filteredDeals = useMemo(() => {
      if (activeFilters.length === 0) return sortedDeals;

      return sortedDeals.filter(deal => {
        const fullDeal = deals.find(d => d.id === deal.id) || deal;

        // Check each active filter — deal must match ALL filters (AND logic)
        return activeFilters.every(filter => {
          if (filter.color === 'amber') {
            // Geography filter — match country, region, or location
            const dealCountries = (Array.isArray(fullDeal.country) ? fullDeal.country : (fullDeal.country ? [fullDeal.country] : [])).map(c => c.toLowerCase().trim());
            const dealRegion = (fullDeal.region || '').toLowerCase().trim();
            const dealLocation = (fullDeal.location || '').toLowerCase().trim();
            const filterValue = (filter.value || filter.label).toLowerCase().trim();
            return dealCountries.some(dc => dc && (dc.includes(filterValue) || filterValue.includes(dc))) ||
                   (dealRegion && (dealRegion.includes(filterValue) || filterValue.includes(dealRegion))) ||
                   (dealLocation && (dealLocation.includes(filterValue) || filterValue.includes(dealLocation)));
          }
          if (filter.color === 'green') {
            // Technology filter — match any tech in array
            const dealTechs = Array.isArray(fullDeal.technology)
              ? fullDeal.technology
              : (fullDeal.technology ? [fullDeal.technology] : []);
            const filterValue = (filter.value || filter.label).toLowerCase();
            return dealTechs.some(t => (t || '').toLowerCase().includes(filterValue) || filterValue.includes((t || '').toLowerCase()));
          }
          if (filter.color === 'blue') {
            // Type filter — match counterpart types in deal
            const dealCps = deal.counterparts || [];
            const filterValue = (filter.value || filter.label).toLowerCase();
            return dealCps.some(cp => {
              const cpData = counterparts.find(c => c.id === cp.id);
              const cpTypes = cpData?.categories || [];
              return cpTypes.some(t => (t || '').toLowerCase().includes(filterValue) || filterValue.includes((t || '').toLowerCase()));
            });
          }
          if (filter.color === 'purple') {
            // Stage filter — match deal stage if available
            const dealStage = (fullDeal.stage || fullDeal.deal_stage || '').toLowerCase();
            const filterValue = (filter.value || filter.label).toLowerCase();
            return dealStage.includes(filterValue) || filterValue.includes(dealStage);
          }
          if (filter.color === 'indigo') {
            // Counterpart "Only" filter — deal must involve this counterpart
            const dealCps = deal.counterparts || [];
            return dealCps.some(cp => cp.id === filter.value);
          }
          return true;
        });
      });
    }, [sortedDeals, activeFilters, deals, counterparts]);

    // Toggle a filter chip on/off
    const toggleFilter = (chip) => {
      setActiveFilters(prev => {
        const exists = prev.some(f => f.label === chip.label && f.color === chip.color);
        if (exists) {
          return prev.filter(f => !(f.label === chip.label && f.color === chip.color));
        } else {
          return [...prev, chip];
        }
      });
      setDealsToShow(20); // Reset pagination when filter changes
    };

    // Check if a chip is active
    const isChipActive = (chip) => {
      return activeFilters.some(f => f.label === chip.label && f.color === chip.color);
    };

    const allMatchedChips = [
      ...(res?.matched?.types || []).map(t => ({ ...t, color: 'blue' })),
      ...(res?.matched?.technologies || []).map(t => ({ ...t, color: 'green' })),
      ...(res?.matched?.geographies || []).map(g => ({ ...g, color: 'amber' })),
      ...(res?.matched?.stages || []).map(s => ({ ...s, color: 'purple' }))
    ];

    const handleViewAllCounterparts = () => {
      // Pass the exact counterpart IDs from search results so the directory
      // shows the same set (not a different taxonomy-filtered set)
      const cpIds = (res?.counterparts || []).map(c => c.id);
      keepSearchOnTabSwitch.current = true;
      setSmartSearchCpIds(cpIds.length > 0 ? cpIds : null);
      setSmartSearchQuery(res?.query || '');
      setSmartSearchResponse(null);
      setActiveTab('counterparts');
    };

    const handleViewAllDeals = () => {
      // Use allDealIds (ALL matching deals) instead of just the displayed top 25
      const dealIds = res?.allDealIds || (res?.deals || []).map(d => d.id);
      keepSearchOnTabSwitch.current = true;
      setSmartSearchDealIds(dealIds.length > 0 ? dealIds : null);
      setSmartSearchDealQuery(res?.query || '');
      setSmartSearchResponse(null);
      setActiveTab('deals');
    };

    const handleDismiss = () => {
      setSmartSearchResponse(null);
      setCompareSelection([]);
    };

    // Loading skeleton — matches AI bubble layout
    if (smartSearchLoading) {
      // Compact bubble-only skeleton for embedded feed search
      if (bubbleOnly) {
        return (
          <div className={`border-l-4 border-blue-500 rounded-xl p-5 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50/50'}`}>
            <div className="flex items-center gap-2 mb-3">
              <img src="/logo192.png" alt="Sherpa" className="w-4 h-4 rounded animate-pulse" />
              <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Sherpa Analysis</span>
            </div>
            <div className="space-y-2.5 animate-pulse">
              <div className={`h-3.5 ${isDark ? 'bg-blue-800/40' : 'bg-blue-100'} rounded w-full`} />
              <div className={`h-3.5 ${isDark ? 'bg-blue-800/40' : 'bg-blue-100'} rounded w-11/12`} />
              <div className={`h-3.5 ${isDark ? 'bg-blue-800/40' : 'bg-blue-100'} rounded w-4/5`} />
            </div>
          </div>
        );
      }
      return (
        <div className={`rounded-2xl border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} shadow-lg overflow-hidden mb-6`}>
          <div className={`px-6 py-4 flex items-center gap-2 border-b ${isDark ? 'border-slate-700 bg-slate-800' : 'border-slate-100 bg-slate-50/50'}`}>
            <Search size={18} className="text-blue-500" />
            <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Searching...</span>
          </div>
          <div className="p-6">
            {/* AI Bubble skeleton */}
            <div className={`border-l-4 border-blue-500 rounded-xl p-5 mb-6 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50/50'}`}>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo192.png" alt="Sherpa" className="w-4 h-4 rounded animate-pulse" />
                <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Sherpa Analysis</span>
              </div>
              <div className="space-y-2.5 animate-pulse">
                <div className={`h-3.5 ${isDark ? 'bg-blue-800/40' : 'bg-blue-100'} rounded w-full`} />
                <div className={`h-3.5 ${isDark ? 'bg-blue-800/40' : 'bg-blue-100'} rounded w-11/12`} />
                <div className={`h-3.5 ${isDark ? 'bg-blue-800/40' : 'bg-blue-100'} rounded w-4/5`} />
                <div className={`h-3.5 ${isDark ? 'bg-blue-800/40' : 'bg-blue-100'} rounded w-3/4`} />
              </div>
              <div className="flex gap-2 mt-4">
                {[1,2,3].map(i => (
                  <div key={i} className={`h-7 w-28 ${isDark ? 'bg-blue-800/30' : 'bg-blue-100/80'} rounded-full animate-pulse`} />
                ))}
              </div>
            </div>
            {/* Deal skeleton lines */}
            <div className="space-y-3 animate-pulse">
              <div className={`h-4 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded w-1/3`} />
              {[1,2,3].map(i => (
                <div key={i} className="space-y-1.5 py-3">
                  <div className={`h-5 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded w-3/4`} />
                  <div className={`h-3 ${isDark ? 'bg-slate-700/60' : 'bg-slate-100'} rounded w-1/2`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (!res) return null;

    // Search is unlimited for all users — no gate UI needed.

    const chipColors = {
      blue: isDark ? 'bg-blue-900/40 text-blue-300 border-blue-700/50' : 'bg-blue-50 text-blue-700 border-blue-200',
      green: isDark ? 'bg-green-900/40 text-green-300 border-green-700/50' : 'bg-green-50 text-green-700 border-green-200',
      amber: isDark ? 'bg-amber-900/40 text-amber-300 border-amber-700/50' : 'bg-amber-50 text-amber-700 border-amber-200',
      purple: isDark ? 'bg-purple-900/40 text-purple-300 border-purple-700/50' : 'bg-purple-50 text-purple-700 border-purple-200'
    };

    // Bubble-only mode for embedded feed search — just the Sherpa Analysis bubble, no deals list
    if (bubbleOnly) {
      if (!hasResults) return null;
      return (
        <div>
          {/* Sherpa Analysis Bubble — rendered inline in the feed */}
          {hasResults && res.summary && (() => {
            const cleanSummary = res.summary
              .replace(/\s*\((?:(?:CP|DL)\d+(?:,\s*)?)+\)/gi, '')
              .replace(/\b(?:CP|DL)\d+\b/gi, '')
              .replace(/\s{2,}/g, ' ').trim();

            const cpEntries = [];
            const cpIdSet = new Set();
            const buildVariants = (name) => {
              const variants = [name];
              const parenMatch = name.match(/\(([A-Z][A-Za-z0-9&]{1,10})\)\s*$/);
              if (parenMatch) {
                variants.push(parenMatch[1]);
                variants.push(name.replace(/\s*\([^)]+\)\s*$/, '').trim());
              }
              return variants;
            };
            const addCp = (id, name) => {
              if (!name || cpIdSet.has(id)) return;
              cpEntries.push({ id, name, variants: buildVariants(name) });
              cpIdSet.add(id);
            };
            (res.counterparts || []).forEach(cp => addCp(cp.id, cp.name));
            const summaryLower = cleanSummary.toLowerCase();
            counterparts.forEach(cp => {
              if (cp.name && cp.name.length >= 4 && !cpIdSet.has(cp.id)) {
                const variants = buildVariants(cp.name);
                if (variants.some(v => summaryLower.includes(v.toLowerCase()))) addCp(cp.id, cp.name);
              }
            });
            const uBound = '(?:^|(?<=[^\\p{L}\\p{N}]))';
            const uBoundEnd = '(?=$|[^\\p{L}\\p{N}])';
            const renderSummary = (text) => {
              if (cpEntries.length === 0) return <>{text}</>;
              const allVariants = [];
              cpEntries.forEach(entry => entry.variants.forEach(v => allVariants.push({ variant: v, entry })));
              allVariants.sort((a, b) => b.variant.length - a.variant.length);
              const escaped = allVariants.map(v => v.variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
              const regex = new RegExp(`(${uBound}(?:${escaped.join('|')})${uBoundEnd})`, 'giu');
              const parts = text.split(regex);
              return parts.map((part, i) => {
                const partLower = part.toLowerCase();
                const matched = allVariants.find(v => v.variant.toLowerCase() === partLower);
                if (matched) {
                  const cpData = counterparts.find(c => c.id === matched.entry.id);
                  return (
                    <button key={i} onClick={() => { if (cpData) { setSearchPanelCp(cpData); setSearchPanelDeal(null); } }}
                      className={`font-semibold underline decoration-blue-400/50 decoration-1 underline-offset-2 hover:decoration-blue-500 transition-colors ${isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-700 hover:text-blue-800'}`}
                    >{part}</button>
                  );
                }
                return <span key={i}>{part}</span>;
              });
            };
            const rawParagraphs = cleanSummary.split(/\n\n+/).filter(p => p.trim());
            const splitLongParagraph = (text, maxSentences = 3) => {
              if (text.length < 400) return [text];
              const sentences = text.match(/[^.!?]+[.!?]+[\s]*/g) || [text];
              const chunks = [];
              for (let i = 0; i < sentences.length; i += maxSentences) chunks.push(sentences.slice(i, i + maxSentences).join('').trim());
              return chunks.filter(Boolean);
            };
            const paragraphs = rawParagraphs.flatMap(p => splitLongParagraph(p));
            const firstParagraph = paragraphs[0] || cleanSummary;
            const hasMore = paragraphs.length > 1;

            return (
              <div className={`border-l-4 border-blue-500 rounded-xl p-5 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50/50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <img src="/logo192.png" alt="Sherpa" className="w-4 h-4 rounded" />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Sherpa Analysis</span>
                </div>
                <div className={`text-sm leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                  <p>{renderSummary(firstParagraph)}{hasMore && !summaryExpanded && (
                    <button onClick={() => setSummaryExpanded(true)} className={`ml-1 font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}>Show more</button>
                  )}</p>
                  {summaryExpanded && paragraphs.slice(1).map((para, idx) => (
                    <p key={idx} className="mt-3">{renderSummary(para)}</p>
                  ))}
                  {summaryExpanded && hasMore && (
                    <button onClick={() => setSummaryExpanded(false)} className={`mt-1 font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}>Show less</button>
                  )}
                </div>
                {/* Mentioned counterparts */}
                {res.counterparts?.length > 0 && res.summary && (
                  <div className="mt-4">
                    <span className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'} mr-2`}>Mentioned:</span>
                    <div className="inline-flex flex-wrap gap-1.5 mt-1">
                      {res.counterparts.filter(cp => {
                        const sLower = (res.summary || '').toLowerCase();
                        const nLower = (cp.name || '').toLowerCase();
                        if (sLower.includes(nLower)) return true;
                        const pm = cp.name?.match(/\(([^)]+)\)/);
                        if (pm && sLower.includes(pm[1].toLowerCase())) return true;
                        const base = (cp.name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
                        return base !== cp.name && base.length > 2 && sLower.includes(base.toLowerCase());
                      }).map(cp => {
                        const cpData = counterparts.find(c => c.id === cp.id);
                        const cpWebsite = cpData?.website || cpData?.websiteUrl || cpData?.enrichment?.website;
                        const cpDomain = cpWebsite ? cpWebsite.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] : null;
                        const faviconUrl = cpDomain ? `https://cdn.brandfetch.io/${cpDomain}/icon?c=1idrTt0p2-SJacppl92` : null;
                        return (
                          <button key={cp.id} onClick={() => { if (cpData) { setSearchPanelCp(cpData); setSearchPanelDeal(null); } }}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all hover:scale-105 ${isDark ? 'bg-slate-700/60 text-slate-200 border-slate-600 hover:border-blue-500' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 shadow-sm'}`}
                          >
                            {faviconUrl ? <img src={faviconUrl} alt="" className="w-3.5 h-3.5 rounded-sm" onError={(e) => { e.target.onerror = null; e.target.src = cpDomain ? `https://cdn.brandfetch.io/${cpDomain}/icon?c=1idrTt0p2-SJacppl92` : ''; if (!cpDomain) e.target.style.display = 'none'; }} /> : <Building2 size={12} className="text-slate-400" />}
                            {cp.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      );
    }

    return (
      <div className={`rounded-2xl border ${isDark ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-slate-200'} shadow-lg overflow-hidden mb-6`}>
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between border-b ${isDark ? 'border-slate-700 bg-slate-800' : 'border-slate-100 bg-slate-50/50'}`}>
          <div className="flex items-center gap-2">
            <Search size={18} className="text-blue-500" />
            <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Search Results for "{res.query}"
            </span>
          </div>
          <button onClick={handleDismiss} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-500'} transition-colors`}>
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          {/* Daily search limit hint (all non-Pro users) */}
          {searchesRemaining !== null && searchesRemaining <= 3 && !isAdmin && !isProUser && (
            <div className={`flex items-center gap-2 text-xs mb-4 px-3 py-2 rounded-lg ${isDark ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
              <AlertCircle size={14} />
              <span>{searchesRemaining} search{searchesRemaining !== 1 ? 'es' : ''} remaining today.</span>
              {isSignedIn ? (
                <a
                  href={`${SUBSCRIPTION_TIERS.pro.paymentLink}?client_reference_id=${user?.id}&prefilled_email=${encodeURIComponent(user?.primaryEmailAddress?.emailAddress || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`font-medium underline hover:no-underline ${isDark ? 'text-amber-300' : 'text-amber-800'}`}
                >Upgrade to Pro for unlimited.</a>
              ) : (
                <SignInButton mode="modal">
                  <button className={`font-medium underline hover:no-underline ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                    Subscribe for more searches.
                  </button>
                </SignInButton>
              )}
            </div>
          )}
          {/* Sherpa Analysis Bubble */}
          {hasResults && res.summary && (() => {
            // Strip CP/DL index codes — computed once, used by both header share button and body
            const cleanSummary = res.summary
              .replace(/\s*\((?:(?:CP|DL)\d+(?:,\s*)?)+\)/gi, '')
              .replace(/\b(?:CP|DL)\d+\b/gi, '')
              .replace(/\s{2,}/g, ' ').trim();
            return (
            <div className={`border-l-4 border-blue-500 rounded-xl p-5 mb-6 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50/50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <img src="/logo192.png" alt="Sherpa" className="w-4 h-4 rounded" />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Sherpa Analysis</span>
                </div>
                <ShareAnalysisIcons
                  analysisText={cleanSummary}
                  entityName={res.query || searchQuery}
                  shareUrl={getSearchShareUrl(res.query || searchQuery)}
                  theme={theme}
                />
              </div>
              {/* Summary text with inline clickable counterpart mentions + expand/collapse */}
              <div className={`text-sm leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                {(() => {

                  // Split into paragraphs — show first paragraph by default, expand reveals the rest
                  // If a paragraph is very long, break it at sentence boundaries for readability
                  const splitLongParagraph = (text, maxSentences = 3) => {
                    if (text.length < 400) return [text];
                    const sentences = text.match(/[^.!?]+[.!?]+[\s]*/g) || [text];
                    const chunks = [];
                    for (let i = 0; i < sentences.length; i += maxSentences) {
                      chunks.push(sentences.slice(i, i + maxSentences).join('').trim());
                    }
                    return chunks.filter(Boolean);
                  };
                  const rawParagraphs = cleanSummary.split(/\n\n+/).filter(p => p.trim());
                  const paragraphs = rawParagraphs.flatMap(p => splitLongParagraph(p));
                  const firstParagraph = paragraphs[0] || cleanSummary;
                  const hasMore = paragraphs.length > 1;

                  // Parse summary text and wrap counterpart names as clickable buttons
                  // Start with search result counterparts, then also match names from full database
                  // Each entry can have multiple matchable variants (full name, base name without parenthetical, acronym)
                  const cpEntries = []; // { id, name, variants: [string] }
                  const cpIdSet = new Set();

                  const buildVariants = (name) => {
                    const variants = [name];
                    // Extract acronym from parenthetical: "Norges Bank Investment Management (NBIM)" → "NBIM"
                    const parenMatch = name.match(/\(([A-Z][A-Za-z0-9&]{1,10})\)\s*$/);
                    if (parenMatch) {
                      variants.push(parenMatch[1]); // acronym
                      variants.push(name.replace(/\s*\([^)]+\)\s*$/, '').trim()); // base name without parenthetical
                    }
                    return variants;
                  };

                  const addCp = (id, name) => {
                    if (!name || cpIdSet.has(id)) return;
                    cpEntries.push({ id, name, variants: buildVariants(name) });
                    cpIdSet.add(id);
                  };

                  (res.counterparts || []).forEach(cp => addCp(cp.id, cp.name));
                  const summaryLower = cleanSummary.toLowerCase();
                  counterparts.forEach(cp => {
                    if (cp.name && cp.name.length >= 4 && !cpIdSet.has(cp.id)) {
                      const variants = buildVariants(cp.name);
                      if (variants.some(v => summaryLower.includes(v.toLowerCase()))) {
                        addCp(cp.id, cp.name);
                      }
                    }
                  });

                  // Unicode-safe word boundary: match at start/end of string or adjacent to non-letter/non-digit
                  const uBound = '(?:^|(?<=[^\\p{L}\\p{N}]))';
                  const uBoundEnd = '(?=$|[^\\p{L}\\p{N}])';

                  const renderSummary = (text) => {
                    if (cpEntries.length === 0) return <>{text}</>;
                    // Build all matchable strings sorted longest-first, each mapped to its parent entry
                    const allVariants = [];
                    cpEntries.forEach(entry => {
                      entry.variants.forEach(v => allVariants.push({ variant: v, entry }));
                    });
                    allVariants.sort((a, b) => b.variant.length - a.variant.length);
                    const escaped = allVariants.map(v => v.variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
                    const regex = new RegExp(`(${uBound}(?:${escaped.join('|')})${uBoundEnd})`, 'giu');
                    const parts = text.split(regex);
                    return parts.map((part, i) => {
                      const partLower = part.toLowerCase();
                      const matched = allVariants.find(v => v.variant.toLowerCase() === partLower);
                      if (matched) {
                        const cpData = counterparts.find(c => c.id === matched.entry.id);
                        return (
                          <button
                            key={i}
                            onClick={() => {
                              if (cpData) { setSearchPanelCp(cpData); setSearchPanelDeal(null); }
                            }}
                            className={`font-semibold underline decoration-blue-400/50 decoration-1 underline-offset-2 hover:decoration-blue-500 transition-colors ${isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-700 hover:text-blue-800'}`}
                          >
                            {part}
                          </button>
                        );
                      }
                      return <span key={i}>{part}</span>;
                    });
                  };

                  return (
                    <div>
                      <p>{renderSummary(firstParagraph)}{hasMore && !summaryExpanded && (
                        <button
                          onClick={() => setSummaryExpanded(true)}
                          className={`ml-1 font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                        >
                          Show more
                        </button>
                      )}</p>
                      {summaryExpanded && paragraphs.slice(1).map((para, idx) => (
                        <p key={idx} className="mt-3">{renderSummary(para)}</p>
                      ))}
                      {summaryExpanded && hasMore && (
                        <button
                          onClick={() => setSummaryExpanded(false)}
                          className={`mt-1 font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                        >
                          Show less
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Mentioned counterparts row — only those named in the narrative */}
              {res.counterparts?.length > 0 && res.summary && (
                <div className="mt-4">
                  <span className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'} mr-2`}>Mentioned:</span>
                  <div className="inline-flex flex-wrap gap-1.5 mt-1">
                    {res.counterparts.filter(cp => {
                      const summaryLower = (res.summary || '').toLowerCase();
                      const nameLower = (cp.name || '').toLowerCase();
                      if (summaryLower.includes(nameLower)) return true;
                      const parenMatch = cp.name?.match(/\(([^)]+)\)/);
                      if (parenMatch && summaryLower.includes(parenMatch[1].toLowerCase())) return true;
                      const base = (cp.name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
                      if (base !== cp.name && base.length > 2 && summaryLower.includes(base.toLowerCase())) return true;
                      return false;
                    }).map(cp => {
                      const cpData = counterparts.find(c => c.id === cp.id);
                      const cpWebsite = cpData?.website || cpData?.websiteUrl || cpData?.enrichment?.website;
                      const cpDomain = cpWebsite ? cpWebsite.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] : null;
                      const faviconUrl = cpDomain ? `https://cdn.brandfetch.io/${cpDomain}/icon?c=1idrTt0p2-SJacppl92` : null;
                      return (
                        <button
                          key={cp.id}
                          onClick={() => {
                            if (cpData) { setSearchPanelCp(cpData); setSearchPanelDeal(null); }
                          }}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all hover:scale-105 ${
                            isDark
                              ? 'bg-slate-700/60 text-slate-200 border-slate-600 hover:border-blue-500'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 shadow-sm'
                          }`}
                        >
                          {faviconUrl ? (
                            <img
                              src={faviconUrl}
                              alt=""
                              className="w-3.5 h-3.5 rounded-sm"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = cpDomain ? `https://cdn.brandfetch.io/${cpDomain}/icon?c=1idrTt0p2-SJacppl92` : '';
                                if (!cpDomain) e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <Building2 size={12} className="text-slate-400" />
                          )}
                          {cp.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Filter chips — inside the bubble */}
              {allMatchedChips.length > 0 && (
                <div className={`flex flex-wrap gap-1.5 mt-4 pt-3 border-t ${isDark ? 'border-blue-800/30' : 'border-blue-100'}`}>
                  {allMatchedChips.map((chip, i) => {
                    const active = isChipActive(chip);
                    return (
                      <button
                        key={i}
                        onClick={() => toggleFilter(chip)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-all ${
                          active
                            ? 'ring-2 ring-offset-1 scale-105 ' + (isDark
                                ? 'ring-blue-500 ring-offset-slate-800'
                                : 'ring-blue-400 ring-offset-white')
                            : 'hover:scale-105'
                        } ${chipColors[chip.color]}`}
                      >
                        {active && <span className="mr-1">✓</span>}
                        {chip.label}
                      </button>
                    );
                  })}
                  {activeFilters.length > 0 && (
                    <button
                      onClick={() => setActiveFilters([])}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                        isDark
                          ? 'border-slate-600 text-slate-400 hover:text-slate-200 hover:border-slate-500'
                          : 'border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400'
                      }`}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {/* Filter chips fallback — outside bubble when no summary */}
          {hasResults && !res.summary && allMatchedChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {allMatchedChips.map((chip, i) => {
                const active = isChipActive(chip);
                return (
                  <button
                    key={i}
                    onClick={() => toggleFilter(chip)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-all ${
                      active
                        ? 'ring-2 ring-offset-1 scale-105 ' + (isDark
                            ? 'ring-blue-500 ring-offset-slate-800'
                            : 'ring-blue-400 ring-offset-white')
                        : 'hover:scale-105'
                    } ${chipColors[chip.color]}`}
                  >
                    {active && <span className="mr-1">✓</span>}
                    {chip.label}
                  </button>
                );
              })}
              {activeFilters.length > 0 && (
                <button
                  onClick={() => setActiveFilters([])}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                    isDark
                      ? 'border-slate-600 text-slate-400 hover:text-slate-200 hover:border-slate-500'
                      : 'border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400'
                  }`}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Result summary strip */}
          {hasResults && (
            <div className={`mb-4 pb-4 border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {activeFilters.length > 0
                  ? `${filteredDeals.length} of ${res.allDealIds?.length || res.deals?.length || 0} deals (filtered)`
                  : `${res.allDealIds?.length || res.deals?.length || 0} deals`
                } · {res.counterparts?.length || 0} counterparties
              </p>
            </div>
          )}

          {/* Deals - single column Google News style */}
          {hasResults ? (
            <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className={`text-sm font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'} uppercase tracking-wide flex items-center gap-2`}>
                    <TrendingUp size={14} />
                    Deals ({activeFilters.length > 0 ? filteredDeals.length : (res.allDealIds?.length || res.deals?.length || 0)})
                  </h4>
                  <div className="flex items-center gap-3">
                    {/* Sort toggle */}
                    <div className={`flex rounded-lg border ${isDark ? 'border-slate-600 bg-slate-700/50' : 'border-slate-200 bg-slate-100'} p-0.5`}>
                      <button
                        onClick={() => setDealSortMode('relevance')}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                          dealSortMode === 'relevance'
                            ? (isDark ? 'bg-blue-600 text-white' : 'bg-white text-blue-700 shadow-sm')
                            : (isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700')
                        }`}
                      >
                        Relevance
                      </button>
                      <button
                        onClick={() => setDealSortMode('date')}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                          dealSortMode === 'date'
                            ? (isDark ? 'bg-blue-600 text-white' : 'bg-white text-blue-700 shadow-sm')
                            : (isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700')
                        }`}
                      >
                        Date
                      </button>
                    </div>
                    {/* Only show "View all" when there are more deals than initially displayed (20) and no filters active */}
                    {activeFilters.length === 0 && (res.allDealIds?.length || res.deals?.length || 0) > 20 && (
                      <button
                        onClick={handleViewAllDeals}
                        className={`text-xs font-medium flex items-center gap-1 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                      >
                        View all {res.allDealIds?.length || res.deals.length} <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {/* Counterpart "Only" filter chip */}
                {activeFilters.some(f => f.color === 'indigo') && (
                  <div className="mb-3 flex items-center gap-2">
                    {activeFilters.filter(f => f.color === 'indigo').map(f => (
                      <span key={f.value} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-700' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'}`}>
                        Showing {f.label} only ({filteredDeals.length})
                        <button onClick={() => setActiveFilters(prev => prev.filter(af => af.color !== 'indigo'))} className="hover:opacity-70">×</button>
                      </span>
                    ))}
                  </div>
                )}
                {filteredDeals.length > 0 ? (
                  <div className="space-y-3">
                    {(() => {
                      const formatTechLabel = (tech) => tech.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\b(Ppa|Ipo|Epc|Ipp)\b/g, m => m.toUpperCase());
                      return filteredDeals.slice(0, dealsToShow).map(deal => {
                      const dealCps = deal.counterparts || [];
                      const fullDeal = deals.find(d => d.id === deal.id);
                      // Use search API data directly — fullDeal may be null for anonymous users
                      const d = fullDeal || deal;
                      const dealDate = d.announcement_date || d.created_at;
                      const relativeTime = dealDate ? formatRelativeTime(dealDate) : null;
                      const description = d.description;
                      const techs = d.technology ? (Array.isArray(d.technology) ? d.technology : [d.technology]) : [];
                      const country = (Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).join(', ');
                      const dealType = d.deal_type;
                      const capacity = d.capacity;
                      const capacityUnit = d.capacity_unit;
                      const financials = d.financials;

                      return (
                        <div
                          role="button"
                          tabIndex={0}
                          key={deal.id}
                          onClick={() => {
                            setSearchPanelDeal(fullDeal || deal); setSearchPanelCp(null);
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSearchPanelDeal(fullDeal || deal); setSearchPanelCp(null); } }}
                          className={`relative w-full text-left group rounded-lg border px-5 py-3 cursor-pointer transition-all ${isDark ? 'border-slate-700 hover:border-slate-600 hover:shadow-lg hover:shadow-slate-900/30 bg-slate-800/40' : 'border-slate-200 hover:border-slate-300 hover:shadow-md bg-white'}`}
                        >
                          {/* Hover chevron */}
                          <ChevronRight size={16} className={`absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />

                          {/* Counterpart row + timestamp */}
                          {(dealCps.length > 0 || relativeTime) && (
                            <div className="flex items-center gap-1 mb-2 flex-wrap">
                              {dealCps.slice(0, 4).map((dc, i) => {
                                // Use search API data (dc.website, dc.logo_url) directly — don't rely on counterparts state
                                const cpData = counterparts.find(c => c.id === dc.id) || dc;
                                const cpWebsite = dc.website || cpData?.website || cpData?.enrichment?.website;
                                const cpDomain = cpWebsite ? cpWebsite.replace(/^https?:\/\//, '').split('/')[0] : null;
                                const logoUrl = dc.logo_url || cpData?.logo_url;
                                const faviconUrl = logoUrl || (cpDomain ? `https://cdn.brandfetch.io/${cpDomain}/icon?c=1idrTt0p2-SJacppl92` : null);
                                return (
                                  <span key={dc.id || i} className="inline-flex items-center group/cp">
                                    {i > 0 && <span className={`mx-1.5 ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>·</span>}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSearchPanelCp(cpData); setSearchPanelDeal(null);
                                      }}
                                      className={`inline-flex items-center gap-1 text-xs hover:underline ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                                    >
                                      {faviconUrl ? (
                                        <img src={faviconUrl} alt="" className="w-4 h-4 rounded" onError={(e) => e.target.style.display = 'none'} />
                                      ) : (
                                        <Building2 size={13} className="text-slate-400" />
                                      )}
                                      <span className="font-medium">{dc.name}</span>
                                      {dc.role && <span className={`font-normal ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{dc.role}</span>}
                                    </button>
                                    {dc.id && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveFilters(prev => {
                                            const exists = prev.some(f => f.color === 'indigo' && f.value === dc.id);
                                            if (exists) return prev.filter(f => f.color !== 'indigo');
                                            return [...prev.filter(f => f.color !== 'indigo'), { label: dc.name, color: 'indigo', value: dc.id }];
                                          });
                                        }}
                                        className={`ml-0.5 text-xs font-semibold opacity-0 group-hover/cp:opacity-100 transition-opacity ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                                      >
                                        Only
                                      </button>
                                    )}
                                  </span>
                                );
                              })}
                              {dealCps.length > 4 && (
                                <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>+{dealCps.length - 4} more</span>
                              )}
                              {relativeTime && (
                                <span className={`ml-auto text-xs flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                  {relativeTime}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Headline */}
                          <h3 className={`text-base font-semibold leading-snug pr-6 ${isDark ? 'text-slate-100 group-hover:text-blue-400' : 'text-slate-900 group-hover:text-blue-700'}`}>
                            {deal.headline || 'Untitled Deal'}
                          </h3>

                          {/* Description */}
                          {description && (
                            <p className={`text-sm mt-1 leading-relaxed line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                              {description}
                            </p>
                          )}

                          {/* Metadata pills — click to filter current results */}
                          {(techs.length > 0 || country || dealType || capacity || financials?.dealValue?.amount) && (
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              {techs.map((t, ti) => {
                                const label = formatTechLabel(t);
                                const isActive = activeFilters.some(f => f.label === label && f.color === 'green');
                                return (
                                  <button key={ti} onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFilters(prev => isActive ? prev.filter(f => !(f.label === label && f.color === 'green')) : [...prev, { label, color: 'green', value: t }]);
                                  }}
                                    className={`text-xs font-medium px-2.5 py-0.5 rounded-full border cursor-pointer transition-colors ${isActive ? (isDark ? 'ring-2 ring-blue-500 border-blue-500 text-blue-300 bg-blue-900/50' : 'ring-2 ring-blue-400 border-blue-400 text-blue-700 bg-blue-100') : (isDark ? 'border-slate-600 text-blue-400 bg-slate-800/50 hover:bg-blue-900/50' : 'border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100')}`}>
                                    {isActive && '✓ '}{label}
                                  </button>
                                );
                              })}
                              {country && country !== 'null' && (() => {
                                const isActive = activeFilters.some(f => f.label === country && f.color === 'amber');
                                return (
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFilters(prev => isActive ? prev.filter(f => !(f.label === country && f.color === 'amber')) : [...prev, { label: country, color: 'amber', value: country }]);
                                  }}
                                    className={`text-xs font-medium px-2.5 py-0.5 rounded-full border cursor-pointer transition-colors ${isActive ? (isDark ? 'ring-2 ring-emerald-500 border-emerald-500 text-emerald-300 bg-emerald-900/50' : 'ring-2 ring-emerald-400 border-emerald-400 text-emerald-700 bg-emerald-100') : (isDark ? 'border-slate-600 text-emerald-400 bg-slate-800/50 hover:bg-emerald-900/50' : 'border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100')}`}>
                                    {isActive && '✓ '}{country}
                                  </button>
                                );
                              })()}
                              {dealType && (() => {
                                const label = formatTechLabel(dealType);
                                const isActive = activeFilters.some(f => f.label === label);
                                return (
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFilters(prev => isActive ? prev.filter(f => f.label !== label) : [...prev, { label, color: 'amber', value: dealType }]);
                                  }}
                                    className={`text-xs font-medium px-2.5 py-0.5 rounded-full border cursor-pointer transition-colors ${isActive ? (isDark ? 'ring-2 ring-amber-500 border-amber-500 text-amber-300 bg-amber-900/50' : 'ring-2 ring-amber-400 border-amber-400 text-amber-700 bg-amber-100') : (isDark ? 'border-slate-600 text-amber-400 bg-slate-800/50 hover:bg-amber-900/50' : 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100')}`}>
                                    {isActive && '✓ '}{label}
                                  </button>
                                );
                              })()}
                              {capacity && (
                                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${isDark ? 'border-slate-600 text-blue-400 bg-slate-800/50' : 'border-blue-200 text-blue-600 bg-blue-50'}`}>
                                  {capacity}{capacityUnit ? ` ${capacityUnit}` : ' MW'}
                                </span>
                              )}
                              {financials?.dealValue?.amount && (
                                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${isDark ? 'border-slate-600 text-blue-400 bg-slate-800/50' : 'border-blue-200 text-blue-600 bg-blue-50'}`}>
                                  ${financials.dealValue.amount}{financials.dealValue.unit === 'B' ? 'B' : 'M'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                    })()}
                    {/* Show more button */}
                    {filteredDeals.length > dealsToShow && (
                      <button
                        onClick={() => setDealsToShow(prev => prev + 20)}
                        className={`w-full py-3 text-sm font-medium rounded-lg border transition-colors ${isDark
                          ? 'border-slate-600 text-slate-300 hover:bg-slate-700/50'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      >
                        Show more ({filteredDeals.length - dealsToShow} remaining)
                      </button>
                    )}
                  </div>
                ) : (
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No deals found for this search.</p>
                )}
            </div>
          ) : (
            /* No results - friendly empty state */
            <div className={`text-center py-6`}>
              <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                <Search size={20} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
              </div>
              <p className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                No deals found for "{res.query}"
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Try different keywords or check your spelling
              </p>
            </div>
          )}
        </div>

        {/* Sticky Compare Bar */}
        {compareSelection.length >= 2 && (
          <div className={`sticky bottom-0 px-6 py-3 border-t flex items-center gap-3 flex-wrap ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              {compareSelection.length} selected:
            </span>
            <div className="flex items-center gap-1.5 flex-wrap flex-1">
              {compareSelection.map(id => {
                const cp = res.counterparts?.find(c => c.id === id) || counterparts.find(c => c.id === id);
                return (
                  <span key={id} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${isDark ? 'bg-blue-900/40 text-blue-300 border-blue-700/50' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                    {cp?.name || 'Unknown'}
                    <button
                      onClick={() => setCompareSelection(prev => prev.filter(i => i !== id))}
                      className={`ml-0.5 rounded-full hover:bg-blue-200/50 ${isDark ? 'hover:bg-blue-800/50' : ''}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
            </div>
            <button
              onClick={() => setCompareSelection([])}
              className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}
            >
              Clear
            </button>
            <button
              onClick={() => setShowCompareModal(true)}
              className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Compare
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${themeClasses.bg} font-sans ${theme === 'dark' ? 'gis-dark' : ''}`} style={{ '--gis-nav-w': '68px' /* icon rail only — contextual sidebar retired 2026-08-09 */ }}>
      <style>{`
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: ${theme === 'dark' ? '#0f172a' : '#f1f5f9'}; }
        ::-webkit-scrollbar-thumb { background: ${theme === 'dark' ? '#334155' : '#cbd5e1'}; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: ${theme === 'dark' ? '#475569' : '#94a3b8'}; }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      
      {/* Loading State.
          The Research storefront is exempt: it is a public sales surface that
          fetches its own catalogue and needs none of the deals, counterparts or
          user data this overlay waits for. Making a buyer stare at "Loading
          your data..." before they can read a product page would be wrong even
          if that load were fast. */}
      {isLoading && activeTab !== 'research' && activeTab !== 'data' && activeTab !== 'homepage' && activeTab !== 'atlas' && (
        <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} flex items-center justify-center z-50`}>
          <div className="text-center">
            <Loader2 size={32} className="animate-spin text-blue-700 mx-auto mb-3" />
            <p className={themeClasses.textMuted}>{t('loadingData')}</p>
          </div>
        </div>
      )}

      <Suspense fallback={
        <div className={`flex items-center justify-center p-12`}>
          <Loader2 size={24} className="animate-spin text-blue-700" />
        </div>
      }>

      {/* Main app — always renders (signed in, anonymous, or deep-link) */}
      <>

      {/* Slack-style L-frame shell (fixed chrome; content padded pt-12 lg:ml-[308px]) */}
      <SlackShell
        counterparts={counterparts}
        collapsed={navCollapsed}
        setCollapsed={setNavCollapsed}
        onSelectSegment={(tech) => { setPendingDashboardFilter({ type: 'sector', value: tech }); setActiveTab('insights'); }}
        onRunSearch={(q) => { setHeroSearch(q); setActiveTab('dashboard'); performSmartSearch(q); }}
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setSelectedProject(null);
          setSelectedCounterpart(null);
          setSelectedDeal(null);
          setActiveTab(tab);
        }}
        sectorConfig={sectorConfig}
        sectorPresets={SECTOR_PRESETS}
        onSectorChange={(id) => {
          const preset = SECTOR_PRESETS[id];
          if (preset) {
            setSectorConfig(preset);
            setTechnologies(preset.categories);
            setStages(preset.stages);
            setPipelineStatuses(preset.pipelineStatuses);
            setRegions(preset.regions);
          }
        }}
        badges={{}}
        theme={theme}
        setTheme={setTheme}
        gDriveConnected={gDriveConnected}
        gDriveSyncing={gDriveSyncing}
        onSyncGoogleDrive={manualSyncGoogleDrive}
        userTier={userTier}
        user={user}
        isAdmin={isAdmin}
        searchQuery={heroSearch}
        setSearchQuery={setHeroSearch}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        suggestions={heroSuggestions}
        recentSearches={recentSearches}
        searchLoading={smartSearchLoading}
        onSearchSubmit={handleSearchBarSearch}
        onSuggestionSelect={handleSearchBarSelect}
        onSmartSearch={performSmartSearch}
        t={t}
        onOpenReport={(slug) => { setResearchSlug(slug); _setActiveTab('research'); window.history.pushState({}, '', `/research/${slug}`); }}
        onOpenData={(slug) => { setDataSlug(slug); _setActiveTab('data'); window.history.pushState({}, '', `/data/${slug}`); }}
        onLogoClick={() => {
          setHeroSearch('');
          setSmartSearchResponse(null);
          setSmartSearchLoading(false);
          setSearchPanelCp(null);
          setSmartSearchCpIds(null);
          setSmartSearchQuery('');
          setSmartSearchDeals([]);
          setSmartSearchDealIds(null);
          setSmartSearchDealQuery('');
          setSmartSearchDealCps([]);
          setSelectedProject(null);
          setSelectedCounterpart(null);
          setSelectedDeal(null);
          setCompareSelection([]);
          setActiveTab('homepage');
        }}
      />

      {/* Welcome toast — shown after signup + follow deep-link */}
      {welcomeToast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50" style={{ animation: 'slideDown 0.3s ease-out' }}>
          <style>{`@keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          <div className="flex items-center gap-3 px-5 py-3 bg-green-600 text-white rounded-xl shadow-xl text-sm font-medium max-w-lg">
            <svg className="flex-shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            {welcomeToast.message}
            <button onClick={() => setWelcomeToast(null)} className="ml-2 opacity-70 hover:opacity-100 flex-shrink-0">✕</button>
          </div>
        </div>
      )}

      {/* Main Content — padded for the fixed Slack shell on lg+ (top frame + left rails); mobile uses MobileBottomNav */}
      <div className="flex-1 overflow-auto pb-20 lg:pb-10 lg:pt-12 lg:ml-[var(--gis-nav-w)] lg:bg-[color:var(--gis-bg)]">

      {/* Landing Page — dashboard tab with clean centered search (Claude/Google style) */}
      {activeTab === "dashboard" && !selectedDeal && !selectedCounterpart ? (
        <div className={`px-6 ${
          !smartSearchResponse && !smartSearchLoading
            ? 'min-h-[calc(100vh-6.5rem)] flex flex-col items-center justify-center'
            : 'pt-16 flex flex-col items-center'
        }`}>
          <div className={`w-full mx-auto ${smartSearchResponse || smartSearchLoading ? 'max-w-6xl' : 'max-w-3xl'}`}>
            {/* Welcome greeting — centered above search */}
            {!smartSearchResponse && !smartSearchLoading && (
              <div className="text-center mb-10">
                <h1 className={`text-4xl sm:text-5xl font-light tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                  {homeGreeting}{user?.firstName ? `, ${user.firstName}` : ''}.
                </h1>
                <p className={`text-base mt-3 font-light ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                  {welcomeMessage}
                </p>
              </div>
            )}
            {globalSearchBarJSX}

            {/* Subtitle + Stats + Example queries — shown when no search active */}
            {!smartSearchResponse && !smartSearchLoading && (
              <div className="text-center mt-6">
                {/* Subtitle */}
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                  Search across live infrastructure deals, counterparties, and activity trends.
                </p>

                {/* Stats strip */}
                <p className={`text-xs mt-3 ${theme === 'dark' ? 'text-slate-600' : 'text-slate-400'}`}>
                  Live infrastructure deals and counterparties · updated daily
                </p>

                {/* Example query chips — tested to return direct matches (no fallback) */}
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  {[
                    'Solar PPA',
                    'Offshore wind acquisition',
                    'Data center United States',
                    'Battery storage Australia',
                    'Solar financing',
                    'Hydrogen Germany',
                    'KKR',
                    'Iberdrola',
                  ].map((query) => (
                    <button
                      key={query}
                      onClick={() => {
                        setSmartSearchQuery(query);
                        performSmartSearch(query);
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all hover:scale-105 ${
                        theme === 'dark'
                          ? 'border-slate-700 text-slate-300 hover:border-blue-500 hover:text-blue-400 bg-slate-800/50'
                          : 'border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600 bg-white'
                      }`}
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(smartSearchResponse || smartSearchLoading) && <SmartSearchResults />}
          </div>
        </div>
      ) : (
        <div className="p-6 max-w-7xl mx-auto">

        {/* Smart Search Results — inline below search bar (not on Counterparts/Deals/Insights/Ecosystem tabs) */}
        {(smartSearchResponse || smartSearchLoading) && activeTab !== 'counterparts' && activeTab !== 'deals' && activeTab !== 'insights' && activeTab !== 'ecosystem' && activeTab !== 'organizations' && activeTab !== 'signals' && <SmartSearchResults />}

        {/* Hide all legacy content when smart search is active (except tabs with their own handling) */}
        {(!(smartSearchResponse || smartSearchLoading) || activeTab === 'counterparts' || activeTab === 'deals' || activeTab === 'insights' || activeTab === 'ecosystem' || activeTab === 'organizations' || activeTab === 'signals') && (
        <>
        {/* TEMPORARY: Design Demos */}
        {demoMode === 'deals' ? (
          <DealDesignDemo
            deals={deals}
            counterparts={counterparts}
            theme={theme}
            themeClasses={themeClasses}
            setPanelCounterpart={setPanelCounterpart}
            onClose={() => { setDemoMode(null); window.history.replaceState({}, '', '/'); }}
            onDealSelect={viewDeal}
            setActiveTab={setActiveTab}
          />
        ) : demoMode === 'counterparts' ? (
          <CounterpartDesignDemo
            deals={deals}
            counterparts={counterparts}
            theme={theme}
            themeClasses={themeClasses}
            setPanelCounterpart={setPanelCounterpart}
            onClose={() => { setDemoMode(null); window.history.replaceState({}, '', '/'); }}
            onCounterpartSelect={(cp) => { setDemoMode(null); window.history.replaceState({}, '', '/'); setSelectedCounterpart(cp); }}
            setActiveTab={setActiveTab}
          />
        ) :
        /* Deal Detail Page - Full Page View with optional panel */
        selectedDeal && !isPaidViewer ? (
          isUserLoaded ? (
            <LockedDeal
              deal={selectedDeal}
              theme={theme}
              isSignedIn={isSignedIn}
              onClose={() => setSelectedDeal(null)}
              onSubscribe={() => { setSelectedDeal(null); setActiveTab('subscribe'); }}
            />
          ) : <div className="p-10 text-sm text-[color:var(--gis-dim)]">Loading…</div>
        ) :
        selectedDeal ? (
          <div className="flex gap-8 items-start">
            <div className={`${panelCounterpart ? 'flex-1' : 'w-full'} min-w-0 relative`}>
              <div className={isContentGated ? 'relative max-h-[900px] overflow-hidden' : ''}>
              <DealDetailPage
                deal={selectedDeal}
                onClose={() => setSelectedDeal(null)}
                deals={deals}
                counterparts={counterparts}
                dealTechnologies={dealTechnologies}
                theme={theme}
                themeClasses={themeClasses}
                setActiveTab={setActiveTab}
                setPanelCounterpart={setPanelCounterpart}
                setSharingDeal={setSharingDeal}
                isAdmin={isAdmin}
                onDealSelect={viewDeal}
                onDealUpdated={(updatedDeal) => {
                  setDeals(prev => prev.map(d => d.id === updatedDeal.id ? { ...d, ...updatedDeal } : d));
                  setSelectedDeal(prev => prev?.id === updatedDeal.id ? { ...prev, ...updatedDeal } : prev);
                }}
                t={t}
                isSignedIn={isSignedIn}
                isGated={isContentGated}
                guestViewsRemaining={guestViewsRemaining}
                onSubscribe={() => setActiveTab('subscribe')}
              />
              {isContentGated && (
                <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-slate-900 dark:via-slate-900/95 flex items-end justify-center pb-6 z-10">
                  <div className="text-center max-w-sm mx-auto px-4">
                    <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Register free to see the full analysis</p>
                    <p className={`text-xs mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>No credit card required</p>
                    <div className="flex items-center justify-center gap-3">
                      <SignUpButton mode="modal">
                        <button className="px-5 py-2 bg-blue-700 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 shadow-lg transition-colors cursor-pointer">Register Free</button>
                      </SignUpButton>
                      <SignInButton mode="modal">
                        <button className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors cursor-pointer ${theme === 'dark' ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>Sign in</button>
                      </SignInButton>
                    </div>
                  </div>
                </div>
              )}
              </div>
              {/* Paywall overlay when the deal-view quota is used up */}
              {paywallInfo?.type === 'deals' && (
                <PaywallOverlay type="deals" used={paywallInfo.used} limit={paywallInfo.limit} onClose={() => { setPaywallInfo(null); setSelectedDeal(null); }} />
              )}
            </div>
            {panelCounterpart && (
              <div className="w-96 flex-shrink-0 hidden xl:block sticky top-20">
                <CounterpartPanel
                  counterpart={panelCounterpart}
                  onClose={() => setPanelCounterpart(null)}
                  onViewFull={() => {
                    viewCounterpart(panelCounterpart);
                    setPanelCounterpart(null);
                    setSelectedDeal(null);
                  }}
                  isAdmin={user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net')}
                  onEnrich={async () => {
                    const canEnrich = user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net');
                    if (!canEnrich) return;

                    setPanelCounterpart({ ...panelCounterpart, _isEnriching: true });

                    try {
                      const enrichRes = await authFetch('/api/enrich-counterpart', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          counterpartId: panelCounterpart.id,
                          name: panelCounterpart.name,
                          websiteUrl: panelCounterpart.websiteUrl || panelCounterpart.website,
                          linkedinUrl: panelCounterpart.linkedinUrl,
                          industry: 'infrastructure',
                          counterpartType: panelCounterpart.type || 'company'
                        })
                      });

                      const enrichData = await enrichRes.json();

                      if (enrichRes.ok && enrichData.success && enrichData.investmentPreferences) {
                        const prefs = enrichData.investmentPreferences;
                        const summaryText = prefs.summary || prefs.bio;
                        const enrichedCp = {
                          ...panelCounterpart,
                          investmentPreferences: prefs,
                          summary: summaryText,
                          websiteUrl: prefs.website || prefs.linkedinUrl || panelCounterpart.websiteUrl,
                          linkedinUrl: prefs.linkedinUrl || panelCounterpart.linkedinUrl,
                          role: prefs.currentPosition?.title || panelCounterpart.role,
                          company: prefs.currentPosition?.company || panelCounterpart.company,
                          _isEnriching: false
                        };
                        setPanelCounterpart(enrichedCp);
                        setCounterparts(prev => {
                          const exists = prev.some(c => c.id === panelCounterpart.id);
                          if (exists) return prev.map(c => c.id === panelCounterpart.id ? { ...c, ...enrichedCp } : c);
                          return [...prev, enrichedCp];
                        });
                      } else {
                        console.error('[Enrich] Failed:', enrichData.error || enrichData.message || 'Unknown error');
                        alert('Enrichment failed: ' + (enrichData.error || enrichData.message || 'Unknown error'));
                        setPanelCounterpart({ ...panelCounterpart, _isEnriching: false, _enrichError: true });
                      }
                    } catch (err) {
                      console.error('[Enrich] Exception:', err);
                      alert('Enrichment error: ' + err.message);
                      setPanelCounterpart({ ...panelCounterpart, _isEnriching: false, _enrichError: true });
                    }
                  }}
                />
              </div>
            )}
          </div>
        ) : selectedCounterpart ? (
          <div className="relative">
          <div className={isContentGated ? 'relative max-h-[900px] overflow-hidden' : ''}>
          <CounterpartDetailPage
            counterpart={selectedCounterpart}
            onClose={() => setSelectedCounterpart(null)}
            counterparts={counterparts}
            deals={deals}
            documents={documents}
            activities={activities}
            tasks={tasks}
            technologies={technologies}
            sectorConfig={sectorConfig}
            projects={projects}
            theme={theme}
            themeClasses={themeClasses}
            user={user}
            isSignedIn={isSignedIn}
            setActiveTab={setActiveTab}
            setSelectedCounterpart={setSelectedCounterpart}
            setSelectedDeal={setSelectedDeal}
            setPanelCounterpart={setPanelCounterpart}
            setEditingCounterpart={setEditingCounterpart}
            setShowCounterpartModal={setShowCounterpartModal}
            setShowDeleteConfirm={setShowDeleteConfirm}
            setCounterparts={setCounterparts}
            setCompareList={setCompareList}
            compareList={compareList}
            setSharingCounterpart={setSharingCounterpart}
            setDocuments={setDocuments}
            setViewingDocument={setViewingDocument}
            setDocumentReturnTo={setDocumentReturnTo}
            getCounterpartDocuments={getCounterpartDocuments}
            getTasksFor={getTasksFor}
            logActivity={logActivity}
            generateAISummary={generateAISummary}
            summaryLoading={summaryLoading}
            toggleFavorite={toggleFavorite}
            isFavorite={isFavorite}
            createPersonAsCounterpart={createPersonAsCounterpart}
            getDaysAgo={getDaysAgo}
            getFileIcon={getFileIcon}
            t={t}
            CounterpartAvatar={CounterpartAvatar}
            DealsTabComponent={DealsTab}
            RelationshipMapComponent={RelationshipMap}
            isGated={isContentGated}
            guestViewsRemaining={guestViewsRemaining}
            onSubscribe={() => setActiveTab('subscribe')}
          />
          {isContentGated && (
            <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-slate-900 dark:via-slate-900/95 flex items-end justify-center pb-6 z-10">
              <div className="text-center max-w-sm mx-auto px-4">
                <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Register free to see the full profile</p>
                <p className={`text-xs mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>No credit card required</p>
                <div className="flex items-center justify-center gap-3">
                  <SignUpButton mode="modal">
                    <button className="px-5 py-2 bg-blue-700 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 shadow-lg transition-colors cursor-pointer">Register Free</button>
                  </SignUpButton>
                  <SignInButton mode="modal">
                    <button className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors cursor-pointer ${theme === 'dark' ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>Sign in</button>
                  </SignInButton>
                </div>
              </div>
            </div>
              )}
          </div>
          {/* Paywall overlay when the company-view quota is used up */}
          {paywallInfo?.type === 'counterparts' && (
            <PaywallOverlay type="counterparts" used={paywallInfo.used} limit={paywallInfo.limit} onClose={() => { setPaywallInfo(null); setSelectedCounterpart(null); }} />
          )}
          </div>
        ) : (
        <>
        {/* Home Tab — editorial dashboard (default landing) */}
        {activeTab === "home" && (
          <SherpaDashboard
            deals={deals}
            stats={dashboardStats}
            greeting={homeGreeting}
            userName={user?.firstName}
            onViewDeal={viewPanelDeal}
            onSelectCounterpart={viewPanelCounterpart}
            setActiveTab={setActiveTab}
            theme={theme}
          />
        )}

        {/* Insights Tab - Landing page with search + dashboard */}
        {activeTab === "insights" && (
          <div>
            {isSignedIn && sectorProjects.length === 0 && counterparts.length === 0 && centralDataLoaded ? (
              <OnboardingHero />
            ) : (
              <BloombergDashboard
                    theme={theme}
                    favorites={favorites}
                    counterparts={counterparts}
                    deals={deals}
                    setActiveTab={setActiveTab}
                    setScreenerFilters={setScreenerFilters}
                    onSelectCounterpart={viewPanelCounterpart}
                    onViewDeal={viewPanelDeal}
                    initialStats={dashboardStats}
                    onSignUp={() => setActiveTab('subscribe')}
                    userTier={isAdmin ? 'admin' : (isSignedIn ? userTier : 'anonymous')}
                    centralDataLoaded={centralDataLoaded}
                    onSearch={performSmartSearch}
                    searchLoading={smartSearchLoading}
                    searchQuery={smartSearchQuery}
                    searchResponse={smartSearchResponse}
                    searchContent={(smartSearchResponse || smartSearchLoading) ? <SmartSearchResults bubbleOnly /> : null}
                    onClearSearch={() => { setSmartSearchResponse(null); setSmartSearchLoading(false); setSmartSearchQuery(''); setCompareSelection([]); }}
                    searchesRemaining={searchesRemaining}
                    recentSearches={recentSearches}
                    pendingFilter={pendingDashboardFilter}
                    onPendingFilterConsumed={() => setPendingDashboardFilter(null)}
                  />
            )}
          </div>
        )}

        {/* Organizations Tab — open to all. Tier-based row caps inside the page. */}
        {activeTab === "organizations" && !selectedCounterpart && (
          <OrganizationsPage
            counterparts={counterparts}
            deals={deals}
            onSelectCounterpart={viewPanelCounterpart}
            theme={theme}
            userTier={isSignedIn ? userTier : 'anonymous'}
            isSignedIn={isSignedIn}
            isAdmin={isAdmin}
            totalCount={totalCounts.counterparts}
            orgsReady={orgsLoaded}
            onUpgrade={() => setActiveTab('subscribe')}
            onMergeComplete={refreshCentralizedData}
          />
        )}

        {/* Signals Tab — "The Sherpa Signal". Open to all. */}
        {activeTab === "signals" && (
          <SignalsPage
            theme={theme}
            isSignedIn={isSignedIn}
            onSelectCounterpart={viewPanelCounterpart}
            onSelectDeal={viewDeal}
          />
        )}

        {/* Research Tab — Sherpa Publishing storefront. Public: anyone can browse
            and buy; no account required (guest checkout). */}
        {activeTab === "research" && (
          checkoutSession ? (
            <PurchaseComplete
              sessionId={checkoutSession}
              theme={theme}
              onBrowse={() => {
                setCheckoutSession(null);
                setResearchSlug(null);
                window.history.pushState({}, '', '/research');
              }}
            />
          ) : researchSlug ? (
            <ResearchDetail
              slug={researchSlug}
              theme={theme}
              onBack={() => { setResearchSlug(null); window.history.pushState({}, '', '/research'); }}
              onBuy={(report, tierId, licence, deal) => setBuyingReport({ report, tierId: tierId || null, licence: licence || 'single', deal: deal || null })}
              checkoutEnabled
            />
          ) : (
            <ResearchList
              theme={theme}
              onOpenReport={(slug) => { setResearchSlug(slug); window.history.pushState({}, '', `/research/${slug}`); }}
            />
          )
        )}

        {/* Home — the site's front page (redesign 2026-09-28). Public. */}
        {activeTab === "homepage" && (
          <HomePage
            theme={theme}
            onNavigate={setActiveTab}
            onOpenReport={(slug) => { setResearchSlug(slug); _setActiveTab('research'); window.history.pushState({}, '', `/research/${slug}`); }}
            onOpenData={(slug) => { setDataSlug(slug); _setActiveTab('data'); window.history.pushState({}, '', `/data/${slug}`); }}
            onOpenLiveFeed={() => setActiveTab('insights')}
            onSeePlans={() => setActiveTab('subscribe')}
            onOpenAtlas={(path) => { goAtlas(path); _setActiveTab('atlas'); }}
          />
        )}

        {/* Data Tab — Sherpa Data storefront. Data files are research_reports rows
            with publication_type 'Dataset', bought through the same checkout. */}
        {activeTab === "data" && (
          dataSlug ? (
            <DataDetail
              slug={dataSlug}
              theme={theme}
              onBack={() => { setDataSlug(null); window.history.pushState({}, '', '/data'); }}
              onBuy={(report, tierId, licence, deal) => setBuyingReport({ report, tierId: tierId || null, licence: licence || 'single', deal: deal || null })}
            />
          ) : (
            <DataList
              theme={theme}
              onOpenFile={(slug) => { setDataSlug(slug); window.history.pushState({}, '', `/data/${slug}`); }}
            />
          )
        )}

        {/* Sherpa Atlas — public front page and free maps; full maps and
            product previews for admins or share links. */}
        {activeTab === "atlas" && (
          <AtlasSection path={atlasPath} go={goAtlas} theme={theme} isAdmin={isAdmin} ready={isUserLoaded} />
        )}

        {buyingReport && (
          <BuyReportModal
            report={buyingReport.report}
            tierId={buyingReport.tierId}
            licence={buyingReport.licence}
            deal={buyingReport.deal}
            theme={theme}
            userId={user?.id || null}
            onClose={() => setBuyingReport(null)}
          />
        )}

        {/* Ecosystem Tab — open to all. EcosystemPage gates non-demo searches for anonymous/free users. */}
        {activeTab === "ecosystem" && (
          <EcosystemPage
            counterparts={counterparts}
            deals={deals}
            viewPanelCounterpart={viewPanelCounterpart}
            isSignedIn={isSignedIn}
            userTier={isSignedIn ? userTier : 'anonymous'}
            user={user}
            theme={theme}
            totalCount={totalCounts.counterparts}
          />
        )}

        {/* Matchup Tab — open to all. MatchupPage auto-loads a 3-way demo for anonymous/free users. */}
        {activeTab === "matchup" && (
          <MatchupPage
            counterparts={counterparts}
            allDeals={deals}
            theme={theme}
            preselectedIds={matchupPreselected}
            onSelectCounterpart={viewPanelCounterpart}
            userTier={isSignedIn ? userTier : 'anonymous'}
            isSignedIn={isSignedIn}
            totalCount={totalCounts.counterparts}
          />
        )}

        {/* Deals Tab - Full deal list with screener (formerly Dashboard) */}
        {activeTab === "deals" && (
          <div>
            {/* Clip content for anonymous users — gradient fades into CTA below */}
            <div className={!isSignedIn ? 'relative max-h-[500px] overflow-hidden' : ''}>
            {/* Deals Layout — Left Sidebar + Main + Right Panel */}
            <div className="flex gap-8 items-start">

              {/* LEFT: Filter Sidebar (desktop only) */}
              <div className="flex-shrink-0 hidden lg:block w-80 sticky top-20 relative">
                <DealFilterSidebar
                  filters={screenerFilters}
                  onChange={setScreenerFilters}
                  deals={deals}
                  isDark={isDark}
                  smartSearchDealQuery={smartSearchDealQuery}
                  smartSearchDealIds={smartSearchDealIds}
                  setSmartSearchDealIds={setSmartSearchDealIds}
                  setSmartSearchDealQuery={setSmartSearchDealQuery}
                />
                {/* Intercept clicks for unregistered users */}
                {!isSignedIn && (
                  <div
                    className="absolute inset-0 cursor-pointer z-10"
                    onClick={() => openSignUp()}
                  />
                )}
              </div>

              {/* CENTER: Content */}
              <div className="flex-1 min-w-0 space-y-4">
                {/* Smart search results banner */}
                {smartSearchDealIds !== null && <DealsSearchResultsBanner />}

                {/* Deal Flow */}
                <DashboardDealsFeed
                  deals={deals}
                  counterparts={counterparts}
                  theme={theme}
                  themeClasses={themeClasses}
                  user={user}
                  dealStatuses={DEAL_STATUSES}
                  dealTechnologies={dealTechnologies}
                  dealTypes={DEAL_TYPES}
                  dealRoles={DEAL_ROLES}
                  onSelectCounterpart={viewPanelCounterpart}
                  filterCounterpart={dealFilterCounterpart}
                  screenerFilters={screenerFilters}
                  initialExpandedDealId={expandedDealId}
                  onExpandedDealChange={setExpandedDealId}
                  onShareDeal={setSharingDeal}
                  onViewDeal={viewDeal}
                  smartSearchDealIds={smartSearchDealIds}
                  searchQuery={searchQuery}
                  isSignedIn={isSignedIn}
                  onSignUpPrompt={() => openSignUp()}
                  dealCompareList={dealCompareList}
                  setDealCompareList={setDealCompareList}
                />
              </div>
            </div>

            {/* Mobile: floating filter button */}
            <button
              onClick={() => isSignedIn ? setDealSidebarOpen(true) : openSignUp()}
              className={`lg:hidden fixed bottom-24 right-5 z-40 w-14 h-14 rounded-2xl shadow-xl flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700 transition-colors`}
            >
              <Filter size={22} />
              {((screenerFilters.sectors?.length || 0) + (screenerFilters.geographies?.length || 0) + (screenerFilters.dealTypes?.length || 0) + (screenerFilters.statuses?.length || 0) + (screenerFilters.timePeriod !== 'all' ? 1 : 0)) > 0 && (
                <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                  {(screenerFilters.sectors?.length || 0) + (screenerFilters.geographies?.length || 0) + (screenerFilters.dealTypes?.length || 0) + (screenerFilters.statuses?.length || 0) + (screenerFilters.timePeriod !== 'all' ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Mobile: slide-over filter drawer */}
            {dealSidebarOpen && (
              <>
                <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setDealSidebarOpen(false)} />
                <div className={`lg:hidden fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] overflow-y-auto shadow-2xl ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                  <DealFilterSidebar
                    filters={screenerFilters}
                    onChange={setScreenerFilters}
                    onClose={() => setDealSidebarOpen(false)}
                    deals={deals}
                    isDark={isDark}
                    smartSearchDealQuery={smartSearchDealQuery}
                    smartSearchDealIds={smartSearchDealIds}
                    setSmartSearchDealIds={setSmartSearchDealIds}
                    setSmartSearchDealQuery={setSmartSearchDealQuery}
                  />
                </div>
              </>
            )}
            {/* Bottom gradient inside the clipped area */}
            {!isSignedIn && (
              <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent via-white/80 to-white dark:via-slate-900/80 dark:to-slate-900 z-10 pointer-events-none" />
            )}
            </div>{/* end clipping div for anonymous */}
            {/* CTA below clipped content — normal flow, always visible */}
            {!isSignedIn && (
              <div className="text-center py-10 -mt-4">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp size={24} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  Access thousands of infrastructure deals
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
                  Filter by sector, geography, deal type, and more. Get daily alerts for new deals matching your interests.
                </p>
                <button
                  onClick={() => setActiveTab('subscribe')}
                  className="px-6 py-3 bg-blue-700 text-white rounded-xl text-base font-semibold hover:bg-blue-800 transition-colors shadow-lg"
                >
                  Subscribe
                </button>
              </div>
            )}
          </div>
        )}

        {/* Projects Tab removed - counterparts-only platform */}

        {/* Counterparts Tab - Redesigned to match Dashboard */}
        {activeTab === "counterparts" && (
          <div>
            {/* Clip content for anonymous users — gradient fades into CTA below */}
            <div className={!isSignedIn ? 'relative max-h-[500px] overflow-hidden' : ''}>
            {counterparts.length === 0 ? (
              <div className="text-center py-20">
                <div className={`w-20 h-20 ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm`}>
                  <Users size={32} className={themeClasses.textMuted} />
                </div>
                <h3 className={`text-xl font-semibold ${themeClasses.text} mb-3`}>{t('noResults')}</h3>
                <p className={`text-base ${themeClasses.textMuted} mb-6 max-w-md mx-auto`}>{t('welcomeDesc')}</p>
                <button onClick={() => {
                  if (!canAddEntity()) {
                    setShowLimitModal(true);
                    return;
                  }
                  setEditingCounterpart(null);
                  setShowCounterpartModal(true);
                }}
                  className="inline-flex items-center gap-2.5 px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-base font-semibold transition-colors shadow-sm">
                  <Plus size={20} /> {t('addCounterpart')}
                </button>
              </div>
            ) : (
              /* Counterpart Layout — Left Sidebar + Main + Right Panel */
              <div className="flex gap-8 items-start">

                {/* LEFT: Filter Sidebar (desktop only) */}
                <div className="flex-shrink-0 hidden lg:block w-80 sticky top-20 relative">
                  <CounterpartFilterSidebar
                    filters={counterpartFilters}
                    onChange={setCounterpartFilters}
                    taxonomyOptions={taxonomyFilters}
                    counterparts={counterparts}
                    isDark={isDark}
                    smartSearchQuery={smartSearchQuery}
                    smartSearchCpIds={smartSearchCpIds}
                    setSmartSearchCpIds={setSmartSearchCpIds}
                    setSmartSearchQuery={setSmartSearchQuery}
                  />
                  {/* Intercept clicks for unregistered users */}
                  {!isSignedIn && (
                    <div
                      className="absolute inset-0 cursor-pointer z-10"
                      onClick={() => openSignUp()}
                    />
                  )}
                </div>

                {/* CENTER: Content */}
                <div className="flex-1 min-w-0 space-y-4">
                  {/* Smart search results banner */}
                  {smartSearchCpIds !== null && <SearchResultsBanner />}

                  {/* Counterpart Flow Card */}
                  <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} overflow-hidden shadow-sm`}>
                    {/* Header */}
                    <div className={`px-6 py-5 border-b ${themeClasses.border} ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-slate-700' : 'bg-white shadow-sm'}`}>
                            <Users size={20} className="text-blue-600" />
                          </div>
                          <div>
                            <h3 className={`text-xl font-semibold tracking-tight ${themeClasses.text}`}>Counterparts</h3>
                            <p className={`text-sm ${themeClasses.textMuted}`}>
                              {screenerResults.loading ? 'Loading...' : `${filteredCounterparts.length} results`}
                              {screenerResults.hasActiveFilters && !screenerResults.loading && (
                                <span className="text-emerald-600 ml-2">Filtered</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* View mode toggle */}
                          <div className={`flex items-center gap-1 p-1 rounded-xl ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'}`}>
                            <button
                              onClick={() => isSignedIn ? setCounterpartViewMode('list') : openSignUp()}
                              className={`p-2 rounded-lg transition-all ${counterpartViewMode === 'list' ? 'bg-blue-600 text-white shadow-sm' : `${themeClasses.textMuted} hover:bg-slate-200`}`}
                              title="List view"
                            >
                              <List size={16} />
                            </button>
                            <button
                              onClick={() => isSignedIn ? setCounterpartViewMode('treemap') : openSignUp()}
                              className={`p-2 rounded-lg transition-all ${counterpartViewMode === 'treemap' ? 'bg-blue-600 text-white shadow-sm' : `${themeClasses.textMuted} hover:bg-slate-200`}`}
                              title="Market Map"
                            >
                              <LayoutGrid size={16} />
                            </button>
                          </div>
                          {counterpartViewMode === 'list' && (
                            <select
                              value={counterpartSort.field}
                              onChange={(e) => setCounterpartSort({...counterpartSort, field: e.target.value})}
                              className={`text-sm px-3 py-2 rounded-xl border-2 font-medium ${theme === 'dark' ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-200 bg-white text-slate-700'} focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400`}
                            >
                              <option value="name">Name A-Z</option>
                              <option value="deals">Most Deals</option>
                              <option value="newest">Newest First</option>
                            </select>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Counterpart Rows - Compact horizontal format like Deal cards */}
                    {counterpartViewMode === 'list' ? (
                    <div className={`divide-y ${themeClasses.border}`}>
                      {/* Server-side filtering loading indicator */}
                      {screenerResults.loading && (
                        <div className="px-6 py-12 text-center">
                          <div className="inline-flex items-center gap-3 text-blue-600">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-base font-medium">Filtering counterparts...</span>
                          </div>
                        </div>
                      )}
                      {!screenerResults.loading && filteredCounterparts
                        .slice((counterpartsPage - 1) * ITEMS_PER_PAGE, counterpartsPage * ITEMS_PER_PAGE).map(counterpart => {
                        const prefs = counterpart.investmentPreferences;
                        const techs = counterpart.classifications?.technologies?.map(t => t.label) || getDimValues(prefs, 'technologies', 'sectors', 'assetClasses');
                        const regions = counterpart.classifications?.geographies?.map(g => g.label) || getDimValues(prefs, 'geographies');
                        // Get database deals linked via deal_counterparts
                        const dbDeals = deals.filter(d =>
                          (d.deal_counterparts || []).some(dc => dc.counterpart?.id === counterpart.id)
                        ).map(d => ({
                          name: d.headline || d.project_name || d.description?.substring(0, 50),
                          size: d.deal_value || d.capacity,
                          year: d.announcement_date ? new Date(d.announcement_date).getFullYear() : null,
                          id: d.id,
                          isDbDeal: true
                        }));
                        const recentDeals = dbDeals;
                        const totalDealCount = dbDeals.length;
                        const isExpanded = expandedCounterpartIds.has(counterpart.id);
                        const location = counterpart.headquarters
                          ? [counterpart.headquarters.city, counterpart.headquarters.country].filter(Boolean).join(', ')
                          : counterpart.location || '';

                        return (
                          <div
                            key={counterpart.id}
                            className={`transition-all duration-200 ${isExpanded ? (theme === 'dark' ? 'bg-slate-800/60' : 'bg-blue-50/60') : ''}`}
                          >
                            {/* Collapsed View - Enhanced Row */}
                            <div
                              className={`px-6 py-5 ${themeClasses.cardHover} cursor-pointer group`}
                              onClick={() => {
                                setExpandedCounterpartIds(prev => {
                                  const next = new Set(prev);
                                  if (next.has(counterpart.id)) next.delete(counterpart.id);
                                  else next.add(counterpart.id);
                                  return next;
                                });
                              }}
                            >
                              <div className="flex items-center gap-4">
                                {/* Compare checkbox */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCompareList(prev => prev.includes(counterpart.id)
                                      ? prev.filter(id => id !== counterpart.id)
                                      : prev.length < 3 ? [...prev, counterpart.id] : prev
                                    );
                                  }}
                                  className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                    compareList.includes(counterpart.id)
                                      ? 'bg-blue-600 border-blue-600 text-white'
                                      : theme === 'dark' ? 'border-slate-600 hover:border-blue-500' : 'border-slate-300 hover:border-blue-500'
                                  }`}
                                >
                                  {compareList.includes(counterpart.id) && <Check size={12} />}
                                </button>

                                {/* Avatar with favicon */}
                                <CounterpartAvatar counterpart={counterpart} size="md" />

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  {/* Title row with badges */}
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <h4 className={`text-base font-semibold tracking-tight ${themeClasses.text} group-hover:text-blue-600 transition-colors`}>
                                      {counterpart.name}
                                      {isFavorite('counterpart', counterpart.id) && <Star size={14} className="inline ml-1.5 text-amber-500 fill-amber-500" />}
                                    </h4>
                                    {/* Badges */}
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      {counterpart.category && (
                                        <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                          {counterpart.category}
                                        </span>
                                      )}
                                      {techs[0] && (
                                        <span className="text-xs px-2.5 py-1 rounded-lg font-medium bg-amber-100 text-amber-700">
                                          {techs[0]}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {/* Secondary info row */}
                                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                    {location && (
                                      <span className={`text-sm ${themeClasses.textMuted}`}>{location}</span>
                                    )}
                                    {techs.length > 1 && (
                                      <>
                                        <span className={`text-sm ${themeClasses.textMuted}`}>•</span>
                                        <span className={`text-sm ${themeClasses.textMuted}`}>{techs.slice(1, 3).join(', ')}{techs.length > 3 ? ` +${techs.length - 3}` : ''}</span>
                                      </>
                                    )}
                                    {totalDealCount > 0 && (
                                      <>
                                        <span className={`text-sm ${themeClasses.textMuted}`}>•</span>
                                        <span className="text-sm font-semibold text-blue-600">{totalDealCount} deal{totalDealCount !== 1 ? 's' : ''}</span>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Right side - Follow + Key metric + chevron */}
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <FollowButton
                                    targetType="counterpart"
                                    targetId={counterpart.id}
                                    label={counterpart.name}
                                    compact
                                  />
                                  {regions[0] && (
                                    <span className={`text-sm px-3 py-1.5 rounded-xl font-medium ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-blue-50 text-blue-700'}`}>
                                      {regions[0]}
                                    </span>
                                  )}
                                  <div className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'group-hover:bg-slate-700' : 'group-hover:bg-slate-100'}`}>
                                    <ChevronDown
                                      size={18}
                                      className={`${themeClasses.textMuted} transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {isExpanded && (
                              <div className="px-6 pb-6 pt-0">
                                <div className={`ml-14 pl-6 border-l-2 ${theme === 'dark' ? 'border-slate-600' : 'border-blue-200'}`}>
                                  <div className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'} border ${themeClasses.border} shadow-sm`}>
                                    {/* Summary */}
                                    {(counterpart.summary || prefs?.summary) && (
                                      <p className={`text-sm leading-relaxed ${themeClasses.text} mb-5`}>
                                        {counterpart.summary || prefs?.summary}
                                      </p>
                                    )}

                                    {/* Quick Stats */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                                      <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                        <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Category</p>
                                        <p className={`text-sm font-semibold ${themeClasses.text}`}>{counterpart.category || '—'}</p>
                                      </div>
                                      <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                        <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Technologies</p>
                                        <p className={`text-sm font-semibold ${themeClasses.text}`}>{techs.length || '—'}</p>
                                      </div>
                                      <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                        <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Regions</p>
                                        <p className={`text-sm font-semibold ${themeClasses.text}`}>{regions.length || '—'}</p>
                                      </div>
                                      <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                        <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Recent Deals</p>
                                        <p className={`text-sm font-semibold ${themeClasses.text}`}>{totalDealCount}</p>
                                      </div>
                                    </div>

                                    {/* Tags */}
                                    {(techs.length > 0 || regions.length > 0) && (
                                      <div className="flex flex-wrap gap-2 mb-5">
                                        {techs.map(t => (
                                          <span key={t} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-xl text-sm font-medium">{t}</span>
                                        ))}
                                        {regions.map(r => (
                                          <span key={r} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-xl text-sm font-medium">{r}</span>
                                        ))}
                                      </div>
                                    )}

                                    {/* Recent Deals */}
                                    {recentDeals.length > 0 && (
                                      <div className="mb-5">
                                        <p className={`text-sm font-semibold ${themeClasses.textMuted} mb-3`}>Recent Deals</p>
                                        <div className="space-y-1.5">
                                          {recentDeals.slice(0, 3).map((deal, idx) => (
                                            <button
                                              key={deal.id || idx}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // For database deals, get the full deal object
                                                if (deal.isDbDeal && deal.id) {
                                                  const fullDeal = deals.find(d => d.id === deal.id);
                                                  if (fullDeal) {
                                                    setPanelDeal(fullDeal);
                                                    setPanelDealSource(counterpart);
                                                    return;
                                                  }
                                                }
                                                setPanelDeal(deal);
                                                setPanelDealSource(counterpart);
                                              }}
                                              className={`w-full flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left group`}
                                            >
                                              <span className={`text-sm font-medium truncate ${themeClasses.text} group-hover:text-blue-600`}>{deal.name || deal.project || 'Unnamed Deal'}</span>
                                              <span className={`text-sm ${themeClasses.textMuted} ml-3 flex-shrink-0`}>
                                                {deal.size || deal.value ? formatDealValue({ amount: deal.size || deal.value, unit: 'M', currency: 'USD' }) : ''}{deal.year ? ` • ${deal.year}` : ''}
                                              </span>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                                      {(counterpart.websiteUrl || prefs?.website) && (
                                        <a
                                          href={counterpart.websiteUrl || prefs?.website}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                                        >
                                          <ExternalLink size={14} /> Website
                                        </a>
                                      )}
                                      <button
                                        onClick={(e) => { e.stopPropagation(); viewCounterpart(counterpart); trackRecentItem('counterpart', counterpart.id); }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                                      >
                                        View Full Profile
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    ) : null}
                  </div>

                  {/* Market Map View - shown when not in list mode */}
                  {counterpartViewMode !== 'list' && (
                    <MarketMapView
                      counterpartsData={filteredCounterparts}
                      onSelectCounterpart={(cp) => { viewCounterpart(cp); trackRecentItem('counterpart', cp.id); }}
                    />
                  )}

                  {counterpartViewMode === 'list' && (
                  <Pagination
                    currentPage={counterpartsPage}
                    totalItems={filteredCounterparts.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={(page) => setCounterpartsPage(page)}
                    itemName="counterparts"
                  />
                  )}
                </div>

                {/* RIGHT COLUMN - Deal Panel (only shown when deal selected) */}
                {panelDeal && (
                <div className="w-96 flex-shrink-0 hidden xl:block space-y-4">
                  <DealPanel
                    deal={panelDeal}
                    sourceCounterpart={panelDealSource}
                    panelEnrichment={panelDealEnrichment}
                    onClose={() => { setPanelDeal(null); setPanelDealSource(null); }}
                    onViewFull={() => {
                      // Open full Deal Detail Page
                      viewDeal(panelDeal);
                      setPanelDeal(null);
                      setPanelDealSource(null);
                    }}
                  />
                </div>
                )}

              </div>
            )}

            {/* Mobile: floating filter button */}
            <button
              onClick={() => isSignedIn ? setCpSidebarOpen(true) : openSignUp()}
              className={`lg:hidden fixed bottom-24 right-5 z-40 w-14 h-14 rounded-2xl shadow-xl flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700 transition-colors`}
            >
              <Filter size={20} />
              {((counterpartFilters.counterpartTypes?.length || 0) + (counterpartFilters.technologies?.length || 0) + (counterpartFilters.regions?.length || 0) + (counterpartFilters.stages?.length || 0) + (counterpartFilters.type !== 'all' ? 1 : 0)) > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {(counterpartFilters.counterpartTypes?.length || 0) + (counterpartFilters.technologies?.length || 0) + (counterpartFilters.regions?.length || 0) + (counterpartFilters.stages?.length || 0) + (counterpartFilters.type !== 'all' ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Mobile: slide-over filter drawer */}
            {cpSidebarOpen && (
              <>
                <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setCpSidebarOpen(false)} />
                <div className={`lg:hidden fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] overflow-y-auto shadow-2xl ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                  <CounterpartFilterSidebar
                    filters={counterpartFilters}
                    onChange={setCounterpartFilters}
                    taxonomyOptions={taxonomyFilters}
                    onClose={() => setCpSidebarOpen(false)}
                    counterparts={counterparts}
                    isDark={isDark}
                    smartSearchQuery={smartSearchQuery}
                    smartSearchCpIds={smartSearchCpIds}
                    setSmartSearchCpIds={setSmartSearchCpIds}
                    setSmartSearchQuery={setSmartSearchQuery}
                  />
                </div>
              </>
            )}
            {/* Bottom gradient inside the clipped area */}
            {!isSignedIn && (
              <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent via-white/80 to-white dark:via-slate-900/80 dark:to-slate-900 z-10 pointer-events-none" />
            )}
            </div>{/* end clipping div for anonymous */}
            {/* CTA below clipped content — normal flow, always visible */}
            {!isSignedIn && (
              <div className="text-center py-10 -mt-4">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users size={24} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  Browse thousands of companies &amp; investors
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
                  Filter by type, sector, geography, and more. Follow companies to get daily deal alerts.
                </p>
                <button
                  onClick={() => setActiveTab('subscribe')}
                  className="px-6 py-3 bg-blue-700 text-white rounded-xl text-base font-semibold hover:bg-blue-800 transition-colors shadow-lg"
                >
                  Subscribe
                </button>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab removed - counterparts-only platform */}

        {/* Screener Tab merged into Counterparts */}

        {/* Subscribe Tab */}
        {activeTab === "subscribe" && (
          <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={24} /></div>}>
            <SubscribePage
              isSignedIn={isSignedIn}
              userTier={userTier}
              isAdmin={isAdmin}
              user={user}
              theme={theme}
              setActiveTab={setActiveTab}
            />
          </Suspense>
        )}

        {/* My Alerts Tab */}
        {activeTab === "alerts" && isSignedIn && (
          <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={24} /></div>}>
            <ManageAlerts theme={theme} user={user} setActiveTab={setActiveTab} />
          </Suspense>
        )}

        {/* Search Demo Tab — yellow-highlighted matches + entity rail. 2026-05-23. */}
        {activeTab === "search-demo" && (
          <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={24} /></div>}>
            <SearchDemoPage
              theme={theme}
              counterparts={counterparts}
              deals={deals}
              onSelectCounterpart={(cp) => { setSelectedCounterpart(cp); setActiveTab('counterparts'); }}
              onSelectDeal={(d) => { setSelectedDeal(d); setActiveTab('deals'); }}
            />
          </Suspense>
        )}

        {/* Billing Tab */}
        {activeTab === "billing" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{t('billingTitle')}</h2>
              <p className="text-sm text-slate-600">{t('billingDesc')}</p>
            </div>

            {/* Current Plan Card */}
            <div className={`bg-gradient-to-br ${
              isProUser ? 'from-blue-800 to-blue-700' : 'from-teal-600 to-teal-500'
            } rounded-2xl p-6 text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">{t('currentPlan')}</p>
                  <p className="text-3xl font-bold mt-1">{isAdmin ? 'Unlimited' : (SUBSCRIPTION_TIERS[userTier] || SUBSCRIPTION_TIERS.basic).name}</p>
                  <p className="text-white/80 text-sm mt-1">
                    {isAdmin ? 'Admin access — no limits' : isProUser ? 'Unlimited access' : 'Basic plan — 50 deal + 50 counterpart views/month'}
                  </p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  {isProUser || isAdmin ? <Crown size={32} /> : <Zap size={32} />}
                </div>
              </div>

              {/* Usage Bars — shown for basic tier */}
              {isBasicUser && (() => {
                const stats = getUsageStats();
                return (
                  <div className="mt-6 space-y-3">
                    {[
                      { label: 'Deal views', ...stats.dealViews },
                      { label: 'Counterpart views', ...stats.cpViews },
                      { label: 'Searches (daily)', used: stats.searches.used, limit: stats.searches.limit },
                    ].map(({ label, used, limit }) => {
                      const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
                      return (
                        <div key={label}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-white/80">{label}</span>
                            <span className="font-semibold">{used} / {limit}</span>
                          </div>
                          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${pct >= 90 ? 'bg-red-400' : pct >= 75 ? 'bg-amber-300' : 'bg-white'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Pricing Cards — Basic + Pro */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 text-center">{t('choosePlan')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {/* Basic Tier */}
                <div className={`relative bg-white rounded-2xl border-2 p-6 transition-all ${
                  isBasicUser ? 'border-teal-500 ring-2 ring-teal-500/20' : 'border-slate-200 hover:border-slate-300'
                }`}>
                  {isBasicUser && (
                    <div className="absolute -top-3 right-4">
                      <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">Current Plan</span>
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-teal-500 to-teal-400 flex items-center justify-center text-white mb-3">
                      <Zap size={24} />
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">Basic</h4>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-slate-900">$20</span>
                      <span className="text-slate-600">/mo</span>
                    </div>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-teal-500" /><span className="text-slate-700">Unlimited deals in Live Feed</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-teal-500" /><span className="text-slate-700">50 deal views/month</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-teal-500" /><span className="text-slate-700">50 counterpart views/month</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-teal-500" /><span className="text-slate-700">10 searches/day</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-teal-500" /><span className="text-slate-700">Full content access</span></div>
                  </div>
                  {isBasicUser ? (
                    <button disabled className="w-full py-2.5 px-4 bg-slate-100 text-slate-500 rounded-xl text-sm font-medium cursor-not-allowed">
                      Current Plan
                    </button>
                  ) : (
                    <a
                      href={`${SUBSCRIPTION_TIERS.basic.paymentLink}?client_reference_id=${user?.id}&prefilled_email=${encodeURIComponent(user?.primaryEmailAddress?.emailAddress || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-2.5 px-4 bg-teal-500 text-white rounded-xl text-sm font-medium text-center hover:bg-teal-600 transition-colors"
                    >
                      Switch to Basic
                    </a>
                  )}
                </div>

                {/* Pro Tier */}
                <div className={`relative bg-white rounded-2xl border-2 p-6 transition-all ${
                  isProUser ? 'border-blue-700 ring-2 ring-blue-700/20' : 'border-slate-200 hover:border-slate-300'
                }`}>
                  {!isProUser && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-blue-700 text-white text-xs font-semibold rounded-full">Recommended</span>
                    </div>
                  )}
                  {isProUser && (
                    <div className="absolute -top-3 right-4">
                      <span className="px-3 py-1 bg-blue-700 text-white text-xs font-semibold rounded-full">Current Plan</span>
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-blue-800 to-blue-700 flex items-center justify-center text-white mb-3">
                      <Crown size={24} />
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">Pro</h4>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-slate-900">$200</span>
                      <span className="text-slate-600">/mo</span>
                    </div>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-blue-700" /><span className="text-slate-700">Unlimited deals in Live Feed</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-blue-700" /><span className="text-slate-700">Unlimited deal views</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-blue-700" /><span className="text-slate-700">Unlimited counterpart views</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-blue-700" /><span className="text-slate-700">Unlimited smart searches</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-blue-700" /><span className="text-slate-700">Tearsheet PDF exports</span></div>
                    <div className="flex items-center gap-2 text-sm"><CheckCircle size={16} className="text-blue-700" /><span className="text-slate-700">Customer support</span></div>
                  </div>
                  {isProUser ? (
                    <button disabled className="w-full py-2.5 px-4 bg-slate-100 text-slate-500 rounded-xl text-sm font-medium cursor-not-allowed">
                      Current Plan
                    </button>
                  ) : (
                    <a
                      href={`${SUBSCRIPTION_TIERS.pro.paymentLink}?client_reference_id=${user?.id}&prefilled_email=${encodeURIComponent(user?.primaryEmailAddress?.emailAddress || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-2.5 px-4 bg-blue-700 text-white rounded-xl text-sm font-medium text-center hover:bg-blue-800 transition-colors"
                    >
                      Upgrade to Pro
                    </a>
                  )}
                </div>
              </div>

              {/* Contract terms + multi-seat */}
              <div className="mt-4 text-center space-y-1">
                <p className="text-sm text-slate-500">Monthly contracts — no long-term commitment. Cancel anytime.</p>
                <p className="text-sm text-slate-600">
                  Need additional seats for your team?{' '}
                  <a href="#contact" onClick={contactClick('Team seats — Global Infrastructure Sherpa')} className="text-blue-800 hover:text-blue-700 font-medium">
                    Contact us
                  </a>
                  {' '}for multi-user pricing.
                </p>
              </div>
            </div>

            {/* Help Section */}
            <div className="bg-slate-100 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-3">{t('needHelpBilling')}</h3>
              <p className="text-sm text-slate-600 mb-4">
                Manage your subscription, update payment methods, or cancel anytime via the Stripe billing portal.
              </p>
              <div className="flex flex-wrap gap-3">
                {isSignedIn && (
                  <button
                    onClick={async () => {
                      try {
                        // Customer is derived server-side from the caller's Clerk identity.
                        // authFetch (not fetch) — the endpoint requires authentication.
                        const res = await authFetch('/api/create-portal-session', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                        });
                        const data = await res.json();
                        if (data.url) {
                          window.open(data.url, '_blank');
                        } else {
                          alert(data.error || `Could not open billing portal. Please contact ${CONTACT_DISPLAY}`);
                        }
                      } catch {
                        alert(`Could not open billing portal. Please contact ${CONTACT_DISPLAY}`);
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-xl text-sm font-medium hover:bg-blue-800 transition-colors"
                  >
                    <CreditCard size={16} />
                    Manage Subscription
                  </button>
                )}
                <a
                  href="#contact"
                  onClick={contactClick('Support — Global Infrastructure Sherpa')}
                  className="inline-flex items-center gap-2 text-sm text-blue-800 hover:text-blue-700 font-medium"
                >
                  <Mail size={16} />
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Admin Panel - Only visible to admins */}
        {activeTab === "admin" && (user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net')) && (
          <div className="space-y-6">
            {/* Header with action buttons */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className={`text-xl font-semibold ${themeClasses.text}`}>Admin Panel</h2>
                <p className={`text-sm ${themeClasses.textMuted}`}>Manage deals, counterparts, and content</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!canAddEntity()) {
                      setShowLimitModal(true);
                      return;
                    }
                    setEditingCounterpart(null);
                    setShowCounterpartModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700"
                >
                  <Plus size={16} />
                  New Counterpart
                </button>
                <button
                  onClick={() => setShowDealModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-xl text-sm font-medium hover:bg-blue-800"
                >
                  <Plus size={16} />
                  Add Deal
                </button>
              </div>
            </div>

            {/* Admin Tabs */}
            <div className={`flex flex-wrap gap-1 p-1 rounded-xl ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-rule)]'}`}>
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'discover', label: 'Discover', icon: Search },
                { id: 'submissions', label: 'Submissions', icon: Bell },
                { id: 'deals', label: 'Deals', icon: FileText },
                { id: 'counterparts', label: 'Counterparts', icon: Building2 },
                { id: 'seo', label: 'SEO Pages', icon: Globe },
                { id: 'newsletter', label: 'Newsletter', icon: Newspaper },
                { id: 'marketing', label: 'Marketing', icon: Mail },
                { id: 'crm', label: 'CRM', icon: Briefcase },
                { id: 'agents', label: 'Agents', icon: Sparkles },
                { id: 'database', label: 'Database', icon: Database },
                { id: 'visitors', label: 'Visitors', icon: Activity },
                { id: 'settings', label: 'Settings', icon: Settings },
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setAdminTab(tab.id)}
                    className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      adminTab === tab.id
                        ? (theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-white' : 'bg-[color:var(--gis-panel)] text-[color:var(--gis-ink)] shadow-sm')
                        : (theme === 'dark' ? 'text-[color:var(--gis-dim)] hover:text-slate-200' : 'text-[color:var(--gis-body)] hover:text-[color:var(--gis-ink)]')
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Discover Tab - AI-powered deal discovery */}
            {/* Visitor Log — its own admin tab */}
            {adminTab === 'visitors' && (() => {
              const selfIp = visitorLogs?.selfIp;
              const filt = arr => (hideSelf && selfIp ? (arr || []).filter(x => x.ip !== selfIp) : (arr || []));
              const vrows = filt(visitorLogs?.rows);
              const vbyipRaw = filt(visitorLogs?.byIp);
              const vbots = vrows.filter(r => r.kind !== 'browser').length;
              // Sort the "by IP" table by the clicked column.
              const ipLookupUrl = (ip) => `https://whatismyipaddress.com/ip/${encodeURIComponent(ip)}`;
              const vwSortVal = (v, key) => {
                switch (key) {
                  case 'ip': return (v.ip || '').split('.').map(n => String(n).padStart(3, '0')).join('.');
                  case 'location': return [v.city, v.country].filter(Boolean).join(', ').toLowerCase();
                  case 'visits': return v.visits || 0;
                  case 'device': return [v.device, v.os, v.browser].filter(x => x && x !== '—').join(' · ').toLowerCase();
                  case 'type': return v.kind || '';
                  case 'lastSeen': return new Date(v.lastSeen).getTime() || 0;
                  default: return 0;
                }
              };
              const vbyip = [...vbyipRaw].sort((a, b) => {
                const av = vwSortVal(a, vwSort.key), bv = vwSortVal(b, vwSort.key);
                const cmp = av < bv ? -1 : av > bv ? 1 : 0;
                return vwSort.dir === 'asc' ? cmp : -cmp;
              });
              const toggleVwSort = (key) => setVwSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'ip' || key === 'location' || key === 'type' ? 'asc' : 'desc' });
              const sortArrow = (key) => vwSort.key === key ? (vwSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
              return (
              <div className={`${themeClasses.card} rounded-xl border border-indigo-200 dark:border-indigo-900/40 p-6`}>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                      <Globe size={18} className="text-indigo-500" />
                      Visitor Log
                    </h3>
                    <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                      Every page view, server-side — IP, geo, page, where they came from, OS, and bot vs human{visitorLogs?.days ? ` · last ${visitorLogs.days}d` : ''}.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className={`flex items-center gap-1.5 text-xs cursor-pointer select-none ${themeClasses.textMuted}`}>
                      <input
                        type="checkbox"
                        checked={hideSelf}
                        onChange={(e) => { const v = e.target.checked; setHideSelf(v); try { localStorage.setItem('gis_vw_hide_self', v ? '1' : '0'); } catch {} }}
                      />
                      Exclude me{visitorLogs?.selfIp ? ` (${visitorLogs.selfIp})` : ''}
                    </label>
                    <button
                      onClick={loadVisitorLogs}
                      disabled={visitorLoading}
                      className="text-xs px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                    >
                      {visitorLoading ? 'Loading…' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {visitorLogs && !visitorLogs.error && (
                  <div className="flex items-center gap-3 mb-4 text-xs flex-wrap">
                    <span className={themeClasses.textMuted}><b className={themeClasses.text}>{vrows.length}</b> views</span>
                    <span className={themeClasses.textMuted}><b className={themeClasses.text}>{new Set(vrows.map(r => r.ip)).size}</b> distinct IPs</span>
                    <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">{vbots} bot/automated</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">{vrows.length - vbots} browser</span>
                    {hideSelf && <span className="text-indigo-500">· your visits hidden</span>}
                  </div>
                )}

                {visitorLogs?.error ? (
                  <div className="text-sm text-rose-600 py-8 text-center">{visitorLogs.error}</div>
                ) : !visitorLogs ? (
                  <div className={`text-sm ${themeClasses.textMuted} py-8 text-center`}>Loading…</div>
                ) : !vrows.length ? (
                  <div className={`text-sm ${themeClasses.textMuted} py-8 text-center`}>
                    {hideSelf && visitorLogs.rows?.length ? 'Only your own visits so far — hidden by “Exclude me”.' : 'No visits logged yet — they\'ll appear here as people load the site.'}
                  </div>
                ) : (
                  <>
                    {vbyip.length > 0 && (
                      <div className="mb-5">
                        <div className={`text-xs font-semibold ${themeClasses.text} mb-1`}>Recurring visitors (by IP)</div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className={`${themeClasses.textMuted} text-left select-none`}>
                                {[
                                  { key: 'ip', label: 'IP' },
                                  { key: 'location', label: 'Location' },
                                  { key: 'visits', label: 'Visits' },
                                  { key: 'device', label: 'Device / OS / Browser' },
                                  { key: 'type', label: 'Type' },
                                  { key: 'lastSeen', label: 'Last seen' },
                                ].map(col => (
                                  <th
                                    key={col.key}
                                    onClick={() => toggleVwSort(col.key)}
                                    className={`py-1 pr-3 font-medium cursor-pointer hover:opacity-70 whitespace-nowrap ${vwSort.key === col.key ? themeClasses.text : ''}`}
                                    title="Click to sort"
                                  >
                                    {col.label}{sortArrow(col.key)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {vbyip.map(v => (
                                <tr key={v.ip} className={`border-t ${themeClasses.border} align-top`}>
                                  <td className="py-1.5 pr-3 font-mono whitespace-nowrap">
                                    <a href={ipLookupUrl(v.ip)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline" title="Look up this IP">{v.ip}</a>
                                  </td>
                                  <td className="py-1.5 pr-3 whitespace-nowrap">{[v.city, v.country].filter(Boolean).join(', ') || '—'}</td>
                                  <td className={`py-1.5 pr-3 font-semibold ${themeClasses.text}`}>{v.visits}</td>
                                  <td className="py-1.5 pr-3 whitespace-nowrap">{[v.device, v.os, v.browser].filter(x => x && x !== '—').join(' · ') || '—'}</td>
                                  <td className="py-1.5 pr-3 whitespace-nowrap">
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${v.kind === 'browser' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'}`}>
                                      {v.kind === 'browser' ? 'browser' : v.kind === 'no-ua' ? 'no UA' : 'bot'}
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(v.lastSeen).toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className={`text-xs font-semibold ${themeClasses.text} mb-1`}>Recent activity</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className={`${themeClasses.textMuted} text-left`}>
                            <th className="py-1 pr-3 font-medium whitespace-nowrap">Time</th>
                            <th className="py-1 pr-3 font-medium">Location</th>
                            <th className="py-1 pr-3 font-medium">IP</th>
                            <th className="py-1 pr-3 font-medium">Page</th>
                            <th className="py-1 pr-3 font-medium">Came from</th>
                            <th className="py-1 pr-3 font-medium">OS / Browser</th>
                            <th className="py-1 pr-3 font-medium">Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vrows.map(r => (
                            <tr key={r.id} className={`border-t ${themeClasses.border} align-top`}>
                              <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                              <td className="py-1.5 pr-3 whitespace-nowrap">{[r.city, r.country].filter(Boolean).join(', ') || '—'}</td>
                              <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{r.ip ? <a href={ipLookupUrl(r.ip)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline" title="Look up this IP">{r.ip}</a> : '—'}</td>
                              <td className="py-1.5 pr-3 max-w-[180px] truncate" title={r.path}>{r.path || '—'}</td>
                              <td className="py-1.5 pr-3 max-w-[160px] truncate" title={r.referer}>{r.referer || 'direct'}</td>
                              <td className="py-1.5 pr-3 whitespace-nowrap">{[r.os, r.browser].filter(x => x && x !== '—').join(' · ') || '—'}</td>
                              <td className="py-1.5 pr-3 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${r.kind === 'browser' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'}`}>
                                  {r.kind === 'browser' ? 'browser' : r.kind === 'no-ua' ? 'no UA' : 'bot'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              );
            })()}

            {adminTab === 'discover' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${themeClasses.text}`}>AI Deal Discovery</h3>
                    <p className={`text-sm ${themeClasses.textMuted}`}>Search for recent infrastructure deals using AI. Verify sources before importing.</p>
                  </div>
                </div>

                {/* Search Input */}
                <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-4`}>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={discoverQuery}
                      onChange={(e) => setDiscoverQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && discoverQuery.trim()) {
                          handleDiscoverSearch(discoverQuery);
                        }
                      }}
                      placeholder="Search for deals, e.g., 'latest solar PPA deals' or 'renewable energy acquisitions January 2026'"
                      className={`flex-1 px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                    <button
                      onClick={() => handleDiscoverSearch(discoverQuery)}
                      disabled={discoverLoading || !discoverQuery.trim()}
                      className="px-6 py-3 bg-blue-700 text-white rounded-xl font-medium hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {discoverLoading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Analyzing thousands of companies and deals...
                        </>
                      ) : (
                        <>
                          <Search size={18} />
                          Search
                        </>
                      )}
                    </button>
                  </div>

                  {/* Quick Search Templates */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`text-xs ${themeClasses.textMuted}`}>Quick searches:</span>
                    {[
                      { label: 'PPA Deals', query: 'latest power purchase agreement PPA deals signed January 2026' },
                      { label: 'Solar Acquisitions', query: 'recent solar energy acquisition investment deals 2026' },
                      { label: 'Wind Financing', query: 'wind energy project financing deals January 2026' },
                      { label: 'Battery Storage', query: 'battery storage energy deals acquisitions 2026' },
                      { label: 'Data Centers', query: 'data center infrastructure investment deals 2026' },
                      { label: 'Gas Power', query: 'gas-fired power plant financing deals 2026' },
                    ].map((template, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setDiscoverQuery(template.query);
                          handleDiscoverSearch(template.query);
                        }}
                        disabled={discoverLoading}
                        className={`px-3 py-1.5 text-xs rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] hover:bg-[color:var(--gis-rule)]'} disabled:opacity-50`}
                      >
                        {template.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Error Message */}
                {discoverError && (
                  <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'} border`}>
                    <p className="text-red-600 flex items-center gap-2">
                      <AlertCircle size={16} /> {discoverError}
                    </p>
                  </div>
                )}

                {/* Results */}
                {discoverResults.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm ${themeClasses.textMuted}`}>
                        Found {discoverResults.length} deals •
                        <span className="ml-1">{selectedDiscoverDeals.size} selected</span>
                      </p>
                      {selectedDiscoverDeals.size > 0 && (
                        <button
                          onClick={handleBulkImportDiscover}
                          disabled={importingDeal}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          <Download size={14} />
                          Import Selected ({selectedDiscoverDeals.size})
                        </button>
                      )}
                    </div>

                    {/* Sort by date (newest first) and check for duplicates */}
                    {[...discoverResults]
                      .map((deal, originalIdx) => ({ deal, originalIdx }))
                      .sort((a, b) => {
                        if (!a.deal.date && !b.deal.date) return 0;
                        if (!a.deal.date) return 1;
                        if (!b.deal.date) return -1;
                        return new Date(b.deal.date) - new Date(a.deal.date);
                      })
                      .map(({ deal, originalIdx }) => {
                        const idx = originalIdx; // Use original index for selection tracking
                        // Check if deal already exists in database (by headline similarity)
                        const isInDb = deals.some(dbDeal => {
                          const dbHeadline = (dbDeal.headline || dbDeal.description || '').toLowerCase();
                          const discoverHeadline = (deal.headline || '').toLowerCase();
                          // Check for significant overlap (at least 50 chars match or 60% similarity)
                          if (dbHeadline.length < 20 || discoverHeadline.length < 20) return false;
                          return dbHeadline.includes(discoverHeadline.slice(0, 50)) ||
                                 discoverHeadline.includes(dbHeadline.slice(0, 50));
                        });

                        return (
                      <div
                        key={idx}
                        className={`relative ${themeClasses.card} rounded-xl border ${themeClasses.border} p-4 ${selectedDiscoverDeals.has(idx) ? 'ring-2 ring-blue-500' : ''} ${isInDb ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Already in DB badge */}
                          {isInDb && (
                            <span className="absolute top-2 right-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                              In Database
                            </span>
                          )}
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedDiscoverDeals.has(idx)}
                            onChange={(e) => {
                              const newSet = new Set(selectedDiscoverDeals);
                              if (e.target.checked) {
                                newSet.add(idx);
                              } else {
                                newSet.delete(idx);
                              }
                              setSelectedDiscoverDeals(newSet);
                            }}
                            className="mt-1 w-4 h-4 rounded border-[color:var(--gis-rule)] text-blue-600 focus:ring-blue-500"
                          />

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-medium ${themeClasses.text}`}>{deal.headline}</h4>

                            {/* Meta row */}
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {deal.date && (
                                <span className={`text-xs px-2 py-0.5 rounded ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)]' : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)]'}`}>
                                  {deal.date}
                                </span>
                              )}
                              {(Array.isArray(deal.technology) ? deal.technology : [deal.technology]).filter(Boolean).map(t => (
                                <span key={t} className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                                  {fmtLabel(t)}
                                </span>
                              ))}
                              {deal.deal_type && (
                                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                  {fmtLabel(deal.deal_type)}
                                </span>
                              )}
                              {deal.location && (
                                <span className={`text-xs ${themeClasses.textMuted}`}>
                                  {deal.location}
                                </span>
                              )}
                            </div>

                            {/* Value and Capacity */}
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              {deal.value && (
                                <span className="text-sm font-semibold text-green-600">{deal.value}</span>
                              )}
                              {deal.capacity && (
                                <span className="text-sm font-medium text-blue-600">{deal.capacity}</span>
                              )}
                            </div>

                            {/* Parties */}
                            {deal.parties && deal.parties.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {deal.parties.map((party, pIdx) => (
                                  <span
                                    key={pIdx}
                                    className={`text-xs px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)]' : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)]'}`}
                                  >
                                    {party.name} ({party.role})
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Summary */}
                            {deal.summary && (
                              <p className={`text-sm ${themeClasses.textMuted} mt-2`}>{deal.summary}</p>
                            )}

                            {/* Source */}
                            {deal.source_url && (
                              <div className="flex items-center gap-2 mt-3">
                                <a
                                  href={deal.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                >
                                  <ExternalLink size={12} />
                                  {deal.source_name || new URL(deal.source_url).hostname}
                                </a>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            <a
                              href={deal.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`px-3 py-1.5 text-xs rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] hover:bg-[color:var(--gis-rule)]'} flex items-center gap-1`}
                            >
                              <ExternalLink size={12} />
                              Verify
                            </a>
                            <button
                              onClick={() => handleImportDiscoverDeal(deal)}
                              disabled={importingDeal === idx}
                              className="px-3 py-1.5 text-xs rounded-lg bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 flex items-center gap-1"
                            >
                              {importingDeal === idx ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Download size={12} />
                              )}
                              Import
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}

                {/* Empty State */}
                {!discoverLoading && discoverResults.length === 0 && !discoverError && (
                  <div className={`text-center py-12 ${themeClasses.card} rounded-xl border ${themeClasses.border}`}>
                    <Search size={48} className={`mx-auto ${themeClasses.textMuted} mb-4`} />
                    <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>Discover Infrastructure Deals</h3>
                    <p className={`text-sm ${themeClasses.textMuted} max-w-md mx-auto`}>
                      Use AI to search for recent deals across the web. Enter a search query or use one of the quick search templates above.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Submissions Tab - Review crawled deals */}
            {adminTab === 'submissions' && (
              <DealSubmissions
                theme={theme}
                themeClasses={themeClasses}
                deals={deals}
                setDeals={setDeals}
                dealSubmissions={dealSubmissions}
                setDealSubmissions={setDealSubmissions}
                loadingSubmissions={loadingSubmissions}
                setLoadingSubmissions={setLoadingSubmissions}
                selectedSubmissions={selectedSubmissions}
                setSelectedSubmissions={setSelectedSubmissions}
                submissionAgeDays={submissionAgeDays}
                setSubmissionAgeDays={setSubmissionAgeDays}
                submissionSearch={submissionSearch}
                setSubmissionSearch={setSubmissionSearch}
                crawlSources={crawlSources}
                setCrawlSources={setCrawlSources}
                editingSource={editingSource}
                setEditingSource={setEditingSource}
                crawlKeywords={crawlKeywords}
                setCrawlKeywords={setCrawlKeywords}
                editingKeywords={editingKeywords}
                setEditingKeywords={setEditingKeywords}
                keywordsText={keywordsText}
                setKeywordsText={setKeywordsText}
                counterparts={counterparts}
              />
            )}

            {/* Overview Tab */}
            {adminTab === 'overview' && (
              <>
            <Suspense fallback={null}><CrmDueCard theme={theme} onOpen={() => setAdminTab('crm')} /></Suspense>

            {/* Reset Data Confirmation Modal */}
            {showResetConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} p-6 max-w-md w-full shadow-2xl`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <AlertCircle size={24} className="text-red-600" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${themeClasses.text}`}>Reset All Data?</h3>
                      <p className={`text-sm ${themeClasses.textMuted}`}>This action cannot be undone</p>
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl mb-4 ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-sm ${themeClasses.text} mb-2`}>This will:</p>
                    <ul className={`text-sm ${themeClasses.textMuted} space-y-1`}>
                      <li>• Download a backup JSON file first</li>
                      <li>• Delete all {deals.length} deals</li>
                      <li>• Delete all {counterparts.length} counterparts</li>
                      <li>• Clear all linked data</li>
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowResetConfirm(false)}
                      disabled={resettingData}
                      className={`flex-1 px-4 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium hover:bg-[color:var(--gis-rule)] dark:hover:bg-[color:var(--gis-panel)] disabled:opacity-50`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetData}
                      disabled={resettingData}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {resettingData ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          Reset Everything
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <div className={`${themeClasses.card} rounded-xl p-4 border ${themeClasses.border}`}>
                <p className={`text-sm ${themeClasses.textMuted}`}>Total Deals</p>
                <p className={`text-2xl font-bold ${themeClasses.text}`}>{deals.length}</p>
              </div>
              <div className={`${themeClasses.card} rounded-xl p-4 border ${themeClasses.border}`}>
                <p className={`text-sm ${themeClasses.textMuted}`}>Counterparts</p>
                <p className={`text-2xl font-bold ${themeClasses.text}`}>{counterparts.length}</p>
              </div>
              <div className={`${themeClasses.card} rounded-xl p-4 border ${themeClasses.border}`}>
                <p className={`text-sm ${themeClasses.textMuted}`}>Needs Review</p>
                <p className={`text-2xl font-bold text-amber-600`}>
                  {counterparts.filter(c => c.needsReview).length}
                </p>
              </div>
              <div className={`${themeClasses.card} rounded-xl p-4 border ${themeClasses.border}`}>
                <p className={`text-sm ${themeClasses.textMuted}`}>AI Extracted</p>
                <p className={`text-2xl font-bold text-green-600`}>
                  {deals.filter(d => d.extractedData).length}
                </p>
              </div>
            </div>

            {/* Quality Monitor Card */}
            <div className={`${themeClasses.card} rounded-xl border-2 border-red-300 dark:border-red-600 p-6 mb-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <AlertCircle size={18} className="text-red-500" />
                    Quality Monitor
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Data quality, API health, pipeline safety, and code integrity — alerts to contact@globalinfrastructuresherpa.com
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {qualityLastChecked && (
                    <span className={`text-xs ${themeClasses.textMuted}`}>
                      Last: {new Date(qualityLastChecked).toLocaleString()}
                    </span>
                  )}
                  <button
                    onClick={async () => {
                      setQualityRunning(true);
                      try {
                        const res = await authFetch('/api/quality-check', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setQualityReport(data);
                          setQualityLastChecked(data.checkedAt);
                        }
                      } catch (e) {
                        console.error('Quality check failed:', e);
                      }
                      setQualityRunning(false);
                    }}
                    disabled={qualityRunning}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {qualityRunning ? <><Loader2 size={14} className="animate-spin" /> Checking...</> : <><Search size={14} /> Run Quality Check + Email</>}
                  </button>
                </div>
              </div>

              {qualityReport && (
                <>
                  {/* Data stats row */}
                  <div className="grid grid-cols-5 gap-3 mb-3">
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Pub. Deals</p>
                      <p className="text-lg font-bold text-green-600">{qualityReport.stats?.deals?.published || 0}</p>
                    </div>
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Draft Deals</p>
                      <p className="text-lg font-bold text-amber-600">{qualityReport.stats?.deals?.draft || 0}</p>
                    </div>
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Pub. CPs</p>
                      <p className="text-lg font-bold text-blue-600">{qualityReport.stats?.counterparts?.published || 0}</p>
                    </div>
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Links</p>
                      <p className="text-lg font-bold text-[color:var(--gis-body)]">{qualityReport.stats?.links || 0}</p>
                    </div>
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Issues</p>
                      <p className={`text-lg font-bold ${(qualityReport.issues?.length || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {qualityReport.issues?.length || 0}
                      </p>
                    </div>
                  </div>

                  {/* System health row */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">API Health</p>
                      <p className={`text-lg font-bold ${(qualityReport.stats?.apiHealth?.failed || 0) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {qualityReport.stats?.apiHealth ? `${qualityReport.stats.apiHealth.healthy}/${qualityReport.stats.apiHealth.total}` : '—'}
                      </p>
                    </div>
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Active Journals</p>
                      <p className="text-lg font-bold text-violet-600">{qualityReport.stats?.pipeline?.activeJournals || 0}</p>
                    </div>
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Erroring Journals</p>
                      <p className={`text-lg font-bold ${(qualityReport.stats?.pipeline?.errorJournals || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {qualityReport.stats?.pipeline?.errorJournals || 0}
                      </p>
                    </div>
                    <div className={`${themeClasses.card} rounded-lg p-3 border ${themeClasses.border} text-center`}>
                      <p className="text-xs text-[color:var(--gis-dim)]">Stale Drafts</p>
                      <p className={`text-lg font-bold ${(qualityReport.stats?.pipeline?.staleDraftsWithDeals || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {qualityReport.stats?.pipeline?.staleDraftsWithDeals || 0}
                      </p>
                    </div>
                  </div>

                  {/* Issues table */}
                  {qualityReport.issues?.length > 0 && (
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className={`sticky top-0 ${themeClasses.card}`}>
                          <tr className={`border-b ${themeClasses.border}`}>
                            <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted} w-24`}>Severity</th>
                            <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted} w-24`}>Category</th>
                            <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted}`}>Check</th>
                            <th className={`text-center px-3 py-2 font-medium ${themeClasses.textMuted} w-16`}>Count</th>
                            <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted}`}>Samples</th>
                          </tr>
                        </thead>
                        <tbody>
                          {qualityReport.issues.map((issue, idx) => (
                            <tr key={idx} className={`border-b ${themeClasses.border} hover:bg-[color:var(--gis-bg)] dark:hover:bg-[color:var(--gis-panel)]`}>
                              <td className="px-3 py-2">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  issue.severity === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                  issue.severity === 'moderate' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                  'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] dark:bg-[color:var(--gis-rule)] dark:text-[color:var(--gis-dim)]'
                                }`}>
                                  {issue.severity}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  issue.category === 'api' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                                  issue.category === 'pipeline' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' :
                                  issue.category === 'integrity' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                }`}>
                                  {issue.category === 'api' ? 'API' : issue.category === 'integrity' ? 'Integrity' : issue.category === 'pipeline' ? 'Pipeline' : issue.category === 'deal' ? 'Data' : 'Data'}
                                </span>
                              </td>
                              <td className={`px-3 py-2 font-medium ${themeClasses.text}`}>{issue.check.replace(/_/g, ' ')}</td>
                              <td className={`px-3 py-2 text-center font-bold ${themeClasses.text}`}>{issue.count}</td>
                              <td className={`px-3 py-2 text-xs ${themeClasses.textMuted}`}>
                                {(() => {
                                  const isDealIssue = issue.category === 'deal' || issue.check.includes('deal') || issue.check.includes('url');
                                  const isCpIssue = issue.category === 'counterpart' || issue.check.includes('counterpart') || issue.check.includes('description') || issue.check.includes('category');
                                  return (
                                    <div className="flex flex-col gap-1">
                                      {(issue.details || []).slice(0, 5).map((d, di) => {
                                        const label = d.headline || d.name || d.badTech || d.error || d.id || '';
                                        const truncLabel = label.length > 70 ? label.substring(0, 67) + '...' : label;
                                        const entityId = d.id;
                                        const isLinkable = entityId && (isDealIssue || isCpIssue);
                                        return (
                                          <div key={di} className="flex items-center gap-1.5 group/sample">
                                            {isLinkable ? (
                                              <button className="text-blue-600 hover:underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-left" onClick={(e) => { e.stopPropagation(); if (isDealIssue) { const deal = deals.find(dl => dl.id === entityId); if (deal) viewDeal(deal); } else { const cp = counterparts.find(c => c.id === entityId); if (cp) viewCounterpart(cp); } }}>{truncLabel}</button>
                                            ) : (
                                              <span>{truncLabel}</span>
                                            )}
                                            {entityId && isDealIssue && (
                                              <span className="flex-shrink-0 flex items-center gap-0.5">
                                                <button title="Approve — mark as quality checked" className="p-0.5 rounded hover:bg-green-100 dark:hover:bg-green-900/40" onClick={async (e) => { e.stopPropagation(); try { await authFetch(`/api/deals?id=${entityId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deal: { quality_reviewed: true } }) }); setDeals(prev => prev.map(dl => dl.id === entityId ? { ...dl, quality_reviewed: true, quality_reviewed_at: new Date().toISOString() } : dl)); setQualityReport(prev => prev ? { ...prev, issues: prev.issues.map(iss => iss === issue ? { ...iss, count: iss.count - 1, details: iss.details.filter(dd => dd.id !== entityId) } : iss).filter(iss => iss.count > 0) } : prev); } catch (err) { console.error('Approve failed:', err); } }}><Check size={12} className="text-green-600" /></button>
                                                <button title="Edit deal" className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40" onClick={(e) => { e.stopPropagation(); const deal = deals.find(dl => dl.id === entityId); if (deal) { setEditingDeal(deal); setShowDealModal(true); } }}><Edit2 size={12} className="text-blue-600" /></button>
                                                <button title="Delete deal" className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40" onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Delete this deal?\n${label.substring(0, 80)}`)) return; try { await authFetch(`/api/deals?id=${entityId}`, { method: 'DELETE' }); setDeals(prev => prev.filter(dl => dl.id !== entityId)); sessionStorage.setItem('deals_cache_bust', Date.now()); setQualityReport(prev => prev ? { ...prev, issues: prev.issues.map(iss => iss === issue ? { ...iss, count: iss.count - 1, details: iss.details.filter(dd => dd.id !== entityId) } : iss).filter(iss => iss.count > 0) } : prev); } catch (err) { console.error('Delete failed:', err); } }}><Trash2 size={12} className="text-red-500" /></button>
                                              </span>
                                            )}
                                            {entityId && isCpIssue && (
                                              <span className="flex-shrink-0 flex items-center gap-0.5">
                                                <button title="Edit counterpart" className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40" onClick={(e) => { e.stopPropagation(); const cp = counterparts.find(c => c.id === entityId); if (cp) { setEditingCounterpart(cp); setShowCounterpartModal(true); } }}><Edit2 size={12} className="text-blue-600" /></button>
                                                <button title="Delete counterpart" className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40" onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Delete "${d.name || entityId}"?\nThis will remove it from all deals.`)) return; try { await authFetch(`/api/counterparts?id=${entityId}`, { method: 'DELETE' }); setCounterparts(prev => prev.filter(c => c.id !== entityId)); sessionStorage.setItem('counterparts_cache_bust', Date.now()); setQualityReport(prev => prev ? { ...prev, issues: prev.issues.map(iss => iss === issue ? { ...iss, count: iss.count - 1, details: iss.details.filter(dd => dd.id !== entityId) } : iss).filter(iss => iss.count > 0) } : prev); } catch (err) { console.error('Delete failed:', err); } }}><Trash2 size={12} className="text-red-500" /></button>
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {(issue.details || []).length > 5 && (
                                        <span className="text-[color:var(--gis-dim)] italic">+{issue.details.length - 5} more</span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {qualityReport.issues?.length === 0 && (
                    <div className="text-center py-4">
                      <span className="text-green-600 font-medium">All checks passed — no issues found</span>
                    </div>
                  )}

                  {/* Email status */}
                  {qualityReport.emailSent && (
                    <div className={`mt-3 text-xs ${themeClasses.textMuted} flex items-center gap-1`}>
                      {qualityReport.emailSent.ok
                        ? <><span className="text-green-600">Email sent</span> to contact@globalinfrastructuresherpa.com</>
                        : qualityReport.emailSent.skipped
                          ? <span>No email sent (no issues)</span>
                          : <span className="text-red-600">Email failed: {qualityReport.emailSent.error}</span>
                      }
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Admin-only deal export. Deliberately ADMIN ONLY: deal data is the
                paid product, so there is a standing rule against CSV export on
                any user-facing surface. Do not lift this out of the admin tab. */}
            <div className={`${themeClasses.card} rounded-xl border border-slate-300 dark:border-slate-700 p-6`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Download size={18} className="text-slate-500" />
                    Export Deals
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Full deal table with counterparties as CSV. Admin only — never expose to users.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={exportStatus}
                    onChange={(e) => setExportStatus(e.target.value)}
                    className={`text-xs px-2 py-1 rounded border ${themeClasses.border} ${themeClasses.card}`}
                  >
                    <option value="">All statuses</option>
                    <option value="published">Published only</option>
                    <option value="draft">Draft only</option>
                  </select>
                  <button
                    onClick={handleExportDeals}
                    disabled={exportRunning}
                    className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2"
                  >
                    {exportRunning ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {exportRunning ? 'Building…' : 'Download CSV'}
                  </button>
                </div>
              </div>
              {exportMessage && (
                <p className={`text-sm ${exportMessage.startsWith('Failed') ? 'text-red-500' : themeClasses.textMuted}`}>
                  {exportMessage}
                </p>
              )}
            </div>

            {/* S4: Pipeline Rejections dashboard */}
            <div className={`${themeClasses.card} rounded-xl border border-blue-200 dark:border-blue-900/40 p-6`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Filter size={18} className="text-blue-500" />
                    Pipeline Rejections
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Why deals are being filtered out — grouped by reason
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={rejectionWindow}
                    onChange={(e) => setRejectionWindow(parseInt(e.target.value))}
                    className={`text-xs px-2 py-1 rounded border ${themeClasses.border} ${themeClasses.card}`}
                  >
                    <option value={1}>Last 1h</option>
                    <option value={6}>Last 6h</option>
                    <option value={24}>Last 24h</option>
                    <option value={72}>Last 3d</option>
                    <option value={168}>Last 7d</option>
                    <option value={720}>Last 30d</option>
                  </select>
                  <select
                    value={rejectionPipelineFilter || ''}
                    onChange={(e) => setRejectionPipelineFilter(e.target.value || null)}
                    className={`text-xs px-2 py-1 rounded border ${themeClasses.border} ${themeClasses.card}`}
                  >
                    <option value="">All pipelines</option>
                    <option value="journal_discovery">Journal</option>
                    <option value="counterpart_discovery">Counterpart</option>
                    <option value="topic_discovery">Topic</option>
                    <option value="sector_discovery">Sector</option>
                  </select>
                  <button
                    onClick={loadRejectionStats}
                    disabled={rejectionLoading}
                    className="text-xs px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                  >
                    {rejectionLoading ? 'Loading…' : 'Refresh'}
                  </button>
                </div>
              </div>

              {rejectionStats ? (
                <>
                  {/* Stats strip: total + by pipeline */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className={`border ${themeClasses.border} rounded-lg p-3`}>
                      <div className={`text-2xl font-bold ${themeClasses.text}`}>{rejectionStats.total}</div>
                      <div className={`text-xs ${themeClasses.textMuted}`}>Total rejections</div>
                    </div>
                    {['journal_discovery','counterpart_discovery','topic_discovery','sector_discovery'].map(p => {
                      const entry = (rejectionStats.byPipeline || []).find(x => x.pipeline === p);
                      const count = entry?.count || 0;
                      const label = p.replace('_discovery','').replace(/^./, c => c.toUpperCase());
                      return (
                        <div key={p} className={`border ${themeClasses.border} rounded-lg p-3`}>
                          <div className={`text-2xl font-bold ${themeClasses.text}`}>{count}</div>
                          <div className={`text-xs ${themeClasses.textMuted}`}>{label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reasons list */}
                  {(rejectionStats.byReason || []).length === 0 ? (
                    <div className={`text-sm ${themeClasses.textMuted} italic text-center py-4`}>
                      No rejections in the selected window. Pipelines may not have run yet, or all deals passed the gates.
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {(rejectionStats.byReason || []).map(r => {
                        const isExpanded = rejectionExpandedReason === r.reason;
                        const samples = rejectionSamples[r.reason] || [];
                        return (
                          <div key={r.reason} className={`border ${themeClasses.border} rounded-lg overflow-hidden`}>
                            <button
                              onClick={() => {
                                if (isExpanded) {
                                  setRejectionExpandedReason(null);
                                } else {
                                  setRejectionExpandedReason(r.reason);
                                  if (!rejectionSamples[r.reason]) loadRejectionSamples(r.reason);
                                }
                              }}
                              className={`w-full flex items-center justify-between px-4 py-2 text-left hover:bg-[color:var(--gis-bg)] dark:hover:bg-[color:var(--gis-panel)]`}
                            >
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <span className={`text-sm font-medium ${themeClasses.text}`}>{r.reason}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className={`text-xs ${themeClasses.textMuted} w-16 text-right`}>
                                  {((r.count / Math.max(rejectionStats.total, 1)) * 100).toFixed(1)}%
                                </div>
                                <div className={`text-sm font-bold ${themeClasses.text} w-12 text-right`}>
                                  {r.count}
                                </div>
                              </div>
                            </button>
                            {isExpanded && (
                              <div className={`border-t ${themeClasses.border} bg-[color:var(--gis-bg)] dark:bg-[color:var(--gis-panel)] px-4 py-3 text-xs`}>
                                {rejectionSamplesLoading === r.reason ? (
                                  <div className={themeClasses.textMuted}>Loading samples…</div>
                                ) : samples.length === 0 ? (
                                  <div className={themeClasses.textMuted}>No samples available</div>
                                ) : (
                                  <div className="space-y-2">
                                    {samples.map(s => (
                                      <div key={s.id} className={`flex items-start gap-2 pb-2 border-b ${themeClasses.border} last:border-b-0`}>
                                        <div className="flex-1 min-w-0">
                                          <div className={`${themeClasses.text} break-words`}>{s.headline || '(no headline)'}</div>
                                          <div className={`${themeClasses.textMuted} mt-0.5 flex items-center gap-2 flex-wrap`}>
                                            <span className="font-mono">{s.pipeline.replace('_discovery','')}</span>
                                            <span>·</span>
                                            <span>{new Date(s.created_at).toLocaleString()}</span>
                                            {s.source_url && (
                                              <>
                                                <span>·</span>
                                                <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[300px]">
                                                  source
                                                </a>
                                              </>
                                            )}
                                          </div>
                                          {s.party_names && s.party_names.length > 0 && (
                                            <div className={`${themeClasses.textMuted} mt-0.5`}>
                                              parties: {s.party_names.join(', ')}
                                            </div>
                                          )}
                                          {s.detail && Object.keys(s.detail).length > 0 && (
                                            <div className={`${themeClasses.textMuted} mt-0.5 font-mono`}>
                                              {JSON.stringify(s.detail)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {rejectionStats.newest && (
                    <div className={`text-xs ${themeClasses.textMuted} mt-3 text-right`}>
                      Most recent rejection: {new Date(rejectionStats.newest).toLocaleString()}
                    </div>
                  )}
                </>
              ) : (
                <div className={`text-sm ${themeClasses.textMuted} italic text-center py-4`}>
                  {rejectionLoading ? 'Loading…' : 'Click Refresh to load pipeline rejection telemetry'}
                </div>
              )}
            </div>

            {/* Recently Added Deals — Quality Review (top of overview for visibility) */}
            {(() => {
              const recentDeals = deals
                .filter(d => d.created_by) // only show programmatically-added deals
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              const todayDeals = recentDeals.filter(d => new Date(d.created_at) >= new Date(Date.now() - 24 * 60 * 60 * 1000));
              const weekDeals = recentDeals.filter(d => new Date(d.created_at) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
              const discoveryDeals = weekDeals.filter(d => d.created_by === 'counterpart_discovery');
              const missingFields = weekDeals.filter(d => {
                const missing = [!d.technology?.length, !(Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).length, !d.description, !d.deal_type, !d.announcement_date].filter(Boolean).length;
                return missing >= 2;
              });
              const displayDeals = recentDeals.slice(0, 50);

              return (
                <div className={`${themeClasses.card} rounded-xl border-2 border-amber-300 dark:border-amber-600 p-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                        <Zap size={18} className="text-amber-500" />
                        Recently Added Deals — Quality Review
                      </h3>
                      <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                        All programmatically added deals, newest first
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded-full">{todayDeals.length} today</span>
                      <span className="text-xs font-medium text-cyan-600 bg-cyan-50 dark:bg-cyan-900/30 px-2 py-1 rounded-full">{weekDeals.length} this week</span>
                      <span className="text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full">{discoveryDeals.length} from discovery</span>
                      {missingFields.length > 0 && (
                        <span className="text-xs font-medium text-red-600 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded-full">{missingFields.length} incomplete</span>
                      )}
                    </div>
                  </div>
                  <div className="max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className={`sticky top-0 ${themeClasses.card}`}>
                        <tr className={`border-b ${themeClasses.border}`}>
                          <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted}`}>Deal</th>
                          <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted} w-20`}>Source</th>
                          <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted} w-24`}>Tech</th>
                          <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted} w-24`}>Country</th>
                          <th className={`text-center px-3 py-2 font-medium ${themeClasses.textMuted} w-16`}>Fields</th>
                          <th className={`text-center px-3 py-2 font-medium ${themeClasses.textMuted} w-20`}>URL</th>
                          <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted} w-24`}>Deal Date</th>
                          <th className={`text-left px-3 py-2 font-medium ${themeClasses.textMuted} w-20`}>Added</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayDeals.map(d => {
                          const fields = [d.technology?.length, (Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).length, d.description, d.deal_type, d.announcement_date, d.capacity].filter(Boolean).length;
                          const source = d.created_by === 'counterpart_discovery' ? 'Discovery'
                            : d.created_by === 'journal_discovery' ? 'Journal'
                            : d.created_by === 'crawler' ? 'Crawler'
                            : d.created_by || 'Manual';
                          const sourceColor = d.created_by === 'counterpart_discovery' ? 'text-cyan-600 bg-cyan-50 dark:bg-cyan-900/30'
                            : d.created_by === 'journal_discovery' ? 'text-violet-600 bg-violet-50 dark:bg-violet-900/30'
                            : d.created_by === 'crawler' ? 'text-green-600 bg-green-50 dark:bg-green-900/30'
                            : 'text-blue-600 bg-blue-50 dark:bg-blue-900/30';
                          const techs = Array.isArray(d.technology) ? d.technology.join(', ') : d.technology || '';
                          return (
                            <tr key={d.id} className={`border-b ${themeClasses.border} hover:${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-bg)]'} cursor-pointer`}
                              onClick={() => { setSelectedDeal(d); setActiveTab('deals'); }}>
                              <td className={`px-3 py-2 ${themeClasses.text} max-w-xs truncate font-medium`}>
                                {d.headline || d.description?.slice(0, 60) || 'Untitled'}
                                {d.quality_reviewed && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"><Check size={10} /> Reviewed</span>}
                              </td>
                              <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 rounded font-medium ${sourceColor}`}>{source}</span></td>
                              <td className={`px-3 py-2 text-xs ${themeClasses.textMuted} truncate max-w-[100px]`}>{techs || '—'}</td>
                              <td className={`px-3 py-2 text-xs ${themeClasses.textMuted} truncate`}>{(Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).join(', ') || '—'}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`text-xs font-bold ${fields >= 5 ? 'text-green-600' : fields >= 3 ? 'text-amber-600' : 'text-red-600'}`}>{fields}/6</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {d.source_url ? (
                                  d.url_verified ? <span className="text-green-600 text-xs font-medium">Verified</span> : <span className="text-amber-600 text-xs">Unverified</span>
                                ) : <span className={`text-xs ${themeClasses.textMuted}`}>—</span>}
                              </td>
                              <td className={`px-3 py-2 text-xs ${themeClasses.textMuted} whitespace-nowrap`}>{d.announcement_date ? new Date(d.announcement_date).toLocaleDateString() : '—'}</td>
                              <td className={`px-3 py-2 text-xs ${themeClasses.textMuted} whitespace-nowrap`}>{new Date(d.created_at).toLocaleDateString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {displayDeals.length === 0 && (
                      <p className={`text-center py-6 ${themeClasses.textMuted}`}>No programmatically added deals yet</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Data Quality Dashboard */}
            {(() => {
              const counterpartsNeedingEnrichment = counterparts.filter(c =>
                c.type === 'company' && (!c.investmentPreferences || !c.investmentPreferences.summary)
              );
              const dealsWithoutCounterparts = deals.filter(d =>
                !d.deal_counterparts || d.deal_counterparts.length === 0
              );
              const dealsMissingLocation = deals.filter(d =>
                !(Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).length && !d.region && !d.location
              );
              const counterpartsMissingCategory = counterparts.filter(c =>
                !c.category || c.category === 'Unknown'
              );

              const totalIssues = counterpartsNeedingEnrichment.length + dealsWithoutCounterparts.length + dealsMissingLocation.length + counterpartsMissingCategory.length;

              return (
                <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                      <AlertCircle size={18} className="text-amber-500" />
                      Data Quality Dashboard
                    </h3>
                    {totalIssues > 0 && (
                      <span className="px-3 py-1 text-sm rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20">
                        {totalIssues} issues
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Need Enrichment */}
                    <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${themeClasses.text}`}>Need Enrichment</span>
                        <span className={`text-lg font-bold ${counterpartsNeedingEnrichment.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {counterpartsNeedingEnrichment.length}
                        </span>
                      </div>
                      {counterpartsNeedingEnrichment.length > 0 && (
                        <>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {counterpartsNeedingEnrichment.slice(0, 5).map(cp => (
                              <button
                                key={cp.id}
                                onClick={() => { viewCounterpart(cp); setActiveTab('dashboard'); }}
                                className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-500/20 dark:hover:bg-amber-500/30 truncate max-w-[100px]"
                              >
                                {cp.name}
                              </button>
                            ))}
                            {counterpartsNeedingEnrichment.length > 5 && (
                              <span className={`text-xs ${themeClasses.textMuted} py-1`}>+{counterpartsNeedingEnrichment.length - 5} more</span>
                            )}
                          </div>
                          {enrichMissingRunning ? (
                            <div className="mt-2">
                              <div className="flex items-center gap-2 mb-1">
                                <Loader2 size={12} className="animate-spin text-amber-600" />
                                <span className={`text-xs ${themeClasses.text}`}>{enrichMissingProgress.processed}/{enrichMissingProgress.total}</span>
                                {enrichMissingProgress.errors > 0 && <span className="text-xs text-red-500">{enrichMissingProgress.errors} err</span>}
                              </div>
                              <div className="w-full h-1.5 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                                <div className="h-full bg-amber-500 transition-all duration-300" style={{ width: `${enrichMissingProgress.total > 0 ? (enrichMissingProgress.processed / enrichMissingProgress.total) * 100 : 0}%` }} />
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Enrich ${counterpartsNeedingEnrichment.length} counterparts? This uses AI API quota.`)) return;
                                setEnrichMissingRunning(true);
                                setEnrichMissingProgress({ processed: 0, total: counterpartsNeedingEnrichment.length, errors: 0 });
                                let processed = 0, errors = 0;
                                const batchSize = 3;
                                for (let i = 0; i < counterpartsNeedingEnrichment.length; i += batchSize) {
                                  const batch = counterpartsNeedingEnrichment.slice(i, i + batchSize);
                                  await Promise.all(batch.map(async (cp) => {
                                    try {
                                      const res = await authFetch('/api/enrich-counterpart', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          counterpartId: cp.id,
                                          name: cp.name,
                                          websiteUrl: cp.websiteUrl || cp.website || cp.investmentPreferences?.website,
                                          industry: 'infrastructure',
                                          counterpartType: cp.type || 'company'
                                        })
                                      });
                                      await res.json();
                                    } catch (err) {
                                      errors++;
                                    }
                                    processed++;
                                    setEnrichMissingProgress({ processed, total: counterpartsNeedingEnrichment.length, errors });
                                  }));
                                }
                                setEnrichMissingRunning(false);
                                refreshCentralizedData();
                              }}
                              className="mt-2 w-full text-xs px-2 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 flex items-center justify-center gap-1"
                            >
                              <Sparkles size={12} />
                              Enrich All ({counterpartsNeedingEnrichment.length})
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Deals Without Counterparts */}
                    <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${themeClasses.text}`}>No Counterparts</span>
                        <span className={`text-lg font-bold ${dealsWithoutCounterparts.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {dealsWithoutCounterparts.length}
                        </span>
                      </div>
                      {dealsWithoutCounterparts.length > 0 && (
                        <p className={`text-xs ${themeClasses.textMuted} mt-2`}>
                          {dealsWithoutCounterparts.slice(0, 2).map(d => d.headline?.slice(0, 30) || d.description?.slice(0, 30)).join(', ')}
                          {dealsWithoutCounterparts.length > 2 && '...'}
                        </p>
                      )}
                    </div>

                    {/* Missing Location */}
                    <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${themeClasses.text}`}>Missing Location</span>
                        <span className={`text-lg font-bold ${dealsMissingLocation.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {dealsMissingLocation.length}
                        </span>
                      </div>
                      <p className={`text-xs ${themeClasses.textMuted}`}>deals without geography</p>
                    </div>

                    {/* Missing Category */}
                    <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${themeClasses.text}`}>Missing Category</span>
                        <span className={`text-lg font-bold ${counterpartsMissingCategory.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {counterpartsMissingCategory.length}
                        </span>
                      </div>
                      <p className={`text-xs ${themeClasses.textMuted}`}>counterparts to categorize</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Bulk Re-Enrichment Section */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <RefreshCw size={18} className="text-blue-500" />
                    Bulk Classification Update
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Re-enrich counterparts to populate classification tables (Types, Technologies, Geographies, Stages)
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/bulk-reenrich');
                      const data = await res.json();
                      if (data.success) setBulkEnrichStats(data.stats);
                    } catch (err) {
                      console.error('Failed to load stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {/* Stats */}
              {bulkEnrichStats && (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Total</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{bulkEnrichStats.total}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Enriched</p>
                    <p className={`text-lg font-bold text-green-600`}>{bulkEnrichStats.enriched}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>With Classifications</p>
                    <p className={`text-lg font-bold text-blue-600`}>{bulkEnrichStats.withClassifications}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Need Classifications</p>
                    <p className={`text-lg font-bold text-amber-600`}>{bulkEnrichStats.needingClassifications}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Need Enrichment</p>
                    <p className={`text-lg font-bold text-red-600`}>{bulkEnrichStats.pendingEnrichment}</p>
                  </div>
                </div>
              )}

              {/* Progress */}
              {bulkEnrichRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-blue-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Processing... {bulkEnrichProgress.processed} / {bulkEnrichProgress.total}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${bulkEnrichProgress.total > 0 ? (bulkEnrichProgress.processed / bulkEnrichProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                  {bulkEnrichProgress.errors.length > 0 && (
                    <p className="text-xs text-amber-600 mt-2">{bulkEnrichProgress.errors.length} errors</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (bulkEnrichRunning) return;
                    setBulkEnrichRunning(true);
                    setBulkEnrichProgress({ processed: 0, total: bulkEnrichStats?.needingClassifications || 0, errors: [] });

                    let offset = 0;
                    const batchSize = 5;
                    let hasMore = true;
                    let totalProcessed = 0;
                    let allErrors = [];

                    while (hasMore) {
                      try {
                        const res = await fetch('/api/bulk-reenrich', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ mode: 'missing_classifications', batchSize, offset })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalProcessed += data.results?.processed || 0;
                          allErrors = [...allErrors, ...(data.results?.errors || [])];
                          setBulkEnrichProgress({ processed: totalProcessed, total: bulkEnrichStats?.needingClassifications || totalProcessed, errors: allErrors });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Batch error:', err);
                        hasMore = false;
                      }
                    }

                    setBulkEnrichRunning(false);
                    // Refresh stats
                    const statsRes = await fetch('/api/bulk-reenrich');
                    const statsData = await statsRes.json();
                    if (statsData.success) setBulkEnrichStats(statsData.stats);
                    // Reload counterparts
                    refreshCentralizedData();
                  }}
                  disabled={bulkEnrichRunning || !bulkEnrichStats?.needingClassifications}
                  className="px-4 py-2 bg-blue-700 text-white rounded-xl text-sm font-medium hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkEnrichRunning ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Populate Missing Classifications
                    </>
                  )}
                </button>

                <button
                  onClick={async () => {
                    if (bulkEnrichRunning) return;
                    if (!window.confirm('This will re-enrich ALL counterparts. This may take a while and use API quota. Continue?')) return;

                    setBulkEnrichRunning(true);
                    setBulkEnrichProgress({ processed: 0, total: bulkEnrichStats?.total || 0, errors: [] });

                    let offset = 0;
                    const batchSize = 5;
                    let hasMore = true;
                    let totalProcessed = 0;
                    let allErrors = [];

                    while (hasMore) {
                      try {
                        const res = await fetch('/api/bulk-reenrich', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ mode: 'all', batchSize, offset })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalProcessed += data.results?.processed || 0;
                          allErrors = [...allErrors, ...(data.results?.errors || [])];
                          setBulkEnrichProgress({ processed: totalProcessed, total: bulkEnrichStats?.total || totalProcessed, errors: allErrors });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Batch error:', err);
                        hasMore = false;
                      }
                    }

                    setBulkEnrichRunning(false);
                    const statsRes = await fetch('/api/bulk-reenrich');
                    const statsData = await statsRes.json();
                    if (statsData.success) setBulkEnrichStats(statsData.stats);
                    refreshCentralizedData();
                  }}
                  disabled={bulkEnrichRunning}
                  className={`px-4 py-2 border ${themeClasses.border} rounded-xl text-sm font-medium ${themeClasses.text} hover:bg-[color:var(--gis-rule)] dark:hover:bg-[color:var(--gis-panel)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                >
                  <RefreshCw size={16} />
                  Re-enrich All
                </button>
                <button
                  onClick={async () => {
                    if (bulkEnrichRunning) return;
                    if (!window.confirm('This will re-enrich counterparts that have no deals in their enrichment data (e.g. OX2). Uses API quota. Continue?')) return;

                    setBulkEnrichRunning(true);
                    setBulkEnrichProgress({ processed: 0, total: bulkEnrichStats?.missingDeals || 0, errors: [] });

                    let offset = 0;
                    const batchSize = 5;
                    let hasMore = true;
                    let totalProcessed = 0;
                    let allErrors = [];

                    while (hasMore) {
                      try {
                        const res = await fetch('/api/bulk-reenrich', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ mode: 'missing_deals', batchSize, offset })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalProcessed += data.results?.processed || 0;
                          allErrors = [...allErrors, ...(data.results?.errors || [])];
                          setBulkEnrichProgress({ processed: totalProcessed, total: bulkEnrichStats?.missingDeals || totalProcessed, errors: allErrors });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Batch error:', err);
                        hasMore = false;
                      }
                    }

                    setBulkEnrichRunning(false);
                    const statsRes = await fetch('/api/bulk-reenrich');
                    const statsData = await statsRes.json();
                    if (statsData.success) setBulkEnrichStats(statsData.stats);
                    refreshCentralizedData();
                  }}
                  disabled={bulkEnrichRunning || !bulkEnrichStats?.missingDeals}
                  className={`px-4 py-2 border ${themeClasses.border} rounded-xl text-sm font-medium ${themeClasses.text} hover:bg-[color:var(--gis-rule)] dark:hover:bg-[color:var(--gis-panel)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                >
                  <Rss size={16} />
                  Re-enrich Missing Deals {bulkEnrichStats?.missingDeals ? `(${bulkEnrichStats.missingDeals})` : ''}
                </button>
              </div>
            </div>

            {/* Enrich All Incomplete Deals Section */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Database size={18} className="text-indigo-500" />
                    Enrich All Incomplete Deals
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Use AI to extract missing fields (description, technology, country, capacity, date, deal_type, region) from ALL incomplete deals, newest first
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/backfill-deals?stats=true');
                      const data = await res.json();
                      if (data.success) setBackfillAllDealsStats(data.stats);
                    } catch (err) {
                      console.error('Failed to load deal enrichment stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {backfillAllDealsStats && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Total Deals</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{backfillAllDealsStats.totalDeals}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Incomplete (2+ missing)</p>
                    <p className={`text-lg font-bold text-amber-600`}>{backfillAllDealsStats.incomplete}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Complete</p>
                    <p className={`text-lg font-bold text-green-600`}>{backfillAllDealsStats.complete}</p>
                  </div>
                </div>
              )}

              {backfillAllDealsRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-indigo-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Enriching... {backfillAllDealsProgress.updated} updated, {backfillAllDealsProgress.skipped} skipped
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-600 transition-all duration-300"
                      style={{ width: `${backfillAllDealsProgress.total > 0 ? ((backfillAllDealsProgress.updated + backfillAllDealsProgress.skipped) / backfillAllDealsProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (backfillAllDealsRunning) return;
                    if (!window.confirm('This will use AI to enrich all incomplete deals (newest first). Continue?')) return;

                    setBackfillAllDealsRunning(true);
                    setBackfillAllDealsProgress({ updated: 0, skipped: 0, total: backfillAllDealsStats?.incomplete || 0 });

                    let offset = 0;
                    const batchSize = 10;
                    let hasMore = true;
                    let totalUpdated = 0;
                    let totalSkipped = 0;

                    while (hasMore) {
                      try {
                        const res = await fetch('/api/backfill-deals', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ batchSize, offset })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalUpdated += data.results?.updated || 0;
                          totalSkipped += data.results?.skipped || 0;
                          setBackfillAllDealsProgress({ updated: totalUpdated, skipped: totalSkipped, total: backfillAllDealsStats?.incomplete || (totalUpdated + totalSkipped) });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Backfill all deals batch error:', err);
                        hasMore = false;
                      }
                    }

                    setBackfillAllDealsRunning(false);
                    // Refresh stats
                    const statsRes = await fetch('/api/backfill-deals?stats=true');
                    const statsData = await statsRes.json();
                    if (statsData.success) setBackfillAllDealsStats(statsData.stats);
                    refreshCentralizedData();
                    alert(`Enrichment complete! Updated: ${totalUpdated}, Skipped: ${totalSkipped}`);
                  }}
                  disabled={backfillAllDealsRunning || !backfillAllDealsStats?.incomplete}
                  className="px-4 py-2 bg-indigo-700 text-white rounded-xl text-sm font-medium hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {backfillAllDealsRunning ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Database size={16} />
                      Run Enrichment
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Backfill Missing Descriptions Section */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <FileText size={18} className="text-orange-500" />
                    Backfill Missing Descriptions
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Generate descriptions for published deals with empty description field. Phase 1 uses source articles, Phase 2 uses headline extraction.
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/backfill-descriptions');
                      const data = await res.json();
                      if (data.success) setBackfillDescStats(data.stats);
                    } catch (err) {
                      console.error('Failed to load description backfill stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {backfillDescStats && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Total Published</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{backfillDescStats.totalPublished}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Missing Desc</p>
                    <p className={`text-lg font-bold text-orange-600`}>{backfillDescStats.missingDescription}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>With Source</p>
                    <p className={`text-lg font-bold text-blue-600`}>{backfillDescStats.withSourceUrl}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Without Source</p>
                    <p className={`text-lg font-bold text-amber-600`}>{backfillDescStats.withoutSourceUrl}</p>
                  </div>
                </div>
              )}

              {backfillDescRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-orange-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      {backfillDescProgress.phase === 'source' ? 'From Sources' : 'From Headlines'}... {backfillDescProgress.updated} updated, {backfillDescProgress.skipped} skipped, {backfillDescProgress.errors} errors
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-600 transition-all duration-300"
                      style={{ width: `${backfillDescProgress.total > 0 ? ((backfillDescProgress.updated + backfillDescProgress.skipped + backfillDescProgress.errors) / backfillDescProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (backfillDescRunning) return;
                    if (!window.confirm('This will generate descriptions from source articles for deals with verified URLs. Continue?')) return;

                    setBackfillDescRunning(true);
                    setBackfillDescProgress({ updated: 0, skipped: 0, errors: 0, total: backfillDescStats?.withSourceUrl || 0, phase: 'source' });

                    let offset = 0;
                    const batchSize = 20;
                    let hasMore = true;
                    let totalUpdated = 0;
                    let totalSkipped = 0;
                    let totalErrors = 0;

                    while (hasMore) {
                      try {
                        const res = await fetch('/api/backfill-descriptions', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ batchSize, offset, phase: 'source' })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalUpdated += data.results?.updated || 0;
                          totalSkipped += data.results?.skipped || 0;
                          totalErrors += data.results?.errors || 0;
                          setBackfillDescProgress({ updated: totalUpdated, skipped: totalSkipped, errors: totalErrors, total: backfillDescStats?.withSourceUrl || (totalUpdated + totalSkipped + totalErrors), phase: 'source' });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Backfill descriptions (source) batch error:', err);
                        hasMore = false;
                      }
                    }

                    setBackfillDescRunning(false);
                    const statsRes = await fetch('/api/backfill-descriptions');
                    const statsData = await statsRes.json();
                    if (statsData.success) setBackfillDescStats(statsData.stats);
                    refreshCentralizedData();
                    alert(`Source backfill complete! Updated: ${totalUpdated}, Skipped: ${totalSkipped}, Errors: ${totalErrors}`);
                  }}
                  disabled={backfillDescRunning || !backfillDescStats?.withSourceUrl}
                  className="px-4 py-2 bg-orange-700 text-white rounded-xl text-sm font-medium hover:bg-orange-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {backfillDescRunning && backfillDescProgress.phase === 'source' ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <FileText size={16} />
                      From Sources
                    </>
                  )}
                </button>
                <button
                  onClick={async () => {
                    if (backfillDescRunning) return;
                    if (!window.confirm('This will generate descriptions from headlines for deals without usable source URLs. Continue?')) return;

                    setBackfillDescRunning(true);
                    setBackfillDescProgress({ updated: 0, skipped: 0, errors: 0, total: backfillDescStats?.withoutSourceUrl || 0, phase: 'headline' });

                    let offset = 0;
                    const batchSize = 20;
                    let hasMore = true;
                    let totalUpdated = 0;
                    let totalSkipped = 0;
                    let totalErrors = 0;

                    while (hasMore) {
                      try {
                        const res = await fetch('/api/backfill-descriptions', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ batchSize, offset, phase: 'headline' })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalUpdated += data.results?.updated || 0;
                          totalSkipped += data.results?.skipped || 0;
                          totalErrors += data.results?.errors || 0;
                          setBackfillDescProgress({ updated: totalUpdated, skipped: totalSkipped, errors: totalErrors, total: backfillDescStats?.withoutSourceUrl || (totalUpdated + totalSkipped + totalErrors), phase: 'headline' });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Backfill descriptions (headline) batch error:', err);
                        hasMore = false;
                      }
                    }

                    setBackfillDescRunning(false);
                    const statsRes = await fetch('/api/backfill-descriptions');
                    const statsData = await statsRes.json();
                    if (statsData.success) setBackfillDescStats(statsData.stats);
                    refreshCentralizedData();
                    alert(`Headline backfill complete! Updated: ${totalUpdated}, Skipped: ${totalSkipped}, Errors: ${totalErrors}`);
                  }}
                  disabled={backfillDescRunning || !backfillDescStats?.withoutSourceUrl}
                  className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${theme === 'dark' ? 'bg-orange-900/50 text-orange-200 hover:bg-orange-900/70' : 'bg-orange-100 text-orange-800 hover:bg-orange-200'}`}
                >
                  {backfillDescRunning && backfillDescProgress.phase === 'headline' ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <FileText size={16} />
                      From Headlines
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Website Review Queue — counterparts where website discovery failed */}
            <div className={`${themeClasses.card} rounded-xl border-2 border-orange-300 dark:border-orange-800 p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <AlertCircle size={18} className="text-orange-500" /> Website Review Queue
                  </h3>
                  <p className={`text-xs ${themeClasses.textMuted}`}>Counterparts where website discovery failed — need manual website entry</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const resp = await authFetch('/api/retry-enrichment?stats=true');
                      const data = await resp.json();
                      if (data.success) setWebsiteReviewList(data.stats.needsReview || []);
                    } catch (err) { console.error(err); }
                  }}
                  className={`text-xs px-3 py-1.5 rounded-lg ${themeClasses.btnSecondary}`}
                >Refresh</button>
              </div>
              {websiteReviewList.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {websiteReviewList.map(cp => (
                    <div key={cp.id} className={`flex items-center gap-3 p-2 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-orange-50'}`}>
                      <span className={`flex-1 text-sm font-medium ${themeClasses.text}`}>{cp.name}</span>
                      <input
                        type="text"
                        placeholder="Enter website URL..."
                        className={`text-xs px-2 py-1 rounded border w-48 ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] border-[color:var(--gis-rule)] text-white' : 'bg-[color:var(--gis-panel)] border-[color:var(--gis-rule)]'}`}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            let url = e.target.value.trim();
                            if (!url.startsWith('http')) url = 'https://' + url;
                            await authFetch('/api/counterparts', {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ id: cp.id, website: url })
                            });
                            // Clear the flag and trigger enrichment
                            e.target.value = '✓ Saved';
                            e.target.disabled = true;
                            setWebsiteReviewList(prev => prev.filter(r => r.id !== cp.id));
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const fullCp = counterparts.find(c => c.id === cp.id) || { id: cp.id, name: cp.name, type: 'company' };
                          viewCounterpart(fullCp);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >View</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-sm ${themeClasses.textMuted}`}>Click Refresh to load the review queue</p>
              )}
            </div>

            {/* Backfill Missing Websites Section */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Globe size={18} className="text-teal-500" />
                    Backfill Missing Websites
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Use AI to find official websites for counterparts missing a URL (enables favicon display)
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/backfill-websites');
                      const data = await res.json();
                      if (data.success) setBackfillWebsitesStats(data.stats);
                    } catch (err) {
                      console.error('Failed to load website backfill stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {backfillWebsitesStats && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Total Counterparts</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{backfillWebsitesStats.total}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Missing Website</p>
                    <p className={`text-lg font-bold text-amber-600`}>{backfillWebsitesStats.missingWebsite}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Have Website</p>
                    <p className={`text-lg font-bold text-green-600`}>{backfillWebsitesStats.haveWebsite}</p>
                  </div>
                </div>
              )}

              {backfillWebsitesRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-teal-900/20' : 'bg-teal-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-teal-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Backfilling... {backfillWebsitesProgress.updated} updated, {backfillWebsitesProgress.skipped} skipped
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-600 transition-all duration-300"
                      style={{ width: `${backfillWebsitesProgress.total > 0 ? ((backfillWebsitesProgress.updated + backfillWebsitesProgress.skipped) / backfillWebsitesProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (backfillWebsitesRunning) return;
                    if (!window.confirm('This will use AI to find websites for counterparts with missing URLs. Continue?')) return;

                    setBackfillWebsitesRunning(true);
                    setBackfillWebsitesProgress({ updated: 0, skipped: 0, total: backfillWebsitesStats?.missingWebsite || 0 });

                    let offset = 0;
                    const batchSize = 20;
                    let hasMore = true;
                    let totalUpdated = 0;
                    let totalSkipped = 0;

                    while (hasMore) {
                      try {
                        const res = await fetch('/api/backfill-websites', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ batchSize, offset })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalUpdated += data.results?.updated || 0;
                          totalSkipped += data.results?.skipped || 0;
                          setBackfillWebsitesProgress({ updated: totalUpdated, skipped: totalSkipped, total: backfillWebsitesStats?.missingWebsite || (totalUpdated + totalSkipped) });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Backfill websites batch error:', err);
                        hasMore = false;
                      }
                    }

                    setBackfillWebsitesRunning(false);
                    const statsRes = await fetch('/api/backfill-websites');
                    const statsData = await statsRes.json();
                    if (statsData.success) setBackfillWebsitesStats(statsData.stats);
                    refreshCentralizedData();
                    alert(`Website backfill complete! Updated: ${totalUpdated}, Skipped: ${totalSkipped}`);
                  }}
                  disabled={backfillWebsitesRunning || !backfillWebsitesStats?.missingWebsite}
                  className="px-4 py-2 bg-teal-700 text-white rounded-xl text-sm font-medium hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {backfillWebsitesRunning ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Globe size={16} />
                      Run Backfill
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Auto-Discover RSS Sources Section */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`} style={{ borderColor: 'rgb(6 182 212 / 0.3)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Rss size={18} className="text-cyan-500" />
                    Auto-Discover RSS Sources
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Probe counterpart websites for RSS feeds and create counterpart_sources entries automatically
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await authFetch('/api/auto-discover-sources');
                      const data = await res.json();
                      if (data.success) setAutoDiscoverStats(data.stats);
                    } catch (err) {
                      console.error('Failed to load auto-discover stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {autoDiscoverStats && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>With Website</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{autoDiscoverStats.withWebsite}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Have Source</p>
                    <p className={`text-lg font-bold text-green-600`}>{autoDiscoverStats.haveSource}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Without Source</p>
                    <p className={`text-lg font-bold text-amber-600`}>{autoDiscoverStats.withoutSource}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Tier A / B / C</p>
                    <p className={`text-lg font-bold text-cyan-600`}>{autoDiscoverStats.tierA} / {autoDiscoverStats.tierB} / {autoDiscoverStats.tierC}</p>
                  </div>
                </div>
              )}

              {autoDiscoverRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-cyan-900/20' : 'bg-cyan-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-cyan-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Discovering... {autoDiscoverProgress.feedsDiscovered} feeds found, {autoDiscoverProgress.noFeedFound} no feed
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cyan-600 transition-all duration-300"
                      style={{ width: `${autoDiscoverProgress.total > 0 ? ((autoDiscoverProgress.feedsDiscovered + autoDiscoverProgress.noFeedFound) / autoDiscoverProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (autoDiscoverRunning) return;
                    if (!window.confirm('This will probe counterpart websites for RSS feeds. Continue?')) return;

                    setAutoDiscoverRunning(true);
                    setAutoDiscoverProgress({ feedsDiscovered: 0, noFeedFound: 0, total: autoDiscoverStats?.withoutSource || 0 });

                    let offset = 0;
                    const batchSize = 20;
                    let hasMore = true;
                    let totalFeeds = 0;
                    let totalNoFeed = 0;

                    while (hasMore) {
                      try {
                        const res = await authFetch('/api/auto-discover-sources', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ batchSize, offset })
                        });
                        const data = await res.json();

                        if (data.success) {
                          totalFeeds += data.feedsDiscovered || 0;
                          totalNoFeed += data.noFeedFound || 0;
                          setAutoDiscoverProgress({ feedsDiscovered: totalFeeds, noFeedFound: totalNoFeed, total: autoDiscoverStats?.withoutSource || (totalFeeds + totalNoFeed) });
                          hasMore = data.hasMore;
                          offset = data.nextOffset;
                        } else {
                          hasMore = false;
                        }
                      } catch (err) {
                        console.error('Auto-discover batch error:', err);
                        hasMore = false;
                      }
                    }

                    setAutoDiscoverRunning(false);
                    const statsRes = await authFetch('/api/auto-discover-sources');
                    const statsData = await statsRes.json();
                    if (statsData.success) setAutoDiscoverStats(statsData.stats);
                    alert(`RSS discovery complete! Feeds found: ${totalFeeds}, No feed: ${totalNoFeed}`);
                  }}
                  disabled={autoDiscoverRunning || !autoDiscoverStats?.withoutSource}
                  className="px-4 py-2 bg-cyan-700 text-white rounded-xl text-sm font-medium hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {autoDiscoverRunning ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Rss size={16} />
                      Run Discovery
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Merge Duplicate Counterparts Section */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Users size={18} className="text-orange-500" />
                    Merge Duplicate Counterparts
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Detect and merge counterparts with identical or near-identical names
                  </p>
                </div>
                <button
                  disabled={mergeFinding}
                  onClick={async () => {
                    setMergeFinding(true);
                    try {
                      const res = await fetch('/api/merge-counterparts');
                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                      const data = await res.json();
                      if (data.success) { setMergeDuplicates(data); setMergeSelected(new Set()); }
                      else { alert('No duplicates found or error: ' + (data.error || 'unknown')); }
                    } catch (err) {
                      console.error('Failed to find duplicates:', err);
                      alert('Failed to find duplicates: ' + err.message);
                    } finally {
                      setMergeFinding(false);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text} disabled:opacity-50`}
                >
                  {mergeFinding ? 'Scanning...' : 'Find Duplicates'}
                </button>
              </div>

              {mergeDuplicates && (
                <div className="grid grid-cols-5 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Total Groups</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{mergeDuplicates.totalGroups}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Exact</p>
                    <p className={`text-lg font-bold text-green-600`}>{mergeDuplicates.exactCount}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Subsidiary</p>
                    <p className={`text-lg font-bold text-orange-600`}>{mergeDuplicates.subsidiaryCount || 0}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Fuzzy</p>
                    <p className={`text-lg font-bold text-amber-600`}>{mergeDuplicates.fuzzyCount}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Rejected</p>
                    <p className={`text-lg font-bold text-red-600`}>{mergeDuplicates.rejectedCount || 0}</p>
                  </div>
                </div>
              )}

              {mergeRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-orange-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Merging... {mergeProgress.merged} / {mergeProgress.total}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-600 transition-all duration-300"
                      style={{ width: `${mergeProgress.total > 0 ? (mergeProgress.merged / mergeProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Select all / Merge selected bar */}
              {mergeDuplicates?.groups?.length > 0 && (
                <div className={`flex items-center justify-between mb-3 p-2 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-rule)]'}`}>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        if (mergeSelected.size === mergeDuplicates.groups.length) {
                          setMergeSelected(new Set());
                        } else {
                          setMergeSelected(new Set(mergeDuplicates.groups.map((_, i) => i)));
                        }
                      }}
                      className={`flex items-center gap-1.5 text-xs font-medium ${themeClasses.text}`}
                    >
                      {mergeSelected.size === mergeDuplicates.groups.length && mergeSelected.size > 0
                        ? <CheckSquare size={16} className="text-orange-600" />
                        : mergeSelected.size > 0
                        ? <CheckSquare size={16} className="text-orange-400" />
                        : <Square size={16} className={themeClasses.textMuted} />
                      }
                      {mergeSelected.size === mergeDuplicates.groups.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {mergeSelected.size > 0 && (
                      <span className={`text-xs ${themeClasses.textMuted}`}>
                        {mergeSelected.size} of {mergeDuplicates.groups.length} selected
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {mergeSelected.size > 0 && (
                      <button
                        onClick={async () => {
                          if (mergeRunning) return;
                          const selectedGroups = mergeDuplicates.groups.filter((_, i) => mergeSelected.has(i));
                          if (!window.confirm(`Merge ${selectedGroups.length} selected group${selectedGroups.length > 1 ? 's' : ''}? This cannot be undone.`)) return;

                          setMergeRunning(true);
                          setMergeProgress({ merged: 0, total: selectedGroups.length });

                          let merged = 0;
                          for (const group of selectedGroups) {
                            const sorted = [...group.counterparts].sort((a, b) => {
                              if (b.dealCount !== a.dealCount) return b.dealCount - a.dealCount;
                              return (b.hasEnrichment ? 1 : 0) - (a.hasEnrichment ? 1 : 0);
                            });
                            const keeper = sorted[0];
                            for (let d = 1; d < sorted.length; d++) {
                              try {
                                const res = await authFetch('/api/merge-counterparts', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ keeperId: keeper.id, duplicateId: sorted[d].id })
                                });
                                await res.json();
                              } catch (err) {
                                console.error('Bulk merge error:', err);
                              }
                            }
                            merged++;
                            setMergeProgress({ merged, total: selectedGroups.length });
                          }

                          setMergeRunning(false);
                          setMergeSelected(new Set());
                          // Bust edge cache so refreshed data doesn't return stale counterparts
                          sessionStorage.setItem('counterparts_cache_bust', Date.now().toString());
                          // Optimistically remove merged duplicates from frontend state
                          const deletedIds = new Set();
                          for (const group of selectedGroups) {
                            const sorted = [...group.counterparts].sort((a, b) => (b.dealCount - a.dealCount) || ((b.hasEnrichment ? 1 : 0) - (a.hasEnrichment ? 1 : 0)));
                            sorted.slice(1).forEach(d => deletedIds.add(d.id));
                          }
                          if (deletedIds.size > 0) {
                            setCounterparts(prev => prev.filter(cp => !deletedIds.has(cp.id)));
                          }
                          const statsRes = await fetch('/api/merge-counterparts');
                          const statsData = await statsRes.json();
                          if (statsData.success) setMergeDuplicates(statsData);
                          refreshCentralizedData();
                          alert(`Merged ${merged} duplicate group${merged > 1 ? 's' : ''}.`);
                        }}
                        disabled={mergeRunning}
                        className="px-3 py-1.5 text-xs bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {mergeRunning ? (
                          <><Loader2 size={14} className="animate-spin" /> Merging...</>
                        ) : (
                          <><Users size={14} /> Merge Selected ({mergeSelected.size})</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Duplicate groups list */}
              {mergeDuplicates?.groups?.length > 0 && (
                <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                  {mergeDuplicates.groups.map((group, gi) => {
                    const sorted = [...group.counterparts].sort((a, b) => {
                      if (b.dealCount !== a.dealCount) return b.dealCount - a.dealCount;
                      return (b.hasEnrichment ? 1 : 0) - (a.hasEnrichment ? 1 : 0);
                    });
                    const keeper = sorted[0];
                    const isSelected = mergeSelected.has(gi);

                    return (
                      <div
                        key={gi}
                        onClick={() => {
                          if (mergeRunning) return;
                          setMergeSelected(prev => {
                            const next = new Set(prev);
                            if (next.has(gi)) next.delete(gi); else next.add(gi);
                            return next;
                          });
                        }}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                            : `${themeClasses.border} ${theme === 'dark' ? 'bg-[color:var(--gis-panel)] hover:bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)] hover:bg-[color:var(--gis-rule)]'}`
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {isSelected
                              ? <CheckSquare size={16} className="text-orange-600 shrink-0" />
                              : <Square size={16} className={`${themeClasses.textMuted} shrink-0`} />
                            }
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              group.confidence === 'exact'
                                ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                                : group.confidence === 'subsidiary'
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                            }`}>
                              {group.confidence}
                            </span>
                            <span className={`text-sm font-medium ${themeClasses.text}`}>
                              {group.names.join(' / ')}
                            </span>
                          </div>
                          <button
                            title="Reject — not duplicates"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (mergeRunning) return;
                              try {
                                const ids = group.counterparts.map(c => c.id);
                                const res = await authFetch('/api/merge-counterparts', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ action: 'reject', counterpartIds: ids })
                                });
                                const data = await res.json();
                                if (data.success) {
                                  setMergeDuplicates(prev => {
                                    if (!prev) return prev;
                                    const newGroups = prev.groups.filter((_, i) => i !== gi);
                                    return {
                                      ...prev,
                                      groups: newGroups,
                                      totalGroups: newGroups.length,
                                      totalDuplicates: newGroups.reduce((s, g) => s + g.counterparts.length - 1, 0),
                                      exactCount: newGroups.filter(g => g.confidence === 'exact').length,
                                      subsidiaryCount: newGroups.filter(g => g.confidence === 'subsidiary').length,
                                      fuzzyCount: newGroups.filter(g => g.confidence === 'fuzzy').length,
                                      rejectedCount: (prev.rejectedCount || 0) + (data.rejectedPairs || 0)
                                    };
                                  });
                                  setMergeSelected(prev => {
                                    const next = new Set();
                                    for (const idx of prev) {
                                      if (idx < gi) next.add(idx);
                                      else if (idx > gi) next.add(idx - 1);
                                    }
                                    return next;
                                  });
                                }
                              } catch (err) {
                                console.error('Failed to reject group:', err);
                              }
                            }}
                            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-400 hover:text-red-600 transition-colors shrink-0"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 ml-6">
                          {sorted.map(cp => (
                            <span key={cp.id} className={`text-xs px-2 py-1 rounded ${
                              cp.id === keeper.id
                                ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 font-medium'
                                : `${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-panel)]'} ${themeClasses.textMuted}`
                            }`}>
                              {cp.name} ({cp.dealCount} deals{cp.hasEnrichment ? ', enriched' : ''})
                              {cp.id === keeper.id && ' \u2190 keep'}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {mergeDuplicates?.groups?.length === 0 && (
                <p className={`text-sm ${themeClasses.textMuted} mb-4`}>No duplicates found.</p>
              )}
            </div>

            {/* Counterpart Source Discovery Section */}
            <div className={`${themeClasses.card} rounded-xl border-2 border-teal-300 dark:border-teal-600 p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Globe size={18} className="text-teal-500" />
                    Counterpart Source Discovery
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    RSS-first from admin-managed company newsroom URLs · Cron every 3 hours
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const [statsRes, sourcesRes] = await Promise.all([
                        authFetch('/api/discover-counterpart-deals'),
                        fetch('/api/counterpart-sources')
                      ]);
                      const statsData = await statsRes.json();
                      const sourcesData = await sourcesRes.json();
                      if (statsData.success) setCpSourceStats(statsData.stats);
                      if (sourcesData.data) setCounterpartSources(sourcesData.data);
                    } catch (err) {
                      console.error('Failed to fetch counterpart source stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {cpSourceStats && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Active Sources</p>
                    <p className={`text-lg font-bold text-teal-600`}>{cpSourceStats.activeSources}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Inactive</p>
                    <p className={`text-lg font-bold text-[color:var(--gis-dim)]`}>{cpSourceStats.inactiveSources}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Deals Created</p>
                    <p className={`text-lg font-bold text-green-600`}>{cpSourceStats.totalDealsCreated}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Last Run</p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {cpSourceStats.lastRun ? new Date(cpSourceStats.lastRun).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                </div>
              )}

              {cpSourceStats && (
                <div className={`text-sm ${themeClasses.textMuted} mb-4`}>
                  URLs: <span className="text-green-600">{cpSourceStats.urlVerified} verified</span> / <span className="text-red-600">{cpSourceStats.urlUnverified} invalid</span> / <span className="text-[color:var(--gis-dim)]">{cpSourceStats.urlUnchecked} unchecked</span>
                </div>
              )}

              {cpSourceRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-teal-900/20' : 'bg-teal-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-teal-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Processing... {cpSourceProgress.processed}/{cpSourceProgress.total} sources · {cpSourceProgress.dealsCreated} deals created
                      {cpSourceProgress.errors > 0 && ` · ${cpSourceProgress.errors} errors`}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-600 transition-all duration-300"
                      style={{ width: `${cpSourceProgress.total > 0 ? (cpSourceProgress.processed / cpSourceProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 mb-4">
                <button
                  onClick={async () => {
                    if (cpSourceRunning) return;
                    setCpSourceRunning(true);
                    setCpSourceLog([]);
                    // Auto-load sources + stats if not yet loaded
                    let currentSources = counterpartSources;
                    let currentStats = cpSourceStats;
                    if (!currentSources.length || !currentStats) {
                      try {
                        const [statsRes, sourcesRes] = await Promise.all([
                          authFetch('/api/discover-counterpart-deals'),
                          fetch('/api/counterpart-sources')
                        ]);
                        const statsData = await statsRes.json();
                        const sourcesData = await sourcesRes.json();
                        if (statsData.success) { currentStats = statsData.stats; setCpSourceStats(statsData.stats); }
                        if (sourcesData.data) { currentSources = sourcesData.data; setCounterpartSources(sourcesData.data); }
                      } catch (e) { console.error('Failed to pre-load counterpart sources:', e); }
                    }
                    const activeCount = currentSources.filter(s => s.active).length || currentStats?.activeSources || 0;
                    setCpSourceProgress({ processed: 0, dealsCreated: 0, errors: 0, total: activeCount });
                    try {
                      const res = await authFetch('/api/discover-counterpart-deals', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({})
                      });
                      if (!res.ok) {
                        const errText = await res.text();
                        throw new Error(errText);
                      }
                      const data = await res.json();
                      setCpSourceProgress({
                        processed: data.processed,
                        dealsCreated: data.dealsCreated,
                        errors: data.errors?.length || 0,
                        total: data.processed
                      });
                      if (data.sourceResults) setCpSourceLog(data.sourceResults);
                      // Refresh stats
                      const statsRes = await authFetch('/api/discover-counterpart-deals');
                      const statsData = await statsRes.json();
                      if (statsData.success) setCpSourceStats(statsData.stats);
                      refreshCentralizedData();
                    } catch (err) {
                      console.error('Counterpart source discovery error:', err);
                      alert('Discovery error: ' + err.message);
                    }
                    setCpSourceRunning(false);
                  }}
                  disabled={cpSourceRunning}
                  className="px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {cpSourceRunning ? (
                    <><Loader2 size={16} className="animate-spin" /> Running...</>
                  ) : (
                    <><Globe size={16} /> Run Counterpart Source Discovery</>
                  )}
                </button>
              </div>

              {/* Counterpart Sources CRUD */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-medium ${themeClasses.text}`}>Company Sources ({counterpartSources.length})</h4>
                  <button
                    onClick={() => setEditingCpSource({ name: '', company_url: '', feed_url: '', scrape_url: '', counterpart_id: '', category: 'infrastructure', active: true, priority: 'normal' })}
                    className="px-3 py-1.5 text-xs rounded-lg bg-teal-600 text-white hover:bg-teal-700 flex items-center gap-1"
                  >
                    <Plus size={14} /> Add Source
                  </button>
                </div>

                {editingCpSource && (
                  <div className={`p-4 rounded-lg mb-3 border ${themeClasses.border} ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <input
                        placeholder="Company name"
                        value={editingCpSource.name}
                        onChange={(e) => setEditingCpSource({...editingCpSource, name: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                      />
                      <select
                        value={editingCpSource.category}
                        onChange={(e) => setEditingCpSource({...editingCpSource, category: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                      >
                        <option value="infrastructure">Infrastructure</option>
                        <option value="solar">Solar</option>
                        <option value="wind">Wind</option>
                        <option value="renewables">Renewables</option>
                        <option value="storage">Storage</option>
                        <option value="data-centers">Data Centers</option>
                        <option value="hydrogen">Hydrogen</option>
                        <option value="nuclear">Nuclear</option>
                        <option value="biomass">Biomass</option>
                        <option value="ev-charging">EV / Charging</option>
                        <option value="transport">Transport & Logistics</option>
                        <option value="oil_gas">Oil & Gas</option>
                        <option value="hospitality">Hospitality</option>
                        <option value="agriculture">Agriculture & Natural Resources</option>
                        <option value="mining">Mining & Extractives</option>
                        <option value="defense">Defense</option>
                        <option value="telecom">Telecom & Digital Infra</option>
                        <option value="water_waste">Water & Waste</option>
                      </select>
                    </div>
                    <input
                      placeholder="Company newsroom URL (e.g. https://company.com/news)"
                      value={editingCpSource.company_url}
                      onChange={(e) => setEditingCpSource({...editingCpSource, company_url: e.target.value})}
                      className={`w-full px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                    />
                    <input
                      placeholder="RSS Feed URL (optional — auto-discovered if blank)"
                      value={editingCpSource.feed_url || ''}
                      onChange={(e) => setEditingCpSource({...editingCpSource, feed_url: e.target.value})}
                      className={`w-full px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                    />
                    <input
                      placeholder="Scrape URL (fallback for sources without RSS)"
                      value={editingCpSource.scrape_url || ''}
                      onChange={(e) => setEditingCpSource({...editingCpSource, scrape_url: e.target.value})}
                      className={`w-full px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                    />
                    <div className="mb-3">
                      <label className={`block text-xs ${themeClasses.textMuted} mb-1`}>Link to counterpart (optional — auto-resolved from URL domain)</label>
                      <select
                        value={editingCpSource.counterpart_id || ''}
                        onChange={(e) => setEditingCpSource({...editingCpSource, counterpart_id: e.target.value || null})}
                        className={`w-full px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                      >
                        <option value="">Auto-resolve from URL</option>
                        {counterparts.slice(0, 200).map(cp => (
                          <option key={cp.id} value={cp.id}>{cp.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <label className={`flex items-center gap-2 text-sm ${themeClasses.text}`}>
                          <input
                            type="checkbox"
                            checked={editingCpSource.active}
                            onChange={(e) => setEditingCpSource({...editingCpSource, active: e.target.checked})}
                            className="rounded"
                          />
                          Active
                        </label>
                        <label className={`flex items-center gap-2 text-sm ${themeClasses.text}`}>
                          <input
                            type="checkbox"
                            checked={editingCpSource.priority === 'high'}
                            onChange={(e) => setEditingCpSource({...editingCpSource, priority: e.target.checked ? 'high' : 'normal'})}
                            className="rounded accent-red-500"
                          />
                          <span className="text-red-500 font-medium">High priority</span>
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingCpSource(null)}
                          className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const isNew = !editingCpSource.id;
                              const response = await authFetch('/api/counterpart-sources', {
                                method: isNew ? 'POST' : 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(editingCpSource)
                              });
                              const result = await response.json();
                              if (result.success) {
                                if (isNew) {
                                  setCounterpartSources(prev => [...prev, result.data]);
                                } else {
                                  setCounterpartSources(prev => prev.map(s => s.id === editingCpSource.id ? result.data : s));
                                }
                                setEditingCpSource(null);
                              }
                            } catch (err) {
                              console.error('Save counterpart source error:', err);
                            }
                          }}
                          disabled={!editingCpSource.name || !editingCpSource.company_url}
                          className="px-3 py-1.5 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                        >
                          {editingCpSource.id ? 'Update' : 'Add'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className={`rounded-lg border ${themeClasses.border} overflow-hidden max-h-[300px] overflow-y-auto`}>
                  {counterpartSources.length === 0 ? (
                    <div className={`p-4 text-sm ${themeClasses.textMuted} text-center`}>
                      Click "Refresh Stats" to load sources, or add company newsroom URLs
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Name</th>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>URL / Feed</th>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Counterpart</th>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Category</th>
                          <th className={`px-3 py-2 text-center ${themeClasses.textMuted} font-medium`}>Status</th>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Last Crawled</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {counterpartSources.map(s => {
                          const linkedCp = s.counterpart_id ? counterparts.find(cp => cp.id === s.counterpart_id) : null;
                          return (
                            <tr key={s.id} className={`border-t ${themeClasses.border}`}>
                              <td className={`px-3 py-2 ${themeClasses.text} font-medium`}>
                                {s.name}
                                {s.priority === 'high' && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">HIGH</span>}
                              </td>
                              <td className={`px-3 py-2 ${themeClasses.textMuted}`}>
                                <div className="truncate max-w-[200px]" title={s.company_url}>{s.company_url?.replace(/^https?:\/\//, '').slice(0, 30)}</div>
                                {s.feed_url && <div className="text-xs text-teal-500 truncate max-w-[200px]" title={s.feed_url}>RSS: {s.feed_url.replace(/^https?:\/\//, '').slice(0, 30)}...</div>}
                                {s.scrape_url && <div className="text-xs text-orange-500 truncate max-w-[200px]" title={s.scrape_url}>Scrape: {s.scrape_url.replace(/^https?:\/\//, '').slice(0, 30)}...</div>}
                              </td>
                              <td className={`px-3 py-2 ${themeClasses.textMuted} text-xs`}>
                                {linkedCp ? linkedCp.name : (s.counterpart_id ? '(linked)' : '—')}
                              </td>
                              <td className={`px-3 py-2`}>
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  s.category === 'infrastructure' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                  s.category === 'solar' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                  s.category === 'renewables' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                  s.category === 'data-centers' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                  'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] dark:bg-[color:var(--gis-panel)] dark:text-[color:var(--gis-dim)]'
                                }`}>{s.category}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {s.active ? (
                                  <span className="inline-block w-2 h-2 rounded-full bg-green-500" title="Active" />
                                ) : (
                                  <span className="inline-block w-2 h-2 rounded-full bg-slate-400" title="Inactive" />
                                )}
                              </td>
                              <td className={`px-3 py-2 ${themeClasses.textMuted} text-xs`}>
                                {s.last_crawled_at ? new Date(s.last_crawled_at).toLocaleDateString() : 'Never'}
                                {s.discovery_status === 'error' && (
                                  <span className="ml-1 text-red-500" title={s.discovery_error}>⚠</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => setEditingCpSource({ ...s })}
                                    className={`p-1 rounded hover:bg-[color:var(--gis-rule)] dark:hover:bg-[color:var(--gis-rule)] ${themeClasses.textMuted}`}
                                    title="Edit"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm(`Delete "${s.name}"?`)) return;
                                      try {
                                        await authFetch(`/api/counterpart-sources?id=${s.id}`, { method: 'DELETE' });
                                        setCounterpartSources(prev => prev.filter(x => x.id !== s.id));
                                      } catch (err) {
                                        console.error('Delete counterpart source error:', err);
                                      }
                                    }}
                                    className={`p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500`}
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Discovery Results Log */}
              {cpSourceLog.length > 0 && (
                <div className="mt-4">
                  <h4 className={`text-sm font-medium ${themeClasses.text} mb-2`}>Results Log ({cpSourceLog.length} sources)</h4>
                  <div className={`rounded-lg border ${themeClasses.border} overflow-hidden max-h-[250px] overflow-y-auto`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Source</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Articles</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Found</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cpSourceLog.map((entry, i) => (
                          <tr key={i} className={`border-t ${themeClasses.border}`}>
                            <td className={`px-3 py-1.5 ${themeClasses.text}`}>{entry.name}</td>
                            <td className={`px-3 py-1.5 text-right ${themeClasses.textMuted}`}>{entry.articlesNew ?? 0}</td>
                            <td className={`px-3 py-1.5 text-right ${themeClasses.textMuted}`}>{entry.dealsFound}</td>
                            <td className={`px-3 py-1.5 text-right font-medium ${entry.dealsCreated > 0 ? 'text-green-600' : themeClasses.textMuted}`}>{entry.dealsCreated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Journal Discovery Section */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Newspaper size={18} className="text-violet-500" />
                    Journal Discovery
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Fetch RSS feeds from industry journals, AI-extract deals · Cron every 3 hours
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const [statsRes, sourcesRes] = await Promise.all([
                        authFetch('/api/discover-journal-deals'),
                        fetch('/api/journal-sources')
                      ]);
                      const statsData = await statsRes.json();
                      const sourcesData = await sourcesRes.json();
                      if (statsData.success) setJournalDiscoveryStats(statsData.stats);
                      if (sourcesData.data) setJournalSources(sourcesData.data);
                    } catch (err) {
                      console.error('Failed to fetch journal stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {journalDiscoveryStats && (
                <div className="grid grid-cols-5 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Active Journals</p>
                    <p className={`text-lg font-bold text-violet-600`}>{journalDiscoveryStats.activeJournals}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Inactive</p>
                    <p className={`text-lg font-bold text-[color:var(--gis-dim)]`}>{journalDiscoveryStats.inactiveJournals}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Auto-Deactivated</p>
                    <p className={`text-lg font-bold text-amber-500`}>{journalDiscoveryStats.autoDeactivated || 0}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Deals Created</p>
                    <p className={`text-lg font-bold text-green-600`}>{journalDiscoveryStats.totalDealsCreated}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Last Run</p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {journalDiscoveryStats.lastRun ? new Date(journalDiscoveryStats.lastRun).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                </div>
              )}

              {/* Auto-deactivated journals list */}
              {journalDiscoveryStats?.autoDeactivatedJournals?.length > 0 && (
                <div className={`mb-4 p-3 rounded-lg border ${theme === 'dark' ? 'border-amber-800 bg-amber-900/10' : 'border-amber-200 bg-amber-50'}`}>
                  <p className={`text-sm font-medium text-amber-600 mb-2`}>
                    Auto-deactivated ({journalDiscoveryStats.autoDeactivatedJournals.length} journals — 20+ consecutive runs with 0 deals)
                  </p>
                  <div className="space-y-1">
                    {journalDiscoveryStats.autoDeactivatedJournals.map(j => (
                      <div key={j.id} className="flex items-center justify-between text-sm">
                        <span className={themeClasses.text}>{j.name} <span className={themeClasses.textMuted}>· {j.consecutiveZeroRuns} zero runs</span></span>
                        <button
                          onClick={async () => {
                            try {
                              await authFetch('/api/journal-sources', {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ id: j.id, active: true, consecutive_zero_runs: 0, auto_deactivated_at: null })
                              });
                              setJournalDiscoveryStats(prev => ({
                                ...prev,
                                activeJournals: (prev.activeJournals || 0) + 1,
                                autoDeactivated: (prev.autoDeactivated || 1) - 1,
                                autoDeactivatedJournals: prev.autoDeactivatedJournals.filter(aj => aj.id !== j.id)
                              }));
                            } catch (err) { console.error('Reactivate failed:', err); }
                          }}
                          className="px-2 py-0.5 text-xs rounded bg-amber-100 hover:bg-amber-200 text-amber-700 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 dark:text-amber-400"
                        >
                          Reactivate
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {journalDiscoveryStats && (
                <div className={`text-sm ${themeClasses.textMuted} mb-4`}>
                  URLs: <span className="text-green-600">{journalDiscoveryStats.urlVerified} verified</span> / <span className="text-red-600">{journalDiscoveryStats.urlUnverified} invalid</span> / <span className="text-[color:var(--gis-dim)]">{journalDiscoveryStats.urlUnchecked} unchecked</span>
                </div>
              )}

              {journalDiscoveryRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-violet-900/20' : 'bg-violet-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-violet-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Processing... {journalDiscoveryProgress.processed}/{journalDiscoveryProgress.total} journals · {journalDiscoveryProgress.dealsCreated} deals created
                      {journalDiscoveryProgress.errors > 0 && ` · ${journalDiscoveryProgress.errors} errors`}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-[color:var(--gis-rule)] dark:bg-[color:var(--gis-rule)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-violet-600 transition-all duration-300"
                      style={{ width: `${journalDiscoveryProgress.total > 0 ? (journalDiscoveryProgress.processed / journalDiscoveryProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 mb-4">
                <button
                  onClick={async () => {
                    if (journalDiscoveryRunning) return;
                    setJournalDiscoveryRunning(true);
                    setJournalDiscoveryLog([]);
                    // Auto-load journal sources + stats if not yet loaded (triggers auto-seeding)
                    let currentSources = journalSources;
                    let currentStats = journalDiscoveryStats;
                    if (!currentSources.length || !currentStats) {
                      try {
                        const [statsRes, sourcesRes] = await Promise.all([
                          authFetch('/api/discover-journal-deals'),
                          fetch('/api/journal-sources')
                        ]);
                        const statsData = await statsRes.json();
                        const sourcesData = await sourcesRes.json();
                        if (statsData.success) { currentStats = statsData.stats; setJournalDiscoveryStats(statsData.stats); }
                        if (sourcesData.data) { currentSources = sourcesData.data; setJournalSources(sourcesData.data); }
                      } catch (e) { console.error('Failed to pre-load journal sources:', e); }
                    }
                    const activeCount = currentSources.filter(j => j.active).length || currentStats?.activeJournals || 10;
                    setJournalDiscoveryProgress({ processed: 0, dealsCreated: 0, newCounterparts: 0, errors: 0, total: activeCount });
                    try {
                      const res = await authFetch('/api/discover-journal-deals', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({})
                      });
                      if (!res.ok) {
                        const errText = await res.text();
                        throw new Error(errText);
                      }
                      const data = await res.json();
                      setJournalDiscoveryProgress({
                        processed: data.processed,
                        dealsCreated: data.dealsCreated,
                        newCounterparts: data.newCounterpartsCreated,
                        errors: data.errors?.length || 0,
                        total: data.processed
                      });
                      if (data.journalResults) setJournalDiscoveryLog(data.journalResults);
                      // Refresh stats
                      const statsRes = await authFetch('/api/discover-journal-deals');
                      const statsData = await statsRes.json();
                      if (statsData.success) setJournalDiscoveryStats(statsData.stats);
                      refreshCentralizedData();
                    } catch (err) {
                      console.error('Journal discovery error:', err);
                      alert('Journal discovery error: ' + err.message);
                    }
                    setJournalDiscoveryRunning(false);
                  }}
                  disabled={journalDiscoveryRunning}
                  className="px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {journalDiscoveryRunning ? (
                    <><Loader2 size={16} className="animate-spin" /> Running...</>
                  ) : (
                    <><Newspaper size={16} /> Run Journal Discovery</>
                  )}
                </button>
              </div>

              {/* Journal Sources CRUD */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-medium ${themeClasses.text}`}>Journal Sources ({journalSources.length})</h4>
                  <button
                    onClick={() => setEditingJournal({ name: '', website: '', category: 'infrastructure', feed_url: '', scrape_url: '', active: true })}
                    className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white hover:bg-violet-700 flex items-center gap-1"
                  >
                    <Plus size={14} /> Add Journal
                  </button>
                </div>

                {editingJournal && (
                  <div className={`p-4 rounded-lg mb-3 border ${themeClasses.border} ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <input
                        placeholder="Journal name"
                        value={editingJournal.name}
                        onChange={(e) => setEditingJournal({...editingJournal, name: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                      />
                      <select
                        value={editingJournal.category}
                        onChange={(e) => setEditingJournal({...editingJournal, category: e.target.value})}
                        className={`px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                      >
                        <option value="infrastructure">Infrastructure</option>
                        <option value="solar">Solar</option>
                        <option value="wind">Wind</option>
                        <option value="renewables">Renewables</option>
                        <option value="storage">Storage</option>
                        <option value="data-centers">Data Centers</option>
                        <option value="hydrogen">Hydrogen</option>
                        <option value="nuclear">Nuclear</option>
                        <option value="biomass">Biomass</option>
                        <option value="ev-charging">EV / Charging</option>
                        <option value="transport">Transport & Logistics</option>
                        <option value="oil_gas">Oil & Gas</option>
                        <option value="hospitality">Hospitality</option>
                        <option value="agriculture">Agriculture & Natural Resources</option>
                        <option value="mining">Mining & Extractives</option>
                        <option value="defense">Defense</option>
                        <option value="telecom">Telecom & Digital Infra</option>
                        <option value="water_waste">Water & Waste</option>
                      </select>
                    </div>
                    <input
                      placeholder="Website domain (e.g. ijglobal.com)"
                      value={editingJournal.website}
                      onChange={(e) => setEditingJournal({...editingJournal, website: e.target.value})}
                      className={`w-full px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                    />
                    <input
                      placeholder="RSS Feed URL (optional — auto-discovered if blank)"
                      value={editingJournal.feed_url || ''}
                      onChange={(e) => setEditingJournal({...editingJournal, feed_url: e.target.value})}
                      className={`w-full px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                    />
                    <input
                      placeholder="Scrape URL (fallback for sources without RSS)"
                      value={editingJournal.scrape_url || ''}
                      onChange={(e) => setEditingJournal({...editingJournal, scrape_url: e.target.value})}
                      className={`w-full px-3 py-2 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} mb-3`}
                    />
                    <div className="flex items-center gap-3 mb-3">
                      <label className={`text-sm ${themeClasses.text} w-32`}>Max articles/run:</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        placeholder="Default: 10"
                        value={editingJournal.max_articles || ''}
                        onChange={(e) => setEditingJournal({...editingJournal, max_articles: e.target.value ? parseInt(e.target.value) : null})}
                        className={`w-24 px-3 py-1.5 rounded-lg text-sm border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text}`}
                      />
                      <span className={`text-xs ${themeClasses.textMuted}`}>(empty = use global default of 10)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <label className={`flex items-center gap-2 text-sm ${themeClasses.text}`}>
                          <input
                            type="checkbox"
                            checked={editingJournal.active}
                            onChange={(e) => setEditingJournal({...editingJournal, active: e.target.checked})}
                            className="rounded"
                          />
                          Active
                        </label>
                        <label className={`flex items-center gap-2 text-sm ${themeClasses.text}`}>
                          <input
                            type="checkbox"
                            checked={editingJournal.priority === 'high'}
                            onChange={(e) => setEditingJournal({...editingJournal, priority: e.target.checked ? 'high' : 'normal'})}
                            className="rounded accent-red-500"
                          />
                          <span className="text-red-500 font-medium">High priority</span>
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingJournal(null)}
                          className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const isNew = !editingJournal.id;
                              const response = await authFetch('/api/journal-sources', {
                                method: isNew ? 'POST' : 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(editingJournal)
                              });
                              const result = await response.json();
                              if (result.success) {
                                if (isNew) {
                                  setJournalSources(prev => [...prev, result.data]);
                                } else {
                                  setJournalSources(prev => prev.map(s => s.id === editingJournal.id ? result.data : s));
                                }
                                setEditingJournal(null);
                              }
                            } catch (err) {
                              console.error('Save journal source error:', err);
                            }
                          }}
                          disabled={!editingJournal.name || !editingJournal.website}
                          className="px-3 py-1.5 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                        >
                          {editingJournal.id ? 'Update' : 'Add'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className={`rounded-lg border ${themeClasses.border} overflow-hidden max-h-[300px] overflow-y-auto`}>
                  {journalSources.length === 0 ? (
                    <div className={`p-4 text-sm ${themeClasses.textMuted} text-center`}>
                      Click "Refresh Stats" to load journal sources
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Name</th>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Domain / Feed</th>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Category</th>
                          <th className={`px-3 py-2 text-center ${themeClasses.textMuted} font-medium`}>Status</th>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Last Crawled</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {journalSources.map(j => (
                          <tr key={j.id} className={`border-t ${themeClasses.border}`}>
                            <td className={`px-3 py-2 ${themeClasses.text} font-medium`}>
                              {j.name}
                              {j.priority === 'high' && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">HIGH</span>}
                            </td>
                            <td className={`px-3 py-2 ${themeClasses.textMuted}`}>
                              <div>{j.website}</div>
                              {j.feed_url && <div className="text-xs text-violet-500 truncate max-w-[200px]" title={j.feed_url}>RSS: {j.feed_url.replace(/^https?:\/\//, '').slice(0, 30)}...</div>}
                              {j.scrape_url && <div className="text-xs text-orange-500 truncate max-w-[200px]" title={j.scrape_url}>Scrape: {j.scrape_url.replace(/^https?:\/\//, '').slice(0, 30)}...</div>}
                            </td>
                            <td className={`px-3 py-2`}>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                j.category === 'infrastructure' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                j.category === 'solar' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                j.category === 'wind' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' :
                                j.category === 'renewables' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                j.category === 'storage' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                j.category === 'data-centers' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                j.category === 'hospitality' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                j.category === 'hydrogen' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' :
                                j.category === 'nuclear' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' :
                                j.category === 'biomass' ? 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400' :
                                j.category === 'ev-charging' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' :
                                j.category === 'transport' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                j.category === 'oil_gas' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                j.category === 'agriculture' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                j.category === 'mining' ? 'bg-stone-100 text-stone-700 dark:bg-stone-900/30 dark:text-stone-400' :
                                j.category === 'defense' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' :
                                j.category === 'telecom' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                j.category === 'water_waste' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' :
                                'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] dark:bg-[color:var(--gis-panel)] dark:text-[color:var(--gis-dim)]'
                              }`}>{j.category}</span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {j.active ? (
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500" title="Active" />
                              ) : (
                                <span className="inline-block w-2 h-2 rounded-full bg-slate-400" title="Inactive" />
                              )}
                            </td>
                            <td className={`px-3 py-2 ${themeClasses.textMuted} text-xs`}>
                              {j.last_crawled_at ? new Date(j.last_crawled_at).toLocaleDateString() : 'Never'}
                              {j.discovery_status === 'error' && (
                                <span className="ml-1 text-red-500" title={j.discovery_error}>⚠</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setEditingJournal({ ...j })}
                                  className={`p-1 rounded hover:bg-[color:var(--gis-rule)] dark:hover:bg-[color:var(--gis-rule)] ${themeClasses.textMuted}`}
                                  title="Edit"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete "${j.name}"?`)) return;
                                    try {
                                      await authFetch(`/api/journal-sources?id=${j.id}`, { method: 'DELETE' });
                                      setJournalSources(prev => prev.filter(s => s.id !== j.id));
                                    } catch (err) {
                                      console.error('Delete journal source error:', err);
                                    }
                                  }}
                                  className={`p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500`}
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Journal Discovery Results Log */}
              {journalDiscoveryLog.length > 0 && (
                <div className="mt-4">
                  <h4 className={`text-sm font-medium ${themeClasses.text} mb-2`}>Results Log ({journalDiscoveryLog.length} journals)</h4>
                  <div className={`rounded-lg border ${themeClasses.border} overflow-hidden max-h-[250px] overflow-y-auto`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}>
                          <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Journal</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Articles</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Found</th>
                          <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {journalDiscoveryLog.map((entry, i) => (
                          <tr key={i} className={`border-t ${themeClasses.border}`}>
                            <td className={`px-3 py-1.5 ${themeClasses.text}`}>{entry.name}</td>
                            <td className={`px-3 py-1.5 text-right ${themeClasses.textMuted}`}>{entry.articlesNew ?? entry.citations ?? 0}</td>
                            <td className={`px-3 py-1.5 text-right ${themeClasses.textMuted}`}>{entry.dealsFound}</td>
                            <td className={`px-3 py-1.5 text-right font-medium ${entry.dealsCreated > 0 ? 'text-green-600' : themeClasses.textMuted}`}>{entry.dealsCreated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Deep Historical Backfill Card */}
            <div className={`${themeClasses.card} rounded-xl border-2 border-indigo-300 dark:border-indigo-600 p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <History size={18} className="text-indigo-500" />
                    Deep Historical Backfill
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Search 36 months of press releases for Tier A counterparts · ~$0.12/counterpart
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await authFetch('/api/backfill-counterpart-deep');
                      const data = await res.json();
                      if (data.success) setDeepBackfillStats(data.stats);
                    } catch (err) {
                      console.error('Failed to fetch deep backfill stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {deepBackfillStats && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Tier A Total</p>
                    <p className="text-lg font-bold text-indigo-600">{deepBackfillStats.tierATotal}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Backfilled</p>
                    <p className="text-lg font-bold text-indigo-600">{deepBackfillStats.backfilled}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Deals Created</p>
                    <p className="text-lg font-bold text-green-600">{deepBackfillStats.dealsCreatedByBackfill}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Cost Est</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>~${((deepBackfillStats.tierATotal - deepBackfillStats.backfilled) * 0.12).toFixed(2)}</p>
                  </div>
                </div>
              )}

              {/* Counterpart Selector */}
              <div className="relative mb-4">
                <label className={`text-xs font-medium ${themeClasses.textMuted} mb-1 block`}>Select Counterpart</label>
                {deepBackfillCpId ? (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-sm font-medium">
                      <Building2 size={14} />
                      {deepBackfillCpName}
                      <button
                        onClick={() => { setDeepBackfillCpId(null); setDeepBackfillCpName(''); setDeepBackfillCpSearch(''); setDeepBackfillResult(null); }}
                        className="ml-1 hover:text-indigo-900 dark:hover:text-indigo-100"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={deepBackfillCpSearch}
                      onChange={e => setDeepBackfillCpSearch(e.target.value)}
                      placeholder="Type counterpart name..."
                      className={`w-full px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {deepBackfillCpSearch.length >= 2 && (
                      <div className={`absolute z-10 mt-1 w-full rounded-lg border ${themeClasses.border} ${themeClasses.card} shadow-lg max-h-60 overflow-y-auto`}>
                        {counterparts
                          .filter(cp => cp.name?.toLowerCase().includes(deepBackfillCpSearch.toLowerCase()))
                          .slice(0, 10)
                          .map(cp => (
                            <button
                              key={cp.id}
                              onClick={() => {
                                setDeepBackfillCpId(cp.id);
                                setDeepBackfillCpName(cp.name);
                                setDeepBackfillCpSearch('');
                                setDeepBackfillResult(null);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm ${themeClasses.text} hover:bg-indigo-50 dark:hover:bg-indigo-900/30 flex items-center gap-2`}
                            >
                              <Building2 size={14} className="text-indigo-400 flex-shrink-0" />
                              <span className="truncate">{cp.name}</span>
                              {cp.category && <span className={`text-xs ${themeClasses.textMuted} ml-auto flex-shrink-0`}>{cp.category}</span>}
                            </button>
                          ))}
                        {counterparts.filter(cp => cp.name?.toLowerCase().includes(deepBackfillCpSearch.toLowerCase())).length === 0 && (
                          <p className={`px-3 py-2 text-sm ${themeClasses.textMuted}`}>No matching counterparts</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Run Button */}
              <button
                onClick={async () => {
                  if (!deepBackfillCpId || deepBackfillRunning) return;
                  setDeepBackfillRunning(true);
                  setDeepBackfillResult(null);
                  try {
                    const res = await authFetch('/api/backfill-counterpart-deep', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ counterpartId: deepBackfillCpId })
                    });
                    const data = await res.json();
                    if (data.success) {
                      setDeepBackfillResult(data);
                      // Refresh stats
                      const statsRes = await authFetch('/api/backfill-counterpart-deep');
                      const statsData = await statsRes.json();
                      if (statsData.success) setDeepBackfillStats(statsData.stats);
                    } else {
                      setDeepBackfillResult({ error: data.error || 'Backfill failed' });
                    }
                  } catch (err) {
                    setDeepBackfillResult({ error: err.message });
                  } finally {
                    setDeepBackfillRunning(false);
                  }
                }}
                disabled={!deepBackfillCpId || deepBackfillRunning}
                className={`w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
                  !deepBackfillCpId || deepBackfillRunning
                    ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)] dark:bg-[color:var(--gis-rule)] dark:text-[color:var(--gis-dim)] cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}
              >
                {deepBackfillRunning ? (
                  <><Loader2 size={16} className="animate-spin" /> Running backfill for {deepBackfillCpName}...</>
                ) : (
                  <><History size={16} /> {deepBackfillCpId ? `Run Deep Backfill for ${deepBackfillCpName}` : 'Select a counterpart'}</>
                )}
              </button>

              {/* Running indicator */}
              {deepBackfillRunning && (
                <div className="mt-3">
                  <div className={`w-full h-2 rounded-full ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-indigo-100'} overflow-hidden`}>
                    <div className="h-full bg-indigo-500 rounded-full animate-pulse" style={{ width: '100%' }} />
                  </div>
                  <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                    Searching 12 quarters of press releases... this may take 2-4 minutes
                  </p>
                </div>
              )}

              {/* Error */}
              {deepBackfillResult?.error && (
                <div className="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-600 dark:text-red-400">{deepBackfillResult.error}</p>
                </div>
              )}

              {/* Results Table */}
              {deepBackfillResult?.quarterResults && (
                <div className="mt-4">
                  <h4 className={`text-sm font-medium ${themeClasses.text} mb-2`}>
                    Results for {deepBackfillResult.counterpart}: {deepBackfillResult.dealsCreated} deals from {deepBackfillResult.citationsFound} citations across {deepBackfillResult.quartersSearched} quarters
                  </h4>
                  <div className={`rounded-lg border ${themeClasses.border} overflow-hidden max-h-[300px] overflow-y-auto`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}>
                          <th className={`px-3 py-2 text-left text-xs font-medium ${themeClasses.textMuted}`}>Quarter</th>
                          <th className={`px-3 py-2 text-right text-xs font-medium ${themeClasses.textMuted}`}>Citations</th>
                          <th className={`px-3 py-2 text-right text-xs font-medium ${themeClasses.textMuted}`}>Articles</th>
                          <th className={`px-3 py-2 text-right text-xs font-medium ${themeClasses.textMuted}`}>Deals Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deepBackfillResult.quarterResults.map((qr, i) => (
                          <tr key={i} className={`border-t ${themeClasses.border} ${qr.dealsCreated > 0 ? (theme === 'dark' ? 'bg-green-900/10' : 'bg-green-50/50') : ''}`}>
                            <td className={`px-3 py-1.5 ${themeClasses.text} font-medium`}>{qr.quarter}</td>
                            <td className={`px-3 py-1.5 text-right ${themeClasses.text}`}>{qr.citations}</td>
                            <td className={`px-3 py-1.5 text-right ${themeClasses.text}`}>{qr.articlesProcessed || 0}</td>
                            <td className={`px-3 py-1.5 text-right font-medium ${qr.dealsCreated > 0 ? 'text-green-600' : themeClasses.textMuted}`}>{qr.dealsCreated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {deepBackfillResult.errors?.length > 0 && (
                    <p className={`text-xs text-red-500 mt-2`}>{deepBackfillResult.errors.length} error(s) during backfill</p>
                  )}
                  <p className={`text-xs ${themeClasses.textMuted} mt-2`}>
                    Completed in {((deepBackfillResult.elapsedMs || 0) / 1000).toFixed(1)}s · {deepBackfillResult.newCounterpartsCreated || 0} new counterparts created
                  </p>
                </div>
              )}
            </div>

            {/* Tearsheet Downloads Card */}
            <div className={`${themeClasses.card} rounded-xl border-2 border-rose-300 dark:border-rose-600 p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <FileText size={18} className="text-rose-500" />
                    Tearsheet Downloads
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    PDF tearsheet generation activity (last 30 days)
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/tearsheet-stats');
                      const data = await res.json();
                      if (data.success) setTearsheetStats(data);
                    } catch (err) {
                      console.error('Failed to fetch tearsheet stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {tearsheetStats && (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-rose-900/20' : 'bg-rose-50'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>This Month</p>
                      <p className="text-lg font-bold text-rose-600">{tearsheetStats.stats.totalMonth}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-rose-900/20' : 'bg-rose-50'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>This Week</p>
                      <p className="text-lg font-bold text-rose-600">{tearsheetStats.stats.totalWeek}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>Unique Users</p>
                      <p className={`text-lg font-bold ${themeClasses.text}`}>{tearsheetStats.stats.uniqueUsers}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>Avg Time</p>
                      <p className={`text-lg font-bold ${themeClasses.text}`}>{tearsheetStats.stats.avgDurationMs > 0 ? `${(tearsheetStats.stats.avgDurationMs / 1000).toFixed(1)}s` : '—'}</p>
                    </div>
                  </div>

                  {/* Summary line */}
                  <p className={`text-xs ${themeClasses.textMuted} mb-3`}>
                    Today: <span className="text-rose-600 font-medium">{tearsheetStats.stats.totalToday}</span> · Success: <span className="text-green-600 font-medium">{tearsheetStats.stats.successCount}</span> · Errors: <span className="text-red-600 font-medium">{tearsheetStats.stats.errorCount}</span> · Unique Counterparts: <span className="font-medium">{tearsheetStats.stats.uniqueCounterparts}</span>
                  </p>

                  {/* Most Downloaded Counterparts */}
                  {tearsheetStats.stats.topCounterparts?.length > 0 && (
                    <div className="mb-4">
                      <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2`}>Most Downloaded</p>
                      <div className="flex flex-wrap gap-2">
                        {tearsheetStats.stats.topCounterparts.map((cp, i) => (
                          <span key={i} className="px-2.5 py-1 text-xs rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 font-medium">
                            {cp.name} ({cp.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Events Table */}
                  {tearsheetStats.recentEvents?.length > 0 && (
                    <div className={`rounded-lg border ${themeClasses.border} overflow-hidden max-h-[300px] overflow-y-auto`}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}>
                            <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Counterpart</th>
                            <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>User</th>
                            <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>IP</th>
                            <th className={`px-3 py-2 text-center ${themeClasses.textMuted} font-medium`}>Status</th>
                            <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>Time</th>
                            <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tearsheetStats.recentEvents.map((ev) => (
                            <tr key={ev.id} className={`border-t ${themeClasses.border}`}>
                              <td className={`px-3 py-1.5 ${themeClasses.text} font-medium truncate max-w-[200px]`}>{ev.counterpart_name}</td>
                              <td className={`px-3 py-1.5 ${themeClasses.textMuted} truncate max-w-[180px]`}>{ev.user_email || '—'}</td>
                              <td className={`px-3 py-1.5 ${themeClasses.textMuted} text-xs font-mono`} title={ev.user_agent || ''}>{ev.ip_address || '—'}</td>
                              <td className="px-3 py-1.5 text-center">
                                {ev.status === 'success' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                    <CheckCircle size={12} /> OK
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" title={ev.error_message || ''}>
                                    <XCircle size={12} /> Err
                                  </span>
                                )}
                              </td>
                              <td className={`px-3 py-1.5 text-right ${themeClasses.textMuted}`}>{ev.duration_ms ? `${(ev.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                              <td className={`px-3 py-1.5 text-right ${themeClasses.textMuted} text-xs`}>
                                {(() => {
                                  const mins = Math.round((Date.now() - new Date(ev.created_at).getTime()) / 60000);
                                  if (mins < 1) return 'just now';
                                  if (mins < 60) return `${mins}m ago`;
                                  const hrs = Math.round(mins / 60);
                                  if (hrs < 24) return `${hrs}h ago`;
                                  return `${Math.round(hrs / 24)}d ago`;
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {!tearsheetStats && (
                <p className={`text-sm ${themeClasses.textMuted} text-center py-4`}>Click "Refresh Stats" to load tearsheet download data</p>
              )}
            </div>

            {/* Email Subscribers Card */}
            <div className={`${themeClasses.card} rounded-xl border-2 border-green-300 dark:border-green-600 p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                  <Mail size={18} className="text-green-500" />
                  Email Subscribers
                </h3>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/email-stats');
                      const data = await res.json();
                      if (data.success) setEmailSubStats(data);
                    } catch (err) {
                      console.error('Failed to fetch email subscriber stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {emailSubStats && (
                <>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>Total</p>
                      <p className="text-lg font-bold text-green-600">{emailSubStats.stats.total}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>Verified</p>
                      <p className="text-lg font-bold text-green-600">{emailSubStats.stats.verified}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-amber-900/20' : 'bg-amber-50'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>Pending</p>
                      <p className="text-lg font-bold text-amber-600">{emailSubStats.stats.pending}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50'}`}>
                      <p className={`text-xs ${themeClasses.textMuted}`}>Unique Emails</p>
                      <p className="text-lg font-bold text-green-600">{emailSubStats.stats.uniqueEmails}</p>
                    </div>
                  </div>

                  <p className={`text-xs ${themeClasses.textMuted} mb-3`}>
                    Today: <span className="text-green-600 font-medium">{emailSubStats.stats.today}</span> ·
                    This Week: <span className="text-green-600 font-medium">{emailSubStats.stats.thisWeek}</span>
                  </p>

                  {Object.keys(emailSubStats.stats.byType || {}).length > 0 && (
                    <div className="mb-4">
                      <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2`}>By Type</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(emailSubStats.stats.byType).map(([type, count]) => (
                          <span key={type} className="px-2.5 py-1 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium capitalize">
                            {type === 'counterpart' ? 'Counterparts' : type === 'technology' ? 'Technologies' : 'Countries'} ({count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {emailSubStats.subscribers?.length > 0 && (
                    <div className={`rounded-lg border ${themeClasses.border} overflow-hidden max-h-[300px] overflow-y-auto`}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}>
                            <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Email</th>
                            <th className={`px-3 py-2 text-left ${themeClasses.textMuted} font-medium`}>Following</th>
                            <th className={`px-3 py-2 text-center ${themeClasses.textMuted} font-medium`}>Status</th>
                            <th className={`px-3 py-2 text-right ${themeClasses.textMuted} font-medium`}>When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailSubStats.subscribers.map((sub) => (
                            <tr key={sub.id} className={`border-t ${themeClasses.border}`}>
                              <td className={`px-3 py-1.5 ${themeClasses.text} font-mono text-xs`}>{sub.email}</td>
                              <td className={`px-3 py-1.5 ${themeClasses.text} truncate max-w-[200px]`}>
                                <span className="text-xs text-[color:var(--gis-dim)] mr-1">{sub.targetType === 'counterpart' ? '🏢' : sub.targetType === 'technology' ? '⚡' : '🌍'}</span>
                                {sub.targetName}
                              </td>
                              <td className="px-3 py-1.5 text-center">
                                {sub.verified
                                  ? <CheckCircle size={14} className="inline text-green-500" />
                                  : <Clock size={14} className="inline text-amber-500" />
                                }
                              </td>
                              <td className={`px-3 py-1.5 text-right text-xs ${themeClasses.textMuted}`}>
                                {(() => {
                                  const diff = Date.now() - new Date(sub.createdAt).getTime();
                                  const mins = Math.floor(diff / 60000);
                                  if (mins < 60) return `${mins}m ago`;
                                  const hrs = Math.floor(mins / 60);
                                  if (hrs < 24) return `${hrs}h ago`;
                                  const days = Math.floor(hrs / 24);
                                  return `${days}d ago`;
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {!emailSubStats && (
                <p className={`text-sm ${themeClasses.textMuted} text-center py-4`}>Click "Refresh Stats" to load email subscriber data</p>
              )}
            </div>

            {/* URL Verification Card */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Globe size={18} className="text-orange-500" />
                    URL Verification
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    Verify discovery deal URLs via HEAD requests, clean up 404s
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/verify-deal-urls?stats=true');
                      const data = await res.json();
                      if (data.success) setUrlVerifyStats(data.stats);
                    } catch (err) {
                      console.error('Failed to fetch URL verify stats:', err);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] hover:bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}
                >
                  Refresh Stats
                </button>
              </div>

              {urlVerifyStats && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Total</p>
                    <p className={`text-lg font-bold ${themeClasses.text}`}>{urlVerifyStats.total}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Unchecked</p>
                    <p className={`text-lg font-bold text-[color:var(--gis-dim)]`}>{urlVerifyStats.unchecked}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Valid</p>
                    <p className={`text-lg font-bold text-green-600`}>{urlVerifyStats.verified}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <p className={`text-xs ${themeClasses.textMuted}`}>Invalid (404)</p>
                    <p className={`text-lg font-bold text-red-600`}>{urlVerifyStats.invalid}</p>
                  </div>
                </div>
              )}

              {urlVerifyRunning && (
                <div className={`p-4 rounded-lg mb-4 ${theme === 'dark' ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={18} className="animate-spin text-orange-600" />
                    <span className={`font-medium ${themeClasses.text}`}>
                      Verified {urlVerifyProgress.processed} URLs · {urlVerifyProgress.valid} valid · {urlVerifyProgress.invalid} invalid
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (urlVerifyRunning) return;
                    setUrlVerifyRunning(true);
                    setUrlVerifyProgress({ processed: 0, valid: 0, invalid: 0 });
                    let totalProcessed = 0, totalValid = 0, totalInvalid = 0;
                    let remaining = 1;
                    while (remaining > 0) {
                      try {
                        const res = await fetch('/api/verify-deal-urls', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ batchSize: 50 })
                        });
                        const data = await res.json();
                        totalProcessed += data.processed;
                        totalValid += data.valid;
                        totalInvalid += data.invalid;
                        setUrlVerifyProgress({ processed: totalProcessed, valid: totalValid, invalid: totalInvalid });
                        remaining = data.remaining || 0;
                        if (data.processed === 0) break;
                      } catch (err) {
                        console.error('URL verify error:', err);
                        break;
                      }
                    }
                    // Refresh stats
                    try {
                      const res = await fetch('/api/verify-deal-urls?stats=true');
                      const data = await res.json();
                      if (data.success) setUrlVerifyStats(data.stats);
                    } catch {}
                    setUrlVerifyRunning(false);
                  }}
                  disabled={urlVerifyRunning}
                  className="px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {urlVerifyRunning ? (
                    <><Loader2 size={16} className="animate-spin" /> Verifying...</>
                  ) : (
                    <><Globe size={16} /> Verify All URLs</>
                  )}
                </button>
                <button
                  onClick={async () => {
                    const invalidCount = urlVerifyStats?.invalid || 0;
                    if (invalidCount === 0) {
                      alert('No invalid URLs to delete. Run verification first.');
                      return;
                    }
                    if (!window.confirm(`This will permanently delete ${invalidCount} deals with invalid (404) URLs.\nThis cannot be undone. Continue?`)) return;
                    try {
                      const res = await fetch('/api/verify-deal-urls', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'delete-invalid' })
                      });
                      const data = await res.json();
                      alert(`Deleted ${data.deleted} deals with invalid URLs.`);
                      // Refresh stats
                      const statsRes = await fetch('/api/verify-deal-urls?stats=true');
                      const statsData = await statsRes.json();
                      if (statsData.success) setUrlVerifyStats(statsData.stats);
                      refreshCentralizedData();
                    } catch (err) {
                      console.error('Delete invalid error:', err);
                      alert('Error: ' + err.message);
                    }
                  }}
                  disabled={urlVerifyRunning || !urlVerifyStats?.invalid}
                  className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    theme === 'dark' ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-50 text-red-700 hover:bg-red-100'
                  }`}
                >
                  Delete Invalid ({urlVerifyStats?.invalid || 0})
                </button>
              </div>
            </div>

            </>
            )}

            {/* Deals Tab */}
            {adminTab === 'deals' && (
            <>
            {/* Deals Table */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} overflow-hidden`}>
              <div className={`px-4 py-3 border-b ${themeClasses.border} flex items-center justify-between gap-4`}>
                <div className="flex items-center gap-3">
                  <h3 className={`font-semibold ${themeClasses.text}`}>All Deals</h3>
                  <span className={`text-sm ${themeClasses.textMuted}`}>{deals.length} total</span>
                  {deals.filter(d => d.content_status === 'draft').length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      {deals.filter(d => d.content_status === 'draft').length} drafts
                    </span>
                  )}
                </div>
                <div className="flex-1 max-w-xs">
                  <input
                    type="text"
                    placeholder="Search deals..."
                    value={adminDealSearch}
                    onChange={(e) => setAdminDealSearch(e.target.value)}
                    className={`w-full px-3 py-1.5 text-sm rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                </div>
              </div>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full">
                  <thead className={`${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'} sticky top-0`}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Deal</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Type</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Location</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Date</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Status</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${themeClasses.border}`}>
                    {/* Filter by search, sort: drafts first, then by created_at desc */}
                    {[...deals]
                      .filter(deal => {
                        if (!adminDealSearch) return true;
                        const search = adminDealSearch.toLowerCase();
                        const counterpartNames = (deal.deal_counterparts || []).map(dc => dc.counterpart?.name || '').join(' ');
                        return (deal.headline || '').toLowerCase().includes(search) ||
                               (deal.description || '').toLowerCase().includes(search) ||
                               (Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).join(' ').toLowerCase().includes(search) ||
                               (Array.isArray(deal.technology) ? deal.technology.join(' ') : (deal.technology || '')).toLowerCase().includes(search) ||
                               (deal.deal_type || '').toLowerCase().includes(search) ||
                               counterpartNames.toLowerCase().includes(search);
                      })
                      .sort((a, b) => {
                        // Drafts first
                        if (a.content_status === 'draft' && b.content_status !== 'draft') return -1;
                        if (b.content_status === 'draft' && a.content_status !== 'draft') return 1;
                        // Then by date
                        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                      })
                      .slice(0, adminDealSearch ? 200 : 100) // Show more results when searching
                      .map((deal, idx) => (
                      <tr
                        key={deal.id || idx}
                        className={`${themeClasses.cardHover} ${deal.content_status === 'draft' ? (theme === 'dark' ? 'bg-amber-900/10' : 'bg-amber-50') : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="max-w-md">
                            <div className="flex items-center gap-2">
                              {deal.content_status === 'draft' && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 font-medium">
                                  DRAFT
                                </span>
                              )}
                              <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                                {deal.headline || deal.description?.slice(0, 80) || 'Untitled'}
                              </p>
                            </div>
                            <p className={`text-xs ${themeClasses.textMuted} truncate mt-0.5`}>
                              {(deal.deal_counterparts || deal.linkedCounterparts || []).map(dc => dc.counterpart?.name || dc.name).join(', ') || 'No counterparts'}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}>
                            {DEAL_TYPES.find(t => t.value === (deal.deal_type || deal.dealType))?.label || fmtLabel(deal.deal_type || deal.dealType) || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${themeClasses.text}`}>{(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).join(', ') || deal.location || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${themeClasses.textMuted}`}>{deal.announcement_date || deal.expected_closing || deal.date || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            deal.status === 'closed' ? 'bg-green-100 text-green-700' :
                            deal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            deal.status === 'announced' ? 'bg-purple-100 text-purple-700' :
                            deal.status === 'tender' ? 'bg-amber-100 text-amber-700' :
                            'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)]'
                          }`}>
                            {DEAL_STATUSES.find(s => s.value === deal.status)?.label || deal.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Publish/Unpublish button */}
                            {deal.content_status === 'draft' ? (
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await authFetch(`/api/deals?id=${deal.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ deal: { ...deal, content_status: 'published' } })
                                    });
                                    if (response.ok) {
                                      setDeals(prev => prev.map(d => d.id === deal.id ? { ...d, content_status: 'published' } : d));
                                      sessionStorage.setItem('deals_cache_bust', Date.now());
                                      sessionStorage.setItem('dashboard_cache_bust', Date.now());
                                    }
                                  } catch (err) {
                                    console.error('Error publishing:', err);
                                  }
                                }}
                                className="p-1.5 rounded-lg bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50"
                                title="Publish"
                              >
                                <CheckCircle size={14} className="text-green-600" />
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await authFetch(`/api/deals?id=${deal.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ deal: { ...deal, content_status: 'draft' } })
                                    });
                                    if (response.ok) {
                                      setDeals(prev => prev.map(d => d.id === deal.id ? { ...d, content_status: 'draft' } : d));
                                      sessionStorage.setItem('deals_cache_bust', Date.now());
                                      sessionStorage.setItem('dashboard_cache_bust', Date.now());
                                    }
                                  } catch (err) {
                                    console.error('Error unpublishing:', err);
                                  }
                                }}
                                className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/50"
                                title="Unpublish (set to draft)"
                              >
                                <EyeOff size={14} className="text-amber-600" />
                              </button>
                            )}
                            <button
                              onClick={() => { setEditingDeal(deal); setShowDealModal(true); }}
                              className={`p-1.5 rounded-lg ${themeClasses.cardHover}`}
                              title="Edit"
                            >
                              <Edit2 size={14} className="text-blue-600" />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this deal?')) {
                                  try {
                                    const response = await authFetch(`/api/deals?id=${deal.id}`, {
                                      method: 'DELETE'
                                    });
                                    if (response.ok) {
                                      setDeals(prev => prev.filter(d => d.id !== deal.id));
                                    } else {
                                      console.error('Failed to delete deal from Supabase');
                                      setDeals(prev => prev.filter(d => d.id !== deal.id));
                                    }
                                    sessionStorage.setItem('deals_cache_bust', Date.now());
                                    sessionStorage.setItem('dashboard_cache_bust', Date.now());
                                  } catch (err) {
                                    console.error('Error deleting deal:', err);
                                    setDeals(prev => prev.filter(d => d.id !== deal.id));
                                  }
                                }
                              }}
                              className={`p-1.5 rounded-lg hover:bg-red-50`}
                              title="Delete"
                            >
                              <Trash2 size={14} className="text-red-500" />
                            </button>
                            {deal.sourceUrl && (
                              <a
                                href={deal.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`p-1.5 rounded-lg ${themeClasses.cardHover}`}
                                title="View Source"
                              >
                                <ExternalLink size={14} className={themeClasses.textMuted} />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {deals.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center">
                          <p className={themeClasses.textMuted}>No deals yet. Click "Add Deal" to extract from a URL.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {deals.length > 50 && (
                <div className={`px-4 py-3 border-t ${themeClasses.border} text-center`}>
                  <p className={`text-sm ${themeClasses.textMuted}`}>Showing 50 of {deals.length} deals</p>
                </div>
              )}
            </div>

            {/* Key People / Executives from Deals */}
            {(() => {
              const allPeople = deals.flatMap(deal =>
                (deal.people || []).map(person => ({
                  ...person,
                  dealId: deal.id,
                  dealHeadline: deal.headline
                }))
              );
              if (allPeople.length === 0) return null;
              return (
                <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} overflow-hidden`}>
                  <div className={`px-4 py-3 border-b ${themeClasses.border} flex items-center justify-between`}>
                    <h3 className={`font-semibold ${themeClasses.text}`}>Key People / Executives</h3>
                    <span className={`text-sm ${themeClasses.textMuted}`}>{allPeople.length} total</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Name</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Title</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Organization</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>From Deal</th>
                          <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${themeClasses.border}`}>
                        {allPeople.slice(0, 50).map((person, idx) => {
                          const existingPerson = counterparts.find(c =>
                            c.type === 'person' &&
                            c.name?.toLowerCase().trim() === person.name?.toLowerCase().trim()
                          );
                          return (
                            <tr
                              key={`${person.dealId}-${idx}`}
                              className={`${themeClasses.cardHover} cursor-pointer`}
                              onClick={async () => {
                                if (existingPerson) {
                                  setActiveTab('counterparts');
                                  setSelectedCounterpart(existingPerson);
                                } else {
                                  const newPerson = await createPersonAsCounterpart(person, null, person.dealId);
                                  if (newPerson) {
                                    setActiveTab('counterparts');
                                    setSelectedCounterpart(newPerson);
                                  }
                                }
                              }}
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'}`}>
                                    <User size={14} className="text-blue-700" />
                                  </div>
                                  <span className={`text-sm font-medium ${themeClasses.text} hover:text-blue-700`}>{person.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm ${themeClasses.text}`}>{person.title || '-'}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm ${themeClasses.text}`}>{person.organization || '-'}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs ${themeClasses.textMuted} truncate max-w-xs block`}>{person.dealHeadline || '-'}</span>
                              </td>
                              <td className="px-4 py-3">
                                <ChevronRight size={16} className={themeClasses.textMuted} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {allPeople.length > 50 && (
                    <div className={`px-4 py-3 border-t ${themeClasses.border} text-center`}>
                      <p className={`text-sm ${themeClasses.textMuted}`}>Showing 50 of {allPeople.length} people</p>
                    </div>
                  )}
                </div>
              );
            })()}
            </>
            )}

            {/* Counterparts Tab */}
            {adminTab === 'counterparts' && (
            <>
            {/* Counterparts Needing Review */}
            {counterparts.filter(c => c.needsReview).length > 0 && (
              <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} overflow-hidden`}>
                <div className={`px-4 py-3 border-b ${themeClasses.border} flex items-center justify-between`}>
                  <h3 className={`font-semibold ${themeClasses.text}`}>Counterparts Needing Review</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                    {counterparts.filter(c => c.needsReview).length} pending
                  </span>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-700">
                  {counterparts.filter(c => c.needsReview).slice(0, 10).map(cp => (
                    <div key={cp.id} className={`px-4 py-3 flex items-center justify-between ${themeClasses.cardHover}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'}`}>
                          <Building2 size={18} className="text-blue-700" />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${themeClasses.text}`}>{cp.name}</p>
                          <p className={`text-xs ${themeClasses.textMuted}`}>{cp.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setCounterparts(prev => prev.map(c =>
                              c.id === cp.id ? { ...c, needsReview: false } : c
                            ));
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Delete this counterpart?')) {
                              setCounterparts(prev => prev.filter(c => c.id !== cp.id));
                            }
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Counterparts Table */}
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} overflow-hidden`}>
              <div className={`px-4 py-3 border-b ${themeClasses.border} flex items-center justify-between gap-4`}>
                <div className="flex items-center gap-3">
                  <h3 className={`font-semibold ${themeClasses.text}`}>All Counterparts</h3>
                  <span className={`text-sm ${themeClasses.textMuted}`}>{counterparts.length} published</span>
                </div>
                <div className="flex-1 max-w-xs">
                  <input
                    type="text"
                    placeholder="Search counterparts..."
                    value={adminCounterpartSearch}
                    onChange={(e) => {
                      setAdminCounterpartSearch(e.target.value);
                      // Also search drafts via API (not in local state)
                      const q = e.target.value.trim();
                      if (q.length >= 2) {
                        authFetch(`/api/counterparts?search=${encodeURIComponent(q)}&status=draft&limit=20`)
                          .then(r => r.json())
                          .then(data => { if (data.counterparts) setAdminDraftResults(data.counterparts); })
                          .catch(() => {});
                      } else {
                        setAdminDraftResults([]);
                      }
                    }}
                    className={`w-full px-3 py-1.5 text-sm rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                </div>
              </div>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full">
                  <thead className={`${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'} sticky top-0`}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Name</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Type</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Country</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Deals</th>
                      <th className={`px-4 py-3 text-right text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${themeClasses.border}`}>
                    {(() => {
                      // Merge published (from state) + drafts (from API search).
                      // deal_count on each counterpart is maintained by a Postgres
                      // trigger on deal_counterparts insert/delete (installed Apr 15
                      // 2026), so cp.deal_count is authoritative — no client-side
                      // counting needed.
                      const publishedFiltered = counterparts.filter(cp => {
                        if (!adminCounterpartSearch) return true;
                        const search = adminCounterpartSearch.toLowerCase();
                        return (cp.name || '').toLowerCase().includes(search) ||
                               (cp.type || '').toLowerCase().includes(search) ||
                               (cp.country || cp.headquarters || '').toLowerCase().includes(search) ||
                               (cp.investmentPreferences?.summary || '').toLowerCase().includes(search);
                      });
                      const publishedIds = new Set(publishedFiltered.map(c => c.id));
                      const drafts = adminDraftResults.filter(c => !publishedIds.has(c.id)).map(c => ({ ...c, _isDraft: true }));
                      return [...publishedFiltered, ...drafts];
                    })()
                      .slice(0, adminCounterpartSearch ? 200 : 50)
                      .map((cp, idx) => (
                      <tr key={cp.id || idx} className={themeClasses.cardHover}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'}`}>
                              <Building2 size={14} className="text-blue-700" />
                            </div>
                            <span className={`text-sm font-medium ${themeClasses.text}`}>{cp.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'} ${themeClasses.text}`}>
                            {cp.type || cp.category || 'company'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${themeClasses.text}`}>{cp.country || cp.headquarters || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${themeClasses.textMuted}`}>{cp.deal_count ?? 0}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => { setEditingCounterpart(cp); setShowCounterpartModal(true); }}
                              className={`p-1.5 rounded-lg ${themeClasses.cardHover}`}
                              title="Edit"
                            >
                              <Edit2 size={14} className="text-blue-600" />
                            </button>
                            <button
                              onClick={async (e) => {
                                if (!window.confirm(`Re-enrich "${cp.name}" using AI? This will update company info from web sources.`)) return;
                                const btn = e.currentTarget;
                                const originalHtml = btn.innerHTML;
                                try {
                                  btn.disabled = true;
                                  btn.innerHTML = '<svg class="animate-spin" width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>';

                                  // Get the website URL from various possible fields
                                  const websiteUrl = cp.website || cp.websiteUrl || cp.investmentPreferences?.website || cp.enrichment?.website;
                                  const response = await authFetch('/api/enrich-counterpart', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      counterpartId: cp.id,
                                      name: cp.name,
                                      websiteUrl: websiteUrl,
                                      industry: 'infrastructure',
                                      counterpartType: cp.type || 'company'
                                    })
                                  });

                                  const result = await response.json();

                                  // API returns investmentPreferences, not enrichment
                                  const enrichmentData = result.enrichment || result.investmentPreferences;
                                  if (response.ok && enrichmentData) {
                                    // Update counterpart with new enrichment in database
                                    const updateResponse = await authFetch(`/api/counterparts?id=${cp.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        counterpart: {
                                          ...cp,
                                          enrichment: enrichmentData,
                                          investmentPreferences: enrichmentData
                                        }
                                      })
                                    });

                                    if (updateResponse.ok) {
                                      // Update local state
                                      setCounterparts(prev => prev.map(c =>
                                        c.id === cp.id ? { ...c, enrichment: result.enrichment, investmentPreferences: result.enrichment } : c
                                      ));
                                      sessionStorage.setItem('counterparts_cache_bust', Date.now());
                                      alert('Enrichment complete!');
                                    } else {
                                      const updateErr = await updateResponse.json();
                                      console.error('[Re-enrich] Update failed:', updateErr);
                                      alert('Enrichment succeeded but failed to save. Check console.');
                                    }
                                  } else {
                                    console.error('[Re-enrich] API error:', result);
                                    alert('Enrichment failed: ' + (result.error || 'Unknown error'));
                                  }
                                } catch (err) {
                                  console.error('[Re-enrich] Error:', err);
                                  alert('Error during enrichment: ' + err.message);
                                } finally {
                                  btn.disabled = false;
                                  btn.innerHTML = originalHtml;
                                }
                              }}
                              className={`p-1.5 rounded-lg hover:bg-purple-50`}
                              title="Re-enrich"
                            >
                              <Sparkles size={14} className="text-purple-600" />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm(`Delete "${cp.name}"? This will also remove it from all deals.`)) {
                                  try {
                                    const response = await authFetch(`/api/counterparts?id=${cp.id}`, {
                                      method: 'DELETE'
                                    });
                                    if (response.ok) {
                                      setCounterparts(prev => prev.filter(c => c.id !== cp.id));
                                    } else {
                                      console.error('Failed to delete counterpart from Supabase');
                                      alert('Failed to delete counterpart');
                                    }
                                    sessionStorage.setItem('counterparts_cache_bust', Date.now());
                                    sessionStorage.setItem('dashboard_cache_bust', Date.now());
                                  } catch (err) {
                                    console.error('Error deleting counterpart:', err);
                                    alert('Error deleting counterpart');
                                  }
                                }
                              }}
                              className={`p-1.5 rounded-lg hover:bg-red-50`}
                              title="Delete"
                            >
                              <Trash2 size={14} className="text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {counterparts.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center">
                          <p className={themeClasses.textMuted}>No counterparts yet.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {counterparts.length > 50 && (
                <div className={`px-4 py-3 border-t ${themeClasses.border} text-center`}>
                  <p className={`text-sm ${themeClasses.textMuted}`}>Showing 50 of {counterparts.length} counterparts</p>
                </div>
              )}
            </div>
            </>
            )}

            {/* SEO Pages Tab */}
            {adminTab === 'seo' && (
            <>
            <div className={`${themeClasses.card} rounded-xl border ${themeClasses.border} p-6`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text} flex items-center gap-2`}>
                    <Globe size={20} className="text-blue-600" />
                    SEO Pages Overview
                  </h3>
                  <p className={`text-sm ${themeClasses.textMuted} mt-1`}>
                    These pages are optimized for Google search and can be shared publicly.
                  </p>
                </div>
                <div className="flex gap-2">
                  <a
                    href="https://www.globalinfrastructuresherpa.com/sitemap.xml"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30"
                  >
                    <FileText size={14} />
                    View Sitemap
                  </a>
                  <a
                    href="https://www.globalinfrastructuresherpa.com/robots.txt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[color:var(--gis-rule)] text-[color:var(--gis-body)] hover:bg-[color:var(--gis-bg)] dark:border-[color:var(--gis-rule)] dark:text-[color:var(--gis-dim)] dark:hover:bg-[color:var(--gis-panel)]"
                  >
                    <FileText size={14} />
                    View robots.txt
                  </a>
                </div>
              </div>

              {/* Deal SEO Pages */}
              <div className="mb-8">
                <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center gap-2`}>
                  <Zap size={16} className="text-amber-500" />
                  Deal Pages ({deals.filter(d => d.content_status === 'published').length} published)
                </h4>
                <div className={`rounded-xl border ${themeClasses.border} overflow-hidden max-h-80 overflow-y-auto`}>
                  <table className="w-full text-sm">
                    <thead className={`sticky top-0 ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <tr>
                        <th className={`px-4 py-2 text-left font-medium ${themeClasses.textMuted}`}>Deal</th>
                        <th className={`px-4 py-2 text-left font-medium ${themeClasses.textMuted}`}>Status</th>
                        <th className={`px-4 py-2 text-right font-medium ${themeClasses.textMuted}`}>SEO Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {deals.map(deal => {
                        const slug = [deal.headline || deal.project_name, ...(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])), ...(Array.isArray(deal.technology) ? deal.technology : [deal.technology])]
                          .filter(Boolean)
                          .join(' ')
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-|-$/g, '')
                          .substring(0, 80);
                        const canonicalSlug = slug ? `${slug}-${deal.id}` : deal.id;
                        const seoUrl = `https://www.globalinfrastructuresherpa.com/deal/${canonicalSlug}`;

                        return (
                          <tr key={deal.id} className={`${themeClasses.cardHover}`}>
                            <td className={`px-4 py-2 ${themeClasses.text}`}>
                              <div className="truncate max-w-xs" title={deal.headline || deal.project_name}>
                                {deal.headline || deal.project_name || 'Untitled Deal'}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                deal.content_status === 'published'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] dark:bg-[color:var(--gis-rule)] dark:text-[color:var(--gis-dim)]'
                              }`}>
                                {deal.content_status || 'draft'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(seoUrl);
                                    alert('Link copied!');
                                  }}
                                  className="p-1.5 rounded hover:bg-[color:var(--gis-rule)] dark:hover:bg-[color:var(--gis-rule)]"
                                  title="Copy link"
                                >
                                  <Copy size={14} className="text-[color:var(--gis-dim)]" />
                                </button>
                                <a
                                  href={seoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                  title="Open SEO page"
                                >
                                  <ExternalLink size={14} className="text-blue-600" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Counterpart SEO Pages */}
              <div>
                <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center gap-2`}>
                  <Building2 size={16} className="text-blue-500" />
                  Counterpart Pages ({counterparts.length} total)
                </h4>
                <div className={`rounded-xl border ${themeClasses.border} overflow-hidden max-h-80 overflow-y-auto`}>
                  <table className="w-full text-sm">
                    <thead className={`sticky top-0 ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                      <tr>
                        <th className={`px-4 py-2 text-left font-medium ${themeClasses.textMuted}`}>Counterpart</th>
                        <th className={`px-4 py-2 text-left font-medium ${themeClasses.textMuted}`}>Type</th>
                        <th className={`px-4 py-2 text-right font-medium ${themeClasses.textMuted}`}>SEO Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {counterparts.map(cp => {
                        const slug = (cp.name || '')
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-|-$/g, '')
                          .substring(0, 60);
                        const canonicalSlug = slug ? `${slug}-${cp.id}` : cp.id;
                        const seoUrl = `https://www.globalinfrastructuresherpa.com/counterpart/${canonicalSlug}`;

                        return (
                          <tr key={cp.id} className={`${themeClasses.cardHover}`}>
                            <td className={`px-4 py-2 ${themeClasses.text}`}>
                              <div className="truncate max-w-xs" title={cp.name}>
                                {cp.name || 'Unnamed'}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                cp.type === 'company'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                  : cp.type === 'investor'
                                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                  : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] dark:bg-[color:var(--gis-rule)] dark:text-[color:var(--gis-dim)]'
                              }`}>
                                {cp.type || 'unknown'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(seoUrl);
                                    alert('Link copied!');
                                  }}
                                  className="p-1.5 rounded hover:bg-[color:var(--gis-rule)] dark:hover:bg-[color:var(--gis-rule)]"
                                  title="Copy link"
                                >
                                  <Copy size={14} className="text-[color:var(--gis-dim)]" />
                                </button>
                                <a
                                  href={seoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                  title="Open SEO page"
                                >
                                  <ExternalLink size={14} className="text-blue-600" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            </>
            )}

            {/* Newsletter Tab - with verticals: General, PPA, Data Center */}
            {adminTab === 'newsletter' && (
              <NewsletterGenerator
                theme={theme}
                themeClasses={themeClasses}
                deals={deals}
                counterparts={counterparts}
                newsletterVertical={newsletterVertical}
                setNewsletterVertical={setNewsletterVertical}
                updateMarkdown={updateMarkdown}
                setUpdateMarkdown={setUpdateMarkdown}
                updateDays={updateDays}
                setUpdateDays={setUpdateDays}
                updateCopied={updateCopied}
                setUpdateCopied={setUpdateCopied}
                updateGenerating={updateGenerating}
                setUpdateGenerating={setUpdateGenerating}
                digestMarkdown={digestMarkdown}
                setDigestMarkdown={setDigestMarkdown}
                digestDays={digestDays}
                setDigestDays={setDigestDays}
                digestCopied={digestCopied}
                setDigestCopied={setDigestCopied}
                dcDigestMarkdown={dcDigestMarkdown}
                setDcDigestMarkdown={setDcDigestMarkdown}
                dcDigestDays={dcDigestDays}
                setDcDigestDays={setDcDigestDays}
                dcDigestCopied={dcDigestCopied}
                setDcDigestCopied={setDcDigestCopied}
                dcDigestGenerating={dcDigestGenerating}
                setDcDigestGenerating={setDcDigestGenerating}
                defenseDigestMarkdown={defenseDigestMarkdown}
                setDefenseDigestMarkdown={setDefenseDigestMarkdown}
                defenseDigestDays={defenseDigestDays}
                setDefenseDigestDays={setDefenseDigestDays}
                defenseDigestCopied={defenseDigestCopied}
                setDefenseDigestCopied={setDefenseDigestCopied}
                defenseDigestGenerating={defenseDigestGenerating}
                setDefenseDigestGenerating={setDefenseDigestGenerating}
              />
            )}

            {/* Settings Tab */}
            {adminTab === 'marketing' && (
              <Suspense fallback={<div className="p-8 text-sm text-slate-500">Loading…</div>}>
                <ResearchMarketing theme={theme} />
              </Suspense>
            )}

            {adminTab === 'crm' && (
              <Suspense fallback={<div className="p-8 text-sm text-slate-500">Loading…</div>}>
                <Crm theme={theme} />
              </Suspense>
            )}

            {adminTab === 'agents' && (
              <Suspense fallback={<div className="p-8 text-sm text-slate-500">Loading…</div>}>
                <Agents theme={theme} />
              </Suspense>
            )}

            {adminTab === 'database' && (
            <>
            {/* Database Export */}
            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6`}>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <Download size={20} className="text-indigo-600" />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text}`}>Export Data</h3>
                  <p className={`text-sm ${themeClasses.textMuted}`}>Download deals, counterparts, or the full database</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {[
                  { label: 'Deals (CSV)', table: 'deals', format: 'csv' },
                  { label: 'Deals (JSON)', table: 'deals', format: 'json' },
                  { label: 'Counterparts (CSV)', table: 'counterparts', format: 'csv' },
                  { label: 'Counterparts (JSON)', table: 'counterparts', format: 'json' },
                  { label: 'Deal Links (CSV)', table: 'deal_counterparts', format: 'csv' },
                  { label: 'Deal Links (JSON)', table: 'deal_counterparts', format: 'json' },
                  { label: 'Full DB (JSON)', table: 'all', format: 'json' },
                ].map(opt => (
                  <button
                    key={`${opt.table}-${opt.format}`}
                    onClick={async () => {
                      try {
                        let exportData = {};
                        const tables = opt.table === 'all' ? ['deals', 'counterparts'] : [opt.table];
                        for (const t of tables) {
                          if (t === 'deals') {
                            // Use already-loaded deals from state
                            exportData.deals = deals.map(d => ({
                              id: d.id, headline: d.headline, description: d.description, deal_type: d.deal_type, status: d.status,
                              country: d.country, region: d.region, technology: d.technology, capacity: d.capacity, capacity_unit: d.capacity_unit,
                              financials: d.financials, announcement_date: d.announcement_date, project_name: d.project_name, stage: d.stage,
                              source_url: d.source_url, url_verified: d.url_verified, content_status: d.content_status, created_by: d.created_by,
                              created_at: d.created_at, updated_at: d.updated_at,
                              counterparts: (d.deal_counterparts || []).map(dc => ({ name: dc.counterpart?.name, role: dc.role, stake: dc.stake }))
                            }));
                          } else if (t === 'counterparts') {
                            exportData.counterparts = counterparts.map(c => ({
                              id: c.id, name: c.name, type: c.type, category: c.category || c.categories?.[0],
                              website: c.websiteUrl || c.website, country: c.country, description: c.description,
                              content_status: c.content_status, created_at: c.created_at
                            }));
                          } else if (t === 'deal_counterparts') {
                            const links = [];
                            deals.forEach(d => (d.deal_counterparts || []).forEach(dc => {
                              links.push({ deal_id: d.id, deal_headline: d.headline, counterpart_id: dc.counterpart_id || dc.counterpart?.id, counterpart_name: dc.counterpart?.name, role: dc.role, stake: dc.stake, is_lead: dc.is_lead });
                            }));
                            exportData.deal_counterparts = links;
                          }
                        }

                        const filename = `sherpa-${opt.table}-${new Date().toISOString().split('T')[0]}`;
                        if (opt.format === 'json') {
                          const blob = new Blob([JSON.stringify(opt.table === 'all' ? exportData : exportData[opt.table], null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a'); a.href = url; a.download = `${filename}.json`; a.click();
                          URL.revokeObjectURL(url);
                        } else {
                          const rows = exportData[opt.table];
                          if (!rows?.length) { alert('No data to export'); return; }
                          const headers = Object.keys(rows[0]);
                          const csvRows = [headers.join(',')];
                          for (const row of rows) {
                            csvRows.push(headers.map(h => {
                              const val = row[h];
                              if (val === null || val === undefined) return '';
                              const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
                              return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
                            }).join(','));
                          }
                          const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
                          URL.revokeObjectURL(url);
                        }
                      } catch (err) {
                        alert('Export error: ' + err.message);
                      }
                    }}
                    className={`px-4 py-3 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm font-medium hover:border-indigo-400 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2`}
                  >
                    <Download size={14} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Database Import */}
            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mt-6`}>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Upload size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text}`}>Import Data</h3>
                  <p className={`text-sm ${themeClasses.textMuted}`}>Upload CSV or JSON to add deals or counterparts</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {['deals', 'counterparts'].map(table => (
                  <div key={table} className={`p-4 rounded-lg border-2 border-dashed ${themeClasses.border}`}>
                    <p className={`text-sm font-semibold ${themeClasses.text} mb-2 capitalize`}>Import {table}</p>
                    <p className={`text-xs ${themeClasses.textMuted} mb-3`}>CSV or JSON file. New records are added — existing records are not overwritten.</p>
                    <label className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border ${themeClasses.border} cursor-pointer hover:border-emerald-400 hover:text-emerald-600 transition-colors ${themeClasses.text} text-sm font-medium`}>
                      <Upload size={14} />
                      Choose File
                      <input
                        type="file"
                        accept=".csv,.json"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const text = await file.text();
                            let rows;
                            if (file.name.endsWith('.json')) {
                              const parsed = JSON.parse(text);
                              rows = Array.isArray(parsed) ? parsed : (parsed[table] || parsed.data || []);
                            } else {
                              // CSV parse
                              const lines = text.split('\n').filter(l => l.trim());
                              if (lines.length < 2) { alert('CSV must have a header row + data'); return; }
                              const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
                              rows = [];
                              for (let i = 1; i < lines.length; i++) {
                                const vals = lines[i].match(/("(?:[^"]*(?:""[^"]*)*)"|[^,]*)/g) || [];
                                const row = {};
                                headers.forEach((h, idx) => {
                                  let v = (vals[idx] || '').trim().replace(/^"|"$/g, '').replace(/""/g, '"');
                                  if (v === '') v = null;
                                  // Parse JSON fields
                                  if (v && (v.startsWith('{') || v.startsWith('['))) {
                                    try { v = JSON.parse(v); } catch {}
                                  }
                                  row[h] = v;
                                });
                                rows.push(row);
                              }
                            }

                            if (!rows.length) { alert('No data found in file'); return; }
                            const confirm = window.confirm(`Import ${rows.length} ${table} from ${file.name}?\n\nNew records will be added. Existing records will NOT be overwritten.`);
                            if (!confirm) return;

                            let imported = 0, skipped = 0, errors = 0;
                            for (const row of rows) {
                              try {
                                // Strip id to avoid conflicts, let DB generate new ones
                                const clean = { ...row };
                                delete clean.id;
                                delete clean.created_at;
                                delete clean.updated_at;
                                const res = await authFetch(`/api/${table}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(table === 'deals' ? { deal: clean } : { counterpart: clean }) });
                                const error = res.ok ? null : await res.text();
                                if (error) { skipped++; } else { imported++; }
                              } catch { errors++; }
                            }
                            alert(`Import complete:\n\n• Imported: ${imported}\n• Skipped (duplicate/error): ${skipped}\n• Errors: ${errors}`);
                            if (imported > 0) refreshCentralizedData();
                          } catch (err) {
                            alert('Import error: ' + err.message);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
            </>
            )}

            {adminTab === 'settings' && (
            <>
            {/* Taxonomy Management */}
            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6`}>
              <h3 className={`text-lg font-semibold ${themeClasses.text} mb-1`}>Taxonomy Management</h3>
              <p className={`text-sm ${themeClasses.textMuted} mb-4`}>Manage classification categories for counterparts and deals.</p>

              {/* Taxonomy Type Tabs */}
              <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-[color:var(--gis-rule)]">
                {[
                  { id: 'counterpart-types', label: 'Counterpart Types' },
                  { id: 'sectors', label: 'Sectors' },
                  { id: 'technologies', label: 'Technologies' },
                  { id: 'geographies', label: 'Geographies' },
                  { id: 'investment-stages', label: 'Investment Stages' },
                  { id: 'deal-types', label: 'Deal Types' },
                  { id: 'deal-statuses', label: 'Deal Statuses' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTaxonomy(tab.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTaxonomy === tab.id
                        ? 'bg-blue-600 text-white'
                        : theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)] hover:bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-body)] hover:bg-[color:var(--gis-rule)]'
                    }`}
                  >
                    {tab.label}
                    <span className="ml-1.5 text-xs opacity-70">({taxonomies[tab.id]?.length || 0})</span>
                  </button>
                ))}
              </div>

              {/* Taxonomy Items List */}
              {!taxonomiesLoaded ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
                    {taxonomies[activeTaxonomy]?.map((item) => (
                      <div key={item.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                        {item.icon && <span className="text-lg">{item.icon}</span>}
                        <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)]' : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)]'}`}>{item.value}</span>
                        <span className={`text-sm flex-1 ${themeClasses.text}`}>{item.label}</span>
                        {/* Show parent/sector info */}
                        {item.parent && (
                          <span className={`text-xs ${themeClasses.textMuted}`}>← {item.parent.label}</span>
                        )}
                        {item.sector && (
                          <span className={`text-xs ${themeClasses.textMuted}`}>← {item.sector.label}</span>
                        )}
                        {item.type === 'country' && item.parent && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-[color:var(--gis-rule)]' : 'bg-[color:var(--gis-rule)]'} ${themeClasses.textMuted}`}>{item.parent.label}</span>
                        )}
                        {item.type === 'region' && (
                          <span className={`text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700`}>Region</span>
                        )}
                        <span className={`text-xs ${themeClasses.textMuted}`}>#{item.sort_order || '-'}</span>
                        {!item.is_system && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Deactivate "${item.label}"?`)) return;
                              try {
                                const res = await fetch(`/api/taxonomy?type=${activeTaxonomy}&id=${item.id}`, { method: 'DELETE' });
                                if (res.ok) {
                                  const updatedList = taxonomies[activeTaxonomy].filter(i => i.id !== item.id);
                                  setTaxonomies(prev => ({
                                    ...prev,
                                    [activeTaxonomy]: updatedList
                                  }));
                                  // Sync dealTechnologies if technologies were updated
                                  if (activeTaxonomy === 'technologies') {
                                    setDealTechnologies(updatedList.map(t => ({ value: t.value, label: t.label })));
                                  }
                                }
                              } catch (err) {
                                console.error('Error deleting item:', err);
                              }
                            }}
                            className="p-1 rounded hover:bg-red-100 text-red-500"
                            title="Deactivate"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {taxonomies[activeTaxonomy]?.length === 0 && (
                      <p className={`text-sm ${themeClasses.textMuted} text-center py-4`}>No items yet</p>
                    )}
                  </div>

                  {/* Add new item */}
                  <div className="flex gap-2 flex-wrap">
                    <input
                      type="text"
                      placeholder="Value (e.g. floating_solar)"
                      id="new-taxonomy-value"
                      className={`flex-1 min-w-32 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`}
                    />
                    <input
                      type="text"
                      placeholder="Label (e.g. Floating Solar)"
                      id="new-taxonomy-label"
                      className={`flex-1 min-w-32 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`}
                    />
                    {/* Parent selector for hierarchical taxonomies */}
                    {(activeTaxonomy === 'sectors' || activeTaxonomy === 'geographies') && (
                      <select
                        id="new-taxonomy-parent"
                        className={`px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`}
                      >
                        <option value="">No parent</option>
                        {taxonomies[activeTaxonomy]
                          ?.filter(i => activeTaxonomy === 'geographies' ? i.type === 'region' : !i.parent_id)
                          .map(i => (
                            <option key={i.id} value={i.id}>{i.label}</option>
                          ))}
                      </select>
                    )}
                    {/* Sector selector for technologies */}
                    {activeTaxonomy === 'technologies' && (
                      <select
                        id="new-taxonomy-sector"
                        className={`px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`}
                      >
                        <option value="">No sector</option>
                        {taxonomies['sectors']?.map(s => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    )}
                    <input
                      type="number"
                      placeholder="Order"
                      id="new-taxonomy-order"
                      defaultValue={50}
                      className={`w-20 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`}
                    />
                    <button
                      onClick={async () => {
                        const value = document.getElementById('new-taxonomy-value').value.trim();
                        const label = document.getElementById('new-taxonomy-label').value.trim();
                        const sort_order = parseInt(document.getElementById('new-taxonomy-order').value) || 50;
                        const parent_id = document.getElementById('new-taxonomy-parent')?.value || null;
                        const sector_id = document.getElementById('new-taxonomy-sector')?.value || null;
                        if (!value || !label) return;
                        try {
                          const body = { value, label, sort_order };
                          if (parent_id) body.parent_id = parent_id;
                          if (sector_id) body.sector_id = sector_id;
                          if (activeTaxonomy === 'geographies') {
                            body.type = parent_id ? 'country' : 'region';
                          }
                          const res = await fetch(`/api/taxonomy?type=${activeTaxonomy}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(body)
                          });
                          const data = await res.json();
                          if (data.success) {
                            const updatedList = [...(taxonomies[activeTaxonomy] || []), data.data].sort((a, b) => (a.sort_order || 50) - (b.sort_order || 50));
                            setTaxonomies(prev => ({
                              ...prev,
                              [activeTaxonomy]: updatedList
                            }));
                            // Sync dealTechnologies if technologies were updated
                            if (activeTaxonomy === 'technologies') {
                              setDealTechnologies(updatedList.map(t => ({ value: t.value, label: t.label })));
                            }
                            document.getElementById('new-taxonomy-value').value = '';
                            document.getElementById('new-taxonomy-label').value = '';
                            document.getElementById('new-taxonomy-order').value = '50';
                            if (document.getElementById('new-taxonomy-parent')) document.getElementById('new-taxonomy-parent').value = '';
                            if (document.getElementById('new-taxonomy-sector')) document.getElementById('new-taxonomy-sector').value = '';
                          } else {
                            alert(data.error || 'Failed to create item');
                          }
                        } catch (err) {
                          console.error('Error creating item:', err);
                        }
                      }}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Deal Roles (separate table) */}
            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mt-6`}>
              <h3 className={`text-lg font-semibold ${themeClasses.text} mb-1`}>Deal Counterpart Roles</h3>
              <p className={`text-sm ${themeClasses.textMuted} mb-4`}>Roles for linking counterparts to deals (used by AI extraction).</p>
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                {DEAL_ROLES.map((role) => (
                  <div key={role.value || role.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                    <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)]' : 'bg-[color:var(--gis-rule)] text-[color:var(--gis-dim)]'}`}>{role.value}</span>
                    <span className={`text-sm flex-1 ${themeClasses.text}`}>{role.label}</span>
                    <span className={`text-xs ${themeClasses.textMuted}`}>#{role.sort_order || '-'}</span>
                    {role.id && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Deactivate role "${role.label}"?`)) return;
                          try {
                            const delRes = await fetch(`/api/deal-roles?id=${role.id}`, { method: 'DELETE' });
                            if (delRes.ok) {
                              setDealRoles(prev => prev.filter(r => r.id !== role.id));
                            }
                          } catch (err) {
                            console.error('Error deleting role:', err);
                          }
                        }}
                        className="p-1 rounded hover:bg-red-100 text-red-500"
                        title="Deactivate role"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" placeholder="Value" id="new-role-value" className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`} />
                <input type="text" placeholder="Label" id="new-role-label" className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`} />
                <input type="number" placeholder="Order" id="new-role-order" defaultValue={50} className={`w-20 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.card} ${themeClasses.text} text-sm`} />
                <button
                  onClick={async () => {
                    const value = document.getElementById('new-role-value').value.trim();
                    const label = document.getElementById('new-role-label').value.trim();
                    const sort_order = parseInt(document.getElementById('new-role-order').value) || 50;
                    if (!value || !label) return;
                    try {
                      const res = await fetch('/api/deal-roles', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value, label, sort_order }) });
                      const data = await res.json();
                      if (data.success) {
                        setDealRoles(prev => [...prev, data.role].sort((a, b) => (a.sort_order || 50) - (b.sort_order || 50)));
                        document.getElementById('new-role-value').value = '';
                        document.getElementById('new-role-label').value = '';
                        document.getElementById('new-role-order').value = '50';
                      }
                    } catch (err) { console.error('Error creating role:', err); }
                  }}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                ><Plus size={16} /></button>
              </div>
            </div>

            {/* Data Operations - Intelligence Backfill */}
            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mt-6`}>
              <h3 className={`text-lg font-semibold ${themeClasses.text} mb-1`}>Data Intelligence Operations</h3>
              <p className={`text-sm ${themeClasses.textMuted} mb-4`}>Generate summaries from deal data. Run in batches to avoid timeouts.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Counterpart Summaries */}
                <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                  <h4 className={`font-medium ${themeClasses.text} mb-2 flex items-center gap-2`}>
                    <Building2 size={16} className="text-blue-500" />
                    Counterpart Summaries
                  </h4>
                  <p className={`text-xs ${themeClasses.textMuted} mb-3`}>
                    Generate proprietary summaries using actual deal activity from the database.
                  </p>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Generate intelligence-based summaries for counterparts without one? This processes 20 at a time.')) return;
                      try {
                        const res = await fetch('/api/backfill-summaries', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ type: 'counterparts', batchSize: 20 })
                        });
                        const data = await res.json();
                        alert(`${data.message || 'Done'}\n\nUpdated: ${data.updated}\nSkipped: ${data.skipped}\nErrors: ${data.errors?.length || 0}`);
                        if (data.timeoutReached) {
                          alert('Time limit reached. Run again to continue processing.');
                        }
                      } catch (err) {
                        alert('Error: ' + err.message);
                      }
                    }}
                    className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <Sparkles size={16} />
                    Generate Counterpart Summaries
                  </button>
                </div>

                {/* Deal Descriptions */}
                <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[color:var(--gis-panel)]' : 'bg-[color:var(--gis-bg)]'}`}>
                  <h4 className={`font-medium ${themeClasses.text} mb-2 flex items-center gap-2`}>
                    <FileText size={16} className="text-green-500" />
                    Deal Descriptions
                  </h4>
                  <p className={`text-xs ${themeClasses.textMuted} mb-3`}>
                    Generate deal descriptions using counterpart context and deal details.
                  </p>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Generate AI descriptions for deals without one? This processes 20 at a time.')) return;
                      try {
                        const res = await fetch('/api/backfill-summaries', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ type: 'deals', batchSize: 20 })
                        });
                        const data = await res.json();
                        alert(`${data.message || 'Done'}\n\nUpdated: ${data.updated}\nSkipped: ${data.skipped}\nErrors: ${data.errors?.length || 0}`);
                        if (data.timeoutReached) {
                          alert('Time limit reached. Run again to continue processing.');
                        }
                      } catch (err) {
                        alert('Error: ' + err.message);
                      }
                    }}
                    className="w-full px-4 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <Sparkles size={16} />
                    Generate Deal Descriptions
                  </button>
                </div>
              </div>

              <p className={`text-xs ${themeClasses.textMuted} mt-4 text-center`}>
                Each run processes up to 20 items. Run multiple times for full database coverage.
              </p>
            </div>

            {/* Danger Zone */}
            <div className={`mt-8 pt-8 border-t-2 border-dashed ${theme === 'dark' ? 'border-red-900/50' : 'border-red-200'}`}>
              <div className={`${theme === 'dark' ? 'bg-red-950/30 border-red-900/50' : 'bg-red-50 border-red-200'} rounded-xl border p-6`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-red-900/50' : 'bg-red-100'} flex items-center justify-center flex-shrink-0`}>
                    <AlertCircle size={24} className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-red-400' : 'text-red-700'} mb-1`}>Danger Zone</h3>
                    <p className={`text-sm ${theme === 'dark' ? 'text-red-300/70' : 'text-red-600/70'} mb-4`}>
                      These actions are destructive and cannot be undone. A backup will be downloaded before any reset.
                    </p>

                    <div className="flex flex-wrap gap-3">
                      {/* Restore from Backup */}
                      <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                        theme === 'dark'
                          ? 'border-[color:var(--gis-rule)] text-[color:var(--gis-dim)] hover:bg-[color:var(--gis-panel)]'
                          : 'border-[color:var(--gis-rule)] text-[color:var(--gis-body)] hover:bg-[color:var(--gis-rule)]'
                      }`}>
                        <Upload size={16} />
                        <span className="text-sm font-medium">Restore from Backup</span>
                        <input
                          type="file"
                          accept=".json"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            try {
                              const text = await file.text();
                              const backup = JSON.parse(text);

                              if (!backup.deals && !backup.counterparts) {
                                alert('Invalid backup file: missing deals or counterparts data');
                                return;
                              }

                              const confirmRestore = window.confirm(
                                `Restore backup from ${backup.exportDate || 'unknown date'}?\n\n` +
                                `This will ADD:\n` +
                                `• ${backup.deals?.length || 0} deals\n` +
                                `• ${backup.counterparts?.length || 0} counterparts\n\n` +
                                `Note: This will not delete existing data, only add new records.`
                              );

                              if (!confirmRestore) return;

                              setResettingData(true);

                              // Restore counterparts first
                              let restoredCounterparts = 0;
                              for (const cp of (backup.counterparts || [])) {
                                try {
                                  const response = await authFetch('/api/counterparts', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ counterpart: { ...cp, id: undefined } })
                                  });
                                  if (response.ok) restoredCounterparts++;
                                } catch (err) {
                                  console.error('Error restoring counterpart:', err);
                                }
                              }

                              // Restore deals
                              let restoredDeals = 0;
                              for (const deal of (backup.deals || [])) {
                                try {
                                  const response = await authFetch('/api/deals', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      deal: { ...deal, id: undefined },
                                      counterparts: deal.deal_counterparts?.map(dc => ({
                                        name: dc.counterpart?.name,
                                        role: dc.role,
                                        stake: dc.stake
                                      })) || []
                                    })
                                  });
                                  if (response.ok) restoredDeals++;
                                } catch (err) {
                                  console.error('Error restoring deal:', err);
                                }
                              }

                              // Reload data
                              window.location.reload();

                              alert(`Restored ${restoredCounterparts} counterparts and ${restoredDeals} deals`);
                            } catch (err) {
                              console.error('Error reading backup file:', err);
                              alert('Error reading backup file: ' + err.message);
                            } finally {
                              setResettingData(false);
                              e.target.value = '';
                            }
                          }}
                        />
                      </label>

                      {/* Reset All Data */}
                      <button
                        onClick={() => setShowResetConfirm(true)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          theme === 'dark'
                            ? 'bg-red-900/50 text-red-300 border border-red-800 hover:bg-red-900'
                            : 'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200'
                        }`}
                      >
                        <Trash2 size={16} />
                        Reset All Data
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Settings Section - Inside Admin */}
            <div className="mt-8">
              <h2 className={`text-xl font-semibold ${themeClasses.text} mb-4`}>Settings</h2>
              <SettingsTab
                projects={projects}
                counterparts={counterparts}
                documents={documents}
                tasks={tasks}
                activities={activities}
                favorites={favorites}
                recentItems={recentItems}
                technologies={technologies}
                stages={stages}
                pipelineStatuses={pipelineStatuses}
                counterpartCategories={counterpartCategories}
                documentCategories={documentCategories}
                regions={regions}
                dealTechnologies={dealTechnologies}
                setProjects={setProjects}
                setCounterparts={setCounterparts}
                setDocuments={setDocuments}
                setTasks={setTasks}
                setActivities={setActivities}
                setFavorites={setFavorites}
                setRecentItems={setRecentItems}
                setTechnologies={setTechnologies}
                setStages={setStages}
                setPipelineStatuses={setPipelineStatuses}
                setCounterpartCategories={setCounterpartCategories}
                setDocumentCategories={setDocumentCategories}
                setRegions={setRegions}
                setDealTechnologies={setDealTechnologies}
                sectorConfig={sectorConfig}
                setSectorConfig={setSectorConfig}
                gDriveConnected={gDriveConnected}
                gDriveLoading={gDriveLoading}
                gDriveSyncing={gDriveSyncing}
                gDriveLastSync={gDriveLastSync}
                gDriveError={gDriveError}
                gDriveFileInfo={gDriveFileInfo}
                connectGoogleDrive={connectGoogleDrive}
                disconnectGoogleDrive={disconnectGoogleDrive}
                manualSyncGoogleDrive={manualSyncGoogleDrive}
                loadFromGoogleDrive={loadFromGoogleDrive}
                aiProvider={aiProvider}
                setAiProvider={setAiProvider}
                aiApiKey={geminiApiKey}
                setAiApiKey={setGeminiApiKey}
                saveAiSettings={saveGeminiApiKey}
                userTier={userTier}
                user={user}
                t={t}
              />
            </div>
            </>
            )}
          </div>
        )}

        {/* Privacy Policy Page */}
        {activeTab === "privacy" && (
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
              <p className="text-slate-600">Last updated: December 16, 2025</p>
            </div>

            <div className="prose prose-slate max-w-none">
              <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Introduction</h2>
                  <p className="text-slate-600">
                    Global Infrastructure Sherpa ("we," "our," or "us") is operated by Ganiz Holding Company, LLC. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our deal flow management application at gis.ganiz.net (the "Service").
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Information We Collect</h2>
                  <p className="text-slate-600 mb-3">We collect information that you provide directly to us:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li><strong>Account Information:</strong> When you sign in with Google, we receive your name, email address, and profile picture.</li>
                    <li><strong>Project Data:</strong> Information about projects, counterparts, tasks, and documents you create within the Service.</li>
                    <li><strong>Google Drive Data:</strong> If you connect Google Drive, we access only the files and folders you explicitly authorize for backup and sync purposes.</li>
                    <li><strong>Questions about our publications:</strong> If you use the "Ask about this report" box on a research or data page, we keep your question and the answer given, together with a one-way coded form of your IP address (never the address itself), to answer you, prevent abuse and improve our publications. Your question is processed by our AI provider, OpenAI, to produce the answer.</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">3. How We Use Your Information</h2>
                  <p className="text-slate-600 mb-3">We use the information we collect to:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li>Provide, maintain, and improve the Service</li>
                    <li>Store and sync your data to your Google Drive (at your request)</li>
                    <li>Send you technical notices and support messages</li>
                    <li>Respond to your comments and questions</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Data Storage and Security</h2>
                  <p className="text-slate-600">
                    Your data is primarily stored in your own Google Drive account. We do not maintain copies of your business data on our servers. Authentication tokens are stored securely and encrypted. We implement industry-standard security measures to protect your information.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Google API Services</h2>
                  <p className="text-slate-600">
                    Global Infrastructure Sherpa's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-blue-800 hover:underline">Google API Services User Data Policy</a>, including the Limited Use requirements. We only request the minimum permissions necessary to provide our Service.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Data Sharing</h2>
                  <p className="text-slate-600">
                    We do not sell, trade, or rent your personal information to third parties. We may share information only in the following circumstances: with your consent, to comply with legal obligations, or to protect our rights and safety.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Your Rights</h2>
                  <p className="text-slate-600 mb-3">You have the right to:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li>Access and export your data at any time</li>
                    <li>Delete your account and associated data</li>
                    <li>Revoke Google Drive access through your Google Account settings</li>
                    <li>Request information about data we hold about you</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Cookies and Tracking</h2>
                  <p className="text-slate-600">
                    We use essential cookies to maintain your session and preferences. We do not use third-party tracking or advertising cookies.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Children's Privacy</h2>
                  <p className="text-slate-600">
                    The Service is not intended for children under 13. We do not knowingly collect information from children under 13.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Changes to This Policy</h2>
                  <p className="text-slate-600">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Contact Us</h2>
                  <p className="text-slate-600">
                    If you have questions about this Privacy Policy, please contact us at:
                  </p>
                  <p className="text-slate-600 mt-2">
                    <strong>Ganiz Holding Company, LLC</strong><br />
                    Email: {CONTACT_DISPLAY}
                  </p>
                </div>
              </section>
            </div>

            <div className="text-center">
              <button 
                onClick={() => { setActiveTab('organizations'); window.history.pushState({}, '', '/'); }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-700 text-white rounded-xl hover:bg-blue-800 font-medium"
              >
                ← Back to Global Infrastructure Sherpa
              </button>
            </div>
          </div>
        )}

        {/* Terms of Service Page */}
        {activeTab === "terms" && (
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
              <p className="text-slate-600">Last updated: December 16, 2025</p>
            </div>

            <div className="prose prose-slate max-w-none">
              <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Acceptance of Terms</h2>
                  <p className="text-slate-600">
                    By accessing or using Global Infrastructure Sherpa ("the Service"), operated by Ganiz Holding Company, LLC ("we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Description of Service</h2>
                  <p className="text-slate-600">
                    Global Infrastructure Sherpa is a deal flow management application that helps users track projects, counterparts, tasks, and documents. The Service integrates with Google Drive for data storage and synchronization.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Account Registration</h2>
                  <p className="text-slate-600">
                    To use the Service, you must sign in with a Google account. You are responsible for maintaining the security of your account and for all activities that occur under your account.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Acceptable Use</h2>
                  <p className="text-slate-600 mb-3">You agree not to:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li>Use the Service for any unlawful purpose</li>
                    <li>Attempt to gain unauthorized access to the Service or its systems</li>
                    <li>Interfere with or disrupt the integrity or performance of the Service</li>
                    <li>Upload malicious code or content</li>
                    <li>Violate any applicable laws or regulations</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Your Data</h2>
                  <p className="text-slate-600">
                    You retain all rights to your data. By using the Service, you grant us a limited license to process your data solely for the purpose of providing the Service. Your data is stored in your Google Drive account, and you can export or delete it at any time.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Subscription and Billing</h2>
                  <p className="text-slate-600">
                    The Service offers both free and paid subscription tiers. Paid subscriptions are billed in advance on a monthly basis. You may cancel your subscription at any time, and cancellation will take effect at the end of the current billing period.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Service Availability</h2>
                  <p className="text-slate-600">
                    We strive to maintain high availability of the Service but do not guarantee uninterrupted access. We may modify, suspend, or discontinue the Service at any time with reasonable notice.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Intellectual Property</h2>
                  <p className="text-slate-600">
                    The Service, including its original content, features, and functionality, is owned by Ganiz Holding Company, LLC and is protected by international copyright, trademark, and other intellectual property laws.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Limitation of Liability</h2>
                  <p className="text-slate-600">
                    To the maximum extent permitted by law, Ganiz Holding Company, LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service. Our total liability shall not exceed the amount you paid us in the twelve months preceding the claim.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Disclaimer of Warranties</h2>
                  <p className="text-slate-600">
                    The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Termination</h2>
                  <p className="text-slate-600">
                    We may terminate or suspend your access to the Service immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Service will cease immediately.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">12. Governing Law</h2>
                  <p className="text-slate-600">
                    These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">13. Changes to Terms</h2>
                  <p className="text-slate-600">
                    We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page. Continued use of the Service after changes constitutes acceptance of the new Terms.
                  </p>
                </div>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">14. Contact Us</h2>
                  <p className="text-slate-600">
                    If you have questions about these Terms, please contact us at:
                  </p>
                  <p className="text-slate-600 mt-2">
                    <strong>Ganiz Holding Company, LLC</strong><br />
                    Email: {CONTACT_DISPLAY}
                  </p>
                </div>
              </section>
            </div>

            <div className="text-center">
              <button 
                onClick={() => { setActiveTab('organizations'); window.history.pushState({}, '', '/'); }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-700 text-white rounded-xl hover:bg-blue-800 font-medium"
              >
                ← Back to Global Infrastructure Sherpa
              </button>
            </div>
          </div>
        )}
        </>
        )}
        </>
        )}
        </div>
      )}
      </div>

      {/* Search Results Sidebar Panel — slide-in from right */}
      {(searchPanelCp || searchPanelDeal) && (
        <>
          <div className="fixed inset-0 bg-black/20 z-[60]" onClick={() => { setSearchPanelCp(null); setSearchPanelDeal(null); }} />
          <div className={`fixed top-14 right-0 bottom-0 w-[420px] max-w-[90vw] z-[61] overflow-y-auto shadow-2xl border-l
            ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}
          >
            {searchPanelCp && (
              <CounterpartPanel
                counterpart={searchPanelCp}
                onClose={() => setSearchPanelCp(null)}
                onViewFull={() => {
                  viewCounterpart(searchPanelCp);
                  setSearchPanelCp(null);
                  setSmartSearchResponse(null);
                }}
                isAdmin={user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net')}
                onEnrich={async () => {
                  const canEnrich = user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net');
                  if (!canEnrich) return;
                  setSearchPanelCp(prev => ({ ...prev, _isEnriching: true }));
                  try {
                    const enrichRes = await authFetch('/api/enrich-counterpart', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        counterpartId: searchPanelCp.id,
                        name: searchPanelCp.name,
                        websiteUrl: searchPanelCp.websiteUrl || searchPanelCp.website,
                        industry: 'infrastructure',
                        counterpartType: searchPanelCp.type || 'company'
                      })
                    });
                    if (enrichRes.ok) {
                      const enrichData = await enrichRes.json();
                      if (enrichData.success && enrichData.investmentPreferences) {
                        const enrichedCp = {
                          ...searchPanelCp,
                          investmentPreferences: enrichData.investmentPreferences,
                          summary: enrichData.investmentPreferences?.summary,
                          websiteUrl: enrichData.investmentPreferences?.website || searchPanelCp.websiteUrl,
                          headquarters: enrichData.investmentPreferences?.headquarters,
                          _isEnriching: false
                        };
                        setSearchPanelCp(enrichedCp);
                        setCounterparts(prev => {
                          const exists = prev.some(c => c.id === searchPanelCp.id);
                          if (exists) return prev.map(c => c.id === searchPanelCp.id ? { ...c, ...enrichedCp } : c);
                          return [...prev, enrichedCp];
                        });
                      } else {
                        setSearchPanelCp(prev => ({ ...prev, _isEnriching: false, _enrichError: true }));
                      }
                    } else {
                      setSearchPanelCp(prev => ({ ...prev, _isEnriching: false, _enrichError: true }));
                    }
                  } catch (err) {
                    console.error('Enrichment error:', err);
                    setSearchPanelCp(prev => ({ ...prev, _isEnriching: false, _enrichError: true }));
                  }
                }}
              />
            )}
            {searchPanelDeal && (
              <DealPanel
                deal={searchPanelDeal}
                panelEnrichment={searchPanelDealEnrichment}
                onClose={() => setSearchPanelDeal(null)}
                onViewFull={() => {
                  viewDeal(searchPanelDeal);
                  setSearchPanelDeal(null);
                  setSmartSearchResponse(null);
                }}
              />
            )}
          </div>
        </>
      )}

      {/* Sign Up Prompt removed — openSignUp() opens Clerk modal directly */}

      {/* LinkedIn-style paywall — shown when monthly quota exceeded */}
      {paywallInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-sm text-center shadow-2xl">
            <Lock size={32} className="mx-auto mb-3 text-blue-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
              You've viewed {paywallInfo.used} of {paywallInfo.limit} free {paywallInfo.type}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              {!isSignedIn
                ? 'Register free to get 4x more views and email alerts.'
                : userTier === 'free'
                  ? 'Upgrade for more monthly views and full access.'
                  : 'Upgrade to Pro for unlimited access.'}
            </p>
            {!isSignedIn ? (
              <SignUpButton mode="modal">
                <button className="w-full py-2.5 bg-blue-700 text-white rounded-xl text-sm font-medium hover:bg-blue-800 cursor-pointer">
                  Register free
                </button>
              </SignUpButton>
            ) : (
              <div className="space-y-2">
                {userTier === 'free' && (
                  <a href={`${SUBSCRIPTION_TIERS.basic.paymentLink}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.primaryEmailAddress?.emailAddress || '')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="block w-full py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
                    Basic — $20/mo (100 views)
                  </a>
                )}
                <a href={`${SUBSCRIPTION_TIERS.pro.paymentLink}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.primaryEmailAddress?.emailAddress || '')}`}
                  target="_blank" rel="noopener noreferrer"
                  className={`block w-full py-2.5 ${userTier === 'free' ? 'border border-blue-600 text-blue-700 hover:bg-blue-50' : 'bg-blue-700 text-white hover:bg-blue-800'} rounded-xl text-sm font-medium`}>
                  Pro — $200/mo (unlimited)
                </a>
              </div>
            )}
            <button onClick={() => { setPaywallInfo(null); setActiveTab('subscribe'); }} className="mt-3 text-sm text-blue-600 hover:text-blue-700">
              See all plans
            </button>
            <button onClick={() => setPaywallInfo(null)} className="block mx-auto mt-2 text-xs text-slate-400 hover:text-slate-600">
              Go back
            </button>
          </div>
        </div>
      )}

      {showLimitModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('limitReached')}</h3>
              <p className="text-slate-600 mb-4">
                {t('limitReachedDesc')}
              </p>
              <div className="bg-slate-100 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">{t('currentPlan')}</span>
                  <span className="font-semibold text-slate-900">{(SUBSCRIPTION_TIERS[userTier] || SUBSCRIPTION_TIERS.basic).name}</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowLimitModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-700">
                  {t('cancel')}
                </button>
                <button onClick={() => { setShowLimitModal(false); setActiveTab('billing'); }} className="flex-1 px-4 py-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800">
                  {t('upgrade')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ProjectModal removed - counterparts-only platform */}
      <CompareCounterpartsModal />
      {showCounterpartModal && (
        <CounterpartModal
          counterpart={editingCounterpart}
          onClose={() => { setShowCounterpartModal(false); setEditingCounterpart(null); }}
          onSave={handleSaveCounterpart}
          counterparts={counterparts}
          technologies={technologies}
          stages={stages}
          regions={regions}
          counterpartCategories={counterpartCategories}
          taxonomyFilters={taxonomyFilters}
          counterpartTags={counterpartTags}
          sectorConfig={sectorConfig}
          projects={projects}
          user={user}
          t={t}
          setActiveTab={setActiveTab}
        />
      )}
      {showDealModal && (
        <DealModal
          deal={editingDeal}
          onClose={() => { setShowDealModal(false); setEditingDeal(null); }}
          onSave={handleSaveDeal}
          counterparts={counterparts}
          setCounterparts={setCounterparts}
          dealTechnologies={dealTechnologies}
          dealRoles={DEAL_ROLES}
          theme={theme}
          themeClasses={themeClasses}
          user={user}
        />
      )}
      {showCounterpartPicker && (
        <CounterpartPickerModal 
          project={showCounterpartPicker} 
          onClose={() => setShowCounterpartPicker(null)} 
        />
      )}
      {/* Document and Project modals removed - counterparts-only platform */}
      {sharingCounterpart && (
        <ShareCounterpartModal
          counterpart={sharingCounterpart}
          documents={documents}
          onClose={() => setSharingCounterpart(null)}
          user={user}
        />
      )}
      {/* Floating Compare Bar */}
      {compareList.length > 0 && !showCompareView && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className={`${themeClasses.card} border ${themeClasses.border} rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-4`}>
            <div className="flex items-center gap-2">
              {compareList.map(cpId => {
                const cp = counterparts.find(c => c.id === cpId);
                if (!cp) return null;
                return (
                  <div key={cpId} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <span className={`text-sm font-medium ${themeClasses.text}`}>{cp.name}</span>
                    <button onClick={() => setCompareList(prev => prev.filter(id => id !== cpId))} className="text-slate-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
              {compareList.length < 3 && (
                <span className={`text-xs ${themeClasses.textMuted}`}>Add up to {3 - compareList.length} more</span>
              )}
            </div>
            <button
              onClick={() => setShowCompareView(true)}
              disabled={compareList.length < 2}
              className="px-5 py-2 bg-blue-700 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Compare ({compareList.length})
            </button>
            <button onClick={() => setCompareList([])} className={`text-sm ${themeClasses.textMuted} hover:text-red-500`}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Floating Deal Compare Bar */}
      {dealCompareList.length > 0 && !showDealCompareView && (
        <div className={`fixed ${compareList.length > 0 && !showCompareView ? 'bottom-24' : 'bottom-6'} left-1/2 -translate-x-1/2 z-50`}>
          <div className={`${themeClasses.card} border ${themeClasses.border} rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-4`}>
            <div className="flex items-center gap-2">
              {dealCompareList.map(dealId => {
                const deal = deals.find(d => d.id === dealId);
                if (!deal) return null;
                return (
                  <div key={dealId} className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <span className={`text-sm font-medium ${themeClasses.text} max-w-[200px] truncate`}>{deal.description || deal.headline || 'Deal'}</span>
                    <button onClick={() => setDealCompareList(prev => prev.filter(id => id !== dealId))} className="text-slate-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
              {dealCompareList.length < 3 && (
                <span className={`text-xs ${themeClasses.textMuted}`}>Add up to {3 - dealCompareList.length} more</span>
              )}
            </div>
            <button
              onClick={() => setShowDealCompareView(true)}
              disabled={dealCompareList.length < 2}
              className="px-5 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Compare ({dealCompareList.length})
            </button>
            <button onClick={() => setDealCompareList([])} className={`text-sm ${themeClasses.textMuted} hover:text-red-500`}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Deal Comparison View Modal */}
      {showDealCompareView && dealCompareList.length >= 2 && (() => {
        const compareDeals = dealCompareList.map(id => deals.find(d => d.id === id)).filter(Boolean);
        const fmtCap = (deal) => {
          const cap = deal.capacity;
          const unit = deal.capacity_unit || deal.capacityUnit || 'MW';
          if (!cap) return null;
          if (typeof cap === 'object' && cap !== null) return cap.amount ? `${cap.amount} ${cap.unit || unit}` : null;
          return `${cap} ${unit}`;
        };
        const fmtSize = (deal) => {
          const dv = deal.financials?.dealValue;
          if (dv?.amount) {
            const num = parseFloat(dv.amount);
            if (isNaN(num)) return null;
            const cur = dv.currency || '$';
            const u = dv.unit || 'M';
            if (u === 'B') return `${cur}${num}B`;
            if (num >= 1000) return `${cur}${(num / 1000).toFixed(1)}B`;
            return `${cur}${num}M`;
          }
          return deal.size || deal.value || null;
        };
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4 pt-8">
            <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} shadow-2xl w-full max-w-6xl`}>
              {/* Header */}
              <div className={`p-6 border-b ${themeClasses.border} flex items-center justify-between`}>
                <div>
                  <h2 className={`text-xl font-semibold ${themeClasses.text}`}>Compare Deals</h2>
                  <p className={`text-sm ${themeClasses.textMuted}`}>Side-by-side comparison of {dealCompareList.length} deals</p>
                </div>
                <button onClick={() => setShowDealCompareView(false)} className={`p-2 rounded-xl ${themeClasses.cardHover}`}>
                  <X size={24} className={themeClasses.textMuted} />
                </button>
              </div>

              {/* Comparison Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${themeClasses.border}`}>
                      <th className={`p-4 text-left text-xs font-semibold uppercase ${themeClasses.textMuted} w-40`}>Metric</th>
                      {compareDeals.map(deal => (
                        <th key={deal.id} className="p-4 text-center min-w-[200px]">
                          <div className="flex flex-col items-center gap-2">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-amber-700/20' : 'bg-amber-100'}`}>
                              <TrendingUp size={20} className="text-amber-700" />
                            </div>
                            <p className={`font-semibold ${themeClasses.text} line-clamp-2 text-sm`}>{deal.description || deal.headline}</p>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${themeClasses.border}`}>
                    {/* Status */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Status</td>
                      {compareDeals.map(deal => (
                        <td key={deal.id} className={`p-4 text-center text-sm ${themeClasses.text}`}>
                          {deal.status ? (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              deal.status === 'closed' ? 'bg-green-50 text-green-700' :
                              deal.status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                              deal.status === 'announced' ? 'bg-purple-50 text-purple-700' :
                              deal.status === 'tender' ? 'bg-amber-50 text-amber-700' :
                              deal.status === 'cancelled' ? 'bg-red-50 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>{DEAL_STATUSES.find(s => s.value === deal.status)?.label || deal.status}</span>
                          ) : '-'}
                        </td>
                      ))}
                    </tr>
                    {/* Deal Type */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Deal Type</td>
                      {compareDeals.map(deal => {
                        const dt = deal.dealType || deal.deal_type;
                        return <td key={deal.id} className={`p-4 text-center text-sm ${themeClasses.text}`}>{DEAL_TYPES.find(t => t.value === dt)?.label || dt || '-'}</td>;
                      })}
                    </tr>
                    {/* Technology */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Technology</td>
                      {compareDeals.map(deal => {
                        const techs = (Array.isArray(deal.technology) ? deal.technology : [deal.technology]).filter(Boolean);
                        return (
                          <td key={deal.id} className="p-4 text-center">
                            <div className="flex flex-wrap justify-center gap-1">
                              {techs.length > 0 ? techs.map((t, i) => (
                                <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{dealTechnologies.find(dt => dt.value === t)?.label || t}</span>
                              )) : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {/* Country */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Country</td>
                      {compareDeals.map(deal => (
                        <td key={deal.id} className="p-4 text-center">
                          {(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).length > 0 ? (
                            <div className="flex flex-wrap justify-center gap-1">
                              {(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).map((c, ci) => (
                                <span key={ci} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">{c}</span>
                              ))}
                            </div>
                          ) : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                        </td>
                      ))}
                    </tr>
                    {/* Capacity */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Capacity</td>
                      {compareDeals.map(deal => {
                        const cap = fmtCap(deal);
                        return (
                          <td key={deal.id} className="p-4 text-center">
                            {cap ? <span className="text-lg font-bold text-blue-700">{cap}</span> : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Deal Size */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Deal Size</td>
                      {compareDeals.map(deal => {
                        const size = fmtSize(deal);
                        return (
                          <td key={deal.id} className="p-4 text-center">
                            {size ? <span className="text-lg font-bold text-blue-700">{size}</span> : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Date */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Date</td>
                      {compareDeals.map(deal => (
                        <td key={deal.id} className={`p-4 text-center text-sm ${themeClasses.text}`}>
                          {deal.announcement_date || deal.signing_date || deal.date || '-'}
                        </td>
                      ))}
                    </tr>
                    {/* Counterparts */}
                    <tr>
                      <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Counterparts</td>
                      {compareDeals.map(deal => {
                        const lcs = deal.linkedCounterparts || (deal.deal_counterparts || []).map(dc => ({ name: dc.counterpart?.name || dc.name, role: dc.role }));
                        return (
                          <td key={deal.id} className="p-4 text-center">
                            <div className="flex flex-wrap justify-center gap-1">
                              {lcs.length > 0 ? lcs.filter(lc => lc.name).map((lc, i) => (
                                <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded">{lc.name}</span>
                              )) : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className={`p-4 border-t ${themeClasses.border} flex justify-between`}>
                <button onClick={() => { setShowDealCompareView(false); setDealCompareList([]); }} className={`px-4 py-2 text-sm ${themeClasses.textMuted} hover:text-red-500`}>
                  Clear Comparison
                </button>
                <button onClick={() => setShowDealCompareView(false)} className="px-6 py-2 bg-blue-700 text-white rounded-xl text-sm font-medium hover:bg-blue-800">
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Comparison View Modal */}
      {showCompareView && compareList.length >= 2 && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4 pt-8">
          <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} shadow-2xl w-full max-w-6xl`}>
            {/* Header */}
            <div className={`p-6 border-b ${themeClasses.border} flex items-center justify-between`}>
              <div>
                <h2 className={`text-xl font-semibold ${themeClasses.text}`}>Compare Counterparts</h2>
                <p className={`text-sm ${themeClasses.textMuted}`}>Side-by-side comparison of {compareList.length} counterparts</p>
              </div>
              <button onClick={() => setShowCompareView(false)} className={`p-2 rounded-xl ${themeClasses.cardHover}`}>
                <X size={24} className={themeClasses.textMuted} />
              </button>
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${themeClasses.border}`}>
                    <th className={`p-4 text-left text-xs font-semibold uppercase ${themeClasses.textMuted} w-40`}>Metric</th>
                    {compareList.map(cpId => {
                      const cp = counterparts.find(c => c.id === cpId);
                      const websiteUrl = cp?.websiteUrl || cp?.website || cp?.investmentPreferences?.website;
                      const domain = websiteUrl ? websiteUrl.replace(/^https?:\/\//, '').split('/')[0] : '';
                      return (
                        <th key={cpId} className="p-4 text-center min-w-[200px]">
                          <div className="flex flex-col items-center gap-2">
                            {domain ? (
                              <img src={`https://cdn.brandfetch.io/${domain}/icon?c=1idrTt0p2-SJacppl92`} alt="" className="w-10 h-10 rounded-lg bg-white border border-slate-200 p-1.5" onError={(e) => e.target.style.display = 'none'} />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-green-700 font-bold">{cp?.name?.charAt(0)}</div>
                            )}
                            <button onClick={() => { setShowCompareView(false); viewCounterpart(cp); }} className={`font-semibold ${themeClasses.text} hover:text-blue-700`}>
                              {cp?.name}
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className={`divide-y ${themeClasses.border}`}>
                  {/* Category */}
                  <tr>
                    <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Category</td>
                    {compareList.map(cpId => {
                      const cp = counterparts.find(c => c.id === cpId);
                      return <td key={cpId} className={`p-4 text-center text-sm ${themeClasses.text}`}>{cp?.category || '-'}</td>;
                    })}
                  </tr>
                  {/* Location */}
                  <tr>
                    <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Location</td>
                    {compareList.map(cpId => {
                      const cp = counterparts.find(c => c.id === cpId);
                      const hq = cp?.headquarters || cp?.investmentPreferences?.headquarters;
                      const loc = hq ? [hq.city, hq.country].filter(Boolean).join(', ') : cp?.location || '-';
                      return <td key={cpId} className={`p-4 text-center text-sm ${themeClasses.text}`}>{loc}</td>;
                    })}
                  </tr>
                  {/* Total Deals */}
                  <tr>
                    <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Deals</td>
                    {compareList.map(cpId => {
                      const cp = counterparts.find(c => c.id === cpId);
                      const dbCount = deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cpId)).length;
                      return <td key={cpId} className={`p-4 text-center`}><span className="text-lg font-bold text-blue-700">{dbCount}</span></td>;
                    })}
                  </tr>
                  {/* Technologies */}
                  <tr>
                    <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Technologies</td>
                    {compareList.map(cpId => {
                      const cpDeals = deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cpId));
                      const techs = [...new Set(cpDeals.flatMap(d => Array.isArray(d.technology) ? d.technology : (d.technology ? [d.technology] : [])).filter(Boolean))];
                      return (
                        <td key={cpId} className="p-4 text-center">
                          <div className="flex flex-wrap justify-center gap-1">
                            {techs.length > 0 ? techs.slice(0, 5).map((t, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{t}</span>
                            )) : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Markets */}
                  <tr>
                    <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Markets</td>
                    {compareList.map(cpId => {
                      const cpDeals = deals.filter(d => (d.deal_counterparts || []).some(dc => dc.counterpart?.id === cpId));
                      const geos = [...new Set(cpDeals.flatMap(d => Array.isArray(d.country) ? d.country : (d.country ? [d.country] : [])).filter(Boolean))];
                      return (
                        <td key={cpId} className="p-4 text-center">
                          <div className="flex flex-wrap justify-center gap-1">
                            {geos.length > 0 ? geos.slice(0, 5).map((g, i) => (
                              <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">{g}</span>
                            )) : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Roles */}
                  <tr>
                    <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Roles</td>
                    {compareList.map(cpId => {
                      const cpDealLinks = deals.flatMap(d => (d.deal_counterparts || []).filter(dc => dc.counterpart?.id === cpId));
                      const roles = [...new Set(cpDealLinks.map(dc => dc.role).filter(Boolean))];
                      return (
                        <td key={cpId} className="p-4 text-center">
                          <div className="flex flex-wrap justify-center gap-1">
                            {roles.length > 0 ? roles.map((r, i) => (
                              <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded capitalize">{r.replace(/_/g, ' ')}</span>
                            )) : <span className={`text-sm ${themeClasses.textMuted}`}>-</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Summary */}
                  <tr>
                    <td className={`p-4 text-sm font-medium ${themeClasses.textMuted}`}>Summary</td>
                    {compareList.map(cpId => {
                      const cp = counterparts.find(c => c.id === cpId);
                      const summary = cp?.summary || cp?.investmentPreferences?.summary || '';
                      return <td key={cpId} className={`p-4 text-sm ${themeClasses.text} leading-relaxed`}>{summary ? summary.substring(0, 200) + (summary.length > 200 ? '...' : '') : '-'}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className={`p-4 border-t ${themeClasses.border} flex justify-between`}>
              <button onClick={() => { setShowCompareView(false); setCompareList([]); }} className={`px-4 py-2 text-sm ${themeClasses.textMuted} hover:text-red-500`}>
                Clear Comparison
              </button>
              <button onClick={() => setShowCompareView(false)} className="px-6 py-2 bg-blue-700 text-white rounded-xl text-sm font-medium hover:bg-blue-800">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {sharingDeal && (
        <ShareDealModal
          deal={sharingDeal}
          onClose={() => setSharingDeal(null)}
          user={user}
        />
      )}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          type={showDeleteConfirm.type}
          item={showDeleteConfirm.item}
          onConfirm={() => {
            if (showDeleteConfirm.type === "project") {
              handleDeleteProject(showDeleteConfirm.item.id);
            } else {
              handleDeleteCounterpart(showDeleteConfirm.item.id);
            }
          }}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}
      
      {/* AI Summary Modal */}
      {aiSummary && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className={`${theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white'} rounded-2xl max-w-2xl w-full shadow-xl border`}>
            <div className={`p-6 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'} flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-100'}`}>
                  <Sparkles size={20} className="text-blue-800" />
                </div>
                <div>
                  <h3 className={`font-semibold ${themeClasses.text}`}>AI Summary</h3>
                  {aiSummary.itemName && (
                    <p className={`text-sm ${themeClasses.textMuted}`}>{aiSummary.itemName}</p>
                  )}
                </div>
              </div>
              <button onClick={() => setAiSummary(null)} className={`p-2 rounded-xl ${theme === 'dark' ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
                <X size={20} className={themeClasses.textMuted} />
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {aiSummary.error ? (
                <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'} border`}>
                  <p className="text-red-600 flex items-center gap-2">
                    <AlertCircle size={16} /> {aiSummary.error}
                  </p>
                </div>
              ) : (
                <div className={`prose prose-sm max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
                  <p className={`${themeClasses.text} whitespace-pre-wrap leading-relaxed`}>{aiSummary.summary}</p>
                </div>
              )}
            </div>
            <div className={`p-4 border-t ${theme === 'dark' ? 'border-slate-700 bg-slate-800/50' : 'border-slate-100 bg-slate-50'} rounded-b-2xl flex justify-end`}>
              <button onClick={() => setAiSummary(null)} className={`${themeClasses.btnSecondary} px-4 py-2 rounded-xl text-sm font-medium`}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Global Slide-in Counterpart Panel - works from any page */}
      {panelCounterpart && (selectedDeal || selectedCounterpart || activeTab === 'home' || activeTab === 'deals' || activeTab === 'insights' || activeTab === 'ecosystem' || activeTab === 'matchup' || activeTab === 'organizations' || activeTab === 'signals') && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setPanelCounterpart(null)}
          />
          {/* Panel */}
          <div className={`relative w-96 max-w-[90vw] h-full overflow-y-auto pb-16 lg:pb-0 ${theme === 'dark' ? 'bg-slate-900' : 'bg-white'} shadow-2xl animate-slide-in-right`}>
            <CounterpartPanel
              counterpart={panelCounterpart}
              onClose={() => setPanelCounterpart(null)}
              onViewFull={() => {
                viewCounterpart(panelCounterpart);
                setPanelCounterpart(null);
                setSelectedDeal(null); // Close deal page when going to full counterpart
              }}
              isAdmin={user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net')}
              onEnrich={async () => {
                const canEnrich = user?.publicMetadata?.admin || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') || user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net');
                if (!canEnrich) return;
                setPanelCounterpart({ ...panelCounterpart, _isEnriching: true });
                try {
                  const enrichRes = await authFetch('/api/enrich-counterpart', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      counterpartId: panelCounterpart.id,
                      name: panelCounterpart.name,
                      websiteUrl: panelCounterpart.websiteUrl || panelCounterpart.website,
                      industry: 'infrastructure',
                      counterpartType: panelCounterpart.type || 'company'
                    })
                  });
                  if (enrichRes.ok) {
                    const enrichData = await enrichRes.json();
                    if (enrichData.success && enrichData.investmentPreferences) {
                      const enrichedCp = {
                        ...panelCounterpart,
                        investmentPreferences: enrichData.investmentPreferences,
                        summary: enrichData.investmentPreferences?.summary,
                        websiteUrl: enrichData.investmentPreferences?.website || panelCounterpart.websiteUrl,
                        headquarters: enrichData.investmentPreferences?.headquarters,
                        _isEnriching: false
                      };
                      setPanelCounterpart(enrichedCp);
                      setCounterparts(prev => {
                        const exists = prev.some(c => c.id === panelCounterpart.id);
                        if (exists) return prev.map(c => c.id === panelCounterpart.id ? { ...c, ...enrichedCp } : c);
                        return [...prev, enrichedCp];
                      });
                    } else {
                      setPanelCounterpart({ ...panelCounterpart, _isEnriching: false, _enrichError: true });
                    }
                  } else {
                    setPanelCounterpart({ ...panelCounterpart, _isEnriching: false, _enrichError: true });
                  }
                } catch (err) {
                  console.error('Enrichment error:', err);
                  setPanelCounterpart({ ...panelCounterpart, _isEnriching: false, _enrichError: true });
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Global Slide-in Deal Panel - works from Insights/Dashboard */}
      {panelDeal && !selectedDeal && (activeTab === 'insights' || activeTab === 'home') && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => { setPanelDeal(null); setPanelDealSource(null); }}
          />
          <div className={`relative w-96 max-w-[90vw] h-full overflow-y-auto pb-16 lg:pb-0 ${theme === 'dark' ? 'bg-slate-900' : 'bg-white'} shadow-2xl animate-slide-in-right`}>
            <DealPanel
              deal={panelDeal}
              sourceCounterpart={panelDealSource}
              panelEnrichment={panelDealEnrichment}
              onClose={() => { setPanelDeal(null); setPanelDealSource(null); }}
              onViewFull={() => {
                viewDeal(panelDeal);
                setPanelDeal(null);
                setPanelDealSource(null);
              }}
              onSelectCounterpart={(cp) => {
                viewPanelCounterpart(cp);
                setPanelDeal(null);
                setPanelDealSource(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setSelectedProject(null);
          setSelectedCounterpart(null);
          setSelectedDeal(null);
          setActiveTab(tab);
        }}
        badges={{
          projects: projects.filter(p => p.isLive).length,
          tasks: taskStats.overdue ? taskStats.overdue.length : 0,
        }}
        theme={theme}
        onMoreClick={() => setShowMobileMoreMenu(true)}
      />
      
      {/* Mobile More Menu */}
      <MobileMoreMenu
        isOpen={showMobileMoreMenu}
        onClose={() => setShowMobileMoreMenu(false)}
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setSelectedProject(null);
          setSelectedCounterpart(null);
          setSelectedDeal(null);
          setActiveTab(tab);
        }}
        theme={theme}
        isAdmin={isAdmin}
      />
      
      {/* Landing page removed — anonymous users now see the full app with
          LinkedIn-style usage quotas. The value is shown first; gates appear
          after N uses. See src/lib/usageQuotas.js for quota config. */}

      {/* Footer with Privacy Link - visible on desktop only */}
      <div className={`hidden lg:block fixed bottom-0 left-0 lg:left-[var(--gis-nav-w)] right-0 ${theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} border-t px-4 py-2 text-center text-xs z-40`}>
        <span className={theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}>© 2026 Global Infrastructure Sherpa by Ganiz</span>
        <span className={theme === 'dark' ? 'text-slate-600' : 'text-slate-300'}> · </span>
        <button onClick={() => setActiveTab('subscribe')} className={`${theme === 'dark' ? 'text-blue-800 hover:text-blue-300' : 'text-blue-800 hover:text-blue-700'} font-medium transition-colors`}>Pricing</button>
        <span className={theme === 'dark' ? 'text-slate-600' : 'text-slate-300'}> · </span>
        <a href="/about" className={`${theme === 'dark' ? 'text-blue-800 hover:text-blue-300' : 'text-blue-800 hover:text-blue-700'} font-medium transition-colors`}>About</a>
        <span className={theme === 'dark' ? 'text-slate-600' : 'text-slate-300'}> · </span>
        <a href="/help" className={`${theme === 'dark' ? 'text-blue-800 hover:text-blue-300' : 'text-blue-800 hover:text-blue-700'} font-medium transition-colors`}>Help Center</a>
        <span className={theme === 'dark' ? 'text-slate-600' : 'text-slate-300'}> · </span>
        <a href="/privacy" className={`${theme === 'dark' ? 'text-blue-800 hover:text-blue-300' : 'text-blue-800 hover:text-blue-700'} font-medium transition-colors`}>Privacy Policy</a>
        <span className={theme === 'dark' ? 'text-slate-600' : 'text-slate-300'}> · </span>
        <a href="https://globalinfrastructuresherpa.substack.com/" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'} font-medium transition-colors`}>Newsletter</a>
      </div>

      </>

      {/* LinkedIn-style dismissible registration modal — floats over content */}
      <RegistrationModal />

      </Suspense>
    </div>
  );
}